"use client"

// src/theme/ThemeProvider.tsx
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

// src/theme/cofactorTokens.ts
var cofactorFonts = {
  sans: '"Inter","Plus Jakarta Sans", "S\xF6hne", Helvetica, Arial, sans-serif',
  mono: '"JetBrains Mono", ui-monospace, Menlo, monospace',
  serif: '"Instrument Serif", "Times New Roman", serif'
};

// src/theme/theme.ts
var themes = {
  cofactor: {
    colors: {
      background: "0 0% 100%",
      // #ffffff — paper canvas (sidebar rail uses surface-2)
      foreground: "220 49% 8%",
      // #0b1220 — ink
      card: "0 0% 100%",
      // #ffffff — paper
      cardForeground: "220 49% 8%",
      popover: "0 0% 100%",
      popoverForeground: "220 49% 8%",
      primary: "219.8 84.1% 55.7%",
      // #2f6fed — accent
      primaryForeground: "0 0% 100%",
      secondary: "216 45.5% 97.8%",
      // #f7f9fc — paper (inset surfaces)
      secondaryForeground: "220 49% 8%",
      muted: "216 45.5% 97.8%",
      // paper
      mutedForeground: "215 22% 32%",
      // #3f4d63 — ink-3
      accent: "216 45.5% 97.8%",
      // paper
      accentForeground: "220 49% 8%",
      destructive: "17.5 88.3% 40.4%",
      // #c2410c — fail / burnt orange
      destructiveForeground: "0 0% 100%",
      border: "210 5% 89%",
      // ≈ rgba(11,18,32,0.12) on white — website rule-soft
      input: "216 45.5% 97.8%",
      // #f7f9fc — paper fill (website input)
      ring: "219.8 84.1% 55.7%",
      // Soft pill backgrounds (HSL — pair with soft-fg vars below)
      accentSoft: "217 92% 95%",
      // #e8f0fe
      successSoft: "160 62% 92%",
      // #def7ee
      warnSoft: "33 89% 93%",
      // #fbeede
      dangerSoft: "20 85% 92%",
      // #fde5d6 — fail-soft
      waitingSoft: "190 35% 94%"
    },
    vars: {
      // Soft pill foregrounds (raw hex — paired with the *-soft bgs above)
      accentSoftFg: "#1f4fb8",
      waitingSoftFg: "#215059",
      // Ground (white) / paper (inset) / ink (sidebar, tooltips) / cool hairline
      bg: "#ffffff",
      paper: "#f7f9fc",
      ink: "#0b1220",
      // Schematic chrome tokens (consumed by Phase 2 surfaces)
      rule: "#14213d",
      ruleSoft: "rgba(11, 18, 32, 0.12)",
      ruleSofter: "rgba(11, 18, 32, 0.06)",
      shadowHardSm: "4px 4px 0 #0b1220",
      shadowHard: "8px 8px 0 rgba(11, 18, 32, 0.1)",
      frameRadius: "8px",
      fontSans: cofactorFonts.sans,
      fontMono: cofactorFonts.mono,
      fontSerif: cofactorFonts.serif
    }
  },
  // Cofactor dark — derived from the cofactor (light) palette: surfaces flipped
  // to a deep cool navy family (consistent with the existing navy sidebar rail),
  // ink lifted to near-white, hairlines darkened, soft pills recomputed as
  // low-lightness tints. Azure primary is kept; pass/fail are the lifted green /
  // burnt-orange pair. Starting values — iterate in-browser.
  cofactorDark: {
    colors: {
      background: "220 49% 8%",
      // #0b1220 — ground (dark)
      foreground: "214 32% 92%",
      // #e6ebf2 — ink (near-white cool)
      card: "220 49% 8%",
      // ground — panels separate by border, like light
      cardForeground: "214 32% 92%",
      popover: "215 38.7% 12.2%",
      // #131d2b — paper
      popoverForeground: "214 32% 92%",
      primary: "218 84% 60%",
      // #3b82f6 — accent (lifted azure)
      primaryForeground: "0 0% 100%",
      secondary: "215 38.7% 12.2%",
      // #131d2b — paper
      secondaryForeground: "214 32% 92%",
      muted: "215 38.7% 12.2%",
      mutedForeground: "214 20% 72%",
      // #a9b4c4 — ink-3
      accent: "215 38.7% 12.2%",
      accentForeground: "214 32% 92%",
      destructive: "19.8 74.6% 52.2%",
      // #e0662a — fail (lifted burnt orange)
      destructiveForeground: "0 0% 100%",
      border: "217 21% 19%",
      // ≈ rgba(238,242,247,0.12) on #0b1220
      input: "215 38.7% 12.2%",
      // #131d2b — paper fill
      ring: "218 84% 60%",
      // Soft pill backgrounds (HSL — pair with soft-fg vars below)
      accentSoft: "218 55% 18%",
      // deep azure tint
      successSoft: "162 45% 15%",
      // deep green tint
      warnSoft: "33 50% 18%",
      // deep amber tint
      dangerSoft: "12 45% 20%",
      // deep orange tint
      waitingSoft: "190 30% 16%"
      // deep slate-teal tint
    },
    vars: {
      // Soft pill foregrounds (raw hex — paired with the *-soft bgs above)
      accentSoftFg: "#9dbcf5",
      waitingSoftFg: "#8cc9d4",
      // Body / paper / cool hairline
      bg: "#0b1220",
      paper: "#131d2b",
      ink: "#eef3f9",
      // Schematic chrome tokens (light-on-dark rules)
      rule: "rgba(238, 242, 247, 0.3)",
      ruleSoft: "rgba(238, 242, 247, 0.12)",
      ruleSofter: "rgba(238, 242, 247, 0.06)",
      shadowHardSm: "4px 4px 0 rgba(238, 242, 247, 0.9)",
      shadowHard: "8px 8px 0 rgba(0, 0, 0, 0.45)",
      frameRadius: "8px",
      fontSans: cofactorFonts.sans,
      fontMono: cofactorFonts.mono,
      fontSerif: cofactorFonts.serif
    }
  }
};
var defaultThemeMode = "light";

// src/theme/ThemeProvider.tsx
import { jsx } from "react/jsx-runtime";
function themeForScheme(scheme) {
  return scheme === "dark" ? "cofactorDark" : "cofactor";
}
var ThemeContext = createContext(void 0);
function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
function getSystemScheme() {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function resolveScheme(mode) {
  return mode === "system" ? getSystemScheme() : mode;
}
function normalizeStoredMode(mode) {
  return mode === "light" || mode === "dark" || mode === "system" ? mode : "light";
}
function ThemeProvider({
  children,
  defaultMode = defaultThemeMode,
  storageKey = "ui-theme"
}) {
  const [state, setState] = useState(() => {
    let mode = defaultMode;
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          mode = normalizeStoredMode(parsed.mode ?? defaultMode);
        } catch {
        }
      }
    }
    return { mode, scheme: resolveScheme(mode) };
  });
  useEffect(() => {
    if (state.mode !== "system") return;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e) => {
      setState((prev) => ({
        ...prev,
        scheme: e.matches ? "dark" : "light"
      }));
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [state.mode]);
  const appliedTheme = themeForScheme(state.scheme);
  const config = React.useMemo(
    () => ({ mode: state.mode, scheme: state.scheme, theme: appliedTheme }),
    [state.mode, state.scheme, appliedTheme]
  );
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("cofactor", "cofactor-dark");
    if (appliedTheme === "cofactorDark") {
      root.classList.add("cofactor", "cofactor-dark");
    } else {
      root.classList.add(appliedTheme);
    }
    const themeEntry = themes[appliedTheme];
    Object.entries(themeEntry.colors).forEach(([key, value]) => {
      root.style.setProperty(`--${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`, value);
    });
    Object.entries(themeEntry.vars).forEach(([key, value]) => {
      root.style.setProperty(`--${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`, value);
    });
    root.style.setProperty("--radius", "0px");
    const toStore = { mode: state.mode };
    localStorage.setItem(storageKey, JSON.stringify(toStore));
  }, [appliedTheme, state.mode, storageKey]);
  const setMode = useCallback((mode) => {
    setState((prev) => ({
      ...prev,
      mode,
      scheme: resolveScheme(mode)
    }));
  }, []);
  return /* @__PURE__ */ jsx(ThemeContext.Provider, { value: { config, setMode }, children });
}

// src/utils/cn.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/components/Button.tsx
import React3 from "react";
import { Button as AriaButton } from "react-aria-components";

// src/components/Tooltip.tsx
import React2 from "react";
import { createPortal } from "react-dom";
import { Fragment, jsx as jsx2, jsxs } from "react/jsx-runtime";
var TooltipContext = React2.createContext(null);
var mergeRefs = (...refs) => {
  return (value) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") {
        ref(value);
      } else {
        ref.current = value;
      }
    });
  };
};
var composeEventHandlers = (original, next) => (event) => {
  original?.(event);
  next?.(event);
};
var getOppositePlacement = (placement) => {
  switch (placement) {
    case "top":
      return "bottom";
    case "bottom":
      return "top";
    case "left":
      return "right";
    case "right":
    default:
      return "left";
  }
};
var TooltipTrigger = ({
  children,
  delay = 100,
  closeDelay = 50,
  placement = "top",
  offset = 8
}) => {
  const [isOpen, setIsOpen] = React2.useState(false);
  const [triggerRect, setTriggerRect] = React2.useState(null);
  const triggerRef = React2.useRef(null);
  const openTimeout = React2.useRef(void 0);
  const closeTimeout = React2.useRef(void 0);
  const show = React2.useCallback(() => {
    if (!triggerRef.current) {
      return;
    }
    setTriggerRect(triggerRef.current.getBoundingClientRect());
    setIsOpen(true);
  }, [placement, offset]);
  const hide = React2.useCallback(() => {
    setIsOpen(false);
  }, []);
  const handleEnter = React2.useCallback(() => {
    window.clearTimeout(closeTimeout.current);
    if (delay === 0) {
      show();
    } else {
      openTimeout.current = window.setTimeout(show, delay);
    }
  }, [delay, show]);
  const handleLeave = React2.useCallback(() => {
    window.clearTimeout(openTimeout.current);
    if (closeDelay === 0) {
      hide();
    } else {
      closeTimeout.current = window.setTimeout(hide, closeDelay);
    }
  }, [closeDelay, hide]);
  React2.useEffect(() => {
    if (!isOpen) {
      return;
    }
    const updatePosition = () => {
      if (!triggerRef.current) {
        return;
      }
      setTriggerRect(triggerRef.current.getBoundingClientRect());
    };
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, placement, offset]);
  React2.useEffect(() => {
    return () => {
      window.clearTimeout(openTimeout.current);
      window.clearTimeout(closeTimeout.current);
    };
  }, []);
  const nodes = React2.Children.toArray(children);
  const triggerElement = nodes[0];
  const tooltipElements = nodes.slice(1);
  const originalRef = triggerElement.props.ref;
  const clonedTrigger = React2.cloneElement(triggerElement, {
    ref: mergeRefs(originalRef, (node) => {
      triggerRef.current = node;
    }),
    onMouseEnter: composeEventHandlers(triggerElement.props.onMouseEnter, handleEnter),
    onMouseLeave: composeEventHandlers(triggerElement.props.onMouseLeave, handleLeave),
    onFocus: composeEventHandlers(triggerElement.props.onFocus, show),
    onBlur: composeEventHandlers(triggerElement.props.onBlur, hide)
  });
  return /* @__PURE__ */ jsxs(TooltipContext.Provider, { value: { isOpen, triggerRect, placement, offset, triggerRef }, children: [
    clonedTrigger,
    tooltipElements
  ] });
};
var ARROW_SIZE = 6;
var arrowColors = {
  dark: "#171717",
  light: "hsl(var(--popover))"
};
var ARROW_BORDER_COLOR = "hsl(var(--border))";
var ARROW_SHADOW = "drop-shadow(0 1px 2px rgb(0 0 0 / 0.10))";
var buildArrowTriangle = (placement, color, size, inset = 0) => {
  const base = {
    position: "absolute",
    width: 0,
    height: 0
  };
  const offset = -(size - inset);
  switch (placement) {
    case "bottom":
      return {
        ...base,
        top: offset,
        left: "50%",
        transform: "translateX(-50%)",
        borderLeft: `${size}px solid transparent`,
        borderRight: `${size}px solid transparent`,
        borderBottom: `${size}px solid ${color}`
      };
    case "top":
      return {
        ...base,
        bottom: offset,
        left: "50%",
        transform: "translateX(-50%)",
        borderLeft: `${size}px solid transparent`,
        borderRight: `${size}px solid transparent`,
        borderTop: `${size}px solid ${color}`
      };
    case "left":
      return {
        ...base,
        right: offset,
        top: "50%",
        transform: "translateY(-50%)",
        borderTop: `${size}px solid transparent`,
        borderBottom: `${size}px solid transparent`,
        borderLeft: `${size}px solid ${color}`
      };
    case "right":
      return {
        ...base,
        left: offset,
        top: "50%",
        transform: "translateY(-50%)",
        borderTop: `${size}px solid transparent`,
        borderBottom: `${size}px solid transparent`,
        borderRight: `${size}px solid ${color}`
      };
  }
};
var TooltipArrow = ({
  placement,
  variant
}) => {
  if (variant === "light") {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx2(
        "div",
        {
          style: {
            ...buildArrowTriangle(placement, ARROW_BORDER_COLOR, ARROW_SIZE + 1),
            filter: ARROW_SHADOW
          }
        }
      ),
      /* @__PURE__ */ jsx2("div", { style: buildArrowTriangle(placement, arrowColors.light, ARROW_SIZE + 1, 1) })
    ] });
  }
  return /* @__PURE__ */ jsx2(
    "div",
    {
      style: {
        ...buildArrowTriangle(placement, arrowColors.dark, ARROW_SIZE),
        filter: ARROW_SHADOW
      }
    }
  );
};
var variantStyles = {
  dark: "rounded bg-neutral-900 text-xs text-white",
  light: "rounded bg-popover text-xs text-popover-foreground border border-border shadow-lg"
};
var Tooltip = ({ children, className, variant = "dark" }) => {
  const context = React2.useContext(TooltipContext);
  const isOpen = Boolean(context?.isOpen);
  const triggerRect = context?.triggerRect ?? null;
  const preferredPlacement = context?.placement ?? "top";
  const offset = context?.offset ?? 8;
  const tooltipRef = React2.useRef(null);
  const [computed, setComputed] = React2.useState({ top: 0, left: 0, placement: preferredPlacement, ready: false });
  const totalOffset = offset + ARROW_SIZE;
  React2.useLayoutEffect(() => {
    if (!isOpen || !triggerRect || !tooltipRef.current) {
      return;
    }
    const tooltipEl = tooltipRef.current;
    const width = tooltipEl.offsetWidth;
    const height = tooltipEl.offsetHeight;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 8;
    const candidates = [
      preferredPlacement,
      getOppositePlacement(preferredPlacement),
      "top",
      "bottom",
      "right",
      "left"
    ];
    const uniqueCandidates = Array.from(new Set(candidates));
    const computeForPlacement = (p) => {
      switch (p) {
        case "bottom":
          return {
            top: triggerRect.bottom + totalOffset,
            left: triggerRect.left + triggerRect.width / 2 - width / 2
          };
        case "left":
          return {
            top: triggerRect.top + triggerRect.height / 2 - height / 2,
            left: triggerRect.left - totalOffset - width
          };
        case "right":
          return {
            top: triggerRect.top + triggerRect.height / 2 - height / 2,
            left: triggerRect.right + totalOffset
          };
        case "top":
        default:
          return {
            top: triggerRect.top - totalOffset - height,
            left: triggerRect.left + triggerRect.width / 2 - width / 2
          };
      }
    };
    const fitsViewport = (pos2) => pos2.top >= margin && pos2.left >= margin && pos2.top + height <= viewportHeight - margin && pos2.left + width <= viewportWidth - margin;
    let chosenPlacement = preferredPlacement;
    let pos = computeForPlacement(preferredPlacement);
    for (const candidate of uniqueCandidates) {
      const candidatePos = computeForPlacement(candidate);
      if (fitsViewport(candidatePos)) {
        chosenPlacement = candidate;
        pos = candidatePos;
        break;
      }
    }
    const clampedTop = Math.min(Math.max(pos.top, margin), viewportHeight - margin - height);
    const clampedLeft = Math.min(Math.max(pos.left, margin), viewportWidth - margin - width);
    setComputed({
      top: clampedTop,
      left: clampedLeft,
      placement: chosenPlacement,
      ready: true
    });
  }, [isOpen, triggerRect, preferredPlacement, totalOffset, children]);
  if (!isOpen) {
    return null;
  }
  return createPortal(
    /* @__PURE__ */ jsxs(
      "div",
      {
        ref: tooltipRef,
        role: "tooltip",
        className: cn("pointer-events-none z-50 px-2.5 py-1", variantStyles[variant], className),
        style: {
          position: "fixed",
          top: computed.top,
          left: computed.left,
          visibility: computed.ready ? "visible" : "hidden"
        },
        children: [
          children,
          /* @__PURE__ */ jsx2(TooltipArrow, { placement: computed.placement, variant })
        ]
      }
    ),
    document.body
  );
};

// src/components/Button.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var SECONDARY_BUTTON = "bg-card text-foreground border border-[color:hsl(var(--line-2))] hover:border-[color:var(--ink)] hover:bg-[var(--paper)]";
var buttonVariants = {
  primary: "bg-primary text-primary-foreground border border-transparent",
  secondary: SECONDARY_BUTTON,
  ghost: "hover:bg-accent hover:text-accent-foreground",
  destructive: "bg-destructive text-destructive-foreground border border-transparent",
  outline: SECONDARY_BUTTON,
  link: "text-primary underline-offset-4 hover:underline",
  vibrant: "text-white bg-gradient-to-r from-amber-500 via-rose-500 to-purple-500 hover:brightness-110 focus-visible:ring-amber-200",
  icon: "btn-icon-hover text-foreground/70 hover:text-foreground"
};
var buttonSizes = {
  sm: "h-[34px] rounded-none px-3 text-[13px]",
  md: "h-[40px] rounded-none px-[16px] py-2 text-[14px]",
  lg: "h-[46px] rounded-none px-8 text-[15px]"
};
var iconOnlySizes = {
  sm: "h-[34px] w-[34px] text-[13px]",
  md: "h-[40px] w-[40px] text-[14px]",
  lg: "h-[46px] w-[46px] text-[15px]"
};
var iconVariantSizes = {
  sm: "h-8 rounded-md px-3 text-xs",
  md: "h-[40px] w-[40px] px-4 py-2 rounded-md text-sm",
  lg: "h-10 rounded-md px-8 text-base"
};
var iconVariantIconOnlySizes = {
  sm: "h-8 w-[34px]",
  // 28px tall x 34px wide — an ellipse under `rounded-full`
  md: "h-[40px] w-[40px]",
  lg: "h-10 w-[46px]"
  // 35px x 46px — same artifact
};
function resolveSizeClasses(variant, size, iconOnly) {
  if (iconOnly) return variant === "icon" ? iconVariantIconOnlySizes[size] : iconOnlySizes[size];
  return variant === "icon" ? iconVariantSizes[size] : buttonSizes[size];
}
var Button = React3.forwardRef(
  ({
    className,
    variant = "primary",
    size = "md",
    iconLeft,
    iconRight,
    iconOnly = false,
    hoverAnimation,
    tone,
    tooltip,
    "aria-label": explicitAriaLabel,
    children,
    ...props
  }, ref) => {
    const ariaLabel = explicitAriaLabel ?? (tooltip || void 0);
    const button = /* @__PURE__ */ jsxs2(
      AriaButton,
      {
        ref,
        "aria-label": ariaLabel,
        "data-size": size,
        "data-variant": variant,
        "data-icon-only": iconOnly || void 0,
        "data-tone": tone && tone !== "default" ? tone : void 0,
        className: (renderProps) => {
          const baseClasses = cn(
            "inline-flex items-center justify-center whitespace-nowrap font-medium transition-colors",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            "disabled:pointer-events-none disabled:opacity-50",
            // Cross-brand on purpose: a scale-down reads as rubbery on a
            // hard-edged button, and Button/SplitButton previously disagreed
            // (0.97 vs 0.98). A 1px nudge is one shared, quieter affordance.
            "data-[pressed]:translate-y-px",
            variant === "icon" && "group",
            buttonVariants[variant],
            resolveSizeClasses(variant, size, iconOnly),
            iconOnly && (variant === "icon" ? "rounded-full" : "rounded-none"),
            iconOnly && "gap-0",
            !iconOnly && (iconLeft || iconRight) && "gap-2",
            typeof className === "function" ? className(renderProps) : className
          );
          return baseClasses;
        },
        ...props,
        children: [
          iconLeft && !iconOnly && /* @__PURE__ */ jsx3("span", { className: "flex-shrink-0", children: iconLeft }),
          iconOnly ? (() => {
            const iconEl = iconLeft || iconRight || children;
            if (variant === "icon" && React3.isValidElement(iconEl)) {
              if (hoverAnimation) {
                return /* @__PURE__ */ jsx3("span", { className: "relative", "data-hover-anim": hoverAnimation, children: iconEl });
              }
              return /* @__PURE__ */ jsxs2("span", { className: "relative", children: [
                /* @__PURE__ */ jsx3("span", { className: "group-hover:opacity-0 transition-opacity", children: iconEl }),
                /* @__PURE__ */ jsx3("span", { className: "absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity", children: React3.cloneElement(iconEl, {
                  weight: "fill"
                }) })
              ] });
            }
            return iconEl;
          })() : children,
          iconRight && !iconOnly && /* @__PURE__ */ jsx3("span", { className: "flex-shrink-0", children: iconRight })
        ]
      }
    );
    if (tooltip) {
      return /* @__PURE__ */ jsxs2(TooltipTrigger, { delay: 0, children: [
        button,
        /* @__PURE__ */ jsx3(Tooltip, { children: tooltip })
      ] });
    }
    return button;
  }
);
Button.displayName = "Button";

// src/components/MorphButton.tsx
import React4 from "react";
import { AnimatePresence, motion } from "framer-motion";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var MorphButton = React4.forwardRef(
  ({ state, states, fallbackState, className, children, variant, ...props }, ref) => {
    const activeState = states[state] ?? (fallbackState ? states[fallbackState] : void 0);
    const resolvedVariant = activeState?.variant ?? variant ?? "primary";
    const iconAnimation = activeState?.iconAnimation;
    return /* @__PURE__ */ jsx4(
      Button,
      {
        ...props,
        ref,
        variant: resolvedVariant,
        className: cn(activeState?.className, className),
        children: /* @__PURE__ */ jsxs3("span", { className: "inline-flex items-center gap-2", children: [
          /* @__PURE__ */ jsx4(AnimatePresence, { mode: "wait", initial: false, children: activeState?.icon && /* @__PURE__ */ jsx4(
            motion.span,
            {
              initial: { opacity: 0, rotate: -20, scale: 0.9 },
              animate: { opacity: 1, rotate: 0, scale: 1 },
              exit: { opacity: 0, rotate: 20, scale: 0.9 },
              transition: { duration: 0.2, ease: "easeOut" },
              className: "flex items-center",
              children: /* @__PURE__ */ jsx4(
                motion.span,
                {
                  animate: iconAnimation === "spin" ? { rotate: 360 } : iconAnimation === "pulse" ? { scale: [1, 1.12, 1] } : void 0,
                  transition: iconAnimation === "spin" ? { repeat: Infinity, duration: 1, ease: "linear" } : iconAnimation === "pulse" ? { repeat: Infinity, duration: 1.4, ease: "easeInOut" } : void 0,
                  children: activeState.icon
                }
              )
            },
            `${state}-icon`
          ) }),
          /* @__PURE__ */ jsx4(AnimatePresence, { mode: "wait", initial: false, children: /* @__PURE__ */ jsx4(
            motion.span,
            {
              initial: { opacity: 0, y: 8 },
              animate: { opacity: 1, y: 0 },
              exit: { opacity: 0, y: -8 },
              transition: { duration: 0.2, ease: "easeOut" },
              className: "inline-flex items-center",
              children: activeState?.label ?? children
            },
            `${state}-label`
          ) })
        ] })
      }
    );
  }
);
MorphButton.displayName = "MorphButton";

// src/components/WaterfallChart/WaterfallChart.tsx
import React5, { useState as useState2, useMemo, useCallback as useCallback2 } from "react";
import { MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowsOutSimple } from "@phosphor-icons/react";

// src/components/WaterfallChart/utils.ts
var DEFAULT_LABEL_COL_PX = 280;
var COMPACT_LABEL_COL_PX = 144;
function msToPercent(ms, view) {
  const range = view.endMs - view.startMs;
  if (range <= 0) return 0;
  return (ms - view.startMs) / range * 100;
}
function barWidthPercent(durationMs, view) {
  const range = view.endMs - view.startMs;
  if (range <= 0) return 0;
  const pct = durationMs / range * 100;
  return Math.max(0.5, pct);
}
function computeTicks(view, maxTicks = 10) {
  const range = view.endMs - view.startMs;
  if (range <= 0) return [{ ms: view.startMs, label: formatTickLabel(view.startMs) }];
  const intervals = [
    100,
    250,
    500,
    1e3,
    2e3,
    5e3,
    1e4,
    15e3,
    3e4,
    6e4,
    12e4,
    3e5,
    6e5
  ];
  const interval = intervals.find((candidate) => range / candidate <= maxTicks) ?? intervals[intervals.length - 1];
  const firstTick = Math.floor(view.startMs / interval) * interval;
  const ticks = [];
  for (let ms = firstTick; ms <= view.endMs; ms += interval) {
    if (ms >= view.startMs) {
      ticks.push({ ms, label: formatTickLabel(ms) });
    }
  }
  return ticks;
}
function formatTickLabel(ms) {
  if (ms === 0) return "0s";
  const totalSeconds = ms / 1e3;
  if (totalSeconds < 60) {
    return totalSeconds % 1 === 0 ? `${totalSeconds}s` : `${totalSeconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.round(totalSeconds % 60);
  if (seconds === 0) return `${minutes}m`;
  return `${minutes}m ${seconds}s`;
}
function formatDurationLabel(ms) {
  if (ms < 1e3) return `${Math.round(ms)}ms`;
  if (ms < 6e4) return `${(ms / 1e3).toFixed(1)}s`;
  const minutes = Math.floor(ms / 6e4);
  const seconds = Math.round(ms % 6e4 / 1e3);
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

// src/components/WaterfallChart/WaterfallTimeAxis.tsx
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
function WaterfallTimeAxis({ view, maxTicks, className }) {
  const ticks = computeTicks(view, maxTicks);
  return /* @__PURE__ */ jsx5("div", { className: cn("relative h-6 border-b border-border/50", className), children: ticks.map((tick) => {
    const left = msToPercent(tick.ms, view);
    if (left < -1 || left > 101) return null;
    return /* @__PURE__ */ jsxs4(
      "div",
      {
        className: "absolute top-0 flex flex-col items-center",
        style: { left: `${left}%` },
        children: [
          /* @__PURE__ */ jsx5("span", { className: "text-[10px] text-muted-foreground whitespace-nowrap -translate-x-1/2", children: tick.label }),
          /* @__PURE__ */ jsx5("div", { className: "w-px h-1.5 bg-border/60 mt-0.5" })
        ]
      },
      tick.ms
    );
  }) });
}
WaterfallTimeAxis.displayName = "WaterfallTimeAxis";

// src/components/WaterfallChart/WaterfallRow.tsx
import { CaretDown, CaretRight } from "@phosphor-icons/react";

// src/components/WaterfallChart/WaterfallBar.tsx
import { jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
var toneClasses = {
  success: "bg-status-success/80",
  danger: "bg-status-failed/80",
  warning: "bg-status-running/80",
  muted: "bg-status-queued/60 border border-dashed border-border/60",
  cached: "bg-emerald-500/70",
  agent: "bg-primary/70",
  recovery: "bg-amber-500/70",
  playbook: "bg-teal-500/70"
};
var heightClasses = {
  sm: "h-3",
  md: "h-5"
};
function WaterfallBar({
  startMs,
  durationMs,
  view,
  tone,
  isRunning = false,
  tooltipContent,
  height = "md",
  className
}) {
  const left = msToPercent(startMs, view);
  const width = barWidthPercent(durationMs, view);
  const bar = /* @__PURE__ */ jsx6(
    "div",
    {
      className: cn(
        "absolute top-1/2 -translate-y-1/2 rounded-sm transition-opacity",
        heightClasses[height],
        toneClasses[tone],
        isRunning && "animate-pulse",
        className
      ),
      style: { left: `${left}%`, width: `${width}%` },
      title: tooltipContent ? void 0 : formatDurationLabel(durationMs),
      role: "presentation"
    }
  );
  if (tooltipContent) {
    return /* @__PURE__ */ jsxs5(TooltipTrigger, { delay: 200, placement: "top", children: [
      bar,
      /* @__PURE__ */ jsx6(Tooltip, { variant: "light", className: "max-w-md px-3 py-2 text-left", children: tooltipContent })
    ] });
  }
  return bar;
}
WaterfallBar.displayName = "WaterfallBar";

// src/components/WaterfallChart/WaterfallSubRows.tsx
import { jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
var typePrefix = {
  "tool-call": "Tool",
  thought: "Think",
  wait: "Wait",
  action: "Act",
  "llm-turn": "Turn",
  "retry-error": "Retry",
  container: "Build"
};
function SubEntryTooltipContent({ sub }) {
  return /* @__PURE__ */ jsxs6("div", { className: "space-y-1", children: [
    /* @__PURE__ */ jsx7("div", { className: "text-xs font-medium", children: sub.label }),
    /* @__PURE__ */ jsx7("div", { className: "text-[11px] text-muted-foreground whitespace-pre-line", children: sub.tooltipDetail }),
    /* @__PURE__ */ jsxs6("div", { className: "text-[10px] text-muted-foreground", children: [
      "Duration: ",
      formatDurationLabel(sub.durationMs)
    ] })
  ] });
}
function WaterfallSubRows({
  subEntries,
  parentStartMs,
  view,
  labelColPx,
  className
}) {
  if (subEntries.length === 0) return null;
  return /* @__PURE__ */ jsx7("div", { className: cn("flex flex-col", className), children: subEntries.map((sub) => {
    const hasTooltip = !!sub.tooltipDetail;
    const inlineLabel = /* @__PURE__ */ jsxs6("span", { className: "text-[11px] text-muted-foreground truncate", children: [
      /* @__PURE__ */ jsx7("span", { className: "font-medium", children: typePrefix[sub.type] }),
      " \xB7 ",
      sub.label
    ] });
    return /* @__PURE__ */ jsxs6("div", { className: "flex items-center h-6 group/subrow", children: [
      /* @__PURE__ */ jsx7(
        "div",
        {
          className: "shrink-0 pl-8 pr-2 min-w-0 flex items-center",
          style: { width: labelColPx },
          children: hasTooltip ? /* @__PURE__ */ jsxs6(TooltipTrigger, { delay: 200, placement: "right", children: [
            /* @__PURE__ */ jsx7("span", { className: "truncate cursor-default", children: inlineLabel }),
            /* @__PURE__ */ jsx7(Tooltip, { variant: "light", className: "max-w-sm px-3 py-2 text-left", children: /* @__PURE__ */ jsx7(SubEntryTooltipContent, { sub }) })
          ] }) : /* @__PURE__ */ jsx7("span", { className: "truncate", title: sub.label, children: inlineLabel })
        }
      ),
      /* @__PURE__ */ jsx7("div", { className: "relative flex-1 h-full overflow-hidden", children: /* @__PURE__ */ jsx7(
        WaterfallBar,
        {
          startMs: parentStartMs + sub.startMs,
          durationMs: sub.durationMs,
          view,
          tone: sub.tone,
          height: "sm",
          tooltipContent: hasTooltip ? /* @__PURE__ */ jsx7(SubEntryTooltipContent, { sub }) : void 0
        }
      ) })
    ] }, sub.id);
  }) });
}
WaterfallSubRows.displayName = "WaterfallSubRows";

// src/components/WaterfallChart/WaterfallRow.tsx
import { Fragment as Fragment2, jsx as jsx8, jsxs as jsxs7 } from "react/jsx-runtime";
var statusDot = {
  success: "bg-status-success",
  failed: "bg-status-failed",
  inconclusive: "bg-muted-foreground/60",
  running: "bg-status-running animate-pulse",
  pending: "bg-status-queued",
  skipped: "bg-muted-foreground/50",
  waiting: "bg-status-running/60"
};
var badgeToneClasses = {
  cached: "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400",
  agent: "bg-primary/20 text-primary",
  recovery: "bg-amber-500/20 text-amber-600 dark:text-amber-400",
  success: "bg-status-success/20 text-status-success",
  danger: "bg-status-failed/20 text-status-failed",
  warning: "bg-status-running/20 text-status-running",
  muted: "bg-muted text-muted-foreground"
};
function WaterfallRow({
  entry,
  view,
  labelColPx,
  isExpanded,
  isHighlighted,
  onToggleExpand,
  onEntryClick,
  labelIndentPx = 0
}) {
  const hasSubEntries = (entry.subEntries?.length ?? 0) > 0;
  const isRunning = entry.status === "running";
  const hasTiming = entry.durationMs > 0;
  const endPercent = msToPercent(entry.startMs + entry.durationMs, view);
  const labelOverflows = endPercent > 80;
  return /* @__PURE__ */ jsxs7(
    "div",
    {
      className: cn("border-b border-border/30 transition-colors", isHighlighted && "bg-accent/40"),
      children: [
        /* @__PURE__ */ jsxs7("div", { className: "flex items-center h-8 group/row", children: [
          /* @__PURE__ */ jsxs7(
            "div",
            {
              className: "shrink-0 flex items-center gap-1.5 px-2 min-w-0",
              style: { width: labelColPx, paddingLeft: labelIndentPx ? labelIndentPx + 8 : void 0 },
              children: [
                /* @__PURE__ */ jsx8(
                  "button",
                  {
                    type: "button",
                    className: cn(
                      "shrink-0 w-4 h-4 flex items-center justify-center rounded text-muted-foreground hover:text-foreground transition-colors",
                      !hasSubEntries && "invisible"
                    ),
                    onClick: () => onToggleExpand?.(entry.id),
                    "aria-label": isExpanded ? "Collapse" : "Expand",
                    "aria-expanded": isExpanded,
                    children: isExpanded ? /* @__PURE__ */ jsx8(CaretDown, { size: 12 }) : /* @__PURE__ */ jsx8(CaretRight, { size: 12 })
                  }
                ),
                /* @__PURE__ */ jsx8("span", { className: cn("shrink-0 w-2 h-2 rounded-full", statusDot[entry.status]) }),
                /* @__PURE__ */ jsxs7(TooltipTrigger, { delay: 400, placement: "right", children: [
                  /* @__PURE__ */ jsx8(
                    "button",
                    {
                      type: "button",
                      className: "truncate text-xs font-medium text-foreground hover:underline cursor-pointer text-left",
                      onClick: () => onEntryClick?.(entry),
                      children: entry.label
                    }
                  ),
                  /* @__PURE__ */ jsxs7(Tooltip, { variant: "light", className: "max-w-sm px-3 py-2 text-left", children: [
                    /* @__PURE__ */ jsx8("div", { className: "text-xs font-medium", children: entry.label }),
                    entry.sublabel && /* @__PURE__ */ jsxs7("div", { className: "text-[11px] text-muted-foreground mt-0.5", children: [
                      "Type: ",
                      entry.sublabel
                    ] }),
                    /* @__PURE__ */ jsxs7("div", { className: "text-[11px] text-muted-foreground", children: [
                      "Duration: ",
                      formatDurationLabel(entry.durationMs)
                    ] }),
                    hasSubEntries && /* @__PURE__ */ jsxs7("div", { className: "text-[10px] text-muted-foreground mt-0.5", children: [
                      "Click to zoom \xB7 ",
                      entry.subEntries?.length,
                      " sub-entries"
                    ] })
                  ] })
                ] }),
                entry.badges?.map((badge) => /* @__PURE__ */ jsx8(
                  "span",
                  {
                    className: cn(
                      "shrink-0 text-[9px] font-medium px-1 py-px rounded",
                      badgeToneClasses[badge.tone] ?? badgeToneClasses.muted
                    ),
                    children: badge.label
                  },
                  badge.label
                ))
              ]
            }
          ),
          /* @__PURE__ */ jsx8("div", { className: "relative flex-1 h-full overflow-hidden", children: hasTiming ? /* @__PURE__ */ jsxs7(Fragment2, { children: [
            /* @__PURE__ */ jsx8(
              WaterfallBar,
              {
                startMs: entry.startMs,
                durationMs: entry.durationMs,
                view,
                tone: entry.tone,
                isRunning,
                tooltipContent: /* @__PURE__ */ jsxs7("div", { className: "space-y-1", children: [
                  /* @__PURE__ */ jsx8("div", { className: "text-xs font-medium", children: entry.label }),
                  /* @__PURE__ */ jsxs7("div", { className: "text-[11px] text-muted-foreground", children: [
                    "Duration: ",
                    formatDurationLabel(entry.durationMs)
                  ] }),
                  entry.sublabel && /* @__PURE__ */ jsxs7("div", { className: "text-[11px] text-muted-foreground", children: [
                    "Type: ",
                    entry.sublabel
                  ] }),
                  entry.status === "failed" && entry.errorMessage && /* @__PURE__ */ jsx8("div", { className: "text-[11px] text-red-600 dark:text-red-400 mt-1 border-t border-border/50 pt-1 whitespace-pre-line", children: entry.errorMessage })
                ] })
              }
            ),
            /* @__PURE__ */ jsx8(
              "span",
              {
                className: cn(
                  "absolute top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground whitespace-nowrap pointer-events-none",
                  labelOverflows ? "-translate-x-full pr-1.5" : "ml-1"
                ),
                style: { left: `${endPercent}%` },
                children: formatDurationLabel(entry.durationMs)
              }
            )
          ] }) : null })
        ] }),
        isExpanded && entry.subEntries && /* @__PURE__ */ jsx8(
          WaterfallSubRows,
          {
            subEntries: entry.subEntries,
            parentStartMs: entry.startMs,
            view,
            labelColPx
          }
        )
      ]
    }
  );
}
WaterfallRow.displayName = "WaterfallRow";

// src/components/WaterfallChart/WaterfallGroupHeader.tsx
import { CaretDown as CaretDown2, CaretRight as CaretRight2 } from "@phosphor-icons/react";
import { Fragment as Fragment3, jsx as jsx9, jsxs as jsxs8 } from "react/jsx-runtime";
function WaterfallGroupHeader({
  group,
  startMs,
  durationMs,
  memberCount,
  view,
  labelColPx,
  isExpanded,
  onToggleExpand
}) {
  const hasTiming = durationMs > 0;
  const endPercent = msToPercent(startMs + durationMs, view);
  const labelOverflows = endPercent > 80;
  return /* @__PURE__ */ jsx9("div", { className: "border-b border-border/50 bg-muted/20", children: /* @__PURE__ */ jsxs8("div", { className: "flex items-center h-8", children: [
    /* @__PURE__ */ jsxs8("div", { className: "shrink-0 flex items-center gap-1.5 px-2 min-w-0", style: { width: labelColPx }, children: [
      /* @__PURE__ */ jsx9(
        "button",
        {
          type: "button",
          className: "shrink-0 w-4 h-4 flex items-center justify-center rounded text-muted-foreground hover:text-foreground transition-colors",
          onClick: () => onToggleExpand?.(group.id),
          "aria-label": isExpanded ? "Collapse group" : "Expand group",
          "aria-expanded": isExpanded,
          children: isExpanded ? /* @__PURE__ */ jsx9(CaretDown2, { size: 12 }) : /* @__PURE__ */ jsx9(CaretRight2, { size: 12 })
        }
      ),
      /* @__PURE__ */ jsxs8(TooltipTrigger, { delay: 400, placement: "right", children: [
        /* @__PURE__ */ jsx9(
          "button",
          {
            type: "button",
            className: "truncate text-xs font-semibold text-foreground hover:underline cursor-pointer text-left uppercase tracking-wide",
            onClick: () => onToggleExpand?.(group.id),
            children: group.label
          }
        ),
        /* @__PURE__ */ jsxs8(Tooltip, { variant: "light", className: "max-w-sm px-3 py-2 text-left", children: [
          /* @__PURE__ */ jsx9("div", { className: "text-xs font-medium", children: group.label }),
          group.description && /* @__PURE__ */ jsx9("div", { className: "text-[11px] text-muted-foreground mt-0.5", children: group.description }),
          /* @__PURE__ */ jsxs8("div", { className: "text-[11px] text-muted-foreground", children: [
            "Duration: ",
            formatDurationLabel(durationMs),
            " \xB7 ",
            memberCount,
            " step",
            memberCount === 1 ? "" : "s"
          ] })
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx9("div", { className: "relative flex-1 h-full overflow-hidden", children: hasTiming ? /* @__PURE__ */ jsxs8(Fragment3, { children: [
      /* @__PURE__ */ jsx9(
        WaterfallBar,
        {
          startMs,
          durationMs,
          view,
          tone: group.tone ?? "muted",
          tooltipContent: /* @__PURE__ */ jsxs8("div", { className: "space-y-1", children: [
            /* @__PURE__ */ jsx9("div", { className: "text-xs font-medium", children: group.label }),
            /* @__PURE__ */ jsxs8("div", { className: "text-[11px] text-muted-foreground", children: [
              "Duration: ",
              formatDurationLabel(durationMs)
            ] })
          ] })
        }
      ),
      /* @__PURE__ */ jsx9(
        "span",
        {
          className: cn(
            "absolute top-1/2 -translate-y-1/2 text-[10px] font-medium text-muted-foreground whitespace-nowrap pointer-events-none",
            labelOverflows ? "-translate-x-full pr-1.5" : "ml-1"
          ),
          style: { left: `${endPercent}%` },
          children: formatDurationLabel(durationMs)
        }
      )
    ] }) : null })
  ] }) });
}
WaterfallGroupHeader.displayName = "WaterfallGroupHeader";

// src/components/WaterfallChart/WaterfallChart.tsx
import { jsx as jsx10, jsxs as jsxs9 } from "react/jsx-runtime";
var GROUP_MEMBER_INDENT_PX = 16;
var ZOOM_STEP = 0.4;
var ZOOM_PADDING = 0.15;
var zoomBtnClass = "p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/80 transition-colors";
var zoomBtnDisabled = "opacity-30 pointer-events-none";
function WaterfallChart({
  entries,
  totalDurationMs,
  groups,
  expandedIds: controlledExpandedIds,
  onToggleExpand: controlledToggleExpand,
  highlightedId,
  onEntryClick,
  compact = false,
  className
}) {
  const [internalExpanded, setInternalExpanded] = useState2(/* @__PURE__ */ new Set());
  const labelColPx = compact ? COMPACT_LABEL_COL_PX : DEFAULT_LABEL_COL_PX;
  const maxTicks = compact ? 5 : 10;
  const expandedIds = controlledExpandedIds ?? internalExpanded;
  const onToggleExpand = controlledToggleExpand ?? ((id) => {
    setInternalExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  });
  const effectiveTotal = useMemo(() => {
    if (totalDurationMs > 0) return totalDurationMs;
    let max = 0;
    for (const entry of entries) {
      const end = entry.startMs + entry.durationMs;
      if (end > max) max = end;
    }
    return max || 1e3;
  }, [totalDurationMs, entries]);
  const { groupLayout, ungroupedEntries } = useMemo(() => {
    if (!groups || groups.length === 0) {
      return { groupLayout: [], ungroupedEntries: entries };
    }
    const known = new Set(groups.map((g) => g.id));
    const byGroup = /* @__PURE__ */ new Map();
    const ungrouped = [];
    for (const entry of entries) {
      if (entry.groupId && known.has(entry.groupId)) {
        const bucket = byGroup.get(entry.groupId);
        if (bucket) bucket.push(entry);
        else byGroup.set(entry.groupId, [entry]);
      } else {
        ungrouped.push(entry);
      }
    }
    const layout = groups.map((group) => {
      const members = byGroup.get(group.id) ?? [];
      if (members.length === 0) return null;
      const startMs = Math.min(...members.map((m) => m.startMs));
      const endMs = Math.max(...members.map((m) => m.startMs + m.durationMs));
      return { group, members, startMs, durationMs: Math.max(0, endMs - startMs) };
    }).filter((g) => g !== null);
    return { groupLayout: layout, ungroupedEntries: ungrouped };
  }, [groups, entries]);
  const [viewWindow, setViewWindow] = useState2(null);
  const fullView = useMemo(
    () => ({ startMs: 0, endMs: effectiveTotal }),
    [effectiveTotal]
  );
  const currentView = viewWindow ?? fullView;
  const isZoomed = viewWindow !== null;
  const zoomIn = useCallback2(() => {
    const range = currentView.endMs - currentView.startMs;
    const shrink = range * ZOOM_STEP * 0.5;
    setViewWindow({
      startMs: Math.max(0, currentView.startMs + shrink),
      endMs: Math.min(effectiveTotal, currentView.endMs - shrink)
    });
  }, [currentView, effectiveTotal]);
  const zoomOut = useCallback2(() => {
    if (!isZoomed) return;
    const range = currentView.endMs - currentView.startMs;
    const expand = range * ZOOM_STEP * 0.5;
    const newStart = Math.max(0, currentView.startMs - expand);
    const newEnd = Math.min(effectiveTotal, currentView.endMs + expand);
    const snapThreshold = effectiveTotal * 0.02;
    if (newStart <= snapThreshold && newEnd >= effectiveTotal - snapThreshold) {
      setViewWindow(null);
    } else {
      setViewWindow({ startMs: newStart, endMs: newEnd });
    }
  }, [currentView, effectiveTotal, isZoomed]);
  const resetZoom = useCallback2(() => setViewWindow(null), []);
  const zoomToEntry = useCallback2(
    (entry) => {
      if (entry.durationMs <= 0) return;
      const padding = entry.durationMs * ZOOM_PADDING;
      setViewWindow({
        startMs: Math.max(0, entry.startMs - padding),
        endMs: Math.min(effectiveTotal, entry.startMs + entry.durationMs + padding)
      });
      if (!expandedIds.has(entry.id)) {
        onToggleExpand(entry.id);
      }
    },
    [effectiveTotal, expandedIds, onToggleExpand]
  );
  const handleEntryClick = useCallback2(
    (entry) => {
      zoomToEntry(entry);
      onEntryClick?.(entry);
    },
    [zoomToEntry, onEntryClick]
  );
  if (entries.length === 0) {
    return /* @__PURE__ */ jsx10(
      "div",
      {
        className: cn(
          "flex items-center justify-center py-8 text-sm text-muted-foreground",
          className
        ),
        children: "No timing data available"
      }
    );
  }
  return /* @__PURE__ */ jsxs9(
    "div",
    {
      className: cn(
        "relative border border-border rounded-md overflow-hidden bg-background group/chart",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs9("div", { className: "absolute top-2 right-3 z-10 flex items-center gap-1 rounded-lg border border-border/60 bg-background/90 backdrop-blur-sm px-1.5 py-1 shadow-sm opacity-0 group-hover/chart:opacity-100 transition-opacity", children: [
          /* @__PURE__ */ jsx10("button", { type: "button", className: zoomBtnClass, onClick: zoomIn, title: "Zoom in", children: /* @__PURE__ */ jsx10(MagnifyingGlassPlus, { size: 18 }) }),
          /* @__PURE__ */ jsx10(
            "button",
            {
              type: "button",
              className: cn(zoomBtnClass, !isZoomed && zoomBtnDisabled),
              onClick: zoomOut,
              title: "Zoom out",
              children: /* @__PURE__ */ jsx10(MagnifyingGlassMinus, { size: 18 })
            }
          ),
          /* @__PURE__ */ jsx10(
            "button",
            {
              type: "button",
              className: cn(zoomBtnClass, !isZoomed && zoomBtnDisabled),
              onClick: resetZoom,
              title: "Reset zoom",
              children: /* @__PURE__ */ jsx10(ArrowsOutSimple, { size: 18 })
            }
          )
        ] }),
        /* @__PURE__ */ jsxs9("div", { className: "flex items-center", children: [
          /* @__PURE__ */ jsx10("div", { className: "shrink-0", style: { width: labelColPx } }),
          /* @__PURE__ */ jsx10("div", { className: "flex-1 min-w-0", children: /* @__PURE__ */ jsx10(WaterfallTimeAxis, { view: currentView, maxTicks }) })
        ] }),
        /* @__PURE__ */ jsxs9("div", { className: "overflow-y-auto max-h-[600px]", children: [
          groupLayout.map(({ group, members, startMs, durationMs }) => {
            const collapsed = expandedIds.has(`collapsed:${group.id}`);
            return /* @__PURE__ */ jsxs9(React5.Fragment, { children: [
              /* @__PURE__ */ jsx10(
                WaterfallGroupHeader,
                {
                  group,
                  startMs,
                  durationMs,
                  memberCount: members.length,
                  view: currentView,
                  labelColPx,
                  isExpanded: !collapsed,
                  onToggleExpand: () => onToggleExpand(`collapsed:${group.id}`)
                }
              ),
              !collapsed && members.map((entry) => /* @__PURE__ */ jsx10(
                WaterfallRow,
                {
                  entry,
                  view: currentView,
                  labelColPx,
                  labelIndentPx: GROUP_MEMBER_INDENT_PX,
                  isExpanded: expandedIds.has(entry.id),
                  isHighlighted: entry.id === highlightedId,
                  onToggleExpand,
                  onEntryClick: handleEntryClick
                },
                entry.id
              ))
            ] }, group.id);
          }),
          ungroupedEntries.map((entry) => /* @__PURE__ */ jsx10(
            WaterfallRow,
            {
              entry,
              view: currentView,
              labelColPx,
              isExpanded: expandedIds.has(entry.id),
              isHighlighted: entry.id === highlightedId,
              onToggleExpand,
              onEntryClick: handleEntryClick
            },
            entry.id
          ))
        ] })
      ]
    }
  );
}
WaterfallChart.displayName = "WaterfallChart";

// src/components/charts/AreaChart.tsx
import {
  Area,
  AreaChart as RechartsAreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as Tooltip2,
  XAxis,
  YAxis
} from "recharts";

// src/theme/statusColors.ts
function hslToHex(h, s, l) {
  l /= 100;
  const a = s * Math.min(l, 1 - l) / 100;
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}
var statusHexColors = {
  // Cofactor palette — pass=green #14b88a / fail=burnt orange #c2410c. Hue
  // separation is weak for red/green-blind viewers, so every status mark also
  // carries a shape (glyph, hatch). See docs/ui/decisions.md 2026-10-01.
  cofactor: {
    success: hslToHex(163.2, 80.4, 40),
    // #14b88a
    failed: hslToHex(17.5, 88.3, 40.4),
    // #c2410c
    running: hslToHex(270, 70, 60),
    // violet — distinct from success
    queued: hslToHex(215, 20, 70),
    waiting: hslToHex(190, 30, 50),
    // slate-teal — never collides with success
    canceled: hslToHex(215, 20, 50),
    timeout: hslToHex(215, 20, 50),
    flaky: hslToHex(38, 92, 50),
    warning: hslToHex(38, 92, 50),
    skipped: hslToHex(215, 20, 60),
    degraded: hslToHex(350, 60, 55)
  },
  // Cofactor dark — same green/burnt-orange axis, lifted for dark surfaces
  cofactorDark: {
    success: hslToHex(162.2, 64.8, 47.8),
    // #2bc99a
    failed: hslToHex(19.8, 74.6, 52.2),
    // #e0662a
    running: hslToHex(270, 65, 65),
    // violet — distinct from success
    queued: hslToHex(215, 18, 45),
    waiting: hslToHex(190, 30, 58),
    // slate-teal
    canceled: hslToHex(215, 14, 48),
    timeout: hslToHex(215, 14, 48),
    flaky: hslToHex(38, 85, 60),
    warning: hslToHex(38, 85, 60),
    skipped: hslToHex(215, 16, 50),
    degraded: hslToHex(350, 65, 62)
  }
};
var failHatchBackground = "repeating-linear-gradient(135deg, rgba(255,255,255,0.28) 0 1px, transparent 1px 4px)";
function failHatchStyle(isFailed) {
  return isFailed ? { backgroundImage: failHatchBackground } : void 0;
}
function getStatusHex(status, theme = "cofactor") {
  return statusHexColors[theme][status];
}
var statusTailwindClasses = {
  success: {
    bg: "bg-status-success",
    bgSubtle: "bg-status-success/15",
    border: "border-status-success",
    text: "text-status-success"
  },
  failed: {
    bg: "bg-status-failed",
    bgSubtle: "bg-status-failed/15",
    border: "border-status-failed",
    text: "text-status-failed"
  },
  running: {
    bg: "bg-status-running",
    bgSubtle: "bg-status-running/15",
    border: "border-status-running",
    text: "text-status-running"
  },
  queued: {
    bg: "bg-status-queued",
    bgSubtle: "bg-status-queued/15",
    border: "border-status-queued",
    text: "text-status-queued"
  },
  waiting: {
    bg: "bg-status-waiting",
    bgSubtle: "bg-status-waiting/15",
    border: "border-status-waiting",
    text: "text-status-waiting"
  },
  canceled: {
    bg: "bg-status-canceled",
    bgSubtle: "bg-status-canceled/15",
    border: "border-status-canceled",
    text: "text-status-canceled"
  },
  timeout: {
    bg: "bg-status-timeout",
    bgSubtle: "bg-status-timeout/15",
    border: "border-status-timeout",
    text: "text-status-timeout"
  },
  flaky: {
    bg: "bg-status-flaky",
    bgSubtle: "bg-status-flaky/15",
    border: "border-status-flaky",
    text: "text-status-flaky"
  },
  warning: {
    bg: "bg-status-warning",
    bgSubtle: "bg-status-warning/15",
    border: "border-status-warning",
    text: "text-status-warning"
  },
  skipped: {
    bg: "bg-status-skipped",
    bgSubtle: "bg-status-skipped/15",
    border: "border-status-skipped",
    text: "text-status-skipped"
  },
  degraded: {
    bg: "bg-status-degraded",
    bgSubtle: "bg-status-degraded/15",
    border: "border-status-degraded",
    text: "text-status-degraded"
  }
};
var statusLabels = {
  success: "Success",
  failed: "Failed",
  running: "Running",
  queued: "Queued",
  waiting: "Waiting",
  canceled: "Canceled",
  timeout: "Timed Out",
  flaky: "Flaky",
  warning: "Warning",
  skipped: "Skipped",
  degraded: "Degraded"
};
function getStatusLabel(status) {
  return statusLabels[status] ?? status;
}
var badgeVariantMap = {
  success: "success",
  failed: "destructive",
  running: "warning",
  queued: "secondary",
  waiting: "outline",
  canceled: "outline",
  timeout: "outline",
  flaky: "warning",
  warning: "warning",
  skipped: "secondary",
  degraded: "destructive"
};
function getStatusBadgeVariant(status) {
  return badgeVariantMap[status] ?? "outline";
}
function normalizeTestStatus(status) {
  if (status === "flaky_success") return "success";
  if (status === "inconclusive") return "skipped";
  return status;
}
function normalizeRunStatus(status) {
  switch (status) {
    case "completed":
      return "success";
    case "pending":
      return "queued";
    case "completed_with_errors":
      return "failed";
    case "completed_with_inconclusive":
      return "skipped";
    case "in_progress":
      return "running";
    case "flaky_success":
      return "success";
    // Pass through values that are already valid StatusNames
    case "success":
    case "failed":
    case "running":
    case "queued":
    case "waiting":
    case "canceled":
    case "timeout":
    case "flaky":
    case "warning":
    case "skipped":
    case "degraded":
      return status;
    default:
      return "queued";
  }
}

// src/theme/chartTheme.ts
var lightStatus = statusHexColors.cofactor;
var darkStatus = statusHexColors.cofactorDark;
function tripletToHex(triplet) {
  const [h, s, l] = triplet.split(" ").map(parseFloat);
  return hslToHex(h, s, l);
}
var lightPrimary = tripletToHex(themes.cofactor.colors.primary);
var darkPrimary = tripletToHex(themes.cofactorDark.colors.primary);
var chartThemes = {
  cofactor: {
    primary: lightPrimary,
    secondary: hslToHex(214, 32, 91),
    // #e2e8f0 bg-2
    accent: lightStatus.success,
    muted: hslToHex(214, 32, 91),
    // Semantic status — green pass / burnt-orange fail (shape carries the signal too)
    success: lightStatus.success,
    warning: lightStatus.warning,
    destructive: lightStatus.failed,
    neutral: hslToHex(215, 14, 58),
    // neutral gray - "no data / not applicable" series
    info: lightPrimary,
    colors: [
      lightPrimary,
      lightStatus.failed,
      lightStatus.success,
      lightStatus.warning,
      hslToHex(270, 60, 55),
      // purple
      hslToHex(180, 60, 45),
      // teal
      hslToHex(330, 70, 55),
      // pink
      hslToHex(60, 70, 45)
      // yellow-green
    ],
    background: hslToHex(0, 0, 100),
    // #ffffff ground
    text: hslToHex(220, 49, 8),
    // #0b1220
    grid: hslToHex(215, 25, 87),
    gauge: {
      poor: lightStatus.failed,
      needsWork: lightStatus.warning,
      good: hslToHex(45, 80, 50),
      excellent: lightStatus.success
    }
  },
  cofactorDark: {
    primary: darkPrimary,
    secondary: hslToHex(215, 38.7, 12.2),
    // paper (dark)
    accent: darkStatus.success,
    muted: hslToHex(215, 38.7, 12.2),
    success: darkStatus.success,
    warning: darkStatus.warning,
    destructive: darkStatus.failed,
    neutral: hslToHex(216, 14, 50),
    // neutral gray - "no data / not applicable" series
    info: darkPrimary,
    colors: [
      darkPrimary,
      darkStatus.failed,
      darkStatus.success,
      darkStatus.warning,
      hslToHex(270, 50, 65),
      // purple
      hslToHex(180, 50, 55),
      // teal
      hslToHex(330, 60, 65),
      // pink
      hslToHex(60, 60, 55)
      // yellow-green
    ],
    background: hslToHex(220, 48.8, 8.4),
    // #0b1220
    text: hslToHex(214, 32, 92),
    // #e6ebf2
    grid: hslToHex(216, 31, 21),
    // #243044
    gauge: {
      poor: darkStatus.failed,
      needsWork: darkStatus.warning,
      good: hslToHex(45, 75, 58),
      excellent: darkStatus.success
    }
  }
};
var gaugeRanges = {
  poor: { min: 0, max: 49 },
  needsWork: { min: 50, max: 69 },
  good: { min: 70, max: 89 },
  excellent: { min: 90, max: 100 }
};
function getGaugeColor(score, theme = "cofactor") {
  const colors = chartThemes[theme].gauge;
  if (score >= gaugeRanges.excellent.min) return colors.excellent;
  if (score >= gaugeRanges.good.min) return colors.good;
  if (score >= gaugeRanges.needsWork.min) return colors.needsWork;
  return colors.poor;
}
function getGaugeLabel(score) {
  if (score >= gaugeRanges.excellent.min) return "Excellent";
  if (score >= gaugeRanges.good.min) return "Good";
  if (score >= gaugeRanges.needsWork.min) return "Needs Work";
  return "Poor";
}
var runStatusColors = {
  cofactor: {
    success: statusHexColors.cofactor.success,
    failed: statusHexColors.cofactor.failed,
    flaky_success: statusHexColors.cofactor.flaky,
    skipped: statusHexColors.cofactor.skipped,
    running: statusHexColors.cofactor.running,
    waiting: statusHexColors.cofactor.waiting,
    queued: statusHexColors.cofactor.queued
  },
  cofactorDark: {
    success: statusHexColors.cofactorDark.success,
    failed: statusHexColors.cofactorDark.failed,
    flaky_success: statusHexColors.cofactorDark.flaky,
    skipped: statusHexColors.cofactorDark.skipped,
    running: statusHexColors.cofactorDark.running,
    waiting: statusHexColors.cofactorDark.waiting,
    queued: statusHexColors.cofactorDark.queued
  }
};
function runBarColor(result, theme) {
  const palette = runStatusColors[theme];
  switch (result) {
    case "pass":
      return palette.success;
    case "flaky":
      return palette.flaky_success;
    case "running":
      return palette.running;
    case "fail":
      return palette.failed;
    case "skip":
      return palette.skipped;
    case "idle":
      return chartThemes[theme].grid;
  }
}

// src/components/Typography.tsx
import React6 from "react";
import { Heading as AriaHeading, Text as AriaText } from "react-aria-components";
import { jsx as jsx11 } from "react/jsx-runtime";
var headingSizes = {
  xs: "text-xs",
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
  xl: "text-xl",
  "2xl": "text-2xl",
  "3xl": "text-3xl",
  "4xl": "text-4xl"
};
var defaultSizeByLevel = {
  1: "4xl",
  2: "3xl",
  3: "2xl",
  4: "xl",
  5: "lg",
  6: "md"
};
var Heading = React6.forwardRef(
  ({ level = 1, size, className, ...props }, ref) => {
    const defaultSize = defaultSizeByLevel[level];
    const actualSize = size || defaultSize;
    return /* @__PURE__ */ jsx11(
      AriaHeading,
      {
        ref,
        level,
        className: cn(
          "scroll-m-20 font-semibold tracking-tight",
          headingSizes[actualSize],
          className
        ),
        ...props
      }
    );
  }
);
Heading.displayName = "Heading";
var textSizes = {
  xs: "text-xs",
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
  xl: "text-xl"
};
var textWeights = {
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold"
};
var textColors = {
  default: "text-foreground",
  muted: "text-muted-foreground",
  primary: "text-primary",
  destructive: "text-destructive",
  success: "text-green-600 dark:text-green-400",
  warning: "text-yellow-600 dark:text-yellow-400",
  info: "text-blue-600 dark:text-blue-400"
};
var Text = React6.forwardRef(
  ({ size = "md", weight = "normal", color = "default", as = "p", className, ...props }, ref) => {
    const Component = as;
    return /* @__PURE__ */ jsx11(
      AriaText,
      {
        ref,
        elementType: Component,
        className: cn(
          textSizes[size],
          textWeights[weight],
          textColors[color],
          className
        ),
        ...props
      }
    );
  }
);
Text.displayName = "Text";

// src/components/charts/ChartTooltip.tsx
import { jsx as jsx12, jsxs as jsxs10 } from "react/jsx-runtime";
function ChartTooltip({ active, payload, label, theme, formatValue }) {
  const visiblePayload = (payload ?? []).filter(
    (entry) => entry?.value !== null && entry?.value !== void 0
  );
  if (!active || visiblePayload.length === 0) return null;
  const colors = chartThemes[theme];
  return /* @__PURE__ */ jsxs10(
    "div",
    {
      className: "p-3 rounded-lg border shadow-lg",
      style: {
        backgroundColor: colors.background,
        borderColor: colors.grid,
        color: colors.text
      },
      children: [
        label && /* @__PURE__ */ jsx12(Text, { size: "sm", weight: "medium", className: "mb-1", children: label }),
        visiblePayload.map((entry, index) => /* @__PURE__ */ jsxs10("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx12("div", { className: "w-3 h-3 rounded", style: { backgroundColor: entry.color } }),
          /* @__PURE__ */ jsxs10(Text, { size: "sm", children: [
            entry.name,
            ": ",
            formatValue && typeof entry.value === "number" ? formatValue(entry.value) : entry.value
          ] })
        ] }, index))
      ]
    }
  );
}

// src/components/charts/AreaChart.tsx
import { jsx as jsx13, jsxs as jsxs11 } from "react/jsx-runtime";
function AreaChart({
  data,
  xDataKey,
  areas,
  className,
  height = 300,
  margin = { top: 10, right: 30, left: 0, bottom: 0 }
}) {
  const { config } = useTheme();
  const colors = chartThemes[config.theme];
  return /* @__PURE__ */ jsx13("div", { className: cn("w-full", className), children: /* @__PURE__ */ jsx13(ResponsiveContainer, { width: "100%", height, children: /* @__PURE__ */ jsxs11(RechartsAreaChart, { data, margin, children: [
    /* @__PURE__ */ jsx13("defs", { children: areas.map((area, index) => {
      const color = area.color || colors.colors[index % colors.colors.length];
      return /* @__PURE__ */ jsxs11(
        "linearGradient",
        {
          id: `color${area.dataKey}`,
          x1: "0",
          y1: "0",
          x2: "0",
          y2: "1",
          children: [
            /* @__PURE__ */ jsx13("stop", { offset: "5%", stopColor: color, stopOpacity: 0.8 }),
            /* @__PURE__ */ jsx13("stop", { offset: "95%", stopColor: color, stopOpacity: 0.1 })
          ]
        },
        area.dataKey
      );
    }) }),
    /* @__PURE__ */ jsx13(
      XAxis,
      {
        dataKey: xDataKey,
        stroke: colors.text,
        fontSize: 12,
        tickLine: false,
        axisLine: false
      }
    ),
    /* @__PURE__ */ jsx13(YAxis, { stroke: colors.text, fontSize: 12, tickLine: false, axisLine: false }),
    /* @__PURE__ */ jsx13(CartesianGrid, { strokeDasharray: "3 3", stroke: colors.grid }),
    /* @__PURE__ */ jsx13(Tooltip2, { content: (props) => /* @__PURE__ */ jsx13(ChartTooltip, { ...props, theme: config.theme }) }),
    /* @__PURE__ */ jsx13(Legend, {}),
    areas.map((area, index) => /* @__PURE__ */ jsx13(
      Area,
      {
        type: "monotone",
        dataKey: area.dataKey,
        name: area.name || area.dataKey,
        stroke: area.color || colors.colors[index % colors.colors.length],
        fillOpacity: area.fillOpacity || 0.6,
        fill: `url(#color${area.dataKey})`
      },
      area.dataKey
    ))
  ] }) }) });
}

// src/components/charts/BarChart.tsx
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid as CartesianGrid2,
  Cell,
  Legend as Legend2,
  ResponsiveContainer as ResponsiveContainer2,
  Tooltip as Tooltip3,
  XAxis as XAxis2,
  YAxis as YAxis2
} from "recharts";
import { jsx as jsx14, jsxs as jsxs12 } from "react/jsx-runtime";
function BarChart({
  data,
  xDataKey,
  bars,
  className,
  height = 300,
  margin = { top: 20, right: 30, left: 20, bottom: 5 },
  showLegend = true,
  showYAxis = true,
  hasDataKey = "hasData",
  xAxisInterval
}) {
  const { config } = useTheme();
  const colors = chartThemes[config.theme];
  return /* @__PURE__ */ jsx14("div", { className: cn("w-full", className), children: /* @__PURE__ */ jsx14(ResponsiveContainer2, { width: "100%", height, children: /* @__PURE__ */ jsxs12(RechartsBarChart, { data, margin, children: [
    /* @__PURE__ */ jsx14(CartesianGrid2, { strokeDasharray: "3 3", stroke: colors.grid }),
    /* @__PURE__ */ jsx14(
      XAxis2,
      {
        dataKey: xDataKey,
        stroke: colors.text,
        fontSize: 12,
        tickLine: false,
        axisLine: false,
        interval: xAxisInterval
      }
    ),
    showYAxis && /* @__PURE__ */ jsx14(YAxis2, { stroke: colors.text, fontSize: 12, tickLine: false, axisLine: false }),
    /* @__PURE__ */ jsx14(Tooltip3, { content: (props) => /* @__PURE__ */ jsx14(ChartTooltip, { ...props, theme: config.theme }) }),
    showLegend && /* @__PURE__ */ jsx14(Legend2, {}),
    bars.map((bar, index) => /* @__PURE__ */ jsx14(
      Bar,
      {
        dataKey: bar.dataKey,
        name: bar.name || bar.dataKey,
        fill: bar.color || colors.colors[index % colors.colors.length],
        radius: [2, 2, 0, 0],
        children: hasDataKey && data.map((item, dataIndex) => /* @__PURE__ */ jsx14(Cell, { opacity: item[hasDataKey] ? 1 : 0.2 }, `cell-${dataIndex}`))
      },
      bar.dataKey
    ))
  ] }) }) });
}

// src/components/charts/ChartEmptyState.tsx
import { ChartBar, ChartLineUp, WifiSlash } from "@phosphor-icons/react";
import { jsx as jsx15, jsxs as jsxs13 } from "react/jsx-runtime";
function ChartEmptyState({
  message = "No data available",
  description,
  icon = "line",
  height = 200,
  className
}) {
  const IconComponent = {
    line: ChartLineUp,
    bar: ChartBar,
    offline: WifiSlash,
    none: null
  }[icon];
  return /* @__PURE__ */ jsxs13(
    "div",
    {
      className: cn(
        "flex flex-col items-center justify-center rounded-lg bg-muted/20 border border-dashed border-muted",
        className
      ),
      style: { height },
      children: [
        IconComponent && /* @__PURE__ */ jsx15(IconComponent, { size: 32, weight: "duotone", className: "text-muted-foreground/50 mb-2" }),
        /* @__PURE__ */ jsx15(Text, { className: "text-muted-foreground font-medium", children: message }),
        description && /* @__PURE__ */ jsx15(Text, { size: "sm", className: "text-muted-foreground/70 mt-1", children: description })
      ]
    }
  );
}

// src/components/AnimatedNumber.tsx
import React7 from "react";
import { jsx as jsx16 } from "react/jsx-runtime";
function useAnimatedNumber(value, options = {}) {
  const { duration = 1600 } = options;
  const [animatedValue, setAnimatedValue] = React7.useState(0);
  const [isVisible, setIsVisible] = React7.useState(false);
  const prevValueRef = React7.useRef(null);
  const containerRef = React7.useRef(null);
  React7.useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(element);
    return () => {
      observer.unobserve(element);
    };
  }, [isVisible]);
  React7.useEffect(() => {
    if (!isVisible) return;
    if (prevValueRef.current === value) return;
    const startValue = prevValueRef.current ?? 0;
    prevValueRef.current = value;
    const startTime = Date.now();
    const animate = () => {
      const now2 = Date.now();
      const elapsed = now2 - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 5);
      const currentValue = Math.round(startValue + easeOut * (value - startValue));
      setAnimatedValue(currentValue);
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [value, duration, isVisible]);
  return { animatedValue, containerRef };
}
function AnimatedNumber({
  value,
  duration = 1600,
  className,
  formatFn
}) {
  const { animatedValue, containerRef } = useAnimatedNumber(value, { duration });
  const displayValue = formatFn ? formatFn(animatedValue) : animatedValue;
  return /* @__PURE__ */ jsx16("span", { ref: containerRef, className: cn(className), children: displayValue });
}
AnimatedNumber.displayName = "AnimatedNumber";

// src/components/charts/GaugeChart.tsx
import { jsx as jsx17, jsxs as jsxs14 } from "react/jsx-runtime";
function GaugeChart({
  score,
  label,
  className,
  size = "md",
  showScore = true,
  showLabel = true,
  labelClassName,
  valueClassName,
  strokeWidth: strokeWidthOverride,
  displayValue,
  displayFormatter,
  color: colorOverride
}) {
  const { config } = useTheme();
  const { animatedValue: animatedScore, containerRef } = useAnimatedNumber(score);
  const color = colorOverride ?? getGaugeColor(score, config.theme);
  const sizeConfig5 = {
    xxs: { radius: 20, strokeWidth: 3, fontSize: "text-xs", labelSize: "text-[10px]" },
    xs: { radius: 14, strokeWidth: 2, fontSize: "text-xs", labelSize: "text-xs" },
    sm: { radius: 45, strokeWidth: 4, fontSize: "text-lg", labelSize: "text-sm" },
    md: { radius: 60, strokeWidth: 5, fontSize: "text-2xl", labelSize: "text-base" },
    lg: { radius: 80, strokeWidth: 6, fontSize: "text-3xl", labelSize: "text-lg" }
  };
  const { radius } = sizeConfig5[size];
  const strokeWidth = strokeWidthOverride ?? sizeConfig5[size].strokeWidth;
  const normalizedRadius = radius - strokeWidth * 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDasharray = `${circumference} ${circumference}`;
  const strokeDashoffset = circumference - animatedScore / 100 * circumference;
  const svgSize = radius * 2 + strokeWidth * 2;
  return /* @__PURE__ */ jsxs14("div", { ref: containerRef, className: cn("flex flex-col items-center", className), children: [
    /* @__PURE__ */ jsxs14("div", { className: "relative", children: [
      /* @__PURE__ */ jsxs14("svg", { width: svgSize, height: svgSize, className: "transform -rotate-90", children: [
        /* @__PURE__ */ jsx17(
          "circle",
          {
            stroke: color,
            fill: "transparent",
            strokeWidth,
            strokeDasharray,
            strokeDashoffset: "0",
            r: normalizedRadius,
            cx: radius + strokeWidth,
            cy: radius + strokeWidth,
            className: "opacity-15"
          }
        ),
        /* @__PURE__ */ jsx17(
          "circle",
          {
            stroke: color,
            fill: "transparent",
            strokeWidth,
            strokeDasharray,
            strokeDashoffset,
            strokeLinecap: "round",
            r: normalizedRadius,
            cx: radius + strokeWidth,
            cy: radius + strokeWidth
          }
        )
      ] }),
      showScore && /* @__PURE__ */ jsx17(
        "div",
        {
          className: "absolute inset-0 flex flex-col items-center justify-center",
          style: {
            width: svgSize,
            height: svgSize
          },
          children: /* @__PURE__ */ jsx17(
            Heading,
            {
              level: 2,
              className: cn(sizeConfig5[size].fontSize, valueClassName ?? "font-bold"),
              children: typeof displayValue === "number" ? displayFormatter ? displayFormatter(displayValue) : displayValue : displayValue ?? animatedScore
            }
          )
        }
      )
    ] }),
    showLabel && /* @__PURE__ */ jsx17(
      Text,
      {
        className: cn(
          sizeConfig5[size].labelSize,
          labelClassName ?? "mt-2",
          "text-center font-medium"
        ),
        children: label
      }
    )
  ] });
}

// src/components/charts/LineChart.tsx
import React8 from "react";
import {
  Area as Area2,
  CartesianGrid as CartesianGrid3,
  ComposedChart,
  Legend as Legend3,
  Line,
  ReferenceLine,
  ResponsiveContainer as ResponsiveContainer3,
  Tooltip as Tooltip4,
  XAxis as XAxis3,
  YAxis as YAxis3
} from "recharts";
import { jsx as jsx18, jsxs as jsxs15 } from "react/jsx-runtime";
function LineChart({
  data,
  xDataKey,
  lines,
  className,
  height = 300,
  margin = { top: 5, right: 30, left: 20, bottom: 5 },
  showLegend = true,
  showYAxis = true,
  connectNulls = false,
  dot = { r: 4 },
  band,
  formatValue,
  gridOpacity = 1,
  showEmptyAsDashed = false,
  hasDataKey = "hasData",
  xAxisInterval,
  referenceLines
}) {
  const { config } = useTheme();
  const colors = chartThemes[config.theme];
  const solidLineData = React8.useMemo(() => {
    if (!showEmptyAsDashed) return data;
    return data.map((item) => ({
      ...item,
      ...Object.fromEntries(
        lines.map((line) => [`${line.dataKey}_solid`, item[hasDataKey] ? item[line.dataKey] : null])
      )
    }));
  }, [data, showEmptyAsDashed, hasDataKey, lines]);
  return /* @__PURE__ */ jsx18("div", { className: cn("w-full", className), children: /* @__PURE__ */ jsx18(ResponsiveContainer3, { width: "100%", height, children: /* @__PURE__ */ jsxs15(ComposedChart, { data: solidLineData, margin, children: [
    /* @__PURE__ */ jsx18(
      CartesianGrid3,
      {
        strokeDasharray: "2 4",
        stroke: colors.grid,
        strokeOpacity: gridOpacity,
        vertical: false
      }
    ),
    /* @__PURE__ */ jsx18(
      XAxis3,
      {
        dataKey: xDataKey,
        stroke: colors.text,
        fontSize: 12,
        tickLine: false,
        axisLine: false,
        interval: xAxisInterval
      }
    ),
    showYAxis ? /* @__PURE__ */ jsx18(
      YAxis3,
      {
        stroke: colors.text,
        fontSize: 12,
        tickLine: false,
        axisLine: false,
        tickFormatter: (value) => formatValue ? formatValue(Number(value)) : `${value}`
      }
    ) : /* @__PURE__ */ jsx18(YAxis3, { hide: true, tickCount: 5 }),
    /* @__PURE__ */ jsx18(Tooltip4, { content: (props) => /* @__PURE__ */ jsx18(ChartTooltip, { ...props, theme: config.theme, formatValue }) }),
    showLegend && /* @__PURE__ */ jsx18(Legend3, {}),
    band && /* @__PURE__ */ jsx18(
      Area2,
      {
        type: "linear",
        dataKey: band.dataKey,
        stroke: "none",
        fill: band.color,
        fillOpacity: 0.3,
        activeDot: false,
        legendType: "none",
        tooltipType: "none",
        isAnimationActive: false
      }
    ),
    lines.map((line, index) => {
      const lineColor = line.color || colors.colors[index % colors.colors.length];
      if (showEmptyAsDashed) {
        return /* @__PURE__ */ jsxs15(React8.Fragment, { children: [
          /* @__PURE__ */ jsx18(
            Line,
            {
              type: "monotone",
              dataKey: line.dataKey,
              name: line.name || line.dataKey,
              stroke: lineColor,
              strokeWidth: line.strokeWidth || 2,
              strokeDasharray: "4 4",
              strokeOpacity: 0.4,
              dot: false,
              activeDot: false,
              legendType: "none",
              connectNulls
            }
          ),
          /* @__PURE__ */ jsx18(
            Line,
            {
              type: "monotone",
              dataKey: `${line.dataKey}_solid`,
              name: line.name || line.dataKey,
              stroke: lineColor,
              strokeWidth: line.strokeWidth || 2,
              dot,
              activeDot: { r: 6 },
              connectNulls: false
            }
          )
        ] }, line.dataKey);
      }
      return /* @__PURE__ */ jsx18(
        Line,
        {
          type: "monotone",
          dataKey: line.dataKey,
          name: line.name || line.dataKey,
          stroke: lineColor,
          strokeWidth: line.strokeWidth || 2,
          dot,
          activeDot: { r: 6 },
          connectNulls
        },
        line.dataKey
      );
    }),
    referenceLines?.map((ref, index) => /* @__PURE__ */ jsx18(
      ReferenceLine,
      {
        y: ref.y,
        ifOverflow: "extendDomain",
        stroke: ref.color || colors.destructive,
        strokeDasharray: ref.strokeDasharray || "8 4",
        strokeWidth: 2,
        label: {
          value: ref.label || "",
          position: "insideTopRight",
          fill: ref.color || colors.destructive,
          fontSize: 12
        }
      },
      `ref-${index}`
    ))
  ] }) }) });
}

// src/components/charts/PieChart.tsx
import { Cell as Cell2, Legend as Legend4, Pie, PieChart as RechartsPieChart, ResponsiveContainer as ResponsiveContainer4, Tooltip as Tooltip5 } from "recharts";
import { jsx as jsx19, jsxs as jsxs16 } from "react/jsx-runtime";
function PieChart({
  data,
  dataKey,
  nameKey,
  className,
  height = 300,
  innerRadius = 0,
  outerRadius = 80,
  showLabels = true
}) {
  const { config } = useTheme();
  const colors = chartThemes[config.theme];
  return /* @__PURE__ */ jsx19("div", { className: cn("w-full", className), children: /* @__PURE__ */ jsx19(ResponsiveContainer4, { width: "100%", height, children: /* @__PURE__ */ jsxs16(RechartsPieChart, { children: [
    /* @__PURE__ */ jsx19(
      Pie,
      {
        data,
        cx: "50%",
        cy: "50%",
        innerRadius,
        outerRadius,
        paddingAngle: 2,
        dataKey,
        nameKey,
        label: showLabels,
        children: data.map((_, index) => /* @__PURE__ */ jsx19(Cell2, { fill: colors.colors[index % colors.colors.length] }, `cell-${index}`))
      }
    ),
    /* @__PURE__ */ jsx19(Tooltip5, { content: (props) => /* @__PURE__ */ jsx19(ChartTooltip, { ...props, theme: config.theme }) }),
    /* @__PURE__ */ jsx19(Legend4, {})
  ] }) }) });
}

// src/components/charts/StackedBarChart.tsx
import React9 from "react";
import {
  Bar as Bar2,
  BarChart as RechartsBarChart2,
  CartesianGrid as CartesianGrid4,
  Cell as Cell3,
  Legend as Legend5,
  ResponsiveContainer as ResponsiveContainer5,
  Tooltip as Tooltip6,
  XAxis as XAxis4,
  YAxis as YAxis4
} from "recharts";
import { jsx as jsx20, jsxs as jsxs17 } from "react/jsx-runtime";
function StackedBarChart({
  data,
  xDataKey,
  stacks,
  className,
  height = 120,
  margin = { top: 8, right: 8, left: 8, bottom: 8 },
  onBarClick,
  selectedIndex,
  showLegend = true,
  showXAxis = false,
  showGrid = true,
  showYAxis = true,
  yAxisWidth,
  yAxisTickFormatter,
  barSize = 24,
  barGap = "5%",
  renderTooltip,
  minBarHeight = 0,
  allowDecimals = true,
  grouped = false
}) {
  const { config } = useTheme();
  const colors = chartThemes[config.theme];
  const handleClick = (dataItem, index) => {
    onBarClick?.(dataItem, index);
  };
  const processedData = React9.useMemo(() => {
    if (minBarHeight <= 0) return data;
    return data.map((item) => {
      const total = stacks.reduce((sum, stack) => sum + (Number(item[stack.dataKey]) || 0), 0);
      if (total === 0) {
        const firstStack = stacks[0];
        if (!firstStack) return item;
        return { ...item, [firstStack.dataKey]: minBarHeight, _isEmpty: true };
      }
      return item;
    });
  }, [data, stacks, minBarHeight]);
  const CustomStackedTooltip = ({
    active,
    payload
  }) => {
    if (!active || !payload || payload.length === 0) return null;
    const dataItem = payload[0]?.payload;
    if (!dataItem) return null;
    if (renderTooltip) {
      return /* @__PURE__ */ jsx20(
        "div",
        {
          className: "p-3 rounded-lg border shadow-lg bg-popover",
          style: {
            borderColor: colors.grid,
            color: colors.text
          },
          children: renderTooltip(dataItem)
        }
      );
    }
    const labelValue = dataItem[xDataKey];
    const labelText = labelValue === null || labelValue === void 0 ? null : String(labelValue);
    return /* @__PURE__ */ jsxs17(
      "div",
      {
        className: "p-3 rounded-lg border shadow-lg bg-popover",
        style: {
          borderColor: colors.grid,
          color: colors.text
        },
        children: [
          labelText && /* @__PURE__ */ jsx20(Text, { size: "sm", weight: "medium", className: "mb-1", children: labelText }),
          payload.map((entry, index) => /* @__PURE__ */ jsxs17("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx20("div", { className: "w-3 h-3 rounded", style: { backgroundColor: entry.color } }),
            /* @__PURE__ */ jsxs17(Text, { size: "sm", children: [
              String(entry.name),
              ": ",
              String(entry.value)
            ] })
          ] }, index))
        ]
      }
    );
  };
  return /* @__PURE__ */ jsx20("div", { className: cn("w-full", className), children: /* @__PURE__ */ jsx20(ResponsiveContainer5, { width: "100%", height, children: /* @__PURE__ */ jsxs17(
    RechartsBarChart2,
    {
      data: processedData,
      margin,
      barCategoryGap: barGap,
      maxBarSize: barSize,
      children: [
        showGrid && /* @__PURE__ */ jsx20(CartesianGrid4, { strokeDasharray: "3 3", stroke: colors.grid, vertical: false }),
        showXAxis && /* @__PURE__ */ jsx20(
          XAxis4,
          {
            dataKey: xDataKey,
            stroke: colors.text,
            fontSize: 10,
            tickLine: false,
            axisLine: false,
            interval: "preserveStartEnd",
            angle: -45,
            textAnchor: "end",
            height: 50
          }
        ),
        showYAxis && /* @__PURE__ */ jsx20(
          YAxis4,
          {
            stroke: colors.text,
            fontSize: 10,
            tickLine: false,
            axisLine: false,
            width: yAxisWidth ?? 24,
            allowDecimals,
            tickFormatter: yAxisTickFormatter
          }
        ),
        /* @__PURE__ */ jsx20(Tooltip6, { content: CustomStackedTooltip, cursor: { fill: "transparent" } }),
        showLegend && /* @__PURE__ */ jsx20(Legend5, { wrapperStyle: { fontSize: "12px" }, iconSize: 10 }),
        stacks.map((stack, stackIndex) => /* @__PURE__ */ jsx20(
          Bar2,
          {
            dataKey: stack.dataKey,
            name: stack.name || stack.dataKey,
            ...grouped ? {} : { stackId: "stack" },
            fill: stack.color || colors.colors[stackIndex % colors.colors.length],
            radius: grouped || stackIndex === stacks.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0],
            onClick: (entry, index) => {
              const dataItem = entry?.payload ?? data[index];
              if (dataItem) handleClick(dataItem, index);
            },
            cursor: onBarClick ? "pointer" : void 0,
            children: processedData.map((item, dataIndex) => /* @__PURE__ */ jsx20(
              Cell3,
              {
                opacity: item._isEmpty === true ? 0.2 : selectedIndex !== null && selectedIndex !== void 0 && selectedIndex !== dataIndex ? 0.4 : 1,
                stroke: selectedIndex === dataIndex ? "var(--primary)" : "transparent",
                strokeWidth: selectedIndex === dataIndex && stackIndex === stacks.length - 1 ? 2 : 0
              },
              `cell-${dataIndex}`
            ))
          },
          stack.dataKey
        ))
      ]
    }
  ) }) });
}

// src/components/TextField.tsx
import React10 from "react";
import {
  TextField as AriaTextField,
  Label,
  Input,
  Text as Text2
} from "react-aria-components";
import { jsx as jsx21, jsxs as jsxs18 } from "react/jsx-runtime";
var inputVariants = {
  default: "bg-input border-border",
  filled: "bg-muted border-border",
  ghost: "bg-transparent border-transparent shadow-none"
};
var TextField = React10.forwardRef(
  ({
    label,
    description,
    errorMessage,
    placeholder,
    type = "text",
    variant = "default",
    className,
    leftIcon,
    rightIcon,
    onLeftIconClick,
    onRightIconClick,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs18(
      AriaTextField,
      {
        ...props,
        className: cn("flex flex-col gap-1.5", className),
        isInvalid: !!errorMessage,
        children: [
          label && /* @__PURE__ */ jsxs18(Label, { className: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70", children: [
            label,
            props.isRequired && /* @__PURE__ */ jsx21("span", { className: "text-destructive ml-0.5", children: "*" })
          ] }),
          /* @__PURE__ */ jsxs18("div", { className: "relative", children: [
            /* @__PURE__ */ jsx21(
              Input,
              {
                ref,
                type,
                placeholder,
                "data-control": "",
                className: cn(
                  "flex h-9 w-full rounded-md py-1 text-sm transition-colors",
                  "file:border-0 file:bg-transparent file:text-sm file:font-medium",
                  "placeholder:text-muted-foreground",
                  "focus-visible:outline-none focus-visible:border-ring/70",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  "data-[invalid]:border-destructive",
                  variant !== "ghost" && "border",
                  inputVariants[variant],
                  leftIcon ? "pl-10" : "pl-3",
                  rightIcon ? "pr-10" : "pr-3"
                )
              }
            ),
            leftIcon && /* @__PURE__ */ jsx21(
              "button",
              {
                type: "button",
                onClick: onLeftIconClick,
                disabled: props.isDisabled,
                tabIndex: -1,
                className: cn(
                  "absolute left-3 top-1/2 -translate-y-1/2",
                  "text-muted-foreground hover:text-foreground",
                  "transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm",
                  "disabled:opacity-50 disabled:pointer-events-none",
                  !onLeftIconClick && "pointer-events-none"
                ),
                "aria-label": "Left icon",
                children: leftIcon
              }
            ),
            rightIcon && /* @__PURE__ */ jsx21(
              "button",
              {
                type: "button",
                onClick: onRightIconClick,
                disabled: props.isDisabled,
                tabIndex: -1,
                className: cn(
                  "absolute right-3 top-1/2 -translate-y-1/2",
                  "text-muted-foreground hover:text-foreground",
                  "transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm",
                  "disabled:opacity-50 disabled:pointer-events-none",
                  !onRightIconClick && "pointer-events-none"
                ),
                "aria-label": "Right icon",
                children: rightIcon
              }
            )
          ] }),
          description && /* @__PURE__ */ jsx21(Text2, { slot: "description", className: "text-xs text-muted-foreground", children: description }),
          errorMessage && /* @__PURE__ */ jsx21(Text2, { slot: "errorMessage", className: "text-xs text-destructive", children: errorMessage })
        ]
      }
    );
  }
);
TextField.displayName = "TextField";

// src/components/NumberField.tsx
import React11 from "react";
import { Minus, Plus } from "@phosphor-icons/react";

// src/hooks/useNumberInput.ts
import {
  useState as useState3,
  useCallback as useCallback3,
  useMemo as useMemo2
} from "react";
function useNumberInput({
  value,
  onChange,
  min,
  max,
  clampOnCommit = true,
  step = 1
}) {
  const [localValue, setLocalValue] = useState3(
    () => value !== null && value !== void 0 ? String(value) : ""
  );
  const [isEditing, setIsEditing] = useState3(false);
  const displayValue = isEditing ? localValue : value !== null && value !== void 0 ? String(value) : "";
  const parsedValue = useMemo2(() => {
    if (displayValue === "" || displayValue === "-") return null;
    const num = parseFloat(displayValue);
    return Number.isFinite(num) ? num : null;
  }, [displayValue]);
  const isBelowMin = parsedValue !== null && min !== null && min !== void 0 && parsedValue < min;
  const isAboveMax = parsedValue !== null && max !== null && max !== void 0 && parsedValue > max;
  const isOutOfRange = isBelowMin || isAboveMax;
  const clampValue = useCallback3(
    (val) => {
      let clamped = val;
      if (min !== null && min !== void 0) clamped = Math.max(min, clamped);
      if (max !== null && max !== void 0) clamped = Math.min(max, clamped);
      return clamped;
    },
    [min, max]
  );
  const commit = useCallback3(() => {
    setIsEditing(false);
    if (localValue === "" || localValue === "-") {
      onChange(null);
      return;
    }
    const parsed = parseFloat(localValue);
    if (!Number.isFinite(parsed)) {
      setLocalValue(value !== null && value !== void 0 ? String(value) : "");
      return;
    }
    const finalValue = clampOnCommit ? clampValue(parsed) : parsed;
    onChange(finalValue);
    setLocalValue(String(finalValue));
  }, [localValue, value, onChange, clampOnCommit, clampValue]);
  const handleChange = useCallback3((e) => {
    const newValue = e.target.value;
    if (/^-?\d*\.?\d*$/.test(newValue)) {
      setIsEditing(true);
      setLocalValue(newValue);
    }
  }, []);
  const handleBlur = useCallback3(
    (_e) => {
      commit();
    },
    [commit]
  );
  const handleKeyDown = useCallback3(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commit();
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        const current = parsedValue ?? min ?? 0;
        const newValue = clampValue(current + step);
        onChange(newValue);
        setLocalValue(String(newValue));
        setIsEditing(false);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        const current = parsedValue ?? max ?? 0;
        const newValue = clampValue(current - step);
        onChange(newValue);
        setLocalValue(String(newValue));
        setIsEditing(false);
        return;
      }
    },
    [commit, parsedValue, min, max, step, clampValue, onChange]
  );
  const increment = useCallback3(() => {
    const current = parsedValue ?? min ?? 0;
    const newValue = clampValue(current + step);
    onChange(newValue);
    setLocalValue(String(newValue));
    setIsEditing(false);
  }, [parsedValue, min, step, clampValue, onChange]);
  const decrement = useCallback3(() => {
    const current = parsedValue ?? max ?? 0;
    const newValue = clampValue(current - step);
    onChange(newValue);
    setLocalValue(String(newValue));
    setIsEditing(false);
  }, [parsedValue, max, step, clampValue, onChange]);
  return {
    displayValue,
    inputProps: {
      value: displayValue,
      onChange: handleChange,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      type: "text",
      inputMode: "numeric"
    },
    isOutOfRange,
    isBelowMin,
    isAboveMax,
    commit,
    increment,
    decrement
  };
}

// src/components/NumberField.tsx
import { jsx as jsx22, jsxs as jsxs19 } from "react/jsx-runtime";
var inputVariants2 = {
  default: "bg-input border-border",
  filled: "bg-muted border-border",
  ghost: "bg-transparent border-transparent shadow-none"
};
var NumberField = React11.forwardRef(
  ({
    label,
    description,
    errorMessage,
    placeholder,
    value,
    onChange,
    minValue,
    maxValue,
    step = 1,
    showStepper = false,
    variant = "default",
    className,
    isDisabled,
    isRequired,
    leftIcon,
    rightIcon
  }, ref) => {
    const {
      inputProps,
      isOutOfRange,
      isBelowMin,
      isAboveMax,
      increment,
      decrement
    } = useNumberInput({
      value: value ?? null,
      onChange: onChange ?? (() => {
      }),
      min: minValue,
      max: maxValue,
      step
    });
    const rangeWarning = React11.useMemo(() => {
      if (!isOutOfRange) return null;
      if (isBelowMin && minValue !== void 0) {
        return `Minimum value is ${minValue}`;
      }
      if (isAboveMax && maxValue !== void 0) {
        return `Maximum value is ${maxValue}`;
      }
      return null;
    }, [isOutOfRange, isBelowMin, isAboveMax, minValue, maxValue]);
    const showWarning = !errorMessage && isOutOfRange;
    const inputId = React11.useId();
    return /* @__PURE__ */ jsxs19("div", { className: cn("flex flex-col gap-1.5", className), children: [
      label && /* @__PURE__ */ jsxs19(
        "label",
        {
          htmlFor: inputId,
          className: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
          children: [
            label,
            isRequired && /* @__PURE__ */ jsx22("span", { className: "text-destructive ml-0.5", children: "*" })
          ]
        }
      ),
      /* @__PURE__ */ jsx22("div", { className: "relative", children: /* @__PURE__ */ jsxs19(
        "div",
        {
          "data-control": "",
          className: cn(
            "flex h-9 w-full items-center rounded-md py-1 text-sm transition-colors",
            "focus-within:outline-none focus-within:border-ring/70",
            variant !== "ghost" && "border shadow-sm",
            inputVariants2[variant],
            errorMessage && "border-destructive focus-within:border-destructive",
            showWarning && "border-warning focus-within:border-warning",
            isDisabled && "cursor-not-allowed opacity-50",
            leftIcon && "pl-10",
            rightIcon && !showStepper && "pr-10",
            !leftIcon && !showStepper && "px-3",
            !leftIcon && showStepper && "pl-3"
          ),
          children: [
            leftIcon && /* @__PURE__ */ jsx22("span", { className: "absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground", children: leftIcon }),
            /* @__PURE__ */ jsx22(
              "input",
              {
                ...inputProps,
                ref,
                id: inputId,
                placeholder,
                disabled: isDisabled,
                required: isRequired,
                "aria-invalid": !!errorMessage,
                "aria-describedby": errorMessage ? `${inputId}-error` : rangeWarning ? `${inputId}-warning` : description ? `${inputId}-description` : void 0,
                className: cn(
                  "flex-1 bg-transparent outline-none w-full",
                  "placeholder:text-muted-foreground",
                  "disabled:cursor-not-allowed",
                  "tabular-nums",
                  leftIcon ? "pl-10" : "",
                  showStepper ? "text-center" : ""
                )
              }
            ),
            showStepper && /* @__PURE__ */ jsxs19("div", { className: "flex items-center gap-0.5 pr-1", children: [
              /* @__PURE__ */ jsx22(
                "button",
                {
                  type: "button",
                  onClick: decrement,
                  disabled: isDisabled,
                  tabIndex: -1,
                  className: cn(
                    "h-6 w-6 flex items-center justify-center rounded-sm",
                    "hover:bg-accent hover:text-accent-foreground",
                    "outline-none focus:ring-2 focus:ring-ring",
                    "transition-colors",
                    "disabled:opacity-50 disabled:pointer-events-none"
                  ),
                  "aria-label": "Decrease value",
                  children: /* @__PURE__ */ jsx22(Minus, { className: "h-3 w-3" })
                }
              ),
              /* @__PURE__ */ jsx22(
                "button",
                {
                  type: "button",
                  onClick: increment,
                  disabled: isDisabled,
                  tabIndex: -1,
                  className: cn(
                    "h-6 w-6 flex items-center justify-center rounded-sm",
                    "hover:bg-accent hover:text-accent-foreground",
                    "outline-none focus:ring-2 focus:ring-ring",
                    "transition-colors",
                    "disabled:opacity-50 disabled:pointer-events-none"
                  ),
                  "aria-label": "Increase value",
                  children: /* @__PURE__ */ jsx22(Plus, { className: "h-3 w-3" })
                }
              )
            ] }),
            rightIcon && !showStepper && /* @__PURE__ */ jsx22("span", { className: "absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground", children: rightIcon })
          ]
        }
      ) }),
      description && !errorMessage && !rangeWarning && /* @__PURE__ */ jsx22("p", { id: `${inputId}-description`, className: "text-xs text-muted-foreground", children: description }),
      rangeWarning && !errorMessage && /* @__PURE__ */ jsx22("p", { id: `${inputId}-warning`, className: "text-xs text-warning", children: rangeWarning }),
      errorMessage && /* @__PURE__ */ jsx22("p", { id: `${inputId}-error`, className: "text-xs text-destructive", children: errorMessage })
    ] });
  }
);
NumberField.displayName = "NumberField";

// src/components/KeyValueEditor.tsx
import React13 from "react";
import { Plus as Plus2, X } from "@phosphor-icons/react";

// src/components/Layout.tsx
import React12 from "react";
import { jsx as jsx23 } from "react/jsx-runtime";
var gapSizes = {
  xs: "gap-1",
  sm: "gap-2",
  md: "gap-3",
  lg: "gap-4",
  xl: "gap-6",
  "2xl": "gap-8"
};
var alignItems = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch",
  baseline: "items-baseline"
};
var justifyContent = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
  around: "justify-around",
  evenly: "justify-evenly"
};
var Flex = React12.forwardRef(
  ({
    direction = "row",
    align = "stretch",
    justify = "start",
    wrap = false,
    gap = "md",
    flex,
    className,
    style,
    ...props
  }, ref) => {
    const wrapClass = wrap === true || wrap === "wrap" ? "flex-wrap" : wrap === "wrap-reverse" ? "flex-wrap-reverse" : "flex-nowrap";
    return /* @__PURE__ */ jsx23(
      "div",
      {
        ref,
        className: cn(
          "flex",
          direction === "column" ? "flex-col" : direction === "column-reverse" ? "flex-col-reverse" : direction === "row-reverse" ? "flex-row-reverse" : "flex-row",
          alignItems[align],
          justifyContent[justify],
          wrap !== false && wrapClass,
          gap && gapSizes[gap],
          className
        ),
        style: { flex, ...style },
        ...props
      }
    );
  }
);
Flex.displayName = "Flex";
var Stack = React12.forwardRef(
  ({ direction = "vertical", ...props }, ref) => {
    return /* @__PURE__ */ jsx23(Flex, { ref, direction: direction === "vertical" ? "column" : "row", ...props });
  }
);
Stack.displayName = "Stack";
var paddingSizes = {
  none: "p-0",
  xs: "p-1",
  sm: "p-2",
  md: "p-3",
  lg: "p-4",
  xl: "p-6",
  "2xl": "p-8"
};
var marginSizes = {
  none: "m-0",
  xs: "m-1",
  sm: "m-2",
  md: "m-3",
  lg: "m-4",
  xl: "m-6",
  "2xl": "m-8"
};
var Box = React12.forwardRef(
  ({ padding = "none", margin = "none", className, ...props }, ref) => {
    return /* @__PURE__ */ jsx23(
      "div",
      {
        ref,
        className: cn(
          paddingSizes[padding],
          marginSizes[margin],
          className
        ),
        ...props
      }
    );
  }
);
Box.displayName = "Box";

// src/components/KeyValueEditor.tsx
import { jsx as jsx24, jsxs as jsxs20 } from "react/jsx-runtime";
function KeyValueEditor({
  value,
  onChange,
  disabled = false,
  placeholder = { key: "Key", value: "Value" },
  addButtonLabel = "Add Entry",
  maxEntries = 0
}) {
  const [entries, setEntries] = React13.useState(
    () => {
      const result = Object.entries(value).map(([k, v], idx) => ({
        id: `entry-${idx}-${Date.now()}`,
        key: k,
        value: v
      }));
      return result;
    }
  );
  React13.useEffect(() => {
    const currentKeys = new Set(entries.map((e) => e.key).filter(Boolean));
    const valueKeys = new Set(Object.keys(value));
    const needsSync = currentKeys.size !== valueKeys.size || [...valueKeys].some((k) => !currentKeys.has(k)) || entries.some((e) => e.key && value[e.key] !== e.value);
    if (needsSync && Object.keys(value).length > 0) {
      setEntries(
        Object.entries(value).map(([k, v], idx) => ({
          id: `entry-${idx}-${Date.now()}`,
          key: k,
          value: v
        }))
      );
    } else if (Object.keys(value).length === 0 && entries.length > 0 && entries.every((e) => e.key)) {
      setEntries([]);
    }
  }, [value]);
  const syncToParent = (newEntries) => {
    const newValue = {};
    for (const entry of newEntries) {
      if (entry.key.trim()) {
        newValue[entry.key.trim()] = entry.value;
      }
    }
    onChange(newValue);
  };
  const addEntry = () => {
    if (maxEntries > 0 && entries.length >= maxEntries) return;
    const newEntries = [...entries, { id: `entry-${Date.now()}`, key: "", value: "" }];
    setEntries(newEntries);
  };
  const updateKey = (id, newKey) => {
    const newEntries = entries.map((e) => e.id === id ? { ...e, key: newKey } : e);
    setEntries(newEntries);
    syncToParent(newEntries);
  };
  const updateValue = (id, newValue) => {
    const newEntries = entries.map((e) => e.id === id ? { ...e, value: newValue } : e);
    setEntries(newEntries);
    syncToParent(newEntries);
  };
  const removeEntry = (id) => {
    const newEntries = entries.filter((e) => e.id !== id);
    setEntries(newEntries);
    syncToParent(newEntries);
  };
  const canAddMore = maxEntries === 0 || entries.length < maxEntries;
  return /* @__PURE__ */ jsxs20(Flex, { direction: "column", gap: "sm", children: [
    entries.map((entry) => /* @__PURE__ */ jsxs20(Flex, { gap: "sm", align: "center", children: [
      /* @__PURE__ */ jsx24(
        TextField,
        {
          value: entry.key,
          onChange: (k) => updateKey(entry.id, k),
          isDisabled: disabled,
          placeholder: placeholder.key,
          className: "flex-1",
          "aria-label": "Key"
        }
      ),
      /* @__PURE__ */ jsx24(
        TextField,
        {
          value: entry.value,
          onChange: (v) => updateValue(entry.id, v),
          isDisabled: disabled,
          placeholder: placeholder.value,
          className: "flex-1",
          "aria-label": "Value"
        }
      ),
      /* @__PURE__ */ jsx24(
        Button,
        {
          variant: "ghost",
          size: "sm",
          iconOnly: true,
          onPress: () => removeEntry(entry.id),
          isDisabled: disabled,
          "aria-label": "Remove entry",
          children: /* @__PURE__ */ jsx24(X, { size: 16 })
        }
      )
    ] }, entry.id)),
    entries.length === 0 && /* @__PURE__ */ jsxs20(Text, { size: "xs", className: "text-muted-foreground italic", children: [
      'No entries. Click "',
      addButtonLabel,
      '" to add one.'
    ] }),
    /* @__PURE__ */ jsxs20(
      Button,
      {
        variant: "outline",
        size: "sm",
        onPress: addEntry,
        isDisabled: disabled || !canAddMore,
        className: "self-start",
        children: [
          /* @__PURE__ */ jsx24(Plus2, { size: 16 }),
          /* @__PURE__ */ jsx24("span", { className: "ml-1", children: addButtonLabel })
        ]
      }
    )
  ] });
}

// src/components/TextArea.tsx
import React14 from "react";
import {
  TextField as AriaTextField2,
  Label as Label2,
  TextArea as AriaTextArea,
  Text as Text3
} from "react-aria-components";
import { jsx as jsx25, jsxs as jsxs21 } from "react/jsx-runtime";
var textAreaVariants = {
  default: "bg-input border-border",
  filled: "bg-muted border-border",
  ghost: "bg-transparent border-transparent shadow-none"
};
var resizeVariants = {
  none: "resize-none",
  vertical: "resize-y",
  horizontal: "resize-x",
  both: "resize"
};
var TextArea = React14.forwardRef(
  ({
    label,
    description,
    errorMessage,
    placeholder,
    variant = "default",
    className,
    rows = 4,
    resize = "vertical",
    onKeyDown,
    autoExpand = false,
    maxHeight,
    ...props
  }, ref) => {
    const internalRef = React14.useRef(null);
    const setRefs = React14.useCallback(
      (element) => {
        internalRef.current = element;
        if (typeof ref === "function") {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      },
      [ref]
    );
    React14.useLayoutEffect(() => {
      if (!autoExpand || !internalRef.current) return;
      const textarea = internalRef.current;
      textarea.style.height = "auto";
      const height = maxHeight ? Math.min(textarea.scrollHeight, maxHeight) : textarea.scrollHeight;
      textarea.style.height = `${height}px`;
    }, [autoExpand, maxHeight, props.value]);
    return /* @__PURE__ */ jsxs21(
      AriaTextField2,
      {
        ...props,
        className: cn("flex flex-col gap-1.5", className),
        isInvalid: !!errorMessage,
        children: [
          label && /* @__PURE__ */ jsxs21(Label2, { className: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70", children: [
            label,
            props.isRequired && /* @__PURE__ */ jsx25("span", { className: "text-destructive ml-0.5", children: "*" })
          ] }),
          /* @__PURE__ */ jsx25(
            AriaTextArea,
            {
              ref: setRefs,
              placeholder,
              rows,
              onKeyDown,
              className: cn(
                "flex w-full rounded-md px-3 py-2 text-sm transition-colors",
                "placeholder:text-muted-foreground",
                "focus-visible:outline-none focus-visible:border-ring/70",
                "disabled:cursor-not-allowed disabled:opacity-50",
                "data-[invalid]:border-destructive",
                variant !== "ghost" && "border shadow-sm",
                textAreaVariants[variant],
                // Capped auto-expand scrolls internally once it hits the cap;
                // uncapped auto-expand always fits its content, so it clips instead.
                !autoExpand ? resizeVariants[resize] : maxHeight ? "resize-none overflow-y-auto" : "resize-none overflow-hidden"
              )
            }
          ),
          description && /* @__PURE__ */ jsx25(Text3, { slot: "description", className: "text-xs text-muted-foreground", children: description }),
          errorMessage && /* @__PURE__ */ jsx25(Text3, { slot: "errorMessage", className: "text-xs text-destructive", children: errorMessage })
        ]
      }
    );
  }
);
TextArea.displayName = "TextArea";

// src/components/PasswordField.tsx
import React15 from "react";

// src/components/Icons.tsx
import {
  House,
  MagnifyingGlass,
  Bell,
  User,
  Gear,
  SignOut,
  Plus as Plus3,
  Minus as Minus2,
  X as X2,
  Check,
  Play,
  Sun,
  Moon,
  CaretDown as CaretDown3,
  CaretUp,
  CaretLeft,
  CaretRight as CaretRight3,
  CaretDown as CaretDown4,
  CaretUp as CaretUp2,
  CaretLeft as CaretLeft2,
  CaretRight as CaretRight4,
  CaretUpDown,
  Heart,
  Star,
  Share,
  Download,
  Upload,
  File,
  Folder,
  FolderOpen,
  Image,
  Video,
  MusicNote,
  FileText,
  FilePdf,
  ChatCircle,
  Envelope,
  Phone,
  VideoCamera,
  Microphone,
  MicrophoneSlash,
  Calendar,
  Clock,
  MapPin,
  Bookmark,
  Archive,
  Trash,
  PencilSimple,
  Copy,
  Eye,
  EyeSlash,
  Buildings,
  UsersThree,
  ChartBar as ChartBar2,
  ChartLine,
  TrendUp,
  TrendDown,
  Database,
  CloudArrowUp,
  CloudArrowDown,
  CheckCircle,
  XCircle,
  Warning,
  Info,
  Question,
  Lightbulb,
  Shield,
  ShieldCheck,
  List,
  Rows,
  Columns,
  GridFour,
  Sidebar,
  DotsThree,
  DotsThreeVertical,
  ThumbsUp,
  ThumbsDown,
  LinkSimple,
  Export,
  ArrowSquareIn,
  ShoppingCart,
  CreditCard,
  Receipt,
  Package,
  Code,
  Terminal,
  Gear as Gear2,
  Cpu,
  HardDrives,
  FlowArrow,
  GitBranch,
  ArrowClockwise,
  ArrowCounterClockwise,
  ArrowsLeftRight,
  ArrowRight
} from "@phosphor-icons/react";
var iconSizes = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32
};

// src/components/PasswordField.tsx
import { jsx as jsx26 } from "react/jsx-runtime";
var PasswordField = React15.forwardRef(
  ({ allowToggle = true, isVisible, onVisibilityChange, ...props }, ref) => {
    const [internalVisible, setInternalVisible] = React15.useState(false);
    const showPassword = isVisible ?? internalVisible;
    const setShowPassword = (visible) => {
      if (isVisible === void 0) setInternalVisible(visible);
      onVisibilityChange?.(visible);
    };
    return /* @__PURE__ */ jsx26(
      TextField,
      {
        ref,
        type: showPassword ? "text" : "password",
        rightIcon: allowToggle ? showPassword ? /* @__PURE__ */ jsx26(EyeSlash, { size: 18 }) : /* @__PURE__ */ jsx26(Eye, { size: 18 }) : void 0,
        onRightIconClick: allowToggle ? () => setShowPassword(!showPassword) : void 0,
        ...props
      }
    );
  }
);
PasswordField.displayName = "PasswordField";

// src/components/PasswordStrengthIndicator.tsx
import React16 from "react";
import { validatePassword } from "@chatsdet/types/password";
import { jsx as jsx27, jsxs as jsxs22 } from "react/jsx-runtime";
function calculatePasswordStrength(password) {
  return validatePassword(password);
}
function usePasswordStrength(password) {
  return React16.useMemo(() => calculatePasswordStrength(password), [password]);
}
var strengthColors = {
  weak: "bg-destructive",
  fair: "bg-warning",
  good: "bg-success/70",
  strong: "bg-success"
};
var strengthLabels = {
  weak: "Weak",
  fair: "Fair",
  good: "Good",
  strong: "Strong"
};
var PasswordStrengthIndicator = ({
  password,
  className,
  showRequirements = true
}) => {
  const { score, level, requirements } = usePasswordStrength(password);
  if (!password) {
    return null;
  }
  return /* @__PURE__ */ jsxs22("div", { className: cn("space-y-3", className), children: [
    /* @__PURE__ */ jsxs22("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsxs22("div", { className: "flex justify-between items-center text-xs", children: [
        /* @__PURE__ */ jsx27("span", { className: "text-muted-foreground", children: "Password strength" }),
        /* @__PURE__ */ jsx27(
          "span",
          {
            className: cn(
              "font-medium",
              level === "weak" && "text-destructive",
              level === "fair" && "text-warning",
              level === "good" && "text-success/70",
              level === "strong" && "text-success"
            ),
            children: strengthLabels[level]
          }
        )
      ] }),
      /* @__PURE__ */ jsx27("div", { className: "h-1.5 w-full bg-muted rounded-full overflow-hidden", children: /* @__PURE__ */ jsx27(
        "div",
        {
          className: cn(
            "h-full transition-all duration-300 rounded-full",
            strengthColors[level]
          ),
          style: { width: `${score}%` }
        }
      ) })
    ] }),
    showRequirements && /* @__PURE__ */ jsx27("div", { className: "space-y-1", children: requirements.map((req, idx) => /* @__PURE__ */ jsxs22(
      "div",
      {
        className: cn(
          "flex items-center gap-2 text-xs transition-colors",
          req.met ? "text-success" : "text-muted-foreground"
        ),
        children: [
          req.met ? /* @__PURE__ */ jsx27(Check, { size: 14, weight: "bold", className: "flex-shrink-0" }) : /* @__PURE__ */ jsx27(X2, { size: 14, weight: "bold", className: "flex-shrink-0 opacity-50" }),
          /* @__PURE__ */ jsx27("span", { children: req.label })
        ]
      },
      idx
    )) })
  ] });
};
PasswordStrengthIndicator.displayName = "PasswordStrengthIndicator";

// src/components/Checkbox.tsx
import React17 from "react";
import { Checkbox as AriaCheckbox } from "react-aria-components";
import { Fragment as Fragment4, jsx as jsx28, jsxs as jsxs23 } from "react/jsx-runtime";
var Checkbox = React17.forwardRef(
  ({ label, description, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx28(
      AriaCheckbox,
      {
        ref,
        className: (renderProps) => cn(
          "group relative flex items-start gap-2 cursor-pointer",
          typeof className === "function" ? className(renderProps) : className
        ),
        ...props,
        children: (renderProps) => /* @__PURE__ */ jsxs23(Fragment4, { children: [
          /* @__PURE__ */ jsxs23(
            "div",
            {
              className: cn(
                // Flat box: neutral border at rest, primary fill when active —
                // no shadow, no always-on accent border (reads as noisy in lists).
                "relative h-4 w-4 shrink-0 rounded-sm border border-muted-foreground/40 bg-background flex items-center justify-center transition-colors",
                "group-hover:border-muted-foreground/70",
                "group-data-[focus-visible]:outline-none group-data-[focus-visible]:ring-2 group-data-[focus-visible]:ring-ring/40",
                "disabled:cursor-not-allowed disabled:opacity-50",
                (renderProps.isSelected || renderProps.isIndeterminate) && "bg-primary border-primary text-primary-foreground"
              ),
              children: [
                /* @__PURE__ */ jsx28(
                  "svg",
                  {
                    className: cn(
                      "h-3 w-3 transition-all duration-150",
                      renderProps.isSelected && !renderProps.isIndeterminate ? "opacity-100 scale-100" : "opacity-0 scale-75"
                    ),
                    viewBox: "0 0 16 16",
                    fill: "none",
                    xmlns: "http://www.w3.org/2000/svg",
                    children: /* @__PURE__ */ jsx28(
                      "path",
                      {
                        d: "M12.5 4.5L6.5 10.5L3.5 7.5",
                        stroke: "currentColor",
                        strokeWidth: "2",
                        strokeLinecap: "round",
                        strokeLinejoin: "round"
                      }
                    )
                  }
                ),
                /* @__PURE__ */ jsx28(
                  "span",
                  {
                    className: cn(
                      "absolute h-0.5 w-2 rounded bg-current transition-all duration-150",
                      renderProps.isIndeterminate ? "opacity-100 scale-x-100" : "opacity-0 scale-x-50"
                    )
                  }
                )
              ]
            }
          ),
          (label || description) && /* @__PURE__ */ jsxs23("div", { className: "grid gap-1.5 leading-none min-w-0 flex-1", children: [
            label && /* @__PURE__ */ jsxs23("span", { className: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70", children: [
              label,
              props.isRequired && /* @__PURE__ */ jsx28("span", { className: "text-destructive ml-0.5", children: "*" })
            ] }),
            description && /* @__PURE__ */ jsx28("div", { className: "text-xs text-muted-foreground", children: description })
          ] })
        ] })
      }
    );
  }
);
Checkbox.displayName = "Checkbox";

// src/components/Switch.tsx
import React18 from "react";
import { Switch as AriaSwitch } from "react-aria-components";
import { Fragment as Fragment5, jsx as jsx29, jsxs as jsxs24 } from "react/jsx-runtime";
var sizeConfig = {
  sm: {
    track: "h-5 w-9",
    thumb: "h-4 w-4",
    translate: "translate-x-4"
  },
  md: {
    track: "h-6 w-11",
    thumb: "h-5 w-5",
    translate: "translate-x-5"
  },
  lg: {
    track: "h-7 w-14",
    thumb: "h-6 w-6",
    translate: "translate-x-7"
  }
};
var Switch = React18.forwardRef(
  ({ size = "md", label, className, ...props }, ref) => {
    const config = sizeConfig[size];
    return /* @__PURE__ */ jsx29(
      AriaSwitch,
      {
        ref,
        className: (renderProps) => cn(
          "group relative inline-flex items-center gap-2 cursor-pointer",
          renderProps.isDisabled && "cursor-not-allowed opacity-50",
          typeof className === "function" ? className(renderProps) : className
        ),
        ...props,
        children: (renderProps) => /* @__PURE__ */ jsxs24(Fragment5, { children: [
          /* @__PURE__ */ jsx29(
            "div",
            {
              className: cn(
                "relative inline-flex shrink-0 rounded-full transition-colors duration-200",
                config.track,
                renderProps.isSelected ? "bg-[#FFD60A]" : "bg-muted",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                "group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-2"
              ),
              children: /* @__PURE__ */ jsx29(
                "span",
                {
                  className: cn(
                    "absolute top-0.5 left-0.5 bg-white rounded-full shadow transition-transform duration-200",
                    config.thumb,
                    renderProps.isSelected && config.translate
                  )
                }
              )
            }
          ),
          label && /* @__PURE__ */ jsx29("span", { className: "text-sm font-medium leading-none select-none", children: label })
        ] })
      }
    );
  }
);
Switch.displayName = "Switch";

// src/components/PillToggle.tsx
import React19 from "react";

// src/theme/tagColors.ts
var TAG_COLORS = {
  red: {
    bg: "bg-red-100 dark:bg-red-900/30",
    text: "text-red-700 dark:text-red-300",
    border: "border-red-200 dark:border-red-800",
    dot: "bg-red-500",
    hover: "hover:bg-red-200 dark:hover:bg-red-800/50"
  },
  orange: {
    bg: "bg-orange-100 dark:bg-orange-900/30",
    text: "text-orange-700 dark:text-orange-300",
    border: "border-orange-200 dark:border-orange-800",
    dot: "bg-orange-500",
    hover: "hover:bg-orange-200 dark:hover:bg-orange-800/50"
  },
  yellow: {
    bg: "bg-yellow-100 dark:bg-yellow-900/30",
    text: "text-yellow-700 dark:text-yellow-300",
    border: "border-yellow-200 dark:border-yellow-800",
    dot: "bg-yellow-500",
    hover: "hover:bg-yellow-200 dark:hover:bg-yellow-800/50"
  },
  green: {
    bg: "bg-green-100 dark:bg-green-900/30",
    text: "text-green-700 dark:text-green-300",
    border: "border-green-200 dark:border-green-800",
    dot: "bg-green-500",
    hover: "hover:bg-green-200 dark:hover:bg-green-800/50"
  },
  teal: {
    bg: "bg-teal-100 dark:bg-teal-900/30",
    text: "text-teal-700 dark:text-teal-300",
    border: "border-teal-200 dark:border-teal-800",
    dot: "bg-teal-500",
    hover: "hover:bg-teal-200 dark:hover:bg-teal-800/50"
  },
  blue: {
    bg: "bg-blue-100 dark:bg-blue-900/30",
    text: "text-blue-700 dark:text-blue-300",
    border: "border-blue-200 dark:border-blue-800",
    dot: "bg-blue-500",
    hover: "hover:bg-blue-200 dark:hover:bg-blue-800/50"
  },
  indigo: {
    bg: "bg-indigo-100 dark:bg-indigo-900/30",
    text: "text-indigo-700 dark:text-indigo-300",
    border: "border-indigo-200 dark:border-indigo-800",
    dot: "bg-indigo-500",
    hover: "hover:bg-indigo-200 dark:hover:bg-indigo-800/50"
  },
  purple: {
    bg: "bg-purple-100 dark:bg-purple-900/30",
    text: "text-purple-700 dark:text-purple-300",
    border: "border-purple-200 dark:border-purple-800",
    dot: "bg-purple-500",
    hover: "hover:bg-purple-200 dark:hover:bg-purple-800/50"
  },
  pink: {
    bg: "bg-pink-100 dark:bg-pink-900/30",
    text: "text-pink-700 dark:text-pink-300",
    border: "border-pink-200 dark:border-pink-800",
    dot: "bg-pink-500",
    hover: "hover:bg-pink-200 dark:hover:bg-pink-800/50"
  },
  gray: {
    bg: "bg-gray-100 dark:bg-gray-800/50",
    text: "text-gray-700 dark:text-gray-300",
    border: "border-gray-200 dark:border-gray-700",
    dot: "bg-gray-500",
    hover: "hover:bg-gray-200 dark:hover:bg-gray-700/50"
  }
};
var TAG_COLOR_KEYS = [
  "red",
  "orange",
  "yellow",
  "green",
  "teal",
  "blue",
  "indigo",
  "purple",
  "pink",
  "gray"
];
function hashStringToTagColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return TAG_COLOR_KEYS[Math.abs(hash) % TAG_COLOR_KEYS.length];
}
var TAG_COLOR_OPTIONS = [
  { id: "red", label: "Red" },
  { id: "orange", label: "Orange" },
  { id: "yellow", label: "Yellow" },
  { id: "green", label: "Green" },
  { id: "teal", label: "Teal" },
  { id: "blue", label: "Blue" },
  { id: "indigo", label: "Indigo" },
  { id: "purple", label: "Purple" },
  { id: "pink", label: "Pink" },
  { id: "gray", label: "Gray" }
];

// src/components/PillToggle.tsx
import { jsx as jsx30, jsxs as jsxs25 } from "react/jsx-runtime";
function PillToggle({
  value,
  onChange,
  options,
  className,
  "aria-label": ariaLabel
}) {
  const containerRef = React19.useRef(null);
  const [indicatorStyle, setIndicatorStyle] = React19.useState({});
  React19.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const activeIndex = options.findIndex((o) => o.value === value);
    const buttons = container.querySelectorAll("[data-pill-option]");
    const activeButton = buttons[activeIndex];
    if (!activeButton) return;
    setIndicatorStyle({
      left: activeButton.offsetLeft,
      width: activeButton.offsetWidth,
      height: activeButton.offsetHeight
    });
  }, [value, options]);
  const activeOption = options.find((o) => o.value === value);
  const activeIndicatorCls = resolveActiveClass(activeOption);
  return /* @__PURE__ */ jsxs25(
    "div",
    {
      ref: containerRef,
      role: "radiogroup",
      "aria-label": ariaLabel,
      className: cn(
        "relative inline-flex rounded-full bg-muted p-0.5 text-xs font-medium",
        className
      ),
      children: [
        /* @__PURE__ */ jsx30(
          "div",
          {
            "aria-hidden": true,
            className: cn(
              "absolute rounded-full border transition-all duration-200 ease-out pointer-events-none",
              activeIndicatorCls
            ),
            style: indicatorStyle
          }
        ),
        options.map((option) => {
          const isActive = option.value === value;
          const activeTextCls = resolveActiveTextClass(option);
          return /* @__PURE__ */ jsxs25(
            "button",
            {
              type: "button",
              role: "radio",
              "aria-checked": isActive,
              "data-pill-option": true,
              onClick: () => onChange(option.value),
              className: cn(
                "relative z-[1] inline-flex items-center gap-1 rounded-full px-3 py-1 transition-colors duration-150",
                "focus-visible:outline-none",
                isActive ? activeTextCls : "text-muted-foreground hover:text-foreground"
              ),
              children: [
                option.icon && /* @__PURE__ */ jsx30("span", { className: "shrink-0", children: option.icon }),
                /* @__PURE__ */ jsx30("span", { children: option.label })
              ]
            },
            option.value
          );
        })
      ]
    }
  );
}
function resolveActiveClass(option) {
  if (!option) return "bg-foreground/10 border-transparent";
  if (option.color) {
    const c = TAG_COLORS[option.color];
    return cn(c.bg, c.border);
  }
  return cn(option.activeClassName ?? "bg-foreground/10", "border-transparent");
}
function resolveActiveTextClass(option) {
  if (option.color) return TAG_COLORS[option.color].text;
  return option.activeClassName ?? "text-foreground";
}

// src/components/SettingsRow.tsx
import { jsx as jsx31, jsxs as jsxs26 } from "react/jsx-runtime";
var SettingsRow = ({
  title,
  description,
  children,
  alignAction = "center",
  onPress,
  className
}) => {
  return /* @__PURE__ */ jsxs26(
    Flex,
    {
      align: alignAction === "center" ? "center" : "start",
      justify: "between",
      gap: "md",
      className: cn(
        "py-3 px-3 -mx-3 rounded-lg transition-colors hover:bg-muted/50",
        onPress && "cursor-pointer",
        className
      ),
      onClick: onPress,
      children: [
        /* @__PURE__ */ jsxs26(Box, { className: "flex-1 min-w-0", children: [
          /* @__PURE__ */ jsx31(Text, { weight: "medium", children: title }),
          description && /* @__PURE__ */ jsx31(Text, { size: "sm", className: "text-muted-foreground mt-1", children: description })
        ] }),
        /* @__PURE__ */ jsx31("div", { className: "shrink-0", onClick: (e) => e.stopPropagation(), children })
      ]
    }
  );
};

// src/components/Select.tsx
import React22 from "react";
import {
  Select as AriaSelect,
  SelectValue,
  Label as Label3,
  Button as Button2,
  Popover,
  ListBox,
  ListBoxItem,
  Text as Text4
} from "react-aria-components";
import { MagnifyingGlass as MagnifyingGlass2 } from "@phosphor-icons/react";

// src/components/Badge.tsx
import React21 from "react";

// src/components/TagBadge.tsx
import React20 from "react";
import { jsx as jsx32, jsxs as jsxs27 } from "react/jsx-runtime";
var sizeStyles = {
  sm: "px-1.5 py-0.5 text-xs gap-1",
  md: "px-2 py-0.5 text-xs gap-1.5",
  lg: "px-2.5 py-1 text-sm gap-2"
};
var dotSizes = {
  sm: "w-1.5 h-1.5",
  md: "w-2 h-2",
  lg: "w-2.5 h-2.5"
};
var TagBadge = React20.forwardRef(
  ({ name, color, size = "md", onRemove, icon, hideDot, tooltip, className }, ref) => {
    const colorStyles = TAG_COLORS[color];
    const labelRef = React20.useRef(null);
    const [isTruncated, setIsTruncated] = React20.useState(false);
    React20.useEffect(() => {
      const el = labelRef.current;
      if (!el) return;
      const measure = () => setIsTruncated(el.scrollWidth > el.clientWidth);
      measure();
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }, [name]);
    const resolvedTooltip = tooltip === null ? null : tooltip ?? (typeof name === "string" ? name : null);
    const showTooltip = isTruncated && resolvedTooltip !== null && resolvedTooltip !== void 0;
    const badge = /* @__PURE__ */ jsxs27(
      "span",
      {
        ref,
        "data-tag-color": color,
        "data-tag-size": size,
        className: cn(
          "inline-flex items-center rounded-none font-medium border transition-colors",
          colorStyles.bg,
          colorStyles.text,
          colorStyles.border,
          sizeStyles[size],
          className
        ),
        children: [
          icon ? /* @__PURE__ */ jsx32("span", { className: "shrink-0", children: icon }) : hideDot ? null : /* @__PURE__ */ jsx32("span", { className: cn("rounded-full shrink-0", colorStyles.dot, dotSizes[size]) }),
          /* @__PURE__ */ jsx32("span", { ref: labelRef, className: "truncate max-w-[150px]", children: name }),
          onRemove && /* @__PURE__ */ jsx32(
            "button",
            {
              type: "button",
              onClick: (e) => {
                e.stopPropagation();
                onRemove();
              },
              className: cn(
                "ml-0.5 rounded-full p-0.5 focus:outline-none focus:ring-1 focus:ring-offset-1",
                colorStyles.hover
              ),
              "aria-label": typeof name === "string" ? `Remove ${name} tag` : "Remove tag",
              children: /* @__PURE__ */ jsxs27(
                "svg",
                {
                  className: "h-3 w-3",
                  viewBox: "0 0 24 24",
                  fill: "none",
                  stroke: "currentColor",
                  strokeWidth: "2",
                  strokeLinecap: "round",
                  strokeLinejoin: "round",
                  children: [
                    /* @__PURE__ */ jsx32("line", { x1: "18", y1: "6", x2: "6", y2: "18" }),
                    /* @__PURE__ */ jsx32("line", { x1: "6", y1: "6", x2: "18", y2: "18" })
                  ]
                }
              )
            }
          )
        ]
      }
    );
    if (!showTooltip) return badge;
    return /* @__PURE__ */ jsxs27(TooltipTrigger, { children: [
      badge,
      /* @__PURE__ */ jsx32(Tooltip, { children: resolvedTooltip })
    ] });
  }
);
TagBadge.displayName = "TagBadge";

// src/components/Badge.tsx
import { jsx as jsx33 } from "react/jsx-runtime";
var legacyBadgeVariantToColor = {
  default: "blue",
  secondary: "gray",
  destructive: "red",
  outline: "gray",
  success: "green",
  warning: "yellow",
  "status-success": "green",
  "status-failed": "orange",
  "status-running": "purple",
  "status-queued": "gray",
  "status-waiting": "teal",
  "status-flaky": "yellow"
};
function badgeVariantToTagColor(variant) {
  return legacyBadgeVariantToColor[variant] ?? "gray";
}
var Badge = React21.forwardRef(
  ({ variant = "default", size = "md", className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx33("span", { ref, ...props, children: /* @__PURE__ */ jsx33(
      TagBadge,
      {
        name: children,
        color: legacyBadgeVariantToColor[variant],
        size,
        hideDot: true,
        className
      }
    ) });
  }
);
Badge.displayName = "Badge";

// src/components/Select.tsx
import { jsx as jsx34, jsxs as jsxs28 } from "react/jsx-runtime";
var defaultComparator = (a, b) => a.label.localeCompare(b.label, void 0, { sensitivity: "base", numeric: true });
var defaultFilter = (item, query) => item.label.toLowerCase().includes(query.toLowerCase());
function hasRichContent(item) {
  return Boolean(item.description || item.leadingIcon || item.tags && item.tags.length > 0);
}
function Select({
  label,
  description,
  placeholder = "Select an option",
  items,
  className,
  sort,
  searchable,
  searchPlaceholder = "Search...",
  filterFn,
  emptyText = "No matches",
  renderSelectedValue,
  ...props
}) {
  const isSearchable = searchable === true || searchable !== false && items.length > 5;
  const [searchQuery, setSearchQuery] = React22.useState("");
  const searchInputRef = React22.useRef(null);
  const sortedItems = React22.useMemo(() => {
    if (!sort) return items;
    const comparator = typeof sort === "function" ? sort : defaultComparator;
    return [...items].sort(comparator);
  }, [items, sort]);
  const filteredItems = React22.useMemo(() => {
    if (!isSearchable || !searchQuery) return sortedItems;
    const filter = filterFn ?? defaultFilter;
    return sortedItems.filter((item) => filter(item, searchQuery));
  }, [sortedItems, isSearchable, searchQuery, filterFn]);
  const disabledKeys = React22.useMemo(
    () => items.filter((i) => i.disabled).map((i) => i.id),
    [items]
  );
  const handleOpenChange = React22.useCallback(
    (isOpen) => {
      if (isOpen && isSearchable) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            searchInputRef.current?.focus();
          });
        });
      }
      if (!isOpen) {
        setSearchQuery("");
      }
      props.onOpenChange?.(isOpen);
    },
    [isSearchable, props.onOpenChange]
  );
  const [internalSelectedKey, setInternalSelectedKey] = React22.useState(
    props.selectedKey ?? props.defaultSelectedKey ?? null
  );
  React22.useEffect(() => {
    if (props.selectedKey !== void 0) {
      setInternalSelectedKey(props.selectedKey);
    }
  }, [props.selectedKey]);
  const handleSelectionChange = React22.useCallback(
    (key) => {
      setInternalSelectedKey(key);
      props.onSelectionChange?.(key);
    },
    [props.onSelectionChange]
  );
  const selectedItem = React22.useMemo(
    () => internalSelectedKey !== null && internalSelectedKey !== void 0 ? items.find((i) => i.id === String(internalSelectedKey)) : void 0,
    [internalSelectedKey, items]
  );
  const handleSearchKeyDown = React22.useCallback((e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const popover = searchInputRef.current?.closest("[data-rac]")?.parentElement;
      const firstOption = popover?.querySelector('[role="option"]');
      firstOption?.focus();
    }
    if (e.key.length === 1) {
      e.stopPropagation();
    }
  }, []);
  return /* @__PURE__ */ jsxs28(
    AriaSelect,
    {
      className: cn("flex flex-col gap-1.5", className),
      ...props,
      disabledKeys,
      onOpenChange: handleOpenChange,
      onSelectionChange: handleSelectionChange,
      children: [
        label && /* @__PURE__ */ jsxs28(Label3, { className: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70", children: [
          label,
          props.isRequired && /* @__PURE__ */ jsx34("span", { className: "text-destructive ml-0.5", children: "*" })
        ] }),
        /* @__PURE__ */ jsxs28(
          Button2,
          {
            "data-control": "",
            className: cn(
              "flex h-9 w-full items-center justify-between rounded-md border border-border bg-input px-3 py-2 text-sm",
              "placeholder:text-muted-foreground",
              "focus:outline-none focus-visible:border-ring/70",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "hover:border-ring/50 transition-colors"
            ),
            children: [
              /* @__PURE__ */ jsx34(SelectValue, { className: "text-left truncate min-w-0", children: ({ selectedText }) => {
                if (!selectedText) return /* @__PURE__ */ jsx34("span", { className: "text-muted-foreground", children: placeholder });
                if (selectedItem && renderSelectedValue) return renderSelectedValue(selectedItem);
                if (selectedItem && hasRichContent(selectedItem)) {
                  return /* @__PURE__ */ jsxs28("span", { className: "flex items-center gap-1.5 min-w-0", children: [
                    selectedItem.leadingIcon && /* @__PURE__ */ jsx34("span", { className: "shrink-0", children: selectedItem.leadingIcon }),
                    /* @__PURE__ */ jsx34("span", { className: "truncate", children: selectedItem.label }),
                    selectedItem.tags?.map((tag, i) => /* @__PURE__ */ jsx34(Badge, { variant: tag.variant || "secondary", size: "sm", children: tag.label }, i))
                  ] });
                }
                return selectedText;
              } }),
              /* @__PURE__ */ jsx34(
                "svg",
                {
                  className: "h-4 w-4 opacity-50",
                  xmlns: "http://www.w3.org/2000/svg",
                  viewBox: "0 0 24 24",
                  fill: "none",
                  stroke: "currentColor",
                  strokeWidth: "2",
                  strokeLinecap: "round",
                  strokeLinejoin: "round",
                  children: /* @__PURE__ */ jsx34("polyline", { points: "6 9 12 15 18 9" })
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs28(
          Popover,
          {
            className: cn(
              "w-[var(--trigger-width)] rounded-md border bg-popover text-popover-foreground outline-none",
              "shadow-lg",
              "data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95",
              "data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95"
            ),
            children: [
              isSearchable && /* @__PURE__ */ jsxs28("div", { className: "flex items-center gap-2 px-3 py-2 border-b border-border", children: [
                /* @__PURE__ */ jsx34(MagnifyingGlass2, { className: "h-4 w-4 text-muted-foreground shrink-0" }),
                /* @__PURE__ */ jsx34(
                  "input",
                  {
                    ref: searchInputRef,
                    type: "text",
                    className: "flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground",
                    placeholder: searchPlaceholder,
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                    onKeyDown: handleSearchKeyDown,
                    "aria-label": "Search options"
                  }
                )
              ] }),
              /* @__PURE__ */ jsx34(
                ListBox,
                {
                  className: "max-h-60 overflow-auto p-1",
                  items: filteredItems,
                  renderEmptyState: () => /* @__PURE__ */ jsx34("div", { className: "py-4 text-center text-sm text-muted-foreground", children: emptyText }),
                  children: (item) => /* @__PURE__ */ jsx34(
                    ListBoxItem,
                    {
                      className: cn(
                        "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 px-2 text-sm font-normal outline-none",
                        "transition-colors",
                        "data-[focused]:bg-accent data-[focused]:text-accent-foreground",
                        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                      ),
                      id: item.id,
                      textValue: item.label,
                      children: hasRichContent(item) ? /* @__PURE__ */ jsxs28("div", { className: "flex items-start gap-1.5 w-full min-w-0", children: [
                        item.leadingIcon && /* @__PURE__ */ jsx34("span", { className: "shrink-0 mt-0.5", children: item.leadingIcon }),
                        /* @__PURE__ */ jsxs28("div", { className: "flex-1 min-w-0", children: [
                          /* @__PURE__ */ jsxs28("div", { className: "flex items-center gap-1.5", children: [
                            /* @__PURE__ */ jsx34("span", { className: "truncate", children: item.label }),
                            item.tags?.map((tag, i) => /* @__PURE__ */ jsx34(Badge, { variant: tag.variant || "secondary", size: "sm", children: tag.label }, i))
                          ] }),
                          item.description && /* @__PURE__ */ jsx34("div", { className: "text-xs text-muted-foreground truncate", children: item.description })
                        ] })
                      ] }) : item.label
                    }
                  )
                }
              )
            ]
          }
        ),
        description && /* @__PURE__ */ jsx34(Text4, { slot: "description", className: "text-xs text-muted-foreground", children: description })
      ]
    }
  );
}

// src/components/ComboBox.tsx
import {
  ComboBox as AriaComboBox,
  Input as Input2,
  Label as Label4,
  Button as Button3,
  Popover as Popover2,
  ListBox as ListBox2,
  ListBoxItem as ListBoxItem2
} from "react-aria-components";
import { jsx as jsx35, jsxs as jsxs29 } from "react/jsx-runtime";
function ComboBox({
  label,
  description,
  placeholder = "Select an option",
  items,
  isLoading = false,
  emptyState,
  className,
  popoverClassName,
  listBoxClassName,
  renderItem,
  defaultFilter: defaultFilter3 = (textValue, inputValue) => textValue.toLowerCase().includes(inputValue.toLowerCase()),
  ...props
}) {
  const isControlDisabled = props.isDisabled ?? false;
  return /* @__PURE__ */ jsxs29(
    AriaComboBox,
    {
      className: cn("flex flex-col gap-1.5", className),
      menuTrigger: "focus",
      defaultFilter: defaultFilter3,
      ...props,
      children: [
        label && /* @__PURE__ */ jsxs29(Label4, { className: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70", children: [
          label,
          props.isRequired && /* @__PURE__ */ jsx35("span", { className: "text-destructive ml-0.5", children: "*" })
        ] }),
        /* @__PURE__ */ jsxs29(
          "div",
          {
            className: cn(
              "relative rounded-md border border-border bg-card shadow-sm transition-colors",
              "has-[:focus-visible]:border-ring/70",
              "data-[disabled=true]:opacity-50 data-[disabled=true]:cursor-not-allowed"
            ),
            "data-disabled": isControlDisabled ? "true" : void 0,
            children: [
              /* @__PURE__ */ jsx35(
                Input2,
                {
                  "data-control": "",
                  className: cn(
                    "h-9 w-full rounded-md border-none bg-transparent px-3 py-2 pr-9 text-sm",
                    "placeholder:text-muted-foreground",
                    "focus-visible:outline-none",
                    "disabled:cursor-not-allowed"
                  ),
                  placeholder
                }
              ),
              /* @__PURE__ */ jsx35(
                Button3,
                {
                  slot: "trigger",
                  "aria-label": "Toggle suggestions",
                  className: cn(
                    "absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  ),
                  children: /* @__PURE__ */ jsx35(
                    "svg",
                    {
                      className: "h-4 w-4",
                      xmlns: "http://www.w3.org/2000/svg",
                      viewBox: "0 0 24 24",
                      fill: "none",
                      stroke: "currentColor",
                      strokeWidth: "2",
                      strokeLinecap: "round",
                      strokeLinejoin: "round",
                      children: /* @__PURE__ */ jsx35("polyline", { points: "6 9 12 15 18 9" })
                    }
                  )
                }
              ),
              isLoading && /* @__PURE__ */ jsx35("span", { className: "absolute inset-y-0 right-8 flex items-center pr-1", children: /* @__PURE__ */ jsx35("span", { className: "inline-flex h-4 w-4 animate-spin rounded-full border-2 border-border border-t-transparent" }) })
            ]
          }
        ),
        description && /* @__PURE__ */ jsx35("p", { className: "text-xs text-muted-foreground", children: description }),
        /* @__PURE__ */ jsx35(
          Popover2,
          {
            className: cn(
              "w-[var(--trigger-width)] rounded-md border bg-popover text-popover-foreground outline-none",
              "shadow-lg",
              "data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95",
              "data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95",
              popoverClassName
            ),
            children: /* @__PURE__ */ jsx35(
              ListBox2,
              {
                className: cn("max-h-60 overflow-auto p-1", listBoxClassName),
                items,
                renderEmptyState: () => emptyState ?? /* @__PURE__ */ jsx35("div", { className: "px-3 py-2 text-sm text-muted-foreground", children: "No matches" }),
                children: (item) => {
                  const option = item;
                  const combinedText = option.textValue ?? [option.label, option.description].filter(Boolean).join(" ").trim();
                  return /* @__PURE__ */ jsx35(
                    ListBoxItem2,
                    {
                      id: option.id,
                      textValue: combinedText || option.label,
                      className: cn(
                        "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 px-2 text-sm font-normal outline-none",
                        "transition-colors",
                        "data-[focused]:bg-accent data-[focused]:text-accent-foreground",
                        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                      ),
                      isDisabled: option.disabled,
                      children: renderItem ? renderItem(item) : /* @__PURE__ */ jsxs29("div", { className: "flex flex-col", children: [
                        /* @__PURE__ */ jsx35("span", { children: option.label }),
                        option.description && /* @__PURE__ */ jsx35("span", { className: "text-xs text-muted-foreground", children: option.description })
                      ] })
                    }
                  );
                }
              }
            )
          }
        )
      ]
    }
  );
}

// src/components/MultiSelectComboBox.tsx
import React23, { useState as useState4, useMemo as useMemo3, useCallback as useCallback4, useRef } from "react";
import {
  ComboBox as AriaComboBox2,
  Input as Input3,
  Label as Label5,
  Button as Button4,
  Popover as Popover3,
  ListBox as ListBox3,
  ListBoxItem as ListBoxItem3
} from "react-aria-components";
import { jsx as jsx36, jsxs as jsxs30 } from "react/jsx-runtime";
function MultiSelectComboBox({
  label,
  "aria-label": ariaLabel,
  description,
  placeholder = "Search to add...",
  items,
  value,
  defaultValue = [],
  onChange,
  isLoading = false,
  isDisabled = false,
  emptyState,
  className,
  popoverClassName,
  listBoxClassName,
  renderItem,
  renderTag,
  hideSelectedTags = false,
  getTagColor,
  popoverFooter,
  inputValue: inputValueProp,
  onInputChange: onInputChangeProp
}) {
  const [internalSelected, setInternalSelected] = useState4(defaultValue);
  const [internalInputValue, setInternalInputValue] = useState4("");
  const inputValue = inputValueProp ?? internalInputValue;
  const selectedItems = value ?? internalSelected;
  const updateSelection = useCallback4(
    (newSelection) => {
      if (value === void 0) {
        setInternalSelected(newSelection);
      }
      onChange?.(newSelection);
    },
    [value, onChange]
  );
  const availableItems = useMemo3(() => {
    const selectedIds = new Set(selectedItems.map((item) => item.id));
    return items.filter((item) => !selectedIds.has(item.id));
  }, [items, selectedItems]);
  const filteredItems = useMemo3(() => {
    if (!inputValue) return availableItems;
    const lowerInput = inputValue.toLowerCase();
    return availableItems.filter(
      (item) => item.label.toLowerCase().includes(lowerInput) || item.description?.toLowerCase().includes(lowerInput) || item.textValue?.toLowerCase().includes(lowerInput)
    );
  }, [availableItems, inputValue]);
  const handleSelectionChange = (key) => {
    if (key === null) return;
    const selectedItem = items.find((item) => item.id === key);
    if (selectedItem && !selectedItems.some((item) => item.id === selectedItem.id)) {
      updateSelection([...selectedItems, selectedItem]);
      setInternalInputValue("");
      onInputChangeProp?.("");
    }
  };
  const handleRemove = (id) => {
    updateSelection(selectedItems.filter((item) => item.id !== id));
  };
  const handleKeyDown = (e) => {
    if (e.key === "Backspace" && !inputValue && selectedItems.length > 0) {
      updateSelection(selectedItems.slice(0, -1));
    }
  };
  const resolveTagColor = useCallback4(
    (item) => {
      if (getTagColor) return getTagColor(item);
      return hashStringToTagColor(item.label);
    },
    [getTagColor]
  );
  const triggerRef = useRef(null);
  return /* @__PURE__ */ jsxs30("div", { className: cn("flex flex-col gap-1.5", className), children: [
    label && /* @__PURE__ */ jsx36(Label5, { className: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70", children: label }),
    /* @__PURE__ */ jsxs30(
      AriaComboBox2,
      {
        "aria-label": ariaLabel,
        className: "flex flex-col gap-1.5",
        menuTrigger: "focus",
        inputValue,
        onInputChange: (v) => {
          setInternalInputValue(v);
          onInputChangeProp?.(v);
        },
        onSelectionChange: handleSelectionChange,
        selectedKey: null,
        isDisabled,
        allowsEmptyCollection: true,
        children: [
          /* @__PURE__ */ jsxs30(
            "div",
            {
              ref: triggerRef,
              className: cn(
                "relative flex flex-wrap items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1.5 transition-colors",
                "has-[:focus-visible]:border-ring/70",
                "min-h-9",
                isDisabled && "opacity-50 cursor-not-allowed"
              ),
              children: [
                (hideSelectedTags ? [] : selectedItems).map((item) => {
                  const color = resolveTagColor(item);
                  const colorStyles = TAG_COLORS[color];
                  if (renderTag) {
                    const onRemove = isDisabled ? void 0 : () => handleRemove(item.id);
                    return /* @__PURE__ */ jsx36(React23.Fragment, { children: renderTag(item, onRemove) }, item.id);
                  }
                  return /* @__PURE__ */ jsxs30(
                    "span",
                    {
                      className: cn(
                        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium transition-colors",
                        colorStyles.bg,
                        colorStyles.text,
                        colorStyles.border,
                        isDisabled && "opacity-50"
                      ),
                      children: [
                        /* @__PURE__ */ jsx36("span", { className: cn("h-1.5 w-1.5 rounded-full shrink-0", colorStyles.dot) }),
                        /* @__PURE__ */ jsx36("span", { className: "truncate max-w-[120px]", children: item.label }),
                        !isDisabled && /* @__PURE__ */ jsx36(
                          "button",
                          {
                            type: "button",
                            onClick: (e) => {
                              e.stopPropagation();
                              handleRemove(item.id);
                            },
                            className: cn(
                              "ml-0.5 rounded-full p-0.5 focus:outline-none",
                              colorStyles.hover
                            ),
                            "aria-label": `Remove ${item.label}`,
                            children: /* @__PURE__ */ jsxs30(
                              "svg",
                              {
                                className: "h-3 w-3",
                                viewBox: "0 0 24 24",
                                fill: "none",
                                stroke: "currentColor",
                                strokeWidth: "2",
                                strokeLinecap: "round",
                                strokeLinejoin: "round",
                                children: [
                                  /* @__PURE__ */ jsx36("line", { x1: "18", y1: "6", x2: "6", y2: "18" }),
                                  /* @__PURE__ */ jsx36("line", { x1: "6", y1: "6", x2: "18", y2: "18" })
                                ]
                              }
                            )
                          }
                        )
                      ]
                    },
                    item.id
                  );
                }),
                /* @__PURE__ */ jsx36(
                  Input3,
                  {
                    className: cn(
                      "flex-1 min-w-[100px] h-6 border-none bg-transparent text-sm",
                      "placeholder:text-muted-foreground",
                      // The bordered Group above carries the focus outline; suppress the
                      // inner input's own focus chrome (incl. the cofactor-brand
                      // `box-shadow` ring) so we don't get a double outline.
                      "focus-visible:outline-none focus-visible:!border-transparent focus-visible:!shadow-none",
                      "disabled:cursor-not-allowed"
                    ),
                    placeholder: hideSelectedTags || selectedItems.length === 0 ? placeholder : "",
                    onKeyDown: handleKeyDown
                  }
                ),
                /* @__PURE__ */ jsxs30("div", { className: "flex items-center gap-1 ml-auto shrink-0", children: [
                  isLoading && /* @__PURE__ */ jsx36("span", { className: "inline-flex h-4 w-4 animate-spin rounded-full border-2 border-border border-t-transparent" }),
                  /* @__PURE__ */ jsx36(
                    Button4,
                    {
                      slot: "trigger",
                      "aria-label": "Toggle suggestions",
                      className: cn(
                        "flex items-center px-1 text-muted-foreground",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                      ),
                      children: /* @__PURE__ */ jsx36(
                        "svg",
                        {
                          className: "h-4 w-4",
                          xmlns: "http://www.w3.org/2000/svg",
                          viewBox: "0 0 24 24",
                          fill: "none",
                          stroke: "currentColor",
                          strokeWidth: "2",
                          strokeLinecap: "round",
                          strokeLinejoin: "round",
                          children: /* @__PURE__ */ jsx36("polyline", { points: "6 9 12 15 18 9" })
                        }
                      )
                    }
                  )
                ] })
              ]
            }
          ),
          /* @__PURE__ */ jsxs30(
            Popover3,
            {
              triggerRef,
              className: cn(
                "w-[var(--trigger-width)] rounded-md border bg-popover text-popover-foreground shadow-md",
                "data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95",
                "data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95",
                "data-[placement=bottom]:slide-in-from-top-2",
                "data-[placement=top]:slide-in-from-bottom-2",
                popoverClassName
              ),
              children: [
                /* @__PURE__ */ jsx36(
                  ListBox3,
                  {
                    className: cn("max-h-60 overflow-auto p-1", listBoxClassName),
                    items: filteredItems,
                    renderEmptyState: () => emptyState ?? /* @__PURE__ */ jsx36("div", { className: "px-3 py-2 text-sm text-muted-foreground", children: inputValue ? "No matches" : "No more options" }),
                    children: (item) => {
                      const option = item;
                      const combinedText = option.textValue ?? [option.label, option.description].filter(Boolean).join(" ").trim();
                      return /* @__PURE__ */ jsx36(
                        ListBoxItem3,
                        {
                          id: option.id,
                          textValue: combinedText || option.label,
                          className: cn(
                            "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 px-2 text-sm outline-none",
                            "data-[focused]:bg-accent data-[focused]:text-accent-foreground",
                            "data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                          ),
                          isDisabled: option.disabled,
                          children: renderItem ? renderItem(item) : /* @__PURE__ */ jsxs30("div", { className: "flex flex-col", children: [
                            /* @__PURE__ */ jsx36("span", { children: option.label }),
                            option.description && /* @__PURE__ */ jsx36("span", { className: "text-xs text-muted-foreground", children: option.description })
                          ] })
                        }
                      );
                    }
                  }
                ),
                popoverFooter
              ]
            }
          )
        ]
      }
    ),
    description && /* @__PURE__ */ jsx36("p", { className: "text-xs text-muted-foreground", children: description })
  ] });
}

// src/components/DatePicker.tsx
import {
  DatePicker as AriaDatePicker,
  DateInput,
  DateSegment,
  Label as Label6,
  Button as Button5,
  Calendar as Calendar2,
  CalendarGrid,
  CalendarCell,
  Heading as Heading2,
  Popover as Popover4,
  Dialog,
  Group,
  Text as Text5
} from "react-aria-components";
import { Calendar as CalendarIcon, CaretLeft as CaretLeft3, CaretRight as CaretRight5 } from "@phosphor-icons/react";
import { jsx as jsx37, jsxs as jsxs31 } from "react/jsx-runtime";
function DatePicker({
  label,
  description,
  errorMessage,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs31(
    AriaDatePicker,
    {
      ...props,
      className: cn("flex flex-col gap-1.5", className),
      isInvalid: !!errorMessage,
      children: [
        label && /* @__PURE__ */ jsx37(Label6, { className: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70", children: label }),
        /* @__PURE__ */ jsxs31(
          Group,
          {
            "data-control": "",
            className: cn(
              "flex h-9 w-full items-center rounded-md border border-border bg-card px-3 py-2 text-sm shadow-sm",
              "focus-within:border-ring/70",
              "data-[invalid]:border-destructive",
              "hover:border-ring/50 transition-colors"
            ),
            children: [
              /* @__PURE__ */ jsx37(DateInput, { className: "flex flex-1 items-center", children: (segment) => /* @__PURE__ */ jsx37(
                DateSegment,
                {
                  segment,
                  className: cn(
                    "rounded-sm px-1 tabular-nums outline-none",
                    "focus:bg-accent focus:text-accent-foreground",
                    "data-[placeholder]:text-muted-foreground",
                    "data-[type=literal]:px-0"
                  )
                }
              ) }),
              /* @__PURE__ */ jsx37(
                Button5,
                {
                  className: cn(
                    "ml-2 text-muted-foreground hover:text-foreground",
                    "transition-colors outline-none",
                    "focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm"
                  ),
                  children: /* @__PURE__ */ jsx37(CalendarIcon, { className: "h-4 w-4" })
                }
              )
            ]
          }
        ),
        description && /* @__PURE__ */ jsx37(Text5, { slot: "description", className: "text-xs text-muted-foreground", children: description }),
        errorMessage && /* @__PURE__ */ jsx37(Text5, { slot: "errorMessage", className: "text-xs text-destructive", children: errorMessage }),
        /* @__PURE__ */ jsx37(
          Popover4,
          {
            className: cn(
              "rounded-md border bg-popover text-popover-foreground shadow-md",
              "data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95",
              "data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95",
              "data-[placement=bottom]:slide-in-from-top-2",
              "data-[placement=top]:slide-in-from-bottom-2"
            ),
            children: /* @__PURE__ */ jsx37(Dialog, { className: "p-4", children: /* @__PURE__ */ jsxs31(Calendar2, { children: [
              /* @__PURE__ */ jsxs31("header", { className: "flex items-center justify-between pb-4", children: [
                /* @__PURE__ */ jsx37(
                  Button5,
                  {
                    slot: "previous",
                    className: cn(
                      "h-7 w-7 flex items-center justify-center rounded-sm",
                      "hover:bg-accent hover:text-accent-foreground",
                      "outline-none focus:ring-2 focus:ring-ring",
                      "transition-colors"
                    ),
                    children: /* @__PURE__ */ jsx37(CaretLeft3, { className: "h-4 w-4" })
                  }
                ),
                /* @__PURE__ */ jsx37(Heading2, { className: "text-sm font-medium" }),
                /* @__PURE__ */ jsx37(
                  Button5,
                  {
                    slot: "next",
                    className: cn(
                      "h-7 w-7 flex items-center justify-center rounded-sm",
                      "hover:bg-accent hover:text-accent-foreground",
                      "outline-none focus:ring-2 focus:ring-ring",
                      "transition-colors"
                    ),
                    children: /* @__PURE__ */ jsx37(CaretRight5, { className: "h-4 w-4" })
                  }
                )
              ] }),
              /* @__PURE__ */ jsx37(CalendarGrid, { className: "border-collapse space-y-1", children: (date) => /* @__PURE__ */ jsx37(
                CalendarCell,
                {
                  date,
                  className: cn(
                    "h-9 w-9 text-center text-sm relative flex items-center justify-center",
                    "rounded-sm outline-none",
                    "hover:bg-accent hover:text-accent-foreground",
                    "focus:bg-accent focus:text-accent-foreground",
                    "data-[selected]:bg-primary data-[selected]:text-primary-foreground",
                    "data-[selected]:hover:bg-primary data-[selected]:hover:text-primary-foreground",
                    "data-[outside-month]:text-muted-foreground data-[outside-month]:opacity-50",
                    "data-[disabled]:text-muted-foreground data-[disabled]:opacity-50",
                    "data-[unavailable]:text-destructive-foreground data-[unavailable]:line-through",
                    "transition-colors cursor-default"
                  )
                }
              ) })
            ] }) })
          }
        )
      ]
    }
  );
}

// src/components/DateRangePicker.tsx
import { useContext as useContext2 } from "react";
import {
  DateRangePicker as AriaDateRangePicker,
  DateInput as DateInput2,
  DateSegment as DateSegment2,
  FieldError,
  Label as Label7,
  Button as Button6,
  RangeCalendar,
  RangeCalendarStateContext,
  CalendarGrid as CalendarGrid2,
  CalendarCell as CalendarCell2,
  Heading as Heading3,
  Popover as Popover5,
  Dialog as Dialog2,
  Group as Group2,
  Text as Text6
} from "react-aria-components";
import { Calendar as CalendarIcon2, CaretLeft as CaretLeft4, CaretRight as CaretRight6 } from "@phosphor-icons/react";
import { jsx as jsx38, jsxs as jsxs32 } from "react/jsx-runtime";
function SameDayCalendarCell({ date, className }) {
  const state = useContext2(RangeCalendarStateContext);
  const handlePointerDown = (e) => {
    if (state.anchorDate && date.compare(state.anchorDate) === 0) {
      e.stopPropagation();
      state.setValue({ start: date, end: date });
      state.setAnchorDate(null);
    }
  };
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    /* @__PURE__ */ jsx38("span", { onPointerDown: handlePointerDown, children: /* @__PURE__ */ jsx38(CalendarCell2, { date, className }) })
  );
}
function DateRangePicker({
  label,
  description,
  errorMessage,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs32(
    AriaDateRangePicker,
    {
      ...props,
      className: cn("flex flex-col gap-1.5", className),
      validationBehavior: "aria",
      isInvalid: errorMessage ? true : props.isInvalid,
      children: [
        label && /* @__PURE__ */ jsx38(Label7, { className: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70", children: label }),
        /* @__PURE__ */ jsxs32(
          Group2,
          {
            "data-control": "",
            className: cn(
              "flex h-9 w-full items-center rounded-md border border-border bg-card px-3 py-2 text-sm",
              "focus-within:border-ring/70",
              "data-[invalid]:border-destructive",
              "hover:border-ring/50 transition-colors"
            ),
            children: [
              /* @__PURE__ */ jsx38(
                DateInput2,
                {
                  slot: "start",
                  className: "flex flex-1 items-center",
                  children: (segment) => /* @__PURE__ */ jsx38(
                    DateSegment2,
                    {
                      segment,
                      className: cn(
                        "rounded-sm px-1 tabular-nums outline-none",
                        "focus:bg-accent focus:text-accent-foreground",
                        "data-[placeholder]:text-muted-foreground",
                        "data-[type=literal]:px-0"
                      )
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsx38(
                "span",
                {
                  "aria-hidden": "true",
                  className: "px-2 text-muted-foreground",
                  children: "\u2013"
                }
              ),
              /* @__PURE__ */ jsx38(
                DateInput2,
                {
                  slot: "end",
                  className: "flex flex-1 items-center",
                  children: (segment) => /* @__PURE__ */ jsx38(
                    DateSegment2,
                    {
                      segment,
                      className: cn(
                        "rounded-sm px-1 tabular-nums outline-none",
                        "focus:bg-accent focus:text-accent-foreground",
                        "data-[placeholder]:text-muted-foreground",
                        "data-[type=literal]:px-0"
                      )
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsx38(
                Button6,
                {
                  className: cn(
                    "ml-2 text-muted-foreground hover:text-foreground",
                    "transition-colors outline-none",
                    "focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm"
                  ),
                  children: /* @__PURE__ */ jsx38(CalendarIcon2, { className: "h-4 w-4" })
                }
              )
            ]
          }
        ),
        description && /* @__PURE__ */ jsx38(Text6, { slot: "description", className: "text-xs text-muted-foreground", children: description }),
        errorMessage ? /* @__PURE__ */ jsx38(Text6, { slot: "errorMessage", className: "text-xs text-destructive", children: errorMessage }) : /* @__PURE__ */ jsx38(FieldError, { className: "text-xs text-destructive" }),
        /* @__PURE__ */ jsx38(
          Popover5,
          {
            className: cn(
              "rounded-md border bg-popover text-popover-foreground shadow-md",
              "data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95",
              "data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95",
              "data-[placement=bottom]:slide-in-from-top-2",
              "data-[placement=top]:slide-in-from-bottom-2"
            ),
            children: /* @__PURE__ */ jsx38(Dialog2, { className: "p-4", children: /* @__PURE__ */ jsxs32(RangeCalendar, { children: [
              /* @__PURE__ */ jsxs32("header", { className: "flex items-center justify-between pb-4", children: [
                /* @__PURE__ */ jsx38(
                  Button6,
                  {
                    slot: "previous",
                    className: cn(
                      "h-7 w-7 flex items-center justify-center rounded-sm",
                      "hover:bg-accent hover:text-accent-foreground",
                      "outline-none focus:ring-2 focus:ring-ring",
                      "transition-colors"
                    ),
                    children: /* @__PURE__ */ jsx38(CaretLeft4, { className: "h-4 w-4" })
                  }
                ),
                /* @__PURE__ */ jsx38(Heading3, { className: "text-sm font-medium" }),
                /* @__PURE__ */ jsx38(
                  Button6,
                  {
                    slot: "next",
                    className: cn(
                      "h-7 w-7 flex items-center justify-center rounded-sm",
                      "hover:bg-accent hover:text-accent-foreground",
                      "outline-none focus:ring-2 focus:ring-ring",
                      "transition-colors"
                    ),
                    children: /* @__PURE__ */ jsx38(CaretRight6, { className: "h-4 w-4" })
                  }
                )
              ] }),
              /* @__PURE__ */ jsx38(CalendarGrid2, { className: "border-collapse space-y-1", children: (date) => /* @__PURE__ */ jsx38(
                SameDayCalendarCell,
                {
                  date,
                  className: cn(
                    "h-9 w-9 text-center text-sm relative flex items-center justify-center",
                    "rounded-sm outline-none",
                    "hover:bg-accent hover:text-accent-foreground",
                    "focus:bg-accent focus:text-accent-foreground",
                    // Selection state
                    "data-[selected]:bg-primary data-[selected]:text-primary-foreground",
                    "data-[selected]:hover:bg-primary data-[selected]:hover:text-primary-foreground",
                    // Range state - between start and end
                    "data-[selection-start]:bg-primary data-[selection-start]:text-primary-foreground",
                    "data-[selection-end]:bg-primary data-[selection-end]:text-primary-foreground",
                    // Outside current month
                    "data-[outside-month]:text-muted-foreground data-[outside-month]:opacity-50",
                    // Disabled state
                    "data-[disabled]:text-muted-foreground data-[disabled]:opacity-50",
                    // Unavailable state
                    "data-[unavailable]:text-destructive-foreground data-[unavailable]:line-through",
                    "transition-colors cursor-default"
                  )
                }
              ) })
            ] }) })
          }
        )
      ]
    }
  );
}

// src/components/TimeField.tsx
import {
  TimeField as AriaTimeField,
  DateInput as DateInput3,
  DateSegment as DateSegment3,
  Label as Label8,
  Text as Text7
} from "react-aria-components";
import { jsx as jsx39, jsxs as jsxs33 } from "react/jsx-runtime";
function TimeField({
  label,
  description,
  errorMessage,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs33(
    AriaTimeField,
    {
      ...props,
      className: cn("flex flex-col gap-1.5", className),
      isInvalid: !!errorMessage,
      children: [
        label && /* @__PURE__ */ jsx39(Label8, { className: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70", children: label }),
        /* @__PURE__ */ jsx39(
          DateInput3,
          {
            "data-control": "",
            className: cn(
              "flex h-9 w-full items-center rounded-md border border-border bg-card px-3 py-2 text-sm shadow-sm",
              "focus-within:border-ring/70",
              "data-[invalid]:border-destructive",
              "hover:border-ring/50 transition-colors"
            ),
            children: (segment) => /* @__PURE__ */ jsx39(
              DateSegment3,
              {
                segment,
                className: cn(
                  "rounded-sm px-1 tabular-nums outline-none",
                  "focus:bg-accent focus:text-accent-foreground",
                  "data-[placeholder]:text-muted-foreground",
                  "data-[type=literal]:px-0"
                )
              }
            )
          }
        ),
        description && /* @__PURE__ */ jsx39(Text7, { slot: "description", className: "text-xs text-muted-foreground", children: description }),
        errorMessage && /* @__PURE__ */ jsx39(Text7, { slot: "errorMessage", className: "text-xs text-destructive", children: errorMessage })
      ]
    }
  );
}

// src/components/DateTimePicker.tsx
import React25 from "react";
import {
  DatePicker as AriaDatePicker2,
  DateInput as DateInput4,
  DateSegment as DateSegment4,
  Label as Label9,
  Button as Button7,
  Calendar as Calendar3,
  CalendarGrid as CalendarGrid3,
  CalendarCell as CalendarCell3,
  Heading as Heading4,
  Popover as Popover6,
  Dialog as Dialog3,
  Group as Group3,
  Text as Text8
} from "react-aria-components";
import { Calendar as CalendarIcon3, CaretLeft as CaretLeft5, CaretRight as CaretRight7 } from "@phosphor-icons/react";
import { Time, toCalendarDateTime } from "@internationalized/date";
import { Fragment as Fragment6, jsx as jsx40, jsxs as jsxs34 } from "react/jsx-runtime";
function TimeInput({ value, onChange, min, max, padStart = 2, disabled }) {
  const { inputProps, isOutOfRange } = useNumberInput({
    value,
    onChange: (v) => onChange(v ?? min),
    min,
    max
  });
  const displayValue = inputProps.value !== "" ? inputProps.value.padStart(padStart, "0") : "";
  return /* @__PURE__ */ jsx40(
    "input",
    {
      ...inputProps,
      value: displayValue,
      className: cn(
        "w-12 h-8 text-center rounded-sm border bg-card",
        "focus:outline-none focus:ring-2 focus:ring-ring",
        "tabular-nums text-sm",
        isOutOfRange ? "border-warning" : "border-border"
      ),
      disabled
    }
  );
}
function DateTimePicker({
  label,
  description,
  errorMessage,
  className,
  value,
  onChange,
  isDisabled,
  isReadOnly,
  isRequired,
  minValue,
  maxValue,
  granularity = "minute",
  ...props
}) {
  const [timeValue, setTimeValue] = React25.useState(
    value ? new Time(value.hour, value.minute, value.second) : new Time(9, 0)
  );
  const handleDateChange = (newDate) => {
    if (!newDate) {
      onChange?.(null);
      return;
    }
    const dateTime = toCalendarDateTime(newDate, timeValue);
    onChange?.(dateTime);
  };
  const handleTimeChange = (newTime) => {
    setTimeValue(newTime);
    if (value) {
      const updated = value.set({
        hour: newTime.hour,
        minute: newTime.minute,
        second: newTime.second
      });
      onChange?.(updated);
    }
  };
  React25.useEffect(() => {
    if (value) {
      setTimeValue(new Time(value.hour, value.minute, value.second));
    }
  }, [value]);
  return /* @__PURE__ */ jsxs34(
    AriaDatePicker2,
    {
      value,
      onChange: handleDateChange,
      isDisabled,
      isReadOnly,
      isRequired,
      minValue,
      maxValue,
      granularity: "minute",
      className: cn("flex flex-col gap-1.5", className),
      isInvalid: !!errorMessage,
      ...props,
      children: [
        label && /* @__PURE__ */ jsx40(Label9, { className: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70", children: label }),
        /* @__PURE__ */ jsxs34(
          Group3,
          {
            "data-control": "",
            className: cn(
              "flex h-9 w-full items-center rounded-md border border-border bg-card px-3 py-2 text-sm shadow-sm",
              "focus-within:border-ring/70",
              "data-[invalid]:border-destructive",
              "hover:border-ring/50 transition-colors"
            ),
            children: [
              /* @__PURE__ */ jsx40(DateInput4, { className: "flex flex-1 items-center gap-1", children: (segment) => /* @__PURE__ */ jsx40(
                DateSegment4,
                {
                  segment,
                  className: cn(
                    "rounded-sm px-1 tabular-nums outline-none",
                    "focus:bg-accent focus:text-accent-foreground",
                    "data-[placeholder]:text-muted-foreground",
                    "data-[type=literal]:px-0"
                  )
                }
              ) }),
              /* @__PURE__ */ jsx40(
                Button7,
                {
                  className: cn(
                    "ml-2 text-muted-foreground hover:text-foreground",
                    "transition-colors outline-none",
                    "focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm"
                  ),
                  children: /* @__PURE__ */ jsx40(CalendarIcon3, { className: "h-4 w-4" })
                }
              )
            ]
          }
        ),
        description && /* @__PURE__ */ jsx40(Text8, { slot: "description", className: "text-xs text-muted-foreground", children: description }),
        errorMessage && /* @__PURE__ */ jsx40(Text8, { slot: "errorMessage", className: "text-xs text-destructive", children: errorMessage }),
        /* @__PURE__ */ jsx40(
          Popover6,
          {
            className: cn(
              "rounded-md border bg-popover text-popover-foreground shadow-md",
              "data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95",
              "data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95",
              "data-[placement=bottom]:slide-in-from-top-2",
              "data-[placement=top]:slide-in-from-bottom-2"
            ),
            children: /* @__PURE__ */ jsxs34(Dialog3, { className: "p-4 space-y-4", children: [
              /* @__PURE__ */ jsxs34(Calendar3, { children: [
                /* @__PURE__ */ jsxs34("header", { className: "flex items-center justify-between pb-4", children: [
                  /* @__PURE__ */ jsx40(
                    Button7,
                    {
                      slot: "previous",
                      className: cn(
                        "h-7 w-7 flex items-center justify-center rounded-sm",
                        "hover:bg-accent hover:text-accent-foreground",
                        "outline-none focus:ring-2 focus:ring-ring",
                        "transition-colors"
                      ),
                      children: /* @__PURE__ */ jsx40(CaretLeft5, { className: "h-4 w-4" })
                    }
                  ),
                  /* @__PURE__ */ jsx40(Heading4, { className: "text-sm font-medium" }),
                  /* @__PURE__ */ jsx40(
                    Button7,
                    {
                      slot: "next",
                      className: cn(
                        "h-7 w-7 flex items-center justify-center rounded-sm",
                        "hover:bg-accent hover:text-accent-foreground",
                        "outline-none focus:ring-2 focus:ring-ring",
                        "transition-colors"
                      ),
                      children: /* @__PURE__ */ jsx40(CaretRight7, { className: "h-4 w-4" })
                    }
                  )
                ] }),
                /* @__PURE__ */ jsx40(CalendarGrid3, { className: "border-collapse space-y-1", children: (date) => /* @__PURE__ */ jsx40(
                  CalendarCell3,
                  {
                    date,
                    className: cn(
                      "h-9 w-9 text-center text-sm relative flex items-center justify-center",
                      "rounded-sm outline-none",
                      "hover:bg-accent hover:text-accent-foreground",
                      "focus:bg-accent focus:text-accent-foreground",
                      "data-[selected]:bg-primary data-[selected]:text-primary-foreground",
                      "data-[selected]:hover:bg-primary data-[selected]:hover:text-primary-foreground",
                      "data-[outside-month]:text-muted-foreground data-[outside-month]:opacity-50",
                      "data-[disabled]:text-muted-foreground data-[disabled]:opacity-50",
                      "data-[unavailable]:text-destructive-foreground data-[unavailable]:line-through",
                      "transition-colors cursor-default"
                    )
                  }
                ) })
              ] }),
              /* @__PURE__ */ jsx40("div", { className: "border-t pt-4", children: /* @__PURE__ */ jsxs34("div", { className: "flex items-center gap-3", children: [
                /* @__PURE__ */ jsx40(Text8, { className: "text-sm font-medium text-muted-foreground min-w-[60px]", children: "Time:" }),
                /* @__PURE__ */ jsxs34("div", { className: "flex items-center gap-1", children: [
                  /* @__PURE__ */ jsx40(
                    TimeInput,
                    {
                      value: timeValue.hour,
                      onChange: (hour) => handleTimeChange(new Time(hour, timeValue.minute, timeValue.second)),
                      min: 0,
                      max: 23,
                      disabled: isDisabled || isReadOnly
                    }
                  ),
                  /* @__PURE__ */ jsx40(Text8, { className: "text-muted-foreground", children: ":" }),
                  /* @__PURE__ */ jsx40(
                    TimeInput,
                    {
                      value: timeValue.minute,
                      onChange: (minute) => handleTimeChange(new Time(timeValue.hour, minute, timeValue.second)),
                      min: 0,
                      max: 59,
                      disabled: isDisabled || isReadOnly
                    }
                  ),
                  granularity === "second" && /* @__PURE__ */ jsxs34(Fragment6, { children: [
                    /* @__PURE__ */ jsx40(Text8, { className: "text-muted-foreground", children: ":" }),
                    /* @__PURE__ */ jsx40(
                      TimeInput,
                      {
                        value: timeValue.second,
                        onChange: (second) => handleTimeChange(new Time(timeValue.hour, timeValue.minute, second)),
                        min: 0,
                        max: 59,
                        disabled: isDisabled || isReadOnly
                      }
                    )
                  ] })
                ] })
              ] }) })
            ] })
          }
        )
      ]
    }
  );
}

// src/components/DateTimeRangePicker.tsx
import React26 from "react";
import {
  DateRangePicker as AriaDateRangePicker2,
  DateInput as DateInput5,
  DateSegment as DateSegment5,
  Label as Label10,
  Button as Button8,
  RangeCalendar as RangeCalendar2,
  CalendarGrid as CalendarGrid4,
  CalendarCell as CalendarCell4,
  Heading as Heading5,
  Popover as Popover7,
  Dialog as Dialog4,
  Group as Group4,
  Text as Text9
} from "react-aria-components";
import { Calendar as CalendarIcon4, CaretLeft as CaretLeft6, CaretRight as CaretRight8 } from "@phosphor-icons/react";
import { Time as Time2, toCalendarDateTime as toCalendarDateTime2 } from "@internationalized/date";
import { Fragment as Fragment7, jsx as jsx41, jsxs as jsxs35 } from "react/jsx-runtime";
function TimeInput2({ value, onChange, min, max, padStart = 2, disabled }) {
  const { inputProps, isOutOfRange } = useNumberInput({
    value,
    onChange: (v) => onChange(v ?? min),
    min,
    max
  });
  const displayValue = inputProps.value !== "" ? inputProps.value.padStart(padStart, "0") : "";
  return /* @__PURE__ */ jsx41(
    "input",
    {
      ...inputProps,
      value: displayValue,
      className: cn(
        "w-12 h-8 text-center rounded-sm border bg-card",
        "focus:outline-none focus:ring-2 focus:ring-ring",
        "tabular-nums text-sm",
        isOutOfRange ? "border-warning" : "border-border"
      ),
      disabled
    }
  );
}
function DateTimeRangePicker({
  label,
  description,
  errorMessage,
  className,
  value,
  onChange,
  isDisabled,
  isReadOnly,
  isRequired,
  minValue,
  maxValue,
  granularity = "minute",
  ...props
}) {
  const [startTime, setStartTime] = React26.useState(
    value?.start ? new Time2(value.start.hour, value.start.minute, value.start.second) : new Time2(9, 0)
  );
  const [endTime, setEndTime] = React26.useState(
    value?.end ? new Time2(value.end.hour, value.end.minute, value.end.second) : new Time2(17, 0)
  );
  const handleDateRangeChange = (newRange) => {
    if (!newRange) {
      onChange?.(null);
      return;
    }
    const startDateTime = toCalendarDateTime2(newRange.start, startTime);
    const endDateTime = toCalendarDateTime2(newRange.end, endTime);
    onChange?.({
      start: startDateTime,
      end: endDateTime
    });
  };
  const handleStartTimeChange = (newTime) => {
    setStartTime(newTime);
    if (value?.start) {
      const updatedStart = value.start.set({
        hour: newTime.hour,
        minute: newTime.minute,
        second: newTime.second
      });
      onChange?.({
        start: updatedStart,
        end: value.end
      });
    }
  };
  const handleEndTimeChange = (newTime) => {
    setEndTime(newTime);
    if (value?.end) {
      const updatedEnd = value.end.set({
        hour: newTime.hour,
        minute: newTime.minute,
        second: newTime.second
      });
      onChange?.({
        start: value.start,
        end: updatedEnd
      });
    }
  };
  React26.useEffect(() => {
    if (value?.start) {
      setStartTime(new Time2(value.start.hour, value.start.minute, value.start.second));
    }
    if (value?.end) {
      setEndTime(new Time2(value.end.hour, value.end.minute, value.end.second));
    }
  }, [value]);
  return /* @__PURE__ */ jsxs35(
    AriaDateRangePicker2,
    {
      value,
      onChange: handleDateRangeChange,
      isDisabled,
      isReadOnly,
      isRequired,
      minValue,
      maxValue,
      granularity: "minute",
      className: cn("flex flex-col gap-1.5", className),
      isInvalid: !!errorMessage,
      ...props,
      children: [
        label && /* @__PURE__ */ jsx41(Label10, { className: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70", children: label }),
        /* @__PURE__ */ jsxs35(
          Group4,
          {
            "data-control": "",
            className: cn(
              "flex h-9 w-full items-center rounded-md border border-border bg-card px-3 py-2 text-sm shadow-sm",
              "focus-within:border-ring/70",
              "data-[invalid]:border-destructive",
              "hover:border-ring/50 transition-colors"
            ),
            children: [
              /* @__PURE__ */ jsx41(DateInput5, { slot: "start", className: "flex flex-1 items-center", children: (segment) => /* @__PURE__ */ jsx41(
                DateSegment5,
                {
                  segment,
                  className: cn(
                    "rounded-sm px-1 tabular-nums outline-none",
                    "focus:bg-accent focus:text-accent-foreground",
                    "data-[placeholder]:text-muted-foreground",
                    "data-[type=literal]:px-0"
                  )
                }
              ) }),
              /* @__PURE__ */ jsx41("span", { "aria-hidden": "true", className: "px-2 text-muted-foreground", children: "\u2013" }),
              /* @__PURE__ */ jsx41(DateInput5, { slot: "end", className: "flex flex-1 items-center", children: (segment) => /* @__PURE__ */ jsx41(
                DateSegment5,
                {
                  segment,
                  className: cn(
                    "rounded-sm px-1 tabular-nums outline-none",
                    "focus:bg-accent focus:text-accent-foreground",
                    "data-[placeholder]:text-muted-foreground",
                    "data-[type=literal]:px-0"
                  )
                }
              ) }),
              /* @__PURE__ */ jsx41(
                Button8,
                {
                  className: cn(
                    "ml-2 text-muted-foreground hover:text-foreground",
                    "transition-colors outline-none",
                    "focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm"
                  ),
                  children: /* @__PURE__ */ jsx41(CalendarIcon4, { className: "h-4 w-4" })
                }
              )
            ]
          }
        ),
        description && /* @__PURE__ */ jsx41(Text9, { slot: "description", className: "text-xs text-muted-foreground", children: description }),
        errorMessage && /* @__PURE__ */ jsx41(Text9, { slot: "errorMessage", className: "text-xs text-destructive", children: errorMessage }),
        /* @__PURE__ */ jsx41(
          Popover7,
          {
            className: cn(
              "rounded-md border bg-popover text-popover-foreground shadow-md",
              "data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95",
              "data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95",
              "data-[placement=bottom]:slide-in-from-top-2",
              "data-[placement=top]:slide-in-from-bottom-2"
            ),
            children: /* @__PURE__ */ jsxs35(Dialog4, { className: "p-4 space-y-4", children: [
              /* @__PURE__ */ jsxs35(RangeCalendar2, { children: [
                /* @__PURE__ */ jsxs35("header", { className: "flex items-center justify-between pb-4", children: [
                  /* @__PURE__ */ jsx41(
                    Button8,
                    {
                      slot: "previous",
                      className: cn(
                        "h-7 w-7 flex items-center justify-center rounded-sm",
                        "hover:bg-accent hover:text-accent-foreground",
                        "outline-none focus:ring-2 focus:ring-ring",
                        "transition-colors"
                      ),
                      children: /* @__PURE__ */ jsx41(CaretLeft6, { className: "h-4 w-4" })
                    }
                  ),
                  /* @__PURE__ */ jsx41(Heading5, { className: "text-sm font-medium" }),
                  /* @__PURE__ */ jsx41(
                    Button8,
                    {
                      slot: "next",
                      className: cn(
                        "h-7 w-7 flex items-center justify-center rounded-sm",
                        "hover:bg-accent hover:text-accent-foreground",
                        "outline-none focus:ring-2 focus:ring-ring",
                        "transition-colors"
                      ),
                      children: /* @__PURE__ */ jsx41(CaretRight8, { className: "h-4 w-4" })
                    }
                  )
                ] }),
                /* @__PURE__ */ jsx41(CalendarGrid4, { className: "border-collapse space-y-1", children: (date) => /* @__PURE__ */ jsx41(
                  CalendarCell4,
                  {
                    date,
                    className: cn(
                      "h-9 w-9 text-center text-sm relative flex items-center justify-center",
                      "rounded-sm outline-none",
                      "hover:bg-accent hover:text-accent-foreground",
                      "focus:bg-accent focus:text-accent-foreground",
                      // Selection state
                      "data-[selected]:bg-primary data-[selected]:text-primary-foreground",
                      "data-[selected]:hover:bg-primary data-[selected]:hover:text-primary-foreground",
                      // Range state - between start and end
                      "data-[selection-start]:bg-primary data-[selection-start]:text-primary-foreground",
                      "data-[selection-end]:bg-primary data-[selection-end]:text-primary-foreground",
                      // Outside current month
                      "data-[outside-month]:text-muted-foreground data-[outside-month]:opacity-50",
                      // Disabled state
                      "data-[disabled]:text-muted-foreground data-[disabled]:opacity-50",
                      // Unavailable state
                      "data-[unavailable]:text-destructive-foreground data-[unavailable]:line-through",
                      "transition-colors cursor-default"
                    )
                  }
                ) })
              ] }),
              /* @__PURE__ */ jsxs35("div", { className: "border-t pt-4 space-y-3", children: [
                /* @__PURE__ */ jsxs35("div", { className: "flex items-center gap-3", children: [
                  /* @__PURE__ */ jsx41(Text9, { className: "text-sm font-medium text-muted-foreground min-w-[80px]", children: "Start Time:" }),
                  /* @__PURE__ */ jsxs35("div", { className: "flex items-center gap-1", children: [
                    /* @__PURE__ */ jsx41(
                      TimeInput2,
                      {
                        value: startTime.hour,
                        onChange: (hour) => handleStartTimeChange(new Time2(hour, startTime.minute, startTime.second)),
                        min: 0,
                        max: 23,
                        disabled: isDisabled || isReadOnly
                      }
                    ),
                    /* @__PURE__ */ jsx41(Text9, { className: "text-muted-foreground", children: ":" }),
                    /* @__PURE__ */ jsx41(
                      TimeInput2,
                      {
                        value: startTime.minute,
                        onChange: (minute) => handleStartTimeChange(new Time2(startTime.hour, minute, startTime.second)),
                        min: 0,
                        max: 59,
                        disabled: isDisabled || isReadOnly
                      }
                    ),
                    granularity === "second" && /* @__PURE__ */ jsxs35(Fragment7, { children: [
                      /* @__PURE__ */ jsx41(Text9, { className: "text-muted-foreground", children: ":" }),
                      /* @__PURE__ */ jsx41(
                        TimeInput2,
                        {
                          value: startTime.second,
                          onChange: (second) => handleStartTimeChange(new Time2(startTime.hour, startTime.minute, second)),
                          min: 0,
                          max: 59,
                          disabled: isDisabled || isReadOnly
                        }
                      )
                    ] })
                  ] })
                ] }),
                /* @__PURE__ */ jsxs35("div", { className: "flex items-center gap-3", children: [
                  /* @__PURE__ */ jsx41(Text9, { className: "text-sm font-medium text-muted-foreground min-w-[80px]", children: "End Time:" }),
                  /* @__PURE__ */ jsxs35("div", { className: "flex items-center gap-1", children: [
                    /* @__PURE__ */ jsx41(
                      TimeInput2,
                      {
                        value: endTime.hour,
                        onChange: (hour) => handleEndTimeChange(new Time2(hour, endTime.minute, endTime.second)),
                        min: 0,
                        max: 23,
                        disabled: isDisabled || isReadOnly
                      }
                    ),
                    /* @__PURE__ */ jsx41(Text9, { className: "text-muted-foreground", children: ":" }),
                    /* @__PURE__ */ jsx41(
                      TimeInput2,
                      {
                        value: endTime.minute,
                        onChange: (minute) => handleEndTimeChange(new Time2(endTime.hour, minute, endTime.second)),
                        min: 0,
                        max: 59,
                        disabled: isDisabled || isReadOnly
                      }
                    ),
                    granularity === "second" && /* @__PURE__ */ jsxs35(Fragment7, { children: [
                      /* @__PURE__ */ jsx41(Text9, { className: "text-muted-foreground", children: ":" }),
                      /* @__PURE__ */ jsx41(
                        TimeInput2,
                        {
                          value: endTime.second,
                          onChange: (second) => handleEndTimeChange(new Time2(endTime.hour, endTime.minute, second)),
                          min: 0,
                          max: 59,
                          disabled: isDisabled || isReadOnly
                        }
                      )
                    ] })
                  ] })
                ] })
              ] })
            ] })
          }
        )
      ]
    }
  );
}

// src/components/SideNavLink.tsx
import React27 from "react";
import { Link as AriaLink } from "react-aria-components";
import { jsx as jsx42, jsxs as jsxs36 } from "react/jsx-runtime";
function railLinkClassName(isActive) {
  return cn(
    // Row geometry + quiet interaction, shared by every rail link (SideNav + SubNav).
    // Pixel values, not rem: the cofactor root font-size is 14px.
    "min-h-[34px] text-[14px] leading-[1.35]",
    "transition-colors duration-[120ms] motion-reduce:transition-none",
    // Keyboard focus: 2px inset primary ring (rail links sit flush to the rail edge).
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[hsl(var(--primary))]",
    isActive ? "font-medium text-[var(--rail-fg)] shadow-[inset_2px_0_0_hsl(var(--primary))] hover:bg-[var(--rail-hover)]" : "font-normal text-[var(--rail-fg-muted)] hover:bg-[var(--rail-hover)] hover:text-[var(--rail-fg)]"
  );
}
function railLinkIconClassName(isActive) {
  return isActive ? "text-[var(--rail-fg)]" : "text-[var(--rail-fg-dim)] group-hover:text-[var(--rail-fg)]";
}
var RAIL_TITLE_CLASS = "m-0 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-[var(--rail-fg-dim)]";
var SideNavLink = React27.forwardRef(
  ({ children, isActive = false, icon, badge, variant = "default", className, isCollapsed = false, ...props }, ref) => {
    const linkContent = /* @__PURE__ */ jsxs36(
      AriaLink,
      {
        ref,
        "data-active": isActive ? "" : void 0,
        "data-collapsed": isCollapsed ? "" : void 0,
        className: cn(
          // Base styles — colors come from the --rail-* tokens, so the link reads
          // on whatever the host paints (ink rail in the app, paper in docs).
          "group relative flex items-center",
          // Square everywhere (website language)
          "rounded-none overflow-hidden",
          isCollapsed ? "justify-center w-10 h-10" : cn(
            "gap-[12px] px-[16px] py-[6px]",
            variant === "compact" ? "gap-[8px] px-[8px]" : ""
          ),
          railLinkClassName(isActive),
          // Disabled state
          "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          className
        ),
        ...props,
        children: [
          icon && /* @__PURE__ */ jsxs36(
            "span",
            {
              className: cn(
                "relative flex-shrink-0 transition-colors duration-[120ms] motion-reduce:transition-none",
                railLinkIconClassName(isActive)
              ),
              children: [
                /* @__PURE__ */ jsx42("span", { className: cn("transition-opacity", !isActive && "group-hover:opacity-0"), children: icon }),
                React27.isValidElement(icon) && /* @__PURE__ */ jsx42("span", { className: cn(
                  "absolute inset-0 transition-opacity",
                  isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                ), children: React27.cloneElement(icon, { weight: "fill" }) })
              ]
            }
          ),
          /* @__PURE__ */ jsx42("span", { className: cn("flex-1 truncate", isCollapsed && "hidden"), children }),
          badge && /* @__PURE__ */ jsx42("span", { className: cn("flex-shrink-0", isCollapsed && "hidden"), children: badge })
        ]
      }
    );
    if (isCollapsed && icon) {
      return /* @__PURE__ */ jsxs36(TooltipTrigger, { delay: 0, closeDelay: 0, placement: "right", offset: 2, children: [
        linkContent,
        /* @__PURE__ */ jsx42(Tooltip, { children })
      ] });
    }
    return linkContent;
  }
);
SideNavLink.displayName = "SideNavLink";
var SideNavGroup = React27.forwardRef(
  ({ children, title, className, isCollapsed = false, ...props }, ref) => {
    return /* @__PURE__ */ jsxs36(
      "div",
      {
        ref,
        className: cn(
          "flex w-full flex-col border-t border-[var(--rail-rule)] mt-[16px] pt-[16px] first:mt-0 first:border-t-0 first:pt-0",
          isCollapsed && "items-center",
          className
        ),
        ...props,
        children: [
          title && !isCollapsed && /* @__PURE__ */ jsx42("div", { role: "heading", "aria-level": 3, className: cn(RAIL_TITLE_CLASS, "px-[16px] pb-[8px]"), children: title }),
          /* @__PURE__ */ jsx42("nav", { className: cn("flex flex-col gap-px", isCollapsed && "items-center"), children })
        ]
      }
    );
  }
);
SideNavGroup.displayName = "SideNavGroup";
var SideNav = React27.forwardRef(
  ({ children, className, header, footer, footerTop, searchSlot, position = "sticky", isCollapsed = false, ...props }, ref) => {
    const widthClass = isCollapsed ? "w-16" : "w-[280px]";
    const positionClasses = position === "fixed" ? `fixed top-0 left-0 h-screen ${widthClass} z-40` : `sticky top-[4rem] h-[calc(100vh-4rem)] ${widthClass}`;
    return /* @__PURE__ */ jsxs36(
      "aside",
      {
        ref,
        className: cn(
          positionClasses,
          "flex flex-col border-r border-[var(--rail-rule)] bg-[var(--rail)] transition-[width] duration-300 ease-in-out",
          className
        ),
        ...props,
        children: [
          header ? /* @__PURE__ */ jsx42("div", { className: cn(
            "h-16 flex items-center border-b border-[var(--rail-rule)]",
            isCollapsed ? "justify-center px-2" : "px-[16px]"
          ), children: header }) : null,
          searchSlot && !isCollapsed ? /* @__PURE__ */ jsx42("div", { className: "px-[16px] pt-[12px]", children: searchSlot }) : null,
          /* @__PURE__ */ jsx42("nav", { className: cn(
            "flex flex-1 flex-col overflow-y-auto py-[16px]",
            isCollapsed && "items-center"
          ), children }),
          footerTop || footer ? /* @__PURE__ */ jsxs36("div", { className: "mt-auto flex flex-col", children: [
            footerTop ? /* @__PURE__ */ jsx42("div", { className: "border-t border-[var(--rail-rule)]", children: /* @__PURE__ */ jsx42("div", { className: cn(isCollapsed ? "px-2 py-[12px]" : "px-[16px] py-[12px]"), children: footerTop }) }) : null,
            footer ? /* @__PURE__ */ jsx42("div", { className: "border-t border-[var(--rail-rule)]", children: /* @__PURE__ */ jsx42(
              "div",
              {
                className: cn(
                  "h-16 flex items-center",
                  isCollapsed ? "justify-center px-2" : "px-[16px]"
                ),
                children: footer
              }
            ) }) : null
          ] }) : null
        ]
      }
    );
  }
);
SideNav.displayName = "SideNav";

// src/components/SubNav.tsx
import React28 from "react";
import { Link as AriaLink2 } from "react-aria-components";
import { MagnifyingGlass as MagnifyingGlass3 } from "@phosphor-icons/react";
import { Fragment as Fragment8, jsx as jsx43, jsxs as jsxs37 } from "react/jsx-runtime";
var SubNav = React28.forwardRef(
  ({ title, items, activeId, onItemClick, renderItem, searchable, searchPlaceholder, className }, ref) => {
    const [searchQuery, setSearchQuery] = React28.useState("");
    const filteredItems = React28.useMemo(() => {
      if (!searchQuery) return items;
      const q = searchQuery.toLowerCase();
      return items.filter(
        (item) => item.label.toLowerCase().includes(q) || item.section && item.section.toLowerCase().includes(q)
      );
    }, [items, searchQuery]);
    const groupedItems = React28.useMemo(() => {
      const groups = /* @__PURE__ */ new Map();
      for (const item of filteredItems) {
        const key = item.section ?? "__default";
        const existing = groups.get(key);
        if (existing) {
          existing.items.push(item);
        } else {
          groups.set(key, { section: item.section, items: [item] });
        }
      }
      return Array.from(groups.values());
    }, [filteredItems]);
    return /* @__PURE__ */ jsxs37(
      "aside",
      {
        ref,
        className: cn(
          "fixed left-16 top-0 h-screen w-[calc(280px-4rem)] z-40",
          "flex flex-col border-r border-[var(--rail-rule)] bg-[var(--rail)]",
          "transition-[width] duration-300 ease-in-out",
          className
        ),
        children: [
          /* @__PURE__ */ jsx43("div", { className: "h-16 flex items-end px-3 pb-2", children: /* @__PURE__ */ jsx43("div", { role: "heading", "aria-level": 3, className: cn(RAIL_TITLE_CLASS, "px-[10px] pt-[4px] pb-[6px]"), children: title }) }),
          searchable && /* @__PURE__ */ jsx43("div", { className: "px-3 pt-3", children: /* @__PURE__ */ jsx43(
            TextField,
            {
              variant: "ghost",
              leftIcon: /* @__PURE__ */ jsx43(MagnifyingGlass3, { size: 14 }),
              placeholder: searchPlaceholder ?? "Search\u2026",
              value: searchQuery,
              onChange: setSearchQuery,
              "aria-label": "Filter navigation"
            }
          ) }),
          /* @__PURE__ */ jsxs37("nav", { className: "flex-1 overflow-y-auto py-4 px-3 space-y-4", children: [
            groupedItems.length === 0 && searchQuery && /* @__PURE__ */ jsxs37("div", { className: "px-2 py-4 text-center text-xs text-[var(--rail-fg-dim)]", children: [
              "No items match \u201C",
              searchQuery,
              "\u201D"
            ] }),
            groupedItems.map((group, index) => /* @__PURE__ */ jsxs37("div", { className: "space-y-1", children: [
              group.section && /* @__PURE__ */ jsx43("div", { className: cn(RAIL_TITLE_CLASS, "px-2 pb-1"), children: group.section }),
              group.items.map((item) => {
                const isActive = item.id === activeId;
                if (renderItem) {
                  return /* @__PURE__ */ jsx43(React28.Fragment, { children: renderItem(item, isActive) }, item.id);
                }
                return /* @__PURE__ */ jsx43(
                  SubNavLink,
                  {
                    item,
                    isActive,
                    onClick: () => onItemClick?.(item)
                  },
                  item.id
                );
              })
            ] }, group.section ?? `section-${index}`))
          ] })
        ]
      }
    );
  }
);
SubNav.displayName = "SubNav";
function getSubNavLinkClassName(isActive) {
  return cn(
    // Base styles
    "group relative flex items-center gap-[10px] rounded-none px-[10px] py-[5px] overflow-hidden",
    // Active/rest colors from the --rail-* tokens
    railLinkClassName(isActive),
    // Disabled state
    "data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
  );
}
function SubNavLinkContent({ item, isActive }) {
  return /* @__PURE__ */ jsxs37(Fragment8, { children: [
    item.icon && /* @__PURE__ */ jsx43(
      "span",
      {
        className: cn(
          "flex-shrink-0 transition-colors",
          railLinkIconClassName(isActive)
        ),
        children: item.icon
      }
    ),
    /* @__PURE__ */ jsx43("span", { className: "flex-1 truncate", children: item.label })
  ] });
}
var SubNavLink = React28.forwardRef(
  ({ item, isActive, onClick }, ref) => {
    return /* @__PURE__ */ jsx43(
      AriaLink2,
      {
        ref,
        href: "#",
        className: getSubNavLinkClassName(isActive),
        onPress: (e) => {
          e.continuePropagation();
          onClick();
        },
        children: /* @__PURE__ */ jsx43(SubNavLinkContent, { item, isActive })
      }
    );
  }
);
SubNavLink.displayName = "SubNavLink";

// src/components/Tabs.tsx
import React29 from "react";
import {
  Tabs as AriaTabs,
  TabList as AriaTabList,
  Tab as AriaTab,
  TabPanel as AriaTabPanel
} from "react-aria-components";
import { motion as motion2 } from "framer-motion";
import { jsx as jsx44, jsxs as jsxs38 } from "react/jsx-runtime";
var Tabs = React29.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx44(
    AriaTabs,
    {
      ref,
      className: cn("w-full", className),
      ...props
    }
  )
);
Tabs.displayName = "Tabs";
function TabList({ className, ...props }) {
  return /* @__PURE__ */ jsx44(
    AriaTabList,
    {
      className: cn(
        "inline-flex h-10 items-center justify-start rounded-md bg-muted border border-border p-1 text-muted-foreground",
        className
      ),
      ...props
    }
  );
}
TabList.displayName = "TabList";
var Tab = React29.forwardRef(
  ({ className, tooltip, ...props }, ref) => {
    const tab = /* @__PURE__ */ jsx44(
      AriaTab,
      {
        ref,
        className: cn(
          "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5",
          "text-sm font-medium ring-offset-background transition-all",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:pointer-events-none disabled:opacity-50",
          "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          "data-[selected]:bg-background data-[selected]:text-foreground data-[selected]:shadow-sm",
          className
        ),
        ...props
      }
    );
    if (tooltip) {
      return /* @__PURE__ */ jsxs38(TooltipTrigger, { children: [
        tab,
        /* @__PURE__ */ jsx44(Tooltip, { children: tooltip })
      ] });
    }
    return tab;
  }
);
Tab.displayName = "Tab";
var TabPanel = React29.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx44(
    AriaTabPanel,
    {
      ref,
      className: cn(
        "mt-2 ring-offset-background",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className
      ),
      ...props
    }
  )
);
TabPanel.displayName = "TabPanel";
var UnderlineTabsContext = React29.createContext(null);
function UnderlineTabs({
  selectedId,
  onSelectionChange,
  children,
  className,
  layoutId = "underline-tabs"
}) {
  return /* @__PURE__ */ jsx44(UnderlineTabsContext.Provider, { value: { selectedId, onSelectionChange, layoutId }, children: /* @__PURE__ */ jsx44(
    "div",
    {
      className: cn("flex gap-1 border-b border-border", className),
      role: "tablist",
      "data-underline-tabs": true,
      children
    }
  ) });
}
UnderlineTabs.displayName = "UnderlineTabs";
function UnderlineTab({
  id,
  children,
  className,
  // These props are optional if using context
  isSelected: isSelectedProp,
  onSelect: onSelectProp,
  layoutId: layoutIdProp,
  isDisabled,
  tooltip
}) {
  const context = React29.useContext(UnderlineTabsContext);
  const isSelected = isSelectedProp ?? context?.selectedId === id;
  const handleSelect = onSelectProp ?? (() => context?.onSelectionChange(id));
  const layoutId = layoutIdProp ?? context?.layoutId ?? "underline-tabs";
  const button = /* @__PURE__ */ jsxs38(
    "button",
    {
      type: "button",
      role: "tab",
      "aria-selected": isSelected,
      "aria-disabled": isDisabled || void 0,
      onClick: isDisabled ? (e) => e.preventDefault() : handleSelect,
      className: cn(
        "relative px-4 py-2 text-sm font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        isDisabled ? "text-muted-foreground/50 cursor-not-allowed" : isSelected ? "text-foreground" : "text-muted-foreground hover:text-foreground",
        className
      ),
      children: [
        children,
        isSelected && !isDisabled && /* @__PURE__ */ jsx44(
          motion2.div,
          {
            layoutId,
            "data-tab-indicator": true,
            className: "absolute bottom-0 left-0 right-0 h-0.5 bg-primary",
            initial: false,
            transition: {
              type: "spring",
              stiffness: 500,
              damping: 35
            }
          }
        )
      ]
    }
  );
  if (tooltip) {
    return /* @__PURE__ */ jsxs38(TooltipTrigger, { delay: 300, children: [
      button,
      /* @__PURE__ */ jsx44(Tooltip, { children: tooltip })
    ] });
  }
  return button;
}
UnderlineTab.displayName = "UnderlineTab";

// src/components/Breadcrumbs.tsx
import React31 from "react";
import { Button as AriaButton2 } from "react-aria-components";

// src/components/Menu.tsx
import React30 from "react";
import {
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  MenuTrigger as AriaMenuTrigger,
  SubmenuTrigger as AriaSubmenuTrigger,
  Separator as AriaSeparator,
  Section as AriaSection,
  Header as AriaHeader,
  Popover as Popover8
} from "react-aria-components";
import { CaretRight as CaretRight9 } from "@phosphor-icons/react";
import { jsx as jsx45, jsxs as jsxs39 } from "react/jsx-runtime";
var Menu = React30.forwardRef(
  ({ children, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx45(
      AriaMenu,
      {
        ref,
        className: cn(
          "overflow-hidden p-1 text-popover-foreground",
          "data-[focus-visible]:outline-none",
          className
        ),
        ...props,
        children
      }
    );
  }
);
Menu.displayName = "Menu";
var MenuItem = React30.forwardRef(
  ({ destructive, className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx45(
      AriaMenuItem,
      {
        ref,
        className: cn(
          "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm font-normal outline-none",
          "transition-colors",
          "data-[focused]:bg-accent data-[focused]:text-accent-foreground",
          "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          destructive && "text-destructive data-[focused]:text-destructive",
          className
        ),
        ...props,
        children
      }
    );
  }
);
MenuItem.displayName = "MenuItem";
function MenuTrigger({ children, isOpen, onOpenChange }) {
  return /* @__PURE__ */ jsxs39(AriaMenuTrigger, { isOpen, onOpenChange, children: [
    children[0],
    /* @__PURE__ */ jsx45(
      Popover8,
      {
        className: cn(
          "z-50 min-w-[200px] rounded-md border bg-popover text-popover-foreground outline-none",
          "shadow-lg",
          "data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95",
          "data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95"
        ),
        children: children[1]
      }
    )
  ] });
}
var MenuSeparator = React30.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx45(
  AriaSeparator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-muted", className),
    ...props
  }
));
MenuSeparator.displayName = "MenuSeparator";
function SubmenuTrigger({ children }) {
  return /* @__PURE__ */ jsxs39(AriaSubmenuTrigger, { children: [
    /* @__PURE__ */ jsxs39(MenuItem, { className: "flex items-center justify-between", children: [
      children[0],
      /* @__PURE__ */ jsx45(CaretRight9, { size: 14, className: "ml-auto text-muted-foreground" })
    ] }),
    /* @__PURE__ */ jsx45(
      Popover8,
      {
        className: cn(
          "z-50 min-w-[200px] rounded-md border bg-popover text-popover-foreground outline-none",
          "shadow-lg",
          "data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95",
          "data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95"
        ),
        offset: -4,
        children: children[1]
      }
    )
  ] });
}
function MenuSection({ children, className, ...props }) {
  return /* @__PURE__ */ jsx45(AriaSection, { className: cn("mt-1 first:mt-0", className), ...props, children });
}
MenuSection.displayName = "MenuSection";
var MenuHeader = React30.forwardRef(
  ({ children, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx45(
      AriaHeader,
      {
        ref,
        className: cn(
          "px-2 py-1.5 text-xs text-muted-foreground font-medium",
          className
        ),
        ...props,
        children
      }
    );
  }
);
MenuHeader.displayName = "MenuHeader";

// src/components/Breadcrumbs.tsx
import { jsx as jsx46, jsxs as jsxs40 } from "react/jsx-runtime";
var Breadcrumbs = React31.forwardRef(
  ({ className, separator, children, ...props }, ref) => {
    const childArray = React31.Children.toArray(children);
    return /* @__PURE__ */ jsx46(
      "nav",
      {
        ref,
        "aria-label": "Breadcrumb",
        "data-breadcrumbs": true,
        className: cn("flex min-w-0 items-center space-x-1 text-sm text-muted-foreground", className),
        ...props,
        children: /* @__PURE__ */ jsx46("ol", { className: "flex min-w-0 items-center space-x-1", children: childArray.map((child, index) => {
          const isLast = index === childArray.length - 1;
          return /* @__PURE__ */ jsxs40(React31.Fragment, { children: [
            child,
            !isLast && /* @__PURE__ */ jsx46("li", { className: "flex items-center px-1", "aria-hidden": "true", "data-separator": true, children: separator || /* @__PURE__ */ jsx46(CaretRight3, { size: 12 }) })
          ] }, index);
        }) })
      }
    );
  }
);
Breadcrumbs.displayName = "Breadcrumbs";
var Breadcrumb = React31.forwardRef(
  ({ className, children, href, isDisabled, onPress, ...props }, ref) => {
    return /* @__PURE__ */ jsx46("li", { ref, className: cn("inline-flex items-center", href || onPress ? "shrink-0" : "min-w-0", className), ...props, children: href ? /* @__PURE__ */ jsx46(
      "a",
      {
        href,
        className: cn(
          "inline-flex items-center font-medium transition-colors hover:text-foreground",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm",
          isDisabled ? "text-muted-foreground cursor-default pointer-events-none" : "text-muted-foreground hover:text-foreground"
        ),
        "aria-disabled": isDisabled,
        children
      }
    ) : onPress ? /* @__PURE__ */ jsx46(
      "button",
      {
        type: "button",
        onClick: onPress,
        disabled: isDisabled,
        className: cn(
          "inline-flex items-center font-medium transition-colors hover:text-foreground",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm",
          isDisabled ? "text-muted-foreground cursor-default" : "text-muted-foreground hover:text-foreground"
        ),
        children
      }
    ) : /* @__PURE__ */ jsx46(
      "span",
      {
        "data-current": "true",
        "aria-current": "page",
        className: cn(
          "inline-flex min-w-0 items-center font-medium",
          isDisabled ? "text-muted-foreground" : "text-foreground"
        ),
        children: /* @__PURE__ */ jsx46("span", { className: "block max-w-[24rem] truncate", children })
      }
    ) });
  }
);
Breadcrumb.displayName = "Breadcrumb";
var BreadcrumbDropdown = React31.forwardRef(
  ({
    className,
    items,
    selectedId,
    onSelectItem,
    placeholder = "Select\u2026",
    triggerClassName,
    isDisabled,
    ...props
  }, ref) => {
    const selectedItem = React31.useMemo(
      () => items.find((item) => item.id === selectedId) ?? null,
      [items, selectedId]
    );
    const handleAction = React31.useCallback(
      (key) => {
        if (typeof key !== "string") {
          return;
        }
        if (key === selectedId) {
          return;
        }
        onSelectItem?.(key);
      },
      [onSelectItem, selectedId]
    );
    return /* @__PURE__ */ jsx46("li", { ref, className: cn("inline-flex items-center", className), ...props, children: /* @__PURE__ */ jsxs40(MenuTrigger, { children: [
      /* @__PURE__ */ jsxs40(
        AriaButton2,
        {
          slot: "trigger",
          isDisabled: isDisabled || items.length === 0,
          className: ({ isFocusVisible: _isFocusVisible, isPressed }) => cn(
            "inline-flex items-center gap-1 text-sm font-medium h-auto px-0 py-0 bg-transparent",
            "text-muted-foreground hover:text-foreground",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm",
            (isDisabled || items.length === 0) && "cursor-default opacity-60",
            isPressed && "text-foreground",
            triggerClassName
          ),
          children: [
            /* @__PURE__ */ jsx46("span", { className: "truncate max-w-[12rem]", children: selectedItem?.label ?? placeholder }),
            /* @__PURE__ */ jsx46(CaretDown3, { size: 12, className: "shrink-0 opacity-60" })
          ]
        }
      ),
      /* @__PURE__ */ jsx46(Menu, { className: "min-w-[12rem]", onAction: handleAction, children: items.map((item) => /* @__PURE__ */ jsx46(MenuItem, { id: item.id, isDisabled: item.isDisabled, children: /* @__PURE__ */ jsxs40("span", { className: "flex items-center gap-2", children: [
        selectedId === item.id ? /* @__PURE__ */ jsx46(Check, { size: 14, className: "text-foreground shrink-0" }) : /* @__PURE__ */ jsx46("span", { className: "w-[14px] shrink-0", "aria-hidden": true }),
        /* @__PURE__ */ jsx46("span", { className: "truncate", children: item.label })
      ] }) }, item.id)) })
    ] }) });
  }
);
BreadcrumbDropdown.displayName = "BreadcrumbDropdown";

// src/components/SplitButton.tsx
import React32 from "react";
import {
  Button as AriaButton3,
  MenuTrigger as AriaMenuTrigger2,
  Menu as AriaMenu2,
  MenuItem as AriaMenuItem2,
  Popover as Popover9
} from "react-aria-components";
import { CaretDown as CaretDown5 } from "@phosphor-icons/react";
import { jsx as jsx47, jsxs as jsxs41 } from "react/jsx-runtime";
var buttonVariantsBase = {
  primary: "bg-primary text-primary-foreground hover:bg-primary/90",
  secondary: "bg-secondary text-secondary-foreground",
  ghost: "hover:bg-accent hover:text-accent-foreground",
  destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
  outline: "bg-background hover:bg-accent hover:text-accent-foreground"
};
var containerVariants = {
  primary: "",
  secondary: "border border-border shadow-sm hover:border-ring/50",
  ghost: "",
  destructive: "shadow-sm",
  outline: "border border-border shadow-sm"
};
var buttonHeights = {
  sm: "h-8",
  md: "h-9",
  lg: "h-10"
};
var buttonTextSizes = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base"
};
var iconSizes2 = {
  sm: 12,
  md: 14,
  lg: 16
};
var dividerVariants = {
  primary: "bg-primary-foreground/30",
  secondary: "bg-border",
  ghost: "bg-border",
  destructive: "bg-destructive-foreground/30",
  outline: "bg-border"
};
var SplitButtonItem = React32.forwardRef(
  ({ destructive, className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx47(
      AriaMenuItem2,
      {
        ref,
        className: cn(
          "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors",
          "data-[focused]:bg-accent data-[focused]:text-accent-foreground",
          "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          destructive && "text-destructive data-[focused]:text-destructive",
          className
        ),
        ...props,
        children
      }
    );
  }
);
SplitButtonItem.displayName = "SplitButton.Item";
var SplitButtonSeparator = React32.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx47(
  "hr",
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-muted", className),
    ...props
  }
));
SplitButtonSeparator.displayName = "SplitButton.Separator";
function isMenuChild(child) {
  if (!React32.isValidElement(child)) return false;
  const type = child.type;
  const displayName = type?.displayName;
  return displayName === "SplitButton.Item" || displayName === "SplitButton.Separator";
}
function flattenChildren(children) {
  const result = [];
  React32.Children.forEach(children, (child) => {
    if (!React32.isValidElement(child)) {
      result.push(child);
      return;
    }
    if (child.type === React32.Fragment) {
      const fragment = child;
      result.push(...flattenChildren(fragment.props.children));
    } else {
      result.push(child);
    }
  });
  return result;
}
var SplitButtonRoot = React32.forwardRef(
  ({ className, variant = "primary", size = "md", iconLeft, children, isDisabled, onPress, ...props }, ref) => {
    const childArray = flattenChildren(children);
    const labelContent = childArray.filter((child) => !isMenuChild(child));
    const menuItems = childArray.filter((child) => isMenuChild(child));
    const baseButtonClasses = cn(
      "inline-flex items-center justify-center whitespace-nowrap font-medium transition-colors",
      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:z-10",
      "disabled:pointer-events-none disabled:opacity-50",
      "data-[pressed]:translate-y-px",
      buttonVariantsBase[variant],
      buttonHeights[size],
      buttonTextSizes[size]
    );
    return /* @__PURE__ */ jsxs41(
      "div",
      {
        role: "group",
        "data-component": "split-button",
        "aria-label": typeof labelContent[0] === "string" ? labelContent[0] : void 0,
        className: cn(
          "inline-flex rounded-md",
          containerVariants[variant],
          isDisabled && "opacity-50 pointer-events-none",
          className
        ),
        children: [
          /* @__PURE__ */ jsxs41(
            AriaButton3,
            {
              ref,
              className: cn(
                baseButtonClasses,
                // flex-1 so the main segment absorbs any extra container width (e.g.
                // a min-width set by the caller) and the group fills as one solid
                // block — no transparent gap before the chevron. No-op when the
                // split button is sized to its content.
                "flex-1 rounded-l-md rounded-r-none px-4",
                iconLeft && "gap-2"
              ),
              isDisabled,
              onPress,
              ...props,
              children: [
                iconLeft && /* @__PURE__ */ jsx47("span", { className: "flex-shrink-0", children: iconLeft }),
                labelContent
              ]
            }
          ),
          /* @__PURE__ */ jsx47("div", { className: cn("w-px self-stretch", dividerVariants[variant]) }),
          /* @__PURE__ */ jsxs41(AriaMenuTrigger2, { children: [
            /* @__PURE__ */ jsx47(
              AriaButton3,
              {
                className: cn(
                  baseButtonClasses,
                  "rounded-r-md rounded-l-none px-2"
                ),
                isDisabled,
                "aria-label": "More options",
                children: /* @__PURE__ */ jsx47(CaretDown5, { size: iconSizes2[size], weight: "bold" })
              }
            ),
            /* @__PURE__ */ jsx47(
              Popover9,
              {
                className: cn(
                  "z-50 min-w-[8rem] rounded-md border bg-popover text-popover-foreground shadow-lg",
                  "data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95",
                  "data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95",
                  "data-[placement=bottom]:slide-in-from-top-2",
                  "data-[placement=top]:slide-in-from-bottom-2"
                ),
                children: /* @__PURE__ */ jsx47(
                  AriaMenu2,
                  {
                    className: cn(
                      "min-w-[8rem] overflow-hidden rounded-md p-1 outline-none"
                    ),
                    children: menuItems
                  }
                )
              }
            )
          ] })
        ]
      }
    );
  }
);
SplitButtonRoot.displayName = "SplitButton";
var SplitButton = Object.assign(SplitButtonRoot, {
  Item: SplitButtonItem,
  Separator: SplitButtonSeparator
});

// src/components/HoverCard.tsx
import React33 from "react";
import { createPortal as createPortal2 } from "react-dom";
import { jsx as jsx48, jsxs as jsxs42 } from "react/jsx-runtime";
var HoverCardContext = React33.createContext(null);
var mergeRefs2 = (...refs) => {
  return (value) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") {
        ref(value);
      } else {
        ref.current = value;
      }
    });
  };
};
var composeEventHandlers2 = (original, next) => (event) => {
  original?.(event);
  next?.(event);
};
var getOppositePlacement2 = (placement) => {
  switch (placement) {
    case "top":
      return "bottom";
    case "bottom":
      return "top";
    case "left":
      return "right";
    case "right":
    default:
      return "left";
  }
};
var HoverCardTrigger = ({
  children,
  openDelay = 300,
  closeDelay = 100,
  placement = "top",
  offset = 8,
  isOpen: controlledIsOpen,
  onOpenChange
}) => {
  const [internalIsOpen, setInternalIsOpen] = React33.useState(false);
  const [triggerRect, setTriggerRect] = React33.useState(null);
  const triggerRef = React33.useRef(null);
  const openTimeout = React33.useRef(void 0);
  const closeTimeout = React33.useRef(void 0);
  const isContentHovered = React33.useRef(false);
  const isControlled = controlledIsOpen !== void 0;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
  const setIsOpen = React33.useCallback(
    (value) => {
      if (isControlled) {
        onOpenChange?.(value);
      } else {
        setInternalIsOpen(value);
      }
    },
    [isControlled, onOpenChange]
  );
  const show = React33.useCallback(() => {
    if (!triggerRef.current) {
      return;
    }
    setTriggerRect(triggerRef.current.getBoundingClientRect());
    setIsOpen(true);
  }, [setIsOpen]);
  const hide = React33.useCallback(() => {
    if (isContentHovered.current) return;
    setIsOpen(false);
  }, [setIsOpen]);
  const handleEnter = React33.useCallback(() => {
    window.clearTimeout(closeTimeout.current);
    openTimeout.current = window.setTimeout(show, openDelay);
  }, [openDelay, show]);
  const handleLeave = React33.useCallback(() => {
    window.clearTimeout(openTimeout.current);
    closeTimeout.current = window.setTimeout(hide, closeDelay);
  }, [closeDelay, hide]);
  const handleEnterContent = React33.useCallback(() => {
    isContentHovered.current = true;
    window.clearTimeout(closeTimeout.current);
  }, []);
  const handleLeaveContent = React33.useCallback(() => {
    isContentHovered.current = false;
    closeTimeout.current = window.setTimeout(hide, closeDelay);
  }, [closeDelay, hide]);
  React33.useEffect(() => {
    if (!isOpen) {
      return;
    }
    const updatePosition = () => {
      if (!triggerRef.current) {
        return;
      }
      setTriggerRect(triggerRef.current.getBoundingClientRect());
    };
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);
  React33.useEffect(() => {
    return () => {
      window.clearTimeout(openTimeout.current);
      window.clearTimeout(closeTimeout.current);
    };
  }, []);
  const nodes = React33.Children.toArray(children);
  const triggerElement = nodes[0];
  const hoverCardElements = nodes.slice(1);
  const originalRef = triggerElement.props.ref;
  const clonedTrigger = React33.cloneElement(triggerElement, {
    ref: mergeRefs2(originalRef, (node) => {
      triggerRef.current = node;
    }),
    onMouseEnter: composeEventHandlers2(triggerElement.props.onMouseEnter, handleEnter),
    onMouseLeave: composeEventHandlers2(triggerElement.props.onMouseLeave, handleLeave),
    onFocus: composeEventHandlers2(triggerElement.props.onFocus, show),
    onBlur: composeEventHandlers2(triggerElement.props.onBlur, hide)
  });
  return /* @__PURE__ */ jsxs42(
    HoverCardContext.Provider,
    {
      value: {
        isOpen,
        triggerRect,
        placement,
        offset,
        onEnterContent: handleEnterContent,
        onLeaveContent: handleLeaveContent
      },
      children: [
        clonedTrigger,
        hoverCardElements
      ]
    }
  );
};
var HoverCardContent = ({
  children,
  className,
  maxWidth = 320
}) => {
  const context = React33.useContext(HoverCardContext);
  if (!context || !context.isOpen) {
    return null;
  }
  const { triggerRect, placement: preferredPlacement, offset, onEnterContent, onLeaveContent } = context;
  const contentRef = React33.useRef(null);
  const [computed, setComputed] = React33.useState({ top: 0, left: 0, placement: preferredPlacement, ready: false });
  React33.useLayoutEffect(() => {
    if (!triggerRect || !contentRef.current) {
      return;
    }
    const contentEl = contentRef.current;
    const width = contentEl.offsetWidth;
    const height = contentEl.offsetHeight;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 8;
    const candidates = [
      preferredPlacement,
      getOppositePlacement2(preferredPlacement),
      "top",
      "bottom",
      "right",
      "left"
    ];
    const uniqueCandidates = Array.from(new Set(candidates));
    const computeForPlacement = (p) => {
      switch (p) {
        case "bottom":
          return {
            top: triggerRect.bottom + offset,
            left: triggerRect.left + triggerRect.width / 2 - width / 2
          };
        case "left":
          return {
            top: triggerRect.top + triggerRect.height / 2 - height / 2,
            left: triggerRect.left - offset - width
          };
        case "right":
          return {
            top: triggerRect.top + triggerRect.height / 2 - height / 2,
            left: triggerRect.right + offset
          };
        case "top":
        default:
          return {
            top: triggerRect.top - offset - height,
            left: triggerRect.left + triggerRect.width / 2 - width / 2
          };
      }
    };
    const fitsViewport = (pos2) => pos2.top >= margin && pos2.left >= margin && pos2.top + height <= viewportHeight - margin && pos2.left + width <= viewportWidth - margin;
    let chosenPlacement = preferredPlacement;
    let pos = computeForPlacement(preferredPlacement);
    for (const candidate of uniqueCandidates) {
      const candidatePos = computeForPlacement(candidate);
      if (fitsViewport(candidatePos)) {
        chosenPlacement = candidate;
        pos = candidatePos;
        break;
      }
    }
    const clampedTop = Math.min(Math.max(pos.top, margin), viewportHeight - margin - height);
    const clampedLeft = Math.min(Math.max(pos.left, margin), viewportWidth - margin - width);
    setComputed({
      top: clampedTop,
      left: clampedLeft,
      placement: chosenPlacement,
      ready: true
    });
  }, [triggerRect, preferredPlacement, offset, children]);
  const transformOriginMap = {
    top: "bottom center",
    bottom: "top center",
    left: "center right",
    right: "center left"
  };
  return createPortal2(
    /* @__PURE__ */ jsx48(
      "div",
      {
        ref: contentRef,
        role: "tooltip",
        onMouseEnter: onEnterContent,
        onMouseLeave: onLeaveContent,
        className: cn(
          "z-50 rounded-lg border bg-popover p-3 text-popover-foreground shadow-lg",
          computed.ready && "animate-in fade-in-0 zoom-in-95 duration-150",
          className
        ),
        style: {
          position: "fixed",
          top: computed.top,
          left: computed.left,
          maxWidth,
          visibility: computed.ready ? "visible" : "hidden",
          transformOrigin: transformOriginMap[computed.placement]
        },
        children
      }
    ),
    document.body
  );
};

// src/components/Dialog.tsx
import React34 from "react";
import {
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  Modal,
  ModalOverlay
} from "react-aria-components";
import { Fragment as Fragment9, jsx as jsx49, jsxs as jsxs43 } from "react/jsx-runtime";
var Dialog5 = React34.forwardRef(
  ({ children, title, description, eyebrow, footer, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx49(
      AriaDialog,
      {
        ref,
        "data-dialog": true,
        className: cn(
          // Flex column so the header and footer stay pinned (shrink-0) while the
          // body scrolls between them. Square frame, ink rule, hard offset shadow
          // (website framed-panel language) — no blur.
          "relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-none",
          "border border-[color:var(--rule)] bg-background shadow-[var(--shadow-hard)]",
          // The panel itself is focused on open (focus containment); never ring it.
          "outline-none",
          // Smooth entrance animation
          "data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95",
          "data-[entering]:duration-200 data-[entering]:ease-out",
          // Smooth exit animation
          "data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95",
          "data-[exiting]:duration-150 data-[exiting]:ease-in",
          className
        ),
        ...props,
        children: ({ close }) => /* @__PURE__ */ jsxs43(Fragment9, { children: [
          title ? /* @__PURE__ */ jsxs43(
            "div",
            {
              "data-dialog-header": true,
              className: "flex shrink-0 items-start justify-between gap-4 border-b border-b-border bg-background px-[28px] py-6",
              children: [
                /* @__PURE__ */ jsxs43("div", { className: "flex flex-col gap-1 text-left", children: [
                  eyebrow && /* @__PURE__ */ jsx49(
                    "span",
                    {
                      "data-dialog-eyebrow": true,
                      className: "text-[11px] font-medium uppercase tracking-wider opacity-70",
                      children: eyebrow
                    }
                  ),
                  /* @__PURE__ */ jsx49("h2", { className: "text-[20px] font-medium leading-snug tracking-[-0.02em]", children: title }),
                  description && /* @__PURE__ */ jsx49("p", { className: "text-sm text-muted-foreground", children: description })
                ] }),
                /* @__PURE__ */ jsx49(
                  Button,
                  {
                    onPress: close,
                    variant: "icon",
                    size: "sm",
                    iconOnly: true,
                    tooltip: "Close",
                    className: "-mr-1.5 flex-shrink-0",
                    children: /* @__PURE__ */ jsx49(X2, { size: 18 })
                  }
                )
              ]
            }
          ) : /* @__PURE__ */ jsx49(
            Button,
            {
              onPress: close,
              className: "absolute right-4 top-4 z-10",
              variant: "icon",
              size: "sm",
              iconOnly: true,
              tooltip: "Close",
              children: /* @__PURE__ */ jsx49(X2, { size: 18 })
            }
          ),
          /* @__PURE__ */ jsx49("div", { "data-dialog-body": true, className: "min-h-0 flex-1 overflow-auto bg-background p-[28px]", children }),
          footer && /* @__PURE__ */ jsx49(
            "div",
            {
              "data-dialog-footer": true,
              className: "flex shrink-0 flex-col-reverse gap-2 border-t border-t-border bg-secondary px-[28px] py-4 sm:flex-row sm:items-center sm:justify-end",
              children: footer
            }
          )
        ] })
      }
    );
  }
);
Dialog5.displayName = "Dialog";
var sizeClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "4xl": "max-w-4xl",
  full: "max-w-[95vw]"
};
function DialogTrigger({
  children,
  className,
  size = "2xl",
  isDismissable = true,
  ...props
}) {
  return /* @__PURE__ */ jsxs43(AriaDialogTrigger, { ...props, children: [
    children[0],
    /* @__PURE__ */ jsx49(
      ModalOverlay,
      {
        isDismissable: true,
        className: cn(
          "fixed inset-0 z-50 bg-[rgba(11,18,32,0.45)]",
          // Smooth fade in for overlay
          "data-[entering]:animate-in data-[entering]:fade-in-0",
          "data-[entering]:duration-200 data-[entering]:ease-out",
          // Smooth fade out for overlay
          "data-[exiting]:animate-out data-[exiting]:fade-out-0",
          "data-[exiting]:duration-150 data-[exiting]:ease-in"
        ),
        children: /* @__PURE__ */ jsx49(
          Modal,
          {
            isDismissable,
            className: cn(
              "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
              "w-full",
              sizeClasses[size],
              className
            ),
            children: children[1]
          }
        )
      }
    )
  ] });
}
var DialogContent = React34.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx49("div", { ref, className: cn("grid gap-4", className), ...props })
);
DialogContent.displayName = "DialogContent";
var DialogFooter = React34.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx49(
    "div",
    {
      ref,
      "data-dialog-footer": true,
      className: cn(
        "-mx-[28px] -mb-[28px] mt-6 flex flex-col-reverse gap-2 border-t border-t-border bg-secondary px-[28px] py-4 sm:flex-row sm:justify-end",
        className
      ),
      ...props
    }
  )
);
DialogFooter.displayName = "DialogFooter";

// src/components/Drawer.tsx
import React35 from "react";
import {
  Dialog as AriaDialog2,
  DialogTrigger as AriaDialogTrigger2,
  Modal as Modal2,
  ModalOverlay as ModalOverlay2
} from "react-aria-components";
import { ArrowsOut, ArrowsOutLineHorizontal, ArrowsInLineHorizontal } from "@phosphor-icons/react";
import { jsx as jsx50, jsxs as jsxs44 } from "react/jsx-runtime";
var DrawerContext = React35.createContext(null);
var DrawerWidthContext = React35.createContext(null);
var Drawer = React35.forwardRef(
  ({ children, title, description, className, hideCloseButton = false, onExpand, ...props }, ref) => {
    return /* @__PURE__ */ jsx50(
      AriaDialog2,
      {
        ref,
        "data-drawer": true,
        className: cn(
          "relative h-full w-full overflow-hidden",
          "border-l border-l-[color:var(--rule)] bg-background",
          "flex flex-col",
          className
        ),
        ...props,
        children: ({ close }) => /* @__PURE__ */ jsxs44(DrawerContext.Provider, { value: { close, onExpand }, children: [
          title && /* @__PURE__ */ jsxs44(
            "div",
            {
              "data-drawer-header": true,
              className: "flex items-center justify-between px-[28px] py-6 border-b border-b-border bg-background shrink-0",
              children: [
                /* @__PURE__ */ jsxs44("div", { className: "flex-1 min-w-0", children: [
                  /* @__PURE__ */ jsx50("h2", { className: "text-[20px] font-medium leading-none tracking-[-0.02em] truncate", children: title }),
                  description && /* @__PURE__ */ jsx50("p", { className: "text-sm text-muted-foreground mt-1", children: description })
                ] }),
                !hideCloseButton && /* @__PURE__ */ jsx50(
                  Button,
                  {
                    onPress: close,
                    className: "ml-4 opacity-80 hover:opacity-100 transition-opacity shrink-0",
                    variant: "ghost",
                    size: "lg",
                    iconOnly: true,
                    "aria-label": "Close drawer",
                    children: /* @__PURE__ */ jsx50(X2, { size: 20 })
                  }
                )
              ]
            }
          ),
          !title && !hideCloseButton && /* @__PURE__ */ jsx50(
            Button,
            {
              onPress: close,
              className: "absolute right-5 top-5 shadow-sm opacity-80 hover:opacity-100 transition-opacity z-30",
              variant: "ghost",
              size: "lg",
              iconOnly: true,
              "aria-label": "Close drawer",
              children: /* @__PURE__ */ jsx50(X2, { size: 20 })
            }
          ),
          /* @__PURE__ */ jsx50("div", { className: "flex flex-1 flex-col overflow-auto", children })
        ] })
      }
    );
  }
);
Drawer.displayName = "Drawer";
function DrawerTrigger({
  children,
  className,
  isDismissable = true,
  allowWide = false,
  wide,
  defaultWide = false,
  onWideChange,
  ...props
}) {
  const [internalWide, setInternalWide] = React35.useState(defaultWide);
  const isWide = wide ?? internalWide;
  const toggleWide = React35.useCallback(() => {
    const next = !isWide;
    if (wide === void 0) setInternalWide(next);
    onWideChange?.(next);
  }, [isWide, wide, onWideChange]);
  const widthClass = isWide ? "max-w-[min(1100px,94vw)]" : "max-w-[min(700px,94vw)]";
  return /* @__PURE__ */ jsx50(DrawerWidthContext.Provider, { value: { enabled: allowWide, isWide, toggleWide }, children: /* @__PURE__ */ jsxs44(AriaDialogTrigger2, { ...props, children: [
    children[0],
    /* @__PURE__ */ jsx50(
      ModalOverlay2,
      {
        isDismissable: true,
        className: cn(
          "fixed inset-0 z-50 bg-[rgba(11,18,32,0.45)]",
          // Smooth fade in for overlay
          "data-[entering]:animate-in data-[entering]:fade-in-0",
          "data-[entering]:duration-300 data-[entering]:ease-out",
          // Smooth fade out for overlay
          "data-[exiting]:animate-out data-[exiting]:fade-out-0",
          "data-[exiting]:duration-200 data-[exiting]:ease-in"
        ),
        children: /* @__PURE__ */ jsx50(
          Modal2,
          {
            isDismissable,
            className: cn(
              "fixed right-0 top-0 bottom-0 z-50 w-full",
              widthClass,
              "transition-[max-width] duration-200 ease-out",
              // Slide in from right animation
              "data-[entering]:animate-in data-[entering]:slide-in-from-right",
              "data-[entering]:duration-300 data-[entering]:ease-out",
              // Slide out to right animation
              "data-[exiting]:animate-out data-[exiting]:slide-out-to-right",
              "data-[exiting]:duration-200 data-[exiting]:ease-in",
              className
            ),
            children: children[1]
          }
        )
      }
    )
  ] }) });
}
var DrawerHeader = React35.forwardRef(
  ({ className, children, sticky = true, ...props }, ref) => /* @__PURE__ */ jsx50(
    "header",
    {
      ref,
      "data-drawer-header": true,
      className: cn(
        "px-[28px] py-4 border-b border-b-border bg-background",
        sticky && "sticky top-0 z-20",
        className
      ),
      ...props,
      children
    }
  )
);
DrawerHeader.displayName = "DrawerHeader";
var DrawerContent = React35.forwardRef(
  // `flex-1` lets the content fill the drawer's flex-column scroll area so a
  // sticky `DrawerFooter` pins to the bottom even when content is short. It only
  // ever grows into empty space (min-height stays at content size), so taller
  // content still scrolls normally.
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx50("div", { ref, "data-drawer-body": true, className: cn("flex-1 bg-background p-[28px]", className), ...props })
);
DrawerContent.displayName = "DrawerContent";
var DrawerFooter = React35.forwardRef(
  ({ className, sticky = true, ...props }, ref) => /* @__PURE__ */ jsx50(
    "footer",
    {
      ref,
      "data-drawer-footer": true,
      className: cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:items-center sm:space-x-2 px-[28px] py-4 border-t border-t-border bg-secondary",
        sticky && "sticky bottom-0 z-20",
        className
      ),
      ...props
    }
  )
);
DrawerFooter.displayName = "DrawerFooter";
var useDrawerContext = () => {
  const context = React35.useContext(DrawerContext);
  if (!context) {
    throw new Error("Drawer subcomponents must be used within a Drawer");
  }
  return context;
};
var DrawerCloseButton = React35.forwardRef(
  ({ children, onPress, ...props }, ref) => {
    const { close } = useDrawerContext();
    return /* @__PURE__ */ jsx50(
      Button,
      {
        ref,
        variant: "icon",
        size: "md",
        iconOnly: true,
        tooltip: "Close",
        "aria-label": "Close drawer",
        onPress: (event) => {
          onPress?.(event);
          close();
        },
        ...props,
        children: children ?? /* @__PURE__ */ jsx50(X2, { size: 18 })
      }
    );
  }
);
DrawerCloseButton.displayName = "DrawerCloseButton";
var DrawerExpandButton = React35.forwardRef(
  ({ children, onPress, ...props }, ref) => {
    const { onExpand } = useDrawerContext();
    if (!onExpand) return null;
    return /* @__PURE__ */ jsx50(
      Button,
      {
        ref,
        variant: "ghost",
        size: "lg",
        iconOnly: true,
        "aria-label": "Expand to full page",
        onPress: (event) => {
          onPress?.(event);
          onExpand();
        },
        ...props,
        children: children ?? /* @__PURE__ */ jsx50(ArrowsOut, { size: 20 })
      }
    );
  }
);
DrawerExpandButton.displayName = "DrawerExpandButton";
var DrawerWideButton = React35.forwardRef(
  ({ children, onPress, ...props }, ref) => {
    const ctx = React35.useContext(DrawerWidthContext);
    if (!ctx || !ctx.enabled) return null;
    return /* @__PURE__ */ jsx50(
      Button,
      {
        ref,
        variant: "ghost",
        size: "lg",
        iconOnly: true,
        "aria-label": ctx.isWide ? "Narrow drawer" : "Widen drawer",
        onPress: (event) => {
          onPress?.(event);
          ctx.toggleWide();
        },
        ...props,
        children: children ?? (ctx.isWide ? /* @__PURE__ */ jsx50(ArrowsInLineHorizontal, { size: 20 }) : /* @__PURE__ */ jsx50(ArrowsOutLineHorizontal, { size: 20 }))
      }
    );
  }
);
DrawerWideButton.displayName = "DrawerWideButton";

// src/components/SteppedDialog.tsx
import React37 from "react";

// src/components/StepIndicator.tsx
import React36 from "react";
import { motion as motion3 } from "framer-motion";
import { Fragment as Fragment10, jsx as jsx51, jsxs as jsxs45 } from "react/jsx-runtime";
var StepIndicator = React36.forwardRef(
  ({ steps, currentStepIndex, className, onStepSelect }, ref) => {
    return /* @__PURE__ */ jsx51(
      "nav",
      {
        ref,
        "aria-label": "Progress",
        className: cn("flex items-center justify-center gap-2", className),
        children: steps.map((step, index) => {
          const isCurrent = index === currentStepIndex;
          const sharedIndicator = /* @__PURE__ */ jsxs45(Fragment10, { children: [
            /* @__PURE__ */ jsx51(
              motion3.div,
              {
                className: cn("rounded-full", isCurrent ? "bg-primary" : "bg-muted"),
                animate: {
                  width: isCurrent ? 24 : 8,
                  height: 8
                },
                transition: {
                  type: "spring",
                  stiffness: 300,
                  damping: 30
                }
              }
            ),
            /* @__PURE__ */ jsx51("span", { className: "sr-only", children: step.label })
          ] });
          if (onStepSelect) {
            return /* @__PURE__ */ jsx51(
              "button",
              {
                type: "button",
                onClick: () => onStepSelect(index, step),
                className: cn(
                  "flex items-center justify-center rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                ),
                "aria-current": isCurrent ? "step" : void 0,
                "aria-label": `Step ${index + 1}: ${step.label}`,
                children: sharedIndicator
              },
              step.id
            );
          }
          return /* @__PURE__ */ jsx51(
            "span",
            {
              className: "flex cursor-default items-center justify-center rounded-full",
              "aria-current": isCurrent ? "step" : void 0,
              "aria-label": `Step ${index + 1}: ${step.label}`,
              children: sharedIndicator
            },
            step.id
          );
        })
      }
    );
  }
);
StepIndicator.displayName = "StepIndicator";

// src/components/SteppedDialog.tsx
import { jsx as jsx52, jsxs as jsxs46 } from "react/jsx-runtime";
var SteppedDialog = React37.forwardRef(
  ({
    steps,
    currentStep: controlledStep,
    onStepChange,
    onFinish,
    onCancel,
    renderFooter,
    finishButtonText = "Finish",
    showBackButton = true,
    isNavigationDisabled = false,
    className,
    ...dialogProps
  }, ref) => {
    const [internalStep, setInternalStep] = React37.useState(0);
    const [isValidating, setIsValidating] = React37.useState(false);
    const currentStepIndex = controlledStep !== void 0 ? controlledStep : internalStep;
    const setCurrentStepIndex = (index) => {
      if (controlledStep === void 0) {
        setInternalStep(index);
      }
      onStepChange?.(index);
    };
    const currentStepContent = steps[currentStepIndex];
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === steps.length - 1;
    const goToPreviousStep = () => {
      if (!isFirstStep && !isNavigationDisabled) {
        setCurrentStepIndex(currentStepIndex - 1);
      }
    };
    const goToNextStep = async () => {
      if (isLastStep || isNavigationDisabled) return;
      if (currentStepContent?.validate) {
        setIsValidating(true);
        try {
          const isValid = await currentStepContent.validate();
          if (!isValid) {
            setIsValidating(false);
            return;
          }
        } catch {
          setIsValidating(false);
          return;
        }
        setIsValidating(false);
      }
      setCurrentStepIndex(currentStepIndex + 1);
    };
    const handleFinish = async () => {
      if (isNavigationDisabled) return;
      if (currentStepContent?.validate) {
        setIsValidating(true);
        try {
          const isValid = await currentStepContent.validate();
          if (!isValid) {
            setIsValidating(false);
            return;
          }
        } catch {
          setIsValidating(false);
          return;
        }
        setIsValidating(false);
      }
      await onFinish?.();
    };
    const indicatorSteps = steps.map((step) => ({
      id: step.id,
      label: step.label,
      description: step.description
    }));
    const defaultFooter = /* @__PURE__ */ jsxs46(DialogFooter, { children: [
      /* @__PURE__ */ jsx52(Button, { variant: "ghost", onPress: onCancel, isDisabled: isNavigationDisabled, children: "Cancel" }),
      /* @__PURE__ */ jsx52("div", { className: "flex-1" }),
      showBackButton && !isFirstStep && /* @__PURE__ */ jsx52(Button, { variant: "outline", onPress: goToPreviousStep, isDisabled: isNavigationDisabled, children: "Back" }),
      !isLastStep ? /* @__PURE__ */ jsx52(
        Button,
        {
          variant: "primary",
          onPress: goToNextStep,
          isDisabled: isNavigationDisabled || isValidating,
          children: isValidating ? "Validating..." : "Next"
        }
      ) : /* @__PURE__ */ jsx52(
        Button,
        {
          variant: "primary",
          onPress: handleFinish,
          isDisabled: isNavigationDisabled || isValidating,
          children: isValidating ? "Validating..." : finishButtonText
        }
      )
    ] });
    return /* @__PURE__ */ jsx52(Dialog5, { ref, className: cn("max-w-3xl", className), ...dialogProps, children: /* @__PURE__ */ jsxs46(DialogContent, { children: [
      /* @__PURE__ */ jsx52(
        StepIndicator,
        {
          steps: indicatorSteps,
          currentStepIndex,
          className: "mb-6"
        }
      ),
      /* @__PURE__ */ jsx52("div", { className: "min-h-[300px]", children: currentStepContent?.render() }),
      renderFooter ? renderFooter({
        currentStepIndex,
        totalSteps: steps.length,
        isFirstStep,
        isLastStep,
        goToNextStep,
        goToPreviousStep,
        canGoNext: !isLastStep && !isNavigationDisabled
      }) : defaultFooter
    ] }) });
  }
);
SteppedDialog.displayName = "SteppedDialog";

// src/components/Toast.tsx
import React38 from "react";
import { motion as motion4 } from "framer-motion";
import { jsx as jsx53, jsxs as jsxs47 } from "react/jsx-runtime";
var variantStyles2 = {
  default: "bg-card border-border",
  success: "bg-card border-green-500/50",
  warning: "bg-card border-amber-500/50",
  destructive: "bg-card border-destructive/50",
  info: "bg-card border-blue-500/50"
};
var variantIcons = {
  default: null,
  success: /* @__PURE__ */ jsx53(CheckCircle, { size: 20, className: "text-green-600 dark:text-green-400" }),
  warning: /* @__PURE__ */ jsx53(Warning, { size: 20, className: "text-amber-600 dark:text-amber-400" }),
  destructive: /* @__PURE__ */ jsx53(XCircle, { size: 20, className: "text-destructive" }),
  info: /* @__PURE__ */ jsx53(Info, { size: 20, className: "text-blue-600 dark:text-blue-400" })
};
var Toast = React38.forwardRef(
  ({
    id: _id,
    message,
    variant = "default",
    duration = 2500,
    dismissible = true,
    icon,
    action,
    onDismiss
  }, ref) => {
    const [progress, setProgress] = React38.useState(100);
    const timerRef = React38.useRef(null);
    const startTimeRef = React38.useRef(Date.now());
    const remainingTimeRef = React38.useRef(duration);
    const startTimer = React38.useCallback(() => {
      if (duration === Infinity || !onDismiss) return;
      startTimeRef.current = Date.now();
      const updateProgress = () => {
        const elapsed = Date.now() - startTimeRef.current;
        const remaining = Math.max(0, remainingTimeRef.current - elapsed);
        const percent = remaining / duration * 100;
        setProgress(percent);
        if (remaining <= 0) {
          onDismiss();
        } else {
          timerRef.current = setTimeout(updateProgress, 50);
        }
      };
      updateProgress();
    }, [duration, onDismiss]);
    const pauseTimer = React38.useCallback(() => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      const elapsed = Date.now() - startTimeRef.current;
      remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
    }, []);
    const resumeTimer = React38.useCallback(() => {
      startTimer();
    }, [startTimer]);
    React38.useEffect(() => {
      startTimer();
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      };
    }, [startTimer]);
    const displayIcon = icon !== void 0 ? icon : variantIcons[variant];
    const hasIcon = Boolean(displayIcon);
    return /* @__PURE__ */ jsxs47(
      motion4.div,
      {
        ref,
        layout: true,
        initial: { opacity: 0, x: 300, scale: 0.8 },
        animate: { opacity: 1, x: 0, scale: 1 },
        exit: { opacity: 0, x: 300, scale: 0.8 },
        transition: { type: "spring", stiffness: 500, damping: 40 },
        onMouseEnter: pauseTimer,
        onMouseLeave: resumeTimer,
        onPointerDown: (event) => {
          event.stopPropagation();
        },
        className: cn(
          "relative pointer-events-auto rounded-lg border shadow-lg p-4",
          "grid items-start gap-3",
          hasIcon ? "grid-cols-[auto_1fr_auto]" : "grid-cols-[1fr_auto]",
          "min-w-[320px] max-w-[480px]",
          variantStyles2[variant]
        ),
        children: [
          hasIcon && /* @__PURE__ */ jsx53("div", { className: "flex-shrink-0 mt-0.5", children: displayIcon }),
          /* @__PURE__ */ jsxs47("div", { className: "flex-1 min-w-0", children: [
            /* @__PURE__ */ jsx53("p", { className: "text-sm text-foreground leading-relaxed break-words", children: message }),
            action && /* @__PURE__ */ jsx53(
              "button",
              {
                onClick: (event) => {
                  event.stopPropagation();
                  action.onClick();
                  onDismiss?.();
                },
                className: cn(
                  "mt-2 text-sm font-medium text-primary hover:text-primary/80",
                  "transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
                ),
                children: action.label
              }
            )
          ] }),
          dismissible && /* @__PURE__ */ jsx53(
            "button",
            {
              onClick: (event) => {
                event.stopPropagation();
                onDismiss?.();
              },
              className: cn(
                "self-start ml-1 p-1 rounded-md",
                "text-muted-foreground hover:text-foreground",
                "transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              ),
              "aria-label": "Dismiss notification",
              children: /* @__PURE__ */ jsx53(X2, { size: 16 })
            }
          ),
          duration !== Infinity && onDismiss && /* @__PURE__ */ jsx53("div", { className: "absolute bottom-0 left-0 right-0 h-1 bg-muted/30 rounded-b-lg overflow-hidden", children: /* @__PURE__ */ jsx53(
            motion4.div,
            {
              className: cn(
                "h-full",
                variant === "success" && "bg-green-500",
                variant === "warning" && "bg-amber-500",
                variant === "destructive" && "bg-destructive",
                variant === "info" && "bg-blue-500",
                variant === "default" && "bg-primary"
              ),
              initial: { width: "100%" },
              animate: { width: `${progress}%` },
              transition: { duration: 0.05, ease: "linear" }
            }
          ) })
        ]
      }
    );
  }
);
Toast.displayName = "Toast";

// src/components/ToastProvider.tsx
import React39 from "react";
import { createPortal as createPortal3 } from "react-dom";
import { AnimatePresence as AnimatePresence2 } from "framer-motion";
import { jsx as jsx54, jsxs as jsxs48 } from "react/jsx-runtime";
var ToastContext = React39.createContext(void 0);
var globalToastRef = null;
function getGlobalToast() {
  return globalToastRef;
}
var positionStyles = {
  "top-right": "top-4 right-4",
  "top-left": "top-4 left-4",
  "bottom-right": "bottom-4 right-4",
  "bottom-left": "bottom-4 left-4",
  "top-center": "top-4 left-1/2 -translate-x-1/2",
  "bottom-center": "bottom-4 left-1/2 -translate-x-1/2"
};
function ToastProvider({
  children,
  maxToasts = 3,
  position = "top-right",
  onError
}) {
  const [toasts, setToasts] = React39.useState([]);
  const [mounted, setMounted] = React39.useState(false);
  React39.useEffect(() => {
    setMounted(true);
  }, []);
  const addToast = React39.useCallback(
    (message, options = {}) => {
      const id = Math.random().toString(36).substring(2, 11);
      const newToast = {
        id,
        message,
        ...options
      };
      setToasts((prev) => {
        const updated = [newToast, ...prev];
        return updated.slice(0, maxToasts);
      });
    },
    [maxToasts]
  );
  const removeToast = React39.useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);
  const contextValue = React39.useMemo(
    () => ({
      addToast,
      removeToast,
      toast: {
        default: (message, options) => addToast(message, { ...options, variant: "default" }),
        success: (message, options) => addToast(message, { ...options, variant: "success" }),
        warning: (message, options) => addToast(message, { ...options, variant: "warning" }),
        error: (message, options) => {
          const { reportError = true, ...toastOptions } = options ?? {};
          if (reportError) {
            onError?.(message);
          }
          addToast(message, { ...toastOptions, variant: "destructive" });
        },
        info: (message, options) => addToast(message, { ...options, variant: "info" })
      }
    }),
    [addToast, removeToast, onError]
  );
  React39.useEffect(() => {
    globalToastRef = contextValue.toast;
    return () => {
      globalToastRef = null;
    };
  }, [contextValue.toast]);
  const toastContainer = mounted ? createPortal3(
    /* @__PURE__ */ jsx54(
      "div",
      {
        className: `fixed z-[60] flex flex-col gap-2 ${positionStyles[position]}`,
        "data-react-aria-top-layer": "true",
        "aria-live": "polite",
        "aria-atomic": "true",
        children: /* @__PURE__ */ jsx54(AnimatePresence2, { mode: "popLayout", children: toasts.map((toast) => /* @__PURE__ */ jsx54(Toast, { ...toast, onDismiss: () => removeToast(toast.id) }, toast.id)) })
      }
    ),
    document.body
  ) : null;
  return /* @__PURE__ */ jsxs48(ToastContext.Provider, { value: contextValue, children: [
    children,
    toastContainer
  ] });
}
function useToast() {
  const context = React39.useContext(ToastContext);
  if (context === void 0) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

// src/components/Popover.tsx
import React40 from "react";
import {
  Popover as AriaPopover
} from "react-aria-components";
import { DismissButton } from "react-aria";
import { useOverlayTriggerState } from "react-stately";
import { jsx as jsx55, jsxs as jsxs49 } from "react/jsx-runtime";
var PopoverContext = React40.createContext(null);
var mergeRefs3 = (...refs) => {
  return (value) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") {
        ref(value);
      } else {
        ref.current = value;
      }
    });
  };
};
var composeEventHandlers3 = (original, next) => (event) => {
  original?.(event);
  next?.(event);
};
function Popover10({
  children,
  isOpen,
  defaultOpen,
  onOpenChange,
  placement = "bottom",
  offset = 8,
  isNonModal = false
}) {
  const triggerRef = React40.useRef(null);
  const state = useOverlayTriggerState({
    isOpen,
    defaultOpen,
    onOpenChange
  });
  const contextValue = React40.useMemo(
    () => ({
      state,
      triggerRef,
      placement,
      offset,
      isNonModal
    }),
    [state, placement, offset, isNonModal]
  );
  return /* @__PURE__ */ jsx55(PopoverContext.Provider, { value: contextValue, children });
}
var usePopoverContext = () => {
  const context = React40.useContext(PopoverContext);
  if (!context) {
    throw new Error("Popover components must be used within <Popover>.");
  }
  return context;
};
var PopoverTrigger = React40.forwardRef(
  ({ children }, ref) => {
    const { state, triggerRef } = usePopoverContext();
    const child = React40.Children.only(children);
    const hasOnPress = typeof child.props.onPress === "function";
    const handleToggle = () => {
      state.toggle();
    };
    const childRef = child.ref;
    const mergedRef = mergeRefs3(childRef, triggerRef, ref);
    const triggerProps = {
      ref: mergedRef,
      "aria-expanded": state.isOpen,
      "data-state": state.isOpen ? "open" : "closed"
    };
    if (hasOnPress) {
      triggerProps.onPress = composeEventHandlers3(child.props.onPress, () => handleToggle());
      triggerProps.onClick = child.props.onClick;
    } else {
      triggerProps.onClick = composeEventHandlers3(child.props.onClick, () => {
        handleToggle();
      });
    }
    return React40.cloneElement(child, {
      ...triggerProps
    });
  }
);
PopoverTrigger.displayName = "PopoverTrigger";
var PopoverContent = React40.forwardRef(
  ({ children, className, placement, offset, isNonModal, ...props }, ref) => {
    const context = usePopoverContext();
    const { state, triggerRef } = context;
    return /* @__PURE__ */ jsxs49(
      AriaPopover,
      {
        ref,
        isOpen: state.isOpen,
        onOpenChange: state.setOpen,
        triggerRef,
        placement: placement ?? context.placement,
        offset: offset ?? context.offset,
        isNonModal: isNonModal ?? context.isNonModal,
        className: (renderProps) => cn(
          "z-50 min-w-[200px] rounded-md border bg-popover p-4 text-popover-foreground outline-none",
          "shadow-lg",
          "data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95",
          "data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95",
          typeof className === "function" ? className(renderProps) : className
        ),
        ...props,
        children: [
          /* @__PURE__ */ jsx55(DismissButton, { onDismiss: state.close }),
          children,
          /* @__PURE__ */ jsx55(DismissButton, { onDismiss: state.close })
        ]
      }
    );
  }
);
PopoverContent.displayName = "PopoverContent";

// src/components/TruncatedText.tsx
import React41 from "react";
import { jsx as jsx56, jsxs as jsxs50 } from "react/jsx-runtime";
var truncationClass = (lines) => {
  if (lines === 1) return "block truncate";
  return `block overflow-hidden line-clamp-${lines}`;
};
function checkTruncation(el, lines) {
  if (lines === 1) {
    return el.scrollWidth > el.clientWidth;
  }
  return el.scrollHeight > el.clientHeight;
}
var TruncatedText = ({
  text,
  lines = 1,
  className,
  popoverClassName,
  placement = "top"
}) => {
  const ref = React41.useRef(null);
  const [isTruncated, setIsTruncated] = React41.useState(false);
  const handleMouseEnter = React41.useCallback(() => {
    if (ref.current) {
      setIsTruncated(checkTruncation(ref.current, lines));
    }
  }, [lines]);
  return /* @__PURE__ */ jsxs50(TooltipTrigger, { delay: 300, placement, offset: 8, children: [
    /* @__PURE__ */ jsx56(
      "span",
      {
        ref,
        className: cn(truncationClass(lines), className),
        onMouseEnter: handleMouseEnter,
        children: text
      }
    ),
    isTruncated && /* @__PURE__ */ jsx56(
      Tooltip,
      {
        variant: "light",
        className: cn("max-w-xs whitespace-pre-wrap leading-relaxed", popoverClassName),
        children: text
      }
    )
  ] });
};

// src/components/InlineEditableText.tsx
import React42 from "react";
import { Fragment as Fragment11, jsx as jsx57, jsxs as jsxs51 } from "react/jsx-runtime";
var BOX_METRICS = "rounded-md border px-2 py-1";
function InlineEditableText({
  value,
  onSave,
  canEdit = true,
  multiline = false,
  placeholder,
  className,
  inputClassName,
  renderDisplay,
  ariaLabel,
  autoEdit = false,
  allowEmpty = false,
  onEditingChange
}) {
  const [editing, setEditing] = React42.useState(() => autoEdit && canEdit);
  const [draft, setDraft] = React42.useState(value);
  const canceledRef = React42.useRef(false);
  const fieldRef = React42.useRef(null);
  React42.useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);
  React42.useLayoutEffect(() => {
    onEditingChange?.(editing);
  }, [editing]);
  React42.useEffect(() => {
    if (editing && fieldRef.current) {
      fieldRef.current.focus();
      fieldRef.current.select();
    }
  }, [editing]);
  const start = () => {
    if (!canEdit) return;
    setDraft(value);
    setEditing(true);
  };
  const commit = () => {
    const next = draft.trim();
    if ((allowEmpty || next.length > 0) && next !== value) onSave(next);
    setEditing(false);
  };
  const cancel = () => {
    canceledRef.current = true;
    setDraft(value);
    setEditing(false);
  };
  const handleBlur = () => {
    if (canceledRef.current) {
      canceledRef.current = false;
      return;
    }
    commit();
  };
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      cancel();
      return;
    }
    if (e.key === "Enter") {
      if (!multiline || e.metaKey || e.ctrlKey) {
        e.preventDefault();
        e.stopPropagation();
        commit();
      }
    }
  };
  if (editing) {
    const effectiveInputClassName = inputClassName ?? className;
    const shared = {
      ref: (el) => {
        fieldRef.current = el;
      },
      value: draft,
      onChange: (e) => setDraft(e.target.value),
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      placeholder,
      "aria-label": ariaLabel
    };
    const base = cn(
      BOX_METRICS,
      // `-mx-2` + `w-[calc(100%+1rem)]` bleeds the field 0.5rem past each
      // side of its container and grows it to match, so its own `px-2`
      // lands the caret at the exact x where the display text sits — the
      // same trick display mode uses below, applied to a full-width field
      // instead of a content-hugging one.
      "-mx-2 w-[calc(100%+1rem)] border-ring/60 bg-background outline-none ring-2 ring-ring/15 transition-colors"
    );
    const keepFocus = (e) => e.preventDefault();
    return /* @__PURE__ */ jsxs51("div", { children: [
      multiline ? /* @__PURE__ */ jsx57(
        "textarea",
        {
          ...shared,
          rows: 4,
          className: cn(base, "resize-y leading-relaxed", effectiveInputClassName)
        }
      ) : /* @__PURE__ */ jsx57("input", { ...shared, type: "text", className: cn(base, effectiveInputClassName) }),
      /* @__PURE__ */ jsxs51("div", { className: "mt-2 flex items-center gap-2", children: [
        multiline && /* @__PURE__ */ jsxs51(Fragment11, { children: [
          /* @__PURE__ */ jsx57(
            "button",
            {
              type: "button",
              onMouseDown: keepFocus,
              onClick: commit,
              className: "rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90",
              children: "Save"
            }
          ),
          /* @__PURE__ */ jsx57(
            "button",
            {
              type: "button",
              onMouseDown: keepFocus,
              onClick: cancel,
              className: "rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground",
              children: "Cancel"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs51("span", { className: "select-none text-xs text-muted-foreground", children: [
          /* @__PURE__ */ jsx57("kbd", { className: "font-sans font-medium text-foreground", children: multiline ? "\u2318 + Enter" : "Enter" }),
          " ",
          "to save \xB7",
          " ",
          /* @__PURE__ */ jsx57("kbd", { className: "font-sans font-medium text-foreground", children: "Esc" }),
          " to cancel"
        ] })
      ] })
    ] });
  }
  const isEmpty = value.trim().length === 0;
  return (
    // Three nested elements, each doing exactly one job — this looks like
    // more markup than it needs to be, but each layer is load-bearing
    // against a real cross-browser sizing bug, not a style preference:
    // a negative margin and a percentage `max-width` on the SAME element
    // make `w-fit` intrinsic sizing drop that element's own padding from
    // the computed width (silently shrinking the hover box a few px short
    // of its text), and a percentage `max-width` with no negative margin
    // still can't shrink a `white-space: nowrap` (from a caller's
    // `truncate`) box below its own full-text width unless something
    // upstream has ALREADY been clamped to a concrete pixel size.
    //  - BLEED (outermost): only the `-mx-2` bleed + `block`, no
    //    max-width of its own — it fills 100% of whatever width its own
    //    parent (e.g. a caller's `max-w-[40%]` wrapper) resolves to, which
    //    is exactly how the pre-redesign version aligned text without
    //    fighting this bug, just reused here as the outermost layer.
    //  - CLAMP (middle): `w-fit` + `max-w-full`, no margin of its own —
    //    hugs the INNER box's content width, but forced back down to
    //    BLEED's now-concrete width when content is too wide to fit.
    //  - INNER: the actual padding/border/radius/hover box, plus the
    //    caller's typography/`truncate`/`max-w-*`.
    /* @__PURE__ */ jsx57(
      "div",
      {
        title: canEdit ? "Double-click to edit" : void 0,
        className: cn("-mx-2", multiline ? "block w-full" : "block"),
        children: /* @__PURE__ */ jsx57("div", { className: multiline ? void 0 : "w-fit max-w-full", children: /* @__PURE__ */ jsx57(
          "div",
          {
            onDoubleClick: start,
            className: cn(
              BOX_METRICS,
              // `px-2` here is what the outermost `-mx-2` is bleeding room
              // for — net zero offset, so the text sits exactly where plain
              // text would, while the box has room to grow a border outward
              // on hover without nudging anything. `border-transparent`
              // reserves that border's width at rest so hovering (which only
              // swaps the border's color) can't shift layout by a pixel.
              "border-transparent",
              multiline ? "w-full" : "max-w-full",
              canEdit && "cursor-text transition-colors hover:border-ring/50",
              className
            ),
            children: isEmpty ? /* @__PURE__ */ jsx57("span", { className: "italic text-muted-foreground", children: placeholder ?? "Empty" }) : renderDisplay ? renderDisplay(value) : value
          }
        ) })
      }
    )
  );
}

// src/components/Banner.tsx
import { jsx as jsx58 } from "react/jsx-runtime";
var variantStyles3 = {
  info: "bg-blue-50 cofactor-dark:bg-blue-950 border-blue-200 cofactor-dark:border-blue-800 text-blue-800 cofactor-dark:text-blue-200",
  warning: "bg-amber-50 cofactor-dark:bg-amber-950 border-amber-200 cofactor-dark:border-amber-800 text-amber-800 cofactor-dark:text-amber-200",
  success: "bg-emerald-50 cofactor-dark:bg-emerald-950 border-emerald-200 cofactor-dark:border-emerald-800 text-emerald-800 cofactor-dark:text-emerald-200",
  error: "bg-red-50 cofactor-dark:bg-red-950 border-red-200 cofactor-dark:border-red-800 text-red-800 cofactor-dark:text-red-200"
};
function Banner({ children, variant = "info", className }) {
  return /* @__PURE__ */ jsx58(
    "div",
    {
      className: cn(
        "flex items-start justify-between gap-4 px-4 py-2 border rounded-lg",
        variantStyles3[variant],
        className
      ),
      children
    }
  );
}

// src/components/EditableBadge.tsx
import {
  Button as Button9,
  ListBox as ListBox4,
  ListBoxItem as ListBoxItem4,
  Popover as Popover11,
  Select as Select2,
  SelectValue as SelectValue2
} from "react-aria-components";

// src/components/Spinner.tsx
import { jsx as jsx59 } from "react/jsx-runtime";
var sizeMap = {
  xs: "h-3 w-3",
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-6 w-6"
};
function Spinner({ size = "sm", className }) {
  return /* @__PURE__ */ jsx59(
    "span",
    {
      className: cn(
        "inline-flex animate-spin rounded-full border-2 border-current border-t-transparent",
        sizeMap[size],
        className
      ),
      role: "status",
      "aria-label": "Loading"
    }
  );
}

// src/components/EditableBadge.tsx
import { jsx as jsx60, jsxs as jsxs52 } from "react/jsx-runtime";
function variantClasses(variant) {
  const styles = TAG_COLORS[legacyBadgeVariantToColor[variant]];
  return cn(styles.bg, styles.text, styles.border, styles.hover);
}
var badgeSizes = {
  sm: "px-1.5 py-0.5 text-xs gap-1",
  md: "px-2 py-0.5 text-xs gap-1.5",
  lg: "px-2.5 py-1 text-sm gap-2"
};
function ChevronDownIcon({ className }) {
  return /* @__PURE__ */ jsx60(
    "svg",
    {
      className,
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      children: /* @__PURE__ */ jsx60("polyline", { points: "6 9 12 15 18 9" })
    }
  );
}
function EditableBadge({
  value,
  options,
  onChange,
  variant = "default",
  size = "md",
  isLoading = false,
  isDisabled = false,
  getVariant,
  formatLabel,
  "aria-label": ariaLabel,
  className
}) {
  const selectedOption = options.find((opt) => opt.id === value);
  const displayLabel = formatLabel?.(value) ?? selectedOption?.label ?? value;
  const currentVariant = getVariant?.(value) ?? variant;
  const handleSelectionChange = (key) => {
    if (key && typeof key === "string") {
      onChange(key);
    }
  };
  return /* @__PURE__ */ jsxs52(
    Select2,
    {
      selectedKey: value,
      onSelectionChange: handleSelectionChange,
      isDisabled: isDisabled || isLoading,
      "aria-label": ariaLabel,
      children: [
        /* @__PURE__ */ jsxs52(
          Button9,
          {
            className: cn(
              "inline-flex items-center rounded-md border font-medium transition-colors cursor-pointer",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              "data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed",
              variantClasses(currentVariant ?? "default"),
              badgeSizes[size ?? "md"],
              className
            ),
            onPress: (e) => {
              e.continuePropagation = () => {
              };
            },
            children: [
              isLoading ? /* @__PURE__ */ jsx60(Spinner, { size: "xs" }) : null,
              /* @__PURE__ */ jsx60(SelectValue2, { children: () => displayLabel }),
              /* @__PURE__ */ jsx60(ChevronDownIcon, { className: "h-3 w-3 opacity-60 -mr-0.5" })
            ]
          }
        ),
        /* @__PURE__ */ jsx60(
          Popover11,
          {
            className: cn(
              "min-w-[8rem] rounded-md border bg-popover text-popover-foreground shadow-lg z-50",
              "data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95",
              "data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95",
              "data-[placement=bottom]:slide-in-from-top-2",
              "data-[placement=top]:slide-in-from-bottom-2"
            ),
            children: /* @__PURE__ */ jsx60(ListBox4, { className: "max-h-60 overflow-auto p-1", items: options, children: (item) => /* @__PURE__ */ jsx60(
              ListBoxItem4,
              {
                id: item.id,
                textValue: item.label,
                className: cn(
                  "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 px-2 text-sm outline-none",
                  "data-[focused]:bg-accent data-[focused]:text-accent-foreground",
                  "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                  "data-[selected]:font-semibold"
                ),
                isDisabled: item.disabled,
                children: item.label
              },
              item.id
            ) })
          }
        )
      ]
    }
  );
}

// src/components/Card.tsx
import React43 from "react";
import { jsx as jsx61 } from "react/jsx-runtime";
var cardVariants = {
  default: "bg-card text-card-foreground",
  bordered: "bg-card text-card-foreground border border-border",
  elevated: "bg-card text-card-foreground shadow-lg"
};
var cardPadding = {
  none: "",
  sm: "p-4",
  md: "p-6",
  lg: "p-8"
};
var Card = React43.forwardRef(
  ({ variant = "bordered", padding = "md", className, ...props }, ref) => {
    return /* @__PURE__ */ jsx61(
      "div",
      {
        ref,
        className: cn(
          "rounded-lg",
          "gap-y-2",
          "flex flex-col",
          cardVariants[variant],
          cardPadding[padding],
          className
        ),
        ...props
      }
    );
  }
);
Card.displayName = "Card";
var CardHeader = React43.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx61("div", { ref, className: cn("flex flex-col space-y-1.5", className), ...props })
);
CardHeader.displayName = "CardHeader";
var CardContent = React43.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx61("div", { ref, className: cn("pt-0", className), ...props })
);
CardContent.displayName = "CardContent";
var CardFooter = React43.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx61("div", { ref, className: cn("flex items-center pt-4", className), ...props })
);
CardFooter.displayName = "CardFooter";

// src/components/EmptyState.tsx
import React44 from "react";

// src/components/EmptyStateGraphic.tsx
import { jsx as jsx62, jsxs as jsxs53 } from "react/jsx-runtime";
var EmptyStateGraphic = ({ icon }) => /* @__PURE__ */ jsxs53("div", { "data-empty-state-graphic": true, "aria-hidden": "true", children: [
  /* @__PURE__ */ jsxs53("svg", { viewBox: "0 0 160 110", width: "160", height: "110", fill: "none", children: [
    /* @__PURE__ */ jsx62(
      "path",
      {
        className: "cf-esg-path",
        d: "M14 80 C 30 30, 98 8, 146 36",
        strokeWidth: "1.5",
        strokeDasharray: "3 5",
        strokeLinecap: "round"
      }
    ),
    /* @__PURE__ */ jsx62("circle", { className: "cf-esg-node-start", cx: "14", cy: "80", r: "2.5" }),
    /* @__PURE__ */ jsx62("rect", { className: "cf-esg-card cf-esg-back", x: "30", y: "20", width: "62", height: "44", rx: "8" }),
    /* @__PURE__ */ jsx62("rect", { className: "cf-esg-card cf-esg-mid", x: "58", y: "24", width: "62", height: "44", rx: "8" }),
    /* @__PURE__ */ jsx62("rect", { className: "cf-esg-card cf-esg-front", x: "42", y: "36", width: "72", height: "48", rx: "10" }),
    /* @__PURE__ */ jsx62("circle", { className: "cf-esg-node", cx: "146", cy: "36", r: "4" })
  ] }),
  icon && /* @__PURE__ */ jsx62("div", { "data-empty-state-graphic-icon": true, children: icon })
] });
EmptyStateGraphic.displayName = "EmptyStateGraphic";

// src/components/EmptyState.tsx
import { jsx as jsx63, jsxs as jsxs54 } from "react/jsx-runtime";
var sizeStyles2 = {
  sm: "px-4 py-8 gap-2",
  md: "px-6 py-14 gap-3"
};
var iconSizeStyles = {
  sm: "h-9 w-9",
  md: "h-11 w-11"
};
var EmptyState = React44.forwardRef(
  ({ icon, graphic, title, description, action, size = "md", className, ...rest }, ref) => {
    return /* @__PURE__ */ jsxs54(
      "div",
      {
        ref,
        "data-empty-state": true,
        "data-size": size,
        className: cn(
          "flex flex-col items-center justify-center rounded-xl border border-dashed border-border/60 bg-muted/40 text-center",
          sizeStyles2[size],
          className
        ),
        ...rest,
        children: [
          graphic ?? /* @__PURE__ */ jsx63(EmptyStateGraphic, { icon }),
          icon && /* @__PURE__ */ jsx63(
            "div",
            {
              "data-empty-state-icon": true,
              className: cn(
                "flex items-center justify-center rounded-full bg-background text-muted-foreground/70 shadow-sm",
                iconSizeStyles[size]
              ),
              children: icon
            }
          ),
          /* @__PURE__ */ jsxs54("div", { className: "space-y-1", children: [
            /* @__PURE__ */ jsx63("div", { "data-empty-state-title": true, className: "text-sm font-semibold text-foreground", children: title }),
            description && /* @__PURE__ */ jsx63("div", { "data-empty-state-description": true, className: "text-sm text-muted-foreground", children: description })
          ] }),
          action && /* @__PURE__ */ jsx63("div", { "data-empty-state-action": true, className: "mt-1", children: action })
        ]
      }
    );
  }
);
EmptyState.displayName = "EmptyState";

// src/components/EmptyStateScanGraphic.tsx
import { jsx as jsx64, jsxs as jsxs55 } from "react/jsx-runtime";
var EmptyStateScanGraphic = () => /* @__PURE__ */ jsx64("div", { "data-empty-state-graphic": true, "aria-hidden": "true", children: /* @__PURE__ */ jsxs55("svg", { viewBox: "0 0 160 110", width: "160", height: "110", fill: "none", children: [
  /* @__PURE__ */ jsx64("circle", { className: "cf-scan-ring", cx: "80", cy: "56", r: "44", strokeWidth: "1.5", strokeDasharray: "3 5", strokeLinecap: "round" }),
  /* @__PURE__ */ jsx64("circle", { className: "cf-scan-ring", cx: "80", cy: "56", r: "30", strokeWidth: "1", strokeDasharray: "2 6", strokeLinecap: "round" }),
  /* @__PURE__ */ jsx64("circle", { className: "cf-scan-ring cf-scan-ring-inner", cx: "80", cy: "56", r: "17", strokeWidth: "1" }),
  /* @__PURE__ */ jsxs55("g", { className: "cf-scan-sweep", children: [
    /* @__PURE__ */ jsx64("path", { className: "cf-scan-wedge", d: "M80 56 L80 12 A44 44 0 0 1 108.3 22.3 Z" }),
    /* @__PURE__ */ jsx64("line", { className: "cf-scan-beam", x1: "80", y1: "56", x2: "80", y2: "12", strokeWidth: "1.5", strokeLinecap: "round" })
  ] }),
  /* @__PURE__ */ jsx64("circle", { className: "cf-scan-blip", cx: "110.3", cy: "34.8", r: "3" }),
  /* @__PURE__ */ jsx64("circle", { className: "cf-scan-halo", cx: "80", cy: "56", r: "11", strokeWidth: "1.5" }),
  /* @__PURE__ */ jsx64("circle", { className: "cf-scan-disc", cx: "80", cy: "56", r: "11", strokeWidth: "1" }),
  /* @__PURE__ */ jsx64("path", { className: "cf-scan-check", d: "M74.5 56.4 l3.6 3.6 l7.4 -8", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })
] }) });
EmptyStateScanGraphic.displayName = "EmptyStateScanGraphic";

// src/components/EmptyStateCompareGraphic.tsx
import { jsx as jsx65, jsxs as jsxs56 } from "react/jsx-runtime";
var EmptyStateCompareGraphic = () => /* @__PURE__ */ jsx65("div", { "data-empty-state-graphic": true, "aria-hidden": "true", children: /* @__PURE__ */ jsxs56("svg", { viewBox: "0 0 160 110", width: "160", height: "110", fill: "none", children: [
  /* @__PURE__ */ jsx65("path", { className: "cf-cmp-link", d: "M72 56 H88", strokeWidth: "1.5", strokeDasharray: "3 5", strokeLinecap: "round" }),
  /* @__PURE__ */ jsx65("rect", { className: "cf-cmp-frame", x: "12", y: "24", width: "60", height: "64", rx: "8", strokeWidth: "1" }),
  /* @__PURE__ */ jsx65("rect", { className: "cf-cmp-line", x: "20", y: "33", width: "28", height: "4", rx: "2" }),
  /* @__PURE__ */ jsx65("rect", { className: "cf-cmp-block", x: "20", y: "45", width: "44", height: "14", rx: "3" }),
  /* @__PURE__ */ jsx65("rect", { className: "cf-cmp-line", x: "20", y: "67", width: "34", height: "4", rx: "2" }),
  /* @__PURE__ */ jsx65("rect", { className: "cf-cmp-frame", x: "88", y: "24", width: "60", height: "64", rx: "8", strokeWidth: "1" }),
  /* @__PURE__ */ jsx65("rect", { className: "cf-cmp-line", x: "96", y: "33", width: "28", height: "4", rx: "2" }),
  /* @__PURE__ */ jsx65("rect", { className: "cf-cmp-block", x: "96", y: "45", width: "44", height: "14", rx: "3" }),
  /* @__PURE__ */ jsx65("rect", { className: "cf-cmp-line", x: "96", y: "67", width: "34", height: "4", rx: "2" }),
  /* @__PURE__ */ jsx65("rect", { className: "cf-cmp-diff", x: "92", y: "41", width: "52", height: "22", rx: "5", strokeWidth: "1.5", strokeDasharray: "3 3" })
] }) });
EmptyStateCompareGraphic.displayName = "EmptyStateCompareGraphic";

// src/components/EmptyStateSpeedGraphic.tsx
import { jsx as jsx66, jsxs as jsxs57 } from "react/jsx-runtime";
var EmptyStateSpeedGraphic = () => /* @__PURE__ */ jsx66("div", { "data-empty-state-graphic": true, "aria-hidden": "true", children: /* @__PURE__ */ jsxs57("svg", { viewBox: "0 0 160 110", width: "160", height: "110", fill: "none", children: [
  /* @__PURE__ */ jsx66("line", { className: "cf-spd-guide", x1: "136", y1: "26", x2: "136", y2: "84", strokeWidth: "1.5", strokeDasharray: "3 5", strokeLinecap: "round" }),
  /* @__PURE__ */ jsx66("rect", { className: "cf-spd-base", x: "24", y: "35", width: "112", height: "12", rx: "6" }),
  /* @__PURE__ */ jsx66("rect", { className: "cf-spd-head", x: "24", y: "63", width: "112", height: "12", rx: "6" }),
  /* @__PURE__ */ jsx66("circle", { className: "cf-spd-dot", cx: "136", cy: "69", r: "4" })
] }) });
EmptyStateSpeedGraphic.displayName = "EmptyStateSpeedGraphic";

// src/components/FilterToolbar.tsx
import React45 from "react";
import { CaretDown as CaretDown6, Check as Check2, MagnifyingGlass as MagnifyingGlass4, Prohibit, SlidersHorizontal } from "@phosphor-icons/react";
import {
  Autocomplete,
  Dialog as Dialog6,
  DialogTrigger as DialogTrigger2,
  Input as Input4,
  ListBox as ListBox5,
  ListBoxItem as ListBoxItem5,
  Popover as Popover12,
  SearchField
} from "react-aria-components";
import { jsx as jsx67, jsxs as jsxs58 } from "react/jsx-runtime";
var SEARCH_THRESHOLD = 8;
function optionContains(textValue, inputValue) {
  return textValue.toLowerCase().includes(inputValue.trim().toLowerCase());
}
function sortByLabel(options) {
  return [...options].sort((a, b) => a.label.localeCompare(b.label));
}
function FilterCountBadge({ count }) {
  if (count <= 0) return null;
  return /* @__PURE__ */ jsx67("span", { className: "inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-primary/10 text-[10px] font-semibold text-primary tabular-nums", children: count });
}
function FilterTriggerButton({
  label,
  count,
  isDisabled
}) {
  return /* @__PURE__ */ jsxs58(
    Button,
    {
      variant: "ghost",
      size: "sm",
      className: cn("gap-1.5 text-muted-foreground", count > 0 && "text-foreground"),
      isDisabled,
      children: [
        label,
        /* @__PURE__ */ jsx67(FilterCountBadge, { count }),
        /* @__PURE__ */ jsx67(CaretDown6, { size: 11, weight: "bold", className: "opacity-60" })
      ]
    }
  );
}
function FilterPopoverSearch({ label }) {
  return /* @__PURE__ */ jsx67(SearchField, { "aria-label": `Search ${label}`, className: "px-1.5 pt-1.5 pb-1", autoFocus: true, children: /* @__PURE__ */ jsxs58("div", { className: "relative flex items-center", children: [
    /* @__PURE__ */ jsx67(
      MagnifyingGlass4,
      {
        size: 14,
        className: "absolute left-2 text-muted-foreground pointer-events-none"
      }
    ),
    /* @__PURE__ */ jsx67(
      Input4,
      {
        placeholder: `Search ${label.toLowerCase()}\u2026`,
        className: cn(
          "w-full h-8 rounded-md border border-border bg-muted/50 pl-7 pr-2 text-sm",
          "placeholder:text-muted-foreground",
          "focus-visible:outline-none focus-visible:!border-ring/40 focus-visible:!bg-background focus-visible:!shadow-none"
        )
      }
    )
  ] }) });
}
function FilterPopover({
  label,
  searchable,
  children
}) {
  return /* @__PURE__ */ jsx67(
    Popover12,
    {
      placement: "bottom start",
      className: cn(
        "w-60 rounded-md border bg-popover text-popover-foreground shadow-md outline-none",
        "data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95",
        "data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95"
      ),
      children: /* @__PURE__ */ jsx67(Dialog6, { "aria-label": label, className: "outline-none", children: searchable ? /* @__PURE__ */ jsxs58(Autocomplete, { filter: optionContains, children: [
        /* @__PURE__ */ jsx67(FilterPopoverSearch, { label }),
        children
      ] }) : children })
    }
  );
}
var LIST_ITEM_CLASS = cn(
  "relative flex w-full cursor-default select-none items-center gap-2 rounded-sm py-1.5 px-2 text-sm outline-none",
  "data-[focused]:bg-accent data-[focused]:text-accent-foreground",
  "data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
);
var LIST_BOX_CLASS = "max-h-72 overflow-auto p-1 outline-none";
function ToolbarFilterMenu({
  label,
  options,
  selectedIds,
  onChange,
  isLoading = false,
  selectionMode = "multiple",
  searchable
}) {
  const [isOpen, setIsOpen] = React45.useState(false);
  const selected = React45.useMemo(() => new Set(selectedIds), [selectedIds]);
  const sorted = React45.useMemo(() => sortByLabel(options), [options]);
  const showSearch = searchable ?? options.length > SEARCH_THRESHOLD;
  const handleSelectionChange = React45.useCallback(
    (keys) => {
      if (keys === "all") return;
      onChange(Array.from(keys, String));
      if (selectionMode === "single") setIsOpen(false);
    },
    [onChange, selectionMode]
  );
  return /* @__PURE__ */ jsxs58(DialogTrigger2, { isOpen, onOpenChange: setIsOpen, children: [
    /* @__PURE__ */ jsx67(FilterTriggerButton, { label, count: selected.size, isDisabled: isLoading }),
    /* @__PURE__ */ jsx67(FilterPopover, { label, searchable: showSearch, children: /* @__PURE__ */ jsx67(
      ListBox5,
      {
        items: sorted,
        selectionMode,
        selectedKeys: selected,
        onSelectionChange: handleSelectionChange,
        className: LIST_BOX_CLASS,
        dependencies: [selectedIds],
        renderEmptyState: () => /* @__PURE__ */ jsx67("div", { className: "px-2 py-3 text-center text-sm text-muted-foreground", children: isLoading ? "Loading\u2026" : "No options" }),
        children: (option) => /* @__PURE__ */ jsxs58(ListBoxItem5, { id: option.id, textValue: option.label, className: LIST_ITEM_CLASS, children: [
          /* @__PURE__ */ jsx67("span", { className: "flex h-3.5 w-3.5 items-center justify-center shrink-0", children: selected.has(option.id) && /* @__PURE__ */ jsx67(Check2, { size: 12, weight: "bold" }) }),
          option.label
        ] })
      }
    ) })
  ] });
}
function ToolbarFilterMenuTriState({
  label,
  options,
  value,
  onChange,
  isLoading = false,
  searchable
}) {
  const include = React45.useMemo(() => new Set(value.include), [value.include]);
  const exclude = React45.useMemo(() => new Set(value.exclude), [value.exclude]);
  const sorted = React45.useMemo(() => sortByLabel(options), [options]);
  const showSearch = searchable ?? options.length > SEARCH_THRESHOLD;
  const count = include.size + exclude.size;
  const cycle = React45.useCallback(
    (id) => {
      const nextInclude = new Set(include);
      const nextExclude = new Set(exclude);
      if (include.has(id)) {
        nextInclude.delete(id);
        nextExclude.add(id);
      } else if (exclude.has(id)) {
        nextExclude.delete(id);
      } else {
        nextInclude.add(id);
      }
      onChange({ include: Array.from(nextInclude), exclude: Array.from(nextExclude) });
    },
    [include, exclude, onChange]
  );
  return /* @__PURE__ */ jsxs58(DialogTrigger2, { children: [
    /* @__PURE__ */ jsx67(FilterTriggerButton, { label, count, isDisabled: isLoading }),
    /* @__PURE__ */ jsx67(FilterPopover, { label, searchable: showSearch, children: /* @__PURE__ */ jsx67(
      ListBox5,
      {
        items: sorted,
        selectionMode: "none",
        onAction: (key) => cycle(String(key)),
        className: LIST_BOX_CLASS,
        dependencies: [value.include, value.exclude],
        renderEmptyState: () => /* @__PURE__ */ jsx67("div", { className: "px-2 py-3 text-center text-sm text-muted-foreground", children: isLoading ? "Loading\u2026" : "No options" }),
        children: (option) => {
          const polarity = include.has(option.id) ? "include" : exclude.has(option.id) ? "exclude" : void 0;
          return /* @__PURE__ */ jsxs58(
            ListBoxItem5,
            {
              id: option.id,
              textValue: option.label,
              className: cn(LIST_ITEM_CLASS, polarity === "exclude" && "text-muted-foreground"),
              children: [
                /* @__PURE__ */ jsxs58("span", { className: "flex h-3.5 w-3.5 items-center justify-center shrink-0", children: [
                  polarity === "include" && /* @__PURE__ */ jsx67(Check2, { size: 12, weight: "bold", className: "text-primary" }),
                  polarity === "exclude" && /* @__PURE__ */ jsx67(Prohibit, { size: 12, weight: "bold", className: "text-destructive" })
                ] }),
                /* @__PURE__ */ jsx67("span", { className: cn("flex-1", polarity === "exclude" && "line-through"), children: option.label })
              ]
            }
          );
        }
      }
    ) })
  ] });
}
function FilterToolbarSearch({
  value,
  onChange,
  placeholder,
  "aria-label": ariaLabel,
  className,
  inputRef
}) {
  return /* @__PURE__ */ jsx67(
    TextField,
    {
      ref: inputRef,
      "aria-label": ariaLabel,
      placeholder,
      value,
      onChange,
      leftIcon: /* @__PURE__ */ jsx67(MagnifyingGlass4, { size: 15 }),
      className: cn(
        // !important so the cofactor input-chrome border rules don't
        // re-cordon the well inside the toolbar
        "w-56 mr-1.5 [&_input]:!h-8 [&_input]:!border-transparent [&_input]:!bg-muted/50 [&_input]:focus-visible:!bg-background [&_input]:focus-visible:!border-ring/40",
        className
      )
    }
  );
}
function FilterToolbarDivider() {
  return /* @__PURE__ */ jsx67("span", { className: "h-4 w-px bg-border mx-1", "aria-hidden": true });
}
function FilterToolbarSpacer() {
  return /* @__PURE__ */ jsx67("span", { className: "flex-1", "aria-hidden": true });
}
var MoreFiltersButton = React45.forwardRef(({ count = 0, label = "More", ...rest }, ref) => /* @__PURE__ */ jsxs58(
  Button,
  {
    ref,
    variant: "ghost",
    size: "sm",
    className: "gap-1.5 text-muted-foreground",
    iconLeft: /* @__PURE__ */ jsx67(SlidersHorizontal, { size: 14 }),
    ...rest,
    children: [
      label,
      /* @__PURE__ */ jsx67(FilterCountBadge, { count })
    ]
  }
));
MoreFiltersButton.displayName = "MoreFiltersButton";
function FilterToolbarChips({
  chips,
  onRemove
}) {
  if (chips.length === 0) return null;
  return /* @__PURE__ */ jsx67(
    "div",
    {
      "data-chips-row": true,
      className: "basis-full flex flex-wrap items-center gap-1 border-t border-border/60 -mx-2 px-2.5 pt-1.5 mt-1.5 pb-0.5",
      children: chips.map((chip) => /* @__PURE__ */ jsx67(
        TagBadge,
        {
          name: chip.label,
          color: "gray",
          size: "sm",
          hideDot: true,
          onRemove: () => onRemove(chip.id)
        },
        chip.id
      ))
    }
  );
}
function FilterToolbar({
  attached = false,
  className,
  children
}) {
  return /* @__PURE__ */ jsx67(
    "div",
    {
      "data-component": "filter-toolbar",
      className: cn(
        "flex flex-wrap items-center gap-0.5 rounded-md border bg-card px-2 py-1.5",
        attached && "rounded-b-none border-b-0",
        className
      ),
      children
    }
  );
}

// src/components/EntityCard.tsx
import React46 from "react";
import { jsx as jsx68, jsxs as jsxs59 } from "react/jsx-runtime";
var EntityCard = React46.forwardRef(
  ({ avatar, title, status, meta, description, footer, actionLabel, onPress, className, ...rest }, ref) => {
    const interactive = Boolean(onPress);
    return /* @__PURE__ */ jsxs59(
      "div",
      {
        ref,
        "data-entity-card": true,
        className: cn(
          "group flex flex-col rounded-lg border border-border bg-card p-5 transition-colors",
          interactive && "cursor-pointer hover:border-primary/40",
          className
        ),
        ...interactive ? {
          role: "button",
          tabIndex: 0,
          onClick: () => onPress?.(),
          onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onPress?.();
            }
          }
        } : {},
        ...rest,
        children: [
          /* @__PURE__ */ jsxs59("div", { className: "flex items-start gap-3", children: [
            avatar && /* @__PURE__ */ jsx68("div", { "data-entity-card-avatar": true, className: "shrink-0", children: avatar }),
            /* @__PURE__ */ jsxs59("div", { className: "min-w-0 flex-1", children: [
              /* @__PURE__ */ jsxs59("div", { className: "flex items-start justify-between gap-2", children: [
                /* @__PURE__ */ jsx68("div", { "data-entity-card-title": true, className: "truncate text-base font-semibold", children: title }),
                status && /* @__PURE__ */ jsx68("div", { "data-entity-card-status": true, className: "flex shrink-0 items-center gap-1", children: status })
              ] }),
              meta && /* @__PURE__ */ jsx68("div", { "data-entity-card-meta": true, className: "mt-0.5 text-xs text-muted-foreground", children: meta })
            ] })
          ] }),
          description && /* @__PURE__ */ jsx68("div", { "data-entity-card-description": true, className: "mt-2 text-sm text-muted-foreground", children: description }),
          (footer || actionLabel) && /* @__PURE__ */ jsxs59("div", { className: "mt-4 flex items-end justify-between gap-2", children: [
            /* @__PURE__ */ jsx68("div", { "data-entity-card-footer": true, className: "min-w-0", children: footer }),
            actionLabel && /* @__PURE__ */ jsxs59(
              "span",
              {
                "data-entity-card-action": true,
                className: "inline-flex shrink-0 items-center gap-1 text-sm text-muted-foreground transition-colors group-hover:text-primary",
                children: [
                  actionLabel,
                  /* @__PURE__ */ jsx68(ArrowRight, { size: 14 })
                ]
              }
            )
          ] })
        ]
      }
    );
  }
);
EntityCard.displayName = "EntityCard";

// src/components/SettingsSection.tsx
import React49 from "react";

// src/components/PageHeader.tsx
import React48 from "react";

// src/components/Divider.tsx
import React47 from "react";
import { Separator } from "react-aria-components";
import { jsx as jsx69 } from "react/jsx-runtime";
var dividerSizes = {
  sm: "my-2",
  md: "my-4",
  lg: "my-6"
};
var Divider = React47.forwardRef(
  ({ orientation = "horizontal", size = "md", className, ...props }, ref) => {
    return /* @__PURE__ */ jsx69(
      Separator,
      {
        ref,
        orientation,
        className: cn(
          "shrink-0 bg-border",
          orientation === "horizontal" ? cn("h-[1px] w-full", dividerSizes[size]) : "h-full w-[1px]",
          className
        ),
        ...props
      }
    );
  }
);
Divider.displayName = "Divider";

// src/components/PageHeader.tsx
import { Fragment as Fragment12, jsx as jsx70, jsxs as jsxs60 } from "react/jsx-runtime";
var PageHeader = React48.forwardRef(
  ({
    eyebrow,
    icon,
    title,
    description,
    actions,
    showDivider = false,
    size = "page",
    className,
    ...props
  }, ref) => {
    const isSection = size === "section";
    return /* @__PURE__ */ jsxs60(Fragment12, { children: [
      /* @__PURE__ */ jsx70(
        "div",
        {
          ref,
          "data-page-header": true,
          "data-size": size,
          className: cn("space-y-1", className),
          ...props,
          children: /* @__PURE__ */ jsxs60(Flex, { align: "center", justify: "between", children: [
            /* @__PURE__ */ jsxs60("div", { className: "min-w-0", children: [
              eyebrow && /* @__PURE__ */ jsx70("div", { className: "mb-1", children: eyebrow }),
              /* @__PURE__ */ jsxs60(
                Heading,
                {
                  level: isSection ? 3 : 2,
                  className: cn(isSection && "text-base", "flex items-center", icon && "gap-2"),
                  children: [
                    icon,
                    title
                  ]
                }
              ),
              description && (typeof description === "string" ? /* @__PURE__ */ jsx70(
                Text,
                {
                  size: "sm",
                  "data-page-header-description": true,
                  className: "text-muted-foreground mt-1 block",
                  children: description
                }
              ) : /* @__PURE__ */ jsx70(
                "div",
                {
                  "data-page-header-description": true,
                  className: "text-sm text-muted-foreground mt-1",
                  children: description
                }
              ))
            ] }),
            actions && /* @__PURE__ */ jsx70("div", { className: "flex-shrink-0", children: actions })
          ] })
        }
      ),
      showDivider && /* @__PURE__ */ jsx70(Divider, {})
    ] });
  }
);
PageHeader.displayName = "PageHeader";

// src/components/SettingsSection.tsx
import { jsx as jsx71, jsxs as jsxs61 } from "react/jsx-runtime";
function sectionIdFromTitle(title) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
var SettingsSection = React49.forwardRef(
  ({ title, description, action, footer, children, className, ...rest }, ref) => {
    return /* @__PURE__ */ jsxs61(
      "section",
      {
        ref,
        id: typeof title === "string" ? sectionIdFromTitle(title) : void 0,
        "data-settings-section": true,
        className: cn(
          "rounded-lg border border-border bg-card overflow-hidden scroll-mt-6",
          className
        ),
        ...rest,
        children: [
          /* @__PURE__ */ jsx71("div", { "data-settings-section-header": true, className: "px-4 py-3 bg-muted/40 border-b border-border", children: /* @__PURE__ */ jsx71(PageHeader, { size: "section", title, description, actions: action }) }),
          /* @__PURE__ */ jsx71("div", { "data-settings-section-body": true, className: "p-4 space-y-4 relative", children }),
          footer ? /* @__PURE__ */ jsx71(
            "div",
            {
              "data-settings-section-footer": true,
              className: "px-4 py-3 bg-muted/40 border-t border-border",
              children: footer
            }
          ) : null
        ]
      }
    );
  }
);
SettingsSection.displayName = "SettingsSection";

// src/components/ListCard.tsx
import React50 from "react";
import { motion as motion5 } from "framer-motion";
import { jsx as jsx72, jsxs as jsxs62 } from "react/jsx-runtime";
var ListCard = React50.forwardRef(
  ({ title, titleAccessory, description, action, className, children, ...rest }, ref) => {
    const hasHeader = Boolean(title || titleAccessory || description || action);
    return /* @__PURE__ */ jsxs62(
      "section",
      {
        ref,
        "data-list-card": true,
        className: cn(
          "rounded-lg border border-border bg-card overflow-hidden",
          className
        ),
        ...rest,
        children: [
          hasHeader && /* @__PURE__ */ jsxs62(
            "div",
            {
              "data-list-card-header": true,
              className: "flex items-center justify-between gap-2 px-4 py-3 bg-muted/40 border-b border-border",
              children: [
                /* @__PURE__ */ jsxs62("div", { className: "min-w-0", children: [
                  /* @__PURE__ */ jsxs62("div", { className: "flex items-center gap-2", children: [
                    /* @__PURE__ */ jsx72("h3", { className: "text-base font-semibold leading-tight truncate", children: title }),
                    titleAccessory ? /* @__PURE__ */ jsx72(
                      "span",
                      {
                        "data-list-card-accessory": true,
                        className: "text-xs text-muted-foreground shrink-0",
                        children: titleAccessory
                      }
                    ) : null
                  ] }),
                  description ? /* @__PURE__ */ jsx72("p", { className: "text-sm text-muted-foreground mt-0.5", children: description }) : null
                ] }),
                action ? /* @__PURE__ */ jsx72("div", { className: "shrink-0", children: action }) : null
              ]
            }
          ),
          /* @__PURE__ */ jsx72("div", { className: "divide-y divide-border", children })
        ]
      }
    );
  }
);
ListCard.displayName = "ListCard";
function ListCardRow({
  leading,
  icon,
  title,
  badges,
  meta,
  actions,
  footer,
  index = 0,
  className
}) {
  return /* @__PURE__ */ jsx72(
    motion5.div,
    {
      initial: { opacity: 0, y: 4 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.18, delay: index * 0.03 },
      children: /* @__PURE__ */ jsxs62(
        "div",
        {
          "data-list-card-row": true,
          className: cn("group px-4 py-3 hover:bg-muted/60 transition-colors", className),
          children: [
            /* @__PURE__ */ jsxs62("div", { className: "flex items-center justify-between gap-3", children: [
              /* @__PURE__ */ jsxs62("div", { className: "flex items-center gap-3 flex-1 min-w-0", children: [
                leading,
                icon ? /* @__PURE__ */ jsx72(
                  "div",
                  {
                    "data-list-card-row-icon": true,
                    className: "flex items-center justify-center w-8 h-8 rounded-md bg-muted shrink-0",
                    children: icon
                  }
                ) : null,
                /* @__PURE__ */ jsxs62("div", { className: "flex-1 min-w-0", children: [
                  /* @__PURE__ */ jsxs62("div", { className: "flex items-center gap-2 mb-0.5 flex-wrap", children: [
                    /* @__PURE__ */ jsx72("span", { className: "font-medium truncate", children: title }),
                    badges
                  ] }),
                  meta ? /* @__PURE__ */ jsx72(
                    "div",
                    {
                      "data-list-card-row-meta": true,
                      className: "flex items-center gap-2 text-xs text-muted-foreground flex-wrap",
                      children: meta
                    }
                  ) : null
                ] })
              ] }),
              actions ? /* @__PURE__ */ jsx72("div", { className: "shrink-0", children: actions }) : null
            ] }),
            footer
          ]
        }
      )
    }
  );
}

// src/components/Timeline/Timeline.tsx
import React51 from "react";

// src/utils/scrollWithin.ts
function scrollWithin(container, target, block = "nearest") {
  if (!container || !target) return;
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  let delta = 0;
  if (block === "center") {
    delta = targetRect.top + targetRect.height / 2 - (containerRect.top + containerRect.height / 2);
  } else if (targetRect.top < containerRect.top) {
    delta = targetRect.top - containerRect.top;
  } else if (targetRect.bottom > containerRect.bottom) {
    delta = targetRect.bottom - containerRect.bottom;
  }
  if (delta !== 0) container.scrollBy({ top: delta, behavior: "smooth" });
}

// src/components/Timeline/timelineUtils.ts
function formatTimelineTimestamp(ms) {
  const totalSeconds = Math.floor(ms / 1e3);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
function findActiveTimelineIndex(items, currentTimeMs) {
  let activeIndex = -1;
  for (let i = 0; i < items.length; i++) {
    if (items[i].timestampMs <= currentTimeMs) {
      activeIndex = i;
    } else {
      break;
    }
  }
  return activeIndex;
}

// src/components/Timeline/Timeline.tsx
import { jsx as jsx73, jsxs as jsxs63 } from "react/jsx-runtime";
var TimelineContext = React51.createContext(null);
function useTimelineContext() {
  const ctx = React51.useContext(TimelineContext);
  if (!ctx) {
    throw new Error("Timeline.* components must be rendered inside <Timeline>");
  }
  return ctx;
}
function TimelineRoot({
  currentTimeMs,
  onSeek,
  autoScrollToActive = false,
  resyncSignal = 0,
  className,
  children
}) {
  const value = React51.useMemo(
    () => ({ currentTimeMs, onSeek, autoScrollToActive, resyncSignal }),
    [currentTimeMs, onSeek, autoScrollToActive, resyncSignal]
  );
  return /* @__PURE__ */ jsx73(TimelineContext.Provider, { value, children: /* @__PURE__ */ jsx73(
    "div",
    {
      className: cn(
        "flex flex-col h-full min-h-0 bg-card border-l border-border",
        className
      ),
      children
    }
  ) });
}
function TimelineHeader({ title, subtitle, trailing, className }) {
  return /* @__PURE__ */ jsxs63(
    "div",
    {
      className: cn(
        "flex items-start justify-between gap-3 px-4 py-3 border-b border-border",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs63("div", { className: "min-w-0 flex-1", children: [
          /* @__PURE__ */ jsx73("div", { className: "text-sm font-semibold text-foreground truncate", children: title }),
          subtitle && /* @__PURE__ */ jsx73("div", { className: "text-xs text-muted-foreground mt-0.5 truncate", children: subtitle })
        ] }),
        trailing && /* @__PURE__ */ jsx73("div", { className: "flex items-center gap-2 shrink-0 text-xs text-muted-foreground", children: trailing })
      ]
    }
  );
}
function TimelineBody({ children, className }) {
  const { currentTimeMs, autoScrollToActive, resyncSignal } = useTimelineContext();
  const bodyRef = React51.useRef(null);
  const userScrolledRef = React51.useRef(false);
  const markUserScrolled = React51.useCallback(() => {
    userScrolledRef.current = true;
  }, []);
  const scrollActiveIntoView = React51.useCallback(() => {
    const active = bodyRef.current?.querySelector('[data-timeline-active="true"]');
    scrollWithin(bodyRef.current, active, "nearest");
  }, []);
  React51.useEffect(() => {
    if (!autoScrollToActive || userScrolledRef.current) return;
    scrollActiveIntoView();
  }, [currentTimeMs, autoScrollToActive, scrollActiveIntoView]);
  React51.useEffect(() => {
    if (!resyncSignal) return;
    userScrolledRef.current = false;
    scrollActiveIntoView();
  }, [resyncSignal, scrollActiveIntoView]);
  return /* @__PURE__ */ jsx73(
    "div",
    {
      ref: bodyRef,
      onWheel: markUserScrolled,
      onTouchMove: markUserScrolled,
      className: cn("flex-1 min-h-0 overflow-y-auto", className),
      children
    }
  );
}
function TimelineTimestamp({ value, className }) {
  return /* @__PURE__ */ jsx73("span", { className: cn("text-xs text-muted-foreground font-mono tabular-nums shrink-0", className), children: formatTimelineTimestamp(value) });
}
var Timeline = Object.assign(TimelineRoot, {
  Header: TimelineHeader,
  Body: TimelineBody,
  Timestamp: TimelineTimestamp
});

// src/components/Timeline/TimelineGroup.tsx
import React52 from "react";
import { CaretRight as CaretRight10 } from "@phosphor-icons/react";
import { jsx as jsx74, jsxs as jsxs64 } from "react/jsx-runtime";
var GroupContext = React52.createContext(null);
function useGroupContext() {
  const ctx = React52.useContext(GroupContext);
  if (!ctx) {
    throw new Error("Timeline.Group.* components must be rendered inside <Timeline.Group>");
  }
  return ctx;
}
function TimelineGroupRoot({
  active = false,
  expanded: expandedProp,
  defaultExpanded = false,
  onExpandedChange,
  hasChildren = true,
  className,
  children
}) {
  const [internalExpanded, setInternalExpanded] = React52.useState(defaultExpanded);
  const expanded = expandedProp ?? internalExpanded;
  const setExpanded = React52.useCallback(
    (next) => {
      if (expandedProp === void 0) setInternalExpanded(next);
      onExpandedChange?.(next);
    },
    [expandedProp, onExpandedChange]
  );
  const toggle = React52.useCallback(() => setExpanded(!expanded), [setExpanded, expanded]);
  const value = React52.useMemo(
    () => ({ expanded, hasChildren, toggle, active }),
    [expanded, hasChildren, toggle, active]
  );
  return /* @__PURE__ */ jsx74(GroupContext.Provider, { value, children: /* @__PURE__ */ jsx74("div", { className: cn(active && "bg-blue-50/50 dark:bg-blue-900/10", className), children }) });
}
function TimelineGroupHeader({
  timestamp,
  icon,
  title,
  detail,
  subtitle,
  trailing,
  onClick,
  disabled = false,
  className,
  dataAttributes
}) {
  const ctx = useTimelineContext();
  const group = useGroupContext();
  const isSeekable = !disabled && (onClick !== void 0 || timestamp !== void 0);
  const handleBodyClick = () => {
    if (disabled) return;
    if (onClick) onClick();
    else if (timestamp !== void 0) ctx.onSeek(timestamp);
    if (group.hasChildren) group.toggle();
  };
  const handleCaretClick = (e) => {
    if (disabled || !group.hasChildren) return;
    e.stopPropagation();
    group.toggle();
  };
  return /* @__PURE__ */ jsxs64(
    "button",
    {
      type: "button",
      onClick: handleBodyClick,
      disabled: disabled || !isSeekable && !group.hasChildren,
      "data-timeline-active": group.active ? "true" : void 0,
      ...dataAttributes,
      className: cn(
        "w-full text-left px-4 py-3 transition-colors focus:outline-none",
        "hover:bg-muted/50",
        group.active && !group.expanded && "bg-blue-100 dark:bg-blue-900/30",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs64("div", { className: "flex items-start gap-2", children: [
          /* @__PURE__ */ jsx74(
            "span",
            {
              onClick: handleCaretClick,
              className: "shrink-0 mt-0.5 inline-flex",
              "aria-hidden": "true",
              children: /* @__PURE__ */ jsx74(
                CaretRight10,
                {
                  size: 14,
                  weight: "bold",
                  className: cn(
                    "shrink-0 transition-transform duration-200",
                    group.hasChildren ? "text-muted-foreground" : "text-muted-foreground/30",
                    group.hasChildren && group.expanded && "rotate-90"
                  )
                }
              )
            }
          ),
          icon && /* @__PURE__ */ jsx74("span", { className: "shrink-0 mt-0.5", children: icon }),
          /* @__PURE__ */ jsxs64("span", { className: "font-medium text-sm flex-1 min-w-0 line-clamp-2", children: [
            title,
            detail && /* @__PURE__ */ jsx74("span", { className: "ml-1.5 text-xs text-muted-foreground font-normal", children: detail })
          ] }),
          timestamp !== void 0 && /* @__PURE__ */ jsx74("span", { className: "text-xs text-muted-foreground shrink-0 mt-0.5 font-mono tabular-nums", children: formatMmSs(timestamp) }),
          trailing && /* @__PURE__ */ jsx74("span", { className: "shrink-0 mt-0.5 inline-flex items-center gap-2", children: trailing })
        ] }),
        subtitle && /* @__PURE__ */ jsx74("div", { className: "text-xs text-muted-foreground mt-1 pl-5 flex items-center gap-1.5", children: subtitle })
      ]
    }
  );
}
function formatMmSs(ms) {
  const total = Math.floor(ms / 1e3);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
function TimelineItems({ children, className }) {
  const group = useGroupContext();
  if (!group.expanded) return null;
  return /* @__PURE__ */ jsx74("div", { className, children });
}
var TimelineGroup = Object.assign(TimelineGroupRoot, {
  Header: TimelineGroupHeader,
  Items: TimelineItems
});

// src/components/Timeline/TimelineItem.tsx
import { Fragment as Fragment13, jsx as jsx75, jsxs as jsxs65 } from "react/jsx-runtime";
var statusDotClass = {
  success: "bg-success",
  failed: "bg-destructive",
  pending: "bg-muted-foreground/40",
  running: "bg-blue-500 animate-pulse",
  skipped: "bg-muted-foreground/40",
  annotation: "bg-amber-500"
};
function TimelineItem({
  timestamp,
  active = false,
  status = "success",
  annotationType,
  icon,
  title,
  detail,
  description,
  trailing,
  accentBorderClass,
  onClick,
  disabled = false,
  className,
  dataAttributes
}) {
  const ctx = useTimelineContext();
  const isClickable = !disabled && (onClick !== void 0 || timestamp !== void 0);
  const handleClick = () => {
    if (disabled) return;
    if (onClick) onClick();
    else if (timestamp !== void 0) ctx.onSeek(timestamp);
  };
  const rendered = /* @__PURE__ */ jsxs65(Fragment13, { children: [
    /* @__PURE__ */ jsxs65("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx75("span", { className: "shrink-0", children: icon ?? /* @__PURE__ */ jsx75(
        "span",
        {
          className: cn("block h-2 w-2 rounded-full", statusDotClass[status]),
          "aria-hidden": "true"
        }
      ) }),
      /* @__PURE__ */ jsx75("span", { className: "font-medium text-sm min-w-0 truncate", children: title }),
      detail && /* @__PURE__ */ jsx75("span", { className: "text-xs text-muted-foreground flex-1 min-w-0 truncate", children: detail }),
      !detail && /* @__PURE__ */ jsx75("span", { className: "flex-1" }),
      trailing && /* @__PURE__ */ jsx75("span", { className: "flex items-center gap-2 shrink-0", children: trailing })
    ] }),
    description && /* @__PURE__ */ jsx75("div", { className: "mt-1 pl-6", children: /* @__PURE__ */ jsx75("span", { className: "text-[0.75rem] leading-4 text-muted-foreground break-words block", children: description }) })
  ] });
  const activeClass = active ? "bg-blue-100 dark:bg-blue-900/30 border-l-2 border-l-blue-500" : accentBorderClass ?? "";
  const shared = cn(
    "w-full text-left px-4 py-3 border-b border-border transition-colors",
    activeClass,
    className
  );
  const dataAttrs = {
    "data-timeline-active": active ? "true" : void 0,
    "data-timeline-status": status,
    "data-timeline-annotation": annotationType,
    ...dataAttributes
  };
  if (isClickable) {
    return /* @__PURE__ */ jsx75(
      "button",
      {
        type: "button",
        onClick: handleClick,
        disabled,
        ...dataAttrs,
        className: cn(shared, "hover:bg-muted/50 focus:outline-none focus:ring-1 focus:ring-ring"),
        children: rendered
      }
    );
  }
  return /* @__PURE__ */ jsx75("div", { ...dataAttrs, className: shared, children: rendered });
}

// src/components/Timeline/timelineAnnotations.ts
import {
  Play as Play2,
  CheckCircle as CheckCircle2,
  Clock as Clock2,
  WarningCircle,
  Database as Database2,
  Brain,
  Lightbulb as Lightbulb2,
  Bug,
  NavigationArrow,
  CursorClick,
  Keyboard,
  Eye as Eye2,
  ArrowsOutCardinal,
  ListBullets,
  FileArrowUp,
  ChatCircle as ChatCircle2,
  Command,
  Camera,
  Code as Code2,
  Terminal as Terminal2,
  Table,
  UsersThree as UsersThree2
} from "@phosphor-icons/react";
var HIDDEN_ACTION_TYPES = /* @__PURE__ */ new Set(["snapshot"]);
var ACTION_LABELS = {
  navigate: "Navigate",
  click: "Click",
  type: "Type",
  hover: "Hover",
  drag: "Drag",
  scroll: "Scroll",
  select: "Select",
  "file-upload": "Upload",
  "dialog-handle": "Dialog",
  "press-key": "Press Key",
  snapshot: "Snapshot",
  evaluate: "Evaluate",
  assert: "Assert",
  move: "Move",
  wait: "Wait",
  "run-code": "Run Code",
  python_execute: "Python",
  extract_table: "Extract Table"
};
var ACTION_ICONS = {
  navigate: NavigationArrow,
  click: CursorClick,
  type: Keyboard,
  hover: Eye2,
  drag: ArrowsOutCardinal,
  scroll: ListBullets,
  select: ListBullets,
  "file-upload": FileArrowUp,
  "dialog-handle": ChatCircle2,
  "press-key": Command,
  snapshot: Camera,
  evaluate: Code2,
  assert: CheckCircle2,
  move: ArrowsOutCardinal,
  wait: Clock2,
  "run-code": Code2,
  python_execute: Terminal2,
  extract_table: Table
};
function getTimelineActionIcon(action) {
  return ACTION_ICONS[action] ?? NavigationArrow;
}
function getTimelineActionLabel(action) {
  return ACTION_LABELS[action] ?? action;
}
function getTimelineAnnotationIcon(annotationType) {
  switch (annotationType) {
    case "step-started":
    case "todo-started":
      return Play2;
    case "step-completed":
    case "todo-completed":
      return CheckCircle2;
    case "scenario-started":
    case "scenario-completed":
      return UsersThree2;
    case "wait":
      return Clock2;
    case "recording-gap":
      return WarningCircle;
    case "capture":
      return Database2;
    case "l2-started":
    case "l2-completed":
    case "l2-failed":
      return Brain;
    case "hint-created":
      return Lightbulb2;
    case "issue-filed":
      return Bug;
    default:
      return NavigationArrow;
  }
}
var ANNOTATION_COLORS = {
  "step-started": {
    icon: "text-indigo-600 dark:text-indigo-400",
    bg: "bg-indigo-100 dark:bg-indigo-900/30",
    border: "border-l-indigo-500"
  },
  "step-completed": {
    icon: "text-emerald-600 dark:text-emerald-400",
    bg: "bg-emerald-100 dark:bg-emerald-900/30",
    border: "border-l-emerald-500"
  },
  wait: {
    icon: "text-amber-600 dark:text-amber-400",
    bg: "bg-amber-100 dark:bg-amber-900/30",
    border: "border-l-amber-500"
  },
  "recording-gap": {
    icon: "text-orange-600 dark:text-orange-400",
    bg: "bg-orange-100 dark:bg-orange-900/30",
    border: "border-l-orange-500"
  },
  "cache-fallback": {
    icon: "text-amber-600 dark:text-amber-400",
    bg: "bg-amber-100 dark:bg-amber-900/30",
    border: "border-l-amber-500"
  },
  capture: {
    icon: "text-teal-600 dark:text-teal-400",
    bg: "bg-teal-100 dark:bg-teal-900/30",
    border: "border-l-teal-500"
  },
  "l2-started": {
    icon: "text-emerald-600 dark:text-emerald-400",
    bg: "bg-emerald-100 dark:bg-emerald-900/30",
    border: "border-l-emerald-500"
  },
  "l2-completed": {
    icon: "text-emerald-600 dark:text-emerald-400",
    bg: "bg-emerald-100 dark:bg-emerald-900/30",
    border: "border-l-emerald-500"
  },
  "l2-failed": {
    icon: "text-red-600 dark:text-red-400",
    bg: "bg-red-100 dark:bg-red-900/30",
    border: "border-l-red-500"
  },
  "hint-created": {
    icon: "text-violet-600 dark:text-violet-400",
    bg: "bg-violet-100 dark:bg-violet-900/30",
    border: "border-l-violet-500"
  },
  "issue-filed": {
    icon: "text-amber-600 dark:text-amber-400",
    bg: "bg-amber-100 dark:bg-amber-900/30",
    border: "border-l-amber-500"
  },
  // Adhoc live-checklist items. The entry-level annotationColor carries the
  // per-item outcome tint; these schemes are the neutral fallback.
  "todo-started": {
    icon: "text-indigo-600 dark:text-indigo-400",
    bg: "bg-indigo-100 dark:bg-indigo-900/30",
    border: "border-l-indigo-500"
  },
  "todo-completed": {
    icon: "text-emerald-600 dark:text-emerald-400",
    bg: "bg-emerald-100 dark:bg-emerald-900/30",
    border: "border-l-emerald-500"
  },
  // Adhoc multi-user scenarios — the outer grouping tier (Scenario → TODO).
  // Violet matches the backend scenario annotation color.
  "scenario-started": {
    icon: "text-violet-600 dark:text-violet-400",
    bg: "bg-violet-100 dark:bg-violet-900/30",
    border: "border-l-violet-500"
  },
  "scenario-completed": {
    icon: "text-violet-700 dark:text-violet-300",
    bg: "bg-violet-100 dark:bg-violet-900/30",
    border: "border-l-violet-600"
  }
};
function getTimelineAnnotationColors(annotationType) {
  if (!annotationType) return null;
  return ANNOTATION_COLORS[annotationType] ?? null;
}
function getTimelineCacheBadge(meta) {
  if (!meta?.cacheOutcome) return null;
  if (meta.cacheOutcome === "hit") {
    if (meta.cacheHitSource === "recovery") {
      return { label: "L2 solved", className: "text-emerald-600 dark:text-emerald-400" };
    }
    if (meta.cacheHitSource === "wait_shortcut" || meta.waitShortcut === true) {
      return { label: "Wait shortcut", className: "text-blue-600 dark:text-blue-400" };
    }
    return { label: "Cached", className: "text-blue-600 dark:text-blue-400" };
  }
  return { label: "Agent", className: "text-muted-foreground" };
}
function isStepAnnotation(entry) {
  return entry.isAnnotation === true && (entry.annotationType === "step-started" || entry.annotationType === "step-completed" || entry.annotationType === "todo-started" || entry.annotationType === "todo-completed");
}
function isScenarioAnnotation(entry) {
  return entry.isAnnotation === true && (entry.annotationType === "scenario-started" || entry.annotationType === "scenario-completed");
}
function formatStepLabel(entry) {
  if (!entry.isAnnotation) return entry.action;
  return entry.action.replace(" (started)", "").replace(" (completed)", "");
}
function extractChapters(entries, startedType, completedType) {
  const chapters = [];
  const open = /* @__PURE__ */ new Map();
  for (const entry of entries) {
    if (!entry.isAnnotation || !entry.nodeId) continue;
    if (entry.annotationType === startedType) {
      open.set(entry.nodeId, {
        nodeId: entry.nodeId,
        name: entry.action,
        startMs: entry.timestampMs,
        endMs: null,
        color: entry.annotationColor ?? null
      });
    } else if (entry.annotationType === completedType) {
      const chapter = open.get(entry.nodeId);
      if (chapter) {
        chapter.endMs = entry.timestampMs;
        chapter.color = entry.annotationColor ?? chapter.color;
        chapters.push(chapter);
        open.delete(entry.nodeId);
      }
    }
  }
  chapters.push(...open.values());
  return chapters.sort((a, b) => a.startMs - b.startMs);
}
function extractTodoChapters(entries) {
  return extractChapters(entries, "todo-started", "todo-completed");
}
function extractScenarioChapters(entries) {
  return extractChapters(entries, "scenario-started", "scenario-completed");
}
function filterTimelineEntries(entries, opts) {
  const seen = /* @__PURE__ */ new Set();
  return entries.filter((entry) => {
    if (!entry.isAnnotation && HIDDEN_ACTION_TYPES.has(entry.action)) {
      return false;
    }
    if (opts?.hideEvaluates && !entry.isAnnotation && entry.action === "evaluate") {
      return false;
    }
    const key = `${entry.action}|${entry.timestampMs}|${entry.target ?? ""}|${entry.value ?? ""}|${entry.code ?? ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// src/components/Timeline/CodeDialog.tsx
import React53 from "react";
import { Code as Code3 } from "@phosphor-icons/react";
import { jsx as jsx76, jsxs as jsxs66 } from "react/jsx-runtime";
function CodeDialog({ code, label, description }) {
  const formatted = React53.useMemo(() => {
    try {
      return formatJs(code);
    } catch {
      return code;
    }
  }, [code]);
  const [isOpen, setIsOpen] = React53.useState(false);
  const open = (e) => {
    e.stopPropagation();
    setIsOpen(true);
  };
  return /* @__PURE__ */ jsxs66(DialogTrigger, { size: "lg", isOpen, onOpenChange: setIsOpen, children: [
    /* @__PURE__ */ jsxs66(TooltipTrigger, { delay: 200, children: [
      /* @__PURE__ */ jsx76(
        "button",
        {
          type: "button",
          onClick: open,
          onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") open(e);
          },
          className: "inline-flex items-center justify-center h-6 w-6 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors",
          "aria-label": "Show code behind this step",
          children: /* @__PURE__ */ jsx76(Code3, { size: 14 })
        }
      ),
      /* @__PURE__ */ jsx76(Tooltip, { children: "Show code" })
    ] }),
    /* @__PURE__ */ jsx76(Dialog5, { title: label ?? "Code", description, children: /* @__PURE__ */ jsx76(
      "pre",
      {
        className: "max-h-[60vh] overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-3 text-xs font-mono leading-relaxed",
        "data-testid": "timeline-code-dialog",
        children: formatted
      }
    ) })
  ] });
}
function formatJs(source) {
  const trimmed = source.trim();
  if (trimmed.includes("\n")) return trimmed;
  let out = "";
  let inSingle = false;
  let inDouble = false;
  let inBacktick = false;
  for (let i = 0; i < trimmed.length; i++) {
    const c = trimmed[i];
    const prev = trimmed[i - 1];
    if (c === "'" && prev !== "\\" && !inDouble && !inBacktick) inSingle = !inSingle;
    else if (c === '"' && prev !== "\\" && !inSingle && !inBacktick) inDouble = !inDouble;
    else if (c === "`" && prev !== "\\" && !inSingle && !inDouble) inBacktick = !inBacktick;
    out += c;
    if (c === ";" && !inSingle && !inDouble && !inBacktick) out += "\n";
  }
  return out;
}

// src/components/Timeline/TimelineEntryItem.tsx
import { jsx as jsx77, jsxs as jsxs67 } from "react/jsx-runtime";
var getTimelineEntryInlineDetail = (entry) => {
  if (entry.isAnnotation) return null;
  switch (entry.action) {
    case "press-key":
      return entry.key ?? entry.value ?? null;
    case "type":
      return entry.value ? `"${entry.value}"` : null;
    case "navigate": {
      if (entry.url) {
        try {
          return new URL(entry.url).pathname;
        } catch {
          return entry.url;
        }
      }
      return entry.value ?? null;
    }
    case "select":
      return entry.value ? `"${entry.value}"` : null;
    case "click":
    case "hover":
      if (entry.clickedElement) {
        return entry.clickedElement.textContent ?? entry.clickedElement.ariaLabel ?? null;
      }
      return null;
    case "assert":
      return entry.value ?? null;
    case "extract_table":
      return entry.value ?? null;
    case "python_execute":
      return entry.target ?? null;
    default:
      return null;
  }
};
function getSourceBorderClass(entry) {
  if (entry.source === "cache") {
    return "border-l-2 border-l-blue-300 dark:border-l-blue-700";
  }
  if (entry.source === "recovery") {
    return "border-l-2 border-l-emerald-300 dark:border-l-emerald-700";
  }
  if (entry.annotationType === "cache-fallback") {
    return "border-l-2 border-l-amber-400 dark:border-l-amber-600";
  }
  return void 0;
}
function statusForEntry(entry) {
  if (entry.isAnnotation) return "annotation";
  return entry.success === false ? "failed" : "success";
}
function getTimelineItemTooltipContent(entry) {
  if (entry.isAnnotation && entry.annotationType === "capture" && entry.value) {
    return /* @__PURE__ */ jsxs67("div", { className: "max-w-[380px]", children: [
      /* @__PURE__ */ jsx77("div", { className: "text-[11px] font-medium text-muted-foreground mb-1", children: "Captured values" }),
      /* @__PURE__ */ jsx77("pre", { className: "text-[11px] leading-relaxed font-mono bg-muted/50 rounded p-2 overflow-auto max-h-[240px] whitespace-pre-wrap break-words", children: /* @__PURE__ */ jsx77("code", { children: entry.value }) })
    ] });
  }
  if (entry.isAnnotation) return null;
  const sections = [];
  if (entry.target) {
    sections.push(
      /* @__PURE__ */ jsx77("div", { className: "text-xs text-muted-foreground mb-1.5 break-words", children: entry.target }, "target")
    );
  }
  const action = entry.action;
  if ((action === "evaluate" || action === "python_execute") && entry.value) {
    sections.push(
      /* @__PURE__ */ jsx77(
        "pre",
        {
          className: "text-[11px] leading-relaxed font-mono bg-muted/50 rounded p-2 overflow-auto max-h-[200px] max-w-[360px] whitespace-pre-wrap break-all",
          children: /* @__PURE__ */ jsx77("code", { children: entry.value })
        },
        "code"
      )
    );
  }
  if ((action === "click" || action === "hover") && entry.clickedElement) {
    const el = entry.clickedElement;
    sections.push(
      /* @__PURE__ */ jsxs67(
        "div",
        {
          className: "p-1.5 bg-muted/50 rounded text-[10px] font-mono space-y-0.5 mt-1",
          children: [
            /* @__PURE__ */ jsxs67("div", { className: "flex items-center gap-1 flex-wrap", children: [
              /* @__PURE__ */ jsxs67("span", { className: "text-blue-600 dark:text-blue-400", children: [
                "<",
                el.tagName.toLowerCase(),
                ">"
              ] }),
              el.id && /* @__PURE__ */ jsxs67("span", { className: "text-green-600 dark:text-green-400", children: [
                "#",
                el.id
              ] }),
              el.className && /* @__PURE__ */ jsxs67("span", { className: "text-amber-600 dark:text-amber-400 truncate max-w-[140px]", children: [
                ".",
                el.className.split(" ")[0]
              ] })
            ] }),
            el.role && /* @__PURE__ */ jsxs67("div", { className: "text-purple-600 dark:text-purple-400", children: [
              'role="',
              el.role,
              '"'
            ] }),
            el.ariaLabel && /* @__PURE__ */ jsxs67("div", { className: "text-muted-foreground truncate", children: [
              'aria-label="',
              el.ariaLabel,
              '"'
            ] }),
            el.textContent && /* @__PURE__ */ jsxs67("div", { className: "text-muted-foreground truncate", children: [
              '"',
              el.textContent,
              '"'
            ] })
          ]
        },
        "element"
      )
    );
  }
  if (entry.coordinates && (action === "click" || action === "hover" || action === "drag")) {
    sections.push(
      /* @__PURE__ */ jsxs67("div", { className: "flex items-center gap-2 flex-wrap mt-1", children: [
        /* @__PURE__ */ jsxs67("span", { className: "font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded text-foreground", children: [
          "(",
          entry.coordinates.x,
          ", ",
          entry.coordinates.y,
          ")"
        ] }),
        entry.clickType && /* @__PURE__ */ jsxs67("span", { className: "text-[10px] text-muted-foreground", children: [
          "via ",
          entry.clickType === "ref" ? "locator" : "coordinates"
        ] })
      ] }, "coords")
    );
  }
  if (action === "navigate" && (entry.url || entry.value)) {
    sections.push(
      /* @__PURE__ */ jsx77(
        "div",
        {
          className: "text-[10px] font-mono text-muted-foreground truncate max-w-[360px] mt-1",
          children: entry.url ?? entry.value
        },
        "url"
      )
    );
  }
  if (action === "type" && entry.value) {
    sections.push(
      /* @__PURE__ */ jsxs67("div", { className: "text-xs font-mono bg-muted/50 rounded p-1.5 break-words mt-1", children: [
        '"',
        entry.value,
        '"'
      ] }, "value")
    );
  }
  if (action === "press-key" && (entry.key || entry.value)) {
    sections.push(
      /* @__PURE__ */ jsx77("div", { className: "text-xs font-mono mt-1", children: entry.key ?? entry.value }, "key")
    );
  }
  if (action === "assert" && entry.value) {
    sections.push(
      /* @__PURE__ */ jsx77("div", { className: "text-xs bg-muted/50 rounded p-1.5 break-words mt-1", children: entry.value }, "assert")
    );
  }
  if (entry.error) {
    sections.push(
      /* @__PURE__ */ jsx77("div", { className: "text-[11px] text-red-600 dark:text-red-400 break-words mt-1.5", children: entry.error }, "error")
    );
  }
  if (sections.length === 0) return null;
  return /* @__PURE__ */ jsx77("div", { className: "max-w-[380px]", children: sections });
}
function TimelineEntryItem({
  entry,
  isActive,
  onSeek,
  isStepMarker = false
}) {
  const annotationColors = entry.annotationType ? getTimelineAnnotationColors(entry.annotationType) : null;
  const isCodeAction = !entry.isAnnotation && (entry.action === "evaluate" || entry.action === "assert" || entry.action === "run-code");
  const IconComponent = entry.isAnnotation ? getTimelineAnnotationIcon(entry.annotationType) : getTimelineActionIcon(entry.action);
  const iconColor = annotationColors ? annotationColors.icon : entry.success ? "text-blue-500 dark:text-blue-400" : "text-red-500 dark:text-red-400";
  const hasCode = !entry.isAnnotation && typeof entry.code === "string" && entry.code.trim().length > 0;
  const targetLooksLikeCode = isCodeAction && typeof entry.target === "string" && (entry.target === entry.code || entry.target.length > 120 || /=>|\b(document|window|querySelector|\.includes\()/.test(entry.target));
  const hasNlLabel = !entry.isAnnotation && !!entry.label && entry.label.trim().length > 0;
  const displayLabel = isStepMarker ? formatStepLabel(entry) : entry.isAnnotation ? entry.action : hasNlLabel ? entry.label : isCodeAction && entry.target && !targetLooksLikeCode ? entry.target : getTimelineActionLabel(entry.action);
  const description = entry.isAnnotation && entry.annotationType === "wait" && entry.waitDurationSeconds ? `Duration: ${Math.round(entry.waitDurationSeconds / 60)}m` : !entry.isAnnotation && entry.target && !targetLooksLikeCode && entry.target !== displayLabel ? entry.target : null;
  const tooltipContent = getTimelineItemTooltipContent(entry);
  const trailing = /* @__PURE__ */ jsxs67("div", { className: "flex items-center gap-1", children: [
    hasCode && /* @__PURE__ */ jsx77(
      CodeDialog,
      {
        code: entry.code,
        label: hasNlLabel ? entry.label : entry.target && !targetLooksLikeCode ? entry.target : entry.action === "assert" ? "Assertion" : entry.action === "run-code" ? "Run code" : "Evaluate",
        description: getTimelineActionLabel(entry.action)
      }
    ),
    /* @__PURE__ */ jsx77(Timeline.Timestamp, { value: entry.timestampMs })
  ] });
  const row = /* @__PURE__ */ jsx77(
    TimelineItem,
    {
      timestamp: entry.timestampMs,
      active: isActive,
      status: statusForEntry(entry),
      annotationType: entry.annotationType,
      icon: /* @__PURE__ */ jsx77(
        IconComponent,
        {
          size: 14,
          weight: isStepMarker ? "fill" : "regular",
          className: iconColor
        }
      ),
      title: displayLabel,
      detail: getTimelineEntryInlineDetail(entry) ?? void 0,
      description: description ?? void 0,
      accentBorderClass: getSourceBorderClass(entry),
      onClick: () => onSeek(entry.timestampMs),
      trailing,
      dataAttributes: { "data-timestamp-ms": entry.timestampMs }
    }
  );
  if (!tooltipContent) return row;
  return /* @__PURE__ */ jsxs67(TooltipTrigger, { delay: 200, placement: "left", children: [
    row,
    /* @__PURE__ */ jsx77(Tooltip, { variant: "light", children: tooltipContent })
  ] });
}

// src/components/Timeline/index.ts
var Timeline2 = Object.assign(Timeline, {
  Header: Timeline.Header,
  Body: Timeline.Body,
  Timestamp: Timeline.Timestamp,
  Group: TimelineGroup,
  Item: TimelineItem
});

// src/components/KPICard.tsx
import React54 from "react";
import { Fragment as Fragment14, jsx as jsx78, jsxs as jsxs68 } from "react/jsx-runtime";
var variantStyles4 = {
  default: "border-border hover:border-primary/50",
  success: "border-success/30 hover:border-success/60",
  warning: "border-warning/30 hover:border-warning/60",
  destructive: "border-destructive/30 hover:border-destructive/60",
  muted: "border-muted hover:border-muted-foreground/30"
};
var valueVariantColors = {
  default: "hsl(var(--foreground))",
  success: "var(--success-soft-fg)",
  warning: "var(--warn-soft-fg)",
  destructive: "var(--danger-soft-fg)",
  muted: "hsl(var(--muted-foreground))"
};
var sizeConfig2 = {
  sm: {
    padding: "p-3",
    titleSize: "xs",
    valueSize: "text-2xl",
    subtitleSize: "xs",
    iconSize: 16
  },
  md: {
    padding: "p-4",
    titleSize: "sm",
    valueSize: "text-3xl",
    subtitleSize: "xs",
    iconSize: 18
  },
  lg: {
    padding: "p-6",
    titleSize: "md",
    valueSize: "text-4xl",
    subtitleSize: "sm",
    iconSize: 20
  }
};
var KPICard = React54.forwardRef(
  ({
    value,
    formattedValue,
    title,
    subtitle,
    icon,
    href,
    onClick,
    variant = "default",
    size = "md",
    className
  }, ref) => {
    const config = sizeConfig2[size];
    const isInteractive = !!href || !!onClick;
    const cardClasses = cn(
      "rounded-lg border bg-card text-card-foreground transition-colors block",
      config.padding,
      variantStyles4[variant],
      isInteractive && "cursor-pointer",
      href && "no-underline",
      className
    );
    const dataAttrs = { "data-component": "kpi-card", "data-card": "" };
    const content = /* @__PURE__ */ jsxs68(Fragment14, { children: [
      /* @__PURE__ */ jsxs68("div", { className: "flex items-center gap-1.5", children: [
        icon && /* @__PURE__ */ jsx78("span", { className: "text-muted-foreground flex-shrink-0", children: icon }),
        /* @__PURE__ */ jsx78(Text, { size: config.titleSize, className: "text-muted-foreground font-medium", children: title })
      ] }),
      /* @__PURE__ */ jsx78(
        Heading,
        {
          level: 3,
          className: cn("font-bold mt-1", config.valueSize),
          style: { color: valueVariantColors[variant] },
          children: formattedValue !== void 0 ? formattedValue : /* @__PURE__ */ jsx78(AnimatedNumber, { value: value ?? 0 })
        }
      ),
      subtitle && /* @__PURE__ */ jsx78(Text, { size: config.subtitleSize, className: "text-muted-foreground mt-1", children: subtitle })
    ] });
    if (href) {
      return /* @__PURE__ */ jsx78(
        "a",
        {
          ref,
          href,
          onClick,
          className: cardClasses,
          ...dataAttrs,
          children: content
        }
      );
    }
    return /* @__PURE__ */ jsx78(
      "div",
      {
        ref,
        onClick,
        role: onClick ? "button" : void 0,
        tabIndex: onClick ? 0 : void 0,
        onKeyDown: onClick ? (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick(e);
          }
        } : void 0,
        className: cardClasses,
        ...dataAttrs,
        children: content
      }
    );
  }
);
KPICard.displayName = "KPICard";

// src/components/DangerSection.tsx
import { jsx as jsx79, jsxs as jsxs69 } from "react/jsx-runtime";
var DangerAction = ({
  title,
  description,
  children
}) => {
  return /* @__PURE__ */ jsxs69(Flex, { "data-danger-action": true, align: "start", justify: "between", gap: "md", children: [
    /* @__PURE__ */ jsxs69(Box, { className: "flex-1", children: [
      /* @__PURE__ */ jsx79(Text, { weight: "medium", children: title }),
      /* @__PURE__ */ jsx79(Text, { size: "sm", className: "text-muted-foreground mt-1 text-pretty", children: description })
    ] }),
    children
  ] });
};
var DangerSection = ({
  title = "Danger Zone",
  description = "Irreversible and destructive actions",
  children,
  className
}) => {
  return /* @__PURE__ */ jsxs69(Card, { "data-danger-section": true, className: cn("border-destructive", className), children: [
    /* @__PURE__ */ jsxs69(CardHeader, { children: [
      /* @__PURE__ */ jsx79(Heading, { level: 3, className: "text-destructive text-balance", children: title }),
      /* @__PURE__ */ jsx79(Text, { size: "sm", className: "text-muted-foreground mt-1 text-pretty", children: description })
    ] }),
    /* @__PURE__ */ jsx79(CardContent, { className: "space-y-4", children })
  ] });
};

// src/components/StatusIndicator.tsx
import React55 from "react";
import { jsx as jsx80, jsxs as jsxs70 } from "react/jsx-runtime";
var statusVariants = {
  neutral: "bg-gray-500",
  positive: "bg-status-success",
  negative: "bg-status-failed",
  notice: "bg-yellow-500",
  info: "bg-blue-500"
};
var statusSizes = {
  sm: "h-2 w-2",
  md: "h-3 w-3",
  lg: "h-4 w-4"
};
var StatusIndicator = React55.forwardRef(
  ({ variant = "neutral", size = "md", pulse = false, className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsxs70(
      "div",
      {
        ref,
        className: cn("inline-flex items-center gap-2", className),
        ...props,
        children: [
          /* @__PURE__ */ jsxs70("span", { className: "relative inline-flex", children: [
            /* @__PURE__ */ jsx80(
              "span",
              {
                className: cn(
                  "rounded-full",
                  statusVariants[variant],
                  statusSizes[size],
                  pulse && "animate-pulse"
                )
              }
            ),
            pulse && /* @__PURE__ */ jsx80(
              "span",
              {
                className: cn(
                  "absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping",
                  statusVariants[variant]
                )
              }
            )
          ] }),
          children && /* @__PURE__ */ jsx80("span", { className: "text-sm", children })
        ]
      }
    );
  }
);
StatusIndicator.displayName = "StatusIndicator";

// src/components/StatusTimeline.tsx
import React56 from "react";
import { CaretLeft as CaretLeft7, CaretRight as CaretRight11 } from "@phosphor-icons/react";
import { jsx as jsx81, jsxs as jsxs71 } from "react/jsx-runtime";
var toneStyles = {
  success: "bg-status-success/80 border-status-success/70",
  danger: "bg-status-failed/80 border-status-failed/70",
  warning: "bg-status-running/80 border-status-running/70",
  info: "bg-primary/70 border-primary/60",
  muted: "bg-status-queued/80 border-status-queued/70"
};
var sizeStyles3 = {
  sm: "h-3 w-3",
  md: "h-4 w-4"
};
function StatusTimeline({
  entries,
  selectedId,
  onEntrySelect,
  hasPreviousPage = false,
  hasNextPage = false,
  onPreviousPage,
  onNextPage,
  isPageLoading = false,
  size = "md",
  emptyState,
  slotCount = 10,
  showNavigation = true,
  className,
  ...rest
}) {
  const disabled = isPageLoading;
  const normalizedSlots = Math.max(1, slotCount);
  const cappedEntries = entries.slice(0, normalizedSlots);
  const chronological = [...cappedEntries].reverse();
  const emptySlots = Math.max(0, normalizedSlots - chronological.length);
  const slots = Array.from({ length: normalizedSlots }, (_, index) => {
    const entryIndex = index - emptySlots;
    if (entryIndex >= 0) {
      return { entry: chronological[entryIndex], key: chronological[entryIndex]?.id ?? `slot-${index}` };
    }
    return { entry: null, key: `empty-${index}` };
  });
  const fallback = emptyState ?? null;
  const orderedEntries = slots.map(({ entry }) => entry).filter((item) => Boolean(item));
  const selectedIndex = orderedEntries.findIndex((entry) => entry.id === selectedId);
  const hasLocalPrevious = selectedIndex > 0;
  const hasLocalNext = selectedIndex >= 0 && selectedIndex < orderedEntries.length - 1;
  const selectEntry = (entry) => {
    if (entry) {
      onEntrySelect?.(entry);
    }
  };
  const handlePrevious = () => {
    if (disabled) return;
    if (hasLocalPrevious) {
      selectEntry(orderedEntries[selectedIndex - 1] ?? null);
    } else {
      onPreviousPage?.();
    }
  };
  const handleNext = () => {
    if (disabled) return;
    if (hasLocalNext) {
      selectEntry(orderedEntries[selectedIndex + 1] ?? null);
    } else {
      onNextPage?.();
    }
  };
  const previousDisabled = disabled || !hasLocalPrevious && !hasPreviousPage;
  const nextDisabled = disabled || !hasLocalNext && !hasNextPage;
  return /* @__PURE__ */ jsxs71(
    "div",
    {
      className: cn(
        "flex items-center gap-3",
        !showNavigation && "gap-2",
        className
      ),
      ...rest,
      children: [
        showNavigation ? /* @__PURE__ */ jsxs71("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx81(
            Button,
            {
              variant: "ghost",
              size: "sm",
              iconOnly: true,
              "aria-label": "Previous QA runs",
              onPress: handlePrevious,
              isDisabled: previousDisabled,
              className: "text-muted-foreground",
              children: /* @__PURE__ */ jsx81(CaretLeft7, { size: 16 })
            }
          ),
          /* @__PURE__ */ jsx81(
            Button,
            {
              variant: "ghost",
              size: "sm",
              iconOnly: true,
              "aria-label": "Next QA runs",
              onPress: handleNext,
              isDisabled: nextDisabled,
              className: "text-muted-foreground",
              children: /* @__PURE__ */ jsx81(CaretRight11, { size: 16 })
            }
          )
        ] }) : null,
        /* @__PURE__ */ jsx81("div", { className: "flex items-center gap-1", role: "list", children: slots.map(({ entry, key }) => {
          if (!entry) {
            return /* @__PURE__ */ jsx81(
              "span",
              {
                className: cn(
                  "rounded-none border border-dashed border-border/70 bg-muted/40",
                  sizeStyles3[size]
                ),
                "aria-hidden": "true"
              },
              key
            );
          }
          const isSelected = entry.id === selectedId;
          const tooltipLabel = entry.tooltip ?? entry.label ?? "Timeline entry";
          const isClickable = !!onEntrySelect;
          const dot = /* @__PURE__ */ jsx81(
            "button",
            {
              type: "button",
              className: cn(
                "rounded-none border transition-all transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                sizeStyles3[size],
                toneStyles[entry.tone],
                isSelected ? "scale-110 shadow-sm border-primary" : "scale-100 hover:scale-125",
                disabled && "opacity-60 pointer-events-none",
                !isClickable && "cursor-default"
              ),
              style: failHatchStyle(entry.tone === "danger"),
              "aria-label": tooltipLabel,
              "aria-pressed": isSelected,
              onClick: isClickable ? () => selectEntry(entry) : void 0
            }
          );
          if (entry.tooltipContent) {
            return /* @__PURE__ */ jsx81("div", { role: "listitem", children: /* @__PURE__ */ jsxs71(TooltipTrigger, { delay: 0, offset: 8, placement: "top", children: [
              dot,
              /* @__PURE__ */ jsx81(Tooltip, { variant: "light", className: "max-w-sm px-3 py-2 text-left", children: entry.tooltipContent })
            ] }) }, entry.id);
          }
          const tooltipId = `status-timeline-${entry.id}`;
          return /* @__PURE__ */ jsxs71("div", { className: "relative group", role: "listitem", children: [
            React56.cloneElement(dot, { "aria-describedby": tooltipId, title: tooltipLabel }),
            /* @__PURE__ */ jsx81(
              "div",
              {
                id: tooltipId,
                role: "tooltip",
                className: cn(
                  "pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md border bg-popover px-2 py-1 text-[10px] text-popover-foreground shadow-md opacity-0",
                  "group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
                ),
                children: tooltipLabel
              }
            )
          ] }, entry.id);
        }) }),
        entries.length === 0 && fallback ? /* @__PURE__ */ jsx81("div", { children: fallback }) : null
      ]
    }
  );
}
StatusTimeline.displayName = "StatusTimeline";

// src/components/PulsingDot.tsx
import { jsx as jsx82 } from "react/jsx-runtime";
function PulsingDot({
  colorClassName = "bg-current",
  sizeClassName = "h-2 w-2",
  noAnimate,
  className
}) {
  return /* @__PURE__ */ jsx82(
    "span",
    {
      "aria-hidden": true,
      className: cn(
        "inline-block rounded-full",
        sizeClassName,
        colorClassName,
        !noAnimate && "animate-pulse",
        className
      )
    }
  );
}
PulsingDot.displayName = "PulsingDot";

// src/components/MetadataItem.tsx
import { jsx as jsx83, jsxs as jsxs72 } from "react/jsx-runtime";
function MetadataItem({
  icon,
  label,
  children,
  mono,
  className,
  action
}) {
  return /* @__PURE__ */ jsxs72("div", { className: cn("flex flex-wrap items-start gap-3 py-3", className), children: [
    /* @__PURE__ */ jsxs72("div", { className: "flex items-center gap-2 w-40 shrink-0 text-sm text-muted-foreground", children: [
      icon,
      /* @__PURE__ */ jsx83("span", { children: label })
    ] }),
    /* @__PURE__ */ jsx83("div", { className: cn("flex-1 min-w-0 text-sm", mono && "font-mono text-[13px]"), children }),
    action && /* @__PURE__ */ jsx83("div", { className: "shrink-0 ml-auto", children: action })
  ] });
}
MetadataItem.displayName = "MetadataItem";

// src/components/TestStatusCell.tsx
import React57 from "react";
import { jsx as jsx84 } from "react/jsx-runtime";
var sizeStyles4 = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-6 w-6"
};
var statusStyles = {
  queued: "bg-status-queued/50 border-status-queued/40 text-foreground",
  running: "bg-status-running/80 border-status-running/70 text-white",
  waiting: "bg-status-waiting/80 border-status-waiting/70 text-white",
  success: "bg-status-success/80 border-status-success/70 text-white",
  failed: "bg-status-failed/80 border-status-failed/70 text-white",
  inconclusive: "bg-muted border-border text-muted-foreground",
  skipped: "bg-status-skipped/80 border-status-skipped/70 text-white",
  flaky_success: "bg-status-flaky/80 border-status-flaky/70 text-white",
  canceled: "bg-status-canceled/80 border-status-canceled/70 text-white",
  timeout: "bg-status-timeout/80 border-status-timeout/70 text-white",
  degraded: "bg-status-degraded/80 border-status-degraded/70 text-white"
};
var subtlePulseStyle = {
  animation: "subtle-pulse 3s ease-in-out infinite"
};
var TestStatusCell = React57.forwardRef(
  function TestStatusCell2({ status, size = "md", icon, disabled, onPress, className, ...rest }, ref) {
    return /* @__PURE__ */ jsx84(
      "button",
      {
        ref,
        type: "button",
        className: cn(
          "inline-flex items-center justify-center rounded-none border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-shadow",
          sizeStyles4[size],
          statusStyles[status],
          onPress && "cursor-pointer hover:ring-2 hover:ring-ring/30",
          disabled && "opacity-60 pointer-events-none",
          className
        ),
        style: status === "running" ? subtlePulseStyle : void 0,
        onClick: onPress,
        ...rest,
        children: icon
      }
    );
  }
);
TestStatusCell.displayName = "TestStatusCell";

// src/components/StatusIconBar.tsx
import React58 from "react";
import {
  Check as Check3,
  X as X3,
  CircleNotch,
  Circle,
  Clock as Clock3,
  Minus as Minus3,
  Prohibit as Prohibit2,
  Shuffle,
  WarningOctagon
} from "@phosphor-icons/react";
import { jsx as jsx85, jsxs as jsxs73 } from "react/jsx-runtime";
var MAX_VISIBLE_ICONS = 60;
var statusGroupLabel = {
  success: "Passing",
  failed: "Failing",
  inconclusive: "Platform errors",
  skipped: "Skipped",
  flaky_success: "Passing",
  running: "Running",
  waiting: "Waiting",
  canceled: "Canceled",
  timeout: "Timed out",
  queued: "Pending",
  degraded: "Degraded"
};
var statusIcon = {
  success: /* @__PURE__ */ jsx85(Check3, { size: 10, weight: "bold" }),
  failed: /* @__PURE__ */ jsx85(X3, { size: 10, weight: "bold" }),
  inconclusive: /* @__PURE__ */ jsx85(Minus3, { size: 10, weight: "bold" }),
  skipped: /* @__PURE__ */ jsx85(Minus3, { size: 10, weight: "bold" }),
  flaky_success: /* @__PURE__ */ jsx85(Shuffle, { size: 10, weight: "bold" }),
  running: /* @__PURE__ */ jsx85(CircleNotch, { size: 10, weight: "bold" }),
  waiting: /* @__PURE__ */ jsx85(Clock3, { size: 10, weight: "bold" }),
  canceled: /* @__PURE__ */ jsx85(Prohibit2, { size: 10, weight: "bold" }),
  timeout: /* @__PURE__ */ jsx85(Clock3, { size: 10, weight: "bold" }),
  queued: /* @__PURE__ */ jsx85(Circle, { size: 8 }),
  degraded: /* @__PURE__ */ jsx85(WarningOctagon, { size: 10, weight: "bold" })
};
var StatusIconItem = ({ item, tooltipVariant = "light", onPress }) => /* @__PURE__ */ jsxs73(TooltipTrigger, { delay: 0, offset: 8, placement: "top", children: [
  /* @__PURE__ */ jsx85(
    TestStatusCell,
    {
      status: item.status,
      size: "sm",
      icon: statusIcon[item.status],
      "aria-label": `${item.name} \u2013 ${statusGroupLabel[item.status]}`,
      onPress: onPress ? () => onPress(item) : void 0
    }
  ),
  /* @__PURE__ */ jsx85(Tooltip, { variant: tooltipVariant, className: "max-w-sm px-3 py-2 text-left", children: /* @__PURE__ */ jsx85(
    RunSummaryTooltip,
    {
      name: item.name,
      status: item.status,
      startedAt: item.startedAt,
      finishedAt: item.finishedAt,
      durationMs: item.durationMs,
      errorMessage: item.errorMessage,
      message: item.message,
      linkedIssueId: item.linkedIssueId,
      skipReason: item.skipReason,
      resumeAt: item.resumeAt,
      segmentIndex: item.segmentIndex,
      totalSegments: item.totalSegments
    }
  ) })
] });
var ShowMoreToggle = ({
  hiddenCount,
  expanded,
  onToggle
}) => /* @__PURE__ */ jsx85(
  "button",
  {
    type: "button",
    onClick: onToggle,
    className: "text-xs font-medium text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors",
    children: expanded ? "Show less" : `Show ${hiddenCount} more`
  }
);
function failuresFirst(items) {
  if (items.length <= MAX_VISIBLE_ICONS) return items;
  const failures = items.filter((item) => item.status === "failed");
  if (failures.length === 0) return items;
  const rest = items.filter((item) => item.status !== "failed");
  return [...failures, ...rest];
}
var StatusIconBar = ({
  items,
  tooltipVariant = "light",
  onItemPress,
  className
}) => {
  const [expanded, setExpanded] = React58.useState(false);
  if (items.length === 0) {
    return /* @__PURE__ */ jsx85(Text, { size: "sm", className: "text-muted-foreground", children: "No workflows to display." });
  }
  const ordered = failuresFirst(items);
  const visible = expanded ? ordered : ordered.slice(0, MAX_VISIBLE_ICONS);
  const hiddenCount = ordered.length - visible.length;
  return /* @__PURE__ */ jsxs73("div", { className: cn("flex flex-wrap items-center gap-1.5", className), children: [
    visible.map((item) => /* @__PURE__ */ jsx85(
      StatusIconItem,
      {
        item,
        tooltipVariant,
        onPress: onItemPress
      },
      item.id
    )),
    hiddenCount > 0 && /* @__PURE__ */ jsx85(
      ShowMoreToggle,
      {
        hiddenCount,
        expanded,
        onToggle: () => setExpanded((e) => !e)
      }
    )
  ] });
};

// src/components/StatusBadge.tsx
import { jsx as jsx86 } from "react/jsx-runtime";
var statusToColor = {
  success: "green",
  failed: "orange",
  running: "purple",
  queued: "gray",
  waiting: "teal",
  canceled: "gray",
  timeout: "gray",
  flaky: "yellow",
  warning: "yellow",
  skipped: "gray",
  degraded: "red"
};
var statusToGlyphKey = {
  success: "success",
  failed: "failed",
  running: "running",
  queued: "queued",
  waiting: "waiting",
  canceled: "canceled",
  timeout: "timeout",
  flaky: "flaky_success",
  warning: "degraded",
  skipped: "skipped",
  degraded: "degraded"
};
var StatusBadge = ({
  status,
  label,
  size = "md",
  className
}) => /* @__PURE__ */ jsx86(
  TagBadge,
  {
    name: label ?? getStatusLabel(status),
    color: statusToColor[status],
    size,
    icon: statusIcon[statusToGlyphKey[status]],
    className
  }
);

// src/components/RunSummaryTooltip.tsx
import { Fragment as Fragment15, jsx as jsx87, jsxs as jsxs74 } from "react/jsx-runtime";
var workflowTestStatusLabel = {
  queued: "Queued",
  running: "Running",
  waiting: "Waiting",
  success: "Passed",
  failed: "Failed",
  inconclusive: "Platform error",
  skipped: "Skipped",
  flaky_success: "Flaky",
  canceled: "Canceled",
  timeout: "Timed out",
  degraded: "Degraded"
};
var testStatusToStatusName = {
  queued: "queued",
  running: "running",
  waiting: "waiting",
  success: "success",
  failed: "failed",
  inconclusive: "skipped",
  skipped: "skipped",
  flaky_success: "flaky",
  canceled: "canceled",
  timeout: "timeout",
  degraded: "degraded"
};
var formatTooltipTimestamp = (value) => {
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};
var defaultMessageForStatus = (status) => {
  switch (status) {
    case "success":
      return "Workflow executed successfully";
    case "flaky_success":
      return "Workflow passed (flaky)";
    case "failed":
      return "Workflow failed";
    case "canceled":
      return "Workflow was cancelled";
    case "timeout":
      return "Workflow timed out";
    case "degraded":
      return "Not run \u2014 workflow health is degraded. Edit or re-enable it to resume.";
    default:
      return "Awaiting results\u2026";
  }
};
var formatDurationMs = (durationMs) => {
  if (!durationMs || durationMs < 0) return "--";
  const totalSeconds = Math.floor(durationMs / 1e3);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
};
var RunSummaryTooltip = ({
  name,
  status,
  startedAt,
  finishedAt,
  durationMs,
  errorMessage,
  message,
  linkedIssueId,
  skipReason,
  resumeAt,
  segmentIndex,
  totalSegments
}) => {
  const statusLabel = workflowTestStatusLabel[status];
  return /* @__PURE__ */ jsxs74(Fragment15, { children: [
    /* @__PURE__ */ jsxs74("div", { className: "flex min-w-0 items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsx87(Text, { weight: "medium", size: "sm", className: "min-w-0 break-words", children: name }),
      /* @__PURE__ */ jsx87(
        StatusBadge,
        {
          status: testStatusToStatusName[status],
          label: statusLabel,
          size: "sm",
          className: "shrink-0"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs74("div", { className: "mt-2 space-y-1 text-[12px] text-muted-foreground", children: [
      startedAt != null && /* @__PURE__ */ jsxs74("div", { className: "flex justify-between gap-2", children: [
        /* @__PURE__ */ jsx87("span", { children: "Started" }),
        /* @__PURE__ */ jsx87("span", { children: formatTooltipTimestamp(startedAt) })
      ] }),
      finishedAt != null && /* @__PURE__ */ jsxs74("div", { className: "flex justify-between gap-2", children: [
        /* @__PURE__ */ jsx87("span", { children: "Finished" }),
        /* @__PURE__ */ jsx87("span", { children: formatTooltipTimestamp(finishedAt) })
      ] }),
      durationMs != null && /* @__PURE__ */ jsxs74("div", { className: "flex justify-between gap-2", children: [
        /* @__PURE__ */ jsx87("span", { children: "Duration" }),
        /* @__PURE__ */ jsx87("span", { children: formatDurationMs(durationMs) })
      ] }),
      /* @__PURE__ */ jsx87("div", { className: "min-w-0 pt-2 border-t border-border text-foreground [overflow-wrap:anywhere]", children: errorMessage ? /* @__PURE__ */ jsx87("span", { className: "line-clamp-4 text-destructive", children: errorMessage }) : status === "skipped" ? /* @__PURE__ */ jsx87("span", { children: skipReason ?? message ?? "Workflow was skipped" }) : /* @__PURE__ */ jsx87("span", { children: message ?? defaultMessageForStatus(status) }) }),
      status === "waiting" && resumeAt && /* @__PURE__ */ jsxs74("div", { className: "pt-2 border-t border-border", children: [
        /* @__PURE__ */ jsxs74("div", { className: "flex justify-between gap-2", children: [
          /* @__PURE__ */ jsx87("span", { children: "Resumes at" }),
          /* @__PURE__ */ jsx87("span", { children: formatTooltipTimestamp(resumeAt) })
        ] }),
        segmentIndex && totalSegments && /* @__PURE__ */ jsxs74("div", { className: "flex justify-between gap-2 mt-1", children: [
          /* @__PURE__ */ jsx87("span", { children: "Progress" }),
          /* @__PURE__ */ jsxs74("span", { children: [
            "Segment ",
            segmentIndex,
            "/",
            totalSegments
          ] })
        ] })
      ] }),
      linkedIssueId && /* @__PURE__ */ jsxs74("div", { className: "text-amber-600 text-[11px]", children: [
        "Issue filed: ",
        linkedIssueId
      ] })
    ] })
  ] });
};

// src/components/MultiSegmentGauge.tsx
import { jsx as jsx88, jsxs as jsxs75 } from "react/jsx-runtime";
var sizeConfig3 = {
  xs: { width: 80, strokeWidth: 6, fontSize: 14, labelSize: 9 },
  sm: { width: 120, strokeWidth: 8, fontSize: 20, labelSize: 11 },
  md: { width: 160, strokeWidth: 10, fontSize: 26, labelSize: 13 }
};
var MultiSegmentGauge = ({
  segments,
  total,
  size = "sm",
  centerLabel,
  centerValue,
  className
}) => {
  const config = sizeConfig3[size];
  const radius = (config.width - config.strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = config.width / 2;
  const activeSegments = segments.filter((s) => s.value > 0);
  const effectiveTotal = total > 0 ? total : 1;
  let cumulativeOffset = 0;
  const arcs = activeSegments.map((segment) => {
    const fraction = segment.value / effectiveTotal;
    const dashLength = fraction * circumference;
    const adjustedDash = dashLength;
    const offset = cumulativeOffset;
    cumulativeOffset += dashLength;
    return {
      ...segment,
      dashArray: `${adjustedDash} ${circumference - adjustedDash}`,
      dashOffset: -offset,
      isPulsing: segment.label === "running"
    };
  });
  return /* @__PURE__ */ jsxs75("div", { className: cn("relative inline-flex flex-col items-center", className), children: [
    /* @__PURE__ */ jsxs75(
      "svg",
      {
        width: config.width,
        height: config.width,
        viewBox: `0 0 ${config.width} ${config.width}`,
        className: "transform -rotate-90",
        children: [
          /* @__PURE__ */ jsx88(
            "circle",
            {
              cx: center,
              cy: center,
              r: radius,
              fill: "none",
              stroke: "hsl(var(--muted))",
              strokeWidth: config.strokeWidth,
              opacity: 0.3
            }
          ),
          arcs.map((arc, i) => /* @__PURE__ */ jsx88(
            "circle",
            {
              cx: center,
              cy: center,
              r: radius,
              fill: "none",
              stroke: arc.color,
              strokeWidth: config.strokeWidth,
              strokeDasharray: arc.dashArray,
              strokeDashoffset: arc.dashOffset,
              strokeLinecap: "round",
              className: "transition-[stroke-dashoffset] duration-500 ease-out",
              style: arc.isPulsing ? { animation: "subtle-pulse 3s ease-in-out infinite" } : void 0
            },
            arc.label + i
          ))
        ]
      }
    ),
    (centerValue || centerLabel) && /* @__PURE__ */ jsxs75(
      "div",
      {
        className: "absolute flex flex-col items-center justify-center pointer-events-none",
        style: {
          width: config.width,
          height: config.width
        },
        children: [
          centerValue && /* @__PURE__ */ jsx88(
            "span",
            {
              className: "font-semibold text-foreground leading-none",
              style: { fontSize: config.fontSize },
              children: centerValue
            }
          ),
          centerLabel && /* @__PURE__ */ jsx88(
            "span",
            {
              className: "text-muted-foreground leading-tight mt-0.5",
              style: { fontSize: config.labelSize },
              children: centerLabel
            }
          )
        ]
      }
    )
  ] });
};

// src/components/MultiSegmentBar.tsx
import { jsx as jsx89, jsxs as jsxs76 } from "react/jsx-runtime";
var sizeMap2 = {
  xs: "h-1.5",
  sm: "h-2",
  md: "h-2.5"
};
var MultiSegmentBar = ({
  segments,
  total,
  size = "sm",
  className,
  "aria-label": ariaLabel
}) => {
  const effectiveTotal = total > 0 ? total : 1;
  const activeSegments = segments.filter((s) => s.value > 0);
  return /* @__PURE__ */ jsx89(
    "div",
    {
      className: cn("w-full overflow-hidden rounded-full bg-muted/30", sizeMap2[size], className),
      role: "progressbar",
      "aria-label": ariaLabel,
      "aria-valuemin": 0,
      "aria-valuemax": total,
      children: /* @__PURE__ */ jsx89("div", { className: "flex h-full", children: activeSegments.map((segment, i) => {
        const pct = segment.value / effectiveTotal * 100;
        const segmentClassName = "h-full transition-[width] duration-500 ease-out first:rounded-l-full last:rounded-r-full";
        const segmentStyle = {
          width: `${pct}%`,
          backgroundColor: segment.color
        };
        if (segment.tooltip) {
          return /* @__PURE__ */ jsxs76(TooltipTrigger, { delay: 150, offset: 8, placement: "top", children: [
            /* @__PURE__ */ jsx89("div", { className: segmentClassName, style: segmentStyle }),
            /* @__PURE__ */ jsx89(Tooltip, { variant: "light", children: segment.tooltip })
          ] }, segment.label + i);
        }
        return /* @__PURE__ */ jsx89(
          "div",
          {
            className: segmentClassName,
            style: segmentStyle,
            title: `${segment.label}: ${segment.value}`
          },
          segment.label + i
        );
      }) })
    }
  );
};

// src/components/Sparkline.tsx
import { jsx as jsx90, jsxs as jsxs77 } from "react/jsx-runtime";
var DEFAULT_COLOR = "#4DA6FF";
var Sparkline = ({
  points,
  width = 96,
  height = 24,
  color = DEFAULT_COLOR,
  showLastDot = true,
  className,
  "aria-label": ariaLabel
}) => {
  if (points.length === 0) {
    return /* @__PURE__ */ jsx90(
      "svg",
      {
        width,
        height,
        className,
        role: "img",
        "aria-label": ariaLabel ?? "No data"
      }
    );
  }
  const pad = 2;
  const w = width - pad * 2;
  const h = height - pad * 2;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const denom = points.length > 1 ? points.length - 1 : 1;
  const coords = points.map((value, i) => {
    const x = pad + i / denom * w;
    const y = pad + h - (value - min) / range * h;
    return [x, y];
  });
  const polyline = coords.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
  const last = coords[coords.length - 1];
  return /* @__PURE__ */ jsxs77(
    "svg",
    {
      width,
      height,
      viewBox: `0 0 ${width} ${height}`,
      className: cn("overflow-visible", className),
      role: "img",
      "aria-label": ariaLabel ?? `Trend of ${points.length} points`,
      children: [
        /* @__PURE__ */ jsx90(
          "polyline",
          {
            points: polyline,
            fill: "none",
            stroke: color,
            strokeWidth: 1.5,
            strokeLinejoin: "round",
            strokeLinecap: "round"
          }
        ),
        showLastDot && /* @__PURE__ */ jsx90("circle", { cx: last[0], cy: last[1], r: 2, fill: color })
      ]
    }
  );
};

// src/components/MetricChart.tsx
import React59 from "react";
import { Fragment as Fragment16, jsx as jsx91, jsxs as jsxs78 } from "react/jsx-runtime";
var DEFAULT_COLOR2 = "#4DA6FF";
var MetricChart = ({
  points,
  width = 520,
  height = 160,
  color = DEFAULT_COLOR2,
  unit,
  className
}) => {
  const [hoverIdx, setHoverIdx] = React59.useState(null);
  if (points.length === 0) {
    return /* @__PURE__ */ jsx91(
      "div",
      {
        className: cn(
          "flex items-center justify-center rounded-md border border-border bg-muted/20 text-sm text-muted-foreground",
          className
        ),
        style: { width, height },
        children: "No datapoints yet"
      }
    );
  }
  const padX = 8;
  const padTop = 12;
  const padBottom = 12;
  const w = width - padX * 2;
  const h = height - padTop - padBottom;
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const denom = points.length > 1 ? points.length - 1 : 1;
  const coords = points.map((p, i) => {
    const x = padX + i / denom * w;
    const y = padTop + h - (p.value - min) / range * h;
    return [x, y];
  });
  const polyline = coords.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, (x - padX) / (w || 1)));
    setHoverIdx(Math.round(ratio * denom));
  };
  const hover = hoverIdx !== null ? coords[hoverIdx] : null;
  const hoverPoint = hoverIdx !== null ? points[hoverIdx] : null;
  const fmtTs = (ts) => new Date(ts).toLocaleString();
  return /* @__PURE__ */ jsxs78("div", { className: cn("relative", className), style: { width }, children: [
    /* @__PURE__ */ jsxs78(
      "svg",
      {
        width,
        height,
        viewBox: `0 0 ${width} ${height}`,
        className: "overflow-visible",
        onMouseMove: onMove,
        onMouseLeave: () => setHoverIdx(null),
        role: "img",
        "aria-label": `Metric time series, ${points.length} points`,
        children: [
          /* @__PURE__ */ jsx91(
            "line",
            {
              x1: padX,
              y1: padTop + h,
              x2: padX + w,
              y2: padTop + h,
              stroke: "currentColor",
              className: "text-border",
              strokeWidth: 1
            }
          ),
          /* @__PURE__ */ jsx91(
            "polyline",
            {
              points: polyline,
              fill: "none",
              stroke: color,
              strokeWidth: 2,
              strokeLinejoin: "round",
              strokeLinecap: "round"
            }
          ),
          hover && /* @__PURE__ */ jsxs78(Fragment16, { children: [
            /* @__PURE__ */ jsx91(
              "line",
              {
                x1: hover[0],
                y1: padTop,
                x2: hover[0],
                y2: padTop + h,
                stroke: color,
                strokeWidth: 1,
                strokeDasharray: "3 3",
                opacity: 0.5
              }
            ),
            /* @__PURE__ */ jsx91("circle", { cx: hover[0], cy: hover[1], r: 3.5, fill: color })
          ] })
        ]
      }
    ),
    hover && hoverPoint && /* @__PURE__ */ jsxs78(
      "div",
      {
        className: "pointer-events-none absolute -translate-x-1/2 rounded-md border border-border bg-popover px-2 py-1 text-xs shadow-md",
        style: { left: hover[0], top: 0 },
        children: [
          /* @__PURE__ */ jsxs78("div", { className: "font-medium text-foreground", children: [
            hoverPoint.value,
            unit ? ` ${unit}` : ""
          ] }),
          /* @__PURE__ */ jsx91("div", { className: "text-muted-foreground", children: fmtTs(hoverPoint.capturedAt) })
        ]
      }
    )
  ] });
};

// src/utils/viewTransition.ts
function startViewTransition(callback) {
  if (document.startViewTransition) {
    document.startViewTransition(callback);
  } else {
    callback();
  }
}

// src/components/CollapsibleSection.tsx
import React60 from "react";
import { CaretRight as CaretRight12 } from "@phosphor-icons/react";
import { motion as motion6, AnimatePresence as AnimatePresence3 } from "framer-motion";
import { Fragment as Fragment17, jsx as jsx92, jsxs as jsxs79 } from "react/jsx-runtime";
function CollapsibleSection({
  label,
  icon,
  count,
  header,
  defaultExpanded = false,
  isExpanded: controlledExpanded,
  onExpandedChange,
  hideBorder = false,
  bodyClassName,
  triggerClassName,
  triggerMode = "row",
  actions,
  children,
  className
}) {
  const [uncontrolledExpanded, setUncontrolledExpanded] = React60.useState(defaultExpanded);
  const isControlled = controlledExpanded !== void 0;
  const isExpanded = isControlled ? controlledExpanded : uncontrolledExpanded;
  const toggle = () => {
    const next = !isExpanded;
    if (!isControlled) setUncontrolledExpanded(next);
    onExpandedChange?.(next);
  };
  const headerContent = header ? /* @__PURE__ */ jsx92("div", { className: "min-w-0 flex-1", children: header }) : /* @__PURE__ */ jsxs79(Fragment17, { children: [
    icon && /* @__PURE__ */ jsx92("span", { className: "shrink-0", children: icon }),
    label !== void 0 && /* @__PURE__ */ jsx92(Text, { size: "xs", weight: "medium", className: "text-muted-foreground uppercase tracking-wide", children: label }),
    count !== void 0 && count > 0 && /* @__PURE__ */ jsx92("span", { className: "inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-muted text-[10px] font-medium text-muted-foreground", children: count })
  ] });
  const caret = /* @__PURE__ */ jsx92(
    CaretRight12,
    {
      size: 14,
      weight: "bold",
      className: cn(
        "text-muted-foreground transition-transform duration-200 shrink-0",
        isExpanded && "rotate-90"
      )
    }
  );
  return /* @__PURE__ */ jsxs79("div", { className: cn(!hideBorder && "border-t border-border/50", className), children: [
    triggerMode === "caret" ? /* @__PURE__ */ jsxs79(
      "div",
      {
        className: cn(
          "flex items-center gap-2 w-full py-2.5 hover:bg-muted/30 transition-colors rounded-sm -mx-1 px-1",
          triggerClassName
        ),
        children: [
          /* @__PURE__ */ jsx92(
            "button",
            {
              type: "button",
              onClick: toggle,
              "aria-label": label,
              "aria-expanded": isExpanded,
              className: "-ml-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm hover:bg-muted/60",
              children: caret
            }
          ),
          header ? headerContent : /* @__PURE__ */ jsx92("div", { className: "min-w-0 flex-1 flex items-center gap-2", children: headerContent }),
          actions && /* @__PURE__ */ jsx92("div", { className: "shrink-0", children: actions })
        ]
      }
    ) : actions ? /* @__PURE__ */ jsxs79("div", { className: "flex items-center gap-2 w-full", children: [
      /* @__PURE__ */ jsxs79(
        "button",
        {
          type: "button",
          onClick: toggle,
          className: cn(
            "flex items-center gap-2 flex-1 min-w-0 py-2.5 text-left hover:bg-muted/30 transition-colors rounded-sm -mx-1 px-1",
            triggerClassName
          ),
          children: [
            caret,
            headerContent
          ]
        }
      ),
      /* @__PURE__ */ jsx92("div", { className: "shrink-0", children: actions })
    ] }) : /* @__PURE__ */ jsxs79(
      "button",
      {
        type: "button",
        onClick: toggle,
        className: cn(
          "flex items-center gap-2 w-full py-2.5 text-left hover:bg-muted/30 transition-colors rounded-sm -mx-1 px-1",
          triggerClassName
        ),
        children: [
          caret,
          headerContent
        ]
      }
    ),
    /* @__PURE__ */ jsx92(AnimatePresence3, { initial: false, children: isExpanded && /* @__PURE__ */ jsx92(
      motion6.div,
      {
        initial: { height: 0, opacity: 0 },
        animate: { height: "auto", opacity: 1 },
        exit: { height: 0, opacity: 0 },
        transition: { duration: 0.2, ease: "easeInOut" },
        className: "overflow-hidden",
        children: /* @__PURE__ */ jsx92("div", { className: cn("pb-3", bodyClassName), children })
      }
    ) })
  ] });
}

// src/components/StepProgressBar.tsx
import React61 from "react";
import { motion as motion7, useReducedMotion } from "framer-motion";
import { Check as Check4 } from "@phosphor-icons/react";
import { Fragment as Fragment18, jsx as jsx93, jsxs as jsxs80 } from "react/jsx-runtime";
var StepProgressBar = React61.forwardRef(
  ({ steps, currentStep, className, ariaLabel = "Progress", onStepClick }, ref) => {
    const prefersReducedMotion3 = useReducedMotion();
    return /* @__PURE__ */ jsx93("nav", { ref, "aria-label": ariaLabel, className: cn("w-full", className), children: /* @__PURE__ */ jsx93("ol", { className: "flex items-start justify-between", children: steps.map((step, index) => {
      const isCompleted = index < currentStep;
      const isCurrent = index === currentStep;
      const isUpcoming = index > currentStep;
      const isClickable = isCompleted && Boolean(onStepClick);
      const state = isCompleted ? "completed" : isCurrent ? "current" : "upcoming";
      const description = step.description ? ` \u2014 ${step.description}` : "";
      const stateLabel = isCurrent ? "current" : isCompleted ? "completed" : "upcoming";
      const accessibleLabel = `Step ${index + 1}: ${step.label} (${stateLabel})${description}`;
      const circleClassName = cn(
        "relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 transition-colors motion-reduce:transition-none",
        isCompleted && "border-primary bg-primary text-primary-foreground",
        isCurrent && "border-primary bg-card",
        isUpcoming && "border-border bg-background",
        isClickable && "cursor-pointer hover:opacity-80"
      );
      const circleContent = /* @__PURE__ */ jsxs80(Fragment18, { children: [
        isCompleted ? /* @__PURE__ */ jsx93(
          motion7.div,
          {
            initial: { scale: 0, opacity: 0 },
            animate: { scale: 1, opacity: 1 },
            transition: {
              type: "spring",
              stiffness: 400,
              damping: 20
            },
            children: /* @__PURE__ */ jsx93(Check4, { size: 16, weight: "bold" })
          }
        ) : /* @__PURE__ */ jsx93(
          motion7.span,
          {
            className: cn(
              "text-sm font-medium",
              isCurrent && "text-primary",
              isUpcoming && "text-muted-foreground"
            ),
            initial: false,
            animate: { opacity: 1 },
            children: index + 1
          }
        ),
        isCurrent && !prefersReducedMotion3 && /* @__PURE__ */ jsx93(
          motion7.div,
          {
            className: "absolute inset-0 rounded-full border-2 border-primary",
            initial: { scale: 1, opacity: 0.6 },
            animate: {
              scale: [1, 1.3, 1],
              opacity: [0.6, 0, 0.6]
            },
            transition: {
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            },
            "aria-hidden": "true"
          }
        )
      ] });
      return /* @__PURE__ */ jsxs80(
        "li",
        {
          "data-state": state,
          "aria-current": isCurrent ? "step" : void 0,
          className: cn(
            "relative flex flex-1 flex-col items-center",
            index !== steps.length - 1 && "after:absolute after:left-[calc(50%+20px)] after:right-[calc(-50%+20px)] after:top-4 after:h-[2px]",
            isCompleted && "after:bg-primary",
            isCurrent && "after:bg-gradient-to-r after:from-primary after:to-border",
            isUpcoming && "after:bg-border after:opacity-50"
          ),
          children: [
            isClickable ? /* @__PURE__ */ jsx93(
              motion7.button,
              {
                type: "button",
                onClick: () => onStepClick?.(index),
                "aria-current": isCurrent ? "step" : void 0,
                "aria-label": accessibleLabel,
                "data-state": state,
                className: circleClassName,
                initial: false,
                animate: {
                  scale: isCurrent ? 1 : 1,
                  boxShadow: isCurrent ? "0 0 0 4px hsl(var(--primary) / 0.2)" : "0 0 0 0px hsl(var(--primary) / 0)"
                },
                transition: {
                  type: "spring",
                  stiffness: 300,
                  damping: 25
                },
                children: circleContent
              }
            ) : /* @__PURE__ */ jsx93(
              motion7.div,
              {
                "aria-hidden": "true",
                "data-state": state,
                className: circleClassName,
                initial: false,
                animate: {
                  scale: isCurrent ? 1 : 1,
                  boxShadow: isCurrent ? "0 0 0 4px hsl(var(--primary) / 0.2)" : "0 0 0 0px hsl(var(--primary) / 0)"
                },
                transition: {
                  type: "spring",
                  stiffness: 300,
                  damping: 25
                },
                children: circleContent
              }
            ),
            /* @__PURE__ */ jsxs80(
              motion7.div,
              {
                className: "mt-2 flex flex-col items-center",
                initial: false,
                animate: {
                  opacity: isUpcoming ? 0.5 : 1
                },
                children: [
                  /* @__PURE__ */ jsx93(
                    "span",
                    {
                      className: cn(
                        "text-sm font-medium",
                        isCompleted && "text-foreground",
                        isCurrent && "text-primary",
                        isUpcoming && "text-muted-foreground"
                      ),
                      children: step.label
                    }
                  ),
                  step.description && /* @__PURE__ */ jsx93("span", { className: "text-xs text-muted-foreground mt-0.5 text-center max-w-[100px]", children: step.description })
                ]
              }
            )
          ]
        },
        step.id
      );
    }) }) });
  }
);
StepProgressBar.displayName = "StepProgressBar";

// src/components/ScreenshotCarousel.tsx
import React62 from "react";
import { CaretLeft as CaretLeft8, CaretRight as CaretRight13 } from "@phosphor-icons/react";
import { Fragment as Fragment19, jsx as jsx94, jsxs as jsxs81 } from "react/jsx-runtime";
var ScreenshotCarousel = ({
  items,
  emptyLabel = "No screenshots available",
  dialogTitle = "Screenshot",
  className,
  aspectRatioClass = "aspect-[3/2]",
  resetKey
}) => {
  const [activeIndex, setActiveIndex] = React62.useState(0);
  React62.useEffect(() => {
    setActiveIndex(Math.max(0, items.length - 1));
  }, [resetKey, items.length]);
  if (!items.length) {
    const emptyClasses = [
      "flex",
      aspectRatioClass,
      "w-full items-center justify-center rounded-xl border border-dashed border-border",
      "text-sm text-muted-foreground",
      className
    ].filter(Boolean).join(" ");
    return /* @__PURE__ */ jsx94(Box, { className: emptyClasses, children: /* @__PURE__ */ jsx94(Text, { children: emptyLabel }) });
  }
  const hasMultiple = items.length > 1;
  const steps = items.map((item, index) => ({
    id: item.id,
    label: item.caption || `Screenshot ${index + 1}`
  }));
  return /* @__PURE__ */ jsxs81("div", { className: className ? `space-y-3 ${className}` : "space-y-3", children: [
    /* @__PURE__ */ jsxs81(
      "div",
      {
        className: `relative ${aspectRatioClass} w-full overflow-hidden rounded-xl border border-border bg-muted shadow-sm`,
        children: [
          items.map((item, idx) => /* @__PURE__ */ jsx94(
            "img",
            {
              src: item.src,
              alt: item.caption ?? dialogTitle,
              className: `absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${idx === activeIndex ? "opacity-100" : "opacity-0"}`,
              loading: idx === activeIndex ? "eager" : "lazy",
              "aria-hidden": idx === activeIndex ? void 0 : true
            },
            item.id
          )),
          hasMultiple && /* @__PURE__ */ jsxs81(Fragment19, { children: [
            /* @__PURE__ */ jsx94(
              Button,
              {
                variant: "secondary",
                size: "sm",
                iconOnly: true,
                iconLeft: /* @__PURE__ */ jsx94(CaretLeft8, { size: 16 }),
                className: "absolute left-3 top-1/2 -translate-y-1/2 bg-background/80 shadow",
                onPress: () => setActiveIndex((prev) => prev === 0 ? items.length - 1 : prev - 1),
                "aria-label": "Previous screenshot"
              }
            ),
            /* @__PURE__ */ jsx94(
              Button,
              {
                variant: "secondary",
                size: "sm",
                iconOnly: true,
                iconLeft: /* @__PURE__ */ jsx94(CaretRight13, { size: 16 }),
                className: "absolute right-3 top-1/2 -translate-y-1/2 bg-background/80 shadow",
                onPress: () => setActiveIndex((prev) => prev === items.length - 1 ? 0 : prev + 1),
                "aria-label": "Next screenshot"
              }
            )
          ] }),
          /* @__PURE__ */ jsxs81(DialogTrigger, { size: "4xl", children: [
            /* @__PURE__ */ jsx94(
              Button,
              {
                variant: "outline",
                size: "sm",
                className: "absolute bottom-3 right-3 bg-background/90",
                children: "Open full size"
              }
            ),
            /* @__PURE__ */ jsx94(Dialog5, { title: dialogTitle, children: /* @__PURE__ */ jsxs81("div", { className: "relative", children: [
              /* @__PURE__ */ jsxs81("div", { className: "relative w-full", style: { minHeight: "60vh" }, children: [
                items.map((item, idx) => /* @__PURE__ */ jsx94(
                  "img",
                  {
                    src: item.src,
                    alt: item.caption ?? dialogTitle,
                    className: `max-h-[80vh] w-full rounded-md object-contain transition-opacity duration-300 ${idx === activeIndex ? "opacity-100" : "opacity-0 absolute inset-0"}`,
                    loading: idx === activeIndex ? "eager" : "lazy",
                    "aria-hidden": idx === activeIndex ? void 0 : true
                  },
                  item.id
                )),
                hasMultiple && /* @__PURE__ */ jsxs81(Fragment19, { children: [
                  /* @__PURE__ */ jsx94(
                    Button,
                    {
                      variant: "secondary",
                      size: "sm",
                      iconOnly: true,
                      iconLeft: /* @__PURE__ */ jsx94(CaretLeft8, { size: 16 }),
                      className: "absolute left-3 top-1/2 -translate-y-1/2 bg-background/80 shadow",
                      onPress: () => setActiveIndex((prev) => prev === 0 ? items.length - 1 : prev - 1),
                      "aria-label": "Previous screenshot"
                    }
                  ),
                  /* @__PURE__ */ jsx94(
                    Button,
                    {
                      variant: "secondary",
                      size: "sm",
                      iconOnly: true,
                      iconLeft: /* @__PURE__ */ jsx94(CaretRight13, { size: 16 }),
                      className: "absolute right-3 top-1/2 -translate-y-1/2 bg-background/80 shadow",
                      onPress: () => setActiveIndex((prev) => prev === items.length - 1 ? 0 : prev + 1),
                      "aria-label": "Next screenshot"
                    }
                  )
                ] })
              ] }),
              hasMultiple && /* @__PURE__ */ jsx94(Flex, { justify: "center", className: "mt-4", children: /* @__PURE__ */ jsx94(
                StepIndicator,
                {
                  steps,
                  currentStepIndex: activeIndex,
                  onStepSelect: (index) => setActiveIndex(index)
                }
              ) })
            ] }) })
          ] })
        ]
      }
    ),
    hasMultiple && /* @__PURE__ */ jsx94(Flex, { justify: "center", children: /* @__PURE__ */ jsx94(
      StepIndicator,
      {
        steps,
        currentStepIndex: activeIndex,
        onStepSelect: (index) => setActiveIndex(index)
      }
    ) })
  ] });
};

// src/components/MediaPlayer.tsx
import React63 from "react";
import { Play as Play3, SpinnerGap, WarningCircle as WarningCircle2 } from "@phosphor-icons/react";
import { jsx as jsx95, jsxs as jsxs82 } from "react/jsx-runtime";
var MediaPlayer = ({
  posterSrc,
  videoSrc,
  alt = "Media content",
  className,
  aspectRatioClass = "aspect-video"
}) => {
  const [isPlaying, setIsPlaying] = React63.useState(false);
  const [isLoading, setIsLoading] = React63.useState(false);
  const [hasError, setHasError] = React63.useState(false);
  const videoRef = React63.useRef(null);
  React63.useEffect(() => {
    setIsPlaying(false);
    setIsLoading(false);
    setHasError(false);
  }, [posterSrc, videoSrc]);
  const prefetchedRef = React63.useRef(false);
  const handlePrefetch = React63.useCallback(() => {
    if (prefetchedRef.current || !videoSrc) return;
    prefetchedRef.current = true;
    fetch(videoSrc, { method: "HEAD", credentials: "include" }).catch(() => {
    });
  }, [videoSrc]);
  React63.useEffect(() => {
    prefetchedRef.current = false;
  }, [videoSrc]);
  const handlePlayClick = React63.useCallback(() => {
    setIsLoading(true);
    setIsPlaying(true);
    setHasError(false);
  }, []);
  const handleCanPlay = React63.useCallback(() => {
    setIsLoading(false);
    videoRef.current?.play().catch(() => {
      setIsLoading(false);
    });
  }, []);
  const handleError = React63.useCallback(() => {
    setIsLoading(false);
    setIsPlaying(false);
    setHasError(true);
  }, []);
  const hasVideo = Boolean(videoSrc);
  const hasPoster = Boolean(posterSrc);
  if (!hasPoster && !hasVideo) {
    return /* @__PURE__ */ jsx95(
      Box,
      {
        className: `flex ${aspectRatioClass} w-full items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 ${className ?? ""}`,
        children: /* @__PURE__ */ jsx95(Text, { size: "sm", className: "text-muted-foreground", children: "No media available" })
      }
    );
  }
  if (isPlaying && videoSrc) {
    return /* @__PURE__ */ jsxs82("div", { className: `relative ${aspectRatioClass} w-full overflow-hidden rounded-lg bg-black ${className ?? ""}`, children: [
      /* @__PURE__ */ jsx95(
        "video",
        {
          ref: videoRef,
          src: videoSrc,
          poster: posterSrc ?? void 0,
          controls: true,
          playsInline: true,
          preload: "metadata",
          className: "absolute inset-0 h-full w-full object-contain",
          onCanPlay: handleCanPlay,
          onError: handleError,
          onEnded: () => setIsPlaying(false),
          children: "Your browser does not support video playback."
        }
      ),
      isLoading && /* @__PURE__ */ jsx95("div", { className: "absolute inset-0 flex items-center justify-center bg-black/50", children: /* @__PURE__ */ jsx95(SpinnerGap, { size: 48, className: "animate-spin text-white" }) })
    ] });
  }
  return /* @__PURE__ */ jsxs82("div", { className: `relative ${aspectRatioClass} w-full overflow-hidden rounded-lg border border-border bg-muted ${className ?? ""}`, children: [
    hasPoster ? /* @__PURE__ */ jsx95(
      "img",
      {
        src: posterSrc,
        alt,
        className: hasVideo ? "absolute inset-0 h-full w-full object-cover" : "w-full h-auto object-contain rounded-lg"
      }
    ) : /* @__PURE__ */ jsx95("div", { className: "absolute inset-0 flex items-center justify-center bg-muted", children: /* @__PURE__ */ jsx95(Text, { size: "sm", className: "text-muted-foreground", children: "Video available" }) }),
    hasError && /* @__PURE__ */ jsxs82("div", { className: "absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/50", children: [
      /* @__PURE__ */ jsx95(WarningCircle2, { size: 32, className: "text-white/80" }),
      /* @__PURE__ */ jsx95(Text, { size: "sm", className: "text-white/80", children: "Video failed to load" }),
      /* @__PURE__ */ jsx95(
        "button",
        {
          type: "button",
          onClick: handlePlayClick,
          className: "mt-1 rounded-md bg-white/20 px-3 py-1 text-sm text-white transition-colors hover:bg-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          children: "Retry"
        }
      )
    ] }),
    hasVideo && !hasError && /* @__PURE__ */ jsx95(
      "button",
      {
        type: "button",
        onClick: handlePlayClick,
        onMouseEnter: handlePrefetch,
        className: "absolute inset-0 flex items-center justify-center bg-black/30 transition-colors hover:bg-black/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "aria-label": "Play video",
        children: /* @__PURE__ */ jsx95("div", { className: "flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-lg transition-transform hover:scale-105", children: /* @__PURE__ */ jsx95(Play3, { size: 32, weight: "fill", className: "text-foreground ml-1" }) })
      }
    )
  ] });
};

// src/components/VideoPlayerWithTimeline.tsx
import React67 from "react";
import {
  Play as Play4,
  Pause,
  CornersOut,
  X as X4,
  SpinnerGap as SpinnerGap2,
  WarningCircle as WarningCircle3,
  DownloadSimple,
  Camera as Camera2,
  Brain as Brain2,
  CheckCircle as CheckCircle4,
  Terminal as Terminal3,
  Globe,
  VideoCamera as VideoCamera2,
  Lock,
  Copy as Copy3,
  Check as Check5,
  ArrowSquareOut,
  UsersThree as UsersThree3
} from "@phosphor-icons/react";

// src/components/diagnostics/ConsoleDiagnosticsPanel.tsx
import React65 from "react";
import { CaretDown as CaretDown7, CaretRight as CaretRight14 } from "@phosphor-icons/react";

// src/components/diagnostics/TimelineSyncedList.tsx
import React64 from "react";
import { ArrowDown } from "@phosphor-icons/react";
import { jsx as jsx96, jsxs as jsxs83 } from "react/jsx-runtime";
function TimelineSyncedList({
  items,
  currentTimeMs,
  renderItem,
  getTimestamp,
  getKey,
  onSeek,
  emptyState,
  isLoading,
  className
}) {
  const listRef = React64.useRef(null);
  const [isAutoScrollPaused, setIsAutoScrollPaused] = React64.useState(false);
  const [showJumpToLive, setShowJumpToLive] = React64.useState(false);
  const lastScrollTimeRef = React64.useRef(0);
  const isAutoScrollingRef = React64.useRef(false);
  const activeIndex = React64.useMemo(() => {
    let current = -1;
    for (let i = 0; i < items.length; i++) {
      if (getTimestamp(items[i]) <= currentTimeMs) {
        current = i;
      } else {
        break;
      }
    }
    return current;
  }, [items, currentTimeMs, getTimestamp]);
  const handleScroll = React64.useCallback(() => {
    if (isAutoScrollingRef.current) {
      return;
    }
    const now2 = Date.now();
    if (now2 - lastScrollTimeRef.current < 50) {
      return;
    }
    lastScrollTimeRef.current = now2;
    setIsAutoScrollPaused(true);
    setShowJumpToLive(true);
  }, []);
  React64.useEffect(() => {
    if (isAutoScrollPaused || activeIndex < 0 || !listRef.current) {
      return;
    }
    const items2 = listRef.current.querySelectorAll("[data-timeline-item]");
    const activeItem = items2[activeIndex];
    if (activeItem) {
      isAutoScrollingRef.current = true;
      scrollWithin(listRef.current, activeItem, "nearest");
      setTimeout(() => {
        isAutoScrollingRef.current = false;
      }, 300);
    }
  }, [activeIndex, isAutoScrollPaused]);
  const handleJumpToLive = React64.useCallback(() => {
    setIsAutoScrollPaused(false);
    setShowJumpToLive(false);
  }, []);
  if (isLoading) {
    return /* @__PURE__ */ jsx96("div", { className: `flex items-center justify-center py-8 ${className ?? ""}`, children: /* @__PURE__ */ jsx96("div", { className: "animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" }) });
  }
  if (items.length === 0) {
    return /* @__PURE__ */ jsx96("div", { className: `flex items-center justify-center py-8 text-muted-foreground text-sm ${className ?? ""}`, children: emptyState ?? "No items to display" });
  }
  return /* @__PURE__ */ jsxs83("div", { className: `relative flex flex-col h-full min-h-0 ${className ?? ""}`, children: [
    /* @__PURE__ */ jsx96(
      "div",
      {
        ref: listRef,
        className: "flex-1 min-h-0 overflow-y-auto",
        onScroll: handleScroll,
        children: items.map((item, index) => {
          const timestamp = getTimestamp(item);
          const isActive = index === activeIndex;
          const isPast = timestamp < currentTimeMs && !isActive;
          return /* @__PURE__ */ jsx96(
            "div",
            {
              "data-timeline-item": true,
              onClick: () => onSeek?.(timestamp),
              className: onSeek ? "cursor-pointer" : void 0,
              children: renderItem(item, isActive, isPast)
            },
            getKey(item, index)
          );
        })
      }
    ),
    showJumpToLive && /* @__PURE__ */ jsxs83(
      "button",
      {
        type: "button",
        onClick: handleJumpToLive,
        className: "absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-full text-xs font-medium shadow-lg hover:bg-primary/90 transition-colors",
        children: [
          /* @__PURE__ */ jsx96(ArrowDown, { size: 12, weight: "bold" }),
          "Jump to live"
        ]
      }
    )
  ] });
}

// src/components/diagnostics/format.ts
function formatAbsoluteTimestamp(videoStartedAt, offsetMs) {
  if (!videoStartedAt) return null;
  const baseMs = Date.parse(videoStartedAt);
  if (Number.isNaN(baseMs)) return null;
  const d = new Date(baseMs + offsetMs);
  const time = d.toLocaleTimeString(void 0, {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit"
  });
  const ms = d.getMilliseconds().toString().padStart(3, "0");
  return `${time}.${ms}`;
}

// src/components/diagnostics/ConsoleDiagnosticsPanel.tsx
import { jsx as jsx97, jsxs as jsxs84 } from "react/jsx-runtime";
function getLevelColor(level) {
  switch (level) {
    case "error":
      return "text-destructive";
    case "warning":
      return "text-amber-500";
    case "info":
      return "text-blue-500";
    case "log":
    case "debug":
    default:
      return "text-muted-foreground";
  }
}
function isErrorOrWarning(level) {
  return level === "error" || level === "warning";
}
function formatTimestamp(ms) {
  const seconds = Math.floor(ms / 1e3);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}
function ConsoleLogItem({
  log,
  isActive,
  isPast,
  isExpanded,
  onToggle,
  videoStartedAt,
  displayTimeMs
}) {
  const absoluteTime = formatAbsoluteTimestamp(videoStartedAt, log.timestampMs);
  const relativeLabel = formatTimestamp(
    displayTimeMs ? displayTimeMs(log.timestampMs) : log.timestampMs
  );
  const isLongMessage = log.text.length > 100 || log.text.includes("\n");
  const truncatedText = isLongMessage && !isExpanded ? log.text.slice(0, 100).replace(/\n/g, " ") + "..." : log.text;
  return /* @__PURE__ */ jsxs84("div", { className: `border-b border-border last:border-b-0 ${isActive ? "bg-blue-100 dark:bg-blue-900/30" : ""}`, children: [
    /* @__PURE__ */ jsxs84(
      "button",
      {
        onClick: (e) => {
          e.stopPropagation();
          onToggle();
        },
        className: `w-full px-3 py-2 flex items-start gap-2 hover:bg-muted/50 transition-colors text-left ${isPast ? "opacity-60" : ""}`,
        children: [
          /* @__PURE__ */ jsx97("span", { className: "text-muted-foreground mt-0.5 shrink-0", children: isLongMessage ? isExpanded ? /* @__PURE__ */ jsx97(CaretDown7, { size: 12 }) : /* @__PURE__ */ jsx97(CaretRight14, { size: 12 }) : /* @__PURE__ */ jsx97("span", { className: "w-3 inline-block" }) }),
          /* @__PURE__ */ jsx97(
            Text,
            {
              size: "xs",
              className: `w-16 font-semibold shrink-0 ${getLevelColor(log.type)}`,
              children: log.type.toUpperCase()
            }
          ),
          absoluteTime ? /* @__PURE__ */ jsxs84(TooltipTrigger, { placement: "top", children: [
            /* @__PURE__ */ jsx97("span", { className: "text-muted-foreground font-mono text-xs shrink-0", children: relativeLabel }),
            /* @__PURE__ */ jsx97(Tooltip, { children: absoluteTime })
          ] }) : /* @__PURE__ */ jsx97(Text, { size: "xs", className: "text-muted-foreground font-mono shrink-0", children: relativeLabel }),
          /* @__PURE__ */ jsx97(
            Text,
            {
              size: "xs",
              className: `font-mono flex-1 min-w-0 ${isExpanded ? "whitespace-pre-wrap break-all" : "truncate"}`,
              children: truncatedText
            }
          )
        ]
      }
    ),
    isExpanded && /* @__PURE__ */ jsxs84("div", { className: "px-4 py-3 bg-muted/30 border-t border-border ml-4", children: [
      /* @__PURE__ */ jsx97("div", { className: "rounded-md bg-slate-950 p-3 overflow-auto max-h-48", children: /* @__PURE__ */ jsx97("pre", { className: "text-xs font-mono text-slate-100 whitespace-pre-wrap break-all", children: log.text }) }),
      log.location && /* @__PURE__ */ jsx97(Text, { size: "xs", className: "font-mono text-muted-foreground mt-2", children: log.location })
    ] })
  ] });
}
function ConsoleDiagnosticsPanel({
  logs,
  currentTimeMs,
  onSeek,
  isLoading,
  videoStartedAt,
  displayTimeMs
}) {
  const [filterMode, setFilterMode] = React65.useState("all");
  const [searchQuery, setSearchQuery] = React65.useState("");
  const [expandedRows, setExpandedRows] = React65.useState(/* @__PURE__ */ new Set());
  const filteredLogs = React65.useMemo(() => {
    return logs.filter((log) => {
      if (filterMode === "errors" && !isErrorOrWarning(log.type)) {
        return false;
      }
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        return log.text.toLowerCase().includes(query);
      }
      return true;
    });
  }, [logs, filterMode, searchQuery]);
  const errorWarningCount = logs.filter((l) => isErrorOrWarning(l.type)).length;
  const toggleRow = (index) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };
  return /* @__PURE__ */ jsxs84("div", { className: "flex flex-col h-full min-h-0 bg-card", children: [
    /* @__PURE__ */ jsx97("div", { className: "shrink-0 p-2", children: /* @__PURE__ */ jsxs84(FilterToolbar, { children: [
      /* @__PURE__ */ jsx97(
        ToolbarFilterMenu,
        {
          label: "Level",
          selectionMode: "single",
          options: [{ id: "errors", label: `Errors & warnings (${errorWarningCount})` }],
          selectedIds: filterMode === "errors" ? ["errors"] : [],
          onChange: (ids) => setFilterMode(ids.includes("errors") ? "errors" : "all")
        }
      ),
      /* @__PURE__ */ jsx97(FilterToolbarDivider, {}),
      /* @__PURE__ */ jsx97(
        FilterToolbarSearch,
        {
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: "Search logs...",
          "aria-label": "Search console logs",
          className: "flex-1"
        }
      )
    ] }) }),
    /* @__PURE__ */ jsx97(
      TimelineSyncedList,
      {
        items: filteredLogs,
        currentTimeMs,
        getTimestamp: (log) => log.timestampMs,
        getKey: (log, index) => `${log.timestampMs}-${index}`,
        onSeek,
        isLoading,
        emptyState: searchQuery ? "No matching logs found." : "No console logs captured.",
        renderItem: (log, isActive, isPast) => {
          const originalIndex = logs.indexOf(log);
          return /* @__PURE__ */ jsx97(
            ConsoleLogItem,
            {
              log,
              isActive,
              isPast,
              isExpanded: expandedRows.has(originalIndex),
              onToggle: () => toggleRow(originalIndex),
              videoStartedAt,
              displayTimeMs
            }
          );
        },
        className: "flex-1"
      }
    )
  ] });
}

// src/components/diagnostics/NetworkDiagnosticsPanel.tsx
import React66 from "react";
import { CaretDown as CaretDown8, CaretRight as CaretRight15, Clock as Clock4, Copy as Copy2, CheckCircle as CheckCircle3 } from "@phosphor-icons/react";
import { jsx as jsx98, jsxs as jsxs85 } from "react/jsx-runtime";
function getStatusBadgeVariant2(status) {
  if (status === null) return "secondary";
  if (status >= 200 && status < 300) return "success";
  if (status >= 300 && status < 400) return "warning";
  return "destructive";
}
function isErrorStatus(status) {
  if (status === null) return true;
  return status >= 400;
}
function formatTimestamp2(ms) {
  const seconds = Math.floor(ms / 1e3);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}
function NetworkRequestItem({
  request,
  isActive,
  isPast,
  isExpanded,
  onToggle,
  videoStartedAt,
  displayTimeMs
}) {
  const [copied, setCopied] = React66.useState(false);
  const absoluteTime = formatAbsoluteTimestamp(videoStartedAt, request.timestampMs);
  const relativeLabel = formatTimestamp2(
    displayTimeMs ? displayTimeMs(request.timestampMs) : request.timestampMs
  );
  const urlObj = React66.useMemo(() => {
    try {
      return new URL(request.url);
    } catch {
      return null;
    }
  }, [request.url]);
  const displayUrl = urlObj ? `${urlObj.pathname}${urlObj.search}` : request.url;
  const hostname = urlObj?.hostname ?? "";
  const handleCopyUrl = async (e) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(request.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2e3);
  };
  return /* @__PURE__ */ jsxs85("div", { className: `border-b border-border last:border-b-0 ${isActive ? "bg-blue-100 dark:bg-blue-900/30" : ""}`, children: [
    /* @__PURE__ */ jsxs85(
      "button",
      {
        onClick: (e) => {
          e.stopPropagation();
          onToggle();
        },
        className: `w-full px-3 py-2 flex items-start gap-2 hover:bg-muted/50 transition-colors text-left ${isPast ? "opacity-60" : ""}`,
        children: [
          /* @__PURE__ */ jsx98("span", { className: "text-muted-foreground mt-0.5 shrink-0", children: isExpanded ? /* @__PURE__ */ jsx98(CaretDown8, { size: 12 }) : /* @__PURE__ */ jsx98(CaretRight15, { size: 12 }) }),
          /* @__PURE__ */ jsx98(
            Badge,
            {
              variant: request.method === "GET" ? "secondary" : "outline",
              size: "sm",
              className: "w-12 justify-center font-mono text-[10px] shrink-0",
              children: request.method
            }
          ),
          /* @__PURE__ */ jsx98(
            Badge,
            {
              variant: getStatusBadgeVariant2(request.status),
              size: "sm",
              className: "w-10 justify-center shrink-0",
              children: request.status ?? "\u2014"
            }
          ),
          absoluteTime ? /* @__PURE__ */ jsxs85(TooltipTrigger, { placement: "top", children: [
            /* @__PURE__ */ jsx98("span", { className: "text-muted-foreground font-mono text-xs shrink-0", children: relativeLabel }),
            /* @__PURE__ */ jsx98(Tooltip, { children: absoluteTime })
          ] }) : /* @__PURE__ */ jsx98(Text, { size: "xs", className: "text-muted-foreground font-mono shrink-0", children: relativeLabel }),
          /* @__PURE__ */ jsxs85("div", { className: "flex-1 min-w-0", children: [
            /* @__PURE__ */ jsx98(Text, { size: "xs", className: "font-mono truncate", children: displayUrl }),
            hostname && /* @__PURE__ */ jsx98(Text, { size: "xs", className: "text-muted-foreground truncate", children: hostname })
          ] }),
          request.durationMs !== null && /* @__PURE__ */ jsxs85(Text, { size: "xs", className: "text-muted-foreground shrink-0", children: [
            request.durationMs,
            "ms"
          ] })
        ]
      }
    ),
    isExpanded && /* @__PURE__ */ jsxs85("div", { className: "px-4 py-3 bg-muted/30 border-t border-border ml-4 space-y-3", children: [
      /* @__PURE__ */ jsxs85("div", { className: "flex items-start gap-2 group", children: [
        /* @__PURE__ */ jsx98(Text, { size: "xs", className: "font-mono text-primary break-all flex-1", children: request.url }),
        /* @__PURE__ */ jsx98(
          "button",
          {
            onClick: handleCopyUrl,
            className: "shrink-0 text-muted-foreground hover:text-foreground transition-colors",
            title: "Copy URL",
            children: copied ? /* @__PURE__ */ jsx98(CheckCircle3, { size: 14, className: "text-emerald-500" }) : /* @__PURE__ */ jsx98(Copy2, { size: 14 })
          }
        )
      ] }),
      /* @__PURE__ */ jsxs85("div", { className: "flex items-center gap-3 flex-wrap", children: [
        /* @__PURE__ */ jsx98(Badge, { variant: getStatusBadgeVariant2(request.status), size: "sm", children: request.status ?? "Pending" }),
        request.durationMs !== null && /* @__PURE__ */ jsxs85("div", { className: "flex items-center gap-1 text-muted-foreground", children: [
          /* @__PURE__ */ jsx98(Clock4, { size: 12 }),
          /* @__PURE__ */ jsxs85(Text, { size: "xs", children: [
            request.durationMs,
            "ms"
          ] })
        ] }),
        request.contentType && /* @__PURE__ */ jsx98(Text, { size: "xs", className: "text-muted-foreground font-mono", children: request.contentType })
      ] }),
      request.requestBody && /* @__PURE__ */ jsxs85("div", { children: [
        /* @__PURE__ */ jsx98(Text, { size: "xs", className: "text-muted-foreground uppercase tracking-wide font-medium mb-1", children: "Request Body" }),
        /* @__PURE__ */ jsx98("div", { className: "rounded-md bg-slate-950 p-2 overflow-auto max-h-32", children: /* @__PURE__ */ jsx98("pre", { className: "text-xs font-mono text-slate-100 whitespace-pre-wrap break-all", children: formatBody(request.requestBody) }) })
      ] }),
      request.responseBody && /* @__PURE__ */ jsxs85("div", { children: [
        /* @__PURE__ */ jsx98(Text, { size: "xs", className: "text-muted-foreground uppercase tracking-wide font-medium mb-1", children: "Response Body" }),
        /* @__PURE__ */ jsx98("div", { className: "rounded-md bg-slate-950 p-2 overflow-auto max-h-32", children: /* @__PURE__ */ jsx98("pre", { className: "text-xs font-mono text-slate-100 whitespace-pre-wrap break-all", children: formatBody(request.responseBody) }) })
      ] })
    ] })
  ] });
}
function formatBody(body) {
  try {
    const parsed = JSON.parse(body);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return body;
  }
}
function NetworkDiagnosticsPanel({
  requests,
  currentTimeMs,
  onSeek,
  isLoading,
  videoStartedAt,
  displayTimeMs
}) {
  const [filterMode, setFilterMode] = React66.useState("all");
  const [searchQuery, setSearchQuery] = React66.useState("");
  const [expandedRows, setExpandedRows] = React66.useState(/* @__PURE__ */ new Set());
  const filteredRequests = React66.useMemo(() => {
    return requests.filter((req) => {
      if (filterMode === "errors" && !isErrorStatus(req.status)) {
        return false;
      }
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        return req.url.toLowerCase().includes(query);
      }
      return true;
    });
  }, [requests, filterMode, searchQuery]);
  const errorCount = requests.filter((r) => isErrorStatus(r.status)).length;
  const toggleRow = (index) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };
  return /* @__PURE__ */ jsxs85("div", { className: "flex flex-col h-full min-h-0 bg-card", children: [
    /* @__PURE__ */ jsx98("div", { className: "shrink-0 p-2", children: /* @__PURE__ */ jsxs85(FilterToolbar, { children: [
      /* @__PURE__ */ jsx98(
        ToolbarFilterMenu,
        {
          label: "Severity",
          selectionMode: "single",
          options: [{ id: "errors", label: `Errors only (${errorCount})` }],
          selectedIds: filterMode === "errors" ? ["errors"] : [],
          onChange: (ids) => setFilterMode(ids.includes("errors") ? "errors" : "all")
        }
      ),
      /* @__PURE__ */ jsx98(FilterToolbarDivider, {}),
      /* @__PURE__ */ jsx98(
        FilterToolbarSearch,
        {
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: "Search URLs...",
          "aria-label": "Search network requests",
          className: "flex-1"
        }
      )
    ] }) }),
    /* @__PURE__ */ jsx98(
      TimelineSyncedList,
      {
        items: filteredRequests,
        currentTimeMs,
        getTimestamp: (req) => req.timestampMs,
        getKey: (req, index) => `${req.timestampMs}-${req.url}-${index}`,
        onSeek,
        isLoading,
        emptyState: searchQuery ? "No matching requests found." : "No network requests captured.",
        renderItem: (req, isActive, isPast) => {
          const originalIndex = requests.indexOf(req);
          return /* @__PURE__ */ jsx98(
            NetworkRequestItem,
            {
              request: req,
              isActive,
              isPast,
              isExpanded: expandedRows.has(originalIndex),
              onToggle: () => toggleRow(originalIndex),
              videoStartedAt,
              displayTimeMs
            }
          );
        },
        className: "flex-1"
      }
    )
  ] });
}

// src/components/diagnostics/thoughtContent.ts
function parseThoughtContent(content) {
  const match = content.match(/^\*\*([^*]+)\*\*\s*/);
  if (match) {
    return {
      header: match[1].trim(),
      body: content.slice(match[0].length).trim()
    };
  }
  return { header: null, body: content };
}

// src/components/VideoPlayerWithTimeline.tsx
import { Fragment as Fragment20, jsx as jsx99, jsxs as jsxs86 } from "react/jsx-runtime";
var formatAction = (entry) => {
  if (entry.isAnnotation) return entry.action;
  const label = getTimelineActionLabel(entry.action);
  if (entry.target) {
    const truncatedTarget = entry.target.length > 40 ? entry.target.slice(0, 40) + "..." : entry.target;
    return `${label}: ${truncatedTarget}`;
  }
  return label;
};
var getActionDetail = (entry) => {
  if (entry.isAnnotation) return null;
  const action = entry.action;
  switch (action) {
    case "press-key":
      return entry.key ?? entry.value ?? null;
    case "type":
      return entry.value ?? null;
    case "navigate":
      return entry.url ?? entry.value ?? null;
    case "select":
      return entry.value ?? null;
    case "click":
    case "hover": {
      const parts = [];
      if (entry.clickType === "ref" && entry.ref) {
        parts.push(`ref: ${entry.ref}`);
      } else if (entry.clickType === "coordinates" && entry.coordinates) {
        parts.push(`coords: (${entry.coordinates.x}, ${entry.coordinates.y})`);
      } else if (entry.coordinates) {
        parts.push(`(${entry.coordinates.x}, ${entry.coordinates.y})`);
      } else if (entry.ref) {
        parts.push(`ref: ${entry.ref}`);
      }
      if (entry.clickedElement) {
        const el = entry.clickedElement;
        const desc = [el.tagName.toLowerCase()];
        if (el.id) desc.push(`#${el.id}`);
        if (el.role) desc.push(`[${el.role}]`);
        parts.push(`\u2192 ${desc.join("")}`);
      }
      return parts.length > 0 ? parts.join(" ") : null;
    }
    case "drag":
      if (entry.coordinates) {
        return `from (${entry.coordinates.x}, ${entry.coordinates.y})`;
      }
      return null;
    default:
      return entry.value ?? null;
  }
};
var urlAtTimelinePosition = (timeline, currentTimeMs) => {
  if (!timeline) return null;
  let earliestUrl = null;
  let earliestTimestampMs = Number.POSITIVE_INFINITY;
  let urlAtPlayhead = null;
  let playheadTimestampMs = Number.NEGATIVE_INFINITY;
  const consider = (url, timestampMs) => {
    if (!url) return;
    if (timestampMs < earliestTimestampMs) {
      earliestTimestampMs = timestampMs;
      earliestUrl = url;
    }
    if (timestampMs <= currentTimeMs && timestampMs >= playheadTimestampMs) {
      playheadTimestampMs = timestampMs;
      urlAtPlayhead = url;
    }
  };
  for (const entry of timeline.entries) consider(entry.url, entry.timestampMs);
  for (const event of timeline.navigationEvents ?? []) {
    consider(event.url, event.timestampMs);
  }
  return urlAtPlayhead ?? earliestUrl;
};
var formatTimestamp3 = (ms) => {
  const seconds = Math.floor(ms / 1e3);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};
var getEventColors = (entry) => {
  if (entry.isAnnotation && entry.annotationColor) {
    return {
      dot: "",
      // Will use inline style
      bg: "bg-muted/50",
      text: "text-foreground",
      border: "border-border",
      customColor: entry.annotationColor
    };
  }
  if (entry.success) {
    return {
      dot: "bg-blue-500",
      bg: "bg-blue-100 dark:bg-blue-900/30",
      text: "text-blue-700 dark:text-blue-300",
      border: "border-blue-200 dark:border-blue-800"
    };
  }
  return {
    dot: "bg-red-500",
    bg: "bg-red-100 dark:bg-red-900/30",
    text: "text-red-700 dark:text-red-300",
    border: "border-red-200 dark:border-red-800"
  };
};
var EventTooltip = ({ entry, visible }) => {
  if (!visible) return null;
  const colors = getEventColors(entry);
  const displayLabel = entry.isAnnotation ? entry.action : getTimelineActionLabel(entry.action);
  const detail = getActionDetail(entry);
  const hasCoordinates = entry.coordinates && (entry.action === "click" || entry.action === "hover" || entry.action === "drag");
  const hasClickedElement = entry.clickedElement && (entry.action === "click" || entry.action === "hover");
  return /* @__PURE__ */ jsx99("div", { className: "absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 pointer-events-none", children: /* @__PURE__ */ jsxs86("div", { className: "bg-popover text-popover-foreground border border-border rounded-md shadow-lg px-3 py-2 text-xs max-w-[320px]", children: [
    /* @__PURE__ */ jsxs86("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx99(
        "span",
        {
          className: `w-2 h-2 rounded-full shrink-0 ${colors.dot}`,
          style: colors.customColor ? { backgroundColor: colors.customColor } : void 0
        }
      ),
      /* @__PURE__ */ jsx99("span", { className: "font-medium", children: displayLabel }),
      detail && (entry.action === "press-key" || entry.action === "type") && /* @__PURE__ */ jsxs86("span", { className: "font-mono text-muted-foreground truncate max-w-[120px]", children: [
        '"',
        detail,
        '"'
      ] }),
      /* @__PURE__ */ jsx99("span", { className: "text-muted-foreground ml-auto shrink-0", children: formatTimestamp3(entry.timestampMs) })
    ] }),
    !entry.isAnnotation && entry.target && /* @__PURE__ */ jsx99("div", { className: "mt-1.5 text-muted-foreground truncate", children: entry.target }),
    entry.action === "navigate" && detail && /* @__PURE__ */ jsx99("div", { className: "mt-1.5 text-muted-foreground font-mono text-[10px] truncate", children: detail }),
    hasCoordinates && /* @__PURE__ */ jsxs86("div", { className: "mt-2 flex items-center gap-2 flex-wrap", children: [
      /* @__PURE__ */ jsxs86("span", { className: "font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded text-foreground", children: [
        "(",
        entry.coordinates.x,
        ", ",
        entry.coordinates.y,
        ")"
      ] }),
      entry.clickType && /* @__PURE__ */ jsxs86("span", { className: "text-[10px] text-muted-foreground", children: [
        "via ",
        entry.clickType === "ref" ? "locator" : "coordinates"
      ] }),
      entry.ref && entry.clickType === "ref" && /* @__PURE__ */ jsx99("span", { className: "text-[10px] font-mono text-blue-600 dark:text-blue-400", children: entry.ref })
    ] }),
    hasClickedElement && /* @__PURE__ */ jsxs86("div", { className: "mt-2 p-1.5 bg-muted/50 rounded text-[10px] font-mono space-y-0.5", children: [
      /* @__PURE__ */ jsxs86("div", { className: "flex items-center gap-1 flex-wrap", children: [
        /* @__PURE__ */ jsxs86("span", { className: "text-blue-600 dark:text-blue-400", children: [
          "<",
          entry.clickedElement.tagName.toLowerCase(),
          ">"
        ] }),
        entry.clickedElement.id && /* @__PURE__ */ jsxs86("span", { className: "text-green-600 dark:text-green-400", children: [
          "#",
          entry.clickedElement.id
        ] }),
        entry.clickedElement.className && /* @__PURE__ */ jsxs86("span", { className: "text-amber-600 dark:text-amber-400 truncate max-w-[100px]", children: [
          ".",
          entry.clickedElement.className.split(" ")[0]
        ] })
      ] }),
      entry.clickedElement.role && /* @__PURE__ */ jsxs86("div", { className: "text-purple-600 dark:text-purple-400", children: [
        'role="',
        entry.clickedElement.role,
        '"'
      ] }),
      entry.clickedElement.ariaLabel && /* @__PURE__ */ jsxs86("div", { className: "text-muted-foreground truncate", children: [
        'aria-label="',
        entry.clickedElement.ariaLabel,
        '"'
      ] }),
      entry.clickedElement.textContent && /* @__PURE__ */ jsxs86("div", { className: "text-muted-foreground truncate", children: [
        '"',
        entry.clickedElement.textContent,
        '"'
      ] })
    ] }),
    entry.error && /* @__PURE__ */ jsx99("div", { className: "mt-2 text-red-600 dark:text-red-400 truncate", children: entry.error })
  ] }) });
};
var EventMarker = ({ entry, position, isActive, onSeek, onAnnotationClick }) => {
  const [showTooltip, setShowTooltip] = React67.useState(false);
  const colors = getEventColors(entry);
  return /* @__PURE__ */ jsxs86(
    "div",
    {
      className: "absolute top-1/2 -translate-x-1/2 flex items-center justify-center",
      style: { left: `${position}%`, transform: "translate(-50%, -50%)" },
      children: [
        /* @__PURE__ */ jsx99(
          "button",
          {
            type: "button",
            className: `rounded-full border-2 transition-all hover:scale-125 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 ${isActive ? "w-3.5 h-3.5 scale-110" : "w-2.5 h-2.5"} ${colors.dot} ${colors.border}`,
            style: colors.customColor ? { backgroundColor: colors.customColor, borderColor: colors.customColor } : void 0,
            onClick: (e) => {
              e.stopPropagation();
              onSeek(entry.timestampMs);
              if (entry.isAnnotation && onAnnotationClick) {
                onAnnotationClick(entry);
              }
            },
            onMouseEnter: () => setShowTooltip(true),
            onMouseLeave: () => setShowTooltip(false),
            "aria-label": formatAction(entry)
          }
        ),
        /* @__PURE__ */ jsx99(EventTooltip, { entry, visible: showTooltip })
      ]
    }
  );
};
var THOUGHT_COLORS = {
  dot: "bg-muted-foreground/70",
  bg: "bg-muted/50",
  text: "text-foreground",
  border: "border-border"
};
var ThoughtTooltip = ({ thought, visible }) => {
  if (!visible) return null;
  const { header, body } = parseThoughtContent(thought.content);
  const truncatedBody = body.length > 150 ? body.slice(0, 150) + "..." : body;
  return /* @__PURE__ */ jsx99("div", { className: "absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 pointer-events-none", children: /* @__PURE__ */ jsxs86("div", { className: "bg-popover text-popover-foreground border border-border rounded-md shadow-lg px-3 py-2 text-xs max-w-[280px]", children: [
    /* @__PURE__ */ jsxs86("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx99(Brain2, { size: 12, weight: "fill", className: "text-muted-foreground shrink-0" }),
      /* @__PURE__ */ jsx99("span", { className: "font-medium text-foreground", children: header ?? "Agent Thought" }),
      /* @__PURE__ */ jsx99("span", { className: "text-muted-foreground ml-auto", children: formatTimestamp3(thought.timestampMs) })
    ] }),
    thought.toolName && /* @__PURE__ */ jsx99("div", { className: "mt-1", children: /* @__PURE__ */ jsx99("span", { className: "text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded font-mono", children: thought.toolName }) }),
    truncatedBody && /* @__PURE__ */ jsx99("div", { className: "mt-1.5 text-foreground/80 leading-relaxed whitespace-pre-wrap", children: truncatedBody })
  ] }) });
};
var ThoughtMarker = ({ thought, position, isActive, onSeek }) => {
  const [showTooltip, setShowTooltip] = React67.useState(false);
  return /* @__PURE__ */ jsxs86(
    "div",
    {
      className: "absolute top-1/2 -translate-x-1/2 flex items-center justify-center",
      style: { left: `${position}%`, transform: "translate(-50%, -50%)" },
      children: [
        /* @__PURE__ */ jsx99(
          "button",
          {
            type: "button",
            className: `rounded-full border-2 transition-all hover:scale-125 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 ${isActive ? "w-3.5 h-3.5 scale-110" : "w-2.5 h-2.5"} ${THOUGHT_COLORS.dot} ${THOUGHT_COLORS.border}`,
            onClick: (e) => {
              e.stopPropagation();
              onSeek(thought.timestampMs);
            },
            onMouseEnter: () => setShowTooltip(true),
            onMouseLeave: () => setShowTooltip(false),
            "aria-label": `Agent thought at ${formatTimestamp3(thought.timestampMs)}`
          }
        ),
        /* @__PURE__ */ jsx99(ThoughtTooltip, { thought, visible: showTooltip })
      ]
    }
  );
};
var ThoughtsToggle = ({ showThoughts, onToggle, thoughtCount }) => {
  return /* @__PURE__ */ jsxs86(TooltipTrigger, { delay: 200, children: [
    /* @__PURE__ */ jsxs86(
      "button",
      {
        type: "button",
        onClick: onToggle,
        className: `flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors text-xs ${showThoughts ? "bg-muted text-foreground" : "bg-muted/50 text-muted-foreground hover:bg-muted/80"}`,
        "aria-label": showThoughts ? "Hide debug rows (agent thoughts and evaluate calls)" : "Show debug rows (agent thoughts and evaluate calls)",
        children: [
          /* @__PURE__ */ jsx99(Brain2, { size: 14, weight: showThoughts ? "fill" : "regular" }),
          /* @__PURE__ */ jsx99("span", { className: "font-medium", children: "Debug" }),
          thoughtCount > 0 && /* @__PURE__ */ jsxs86("span", { className: "font-medium", children: [
            "\xB7 ",
            thoughtCount
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsxs86(Tooltip, { children: [
      showThoughts ? "Hide" : "Show",
      " debug (agent thoughts + evaluates)"
    ] })
  ] });
};
function renderThoughtItem(args) {
  const { thought, isActive, onSeek } = args;
  const { header, body } = parseThoughtContent(thought.content);
  return /* @__PURE__ */ jsx99(
    Timeline2.Item,
    {
      timestamp: thought.timestampMs,
      active: isActive,
      status: "pending",
      icon: /* @__PURE__ */ jsx99(Brain2, { size: 14, weight: "fill", className: "text-muted-foreground" }),
      title: header ?? "Agent Thought",
      description: body || void 0,
      onClick: () => onSeek(thought.timestampMs),
      trailing: /* @__PURE__ */ jsx99(Timeline2.Timestamp, { value: thought.timestampMs })
    }
  );
}
var ChapterBand = ({ chapter, duration, onSeek }) => {
  const [showTooltip, setShowTooltip] = React67.useState(false);
  const endMs = chapter.endMs ?? duration;
  const left = Math.max(0, Math.min(100, chapter.startMs / duration * 100));
  const right = Math.max(left, Math.min(100, endMs / duration * 100));
  const width = Math.max(right - left, 0.5);
  return /* @__PURE__ */ jsxs86(
    "div",
    {
      className: "absolute top-0 h-full",
      style: { left: `${left}%`, width: `${width}%` },
      onMouseEnter: () => setShowTooltip(true),
      onMouseLeave: () => setShowTooltip(false),
      children: [
        /* @__PURE__ */ jsx99(
          "button",
          {
            type: "button",
            className: "w-full h-full rounded-sm opacity-40 hover:opacity-75 transition-opacity focus:outline-none focus:ring-1 focus:ring-ring",
            style: { backgroundColor: chapter.color ?? "#6366f1" },
            onClick: (e) => {
              e.stopPropagation();
              onSeek(chapter.startMs);
            },
            "aria-label": `${chapter.name} \u2014 jump to ${formatTimestamp3(chapter.startMs)}`
          }
        ),
        showTooltip && /* @__PURE__ */ jsx99("div", { className: "absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 pointer-events-none", children: /* @__PURE__ */ jsxs86("div", { className: "bg-popover text-popover-foreground border border-border rounded-md shadow-lg px-3 py-1.5 text-xs whitespace-nowrap max-w-[280px] truncate", children: [
          /* @__PURE__ */ jsx99("span", { className: "font-medium", children: chapter.name }),
          /* @__PURE__ */ jsxs86("span", { className: "text-muted-foreground", children: [
            " \xB7 ",
            formatTimestamp3(endMs - chapter.startMs),
            chapter.endMs === null ? "\u2026" : ""
          ] })
        ] }) })
      ]
    }
  );
};
var Timeline3 = ({
  currentTimeMs,
  duration,
  timeline,
  thoughts,
  showThoughts = true,
  currentAction,
  currentThought,
  onSeek,
  onAnnotationClick,
  showChapters = false
}) => {
  const trackRef = React67.useRef(null);
  const [isDragging, setIsDragging] = React67.useState(false);
  const calculateTimeFromPosition = React67.useCallback(
    (clientX) => {
      if (!trackRef.current || duration <= 0) return 0;
      const rect = trackRef.current.getBoundingClientRect();
      const position = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      return position * duration;
    },
    [duration]
  );
  const handleMouseDown = React67.useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(true);
      onSeek(calculateTimeFromPosition(e.clientX));
    },
    [calculateTimeFromPosition, onSeek]
  );
  React67.useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => {
      onSeek(calculateTimeFromPosition(e.clientX));
    };
    const handleMouseUp = () => {
      setIsDragging(false);
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, calculateTimeFromPosition, onSeek]);
  const progress = duration > 0 ? currentTimeMs / duration * 100 : 0;
  const hasTimeline = Boolean(timeline && timeline.entries.length > 0);
  const markerEntries = React67.useMemo(
    () => hasTimeline ? filterTimelineEntries(timeline.entries, { hideEvaluates: !showThoughts }) : [],
    [hasTimeline, timeline, showThoughts]
  );
  const chapters = React67.useMemo(
    () => showChapters && timeline ? extractTodoChapters(timeline.entries) : [],
    [showChapters, timeline]
  );
  const scenarioChapters = React67.useMemo(
    () => showChapters && timeline ? extractScenarioChapters(timeline.entries) : [],
    [showChapters, timeline]
  );
  return /* @__PURE__ */ jsxs86("div", { className: "flex flex-col gap-1", children: [
    scenarioChapters.length > 0 && duration > 0 && /* @__PURE__ */ jsx99("div", { className: "relative h-2", "aria-label": "Scenarios", children: scenarioChapters.map((chapter) => /* @__PURE__ */ jsx99(
      ChapterBand,
      {
        chapter,
        duration,
        onSeek
      },
      `${chapter.nodeId}-${chapter.startMs}`
    )) }),
    chapters.length > 0 && duration > 0 && /* @__PURE__ */ jsx99("div", { className: "relative h-1.5", "aria-label": "Checklist chapters", children: chapters.map((chapter) => /* @__PURE__ */ jsx99(
      ChapterBand,
      {
        chapter,
        duration,
        onSeek
      },
      `${chapter.nodeId}-${chapter.startMs}`
    )) }),
    /* @__PURE__ */ jsxs86(
      "div",
      {
        ref: trackRef,
        className: "relative h-1.5 bg-muted rounded-full cursor-pointer group",
        onMouseDown: handleMouseDown,
        children: [
          /* @__PURE__ */ jsx99(
            "div",
            {
              className: "absolute top-0 left-0 h-full bg-primary rounded-full transition-all",
              style: { width: `${progress}%` }
            }
          ),
          /* @__PURE__ */ jsx99("div", { className: "absolute inset-0 h-1.5 group-hover:h-2.5 group-hover:-top-0.5 transition-all rounded-full" }),
          /* @__PURE__ */ jsx99(
            "div",
            {
              className: "absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 bg-primary rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity",
              style: { left: `${progress}%` }
            }
          ),
          duration > 0 && markerEntries.map((entry, idx) => {
            const position = entry.timestampMs / duration * 100;
            return /* @__PURE__ */ jsx99(
              EventMarker,
              {
                entry,
                position,
                isActive: currentAction === entry,
                onSeek,
                onAnnotationClick
              },
              idx
            );
          }),
          showThoughts && thoughts && duration > 0 && thoughts.map((thought) => {
            const position = thought.timestampMs / duration * 100;
            if (position < 0 || position > 100) return null;
            return /* @__PURE__ */ jsx99(
              ThoughtMarker,
              {
                thought,
                position,
                isActive: currentThought?.id === thought.id,
                onSeek
              },
              thought.id
            );
          })
        ]
      }
    )
  ] });
};
var BrowserAddressBar = ({
  url,
  size = "sm"
}) => {
  const [copied, setCopied] = React67.useState(false);
  const copyTimer = React67.useRef(null);
  React67.useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    },
    []
  );
  const parsed = React67.useMemo(() => {
    if (!url) return null;
    try {
      const u = new URL(url);
      return { secure: u.protocol === "https:", display: `${u.host}${u.pathname}${u.search}`, full: url };
    } catch {
      return { secure: false, display: url, full: url };
    }
  }, [url]);
  const handleCopy = (e) => {
    e.stopPropagation();
    if (!parsed) return;
    const clipboard = navigator.clipboard;
    if (!clipboard) return;
    void clipboard.writeText(parsed.full).then(() => {
      setCopied(true);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 1500);
    }).catch((error) => {
      if (isClipboardWritePermissionDenied(error)) return;
      throw error;
    });
  };
  const iconSize = size === "md" ? 16 : 14;
  const textCls = size === "md" ? "text-xs" : "text-[11px]";
  const padCls = size === "md" ? "px-3 py-1.5" : "px-2.5 py-1";
  const LeadingIcon = parsed?.secure ? Lock : Globe;
  const btnCls = "rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  return /* @__PURE__ */ jsxs86("div", { className: `flex items-center gap-2 border-b border-border bg-muted/40 ${padCls}`, children: [
    /* @__PURE__ */ jsx99(LeadingIcon, { size: iconSize, className: "shrink-0 text-muted-foreground" }),
    /* @__PURE__ */ jsx99(
      "span",
      {
        className: `min-w-0 flex-1 truncate font-mono ${textCls} ${parsed ? "text-foreground/80" : "italic text-muted-foreground"}`,
        title: parsed?.full,
        children: parsed ? parsed.display : "Waiting for first navigation\u2026"
      }
    ),
    parsed && /* @__PURE__ */ jsxs86("div", { className: "flex shrink-0 items-center gap-0.5", children: [
      /* @__PURE__ */ jsxs86(TooltipTrigger, { delay: 0, offset: 6, placement: "top", children: [
        /* @__PURE__ */ jsx99("button", { type: "button", onClick: handleCopy, "aria-label": "Copy URL", className: btnCls, children: copied ? /* @__PURE__ */ jsx99(Check5, { size: iconSize, weight: "bold" }) : /* @__PURE__ */ jsx99(Copy3, { size: iconSize }) }),
        /* @__PURE__ */ jsx99(Tooltip, { variant: "light", className: "px-2 py-1 text-xs", children: copied ? "Copied" : "Copy URL" })
      ] }),
      /* @__PURE__ */ jsxs86(TooltipTrigger, { delay: 0, offset: 6, placement: "top", children: [
        /* @__PURE__ */ jsx99(
          "a",
          {
            href: parsed.full,
            target: "_blank",
            rel: "noopener noreferrer",
            onClick: (e) => e.stopPropagation(),
            "aria-label": "Open URL in new tab",
            className: btnCls,
            children: /* @__PURE__ */ jsx99(ArrowSquareOut, { size: iconSize })
          }
        ),
        /* @__PURE__ */ jsx99(Tooltip, { variant: "light", className: "px-2 py-1 text-xs", children: "Open in new tab" })
      ] })
    ] })
  ] });
};
function isClipboardWritePermissionDenied(error) {
  if (typeof error !== "object" || error === null || !("name" in error)) return false;
  return error.name === "NotAllowedError";
}
var TIMELINE_MODE_OPTIONS = [
  { value: "events", label: "Events" },
  { value: "diagnostic", label: "Diagnostic" }
];
var VIDEO_VARIANT_OPTIONS = [
  { value: "full", label: "Full" },
  { value: "fast", label: "Fast" }
];
var ControlsBar = ({
  isPlaying,
  currentTimeMs,
  duration,
  timeline,
  thoughts,
  showThoughts = true,
  onThoughtsToggle,
  currentAction,
  currentThought,
  onPlayPause,
  onSeek,
  onAnnotationClick,
  showChapters = false,
  onFullscreen,
  showFullscreenButton = true,
  videoRef,
  showCaptureButton = false,
  diagnosticTimeline,
  timelineMode,
  onTimelineModeChange,
  hasFastVariant,
  videoVariant,
  onVideoVariantChange
}) => {
  const activeMode = diagnosticTimeline && timelineMode === "diagnostic" ? "diagnostic" : "events";
  const scrubberTimeline = activeMode === "diagnostic" ? diagnosticTimeline : timeline;
  const [playbackRate, setPlaybackRate] = React67.useState(1);
  const handleCycleSpeed = React67.useCallback(() => {
    const next = playbackRate === 1 ? 1.5 : playbackRate === 1.5 ? 2 : playbackRate === 2 ? 3 : 1;
    setPlaybackRate(next);
    if (videoRef?.current) {
      videoRef.current.playbackRate = next;
    }
  }, [playbackRate, videoRef]);
  React67.useEffect(() => {
    if (videoRef?.current) {
      videoRef.current.playbackRate = playbackRate;
    }
  }, [videoRef, playbackRate]);
  const handleCaptureFrame = React67.useCallback(() => {
    if (!videoRef?.current) return;
    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    try {
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `frame-${formatTimestamp3(currentTimeMs).replace(":", "m")}s.png`;
        a.click();
        URL.revokeObjectURL(url);
      }, "image/png");
    } catch {
      console.warn("Cannot capture frame: video is cross-origin without CORS headers");
    }
  }, [videoRef, currentTimeMs]);
  return /* @__PURE__ */ jsxs86("div", { className: "flex flex-col gap-2 px-3 py-2 bg-card border-t border-border", children: [
    /* @__PURE__ */ jsx99(
      Timeline3,
      {
        currentTimeMs,
        duration,
        timeline: scrubberTimeline,
        thoughts: activeMode === "diagnostic" ? void 0 : thoughts,
        showThoughts: activeMode === "diagnostic" ? false : showThoughts,
        currentAction,
        currentThought,
        onSeek,
        onAnnotationClick,
        showChapters
      }
    ),
    /* @__PURE__ */ jsxs86("div", { className: "flex items-center gap-3", children: [
      /* @__PURE__ */ jsx99(
        "button",
        {
          type: "button",
          onClick: onPlayPause,
          className: "flex items-center justify-center w-8 h-8 rounded-full hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-ring",
          "aria-label": isPlaying ? "Pause" : "Play",
          children: isPlaying ? /* @__PURE__ */ jsx99(Pause, { size: 18, weight: "fill", className: "text-foreground" }) : /* @__PURE__ */ jsx99(Play4, { size: 18, weight: "fill", className: "text-foreground" })
        }
      ),
      /* @__PURE__ */ jsxs86("span", { className: "text-xs text-muted-foreground font-mono min-w-[70px]", children: [
        formatTimestamp3(currentTimeMs),
        " / ",
        formatTimestamp3(duration)
      ] }),
      showCaptureButton && /* @__PURE__ */ jsxs86(TooltipTrigger, { delay: 200, placement: "top", children: [
        /* @__PURE__ */ jsx99(
          "button",
          {
            type: "button",
            onClick: handleCaptureFrame,
            className: `flex items-center justify-center w-8 h-8 rounded-full hover:bg-muted transition-all focus:outline-none focus:ring-2 focus:ring-ring ${isPlaying ? "opacity-0 pointer-events-none w-0" : "opacity-100"}`,
            "aria-label": "Save frame as PNG",
            "aria-hidden": isPlaying,
            tabIndex: isPlaying ? -1 : 0,
            children: /* @__PURE__ */ jsx99(Camera2, { size: 18, className: "text-foreground" })
          }
        ),
        /* @__PURE__ */ jsx99(Tooltip, { children: "Save frame as PNG" })
      ] }),
      /* @__PURE__ */ jsx99("div", { className: "flex-1" }),
      hasFastVariant && onVideoVariantChange && /* @__PURE__ */ jsx99(
        PillToggle,
        {
          "aria-label": "Video variant",
          value: videoVariant ?? "full",
          onChange: onVideoVariantChange,
          options: VIDEO_VARIANT_OPTIONS
        }
      ),
      diagnosticTimeline && onTimelineModeChange && /* @__PURE__ */ jsx99(
        PillToggle,
        {
          "aria-label": "Timeline view",
          value: activeMode,
          onChange: onTimelineModeChange,
          options: TIMELINE_MODE_OPTIONS
        }
      ),
      onThoughtsToggle && thoughts && thoughts.length > 0 && /* @__PURE__ */ jsx99(
        ThoughtsToggle,
        {
          showThoughts,
          onToggle: onThoughtsToggle,
          thoughtCount: thoughts.length
        }
      ),
      /* @__PURE__ */ jsxs86(TooltipTrigger, { delay: 200, placement: "top", children: [
        /* @__PURE__ */ jsxs86(
          "button",
          {
            type: "button",
            onClick: handleCycleSpeed,
            className: "flex items-center justify-center min-w-8 h-8 px-2 rounded-full hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-ring text-xs font-mono text-foreground tabular-nums",
            "aria-label": `Playback speed ${playbackRate}x, click to change`,
            children: [
              playbackRate,
              "x"
            ]
          }
        ),
        /* @__PURE__ */ jsx99(Tooltip, { children: "Playback speed" })
      ] }),
      showFullscreenButton && /* @__PURE__ */ jsx99(
        "button",
        {
          type: "button",
          onClick: onFullscreen,
          className: "flex items-center justify-center w-8 h-8 rounded-full hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-ring",
          "aria-label": "Fullscreen",
          children: /* @__PURE__ */ jsx99(CornersOut, { size: 18, className: "text-foreground" })
        }
      )
    ] })
  ] });
};
var UnifiedEventsList = ({ timeline, thoughts, showThoughts, currentTimeMs, onSeek, resyncSignal }) => {
  const unifiedEntries = React67.useMemo(() => {
    const actionEntries = filterTimelineEntries(timeline.entries, {
      hideEvaluates: !showThoughts
    }).map(
      (entry, originalIdx) => ({
        type: "action",
        entry,
        timestampMs: entry.timestampMs,
        originalIdx
      })
    );
    if (!showThoughts || !thoughts || thoughts.length === 0) {
      return actionEntries;
    }
    const thoughtEntries = thoughts.filter((t) => t.timestampMs >= 0).map((thought) => ({
      type: "thought",
      thought,
      timestampMs: thought.timestampMs
    }));
    return [...actionEntries, ...thoughtEntries].sort((a, b) => a.timestampMs - b.timestampMs);
  }, [timeline.entries, thoughts, showThoughts]);
  const currentEntryIndex = React67.useMemo(() => {
    let current = -1;
    for (let i = 0; i < unifiedEntries.length; i++) {
      if (unifiedEntries[i].timestampMs <= currentTimeMs) current = i;
      else break;
    }
    return current;
  }, [unifiedEntries, currentTimeMs]);
  const actionCount = unifiedEntries.filter((e) => e.type === "action").length;
  const thoughtCount = unifiedEntries.filter((e) => e.type === "thought").length;
  const subtitle = /* @__PURE__ */ jsxs86(Fragment20, { children: [
    actionCount,
    " action",
    actionCount !== 1 ? "s" : "",
    thoughtCount > 0 && ` \xB7 ${thoughtCount} thought${thoughtCount !== 1 ? "s" : ""}`
  ] });
  return /* @__PURE__ */ jsxs86(
    Timeline2,
    {
      currentTimeMs,
      onSeek,
      autoScrollToActive: true,
      resyncSignal,
      children: [
        /* @__PURE__ */ jsx99(Timeline2.Header, { title: "Timeline", subtitle }),
        /* @__PURE__ */ jsx99(Timeline2.Body, { children: unifiedEntries.map((unified, idx) => {
          const isActive = idx === currentEntryIndex;
          if (unified.type === "thought") {
            return /* @__PURE__ */ jsx99(React67.Fragment, { children: renderThoughtItem({ thought: unified.thought, isActive, onSeek }) }, `thought-${unified.thought.id}`);
          }
          return /* @__PURE__ */ jsx99(React67.Fragment, { children: /* @__PURE__ */ jsx99(
            TimelineEntryItem,
            {
              entry: unified.entry,
              isActive,
              onSeek,
              isStepMarker: isStepAnnotation(unified.entry)
            }
          ) }, `action-${unified.originalIdx}`);
        }) })
      ]
    }
  );
};
var groupEntriesByStep = (entries) => {
  const result = [];
  const openGroups = /* @__PURE__ */ new Map();
  for (const entry of entries) {
    if (entry.type === "action" && entry.entry.isAnnotation) {
      const { annotationType, nodeId } = entry.entry;
      if ((annotationType === "step-started" || annotationType === "todo-started") && nodeId) {
        const stale = openGroups.get(nodeId);
        if (stale) result.push({ type: "group", group: stale });
        const group = {
          id: `group-${nodeId}-${entry.timestampMs}`,
          nodeId,
          name: entry.entry.action.replace(" (started)", ""),
          scenarioId: entry.entry.scenarioId,
          startedEntry: entry,
          completedEntry: null,
          children: [],
          startTimestampMs: entry.timestampMs,
          endTimestampMs: null
        };
        openGroups.set(nodeId, group);
        continue;
      }
      if ((annotationType === "step-completed" || annotationType === "todo-completed") && nodeId) {
        const group = openGroups.get(nodeId);
        if (group) {
          group.completedEntry = entry;
          group.endTimestampMs = entry.timestampMs;
          result.push({ type: "group", group });
          openGroups.delete(nodeId);
        } else {
          result.push({ type: "standalone", entry });
        }
        continue;
      }
      if (annotationType === "scenario-started" || annotationType === "scenario-completed") {
        result.push({ type: "standalone", entry });
        continue;
      }
      if (annotationType === "wait") {
        result.push({ type: "standalone", entry });
        continue;
      }
    }
    const entryNodeId = entry.type === "action" ? entry.entry.nodeId : entry.thought.nodeId;
    if (entryNodeId) {
      const group = openGroups.get(entryNodeId);
      if (group) {
        group.children.push(entry);
        continue;
      }
    }
    let addedToGroup = false;
    let bestGroup = null;
    for (const group of openGroups.values()) {
      if (entry.timestampMs >= group.startTimestampMs) {
        if (!bestGroup || group.startTimestampMs > bestGroup.startTimestampMs) {
          bestGroup = group;
        }
      }
    }
    if (bestGroup) {
      bestGroup.children.push(entry);
      addedToGroup = true;
    }
    if (!addedToGroup) {
      result.push({ type: "standalone", entry });
    }
  }
  for (const group of openGroups.values()) {
    result.push({ type: "group", group });
  }
  return result;
};
var scenarioIdOf = (item) => {
  if (item.type === "group") return item.group.scenarioId;
  if (item.entry.type === "action") return item.entry.entry.scenarioId;
  return void 0;
};
var groupItemsByScenario = (items, scenarios) => {
  const buckets = scenarios.map((s) => ({
    nodeId: s.nodeId,
    name: s.name,
    startTimestampMs: s.startMs,
    endTimestampMs: s.endMs,
    items: []
  }));
  const byId = new Map(buckets.map((b) => [b.nodeId, b]));
  const leftover = [];
  const bucketForTime = (ms) => {
    for (let i = buckets.length - 1; i >= 0; i--) {
      const b = buckets[i];
      if (ms >= b.startTimestampMs && ms <= (b.endTimestampMs ?? Infinity)) return b;
    }
    return void 0;
  };
  for (const item of items) {
    if (item.type === "standalone" && item.entry.type === "action" && isScenarioAnnotation(item.entry.entry)) {
      continue;
    }
    const sid = scenarioIdOf(item);
    const ts = item.type === "group" ? item.group.startTimestampMs : item.entry.timestampMs;
    const bucket = (sid ? byId.get(sid) : void 0) ?? bucketForTime(ts);
    if (bucket) bucket.items.push(item);
    else leftover.push(item);
  }
  return { buckets, leftover };
};
function filterGroupChildren(group) {
  return group.children.filter((child) => {
    if (child.type === "action" && child.entry.isAnnotation) {
      const t = child.entry.annotationType;
      if (t === "step-started" || t === "step-completed" || t === "todo-started" || t === "todo-completed") {
        return false;
      }
    }
    return true;
  });
}
var StepGroupItem = ({ group, isActive, onSeek, currentTimeMs, cacheBadge }) => {
  const filteredChildren = filterGroupChildren(group);
  const actionCount = filteredChildren.filter((e) => e.type === "action").length;
  const thoughtCount = filteredChildren.filter((e) => e.type === "thought").length;
  const isCompleted = group.completedEntry !== null;
  const hasChildren = filteredChildren.length > 0;
  const activeChildIndex = React67.useMemo(() => {
    if (!isActive) return -1;
    let current = -1;
    for (let i = 0; i < filteredChildren.length; i++) {
      if (filteredChildren[i].timestampMs <= currentTimeMs) current = i;
      else break;
    }
    return current;
  }, [filteredChildren, currentTimeMs, isActive]);
  const subtitle = cacheBadge || actionCount > 0 || thoughtCount > 0 ? /* @__PURE__ */ jsxs86(Fragment20, { children: [
    cacheBadge && /* @__PURE__ */ jsx99("span", { className: `font-medium ${cacheBadge.className}`, children: cacheBadge.label }),
    cacheBadge && (actionCount > 0 || thoughtCount > 0) && /* @__PURE__ */ jsx99("span", { children: "\xB7" }),
    actionCount > 0 && /* @__PURE__ */ jsxs86("span", { children: [
      actionCount,
      " action",
      actionCount !== 1 ? "s" : ""
    ] }),
    actionCount > 0 && thoughtCount > 0 && /* @__PURE__ */ jsx99("span", { children: "," }),
    thoughtCount > 0 && /* @__PURE__ */ jsxs86("span", { children: [
      thoughtCount,
      " thought",
      thoughtCount !== 1 ? "s" : ""
    ] })
  ] }) : void 0;
  return /* @__PURE__ */ jsxs86(Timeline2.Group, { active: isActive, hasChildren, children: [
    /* @__PURE__ */ jsx99(
      Timeline2.Group.Header,
      {
        timestamp: group.startTimestampMs,
        title: group.name,
        subtitle,
        onClick: !hasChildren ? () => onSeek(group.startTimestampMs) : void 0,
        trailing: isCompleted ? /* @__PURE__ */ jsx99(
          CheckCircle4,
          {
            size: 14,
            className: "text-emerald-600 dark:text-emerald-400",
            weight: "fill"
          }
        ) : void 0,
        dataAttributes: {
          "data-group-node-id": group.nodeId,
          "data-group-start-timestamp-ms": group.startTimestampMs
        }
      }
    ),
    /* @__PURE__ */ jsx99(Timeline2.Group.Items, { children: filteredChildren.map((child, idx) => {
      const childIsActive = idx === activeChildIndex;
      if (child.type === "thought") {
        return /* @__PURE__ */ jsx99(React67.Fragment, { children: renderThoughtItem({ thought: child.thought, isActive: childIsActive, onSeek }) }, `thought-${child.thought.id}`);
      }
      return /* @__PURE__ */ jsx99(React67.Fragment, { children: /* @__PURE__ */ jsx99(
        TimelineEntryItem,
        {
          entry: child.entry,
          isActive: childIsActive,
          onSeek,
          isStepMarker: isStepAnnotation(child.entry)
        }
      ) }, `action-${child.originalIdx}`);
    }) })
  ] });
};
var renderGroupedItem = (item, opts) => {
  if (item.type === "group") {
    return /* @__PURE__ */ jsx99(
      StepGroupItem,
      {
        group: item.group,
        isActive: item.group.id === opts.activeGroupId,
        onSeek: opts.onSeek,
        currentTimeMs: opts.currentTimeMs,
        cacheBadge: getTimelineCacheBadge(opts.stepMetadata?.[item.group.nodeId])
      },
      item.group.id
    );
  }
  const unified = item.entry;
  if (unified.type === "thought") {
    return /* @__PURE__ */ jsx99(React67.Fragment, { children: renderThoughtItem({ thought: unified.thought, isActive: false, onSeek: opts.onSeek }) }, `standalone-thought-${unified.thought.id}`);
  }
  return /* @__PURE__ */ jsx99(React67.Fragment, { children: /* @__PURE__ */ jsx99(
    TimelineEntryItem,
    {
      entry: unified.entry,
      isActive: false,
      onSeek: opts.onSeek,
      isStepMarker: isStepAnnotation(unified.entry)
    }
  ) }, `standalone-action-${unified.originalIdx}`);
};
var ScenarioGroupItem = ({ bucket, isActive, opts }) => {
  const stepCount = bucket.items.filter((i) => i.type === "group").length;
  const colors = getTimelineAnnotationColors("scenario-started");
  return /* @__PURE__ */ jsxs86(Timeline2.Group, { active: isActive, hasChildren: bucket.items.length > 0, defaultExpanded: true, children: [
    /* @__PURE__ */ jsx99(
      Timeline2.Group.Header,
      {
        timestamp: bucket.startTimestampMs,
        icon: /* @__PURE__ */ jsx99(UsersThree3, { size: 16, weight: "fill", className: colors?.icon }),
        title: bucket.name,
        subtitle: `${stepCount} step${stepCount !== 1 ? "s" : ""}`,
        dataAttributes: { "data-scenario-node-id": bucket.nodeId }
      }
    ),
    /* @__PURE__ */ jsx99(Timeline2.Group.Items, { children: bucket.items.map((item) => renderGroupedItem(item, opts)) })
  ] });
};
var GroupedEventsList = ({ timeline, thoughts, showThoughts, currentTimeMs, onSeek, stepMetadata, resyncSignal }) => {
  const unifiedEntries = React67.useMemo(() => {
    const actionEntries = filterTimelineEntries(timeline.entries, {
      hideEvaluates: !showThoughts
    }).map(
      (entry, originalIdx) => ({
        type: "action",
        entry,
        timestampMs: entry.timestampMs,
        originalIdx
      })
    );
    if (!showThoughts || !thoughts || thoughts.length === 0) {
      return actionEntries;
    }
    const thoughtEntries = thoughts.filter((t) => t.timestampMs >= 0).map((thought) => ({
      type: "thought",
      thought,
      timestampMs: thought.timestampMs
    }));
    return [...actionEntries, ...thoughtEntries].sort((a, b) => a.timestampMs - b.timestampMs);
  }, [timeline.entries, thoughts, showThoughts]);
  const groupedItems = React67.useMemo(() => groupEntriesByStep(unifiedEntries), [unifiedEntries]);
  const activeGroupId = React67.useMemo(() => {
    for (let i = groupedItems.length - 1; i >= 0; i--) {
      const item = groupedItems[i];
      if (item.type === "group") {
        const endTime = item.group.endTimestampMs ?? Infinity;
        if (currentTimeMs >= item.group.startTimestampMs && currentTimeMs <= endTime) {
          return item.group.id;
        }
      }
    }
    return null;
  }, [groupedItems, currentTimeMs]);
  const scenarioChapters = React67.useMemo(
    () => extractScenarioChapters(timeline.entries),
    [timeline.entries]
  );
  const scenarioMode = scenarioChapters.length > 0;
  const { buckets, leftover } = React67.useMemo(
    () => groupItemsByScenario(groupedItems, scenarioChapters),
    [groupedItems, scenarioChapters]
  );
  const activeScenarioNodeId = React67.useMemo(() => {
    for (let i = buckets.length - 1; i >= 0; i--) {
      const b = buckets[i];
      if (currentTimeMs >= b.startTimestampMs && currentTimeMs <= (b.endTimestampMs ?? Infinity)) {
        return b.nodeId;
      }
    }
    return null;
  }, [buckets, currentTimeMs]);
  const stepCount = groupedItems.filter((i) => i.type === "group").length;
  const standaloneCount = groupedItems.filter(
    (i) => i.type === "standalone" && !(i.entry.type === "action" && isScenarioAnnotation(i.entry.entry))
  ).length;
  const cacheStats = React67.useMemo(() => {
    if (!stepMetadata) return null;
    let cached = 0;
    let agent = 0;
    for (const item of groupedItems) {
      if (item.type !== "group") continue;
      const meta = stepMetadata[item.group.nodeId];
      if (!meta?.cacheOutcome) continue;
      if (meta.cacheOutcome === "hit") cached++;
      else agent++;
    }
    if (cached === 0 && agent === 0) return null;
    return { cached, agent };
  }, [groupedItems, stepMetadata]);
  const subtitle = /* @__PURE__ */ jsxs86(Fragment20, { children: [
    scenarioMode && `${buckets.length} scenario${buckets.length !== 1 ? "s" : ""} \xB7 `,
    stepCount,
    " step",
    stepCount !== 1 ? "s" : "",
    cacheStats && ` \xB7 ${cacheStats.cached} cached \xB7 ${cacheStats.agent} agent`,
    standaloneCount > 0 && ` \xB7 ${standaloneCount} other event${standaloneCount !== 1 ? "s" : ""}`
  ] });
  const renderOpts = {
    activeGroupId,
    onSeek,
    currentTimeMs,
    stepMetadata
  };
  return /* @__PURE__ */ jsxs86(
    Timeline2,
    {
      currentTimeMs,
      onSeek,
      autoScrollToActive: true,
      resyncSignal,
      children: [
        /* @__PURE__ */ jsx99(Timeline2.Header, { title: "Timeline", subtitle }),
        /* @__PURE__ */ jsx99(Timeline2.Body, { children: scenarioMode ? /* @__PURE__ */ jsxs86(Fragment20, { children: [
          leftover.map((item) => renderGroupedItem(item, renderOpts)),
          buckets.map((bucket) => /* @__PURE__ */ jsx99(
            ScenarioGroupItem,
            {
              bucket,
              isActive: bucket.nodeId === activeScenarioNodeId,
              opts: renderOpts
            },
            `scenario-${bucket.nodeId}`
          ))
        ] }) : groupedItems.map((item) => renderGroupedItem(item, renderOpts)) })
      ]
    }
  );
};
var hasStepMarkers = (timeline) => {
  return timeline.entries.some(
    (entry) => entry.isAnnotation && (entry.annotationType === "step-started" || entry.annotationType === "step-completed" || // Adhoc live-checklist TODO markers group the panel the same way.
    entry.annotationType === "todo-started" || entry.annotationType === "todo-completed" || // Scenario boundaries drive the outer Scenario → TODO tier, so they also
    // warrant the grouped view (multi-user runs always also carry TODOs).
    entry.annotationType === "scenario-started" || entry.annotationType === "scenario-completed")
  );
};
var BrowserActionsTimeline = ({
  timeline,
  thoughts,
  showThoughts = false,
  currentTimeMs,
  onSeek,
  stepMetadata,
  resyncSignal,
  className
}) => {
  if (!timeline || timeline.entries.length === 0) {
    return /* @__PURE__ */ jsx99(
      "div",
      {
        className: `flex h-full min-h-0 items-center justify-center bg-card text-sm text-muted-foreground ${className ?? ""}`,
        children: "No browser actions recorded."
      }
    );
  }
  return /* @__PURE__ */ jsx99("div", { className: `flex h-full min-h-0 flex-col bg-card ${className ?? ""}`, children: hasStepMarkers(timeline) ? /* @__PURE__ */ jsx99(
    GroupedEventsList,
    {
      timeline,
      thoughts,
      showThoughts,
      currentTimeMs,
      onSeek,
      stepMetadata,
      resyncSignal
    }
  ) : /* @__PURE__ */ jsx99(
    UnifiedEventsList,
    {
      timeline,
      thoughts,
      showThoughts,
      currentTimeMs,
      onSeek,
      resyncSignal
    }
  ) });
};
var FullscreenOverlay = ({
  posterSrc,
  videoSrc,
  timeline,
  thoughts,
  showThoughts,
  onThoughtsToggle,
  initialTimeMs,
  initialIsPlaying,
  onClose,
  consoleLogs,
  networkRequests,
  videoStartedAt,
  diagnosticsLoading,
  onLoadDiagnostics,
  seekRequest,
  onTimeUpdate,
  onAnnotationClick,
  stepMetadata,
  showChapters = false,
  diagnosticTimeline
}) => {
  const videoRef = React67.useRef(null);
  const [isPlaying, setIsPlaying] = React67.useState(initialIsPlaying);
  const [currentTimeMs, setCurrentTimeMs] = React67.useState(initialTimeMs);
  const [duration, setDuration] = React67.useState(0);
  const [showPoster, setShowPoster] = React67.useState(true);
  const [activeTab, setActiveTab] = React67.useState("video");
  const [fsTimelineMode, setFsTimelineMode] = React67.useState("events");
  React67.useEffect(() => {
    if ((activeTab === "console" || activeTab === "network") && onLoadDiagnostics) {
      onLoadDiagnostics();
    }
  }, [activeTab, onLoadDiagnostics]);
  React67.useEffect(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = initialTimeMs / 1e3;
      if (initialIsPlaying) {
        videoRef.current.play().catch(() => {
        });
      }
    }
  }, [initialTimeMs, initialIsPlaying]);
  React67.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose(currentTimeMs, isPlaying);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, currentTimeMs, isPlaying]);
  React67.useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);
  const handlePlayPause = React67.useCallback(() => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      setShowPoster(false);
      videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {
      });
    }
  }, [isPlaying]);
  const handleSeek = React67.useCallback((timeMs) => {
    if (videoRef.current) {
      videoRef.current.currentTime = timeMs / 1e3;
      setCurrentTimeMs(timeMs);
      setShowPoster(false);
    }
  }, []);
  React67.useEffect(() => {
    if (!seekRequest || !videoRef.current) return;
    videoRef.current.currentTime = seekRequest.timeMs / 1e3;
    setCurrentTimeMs(seekRequest.timeMs);
    setShowPoster(false);
  }, [seekRequest?.version]);
  const handleTimeUpdate = React67.useCallback(() => {
    if (videoRef.current) {
      const timeMs = videoRef.current.currentTime * 1e3;
      setCurrentTimeMs(timeMs);
      onTimeUpdate?.(timeMs);
    }
  }, [onTimeUpdate]);
  const handleLoadedMetadata = React67.useCallback(() => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration * 1e3);
    }
  }, []);
  const currentAction = React67.useMemo(() => {
    if (!timeline || timeline.entries.length === 0) return null;
    let current = null;
    for (const entry of timeline.entries) {
      if (entry.timestampMs <= currentTimeMs) {
        current = entry;
      } else {
        break;
      }
    }
    return current;
  }, [timeline, currentTimeMs]);
  const currentUrl = React67.useMemo(
    () => urlAtTimelinePosition(timeline, currentTimeMs),
    [timeline, currentTimeMs]
  );
  const currentThought = React67.useMemo(() => {
    if (!thoughts || thoughts.length === 0) return null;
    let current = null;
    for (const thought of thoughts) {
      if (thought.timestampMs <= currentTimeMs) {
        current = thought;
      } else {
        break;
      }
    }
    return current;
  }, [thoughts, currentTimeMs]);
  const hasTimeline = Boolean(timeline && timeline.entries.length > 0);
  const hasDiagnostics = Boolean(consoleLogs?.length || networkRequests?.length);
  const hasDiagnosticsCapability = Boolean(onLoadDiagnostics || hasDiagnostics);
  const hasContent = hasTimeline || thoughts && thoughts.length > 0 || hasDiagnostics || diagnosticsLoading;
  const handleDownload = React67.useCallback(() => {
    const downloadUrl = new URL(videoSrc, window.location.origin);
    downloadUrl.searchParams.set("download", "1");
    window.open(downloadUrl.toString(), "_blank");
  }, [videoSrc]);
  return /* @__PURE__ */ jsxs86("div", { className: "fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center p-6", children: [
    /* @__PURE__ */ jsxs86("div", { className: "absolute top-4 right-4 flex items-center gap-2 z-10", children: [
      /* @__PURE__ */ jsx99(
        "button",
        {
          type: "button",
          onClick: handleDownload,
          className: "flex items-center justify-center w-10 h-10 rounded-full bg-muted hover:bg-muted/80 transition-colors focus:outline-none focus:ring-2 focus:ring-ring",
          "aria-label": "Download video",
          children: /* @__PURE__ */ jsx99(DownloadSimple, { size: 24, className: "text-foreground" })
        }
      ),
      /* @__PURE__ */ jsx99(
        "button",
        {
          type: "button",
          onClick: () => onClose(currentTimeMs, isPlaying),
          className: "flex items-center justify-center w-10 h-10 rounded-full bg-muted hover:bg-muted/80 transition-colors focus:outline-none focus:ring-2 focus:ring-ring",
          "aria-label": "Close fullscreen",
          children: /* @__PURE__ */ jsx99(X4, { size: 24, className: "text-foreground" })
        }
      )
    ] }),
    /* @__PURE__ */ jsx99("div", { className: "flex flex-col w-full h-full max-w-7xl max-h-[90vh] rounded-lg overflow-hidden border border-border shadow-2xl", children: /* @__PURE__ */ jsxs86("div", { className: "flex flex-1 min-h-0", children: [
      /* @__PURE__ */ jsxs86("div", { className: "flex-1 flex flex-col bg-muted/30 min-w-0", children: [
        hasDiagnosticsCapability && /* @__PURE__ */ jsxs86("div", { className: "flex border-b border-border shrink-0 bg-card", children: [
          /* @__PURE__ */ jsxs86(
            "button",
            {
              type: "button",
              onClick: () => setActiveTab("video"),
              className: `flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors ${activeTab === "video" ? "text-primary border-b-2 border-primary -mb-px" : "text-muted-foreground hover:text-foreground"}`,
              children: [
                /* @__PURE__ */ jsx99(VideoCamera2, { size: 16 }),
                "Video"
              ]
            }
          ),
          /* @__PURE__ */ jsxs86(
            "button",
            {
              type: "button",
              onClick: () => setActiveTab("console"),
              className: `flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors ${activeTab === "console" ? "text-primary border-b-2 border-primary -mb-px" : "text-muted-foreground hover:text-foreground"}`,
              children: [
                /* @__PURE__ */ jsx99(Terminal3, { size: 16 }),
                "Console",
                consoleLogs && consoleLogs.length > 0 && /* @__PURE__ */ jsx99("span", { className: "text-xs bg-muted px-1.5 py-0.5 rounded-full", children: consoleLogs.length })
              ]
            }
          ),
          /* @__PURE__ */ jsxs86(
            "button",
            {
              type: "button",
              onClick: () => setActiveTab("network"),
              className: `flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors ${activeTab === "network" ? "text-primary border-b-2 border-primary -mb-px" : "text-muted-foreground hover:text-foreground"}`,
              children: [
                /* @__PURE__ */ jsx99(Globe, { size: 16 }),
                "Network",
                networkRequests && networkRequests.length > 0 && /* @__PURE__ */ jsx99("span", { className: "text-xs bg-muted px-1.5 py-0.5 rounded-full", children: networkRequests.length })
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsxs86("div", { className: "flex-1 min-h-0 flex flex-col overflow-hidden", children: [
          hasTimeline && (!hasDiagnosticsCapability || activeTab === "video") && /* @__PURE__ */ jsx99(BrowserAddressBar, { url: currentUrl, size: "md" }),
          /* @__PURE__ */ jsxs86(
            "div",
            {
              className: `flex-1 relative cursor-pointer min-h-0 ${hasDiagnosticsCapability && activeTab !== "video" ? "hidden" : ""}`,
              onClick: handlePlayPause,
              children: [
                /* @__PURE__ */ jsx99(
                  "video",
                  {
                    ref: videoRef,
                    src: videoSrc,
                    poster: showPoster ? posterSrc ?? void 0 : void 0,
                    playsInline: true,
                    className: "absolute inset-0 w-full h-full object-contain",
                    onTimeUpdate: handleTimeUpdate,
                    onLoadedMetadata: handleLoadedMetadata,
                    onEnded: () => setIsPlaying(false),
                    children: "Your browser does not support video playback."
                  }
                ),
                !isPlaying && currentTimeMs === 0 && /* @__PURE__ */ jsx99("div", { className: "absolute inset-0 flex items-center justify-center bg-black/20", children: /* @__PURE__ */ jsx99("div", { className: "flex h-20 w-20 items-center justify-center rounded-full bg-white/90 shadow-lg", children: /* @__PURE__ */ jsx99(Play4, { size: 40, weight: "fill", className: "text-foreground" }) }) })
              ]
            }
          ),
          hasDiagnosticsCapability && activeTab === "console" && /* @__PURE__ */ jsx99("div", { className: "flex-1 min-h-0 overflow-hidden", children: /* @__PURE__ */ jsx99(
            ConsoleDiagnosticsPanel,
            {
              logs: consoleLogs ?? [],
              currentTimeMs,
              onSeek: handleSeek,
              isLoading: diagnosticsLoading,
              videoStartedAt
            }
          ) }),
          hasDiagnosticsCapability && activeTab === "network" && /* @__PURE__ */ jsx99("div", { className: "flex-1 min-h-0 overflow-hidden", children: /* @__PURE__ */ jsx99(
            NetworkDiagnosticsPanel,
            {
              requests: networkRequests ?? [],
              currentTimeMs,
              onSeek: handleSeek,
              isLoading: diagnosticsLoading,
              videoStartedAt
            }
          ) })
        ] }),
        /* @__PURE__ */ jsx99(
          ControlsBar,
          {
            isPlaying,
            currentTimeMs,
            duration,
            timeline,
            thoughts,
            showThoughts,
            onThoughtsToggle,
            currentAction,
            currentThought,
            onPlayPause: handlePlayPause,
            onSeek: handleSeek,
            onAnnotationClick,
            showChapters,
            onFullscreen: () => onClose(currentTimeMs, isPlaying),
            showFullscreenButton: false,
            videoRef,
            diagnosticTimeline,
            timelineMode: fsTimelineMode,
            onTimelineModeChange: setFsTimelineMode
          }
        )
      ] }),
      hasContent && timeline && /* @__PURE__ */ jsx99("div", { className: "w-80 shrink-0 flex flex-col bg-card border-l border-border", children: hasStepMarkers(timeline) ? /* @__PURE__ */ jsx99(
        GroupedEventsList,
        {
          timeline,
          thoughts,
          showThoughts,
          currentTimeMs,
          onSeek: handleSeek,
          stepMetadata
        }
      ) : /* @__PURE__ */ jsx99(
        UnifiedEventsList,
        {
          timeline,
          thoughts,
          showThoughts,
          currentTimeMs,
          onSeek: handleSeek
        }
      ) })
    ] }) })
  ] });
};
var VideoPlayerWithTimeline = ({
  posterSrc,
  videoSrc,
  timeline,
  thoughts,
  showThoughts: controlledShowThoughts,
  onThoughtsVisibilityChange,
  alt = "Video with timeline",
  className,
  consoleLogs,
  networkRequests,
  videoStartedAt,
  diagnosticsLoading,
  onLoadDiagnostics,
  seekRequest,
  onTimeUpdate,
  onPlayStart,
  onScrub,
  isFullscreen: externalIsFullscreen,
  onFullscreenChange,
  stepMetadata,
  onAnnotationClick,
  showChapters = false,
  fill = false,
  flush = false,
  showFullscreen = true,
  diagnosticTimeline,
  timelineMode,
  onTimelineModeChange,
  hasFastVariant,
  videoVariant,
  onVideoVariantChange,
  autoPlayMuted = false
}) => {
  const [isPlaying, setIsPlaying] = React67.useState(false);
  const [isLoading, setIsLoading] = React67.useState(false);
  const [hasError, setHasError] = React67.useState(false);
  const [currentTimeMs, setCurrentTimeMs] = React67.useState(0);
  const [duration, setDuration] = React67.useState(0);
  const [internalIsFullscreen, setInternalIsFullscreen] = React67.useState(false);
  const isFullscreen = externalIsFullscreen ?? internalIsFullscreen;
  const setIsFullscreen = onFullscreenChange ?? setInternalIsFullscreen;
  const [showPoster, setShowPoster] = React67.useState(true);
  const [internalShowThoughts, setInternalShowThoughts] = React67.useState(true);
  const videoRef = React67.useRef(null);
  const showThoughts = controlledShowThoughts ?? internalShowThoughts;
  const handleThoughtsToggle = React67.useCallback(() => {
    if (onThoughtsVisibilityChange) {
      onThoughtsVisibilityChange(!showThoughts);
    } else {
      setInternalShowThoughts(!showThoughts);
    }
  }, [showThoughts, onThoughtsVisibilityChange]);
  React67.useEffect(() => {
    setIsPlaying(false);
    setIsLoading(false);
    setHasError(false);
    setCurrentTimeMs(0);
    setDuration(0);
    if (!onFullscreenChange) setInternalIsFullscreen(false);
    setShowPoster(true);
  }, [posterSrc, videoSrc]);
  const handlePlayPause = React67.useCallback(() => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      setIsLoading(true);
      setShowPoster(false);
      videoRef.current.play().then(() => {
        setIsPlaying(true);
        setIsLoading(false);
        onPlayStart?.();
      }).catch(() => {
        setIsLoading(false);
        setHasError(true);
      });
    }
  }, [isPlaying, onPlayStart]);
  const handleAutoPlayStarted = React67.useCallback(() => {
    setIsPlaying(true);
    setShowPoster(false);
  }, []);
  const handleCanPlay = React67.useCallback(() => {
    setIsLoading(false);
    if (videoRef.current) {
      setDuration(videoRef.current.duration * 1e3);
    }
  }, []);
  const handleError = React67.useCallback(() => {
    setIsLoading(false);
    setIsPlaying(false);
    setHasError(true);
  }, []);
  const handleTimeUpdate = React67.useCallback(() => {
    if (videoRef.current) {
      const timeMs = videoRef.current.currentTime * 1e3;
      setCurrentTimeMs(timeMs);
      onTimeUpdate?.(timeMs);
    }
  }, [onTimeUpdate]);
  const handleLoadedMetadata = React67.useCallback(() => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration * 1e3);
    }
  }, []);
  const handleSeek = React67.useCallback(
    (timeMs) => {
      if (videoRef.current) {
        videoRef.current.currentTime = timeMs / 1e3;
        setCurrentTimeMs(timeMs);
        setShowPoster(false);
        onScrub?.();
      }
    },
    [onScrub]
  );
  React67.useEffect(() => {
    if (!seekRequest || !videoRef.current) return;
    videoRef.current.currentTime = seekRequest.timeMs / 1e3;
    setCurrentTimeMs(seekRequest.timeMs);
    setShowPoster(false);
  }, [seekRequest?.version]);
  const handleEnded = React67.useCallback(() => {
    setIsPlaying(false);
  }, []);
  const currentAction = React67.useMemo(() => {
    if (!timeline || timeline.entries.length === 0) return null;
    let current = null;
    for (const entry of timeline.entries) {
      if (entry.timestampMs <= currentTimeMs) {
        current = entry;
      } else {
        break;
      }
    }
    return current;
  }, [timeline, currentTimeMs]);
  const currentUrl = React67.useMemo(
    () => urlAtTimelinePosition(timeline, currentTimeMs),
    [timeline, currentTimeMs]
  );
  const currentThought = React67.useMemo(() => {
    if (!thoughts || thoughts.length === 0) return null;
    let current = null;
    for (const thought of thoughts) {
      if (thought.timestampMs <= currentTimeMs) {
        current = thought;
      } else {
        break;
      }
    }
    return current;
  }, [thoughts, currentTimeMs]);
  const hasVideo = Boolean(videoSrc);
  const hasPoster = Boolean(posterSrc);
  const hasTimeline = Boolean(timeline && timeline.entries.length > 0);
  if (!hasPoster && !hasVideo) {
    return /* @__PURE__ */ jsx99(
      Box,
      {
        className: `flex aspect-video w-full items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 ${className ?? ""}`,
        children: /* @__PURE__ */ jsx99(Text, { size: "sm", className: "text-muted-foreground", children: "No media available" })
      }
    );
  }
  return /* @__PURE__ */ jsxs86(Fragment20, { children: [
    /* @__PURE__ */ jsxs86(
      "div",
      {
        className: `overflow-hidden bg-muted/30 ${fill ? "flex h-full flex-col" : ""} ${fill || flush ? "" : "rounded-lg border border-border"} ${className ?? ""}`,
        children: [
          hasTimeline && /* @__PURE__ */ jsx99(BrowserAddressBar, { url: currentUrl }),
          /* @__PURE__ */ jsxs86(
            "div",
            {
              className: `relative cursor-pointer ${fill ? "min-h-0 flex-1" : "aspect-video"}`,
              onClick: handlePlayPause,
              children: [
                hasVideo ? /* @__PURE__ */ jsx99(
                  "video",
                  {
                    ref: videoRef,
                    src: videoSrc,
                    poster: showPoster ? posterSrc ?? void 0 : void 0,
                    playsInline: true,
                    autoPlay: autoPlayMuted,
                    muted: autoPlayMuted,
                    preload: "metadata",
                    className: "absolute inset-0 w-full h-full object-contain",
                    onCanPlay: handleCanPlay,
                    onPlay: autoPlayMuted ? handleAutoPlayStarted : void 0,
                    onError: handleError,
                    onEnded: handleEnded,
                    onTimeUpdate: handleTimeUpdate,
                    onLoadedMetadata: handleLoadedMetadata,
                    children: "Your browser does not support video playback."
                  }
                ) : hasPoster ? /* @__PURE__ */ jsx99(
                  "img",
                  {
                    src: posterSrc,
                    alt,
                    className: "absolute inset-0 w-full h-full object-contain"
                  }
                ) : null,
                isLoading && /* @__PURE__ */ jsx99("div", { className: "absolute inset-0 flex items-center justify-center bg-black/50", children: /* @__PURE__ */ jsx99(SpinnerGap2, { size: 48, className: "animate-spin text-white" }) }),
                hasError && /* @__PURE__ */ jsxs86("div", { className: "absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/50", children: [
                  /* @__PURE__ */ jsx99(WarningCircle3, { size: 32, className: "text-white/80" }),
                  /* @__PURE__ */ jsx99(Text, { size: "sm", className: "text-white/80", children: "Video failed to load" }),
                  /* @__PURE__ */ jsx99(
                    "button",
                    {
                      type: "button",
                      onClick: (e) => {
                        e.stopPropagation();
                        setHasError(false);
                        handlePlayPause();
                      },
                      className: "mt-1 rounded-md bg-white/20 px-3 py-1 text-sm text-white transition-colors hover:bg-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      children: "Retry"
                    }
                  )
                ] }),
                hasVideo && !isPlaying && !isLoading && !hasError && currentTimeMs === 0 && /* @__PURE__ */ jsx99("div", { className: "absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity hover:bg-black/30", children: /* @__PURE__ */ jsx99("div", { className: "flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-lg transition-transform hover:scale-105", children: /* @__PURE__ */ jsx99(Play4, { size: 32, weight: "fill", className: "text-foreground" }) }) }),
                currentAction && isPlaying && !isLoading && (() => {
                  const colors = getEventColors(currentAction);
                  return /* @__PURE__ */ jsx99("div", { className: "absolute bottom-2 left-2 pointer-events-none", children: /* @__PURE__ */ jsxs86(
                    "div",
                    {
                      className: `inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium ${colors.bg} ${colors.text} ${colors.border}`,
                      children: [
                        /* @__PURE__ */ jsx99(
                          "span",
                          {
                            className: `w-1.5 h-1.5 rounded-full ${colors.dot}`,
                            style: colors.customColor ? { backgroundColor: colors.customColor } : void 0
                          }
                        ),
                        /* @__PURE__ */ jsx99("span", { children: formatAction(currentAction) })
                      ]
                    }
                  ) });
                })()
              ]
            }
          ),
          hasVideo && /* @__PURE__ */ jsx99(
            ControlsBar,
            {
              isPlaying,
              currentTimeMs,
              duration,
              timeline,
              thoughts,
              showThoughts,
              onThoughtsToggle: thoughts && thoughts.length > 0 ? handleThoughtsToggle : void 0,
              currentAction,
              currentThought,
              onPlayPause: handlePlayPause,
              onSeek: handleSeek,
              onAnnotationClick,
              showChapters,
              onFullscreen: () => {
                if (videoRef.current) {
                  videoRef.current.pause();
                }
                setIsFullscreen(true);
              },
              showFullscreenButton: showFullscreen,
              videoRef,
              diagnosticTimeline,
              timelineMode,
              onTimelineModeChange,
              hasFastVariant,
              videoVariant,
              onVideoVariantChange
            }
          ),
          !hasVideo && hasTimeline && /* @__PURE__ */ jsx99("div", { className: "px-3 py-2 bg-card border-t border-border", children: /* @__PURE__ */ jsxs86("div", { className: "flex items-center gap-2 text-xs text-muted-foreground", children: [
            /* @__PURE__ */ jsx99("span", { className: "w-2 h-2 rounded-full bg-blue-500" }),
            /* @__PURE__ */ jsxs86("span", { children: [
              timeline.entries.length,
              " actions recorded"
            ] })
          ] }) })
        ]
      }
    ),
    isFullscreen && videoSrc && /* @__PURE__ */ jsx99(
      FullscreenOverlay,
      {
        posterSrc,
        videoSrc,
        timeline,
        thoughts,
        showThoughts,
        onThoughtsToggle: thoughts && thoughts.length > 0 ? handleThoughtsToggle : void 0,
        initialTimeMs: currentTimeMs,
        initialIsPlaying: isPlaying,
        consoleLogs,
        networkRequests,
        videoStartedAt,
        diagnosticsLoading,
        onLoadDiagnostics,
        seekRequest,
        onTimeUpdate,
        stepMetadata,
        onAnnotationClick,
        showChapters,
        diagnosticTimeline,
        onClose: (newTimeMs, newIsPlaying) => {
          if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = newTimeMs / 1e3;
            setCurrentTimeMs(newTimeMs);
            if (newIsPlaying) {
              videoRef.current.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
            } else {
              setIsPlaying(false);
            }
          }
          setIsFullscreen(false);
        }
      }
    )
  ] });
};

// src/components/Page.tsx
import React68 from "react";
import { jsx as jsx100 } from "react/jsx-runtime";
var paddingSizes2 = {
  none: "p-0",
  sm: "p-4",
  md: "p-5",
  lg: "p-6",
  xl: "p-8"
};
var maxWidths = {
  sm: "max-w-2xl",
  md: "max-w-4xl",
  lg: "max-w-6xl",
  xl: "max-w-7xl",
  "2xl": "max-w-screen-2xl",
  full: "max-w-full"
};
var Page = React68.forwardRef(
  ({ padding = "lg", maxWidth = "full", className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx100(
      "div",
      {
        ref,
        className: cn(
          "space-y-6",
          paddingSizes2[padding],
          maxWidths[maxWidth],
          maxWidth !== "full" && "mx-auto",
          className
        ),
        ...props,
        children
      }
    );
  }
);
Page.displayName = "Page";

// src/components/Progress.tsx
import React69 from "react";
import { ProgressBar as AriaProgressBar } from "react-aria-components";
import { Fragment as Fragment21, jsx as jsx101, jsxs as jsxs87 } from "react/jsx-runtime";
var progressSizes = {
  sm: "h-1",
  md: "h-2",
  lg: "h-3"
};
var progressVariants = {
  default: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  destructive: "bg-destructive"
};
var Progress = React69.forwardRef(
  ({ value, size = "md", variant = "default", showLabel = false, className, animate = true, "aria-label": ariaLabel }, ref) => {
    const numericValue = Number(value);
    const normalizedValue = Number.isFinite(numericValue) && numericValue > 0 && numericValue <= 1 ? numericValue * 100 : Number.isFinite(numericValue) ? numericValue : 0;
    const clampedValue = Math.min(100, Math.max(0, normalizedValue));
    return /* @__PURE__ */ jsx101(
      AriaProgressBar,
      {
        ref,
        value: clampedValue,
        minValue: 0,
        maxValue: 100,
        "aria-label": ariaLabel || "Progress",
        className: cn("flex w-full min-w-0 flex-col gap-1", className),
        children: ({ valueText }) => {
          const resolvedPercentage = clampedValue;
          const labelText = valueText ?? `${Math.round(resolvedPercentage)}%`;
          return /* @__PURE__ */ jsxs87(Fragment21, { children: [
            showLabel && /* @__PURE__ */ jsx101("div", { className: "flex justify-between text-sm", children: /* @__PURE__ */ jsx101("span", { className: "text-muted-foreground", children: labelText }) }),
            /* @__PURE__ */ jsx101("div", { className: cn("relative w-full min-w-0 overflow-hidden rounded-full bg-muted", progressSizes[size]), children: /* @__PURE__ */ jsx101(
              "div",
              {
                className: cn(
                  "h-full",
                  animate ? "transition-[width] duration-500 ease-out" : "",
                  progressVariants[variant]
                ),
                style: { width: `${resolvedPercentage}%` }
              }
            ) })
          ] });
        }
      }
    );
  }
);
Progress.displayName = "Progress";

// src/components/Markdown.tsx
import React70 from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { jsx as jsx102 } from "react/jsx-runtime";
var Markdown = React70.forwardRef(
  ({ children, className, compact }, ref) => {
    const components = {
      // Headings
      h1: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102(
        "h1",
        {
          className: "scroll-m-20 text-2xl font-semibold tracking-tight text-foreground mt-6 mb-4 first:mt-0",
          ...props,
          children: children2
        }
      ),
      h2: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102(
        "h2",
        {
          className: cn(
            "scroll-m-20 text-xl font-semibold tracking-tight text-foreground first:mt-0",
            // Generous space above for a clear section break; snug below so the
            // heading reads as attached to the content it introduces.
            compact ? "mt-6 mb-2.5" : "mt-5 mb-3"
          ),
          ...props,
          children: children2
        }
      ),
      h3: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102(
        "h3",
        {
          className: cn(
            "scroll-m-20 text-lg font-semibold tracking-tight text-foreground first:mt-0",
            compact ? "mt-5 mb-2" : "mt-4 mb-2"
          ),
          ...props,
          children: children2
        }
      ),
      h4: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102(
        "h4",
        {
          className: "scroll-m-20 text-base font-semibold tracking-tight text-foreground mt-4 mb-2 first:mt-0",
          ...props,
          children: children2
        }
      ),
      h5: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102(
        "h5",
        {
          className: "scroll-m-20 text-sm font-semibold tracking-tight text-foreground mt-3 mb-2 first:mt-0",
          ...props,
          children: children2
        }
      ),
      h6: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102(
        "h6",
        {
          className: "scroll-m-20 text-sm font-medium tracking-tight text-muted-foreground mt-3 mb-2 first:mt-0",
          ...props,
          children: children2
        }
      ),
      // Paragraphs
      p: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102(
        "p",
        {
          className: cn(
            "text-foreground",
            compact ? "leading-6 [&:not(:first-child)]:mt-3" : "leading-6 [&:not(:first-child)]:mt-4"
          ),
          ...props,
          children: children2
        }
      ),
      // Links
      a: ({ children: children2, href, ...props }) => /* @__PURE__ */ jsx102(
        "a",
        {
          href,
          className: "font-medium text-primary underline underline-offset-4 hover:text-primary/80 transition-colors",
          target: "_blank",
          rel: "noopener noreferrer",
          ...props,
          children: children2
        }
      ),
      // Lists
      ul: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102(
        "ul",
        {
          className: cn(
            "ml-6 list-disc text-foreground",
            compact ? "my-2.5 [&>li]:mt-1.5" : "my-4 [&>li]:mt-2"
          ),
          ...props,
          children: children2
        }
      ),
      ol: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102(
        "ol",
        {
          className: cn(
            "ml-6 list-decimal text-foreground",
            compact ? "my-2.5 [&>li]:mt-1.5" : "my-4 [&>li]:mt-2"
          ),
          ...props,
          children: children2
        }
      ),
      li: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102(
        "li",
        {
          className: cn(
            // Tighten nested lists so sub-bullets sit snugly under their parent
            // item instead of inheriting the full inter-item gap.
            compact ? "leading-6 [&>ul]:mt-1.5 [&>ul]:mb-0 [&>ol]:mt-1.5 [&>ol]:mb-0" : "leading-7"
          ),
          ...props,
          children: children2
        }
      ),
      // Code - inline by default, pre wrapper handles block code
      code: ({ children: children2, className: className2, ...props }) => /* @__PURE__ */ jsx102(
        "code",
        {
          className: cn(
            "relative rounded-md bg-muted px-[0.4rem] py-[0.2rem] font-mono text-foreground",
            className2
          ),
          ...props,
          children: children2
        }
      ),
      // Pre wraps fenced code blocks - provides block styling
      pre: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102(
        "pre",
        {
          className: cn(
            "overflow-x-auto rounded-md bg-muted border border-border",
            compact ? "my-1.5 p-2" : "my-4 p-4"
          ),
          ...props,
          children: children2
        }
      ),
      // Blockquotes
      blockquote: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102(
        "blockquote",
        {
          className: cn(
            "border-l-4 border-border pl-4 italic text-muted-foreground [&>*]:text-muted-foreground",
            compact ? "mt-1.5" : "mt-4"
          ),
          ...props,
          children: children2
        }
      ),
      // Tables
      table: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102("div", { className: "my-4 w-full overflow-x-auto", children: /* @__PURE__ */ jsx102("table", { className: "w-full border-collapse text-sm", ...props, children: children2 }) }),
      thead: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102("thead", { className: "bg-muted/50", ...props, children: children2 }),
      tbody: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102("tbody", { className: "[&_tr:last-child]:border-0", ...props, children: children2 }),
      tr: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102("tr", { className: "border-b border-border transition-colors hover:bg-muted/30", ...props, children: children2 }),
      th: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102(
        "th",
        {
          className: "h-10 px-4 text-left align-middle font-semibold text-foreground [&:has([role=checkbox])]:pr-0",
          ...props,
          children: children2
        }
      ),
      td: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102("td", { className: "p-4 align-middle text-foreground [&:has([role=checkbox])]:pr-0", ...props, children: children2 }),
      // Horizontal rule
      hr: (props) => /* @__PURE__ */ jsx102("hr", { className: "my-6 border-t border-border", ...props }),
      // Strong/Bold
      strong: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102("strong", { className: "font-semibold text-foreground", ...props, children: children2 }),
      // Emphasis/Italic
      em: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102("em", { className: "italic", ...props, children: children2 }),
      // Strikethrough (from GFM)
      del: ({ children: children2, ...props }) => /* @__PURE__ */ jsx102("del", { className: "line-through text-muted-foreground", ...props, children: children2 })
    };
    return /* @__PURE__ */ jsx102("div", { ref, className: cn("markdown-content", className), children: /* @__PURE__ */ jsx102(ReactMarkdown, { remarkPlugins: [remarkGfm], components, children }) });
  }
);
Markdown.displayName = "Markdown";

// src/components/Switcher.tsx
import React71 from "react";
import {
  MenuTrigger as AriaMenuTrigger3,
  Menu as AriaMenu3,
  MenuItem as AriaMenuItem3,
  Popover as Popover13,
  Separator as Separator2
} from "react-aria-components";
import { MagnifyingGlass as MagnifyingGlass5 } from "@phosphor-icons/react";
import { Fragment as Fragment22, jsx as jsx103, jsxs as jsxs88 } from "react/jsx-runtime";
var defaultFilter2 = (item, query) => item.label.toLowerCase().includes(query.toLowerCase());
function getFilteredSwitcherItems({
  items,
  selectedItem,
  isSearchable,
  searchQuery,
  filter = defaultFilter2,
  hideItemsUntilSearch = false
}) {
  if (!isSearchable) return items;
  const query = searchQuery.trim();
  if (!query) {
    return hideItemsUntilSearch ? selectedItem ? [selectedItem] : [] : items;
  }
  return items.filter((item) => filter(item, query));
}
function Switcher({
  value,
  items,
  onSelect,
  actions,
  onAction,
  placeholder = "Select...",
  triggerIcon,
  iconOnly = false,
  searchable,
  searchPlaceholder = "Search...",
  filter,
  hideItemsUntilSearch = false,
  emptyText = "No matches",
  className
}) {
  const selectedItem = items.find((item) => item.id === value);
  const displayLabel = selectedItem?.label ?? placeholder;
  const isSearchable = searchable === true || searchable !== false && items.length > 7;
  const [isOpen, setIsOpen] = React71.useState(false);
  const [searchQuery, setSearchQuery] = React71.useState("");
  const searchInputRef = React71.useRef(null);
  const filteredItems = React71.useMemo(() => {
    return getFilteredSwitcherItems({
      items,
      selectedItem,
      isSearchable,
      searchQuery,
      filter,
      hideItemsUntilSearch
    });
  }, [items, selectedItem, isSearchable, searchQuery, filter, hideItemsUntilSearch]);
  const handleOpenChange = React71.useCallback(
    (open) => {
      setIsOpen(open);
      if (!open) {
        setSearchQuery("");
      } else if (isSearchable) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            searchInputRef.current?.focus();
          });
        });
      }
    },
    [isSearchable]
  );
  const handleSearchKeyDown = React71.useCallback((e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const popover = searchInputRef.current?.closest("[data-rac]")?.parentElement;
      const firstItem = popover?.querySelector('[role="menuitem"]');
      firstItem?.focus();
    }
  }, []);
  return /* @__PURE__ */ jsxs88(AriaMenuTrigger3, { isOpen, onOpenChange: handleOpenChange, children: [
    /* @__PURE__ */ jsx103(
      Button,
      {
        variant: "ghost",
        iconLeft: triggerIcon,
        className: cn(iconOnly ? "p-2" : "", className),
        children: !iconOnly && displayLabel
      }
    ),
    /* @__PURE__ */ jsxs88(
      Popover13,
      {
        className: cn(
          "z-50 min-w-[200px] max-h-[min(400px,var(--visual-viewport-height,100dvh)-100px)] overflow-y-auto rounded-md border bg-popover text-popover-foreground outline-none",
          "shadow-lg",
          "data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95",
          "data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95"
        ),
        children: [
          isSearchable && /* @__PURE__ */ jsxs88("div", { className: "flex items-center gap-2 px-3 py-2 border-b border-border", children: [
            /* @__PURE__ */ jsx103(MagnifyingGlass5, { className: "h-4 w-4 text-muted-foreground shrink-0" }),
            /* @__PURE__ */ jsx103(
              "input",
              {
                ref: searchInputRef,
                type: "text",
                className: "flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground",
                placeholder: searchPlaceholder,
                value: searchQuery,
                onChange: (e) => setSearchQuery(e.target.value),
                onKeyDown: handleSearchKeyDown,
                "aria-label": "Search options"
              }
            )
          ] }),
          /* @__PURE__ */ jsxs88(
            AriaMenu3,
            {
              className: "p-1",
              onAction: (key) => {
                const itemKey = String(key);
                if (actions?.some((a) => a.id === itemKey)) {
                  onAction?.(itemKey);
                } else {
                  onSelect(itemKey);
                }
              },
              children: [
                filteredItems.length === 0 ? /* @__PURE__ */ jsx103(
                  AriaMenuItem3,
                  {
                    id: "__empty__",
                    isDisabled: true,
                    className: "py-4 text-center text-sm text-muted-foreground outline-none",
                    children: emptyText
                  }
                ) : filteredItems.map((item) => /* @__PURE__ */ jsxs88(
                  AriaMenuItem3,
                  {
                    id: item.id,
                    className: cn(
                      "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm font-normal outline-none",
                      "transition-colors",
                      "data-[focused]:bg-accent data-[focused]:text-accent-foreground",
                      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                      item.id === value && "bg-accent/50"
                    ),
                    children: [
                      item.label,
                      item.id === value && /* @__PURE__ */ jsx103("span", { className: "ml-auto", children: /* @__PURE__ */ jsx103(
                        "svg",
                        {
                          className: "h-4 w-4",
                          xmlns: "http://www.w3.org/2000/svg",
                          viewBox: "0 0 24 24",
                          fill: "none",
                          stroke: "currentColor",
                          strokeWidth: "2",
                          strokeLinecap: "round",
                          strokeLinejoin: "round",
                          children: /* @__PURE__ */ jsx103("polyline", { points: "20 6 9 17 4 12" })
                        }
                      ) })
                    ]
                  },
                  item.id
                )),
                actions && actions.length > 0 && /* @__PURE__ */ jsxs88(Fragment22, { children: [
                  /* @__PURE__ */ jsx103(Separator2, { className: "-mx-1 my-1 h-px bg-muted" }),
                  actions.map((action) => /* @__PURE__ */ jsxs88(
                    AriaMenuItem3,
                    {
                      id: action.id,
                      className: cn(
                        "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm font-normal outline-none",
                        "transition-colors",
                        "data-[focused]:bg-accent data-[focused]:text-accent-foreground",
                        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                      ),
                      children: [
                        action.icon && /* @__PURE__ */ jsx103("span", { className: "mr-2 flex-shrink-0 text-muted-foreground", children: action.icon }),
                        action.label
                      ]
                    },
                    action.id
                  ))
                ] })
              ]
            }
          )
        ]
      }
    )
  ] });
}

// src/components/TagGroup.tsx
import React72 from "react";
import {
  TagGroup as AriaTagGroup,
  TagList,
  Tag as AriaTag,
  Label as Label11
} from "react-aria-components";
import { jsx as jsx104, jsxs as jsxs89 } from "react/jsx-runtime";
function TagGroup({ label, items, onRemove, className, ...props }) {
  return /* @__PURE__ */ jsxs89(AriaTagGroup, { className: cn("flex flex-col gap-1.5", className), ...props, children: [
    label && /* @__PURE__ */ jsx104(Label11, { className: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70", children: label }),
    /* @__PURE__ */ jsx104(TagList, { className: "flex flex-wrap gap-1.5", children: items.map((item) => /* @__PURE__ */ jsx104(Tag, { id: item.id, onRemove, children: item.label }, item.id)) })
  ] });
}
var Tag = React72.forwardRef(
  ({ children, onRemove, className, ...props }, ref) => {
    return /* @__PURE__ */ jsxs89(
      AriaTag,
      {
        ref,
        className: cn(
          "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-semibold transition-colors",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "data-[selected]:bg-accent data-[selected]:text-accent-foreground",
          "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          className
        ),
        ...props,
        children: [
          children,
          onRemove && /* @__PURE__ */ jsx104(
            "button",
            {
              onClick: () => onRemove(props.id),
              className: "ml-1 rounded-sm hover:text-destructive focus:outline-none",
              "aria-label": "Remove",
              children: /* @__PURE__ */ jsxs89(
                "svg",
                {
                  className: "h-3 w-3",
                  viewBox: "0 0 24 24",
                  fill: "none",
                  stroke: "currentColor",
                  strokeWidth: "2",
                  strokeLinecap: "round",
                  strokeLinejoin: "round",
                  children: [
                    /* @__PURE__ */ jsx104("line", { x1: "18", y1: "6", x2: "6", y2: "18" }),
                    /* @__PURE__ */ jsx104("line", { x1: "6", y1: "6", x2: "18", y2: "18" })
                  ]
                }
              )
            }
          )
        ]
      }
    );
  }
);
Tag.displayName = "Tag";

// src/components/TagPicker.tsx
import { useState as useState5, useMemo as useMemo4, useCallback as useCallback5 } from "react";
import { Button as Button10 } from "react-aria-components";
import { jsx as jsx105, jsxs as jsxs90 } from "react/jsx-runtime";
function toMultiSelectOption(tag) {
  return { id: tag.id, label: tag.name, color: tag.color, name: tag.name };
}
function toTagOption(option) {
  return { id: option.id, name: option.name, color: option.color };
}
function TagPicker({
  label,
  description,
  placeholder = "Add tags...",
  tags,
  selectedTags,
  onChange,
  onCreateTag,
  isLoading = false,
  isDisabled = false,
  className
}) {
  const [inputValue, setInputValue] = useState5("");
  const [isCreating, setIsCreating] = useState5(false);
  const [newTagColor, setNewTagColor] = useState5("blue");
  const items = useMemo4(() => tags.map(toMultiSelectOption), [tags]);
  const value = useMemo4(() => selectedTags.map(toMultiSelectOption), [selectedTags]);
  const handleChange = useCallback5(
    (selected) => {
      onChange(selected.map(toTagOption));
    },
    [onChange]
  );
  const canCreateTag = useMemo4(() => {
    if (!onCreateTag || !inputValue.trim()) return false;
    const lower = inputValue.toLowerCase().trim();
    return !tags.some((tag) => tag.name.toLowerCase() === lower);
  }, [onCreateTag, inputValue, tags]);
  const handleCreate = useCallback5(async () => {
    if (!onCreateTag || !canCreateTag) return;
    setIsCreating(true);
    try {
      const newTag = await onCreateTag(inputValue.trim(), newTagColor);
      onChange([...selectedTags, newTag]);
      setInputValue("");
      setNewTagColor("blue");
    } finally {
      setIsCreating(false);
    }
  }, [onCreateTag, canCreateTag, inputValue, newTagColor, onChange, selectedTags]);
  const popoverFooter = canCreateTag ? /* @__PURE__ */ jsxs90("div", { className: "border-t border-border p-2", children: [
    /* @__PURE__ */ jsxs90("div", { className: "flex items-center gap-2 mb-2", children: [
      /* @__PURE__ */ jsx105("span", { className: "text-sm text-muted-foreground", children: "Create:" }),
      /* @__PURE__ */ jsx105(TagBadge, { name: inputValue.trim(), color: newTagColor, size: "sm" })
    ] }),
    /* @__PURE__ */ jsxs90("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx105("div", { className: "flex gap-1", children: TAG_COLOR_OPTIONS.slice(0, 5).map((option) => /* @__PURE__ */ jsx105(
        "button",
        {
          type: "button",
          onClick: () => setNewTagColor(option.id),
          className: cn(
            "w-5 h-5 rounded-full transition-all",
            TAG_COLORS[option.id].dot,
            newTagColor === option.id && "ring-2 ring-offset-2 ring-primary"
          ),
          title: option.label
        },
        option.id
      )) }),
      /* @__PURE__ */ jsx105(
        Button10,
        {
          onPress: handleCreate,
          isDisabled: isCreating,
          className: cn(
            "ml-auto px-2 py-1 text-xs rounded-md",
            "bg-primary text-primary-foreground",
            "hover:bg-primary/90",
            "disabled:opacity-50"
          ),
          children: isCreating ? "Creating..." : "Create"
        }
      )
    ] })
  ] }) : void 0;
  return /* @__PURE__ */ jsx105(
    MultiSelectComboBox,
    {
      label,
      description,
      placeholder,
      items,
      value,
      onChange: handleChange,
      isLoading,
      isDisabled,
      className,
      inputValue,
      onInputChange: setInputValue,
      getTagColor: (item) => item.color,
      renderTag: (item, onRemove) => /* @__PURE__ */ jsx105(TagBadge, { name: item.name, color: item.color, size: "sm", onRemove }),
      renderItem: (item) => /* @__PURE__ */ jsx105(TagBadge, { name: item.name, color: item.color, size: "sm" }),
      emptyState: !canCreateTag ? /* @__PURE__ */ jsx105("div", { className: "px-3 py-2 text-sm text-muted-foreground", children: inputValue ? "No matching tags" : "No more tags available" }) : void 0,
      popoverFooter
    }
  );
}

// src/utils/tokenize.ts
function tokenize(value, patterns) {
  if (!value) return [];
  const matches = [];
  for (const pattern of patterns) {
    const regex = new RegExp(pattern.regex.source, pattern.regex.flags.includes("g") ? pattern.regex.flags : pattern.regex.flags + "g");
    let match;
    while ((match = regex.exec(value)) !== null) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        text: match[0],
        type: pattern.type
      });
    }
  }
  matches.sort((a, b) => a.start - b.start);
  const segments = [];
  let cursor = 0;
  for (const match of matches) {
    if (match.start < cursor) continue;
    if (match.start > cursor) {
      segments.push({
        text: value.slice(cursor, match.start),
        type: "plain",
        start: cursor,
        end: match.start
      });
    }
    segments.push({
      text: match.text,
      type: match.type,
      start: match.start,
      end: match.end
    });
    cursor = match.end;
  }
  if (cursor < value.length) {
    segments.push({
      text: value.slice(cursor),
      type: "plain",
      start: cursor,
      end: value.length
    });
  }
  return segments;
}

// src/theme/tokenColors.ts
var TOKEN_COLORS = {
  variable: {
    bg: "bg-emerald-100 dark:bg-emerald-900/30",
    text: "text-emerald-700 dark:text-emerald-300",
    border: "border-emerald-200/60 dark:border-emerald-700/60"
  },
  macro: {
    bg: "bg-blue-100 dark:bg-blue-900/30",
    text: "text-blue-700 dark:text-blue-300",
    border: "border-blue-200/60 dark:border-blue-700/60"
  }
};

// src/components/rich-text-editor/RichTextEditor.tsx
import React75 from "react";
import { EditorContent } from "@tiptap/react";

// src/components/rich-text-editor/TokenNodeView.tsx
import React74 from "react";
import { NodeViewWrapper } from "@tiptap/react";
import { jsx as jsx106 } from "react/jsx-runtime";
var TokenNodeContext = React74.createContext({ tokenColors: {} });
var TokenNodeView = ({ node, editor, getPos }) => {
  const { text, tokenType } = node.attrs;
  const { tokenColors, renderToken } = React74.useContext(TokenNodeContext);
  const colors = tokenColors[tokenType];
  const handleDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = getPos();
    if (pos === void 0) return;
    const end = pos + node.nodeSize;
    editor.chain().focus().deleteRange({ from: pos, to: end }).insertContentAt(pos, text).run();
    const parenOpen = text.lastIndexOf("(");
    const parenClose = text.lastIndexOf(")");
    if (parenOpen !== -1 && parenClose !== -1 && parenClose > parenOpen) {
      const argsStart = pos + parenOpen + 1;
      const argsEnd = pos + parenClose;
      if (argsStart < argsEnd) {
        editor.commands.setTextSelection({ from: argsStart, to: argsEnd });
      } else {
        editor.commands.setTextSelection(argsStart);
      }
    }
  };
  if (renderToken && colors) {
    return /* @__PURE__ */ jsx106(NodeViewWrapper, { as: "span", className: "inline", onDoubleClick: handleDoubleClick, children: renderToken({ text, tokenType }, colors) });
  }
  return /* @__PURE__ */ jsx106(NodeViewWrapper, { as: "span", className: "inline", onDoubleClick: handleDoubleClick, children: /* @__PURE__ */ jsx106(
    "span",
    {
      className: cn(
        "inline-flex items-center px-1 py-0.5 rounded text-xs font-medium font-mono border",
        "cursor-default select-none",
        colors?.bg,
        colors?.text,
        colors?.border,
        !colors && "bg-muted text-muted-foreground border-border"
      ),
      children: text
    }
  ) });
};

// src/components/rich-text-editor/useRichTextEditor.ts
import { useEffect as useEffect2, useRef as useRef2, useMemo as useMemo5 } from "react";
import { useEditor } from "@tiptap/react";
import Document2 from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text10 from "@tiptap/extension-text";
import History from "@tiptap/extension-history";
import Placeholder from "@tiptap/extension-placeholder";

// src/components/rich-text-editor/tokenNode.ts
import { Node as Node2, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
var TokenNode = Node2.create({
  name: "token",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,
  addAttributes() {
    return {
      text: { default: "" },
      tokenType: { default: "" }
    };
  },
  parseHTML() {
    return [{ tag: "span[data-token]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes({ "data-token": "" }, HTMLAttributes)];
  },
  renderText({ node }) {
    return node.attrs.text;
  },
  addNodeView() {
    return ReactNodeViewRenderer(TokenNodeView);
  }
});

// src/components/rich-text-editor/singleLineDoc.ts
import { Extension } from "@tiptap/core";
import Document from "@tiptap/extension-document";
var SingleLineDocument = Document.extend({
  content: "paragraph"
});
var SingleLineKeymap = Extension.create({
  name: "singleLineKeymap",
  addKeyboardShortcuts() {
    return {
      Enter: () => true,
      "Shift-Enter": () => true
    };
  }
});

// src/components/rich-text-editor/triggerExtension.ts
import { Extension as Extension2 } from "@tiptap/core";
import Suggestion from "@tiptap/suggestion";
function isPlainTextInsertion(result) {
  return "plainText" in result;
}
function resolveTrigger(triggerSource) {
  const trigger = typeof triggerSource === "function" ? triggerSource() : triggerSource;
  if (!trigger) {
    throw new Error("Suggestion trigger is no longer available");
  }
  return trigger;
}
function getSuggestionItems(triggerSource, query) {
  return resolveTrigger(triggerSource).items(query);
}
function getSuggestionInsertResult(triggerSource, item) {
  return resolveTrigger(triggerSource).onSelect(item);
}
function createTriggerExtension(triggerSource, index) {
  const initialTrigger = resolveTrigger(triggerSource);
  return Extension2.create({
    name: `suggestion-${index}`,
    addProseMirrorPlugins() {
      return [
        Suggestion({
          editor: this.editor,
          char: initialTrigger.char,
          allowSpaces: initialTrigger.allowSpaces ?? false,
          allowedPrefixes: null,
          items: ({ query }) => {
            const result = getSuggestionItems(triggerSource, query);
            if (Array.isArray(result)) return result;
            return result;
          },
          render: () => {
            const renderer = resolveTrigger(triggerSource).render();
            return {
              onStart: (props) => {
                renderer.onStart({
                  query: props.query,
                  items: props.items,
                  command: props.command,
                  clientRect: props.clientRect ?? null
                });
              },
              onUpdate: (props) => {
                renderer.onUpdate({
                  query: props.query,
                  items: props.items,
                  command: props.command,
                  clientRect: props.clientRect ?? null
                });
              },
              onKeyDown: (props) => renderer.onKeyDown({ event: props.event }),
              onExit: () => renderer.onExit()
            };
          },
          command: ({ editor, range, props: item }) => {
            const result = getSuggestionInsertResult(triggerSource, item);
            if (isPlainTextInsertion(result)) {
              editor.chain().focus().deleteRange(range).insertContent(result.plainText).run();
              const { to } = editor.state.selection;
              editor.commands.setTextSelection(to - result.cursorOffset);
            } else {
              editor.chain().focus().deleteRange(range).insertContent({ type: "token", attrs: result }).run();
            }
          }
        })
      ];
    }
  });
}

// src/components/rich-text-editor/serialization.ts
function stringToDoc(value, tokenPatterns, singleLine = false) {
  if (!value) {
    return { type: "doc", content: [{ type: "paragraph" }] };
  }
  const normalized = singleLine ? value.replace(/\n/g, " ") : value;
  const lines = normalized.split("\n");
  const paragraphs = lines.map((line) => {
    if (!line) return { type: "paragraph" };
    const segments = tokenize(line, tokenPatterns);
    const content = segments.map((seg) => {
      if (seg.type === "plain") {
        return { type: "text", text: seg.text };
      }
      return {
        type: "token",
        attrs: { text: seg.text, tokenType: seg.type }
      };
    });
    return { type: "paragraph", content };
  });
  return { type: "doc", content: paragraphs };
}
function docToString(doc) {
  if (!doc.content?.length) return "";
  return doc.content.map((paragraph) => {
    if (!paragraph.content?.length) return "";
    return paragraph.content.map((node) => {
      if (node.type === "token") return node.attrs?.text ?? "";
      return node.text ?? "";
    }).join("");
  }).join("\n");
}

// src/components/rich-text-editor/useRichTextEditor.ts
function useRichTextEditor({
  value,
  onChange,
  tokenPatterns,
  triggers = [],
  multiline = false,
  placeholder,
  isDisabled = false,
  isReadOnly = false,
  onFocus,
  onBlur,
  onKeyDown
}) {
  const lastStringRef = useRef2(value);
  const isSyncing = useRef2(false);
  const isUserEditRef = useRef2(false);
  const valueRef = useRef2(value);
  valueRef.current = value;
  const onChangeRef = useRef2(onChange);
  onChangeRef.current = onChange;
  const onFocusRef = useRef2(onFocus);
  onFocusRef.current = onFocus;
  const onBlurRef = useRef2(onBlur);
  onBlurRef.current = onBlur;
  const onKeyDownRef = useRef2(onKeyDown);
  onKeyDownRef.current = onKeyDown;
  const tokenPatternsRef = useRef2(tokenPatterns);
  tokenPatternsRef.current = tokenPatterns;
  const triggersRef = useRef2(triggers);
  triggersRef.current = triggers;
  const triggerCount = triggers.length;
  const triggerChars = triggers.map((t) => t.char).join(",");
  const extensions = useMemo5(() => {
    const exts = [
      multiline ? Document2 : SingleLineDocument,
      Paragraph,
      Text10,
      TokenNode,
      History
    ];
    if (!multiline) {
      exts.push(SingleLineKeymap);
    }
    if (placeholder) {
      exts.push(
        Placeholder.configure({
          placeholder: ({ editor: editor2 }) => {
            if (editor2.state.doc.textContent.length > 0) return "";
            return placeholder;
          },
          showOnlyWhenEditable: true
        })
      );
    }
    triggersRef.current.forEach((_, i) => {
      exts.push(createTriggerExtension(() => triggersRef.current[i], i));
    });
    return exts;
  }, [multiline, placeholder, triggerCount, triggerChars]);
  const editor = useEditor(
    {
      extensions,
      // Use ref to always get latest value when editor is (re)created
      content: stringToDoc(valueRef.current, tokenPatternsRef.current, !multiline),
      editable: !isDisabled && !isReadOnly,
      editorProps: {
        attributes: {
          "aria-multiline": multiline ? "true" : "false",
          role: "textbox"
        },
        handleKeyDown: (_view, event) => {
          onKeyDownRef.current?.(event);
          return event.defaultPrevented;
        },
        handlePaste: (_view, _event) => {
          return false;
        }
      },
      onCreate: ({ editor: ed }) => {
        lastStringRef.current = docToString(ed.getJSON());
        isSyncing.current = false;
        isUserEditRef.current = false;
      },
      onUpdate: ({ editor: ed }) => {
        if (isSyncing.current) return;
        const serialized = docToString(ed.getJSON());
        if (serialized === lastStringRef.current) return;
        lastStringRef.current = serialized;
        isUserEditRef.current = true;
        onChangeRef.current(serialized);
      },
      onFocus: () => onFocusRef.current?.(),
      onBlur: ({ editor: ed }) => {
        const currentString = docToString(ed.getJSON());
        const retokenized = stringToDoc(currentString, tokenPatternsRef.current, !multiline);
        const currentJSON = JSON.stringify(ed.getJSON());
        const newJSON = JSON.stringify(retokenized);
        if (currentJSON !== newJSON) {
          isSyncing.current = true;
          lastStringRef.current = currentString;
          ed.commands.setContent(retokenized);
          queueMicrotask(() => {
            isSyncing.current = false;
          });
        }
        onBlurRef.current?.();
      }
    },
    [extensions]
  );
  useEffect2(() => {
    if (!editor || editor.isDestroyed) return;
    if (value === lastStringRef.current) return;
    isSyncing.current = true;
    lastStringRef.current = value;
    const doc = stringToDoc(value, tokenPatterns, !multiline);
    editor.commands.setContent(doc);
    queueMicrotask(() => {
      isSyncing.current = false;
    });
  }, [editor, value, tokenPatterns, multiline]);
  useEffect2(() => {
    if (!editor || editor.isDestroyed) return;
    editor.setEditable(!isDisabled && !isReadOnly);
  }, [editor, isDisabled, isReadOnly]);
  return editor;
}

// src/components/rich-text-editor/RichTextEditor.tsx
import { jsx as jsx107, jsxs as jsxs91 } from "react/jsx-runtime";
var editorVariants = {
  default: "bg-input border-border",
  filled: "bg-muted border-border",
  ghost: "bg-transparent border-transparent shadow-none"
};
var RichTextEditor = React75.forwardRef(
  ({
    value,
    onChange,
    tokenPatterns,
    tokenColors,
    renderToken,
    triggers,
    multiline = false,
    rows = 4,
    variant = "default",
    label,
    description,
    errorMessage,
    placeholder,
    isDisabled = false,
    isReadOnly = false,
    isRequired = false,
    autoFocus = false,
    className,
    onBlur,
    onFocus,
    onKeyDown,
    "aria-label": ariaLabel
  }, ref) => {
    const editor = useRichTextEditor({
      value,
      onChange,
      tokenPatterns,
      triggers,
      multiline,
      placeholder,
      isDisabled,
      isReadOnly,
      onFocus,
      onBlur,
      onKeyDown
    });
    React75.useImperativeHandle(ref, () => ({
      focus: () => editor?.commands.focus()
    }), [editor]);
    React75.useEffect(() => {
      if (autoFocus && editor && !editor.isDestroyed) {
        const frame = requestAnimationFrame(() => {
          if (!editor.isDestroyed) {
            editor.commands.focus();
          }
        });
        return () => cancelAnimationFrame(frame);
      }
    }, [autoFocus, editor]);
    const contextValue = React75.useMemo(
      () => ({ tokenColors, renderToken }),
      [tokenColors, renderToken]
    );
    return /* @__PURE__ */ jsxs91("div", { className: cn("flex flex-col gap-1.5", className), children: [
      label && /* @__PURE__ */ jsxs91("label", { className: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70", children: [
        label,
        isRequired && /* @__PURE__ */ jsx107("span", { className: "text-destructive ml-0.5", children: "*" })
      ] }),
      /* @__PURE__ */ jsx107(TokenNodeContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsx107(
        EditorContent,
        {
          editor,
          "aria-label": ariaLabel,
          className: cn(
            "rte-container",
            "w-full rounded-md text-sm transition-colors",
            variant !== "ghost" && "border shadow-sm",
            editorVariants[variant],
            multiline ? "px-3 py-2" : "min-h-9 px-3 py-1.5 max-h-24 overflow-y-auto",
            isDisabled && "cursor-not-allowed opacity-50",
            errorMessage && "border-destructive",
            "[&_.ProseMirror]:outline-none [&_.ProseMirror]:w-full [&_.ProseMirror]:h-full",
            multiline ? "[&_.ProseMirror]:min-h-[var(--rte-min-height)]" : "[&_.ProseMirror]:min-h-[1.5rem]",
            "[&_.ProseMirror_p]:m-0",
            "[&_.ProseMirror_.is-empty::before]:text-muted-foreground [&_.ProseMirror_.is-empty::before]:content-[attr(data-placeholder)] [&_.ProseMirror_.is-empty::before]:float-left [&_.ProseMirror_.is-empty::before]:h-0 [&_.ProseMirror_.is-empty::before]:pointer-events-none"
          ),
          style: multiline ? { "--rte-min-height": `${rows * 1.5}rem` } : void 0
        }
      ) }),
      description && /* @__PURE__ */ jsx107("p", { className: "text-xs text-muted-foreground", children: description }),
      errorMessage && /* @__PURE__ */ jsx107("p", { className: "text-xs text-destructive", children: errorMessage })
    ] });
  }
);
RichTextEditor.displayName = "RichTextEditor";

// src/components/Table.tsx
import React76 from "react";
import {
  Table as AriaTable,
  TableHeader as AriaTableHeader,
  TableBody as AriaTableBody,
  Row as AriaRow,
  Cell as AriaCell,
  Column as AriaColumn
} from "react-aria-components";
import {
  useTable,
  stockFeatures,
  tableFeatures,
  createFilteredRowModel,
  createSortedRowModel,
  createPaginatedRowModel,
  createExpandedRowModel,
  filterFn_includesString,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  flexRender
} from "@tanstack/react-table";
import { CaretUp as CaretUp3, CaretDown as CaretDown9, CaretUpDown as CaretUpDown2 } from "@phosphor-icons/react";
import { Fragment as Fragment23, jsx as jsx108, jsxs as jsxs92 } from "react/jsx-runtime";
var dataTableFeatures = tableFeatures({
  ...stockFeatures,
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  expandedRowModel: createExpandedRowModel(),
  filterFns: { includesString: filterFn_includesString },
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
    text: sortFn_text
  }
});
function DataTable({
  columns,
  data,
  fullHeight = false,
  containerClassName,
  stickyHeader = false,
  searchable = false,
  searchPlaceholder = "Search...",
  searchValue,
  onSearchChange,
  pageSize = 10,
  className,
  onRowClick,
  getRowHref,
  "aria-label": ariaLabel = "Data table",
  initialSorting = [],
  getRowId,
  manualPagination = false,
  pageCount,
  pageIndex = 0,
  onPaginationChange,
  serverPagination,
  serverSorting,
  enableRowSelection = false,
  rowSelection,
  onRowSelectionChange,
  rowClassName,
  expandableRows,
  toolbarContent,
  preservePageOnDataChange = false
}) {
  const [sorting, setSorting] = React76.useState(initialSorting);
  const [columnFilters, setColumnFilters] = React76.useState([]);
  const [globalFilter, setGlobalFilter] = React76.useState("");
  const serverSearchEnabled = typeof onSearchChange === "function";
  const [internalRowSelection, setInternalRowSelection] = React76.useState({});
  const isRowSelectionControlled = typeof rowSelection !== "undefined";
  const resolvedRowSelection = isRowSelectionControlled ? rowSelection : internalRowSelection;
  const [internalExpanded, setInternalExpanded] = React76.useState(
    expandableRows?.defaultExpanded ?? {}
  );
  const isExpandedControlled = expandableRows && typeof expandableRows.expanded !== "undefined";
  const resolvedExpanded = isExpandedControlled ? expandableRows.expanded : internalExpanded;
  const serverPaginationEnabled = Boolean(serverPagination);
  const effectiveManualPagination = serverPaginationEnabled || manualPagination;
  const serverSortingEnabled = Boolean(serverSorting);
  const effectiveManualSorting = serverSortingEnabled;
  React76.useEffect(() => {
    if (manualPagination && serverPaginationEnabled) {
      console.warn(
        "DataTable: Both serverPagination and manualPagination props were provided. serverPagination takes precedence."
      );
    }
  }, [manualPagination, serverPaginationEnabled]);
  const resolvedPageIndex = React76.useMemo(() => {
    if (serverPaginationEnabled) {
      return Math.max(0, serverPagination.page - 1);
    }
    return pageIndex;
  }, [serverPaginationEnabled, serverPagination, pageIndex]);
  const resolvedPageSize = React76.useMemo(() => {
    if (serverPaginationEnabled) {
      return serverPagination.pageSize;
    }
    return pageSize;
  }, [serverPaginationEnabled, serverPagination, pageSize]);
  const resolvedPageCount = React76.useMemo(() => {
    if (serverPaginationEnabled) {
      return serverPagination.pageCount;
    }
    return pageCount;
  }, [serverPaginationEnabled, serverPagination, pageCount]);
  React76.useEffect(() => {
    if (!serverPagination || serverPagination.isLoading) return;
    const { page, pageCount: pageCount2, onPageChange } = serverPagination;
    if (pageCount2 > 0 && page > pageCount2) {
      onPageChange(pageCount2);
    } else if (pageCount2 === 0 && page !== 1) {
      onPageChange(1);
    }
  }, [
    serverPagination?.page,
    serverPagination?.pageCount,
    serverPagination?.isLoading,
    serverPagination?.onPageChange
  ]);
  const pageSizeSelectItems = React76.useMemo(() => {
    if (!serverPagination?.pageSizeOptions?.length) return [];
    return serverPagination.pageSizeOptions.map((size) => ({
      id: String(size),
      label: `${size} rows`
    }));
  }, [serverPagination]);
  const selectionColumn = React76.useMemo(() => {
    if (!enableRowSelection) return null;
    return {
      id: "__selection__",
      enableSorting: false,
      enableColumnFilter: false,
      // minWidth must fit the 16px checkbox + padding — with flex 0.1 and no
      // floor the cell shrinks below the box and overflow-hidden clips it
      // into "dashes" in tight tables.
      meta: { flex: 0.1, maxWidth: 56, minWidth: 28 },
      // The wrapper div's slot="selection" is only a DOM marker for the row's
      // proximity hit-test. The Checkbox opts out of react-aria's table
      // selection context (slot={null}): that context has no RAC selection
      // mode behind it here, so it renders the input disabled. Selection state
      // lives in TanStack, so the Checkbox is driven by isSelected/onChange.
      header: ({ table: table2 }) => /* @__PURE__ */ jsx108(
        "div",
        {
          className: "px-0.5 flex items-center justify-center w-full h-full",
          slot: "selection",
          onClick: (e) => e.stopPropagation(),
          children: /* @__PURE__ */ jsx108(
            Checkbox,
            {
              "aria-label": "Select all rows on this page",
              isSelected: table2.getIsAllPageRowsSelected(),
              isIndeterminate: table2.getIsSomePageRowsSelected() && !table2.getIsAllPageRowsSelected(),
              slot: null,
              onChange: (checked) => table2.toggleAllPageRowsSelected(checked)
            }
          )
        }
      ),
      cell: ({ row }) => /* @__PURE__ */ jsx108(
        "div",
        {
          className: "px-0.5 flex items-center justify-center w-full h-full",
          slot: "selection",
          onClick: (e) => e.stopPropagation(),
          children: /* @__PURE__ */ jsx108(
            Checkbox,
            {
              "aria-label": "Select row",
              isSelected: row.getIsSelected(),
              isIndeterminate: row.getIsSomeSelected(),
              slot: null,
              onChange: (checked) => row.toggleSelected(checked)
            }
          )
        }
      )
    };
  }, [enableRowSelection]);
  React76.useEffect(() => {
    if (serverSearchEnabled) {
      setGlobalFilter(searchValue ?? "");
    }
  }, [serverSearchEnabled, searchValue]);
  React76.useEffect(() => {
    if (!serverSortingEnabled || !serverSorting) return;
    const newSorting = [];
    if (serverSorting.sortBy && serverSorting.sortOrder) {
      newSorting.push({
        id: serverSorting.sortBy,
        desc: serverSorting.sortOrder === "desc"
      });
    }
    setSorting(newSorting);
  }, [serverSortingEnabled, serverSorting?.sortBy, serverSorting?.sortOrder]);
  const previousSortingRef = React76.useRef(sorting);
  React76.useEffect(() => {
    if (!serverSortingEnabled || !serverSorting) return;
    const currentPropSorting = [];
    if (serverSorting.sortBy && serverSorting.sortOrder) {
      currentPropSorting.push({
        id: serverSorting.sortBy,
        desc: serverSorting.sortOrder === "desc"
      });
    }
    const sortingMatchesProps = sorting.length === currentPropSorting.length && sorting.every(
      (s, i) => s.id === currentPropSorting[i]?.id && s.desc === currentPropSorting[i]?.desc
    );
    if (sorting !== previousSortingRef.current && !sortingMatchesProps) {
      const sortBy = sorting[0]?.id;
      const sortOrder = sorting[0] ? sorting[0].desc ? "desc" : "asc" : void 0;
      serverSorting.onSortChange(sortBy, sortOrder);
    }
    previousSortingRef.current = sorting;
  }, [sorting, serverSortingEnabled, serverSorting]);
  const getColumnLayoutStyle = React76.useCallback(
    (meta) => {
      const flex = meta.flex ?? 1;
      const maxWidth = typeof meta.maxWidth === "number" && Number.isFinite(meta.maxWidth) ? meta.maxWidth : void 0;
      const minWidth = typeof meta.minWidth === "number" && Number.isFinite(meta.minWidth) ? meta.minWidth : 0;
      return {
        display: "flex",
        flex: `${flex} 1 0%`,
        minWidth,
        maxWidth,
        alignItems: "center"
      };
    },
    []
  );
  const expanderPlacement = expandableRows?.expanderColumnPlacement ?? "start";
  const expanderColumn = React76.useMemo(() => {
    if (!expandableRows || expanderPlacement === "none") return null;
    return {
      id: "__expander__",
      enableSorting: false,
      enableColumnFilter: false,
      meta: { flex: 0.1, maxWidth: 44, minWidth: 36 },
      header: () => null,
      cell: ({ row }) => {
        if (!row.getCanExpand()) {
          return /* @__PURE__ */ jsx108("div", { className: "px-0.5 flex items-center justify-center w-full", slot: "expander" });
        }
        return /* @__PURE__ */ jsx108("div", { className: "px-0.5 flex items-center justify-center w-full", slot: "expander", children: /* @__PURE__ */ jsx108(
          Button,
          {
            variant: "ghost",
            size: "sm",
            iconOnly: true,
            "data-expander-trigger": true,
            "aria-label": row.getIsExpanded() ? "Collapse row" : "Expand row",
            "aria-expanded": row.getIsExpanded(),
            onPress: () => row.toggleExpanded(),
            children: /* @__PURE__ */ jsx108(
              CaretDown9,
              {
                size: 16,
                className: cn(
                  "text-muted-foreground transition-transform",
                  row.getIsExpanded() ? "rotate-0" : "-rotate-90"
                )
              }
            )
          }
        ) });
      }
    };
  }, [expandableRows, expanderPlacement]);
  const composedColumns = React76.useMemo(() => {
    const base = [...columns];
    const withSelection = selectionColumn ? [selectionColumn, ...base] : base;
    if (!expanderColumn) return withSelection;
    if (expanderPlacement === "afterSelection" && selectionColumn) {
      return [selectionColumn, expanderColumn, ...base];
    }
    return [expanderColumn, ...selectionColumn ? [selectionColumn] : [], ...base];
  }, [columns, selectionColumn, expanderColumn, expanderPlacement]);
  const normalizeExpandedState = React76.useCallback(
    (next) => {
      if (!expandableRows?.single) return next;
      if (next === true) return next;
      const nextRecord = typeof next === "object" && next !== null ? next : {};
      const keys = Object.keys(nextRecord).filter((k) => nextRecord[k]);
      if (keys.length === 0) return {};
      if (keys.length === 1) return { [keys[0]]: true };
      const currentRecord = typeof resolvedExpanded === "object" && resolvedExpanded !== null ? resolvedExpanded : {};
      const newKey = keys.find((k) => !currentRecord[k]) ?? keys[keys.length - 1];
      return { [newKey]: true };
    },
    [expandableRows?.single, resolvedExpanded]
  );
  const expandOnRowClick = Boolean(expandableRows && expanderPlacement === "none");
  const handleExpandedChange = React76.useCallback(
    (updater) => {
      const next = typeof updater === "function" ? updater(resolvedExpanded) : updater;
      const normalized = normalizeExpandedState(next);
      if (expandableRows?.onExpandedChange) {
        expandableRows.onExpandedChange(normalized);
      } else {
        setInternalExpanded(normalized);
      }
    },
    [expandableRows, normalizeExpandedState, resolvedExpanded]
  );
  const table = useTable({
    features: dataTableFeatures,
    data,
    columns: composedColumns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      ...serverPaginationEnabled ? {
        pagination: {
          pageIndex: resolvedPageIndex,
          pageSize: resolvedPageSize
        }
      } : {},
      ...serverSortingEnabled ? {
        sorting
      } : {},
      ...enableRowSelection ? {
        rowSelection: resolvedRowSelection
      } : {},
      ...expandableRows ? {
        expanded: resolvedExpanded
      } : {}
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize
      }
    },
    getRowId: getRowId ? (row) => getRowId(row) : void 0,
    manualPagination: effectiveManualPagination,
    pageCount: resolvedPageCount,
    // undefined restores table-core's own `!manualPagination` default; only
    // preservePageOnDataChange opts out, with the effects below restoring
    // reset-on-filter/sort for that case.
    autoResetPageIndex: preservePageOnDataChange ? false : void 0,
    manualSorting: effectiveManualSorting,
    enableSorting: true,
    getRowCanExpand: expandableRows ? expandableRows.getIsRowExpandable ?? (() => true) : void 0,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: onRowSelectionChange ?? setInternalRowSelection,
    onExpandedChange: expandableRows ? handleExpandedChange : void 0
  });
  const clientPageCount = table.getPageCount();
  const clientPageIndex = table.state.pagination.pageIndex;
  React76.useLayoutEffect(() => {
    if (!preservePageOnDataChange || effectiveManualPagination) return;
    if (clientPageIndex > 0 && clientPageIndex >= clientPageCount) {
      table.setPageIndex(Math.max(0, clientPageCount - 1));
    }
  }, [
    preservePageOnDataChange,
    effectiveManualPagination,
    clientPageCount,
    clientPageIndex,
    table
  ]);
  const hasMountedRef = React76.useRef(false);
  React76.useEffect(() => {
    if (!preservePageOnDataChange || effectiveManualPagination) return;
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    table.setPageIndex(0);
  }, [
    globalFilter,
    columnFilters,
    sorting,
    preservePageOnDataChange,
    effectiveManualPagination,
    table
  ]);
  const headerColumns = table.getHeaderGroups();
  const rowModel = table.getRowModel();
  return /* @__PURE__ */ jsxs92("div", { className: cn("space-y-4", className, fullHeight && "h-full min-h-0 flex flex-col"), children: [
    (searchable || toolbarContent) && /* @__PURE__ */ jsxs92("div", { className: "space-y-3 py-4", children: [
      searchable && /* @__PURE__ */ jsx108("div", { className: "flex items-center gap-3", children: /* @__PURE__ */ jsx108(
        TextField,
        {
          placeholder: searchPlaceholder,
          value: serverSearchEnabled ? searchValue ?? "" : typeof globalFilter === "string" ? globalFilter : "",
          onChange: (value) => {
            const next = String(value);
            if (serverSearchEnabled) {
              onSearchChange?.(next);
            } else {
              setGlobalFilter(next);
            }
          },
          className: "max-w-md"
        }
      ) }),
      toolbarContent
    ] }),
    /* @__PURE__ */ jsxs92("div", { className: cn(fullHeight && "flex-1 min-h-0 flex flex-col"), children: [
      /* @__PURE__ */ jsx108(
        "div",
        {
          className: cn(
            // Square hairline frame; ink is reserved for structure (topbar, buttons, dialogs).
            "rounded-none border border-border bg-card overflow-auto w-full",
            fullHeight && "flex-1 min-h-0",
            containerClassName
          ),
          children: /* @__PURE__ */ jsxs92(
            AriaTable,
            {
              className: "w-full caption-bottom text-sm",
              "aria-label": ariaLabel,
              onRowAction: onRowClick || expandOnRowClick ? () => {
              } : void 0,
              children: [
                /* @__PURE__ */ jsx108(
                  AriaTableHeader,
                  {
                    className: cn(
                      // RAC wraps Column children in an implicit <tr>; style that row,
                      // not the <thead>, so the columns lay out horizontally.
                      "[&>tr]:flex [&>tr]:border-b [&>tr]:border-b-border [&>tr]:transition-colors [&>tr]:hover:bg-secondary",
                      stickyHeader && "sticky top-0 z-[1] bg-card"
                    ),
                    children: (() => {
                      const leafHeaders = headerColumns[headerColumns.length - 1]?.headers ?? [];
                      const firstContentColumnIndex = leafHeaders.findIndex(
                        (h) => h.column.id !== "__expander__" && h.column.id !== "__selection__"
                      );
                      return leafHeaders.map((header, i) => {
                        const meta = header.column.columnDef.meta ?? {};
                        const isSelectionColumn = header.column.id === "__selection__";
                        return /* @__PURE__ */ jsx108(
                          AriaColumn,
                          {
                            className: cn(
                              // Header typography (mono 11px uppercase) and 40px height are owned by
                              // cofactor/data-table.css, which also styles raw <table> markup.
                              "px-2 text-left align-middle overflow-hidden",
                              "[&:has([role=checkbox])]:px-0",
                              header.column.getCanSort() && "cursor-pointer select-none",
                              i === firstContentColumnIndex && "pl-4"
                            ),
                            allowsSorting: header.column.getCanSort(),
                            isRowHeader: i === 0,
                            style: getColumnLayoutStyle(meta),
                            children: /* @__PURE__ */ jsxs92(
                              "div",
                              {
                                className: cn(
                                  "flex items-center min-w-0 flex-1",
                                  isSelectionColumn ? "justify-center" : "gap-2"
                                ),
                                onClick: header.column.getCanSort() ? header.column.getToggleSortingHandler() : void 0,
                                role: header.column.getCanSort() && !isSelectionColumn ? "button" : void 0,
                                children: [
                                  /* @__PURE__ */ jsx108("span", { className: "truncate min-w-0 block", children: header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext()) }),
                                  header.column.getCanSort() && /* @__PURE__ */ jsx108("span", { className: "inline-flex items-center shrink-0", children: header.column.getIsSorted() === "asc" ? /* @__PURE__ */ jsx108(CaretUp3, { size: 14, className: "text-foreground" }) : header.column.getIsSorted() === "desc" ? /* @__PURE__ */ jsx108(CaretDown9, { size: 14, className: "text-foreground" }) : /* @__PURE__ */ jsx108(CaretUpDown2, { size: 14, className: "text-muted-foreground" }) })
                                ]
                              }
                            )
                          },
                          header.id
                        );
                      });
                    })()
                  }
                ),
                /* @__PURE__ */ jsx108(AriaTableBody, { className: "[&_tr:last-child]:border-0", children: rowModel.rows?.length ? rowModel.rows.map((row) => {
                  const href = getRowHref?.(row.original);
                  return /* @__PURE__ */ jsxs92(React76.Fragment, { children: [
                    /* @__PURE__ */ jsx108(
                      AriaRow,
                      {
                        id: row.id,
                        "data-row-type": "data",
                        href,
                        style: {
                          display: "flex",
                          backgroundColor: "var(--row-bg, transparent)",
                          // min-height mirrors `[data-row-type='data']` in
                          // packages/ui/src/styles/cofactor/data-table.css — this
                          // is the sole emitter of data-row-type, no raw-markup
                          // rule to keep in step with.
                          minHeight: 52
                        },
                        "data-row-selected": row.getIsSelected() ? "" : void 0,
                        className: cn(
                          "border-b transition-colors hover:bg-secondary",
                          (onRowClick || href) && "cursor-pointer",
                          rowClassName?.(row)
                        ),
                        onPress: (event) => {
                          const target = event.target;
                          if (target?.closest("[data-expander-trigger]")) return;
                          if (!href && enableRowSelection && target) {
                            const targetRect = target.getBoundingClientRect();
                            const clickX = targetRect.left + event.x;
                            const clickY = targetRect.top + event.y;
                            const rowEl = target.closest('[role="row"]');
                            const selectionArea = rowEl?.querySelector(
                              '[slot="selection"]'
                            );
                            if (selectionArea) {
                              const rect = selectionArea.getBoundingClientRect();
                              const centerX = rect.left + rect.width / 2;
                              const centerY = rect.top + rect.height / 2;
                              const distance = Math.hypot(clickX - centerX, clickY - centerY);
                              if (distance <= 40) {
                                row.toggleSelected(!row.getIsSelected());
                                return;
                              }
                            }
                          }
                          if (expandOnRowClick && row.getCanExpand()) {
                            row.toggleExpanded();
                          }
                          if (!href && onRowClick)
                            onRowClick(row.original, {
                              metaKey: event.metaKey,
                              ctrlKey: event.ctrlKey,
                              shiftKey: event.shiftKey,
                              altKey: event.altKey
                            });
                        },
                        "aria-expanded": expandableRows ? row.getIsExpanded() : void 0,
                        children: (() => {
                          const cells = row.getVisibleCells();
                          const firstContentCellIndex = cells.findIndex(
                            (c) => c.column.id !== "__expander__" && c.column.id !== "__selection__"
                          );
                          return cells.map((cell, cellIndex) => {
                            const meta = cell.column.columnDef.meta ?? {};
                            return /* @__PURE__ */ jsx108(
                              AriaCell,
                              {
                                className: cn(
                                  "py-2 px-2 align-middle [&:has([role=checkbox])]:px-0 [&:has([role=checkbox])]:py-0 [&:has([role=checkbox])]:h-full",
                                  cellIndex === firstContentCellIndex && "pl-4"
                                ),
                                style: getColumnLayoutStyle(meta),
                                children: flexRender(cell.column.columnDef.cell, cell.getContext())
                              },
                              cell.id
                            );
                          });
                        })()
                      }
                    ),
                    expandableRows && row.getIsExpanded() && /* @__PURE__ */ jsx108(
                      AriaRow,
                      {
                        "data-row-type": "expanded",
                        className: "border-b bg-secondary",
                        style: { display: "block", width: "100%" },
                        "aria-label": "Expanded content",
                        children: /* @__PURE__ */ jsx108(
                          AriaCell,
                          {
                            className: "p-0 align-top",
                            colSpan: row.getVisibleCells().length,
                            style: {
                              flex: "1 1 100%",
                              maxWidth: "100%",
                              minWidth: 0
                            },
                            children: /* @__PURE__ */ jsx108("div", { className: "px-3 py-3", children: expandableRows.render(row) })
                          }
                        )
                      }
                    )
                  ] }, row.id);
                }) : /* @__PURE__ */ jsx108(AriaRow, { style: { display: "flex" }, className: "h-24", children: (() => {
                  const headers = headerColumns[0]?.headers ?? [];
                  const firstContentIndex = headers.findIndex(
                    (h) => h.column.id !== "__expander__" && h.column.id !== "__selection__"
                  );
                  return headers.map((header, index) => {
                    const meta = header.column.columnDef.meta ?? {};
                    return /* @__PURE__ */ jsx108(
                      AriaCell,
                      {
                        className: cn(
                          "py-2 px-2 align-middle",
                          index === firstContentIndex && "pl-4"
                        ),
                        style: getColumnLayoutStyle(meta),
                        children: index === firstContentIndex && "No results."
                      },
                      header.id
                    );
                  });
                })() }) })
              ]
            }
          )
        }
      ),
      (serverPaginationEnabled || table.getPageCount() > 1) && /* @__PURE__ */ jsxs92("div", { className: "flex flex-wrap items-center justify-between gap-3 py-4 text-sm text-muted-foreground", children: [
        /* @__PURE__ */ jsx108("div", { className: "flex-1", children: table.getFilteredSelectedRowModel().rows.length > 0 && /* @__PURE__ */ jsxs92(Fragment23, { children: [
          table.getFilteredSelectedRowModel().rows.length,
          " of",
          " ",
          table.getFilteredRowModel().rows.length,
          " row(s) selected."
        ] }) }),
        /* @__PURE__ */ jsxs92("div", { className: "flex items-center gap-3", children: [
          serverPaginationEnabled && pageSizeSelectItems.length > 0 && /* @__PURE__ */ jsx108(
            Select,
            {
              "aria-label": "Rows per page",
              items: pageSizeSelectItems,
              selectedKey: String(serverPagination.pageSize),
              onSelectionChange: (key) => {
                if (!key) return;
                const nextSize = Number(key);
                if (!Number.isNaN(nextSize)) {
                  serverPagination.onPageSizeChange?.(nextSize);
                }
              },
              className: "min-w-[140px]",
              isDisabled: serverPagination.isLoading
            }
          ),
          /* @__PURE__ */ jsxs92("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx108(
              Button,
              {
                variant: "outline",
                size: "sm",
                onPress: () => {
                  if (serverPaginationEnabled) {
                    serverPagination.onPageChange(Math.max(1, serverPagination.page - 1));
                  } else if (manualPagination && onPaginationChange) {
                    const newPageIndex = table.state.pagination.pageIndex - 1;
                    onPaginationChange(newPageIndex, table.state.pagination.pageSize);
                  } else {
                    table.previousPage();
                  }
                },
                isDisabled: serverPaginationEnabled ? serverPagination.isLoading || serverPagination.page <= 1 : !table.getCanPreviousPage(),
                children: "Previous"
              }
            ),
            /* @__PURE__ */ jsxs92("span", { children: [
              "Page",
              " ",
              serverPaginationEnabled ? serverPagination.page : table.state.pagination.pageIndex + 1,
              " ",
              "of",
              " ",
              serverPaginationEnabled ? Math.max(serverPagination.pageCount, 1) : Math.max(table.getPageCount(), 1)
            ] }),
            /* @__PURE__ */ jsx108(
              Button,
              {
                variant: "outline",
                size: "sm",
                onPress: () => {
                  if (serverPaginationEnabled) {
                    const nextPage = serverPagination.page + 1;
                    const cappedPage = serverPagination.pageCount > 0 ? Math.min(nextPage, serverPagination.pageCount) : nextPage;
                    serverPagination.onPageChange(cappedPage);
                  } else if (manualPagination && onPaginationChange) {
                    const newPageIndex = table.state.pagination.pageIndex + 1;
                    onPaginationChange(newPageIndex, table.state.pagination.pageSize);
                  } else {
                    table.nextPage();
                  }
                },
                isDisabled: serverPaginationEnabled ? serverPagination.isLoading || serverPagination.pageCount > 0 && serverPagination.page >= serverPagination.pageCount : !table.getCanNextPage(),
                children: "Next"
              }
            )
          ] })
        ] }),
        serverPaginationEnabled && typeof serverPagination.totalItems === "number" && /* @__PURE__ */ jsxs92("div", { className: "w-full text-right", children: [
          "Showing ",
          data.length,
          " of ",
          serverPagination.totalItems,
          " items"
        ] })
      ] })
    ] })
  ] });
}
var Table2 = React76.forwardRef(
  ({ className, "aria-label": ariaLabel = "Table", ...props }, ref) => /* @__PURE__ */ jsx108("div", { className: "relative w-full overflow-auto", children: /* @__PURE__ */ jsx108(
    AriaTable,
    {
      ref,
      className: cn("w-full caption-bottom text-sm", className),
      "aria-label": ariaLabel,
      ...props
    }
  ) })
);
Table2.displayName = "Table";
var TableHeader = React76.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx108(AriaTableHeader, { ref, className: cn("[&_tr]:border-b", className), ...props }));
TableHeader.displayName = "TableHeader";
var TableBody = React76.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx108(AriaTableBody, { ref, className: cn("[&_tr:last-child]:border-0", className), ...props }));
TableBody.displayName = "TableBody";
var TableRow = React76.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx108(
    AriaRow,
    {
      ref,
      className: cn(
        "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
        className
      ),
      ...props
    }
  )
);
TableRow.displayName = "TableRow";
var TableHead = React76.forwardRef(
  ({ className, children, ...props }, ref) => /* @__PURE__ */ jsx108(
    AriaColumn,
    {
      ref,
      className: cn(
        "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
        className
      ),
      ...props,
      children
    }
  )
);
TableHead.displayName = "TableHead";
var TableCell = React76.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx108(
    AriaCell,
    {
      ref,
      className: cn("p-4 align-middle [&:has([role=checkbox])]:pr-0", className),
      ...props
    }
  )
);
TableCell.displayName = "TableCell";

// src/components/BulkActionBar.tsx
import React77 from "react";
import { AnimatePresence as AnimatePresence4, motion as motion8 } from "framer-motion";
import { jsx as jsx109, jsxs as jsxs93 } from "react/jsx-runtime";
var pluralize = (count, singular) => count === 1 ? singular : `${singular}s`;
var BulkActionBar = ({
  selectedCount,
  itemLabel = "item",
  selectionMode,
  deselectedCount,
  totalItems,
  onSelectAll,
  onClearSelection,
  actions
}) => {
  const visible = selectedCount > 0;
  const showSelectAll = selectionMode !== "all" && onSelectAll && totalItems && totalItems > selectedCount;
  return /* @__PURE__ */ jsx109(AnimatePresence4, { initial: false, children: visible ? /* @__PURE__ */ jsx109(
    motion8.div,
    {
      layout: true,
      initial: { opacity: 0, y: -8, height: 0 },
      animate: { opacity: 1, y: 0, height: "auto" },
      exit: { opacity: 0, y: -8, height: 0 },
      transition: { duration: 0.2, ease: "easeOut" },
      className: "overflow-hidden",
      children: /* @__PURE__ */ jsx109("div", { className: "rounded-md border border-primary/40 bg-primary/5 px-4 py-3", children: /* @__PURE__ */ jsxs93(Flex, { align: "center", justify: "between", gap: "sm", wrap: true, children: [
        /* @__PURE__ */ jsxs93(Flex, { align: "center", gap: "sm", wrap: true, className: "text-sm text-muted-foreground", children: [
          /* @__PURE__ */ jsx109(Text, { size: "sm", className: "text-foreground", children: selectionMode === "all" ? `All ${selectedCount} ${pluralize(selectedCount, itemLabel)} selected` : `${selectedCount} ${pluralize(selectedCount, itemLabel)} selected` }),
          selectionMode === "all" && deselectedCount && deselectedCount > 0 ? /* @__PURE__ */ jsxs93(Text, { size: "sm", className: "text-muted-foreground", children: [
            deselectedCount,
            " excluded"
          ] }) : null,
          showSelectAll ? /* @__PURE__ */ jsxs93(
            Button,
            {
              variant: "ghost",
              size: "sm",
              className: "px-0 text-primary",
              onPress: onSelectAll,
              children: [
                "Select all ",
                totalItems,
                " ",
                pluralize(totalItems, itemLabel)
              ]
            }
          ) : null
        ] }),
        /* @__PURE__ */ jsxs93(Flex, { align: "center", gap: "sm", wrap: true, className: "justify-end", children: [
          /* @__PURE__ */ jsx109(Button, { variant: "ghost", size: "sm", onPress: onClearSelection, children: "Clear selection" }),
          actions.map(
            (action) => "render" in action ? /* @__PURE__ */ jsx109(React77.Fragment, { children: action.render }, action.key) : /* @__PURE__ */ jsx109(
              Button,
              {
                variant: action.variant ?? "primary",
                size: "sm",
                iconLeft: action.icon,
                onPress: action.onPress,
                isDisabled: action.isLoading,
                children: action.isLoading && action.loadingLabel ? action.loadingLabel : action.label
              },
              action.label
            )
          )
        ] })
      ] }) })
    },
    "bulk-selection-banner"
  ) : null });
};

// src/components/Avatar.tsx
import React78 from "react";
import { Fragment as Fragment24, jsx as jsx110, jsxs as jsxs94 } from "react/jsx-runtime";
function getInitials(name, email) {
  if (name && name.trim()) {
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0][0].toUpperCase();
  }
  if (email) {
    return email[0].toUpperCase();
  }
  return "?";
}
var Avatar = React78.forwardRef(
  ({ src, name, email, size = "md", className }, ref) => {
    const [imageError, setImageError] = React78.useState(false);
    const [imageLoaded, setImageLoaded] = React78.useState(false);
    const sizeClasses2 = {
      xs: "h-[22px] w-[22px] text-[10px]",
      sm: "h-8 w-8 text-[11px]",
      md: "h-10 w-10 text-xs",
      lg: "h-12 w-12 text-sm",
      xl: "h-16 w-16 text-base"
    };
    const showImage = src && !imageError;
    const initials = getInitials(name, email);
    return /* @__PURE__ */ jsx110(
      "div",
      {
        ref,
        className: cn(
          "relative inline-flex items-center justify-center flex-shrink-0 overflow-hidden border border-border bg-secondary font-mono font-medium text-foreground",
          sizeClasses2[size],
          className
        ),
        "aria-label": name || email || "User avatar",
        children: showImage ? /* @__PURE__ */ jsxs94(Fragment24, { children: [
          /* @__PURE__ */ jsx110(
            "img",
            {
              src,
              alt: name || email || "User avatar",
              className: cn(
                "h-full w-full object-cover transition-opacity duration-200",
                imageLoaded ? "opacity-100" : "opacity-0"
              ),
              onLoad: () => setImageLoaded(true),
              onError: () => setImageError(true)
            }
          ),
          !imageLoaded && /* @__PURE__ */ jsx110("div", { className: "absolute inset-0 flex items-center justify-center bg-secondary", children: initials })
        ] }) : initials
      }
    );
  }
);
Avatar.displayName = "Avatar";

// src/components/Chat.tsx
import React79 from "react";
import { jsx as jsx111, jsxs as jsxs95 } from "react/jsx-runtime";
var roleStyles = {
  user: "text-foreground",
  assistant: "text-foreground",
  tool: "text-muted-foreground",
  system: "text-muted-foreground"
};
var roleLabel = {
  tool: "Tool",
  system: "System"
};
var ChatMessage = ({
  role,
  author,
  timestamp,
  className,
  children,
  heading,
  muted
}) => {
  const style = roleStyles[role] ?? roleStyles.assistant;
  const isConversational = role === "user" || role === "assistant";
  const label = author ?? (isConversational ? null : roleLabel[role] ?? "Agent");
  const isUser = role === "user";
  return /* @__PURE__ */ jsx111(
    "div",
    {
      "data-chat-role": role,
      className: cn(
        "flex flex-col gap-1 py-2",
        isUser && "items-end",
        style,
        muted && "opacity-70",
        className
      ),
      children: /* @__PURE__ */ jsxs95("div", { className: cn("flex min-w-0 flex-col gap-1", isUser ? "max-w-[85%]" : "w-full"), children: [
        label || timestamp ? /* @__PURE__ */ jsxs95(
          "div",
          {
            className: cn(
              "flex items-center gap-2 text-[11px] font-medium text-muted-foreground",
              label ? "justify-between" : "justify-end"
            ),
            children: [
              label ? /* @__PURE__ */ jsx111("span", { children: label }) : null,
              timestamp ? /* @__PURE__ */ jsx111("span", { className: "text-[10px] font-normal opacity-70", children: timestamp }) : null
            ]
          }
        ) : null,
        heading ? /* @__PURE__ */ jsx111(Text, { className: "text-xs font-medium text-foreground", children: heading }) : null,
        /* @__PURE__ */ jsx111(
          "div",
          {
            className: cn(
              isConversational ? "text-sm" : "text-xs",
              "leading-relaxed",
              isUser && "self-end border border-border bg-[var(--paper)] px-3 py-2"
            ),
            children
          }
        )
      ] })
    }
  );
};
ChatMessage.displayName = "ChatMessage";
var ANCHOR_OFFSET = 16;
var prefersReducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
var ChatMessageList = React79.forwardRef(
  ({ className, children, isStreaming, ...props }, ref) => {
    const containerRef = React79.useRef(null);
    const spacerRef = React79.useRef(null);
    const [showScrollIndicator, setShowScrollIndicator] = React79.useState(false);
    const prevStreamingRef = React79.useRef(false);
    const anchorRef = React79.useRef(null);
    const spacerHeightRef = React79.useRef(0);
    const followRef = React79.useRef(true);
    React79.useImperativeHandle(ref, () => containerRef.current, []);
    const scrollBehavior = () => prefersReducedMotion() ? "auto" : "smooth";
    const layout = React79.useCallback(() => {
      const el = containerRef.current;
      const spacer = spacerRef.current;
      if (!el || !spacer) return;
      const anchor = anchorRef.current;
      if (anchor && !anchor.isConnected) anchorRef.current = null;
      let height = 0;
      if (anchorRef.current) {
        const contentBottom = spacer.offsetTop;
        height = Math.max(
          0,
          el.clientHeight - (contentBottom - anchorRef.current.offsetTop) - ANCHOR_OFFSET
        );
      }
      if (height !== spacerHeightRef.current) {
        spacerHeightRef.current = height;
        spacer.style.height = `${height}px`;
      }
      if (followRef.current && !anchorRef.current) {
        el.scrollTop = spacer.offsetTop - el.clientHeight;
      }
      const realBelow = spacer.offsetTop - el.scrollTop - el.clientHeight;
      setShowScrollIndicator(realBelow > 80);
    }, []);
    React79.useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const observed = /* @__PURE__ */ new Set();
      const resizeObserver = new ResizeObserver(() => layout());
      const sync = () => {
        for (const child of Array.from(el.children)) {
          if (child === spacerRef.current || child.hasAttribute("data-chat-pill")) continue;
          if (!observed.has(child)) {
            observed.add(child);
            resizeObserver.observe(child);
          }
        }
        for (const child of observed) {
          if (!child.isConnected) {
            observed.delete(child);
            resizeObserver.unobserve(child);
          }
        }
        layout();
      };
      const mutationObserver = new MutationObserver(sync);
      mutationObserver.observe(el, { childList: true });
      resizeObserver.observe(el);
      sync();
      return () => {
        mutationObserver.disconnect();
        resizeObserver.disconnect();
      };
    }, [layout]);
    React79.useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const onScroll = () => {
        const realBelow = (spacerRef.current?.offsetTop ?? 0) - el.scrollTop - el.clientHeight;
        followRef.current = spacerHeightRef.current === 0 && realBelow <= 8;
        setShowScrollIndicator(realBelow > 80);
      };
      el.addEventListener("scroll", onScroll, { passive: true });
      return () => el.removeEventListener("scroll", onScroll);
    }, []);
    React79.useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      if (isStreaming && !prevStreamingRef.current) {
        const users = el.querySelectorAll('[data-chat-role="user"]');
        const lastUser = users[users.length - 1];
        if (lastUser) {
          anchorRef.current = lastUser;
          followRef.current = false;
          layout();
          el.scrollTo({ top: Math.max(0, lastUser.offsetTop - ANCHOR_OFFSET), behavior: scrollBehavior() });
        }
      }
      prevStreamingRef.current = !!isStreaming;
    }, [isStreaming, layout]);
    const scrollToBottom = React79.useCallback(() => {
      const el = containerRef.current;
      if (!el) return;
      el.scrollTo({
        top: (spacerRef.current?.offsetTop ?? 0) - el.clientHeight,
        behavior: scrollBehavior()
      });
    }, []);
    return /* @__PURE__ */ jsxs95(
      "div",
      {
        ref: containerRef,
        className: cn(
          "relative flex h-full flex-col overflow-y-auto pr-1 pt-4",
          "scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent",
          className
        ),
        ...props,
        children: [
          children,
          /* @__PURE__ */ jsx111("div", { ref: spacerRef, className: "shrink-0", "aria-hidden": "true" }),
          showScrollIndicator && /* @__PURE__ */ jsxs95(
            "button",
            {
              type: "button",
              "data-chat-pill": true,
              onClick: scrollToBottom,
              className: "sticky bottom-2 mx-auto z-10 flex items-center gap-1.5 rounded-full border border-border bg-background/90 px-3 py-1.5 text-xs text-muted-foreground shadow-md backdrop-blur transition-colors hover:text-foreground",
              "aria-label": "Scroll to latest",
              children: [
                /* @__PURE__ */ jsx111(CaretDown4, { size: 12 }),
                /* @__PURE__ */ jsx111("span", { children: "New messages below" })
              ]
            }
          )
        ]
      }
    );
  }
);
ChatMessageList.displayName = "ChatMessageList";
var ChatComposer = React79.forwardRef(({
  value,
  placeholder = "Send a message\u2026",
  isSending = false,
  isDisabled = false,
  disabledHelperText,
  onChange,
  onSubmit,
  onStop,
  submitLabel = "Send",
  stopLabel = "Stop",
  className,
  allowEmptySubmit = false,
  attachments,
  actions
}, ref) => {
  const canSubmit = allowEmptySubmit || value.trim().length > 0;
  const handleKeyDown = (event) => {
    if (isDisabled) {
      return;
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (!isSending && canSubmit) {
        onSubmit();
      }
    }
  };
  const helperText = /* @__PURE__ */ jsx111(Text, { className: "text-xs text-muted-foreground", children: isDisabled ? disabledHelperText ?? "Select or create a chat to send a message." : isSending ? "Agent is responding\u2026 Press stop to interrupt." : "Shift + Enter for new line" });
  const stopAndSend = /* @__PURE__ */ jsxs95("div", { className: "flex items-center gap-2", children: [
    isSending && onStop ? /* @__PURE__ */ jsx111(Button, { variant: "secondary", onPress: onStop, size: "sm", children: stopLabel }) : null,
    /* @__PURE__ */ jsx111(
      Button,
      {
        variant: "primary",
        size: "sm",
        onPress: onSubmit,
        isDisabled: isDisabled || isSending || !canSubmit,
        children: submitLabel
      }
    )
  ] });
  return /* @__PURE__ */ jsxs95(
    "div",
    {
      className: cn(
        "rounded-lg border border-border bg-background p-3 shadow-xs",
        "flex flex-col gap-3",
        className
      ),
      children: [
        attachments,
        /* @__PURE__ */ jsx111(
          TextArea,
          {
            ref,
            value,
            placeholder,
            onChange,
            onKeyDown: handleKeyDown,
            rows: 4,
            resize: "none",
            variant: "default",
            isDisabled: isDisabled || isSending && !onStop
          }
        ),
        actions ? (
          // With a toolbar, the icon row needs the width the helper text used to
          // share — a full label beside four icons is cramped in a 400px panel —
          // so the helper text moves to its own left-aligned row above them.
          /* @__PURE__ */ jsxs95("div", { className: "flex flex-col gap-2", children: [
            helperText,
            /* @__PURE__ */ jsxs95("div", { className: "flex items-center justify-between gap-2", children: [
              /* @__PURE__ */ jsx111("div", { className: "flex min-w-0 items-center gap-2", children: actions }),
              stopAndSend
            ] })
          ] })
        ) : (
          // No toolbar: the original single row, unchanged for every other caller.
          /* @__PURE__ */ jsxs95("div", { className: "flex items-center justify-between gap-2", children: [
            /* @__PURE__ */ jsx111("div", { className: "flex min-w-0 items-center gap-2", children: helperText }),
            stopAndSend
          ] })
        )
      ]
    }
  );
});
ChatComposer.displayName = "ChatComposer";

// src/components/ToolCall.tsx
import React80 from "react";
import { CircleNotch as CircleNotch2, CheckCircle as CheckCircle5, XCircle as XCircle2, CaretRight as CaretRight16 } from "@phosphor-icons/react";
import { Fragment as Fragment25, jsx as jsx112, jsxs as jsxs96 } from "react/jsx-runtime";
var SUMMARY_KEYS = ["path", "url", "runId", "entity", "name", "id", "file", "query", "command"];
function summarizeToolInput(input) {
  if (input === null || input === void 0) return "";
  if (typeof input !== "object" || Array.isArray(input)) return "";
  const entries = Object.entries(input);
  if (entries.length === 0) return "";
  for (const key of SUMMARY_KEYS) {
    const val = input[key];
    if (val !== void 0 && val !== null && (typeof val === "string" || typeof val === "number")) {
      const str = String(val);
      return str.length > 40 ? `${str.slice(0, 40)}\u2026` : str;
    }
  }
  if (entries.length === 1) {
    const [, val] = entries[0];
    if (typeof val === "string" || typeof val === "number") {
      const str = String(val);
      return str.length > 40 ? `${str.slice(0, 40)}\u2026` : str;
    }
  }
  return `${entries.length} params`;
}
var StateIcon = ({ state }) => {
  switch (state) {
    case "streaming":
    case "pending":
      return /* @__PURE__ */ jsx112(CircleNotch2, { size: 12, className: "animate-spin text-muted-foreground shrink-0" });
    case "success":
      return /* @__PURE__ */ jsx112(CheckCircle5, { size: 12, className: "text-green-500 shrink-0" });
    case "error":
      return /* @__PURE__ */ jsx112(XCircle2, { size: 12, className: "text-red-500 shrink-0" });
  }
};
var CollapsibleSection2 = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = React80.useState(defaultOpen);
  return /* @__PURE__ */ jsxs96("div", { className: "border-t border-border/50", children: [
    /* @__PURE__ */ jsxs96(
      "button",
      {
        type: "button",
        onClick: () => setIsOpen(!isOpen),
        className: "flex items-center gap-1 w-full py-1 px-2 text-xs text-muted-foreground hover:text-foreground transition-colors",
        children: [
          /* @__PURE__ */ jsx112(
            CaretRight16,
            {
              size: 12,
              className: cn("transition-transform", isOpen && "rotate-90")
            }
          ),
          /* @__PURE__ */ jsx112("span", { children: title })
        ]
      }
    ),
    isOpen && /* @__PURE__ */ jsx112("div", { className: "px-2 pb-2", children })
  ] });
};
var JsonDisplay = ({ data }) => {
  if (data === void 0 || data === null) {
    return /* @__PURE__ */ jsx112("span", { className: "text-muted-foreground italic", children: "No data" });
  }
  return /* @__PURE__ */ jsx112("pre", { className: "text-xs bg-muted/50 rounded p-2 overflow-x-auto max-h-32 overflow-y-auto", children: /* @__PURE__ */ jsx112("code", { children: JSON.stringify(data, null, 2) }) });
};
var ToolCall = ({
  toolName,
  displayName,
  state,
  input,
  output,
  error,
  children,
  grouped = false,
  inputSummary,
  hideDetails = false,
  className
}) => {
  const hasCustomRenderer = !!children;
  const showInput = input !== void 0 && !hasCustomRenderer && !hideDetails;
  const showOutput = (output !== void 0 || error) && !hasCustomRenderer && !hideDetails;
  const isComplete = state === "success" || state === "error";
  const isActive = state === "streaming" || state === "pending";
  const [isExpanded, setIsExpanded] = React80.useState(state === "error");
  React80.useEffect(() => {
    if (state === "error") setIsExpanded(true);
  }, [state]);
  const summary = hideDetails ? "" : inputSummary ?? summarizeToolInput(input);
  const hasExpandableContent = !hideDetails && (hasCustomRenderer || showInput || showOutput);
  return /* @__PURE__ */ jsxs96(
    "div",
    {
      className: cn(
        "overflow-hidden",
        !grouped && "rounded-md border border-border/60 bg-muted/30",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs96(
          "button",
          {
            type: "button",
            onClick: () => hasExpandableContent && setIsExpanded(!isExpanded),
            className: cn(
              "flex items-center gap-2 px-2 py-1 w-full text-left",
              hasExpandableContent && "hover:bg-muted/50 transition-colors cursor-pointer",
              !hasExpandableContent && "cursor-default"
            ),
            children: [
              /* @__PURE__ */ jsx112(StateIcon, { state }),
              /* @__PURE__ */ jsx112(
                "span",
                {
                  className: cn(
                    "text-xs font-medium truncate",
                    isActive ? "animate-text-shimmer" : "text-foreground"
                  ),
                  children: displayName ?? toolName
                }
              ),
              summary && /* @__PURE__ */ jsxs96("span", { className: "text-xs text-muted-foreground/80 truncate font-mono", children: [
                "(",
                summary,
                ")"
              ] }),
              hasExpandableContent && /* @__PURE__ */ jsx112(
                CaretRight16,
                {
                  size: 10,
                  className: cn(
                    "ml-auto text-muted-foreground shrink-0 transition-transform",
                    isExpanded && "rotate-90"
                  )
                }
              )
            ]
          }
        ),
        isExpanded && /* @__PURE__ */ jsxs96(Fragment25, { children: [
          hasCustomRenderer && /* @__PURE__ */ jsx112("div", { className: "px-2 pb-2", children }),
          showInput && /* @__PURE__ */ jsx112(CollapsibleSection2, { title: "Input", children: /* @__PURE__ */ jsx112(JsonDisplay, { data: input }) }),
          showOutput && isComplete && /* @__PURE__ */ jsx112(CollapsibleSection2, { title: error ? "Error" : "Output", defaultOpen: !!error, children: error ? /* @__PURE__ */ jsx112("div", { className: "text-xs text-red-500 bg-red-500/10 rounded p-2", children: error }) : /* @__PURE__ */ jsx112(JsonDisplay, { data: output }) })
        ] })
      ]
    }
  );
};

// src/components/ToolCallGroup.tsx
import React81 from "react";
import { CaretRight as CaretRight17 } from "@phosphor-icons/react";
import { Fragment as Fragment26, jsx as jsx113, jsxs as jsxs97 } from "react/jsx-runtime";
var ToolCallGroup = ({
  items,
  defaultExpanded = false,
  className
}) => {
  const [expanded, setExpanded] = React81.useState(defaultExpanded);
  const count = items.length;
  if (count <= 1) {
    return /* @__PURE__ */ jsx113(Fragment26, { children: items.map((item) => /* @__PURE__ */ jsx113(React81.Fragment, { children: item.node }, item.key)) });
  }
  if (expanded) {
    return /* @__PURE__ */ jsxs97(
      "div",
      {
        className: cn(
          "rounded-md border border-border/60 bg-muted/30 overflow-hidden",
          className
        ),
        children: [
          /* @__PURE__ */ jsx113("div", { className: "divide-y divide-border/40", children: items.map((item) => /* @__PURE__ */ jsx113(React81.Fragment, { children: item.node }, item.key)) }),
          /* @__PURE__ */ jsxs97(
            "button",
            {
              type: "button",
              onClick: () => setExpanded(false),
              className: "flex items-center gap-1 w-full px-2 py-1 text-xs text-muted-foreground hover:text-foreground border-t border-border/40 transition-colors",
              children: [
                /* @__PURE__ */ jsx113(CaretRight17, { size: 10, className: "rotate-90" }),
                /* @__PURE__ */ jsxs97("span", { children: [
                  "Hide ",
                  count,
                  " tool calls"
                ] })
              ]
            }
          )
        ]
      }
    );
  }
  const last = items[count - 1];
  return /* @__PURE__ */ jsxs97(
    "div",
    {
      className: cn(
        "flex items-stretch rounded-md border border-border/60 bg-muted/30 overflow-hidden",
        className
      ),
      children: [
        /* @__PURE__ */ jsx113("div", { className: "min-w-0 flex-1", children: last.node }),
        /* @__PURE__ */ jsxs97(
          "button",
          {
            type: "button",
            onClick: () => setExpanded(true),
            className: "flex shrink-0 items-center gap-1 border-l border-border/40 px-2 text-xs text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground",
            title: `Show all ${count} tool calls`,
            "aria-label": `Show all ${count} tool calls`,
            children: [
              /* @__PURE__ */ jsx113("span", { className: "font-mono", children: count }),
              /* @__PURE__ */ jsx113(CaretRight17, { size: 10 })
            ]
          }
        )
      ]
    }
  );
};
ToolCallGroup.displayName = "ToolCallGroup";
function groupToolCallParts(parts) {
  const result = [];
  let toolBuffer = [];
  const flushToolBuffer = () => {
    if (toolBuffer.length === 0) return;
    if (toolBuffer.length === 1) {
      result.push(toolBuffer[0].node);
    } else {
      result.push(
        /* @__PURE__ */ jsx113(
          ToolCallGroup,
          {
            items: toolBuffer.map((t) => ({ key: t.key, node: t.node }))
          },
          `tool-group-${toolBuffer[0].key}`
        )
      );
    }
    toolBuffer = [];
  };
  for (const part of parts) {
    if (part.isTool) {
      toolBuffer.push({ key: part.key, node: part.node });
    } else {
      flushToolBuffer();
      result.push(part.node);
    }
  }
  flushToolBuffer();
  return result;
}

// src/components/diagnostics/ThoughtsDiagnosticsPanel.tsx
import React82 from "react";
import { Brain as Brain3, CaretDown as CaretDown10, CaretRight as CaretRight18 } from "@phosphor-icons/react";
import { jsx as jsx114, jsxs as jsxs98 } from "react/jsx-runtime";
function formatTimestamp4(ms) {
  const seconds = Math.floor(ms / 1e3);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}
function ThoughtItem({
  thought,
  isActive,
  isPast,
  isExpanded,
  onToggle,
  videoStartedAt,
  displayTimeMs
}) {
  const { header, body } = parseThoughtContent(thought.content);
  const absoluteTime = formatAbsoluteTimestamp(videoStartedAt, thought.timestampMs);
  const relativeLabel = formatTimestamp4(
    displayTimeMs ? displayTimeMs(thought.timestampMs) : thought.timestampMs
  );
  const title = header ?? "Agent thought";
  const isLong = body.length > 120 || body.includes("\n");
  const truncatedBody = isLong && !isExpanded ? body.slice(0, 120).replace(/\n/g, " ") + "\u2026" : body;
  return /* @__PURE__ */ jsx114(
    "div",
    {
      className: `border-b border-border last:border-b-0 ${isActive ? "bg-blue-100 dark:bg-blue-900/30" : ""}`,
      children: /* @__PURE__ */ jsxs98(
        "button",
        {
          onClick: (e) => {
            e.stopPropagation();
            onToggle();
          },
          className: `w-full px-3 py-2 flex items-start gap-2 hover:bg-muted/50 transition-colors text-left ${isPast ? "opacity-60" : ""}`,
          children: [
            /* @__PURE__ */ jsx114("span", { className: "text-muted-foreground mt-0.5 shrink-0", children: isLong ? isExpanded ? /* @__PURE__ */ jsx114(CaretDown10, { size: 12 }) : /* @__PURE__ */ jsx114(CaretRight18, { size: 12 }) : /* @__PURE__ */ jsx114("span", { className: "w-3 inline-block" }) }),
            /* @__PURE__ */ jsx114(Brain3, { size: 14, weight: "fill", className: "text-muted-foreground mt-0.5 shrink-0" }),
            absoluteTime ? /* @__PURE__ */ jsxs98(TooltipTrigger, { placement: "top", children: [
              /* @__PURE__ */ jsx114("span", { className: "text-muted-foreground font-mono text-xs shrink-0", children: relativeLabel }),
              /* @__PURE__ */ jsx114(Tooltip, { children: absoluteTime })
            ] }) : /* @__PURE__ */ jsx114(Text, { size: "xs", className: "text-muted-foreground font-mono shrink-0", children: relativeLabel }),
            /* @__PURE__ */ jsxs98("div", { className: "flex-1 min-w-0", children: [
              /* @__PURE__ */ jsxs98("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsx114(Text, { size: "xs", weight: "medium", className: "truncate", children: title }),
                thought.toolName && /* @__PURE__ */ jsx114("span", { className: "text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded font-mono shrink-0", children: thought.toolName })
              ] }),
              truncatedBody && /* @__PURE__ */ jsx114(
                Text,
                {
                  size: "xs",
                  className: `text-muted-foreground mt-0.5 ${isExpanded ? "whitespace-pre-wrap break-words" : "truncate"}`,
                  children: truncatedBody
                }
              )
            ] })
          ]
        }
      )
    }
  );
}
function ThoughtsDiagnosticsPanel({
  thoughts,
  currentTimeMs,
  onSeek,
  isLoading,
  videoStartedAt,
  displayTimeMs
}) {
  const [searchQuery, setSearchQuery] = React82.useState("");
  const [expandedRows, setExpandedRows] = React82.useState(/* @__PURE__ */ new Set());
  const filteredThoughts = React82.useMemo(() => {
    if (!searchQuery.trim()) return thoughts;
    const query = searchQuery.toLowerCase();
    return thoughts.filter((t) => t.content.toLowerCase().includes(query));
  }, [thoughts, searchQuery]);
  const toggleRow = (index) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };
  return /* @__PURE__ */ jsxs98("div", { className: "flex flex-col h-full min-h-0 bg-card", children: [
    /* @__PURE__ */ jsx114("div", { className: "shrink-0 p-2", children: /* @__PURE__ */ jsx114(FilterToolbar, { children: /* @__PURE__ */ jsx114(
      FilterToolbarSearch,
      {
        value: searchQuery,
        onChange: setSearchQuery,
        placeholder: "Search thoughts...",
        "aria-label": "Search thoughts",
        className: "flex-1"
      }
    ) }) }),
    /* @__PURE__ */ jsx114(
      TimelineSyncedList,
      {
        items: filteredThoughts,
        currentTimeMs,
        getTimestamp: (t) => t.timestampMs,
        getKey: (t) => t.id,
        onSeek,
        isLoading,
        emptyState: searchQuery ? "No matching thoughts found." : "No agent thoughts captured.",
        renderItem: (thought, isActive, isPast) => {
          const originalIndex = thoughts.indexOf(thought);
          return /* @__PURE__ */ jsx114(
            ThoughtItem,
            {
              thought,
              isActive,
              isPast,
              isExpanded: expandedRows.has(originalIndex),
              onToggle: () => toggleRow(originalIndex),
              videoStartedAt,
              displayTimeMs
            }
          );
        },
        className: "flex-1"
      }
    )
  ] });
}

// src/index.ts
import {
  CalendarDate,
  CalendarDateTime as CalendarDateTime3,
  ZonedDateTime,
  Time as Time3,
  parseDate,
  parseDateTime,
  parseZonedDateTime,
  parseTime,
  parseAbsolute,
  parseAbsoluteToLocal,
  today,
  now,
  getLocalTimeZone,
  toCalendarDateTime as toCalendarDateTime3
} from "@internationalized/date";

// src/components/LoadingOrbit.tsx
import { motion as motion9 } from "framer-motion";
import { jsx as jsx115, jsxs as jsxs99 } from "react/jsx-runtime";
var sizeMap3 = {
  // 24px footprint — fits a stepper "step circle" marker without bulging past it.
  xs: { container: 24, blob: 15, orbit: 18, dot: 2 },
  sm: { container: 32, blob: 20, orbit: 24, dot: 3 },
  md: { container: 48, blob: 30, orbit: 36, dot: 4 },
  lg: { container: 64, blob: 40, orbit: 48, dot: 5 },
  xl: { container: 96, blob: 60, orbit: 72, dot: 6 }
};
function LoadingOrbit({
  size = "md",
  className = "",
  primaryColor = "#FFD700",
  // Canary gold
  secondaryColor = "#FFA500"
  // Orange accent
}) {
  const dimensions = sizeMap3[size];
  return /* @__PURE__ */ jsxs99(
    "div",
    {
      className: `relative flex items-center justify-center ${className}`,
      style: {
        width: dimensions.container,
        height: dimensions.container
      },
      children: [
        /* @__PURE__ */ jsx115(
          "span",
          {
            "data-loader-mark": true,
            "aria-hidden": "true",
            style: { width: dimensions.orbit, height: dimensions.orbit }
          }
        ),
        /* @__PURE__ */ jsx115(
          motion9.div,
          {
            "data-loader-orbit": true,
            style: {
              width: dimensions.blob,
              height: dimensions.blob,
              background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`
            },
            animate: {
              borderRadius: [
                "60% 40% 30% 70% / 60% 30% 70% 40%",
                "20% 80% 20% 80% / 80% 20% 80% 20%",
                "70% 30% 50% 50% / 30% 70% 30% 70%",
                "25% 75% 65% 35% / 55% 45% 75% 25%",
                "80% 20% 40% 60% / 20% 80% 60% 40%",
                "45% 55% 80% 20% / 70% 30% 25% 75%",
                "60% 40% 30% 70% / 60% 30% 70% 40%"
              ],
              rotate: [0, 90, 180, 270, 360],
              scale: [1, 1.15, 0.9, 1.1, 1]
            },
            transition: {
              duration: 0.8,
              repeat: Infinity,
              ease: "easeInOut"
            }
          }
        )
      ]
    }
  );
}
function LoadingWave({
  size = "md",
  className = "",
  color = "rgb(99, 102, 241)"
}) {
  const dimensions = sizeMap3[size];
  const barCount = size === "sm" ? 3 : size === "md" ? 4 : size === "lg" ? 5 : 6;
  const barWidth = dimensions.container / (barCount * 2);
  return /* @__PURE__ */ jsx115(
    "div",
    {
      className: `flex items-center justify-center gap-1 ${className}`,
      style: {
        width: dimensions.container,
        height: dimensions.container
      },
      children: [...Array(barCount)].map((_, i) => /* @__PURE__ */ jsx115(
        motion9.div,
        {
          className: "rounded-full",
          style: {
            width: barWidth,
            backgroundColor: color
          },
          animate: {
            height: [
              dimensions.container * 0.2,
              dimensions.container * 0.8,
              dimensions.container * 0.2
            ],
            opacity: [0.5, 1, 0.5]
          },
          transition: {
            duration: 1.2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.15
          }
        },
        i
      ))
    }
  );
}
function LoadingHelix({
  size = "md",
  className = "",
  primaryColor = "rgb(99, 102, 241)",
  secondaryColor = "rgb(236, 72, 153)"
}) {
  const dimensions = sizeMap3[size];
  const dotCount = 8;
  return /* @__PURE__ */ jsx115(
    "div",
    {
      className: `relative ${className}`,
      style: {
        width: dimensions.container,
        height: dimensions.container
      },
      children: [...Array(dotCount)].map((_, i) => {
        const angle = 360 / dotCount * i;
        const isPrimary = i % 2 === 0;
        return /* @__PURE__ */ jsx115(
          motion9.div,
          {
            className: "absolute rounded-full",
            style: {
              width: dimensions.dot,
              height: dimensions.dot,
              backgroundColor: isPrimary ? primaryColor : secondaryColor,
              top: "50%",
              left: "50%"
            },
            animate: {
              x: [
                Math.cos(angle * Math.PI / 180) * dimensions.orbit * 0.5,
                Math.cos((angle + 180) * Math.PI / 180) * dimensions.orbit * 0.5,
                Math.cos(angle * Math.PI / 180) * dimensions.orbit * 0.5
              ],
              y: [
                Math.sin(angle * Math.PI / 180) * dimensions.orbit * 0.5,
                Math.sin((angle + 180) * Math.PI / 180) * dimensions.orbit * 0.5,
                Math.sin(angle * Math.PI / 180) * dimensions.orbit * 0.5
              ],
              scale: [1, 1.5, 1],
              opacity: [0.3, 1, 0.3]
            },
            transition: {
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.1
            }
          },
          i
        );
      })
    }
  );
}
function LoadingBlob({
  size = "md",
  className = "",
  color = "rgb(99, 102, 241)"
}) {
  const dimensions = sizeMap3[size];
  return /* @__PURE__ */ jsxs99(
    "div",
    {
      className: `relative ${className}`,
      style: {
        width: dimensions.container,
        height: dimensions.container
      },
      children: [
        /* @__PURE__ */ jsx115(
          motion9.div,
          {
            className: "absolute inset-0 m-auto",
            style: {
              width: dimensions.orbit,
              height: dimensions.orbit,
              background: `linear-gradient(135deg, ${color}, ${color}80)`,
              filter: "blur(0.5px)"
            },
            animate: {
              borderRadius: [
                "60% 40% 30% 70% / 60% 30% 70% 40%",
                "30% 60% 70% 40% / 50% 60% 30% 60%",
                "60% 40% 30% 70% / 60% 30% 70% 40%"
              ],
              rotate: [0, 360],
              scale: [1, 1.1, 1]
            },
            transition: {
              duration: 8,
              repeat: Infinity,
              ease: "linear"
            }
          }
        ),
        /* @__PURE__ */ jsx115(
          motion9.div,
          {
            className: "absolute inset-0 m-auto",
            style: {
              width: dimensions.orbit * 0.7,
              height: dimensions.orbit * 0.7,
              background: `linear-gradient(225deg, ${color}90, ${color}40)`
            },
            animate: {
              borderRadius: [
                "30% 70% 70% 30% / 30% 30% 70% 70%",
                "70% 30% 30% 70% / 70% 70% 30% 30%",
                "30% 70% 70% 30% / 30% 30% 70% 70%"
              ],
              rotate: [0, -360],
              scale: [1, 0.9, 1]
            },
            transition: {
              duration: 6,
              repeat: Infinity,
              ease: "linear"
            }
          }
        )
      ]
    }
  );
}

// src/components/Skeleton.tsx
import { jsx as jsx116, jsxs as jsxs100 } from "react/jsx-runtime";
function Skeleton({
  className,
  variant = "text",
  width,
  height,
  animation = "pulse"
}) {
  const variantStyles5 = {
    text: "rounded",
    circular: "rounded-full",
    rectangular: "rounded-none",
    rounded: "rounded-lg"
  };
  const animationStyles = {
    pulse: "animate-pulse",
    wave: "animate-shimmer",
    none: ""
  };
  const style = {
    width: typeof width === "number" ? `${width}px` : width,
    height: typeof height === "number" ? `${height}px` : height
  };
  return /* @__PURE__ */ jsx116(
    "div",
    {
      className: cn(
        "bg-muted/60",
        variantStyles5[variant],
        animationStyles[animation],
        // Default sizes for text variant
        variant === "text" && !height && "h-4",
        variant === "text" && !width && "w-full",
        className
      ),
      style,
      "aria-hidden": "true"
    }
  );
}
function SkeletonText({
  lines = 3,
  lastLineWidth = "70%",
  gap = "sm",
  className
}) {
  const gapStyles = {
    xs: "gap-1",
    sm: "gap-2",
    md: "gap-3"
  };
  return /* @__PURE__ */ jsx116("div", { className: cn("flex flex-col", gapStyles[gap], className), children: Array.from({ length: lines }).map((_, i) => /* @__PURE__ */ jsx116(
    Skeleton,
    {
      variant: "text",
      width: i === lines - 1 ? lastLineWidth : "100%"
    },
    i
  )) });
}
function SkeletonCard({
  showHeader = true,
  contentLines = 2,
  showActions = false,
  className
}) {
  return /* @__PURE__ */ jsxs100(
    "div",
    {
      className: cn(
        "rounded-lg border border-border bg-card p-4 space-y-4",
        className
      ),
      children: [
        showHeader && /* @__PURE__ */ jsxs100("div", { className: "flex items-center gap-3", children: [
          /* @__PURE__ */ jsx116(Skeleton, { variant: "circular", width: 40, height: 40 }),
          /* @__PURE__ */ jsxs100("div", { className: "flex-1 space-y-2", children: [
            /* @__PURE__ */ jsx116(Skeleton, { variant: "text", width: "60%", height: 16 }),
            /* @__PURE__ */ jsx116(Skeleton, { variant: "text", width: "40%", height: 12 })
          ] })
        ] }),
        contentLines > 0 && /* @__PURE__ */ jsx116(SkeletonText, { lines: contentLines, lastLineWidth: "80%" }),
        showActions && /* @__PURE__ */ jsxs100("div", { className: "flex gap-2 pt-2", children: [
          /* @__PURE__ */ jsx116(Skeleton, { variant: "rounded", width: 80, height: 32 }),
          /* @__PURE__ */ jsx116(Skeleton, { variant: "rounded", width: 80, height: 32 })
        ] })
      ]
    }
  );
}
function SkeletonChart({
  height = 200,
  showTitle = true,
  className
}) {
  return /* @__PURE__ */ jsxs100(
    "div",
    {
      className: cn(
        "rounded-lg border border-border bg-card overflow-hidden",
        className
      ),
      children: [
        showTitle && /* @__PURE__ */ jsx116("div", { className: "p-4 pb-0", children: /* @__PURE__ */ jsx116(Skeleton, { variant: "text", width: "40%", height: 20 }) }),
        /* @__PURE__ */ jsx116("div", { className: "p-4", children: /* @__PURE__ */ jsxs100(
          "div",
          {
            className: "relative overflow-hidden rounded bg-muted/30",
            style: { height },
            children: [
              /* @__PURE__ */ jsx116("div", { className: "absolute inset-x-4 bottom-4 flex items-end justify-between gap-2", children: [40, 65, 45, 80, 55, 70, 50, 75, 60, 85, 45, 70].map((h, i) => /* @__PURE__ */ jsx116(
                Skeleton,
                {
                  variant: "rectangular",
                  className: "flex-1 rounded-t",
                  height: `${h}%`,
                  animation: "pulse"
                },
                i
              )) }),
              /* @__PURE__ */ jsx116("div", { className: "absolute inset-0 flex flex-col justify-between py-4 px-4", children: [0, 1, 2, 3].map((i) => /* @__PURE__ */ jsx116(
                "div",
                {
                  className: "border-b border-dashed border-muted/50"
                },
                i
              )) })
            ]
          }
        ) })
      ]
    }
  );
}
function SkeletonKPI({ className }) {
  return /* @__PURE__ */ jsxs100(
    "div",
    {
      className: cn(
        "rounded-lg border border-border bg-card p-4 space-y-2",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs100("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx116(Skeleton, { variant: "circular", width: 16, height: 16 }),
          /* @__PURE__ */ jsx116(Skeleton, { variant: "text", width: "50%", height: 14 })
        ] }),
        /* @__PURE__ */ jsx116(Skeleton, { variant: "text", width: "60%", height: 32 }),
        /* @__PURE__ */ jsx116(Skeleton, { variant: "text", width: "40%", height: 12 })
      ]
    }
  );
}
function SkeletonKPIRow({ count = 4, className }) {
  return /* @__PURE__ */ jsx116(
    "div",
    {
      className: cn(
        "grid gap-4",
        count === 2 && "grid-cols-2",
        count === 3 && "grid-cols-3",
        count >= 4 && "grid-cols-2 md:grid-cols-4",
        className
      ),
      children: Array.from({ length: count }).map((_, i) => /* @__PURE__ */ jsx116(SkeletonKPI, {}, i))
    }
  );
}

// src/components/StepsPreviewSkeleton.tsx
import React83 from "react";
import { jsx as jsx117, jsxs as jsxs101 } from "react/jsx-runtime";
function StepsPreviewSkeleton({ stepCount = 5, className }) {
  return /* @__PURE__ */ jsx117("div", { className: cn("py-4 px-6 bg-muted/20 border-t border-border/50", className), children: /* @__PURE__ */ jsxs101("div", { className: "flex items-center justify-between gap-4 mb-3", children: [
    /* @__PURE__ */ jsx117("div", { className: "flex items-center gap-2 overflow-hidden", children: Array.from({ length: stepCount }).map((_, i) => /* @__PURE__ */ jsxs101(React83.Fragment, { children: [
      /* @__PURE__ */ jsxs101("div", { className: "flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-lg border border-border bg-muted/40 shrink-0", children: [
        /* @__PURE__ */ jsx117(Skeleton, { variant: "rounded", width: 28, height: 28, animation: "pulse" }),
        /* @__PURE__ */ jsxs101("div", { className: "flex flex-col gap-1", children: [
          /* @__PURE__ */ jsx117(Skeleton, { variant: "text", width: 40, height: 10, animation: "pulse" }),
          /* @__PURE__ */ jsx117(Skeleton, { variant: "text", width: 80, height: 12, animation: "pulse" })
        ] })
      ] }),
      i < stepCount - 1 && /* @__PURE__ */ jsxs101("div", { className: "flex items-center mx-1.5 shrink-0", children: [
        /* @__PURE__ */ jsx117(
          Skeleton,
          {
            variant: "rectangular",
            width: 16,
            height: 1,
            animation: "none",
            className: "opacity-50"
          }
        ),
        /* @__PURE__ */ jsx117(
          Skeleton,
          {
            variant: "circular",
            width: 12,
            height: 12,
            animation: "none",
            className: "opacity-50 -ml-0.5"
          }
        )
      ] })
    ] }, i)) }),
    /* @__PURE__ */ jsx117(
      Skeleton,
      {
        variant: "rounded",
        width: 110,
        height: 32,
        animation: "pulse",
        className: "shrink-0"
      }
    )
  ] }) });
}

// src/components/workflow-nodes/LoginNode.tsx
import { Key as Key2 } from "@phosphor-icons/react";
import { NODE_TYPE_LABEL as NODE_TYPE_LABEL2 } from "@chatsdet/types";

// src/components/workflow-nodes/WorkflowNodeWrapper.tsx
import React85 from "react";
import { Button as AriaButton4 } from "react-aria-components";
import { DotsThreeVertical as DotsThreeVertical2, Play as Play5, Prohibit as Prohibit3, Trash as Trash2 } from "@phosphor-icons/react";

// src/components/workflow-nodes/statusHelpers.ts
function getOptimizerChip(state) {
  if (!state) return null;
  const steps = (cached, gap) => {
    const parts = [];
    if (cached !== void 0) parts.push(`${cached} cached`);
    if (gap) parts.push(`${gap} gap${gap === 1 ? "" : "s"}`);
    return parts.join(" \xB7 ");
  };
  switch (state.status) {
    case "optimizing":
      return {
        text: "Optimizing\u2026",
        className: "bg-blue-500/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-500/20 dark:border-blue-500/40",
        icon: "spinner",
        tooltip: "Cache optimizer is authoring this node"
      };
    case "optimized":
      return {
        text: steps(state.cachedSteps, state.gapSteps) || "Cached",
        className: "bg-blue-500/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-500/20 dark:border-blue-500/40",
        icon: "check",
        tooltip: "Fully cached \u2014 deterministic replay"
      };
    case "partial":
      return {
        text: steps(state.cachedSteps, state.gapSteps) || "Partial",
        className: "bg-amber-500/10 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/20 dark:border-amber-500/40",
        icon: "partial",
        tooltip: "Some steps cached, some run live (gaps)"
      };
    case "failed":
      return {
        text: "Optimize failed",
        className: "bg-red-500/10 dark:bg-red-500/20 text-red-600 dark:text-red-400 border border-red-500/20 dark:border-red-500/40",
        icon: "x",
        tooltip: state.reason ?? "This node could not be optimized"
      };
    case "skipped":
      return {
        text: "Not optimized",
        className: "bg-zinc-500/10 dark:bg-zinc-500/20 text-zinc-500 dark:text-zinc-400 border border-zinc-500/20 dark:border-zinc-500/40",
        icon: "dash",
        tooltip: "The optimizer did not author this node"
      };
    default:
      return null;
  }
}
function getStatusBorderClasses(status, selected, focused) {
  switch (status) {
    case "running":
      return [
        "border-blue-500 dark:border-blue-400",
        "bg-blue-50 dark:bg-blue-950",
        "shadow-[0_0_0_1px_rgba(59,130,246,0.2)]",
        selected || focused ? "ring-2 ring-blue-200/60 dark:ring-blue-500/40" : ""
      ].filter(Boolean).join(" ").trim();
    case "waiting":
      return [
        "border-amber-500 dark:border-amber-400",
        "bg-amber-50 dark:bg-amber-950",
        "shadow-[0_0_0_1px_rgba(245,158,11,0.15)]",
        selected || focused ? "ring-2 ring-amber-200/60 dark:ring-amber-500/40" : ""
      ].filter(Boolean).join(" ").trim();
    case "success":
      return [
        "border-emerald-500 dark:border-emerald-400",
        "bg-emerald-50 dark:bg-emerald-950",
        "shadow-[0_0_0_1px_rgba(16,185,129,0.15)]",
        selected || focused ? "ring-2 ring-emerald-200/60 dark:ring-emerald-500/40" : ""
      ].filter(Boolean).join(" ").trim();
    case "failed":
      return [
        "border-red-500 dark:border-red-400",
        "bg-red-50 dark:bg-red-950",
        "shadow-[0_0_0_1px_rgba(239,68,68,0.2)]",
        selected || focused ? "ring-2 ring-red-200/60 dark:ring-red-500/40" : ""
      ].filter(Boolean).join(" ").trim();
    case "warning":
      return [
        "border-amber-500 dark:border-amber-400",
        "bg-amber-50/60 dark:bg-amber-950/60",
        "shadow-[0_0_0_1px_rgba(245,158,11,0.15)]",
        selected || focused ? "ring-2 ring-amber-200/60 dark:ring-amber-500/40" : ""
      ].filter(Boolean).join(" ").trim();
    case "live-tested":
      return [
        "border-emerald-400 dark:border-emerald-500",
        "bg-emerald-50/50 dark:bg-emerald-950/50",
        "shadow-[0_0_0_1px_rgba(52,211,153,0.15)]",
        selected || focused ? "ring-2 ring-emerald-200/50 dark:ring-emerald-500/30" : ""
      ].filter(Boolean).join(" ").trim();
    case "skipped":
      return [
        "border-zinc-300 dark:border-zinc-600",
        "bg-zinc-50 dark:bg-zinc-900",
        "shadow-[0_0_0_1px_rgba(161,161,170,0.15)]",
        "opacity-60",
        selected || focused ? "ring-2 ring-zinc-200/60 dark:ring-zinc-500/40" : ""
      ].filter(Boolean).join(" ").trim();
    case "live-executing":
      return [
        "border-blue-500 dark:border-blue-400",
        "bg-blue-50 dark:bg-blue-950",
        "shadow-[0_0_0_1px_rgba(59,130,246,0.2)]",
        selected || focused ? "ring-2 ring-blue-200/60 dark:ring-blue-500/40" : ""
      ].filter(Boolean).join(" ").trim();
    case "idle":
    default:
      if (focused) {
        return "border-primary ring-2 ring-primary/30 bg-primary/5";
      }
      return selected ? "border-primary ring-1 ring-primary/20 bg-primary/5" : "border-[hsl(var(--line-2,var(--border)))] bg-card";
  }
}
function getStatusBadge(status) {
  switch (status) {
    case "running":
      return {
        text: "Running",
        className: "bg-blue-500/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-500/20 dark:border-blue-500/40",
        icon: "spinner"
      };
    case "waiting":
      return {
        text: "Waiting",
        className: "bg-amber-500/10 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/20 dark:border-amber-500/40"
      };
    case "success":
      return {
        text: "Success",
        className: "bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 dark:border-emerald-500/40",
        icon: "check"
      };
    case "failed":
      return {
        text: "Failed",
        className: "bg-red-500/10 dark:bg-red-500/20 text-red-600 dark:text-red-400 border border-red-500/20 dark:border-red-500/40"
      };
    case "warning":
      return {
        text: "Warning",
        className: "bg-amber-500/10 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/20 dark:border-amber-500/40"
      };
    case "skipped":
      return {
        text: "Skipped",
        className: "bg-zinc-500/10 dark:bg-zinc-500/20 text-zinc-500 dark:text-zinc-400 border border-zinc-500/20 dark:border-zinc-500/40"
      };
    case "live-tested":
      return {
        text: "Tested",
        className: "bg-emerald-400/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-400/20 dark:border-emerald-500/40",
        icon: "check"
      };
    case "live-executing":
      return {
        text: "Testing",
        className: "bg-blue-400/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-400/20 dark:border-blue-500/40",
        icon: "spinner"
      };
    default:
      return null;
  }
}

// src/components/workflow-nodes/NodeStatusFooter.tsx
import { CheckCircle as CheckCircle7 } from "@phosphor-icons/react";

// src/components/workflow-nodes/OptimizerChip.tsx
import { CheckCircle as CheckCircle6, MinusCircle, Warning as Warning2, XCircle as XCircle3 } from "@phosphor-icons/react";
import { jsx as jsx118, jsxs as jsxs102 } from "react/jsx-runtime";
function OptimizerChip({ status }) {
  const chip = getOptimizerChip(status);
  if (!chip) return null;
  return /* @__PURE__ */ jsxs102(
    "span",
    {
      className: cn(
        "px-2 py-0.5 rounded-full text-[10px] font-semibold inline-flex items-center gap-1",
        chip.className
      ),
      title: chip.tooltip,
      children: [
        chip.icon === "spinner" && /* @__PURE__ */ jsx118(
          "span",
          {
            className: "inline-flex h-3 w-3 rounded-full border-[1.5px] border-current border-r-transparent animate-spin",
            "aria-hidden": "true"
          }
        ),
        chip.icon === "check" && /* @__PURE__ */ jsx118(CheckCircle6, { size: 12, weight: "fill" }),
        chip.icon === "partial" && /* @__PURE__ */ jsx118(Warning2, { size: 12, weight: "fill" }),
        chip.icon === "x" && /* @__PURE__ */ jsx118(XCircle3, { size: 12, weight: "fill" }),
        chip.icon === "dash" && /* @__PURE__ */ jsx118(MinusCircle, { size: 12, weight: "fill" }),
        /* @__PURE__ */ jsx118("span", { children: chip.text })
      ]
    }
  );
}

// src/components/workflow-nodes/NodeStatusFooter.tsx
import { jsx as jsx119, jsxs as jsxs103 } from "react/jsx-runtime";
function NodeStatusFooter({
  executionStatus = "idle",
  optimizerStatus
}) {
  const badge = getStatusBadge(executionStatus);
  const optimizerChip = getOptimizerChip(optimizerStatus);
  if (!badge && !optimizerChip) return null;
  return /* @__PURE__ */ jsxs103("div", { className: "mt-3 pt-2 border-t border-border/60 flex flex-wrap items-center gap-2", children: [
    badge && /* @__PURE__ */ jsx119(RunStatusPill, { badge, status: executionStatus }),
    optimizerChip && /* @__PURE__ */ jsx119(OptimizerChip, { status: optimizerStatus })
  ] });
}
function RunStatusPill({ badge, status }) {
  const isLiveTested = status === "live-tested";
  const isSuccess = status === "success";
  return /* @__PURE__ */ jsxs103(
    "span",
    {
      className: cn(
        "px-2 py-0.5 rounded-full text-[10px] font-semibold inline-flex items-center gap-1",
        badge.className
      ),
      children: [
        badge.icon === "spinner" && /* @__PURE__ */ jsx119(
          "span",
          {
            className: "inline-flex h-3 w-3 rounded-full border-[1.5px] border-current border-r-transparent animate-spin",
            "aria-hidden": "true"
          }
        ),
        badge.icon === "check" && /* @__PURE__ */ jsx119(CheckCircle7, { size: 12, weight: "fill", className: "text-current" }),
        (isLiveTested || isSuccess) && badge.icon !== "spinner" && badge.icon !== "check" && /* @__PURE__ */ jsx119(CheckCircle7, { size: 12, weight: "fill", className: "text-current" }),
        /* @__PURE__ */ jsx119("span", { children: badge.text })
      ]
    }
  );
}

// src/components/WorkflowHandle.tsx
import { jsx as jsx120, jsxs as jsxs104 } from "react/jsx-runtime";
function getHandleColorClass(handleId) {
  switch (handleId) {
    case "true":
      return "bg-green-500";
    case "false":
      return "bg-red-500";
    default:
      return "bg-primary";
  }
}
function WorkflowHandle({
  type,
  nodeId,
  handleId = "default",
  label,
  tooltip,
  position = "center",
  className
}) {
  const isDraggable = type === "source" && !!nodeId;
  const isSidePosition = position === "side-left";
  const handleDragStart = (e) => {
    if (!isDraggable || !nodeId) {
      e.preventDefault();
      return;
    }
    const dragData = JSON.stringify({ nodeId, handleId });
    e.dataTransfer.setData("application/x-workflow-connection", dragData);
    e.dataTransfer.effectAllowed = "link";
  };
  const tooltipPlacement = isSidePosition ? "left" : "bottom";
  const handleElement = /* @__PURE__ */ jsx120(
    "div",
    {
      "data-handle-type": type,
      "data-handle-id": handleId,
      "data-node-id": nodeId,
      "data-handle-position": isSidePosition ? "side-left" : void 0,
      draggable: isDraggable,
      onDragStart: handleDragStart,
      onPointerDown: (e) => e.stopPropagation(),
      className: cn(
        "absolute w-3 h-3 rounded-full border-2 border-background z-10",
        getHandleColorClass(handleId),
        isSidePosition ? "-left-1.5 top-1/2 -translate-y-1/2" : cn(
          position === "left" ? "left-1/4 -translate-x-1/2" : position === "right" ? "left-3/4 -translate-x-1/2" : "left-1/2 -translate-x-1/2",
          type === "target" ? "-top-1.5" : "-bottom-1.5"
        ),
        isDraggable && "cursor-grab active:cursor-grabbing hover:scale-125 transition-transform",
        className
      ),
      children: label && /* @__PURE__ */ jsx120(
        "span",
        {
          className: cn(
            "absolute text-[9px] font-semibold whitespace-nowrap pointer-events-none",
            isSidePosition ? "right-5 top-1/2 -translate-y-1/2" : type === "target" ? "bottom-4 left-1/2 -translate-x-1/2" : "top-4 left-1/2 -translate-x-1/2",
            handleId === "true" && "text-green-600",
            handleId === "false" && "text-red-600"
          ),
          children: label
        }
      )
    }
  );
  if (tooltip) {
    return /* @__PURE__ */ jsxs104(TooltipTrigger, { placement: tooltipPlacement, delay: 300, children: [
      handleElement,
      /* @__PURE__ */ jsx120(Tooltip, { children: tooltip })
    ] });
  }
  return handleElement;
}

// src/components/workflow-nodes/AddNodeMenu.tsx
import { useCallback as useCallback6, useEffect as useEffect3, useLayoutEffect, useRef as useRef3, useState as useState6 } from "react";
import { createPortal as createPortal4 } from "react-dom";
import { AnimatePresence as AnimatePresence5, motion as motion10 } from "framer-motion";
import { Plus as Plus4 } from "@phosphor-icons/react";
import {
  NODE_TYPE_LABEL,
  NODE_TYPE_META,
  NODE_TYPE_CATEGORY_LABEL,
  NODE_TYPE_CATEGORY_ORDER
} from "@chatsdet/types";

// src/components/workflow-nodes/nodeIcons.tsx
import {
  Key,
  Globe as Globe2,
  Lightning,
  CheckCircle as CheckCircle8,
  Hourglass,
  GitBranch as GitBranch2,
  Plugs,
  PlugsConnected,
  Plant,
  Gear as Gear3,
  XCircle as XCircle4,
  Bell as Bell2,
  Microphone as Microphone2,
  WaveformSlash,
  Camera as Camera3,
  Cube,
  FlowArrow as FlowArrow2
} from "@phosphor-icons/react";
import { jsx as jsx121 } from "react/jsx-runtime";
function getNodeIcon(nodeType, size = 16) {
  switch (nodeType) {
    case "login":
      return {
        icon: /* @__PURE__ */ jsx121(Key, { size, weight: "duotone", className: "text-blue-600" }),
        bgClass: "bg-blue-500/10"
      };
    case "navigate":
      return {
        icon: /* @__PURE__ */ jsx121(Globe2, { size, weight: "duotone", className: "text-green-600" }),
        bgClass: "bg-green-500/10"
      };
    case "action":
      return {
        icon: /* @__PURE__ */ jsx121(Lightning, { size, weight: "duotone", className: "text-purple-600" }),
        bgClass: "bg-purple-500/10"
      };
    case "assertion":
      return {
        icon: /* @__PURE__ */ jsx121(CheckCircle8, { size, weight: "duotone", className: "text-orange-600" }),
        bgClass: "bg-orange-500/10"
      };
    case "wait":
      return {
        icon: /* @__PURE__ */ jsx121(Hourglass, { size, weight: "duotone", className: "text-amber-700" }),
        bgClass: "bg-amber-500/10"
      };
    case "condition":
      return {
        icon: /* @__PURE__ */ jsx121(GitBranch2, { size, weight: "duotone", className: "text-cyan-600" }),
        bgClass: "bg-cyan-500/10"
      };
    case "api_sequence":
      return {
        icon: /* @__PURE__ */ jsx121(Plugs, { size, weight: "duotone", className: "text-cyan-600" }),
        bgClass: "bg-cyan-500/10"
      };
    case "sandbox_action":
      return {
        icon: /* @__PURE__ */ jsx121(Cube, { size, weight: "duotone", className: "text-violet-600" }),
        bgClass: "bg-violet-500/10"
      };
    case "mcp":
      return {
        icon: /* @__PURE__ */ jsx121(PlugsConnected, { size, weight: "duotone", className: "text-fuchsia-600" }),
        bgClass: "bg-fuchsia-500/10"
      };
    case "seed":
      return {
        icon: /* @__PURE__ */ jsx121(Plant, { size, weight: "duotone", className: "text-teal-600" }),
        bgClass: "bg-teal-500/10"
      };
    case "setup":
      return {
        icon: /* @__PURE__ */ jsx121(Gear3, { size, weight: "duotone", className: "text-purple-600" }),
        bgClass: "bg-purple-500/10"
      };
    case "call_flow":
      return {
        icon: /* @__PURE__ */ jsx121(FlowArrow2, { size, weight: "duotone", className: "text-sky-600" }),
        bgClass: "bg-sky-500/10"
      };
    case "notification":
      return {
        icon: /* @__PURE__ */ jsx121(Bell2, { size, weight: "duotone", className: "text-pink-600" }),
        bgClass: "bg-pink-500/10"
      };
    case "audio":
      return {
        icon: /* @__PURE__ */ jsx121(Microphone2, { size, weight: "duotone", className: "text-indigo-600" }),
        bgClass: "bg-indigo-500/10"
      };
    case "audio-assertion":
      return {
        icon: /* @__PURE__ */ jsx121(WaveformSlash, { size, weight: "duotone", className: "text-indigo-600" }),
        bgClass: "bg-indigo-500/10"
      };
    case "visual_checkpoint":
      return {
        icon: /* @__PURE__ */ jsx121(Camera3, { size, weight: "duotone", className: "text-indigo-600" }),
        bgClass: "bg-indigo-500/10"
      };
    case "end":
      return {
        icon: /* @__PURE__ */ jsx121(CheckCircle8, { size, weight: "duotone", className: "text-green-600" }),
        bgClass: "bg-green-500/10"
      };
    case "end_fail":
      return {
        icon: /* @__PURE__ */ jsx121(XCircle4, { size, weight: "duotone", className: "text-red-600" }),
        bgClass: "bg-red-500/10"
      };
    default:
      return {
        icon: /* @__PURE__ */ jsx121(Gear3, { size, weight: "duotone", className: "text-gray-500" }),
        bgClass: "bg-gray-500/10"
      };
  }
}
function NodeIconPill({
  nodeType,
  size = 16,
  className
}) {
  const { icon, bgClass } = getNodeIcon(nodeType, size);
  return /* @__PURE__ */ jsx121("div", { className: cn("p-1.5 rounded", bgClass, className), children: icon });
}

// src/components/workflow-nodes/AddNodeMenu.tsx
import { jsx as jsx122, jsxs as jsxs105 } from "react/jsx-runtime";
var NODE_MENU_ITEMS = [
  { type: "login", label: NODE_TYPE_LABEL.login },
  { type: "navigate", label: NODE_TYPE_LABEL.navigate },
  { type: "action", label: NODE_TYPE_LABEL.action },
  { type: "assertion", label: NODE_TYPE_LABEL.assertion },
  { type: "seed", label: NODE_TYPE_LABEL.seed },
  { type: "call_flow", label: NODE_TYPE_LABEL.call_flow },
  { type: "sandbox_action", label: NODE_TYPE_LABEL.sandbox_action },
  { type: "api_sequence", label: NODE_TYPE_LABEL.api_sequence },
  { type: "mcp", label: NODE_TYPE_LABEL.mcp },
  { type: "condition", label: NODE_TYPE_LABEL.condition },
  { type: "wait", label: NODE_TYPE_LABEL.wait },
  { type: "notification", label: NODE_TYPE_LABEL.notification },
  { type: "audio", label: NODE_TYPE_LABEL.audio },
  { type: "audio-assertion", label: NODE_TYPE_LABEL["audio-assertion"] },
  { type: "visual_checkpoint", label: NODE_TYPE_LABEL.visual_checkpoint },
  { type: "end", label: NODE_TYPE_LABEL.end }
];
function AddNodeMenu({ header, visibleTypes, onSelect, footerActions }) {
  const containerRef = useRef3(null);
  const items = visibleTypes ? NODE_MENU_ITEMS.filter((item) => visibleTypes.includes(item.type)) : NODE_MENU_ITEMS;
  const sections = NODE_TYPE_CATEGORY_ORDER.map((category) => ({
    category,
    label: NODE_TYPE_CATEGORY_LABEL[category],
    items: items.filter((item) => NODE_TYPE_META[item.type].category === category)
  })).filter((section) => section.items.length > 0);
  const showSectionLabels = sections.length > 1;
  const handleKeyDown = useCallback6(
    (e) => {
      const container = containerRef.current;
      if (!container) return;
      const buttons = Array.from(container.querySelectorAll("button[data-node-type]"));
      const currentIndex = buttons.findIndex((b) => b === document.activeElement);
      if (e.key === "ArrowDown") {
        e.preventDefault();
        const next = currentIndex < buttons.length - 1 ? currentIndex + 1 : 0;
        buttons[next]?.focus();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const prev = currentIndex > 0 ? currentIndex - 1 : buttons.length - 1;
        buttons[prev]?.focus();
      }
    },
    []
  );
  return /* @__PURE__ */ jsxs105(
    "div",
    {
      ref: containerRef,
      className: "bg-card border border-border rounded-lg shadow-lg py-1 w-[260px] max-h-[calc(100vh-16px)] overflow-y-auto",
      onKeyDown: handleKeyDown,
      role: "menu",
      children: [
        header && /* @__PURE__ */ jsx122("div", { className: "px-3 py-1 text-xs font-semibold text-muted-foreground border-b border-border mb-0.5", children: header }),
        sections.map((section) => /* @__PURE__ */ jsxs105("div", { children: [
          showSectionLabels && /* @__PURE__ */ jsx122("div", { className: "px-3 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70", children: section.label }),
          section.items.map((item) => /* @__PURE__ */ jsxs105(
            "button",
            {
              "data-node-type": item.type,
              role: "menuitem",
              className: "w-full flex items-center gap-2 px-3 py-1 text-left hover:bg-muted transition-colors",
              onClick: () => onSelect(item.type),
              children: [
                /* @__PURE__ */ jsx122(NodeIconPill, { nodeType: item.type, size: 14, className: "shrink-0 p-1" }),
                /* @__PURE__ */ jsxs105("span", { className: "flex flex-col min-w-0", children: [
                  /* @__PURE__ */ jsx122("span", { className: "text-[13px] text-foreground leading-tight", children: item.label }),
                  /* @__PURE__ */ jsx122("span", { className: "text-[11px] text-muted-foreground leading-tight truncate", children: NODE_TYPE_META[item.type].description })
                ] })
              ]
            },
            item.type
          ))
        ] }, section.category)),
        footerActions && footerActions.length > 0 && /* @__PURE__ */ jsx122("div", { className: "border-t border-border mt-1 pt-1", children: footerActions.map((action) => /* @__PURE__ */ jsx122(
          "button",
          {
            role: "menuitem",
            className: cn(
              "w-full px-3 py-1.5 text-sm text-left transition-colors",
              action.destructive ? "text-destructive hover:bg-destructive/10" : "hover:bg-muted"
            ),
            onClick: action.onClick,
            children: action.label
          },
          action.label
        )) })
      ]
    }
  );
}
function AddNodeMenuPopover({
  ariaLabel = "Add step",
  header,
  visibleTypes,
  onSelect,
  className,
  onHoldDrag
}) {
  const [isOpen, setIsOpen] = useState6(false);
  const triggerRef = useRef3(null);
  const dropdownRef = useRef3(null);
  const [position, setPosition] = useState6(null);
  const holdDragActiveRef = useRef3(false);
  const holdTimerRef = useRef3(null);
  useEffect3(() => {
    if (!isOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({
      x: rect.left + rect.width / 2,
      y: rect.bottom + 4
    });
  }, [isOpen]);
  useLayoutEffect(() => {
    if (!isOpen || !dropdownRef.current || !triggerRef.current) return;
    const menuRect = dropdownRef.current.getBoundingClientRect();
    const triggerRect = triggerRef.current.getBoundingClientRect();
    if (menuRect.bottom > window.innerHeight) {
      setPosition({
        x: triggerRect.left + triggerRect.width / 2,
        y: triggerRect.top - menuRect.height - 4
      });
    }
  }, [isOpen, position?.y]);
  useEffect3(() => {
    if (!isOpen) return;
    function handleClickOutside(e) {
      const target = e.target;
      if (triggerRef.current && !triggerRef.current.contains(target) && dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);
  useEffect3(() => {
    if (!isOpen) return;
    function handleEscape(e) {
      if (e.key === "Escape") setIsOpen(false);
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);
  const handlePointerDownCapture = useCallback6(
    (e) => {
      if (!onHoldDrag) return;
      holdDragActiveRef.current = false;
      const startX = e.clientX;
      const startY = e.clientY;
      const savedEvent = e;
      const cleanup = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        if (holdTimerRef.current) {
          clearTimeout(holdTimerRef.current);
          holdTimerRef.current = null;
        }
      };
      const onMove = (moveEvt) => {
        if (Math.abs(moveEvt.clientX - startX) > 5 || Math.abs(moveEvt.clientY - startY) > 5) {
          cleanup();
        }
      };
      const onUp = () => cleanup();
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      holdTimerRef.current = setTimeout(() => {
        holdTimerRef.current = null;
        cleanup();
        holdDragActiveRef.current = true;
        onHoldDrag(savedEvent);
      }, 200);
    },
    [onHoldDrag]
  );
  useEffect3(() => {
    return () => {
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    };
  }, []);
  const handlePress = useCallback6(() => {
    if (holdDragActiveRef.current) {
      holdDragActiveRef.current = false;
      return;
    }
    setIsOpen((prev) => !prev);
  }, []);
  return /* @__PURE__ */ jsxs105(
    "div",
    {
      ref: triggerRef,
      className: cn("relative", className),
      onPointerDownCapture: handlePointerDownCapture,
      onPointerDown: (e) => e.stopPropagation(),
      onClick: (e) => e.stopPropagation(),
      children: [
        /* @__PURE__ */ jsx122(
          Button,
          {
            variant: "outline",
            size: "sm",
            iconOnly: true,
            "aria-label": ariaLabel,
            className: "shadow-sm bg-background",
            onPress: handlePress,
            children: /* @__PURE__ */ jsx122(Plus4, { size: 14, weight: "bold", className: "text-muted-foreground" })
          }
        ),
        createPortal4(
          /* @__PURE__ */ jsx122(AnimatePresence5, { children: isOpen && position && /* @__PURE__ */ jsx122(
            motion10.div,
            {
              ref: dropdownRef,
              initial: { opacity: 0, scale: 0.95 },
              animate: { opacity: 1, scale: 1 },
              exit: { opacity: 0, scale: 0.95 },
              transition: { duration: 0.1 },
              className: "fixed z-50",
              style: { left: position.x, top: position.y, transform: "translateX(-50%)" },
              onPointerDown: (e) => e.stopPropagation(),
              onClick: (e) => e.stopPropagation(),
              children: /* @__PURE__ */ jsx122(
                AddNodeMenu,
                {
                  header,
                  visibleTypes,
                  onSelect: (nodeType) => {
                    setIsOpen(false);
                    onSelect(nodeType);
                  }
                }
              )
            }
          ) }),
          document.body
        )
      ]
    }
  );
}

// src/components/workflow-nodes/WorkflowNodeWrapper.tsx
import { jsx as jsx123, jsxs as jsxs106 } from "react/jsx-runtime";
function WorkflowNodeWrapper({
  children,
  nodeId,
  selected,
  focused,
  executionStatus = "idle",
  optimizerStatus,
  isConfigured = true,
  hasValidationError = false,
  className,
  maxWidth = true,
  hasTargetHandle = true,
  hasSourceHandle = true,
  quickCreateActions,
  showQuickCreate,
  hasOutgoingEdge = false,
  hasCachedReplay = false,
  cacheRequiresAgent = false,
  cachedAt,
  onCacheClick,
  playbookStepCount,
  onPlaybookClick,
  onDelete,
  isDeactivated = false,
  onToggleDeactivate,
  isDragGhost = false,
  onHoldDrag,
  openIssues
}) {
  const hasAnyQuickAction = !!(quickCreateActions?.onCreateNavigate || quickCreateActions?.onCreateAction || quickCreateActions?.onCreateAssertion || quickCreateActions?.onCreateWait || quickCreateActions?.onCreateLogin || quickCreateActions?.onCreateCondition || quickCreateActions?.onCreateApiSequence || quickCreateActions?.onCreateSandboxAction || quickCreateActions?.onCreateMcp || quickCreateActions?.onCreateNotification || quickCreateActions?.onCreateAudio || quickCreateActions?.onCreateAudioAssertion || quickCreateActions?.onCreateVisualCheckpoint || quickCreateActions?.onCreateEnd);
  const shouldShowQuickCreate = !hasOutgoingEdge && (showQuickCreate ?? hasAnyQuickAction);
  const [showSuccessRipple, setShowSuccessRipple] = React85.useState(false);
  const prevStatusRef = React85.useRef(executionStatus);
  React85.useEffect(() => {
    if (prevStatusRef.current === "live-executing" && executionStatus === "live-tested") {
      setShowSuccessRipple(true);
      const timer = setTimeout(() => setShowSuccessRipple(false), 600);
      return () => clearTimeout(timer);
    }
    prevStatusRef.current = executionStatus;
  }, [executionStatus]);
  return /* @__PURE__ */ jsxs106(
    "div",
    {
      className: cn(
        "px-4 py-3 rounded-lg border bg-card min-w-[200px] relative group",
        "transition-all duration-200",
        maxWidth && "max-w-[300px]",
        getStatusBorderClasses(executionStatus, selected, focused),
        !isConfigured && "border-dashed opacity-75",
        hasValidationError && "border-destructive border-dashed ring-1 ring-destructive/20",
        isDeactivated && "opacity-50 border-dashed grayscale",
        isDragGhost && "opacity-20 border-dashed scale-[0.95]",
        className
      ),
      children: [
        showSuccessRipple && /* @__PURE__ */ jsx123("div", { className: "absolute inset-0 rounded-lg pointer-events-none overflow-hidden", children: /* @__PURE__ */ jsx123("div", { className: "absolute inset-0 bg-emerald-400/20 animate-[ping_0.5s_ease-out_forwards]" }) }),
        (playbookStepCount || hasCachedReplay) && /* @__PURE__ */ jsx123(
          PlaybookIndicator,
          {
            playbookStepCount,
            hasCachedReplay,
            cacheRequiresAgent,
            cachedAt,
            onClick: onPlaybookClick ?? onCacheClick
          }
        ),
        openIssues && openIssues.length > 0 && /* @__PURE__ */ jsx123(NodeOpenIssuesBadge, { issues: openIssues }),
        isDeactivated && /* @__PURE__ */ jsx123("span", { className: "absolute -top-2.5 right-2 z-10 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-muted text-muted-foreground border", children: "Deactivated" }),
        (onDelete || onToggleDeactivate) && /* @__PURE__ */ jsx123(
          NodeContextMenu,
          {
            onDelete,
            isDeactivated,
            onToggleDeactivate
          }
        ),
        hasTargetHandle && /* @__PURE__ */ jsx123(WorkflowHandle, { type: "target", nodeId }),
        children,
        shouldShowQuickCreate && quickCreateActions && /* @__PURE__ */ jsx123(QuickCreateMenu, { actions: quickCreateActions, onHoldDrag }),
        /* @__PURE__ */ jsx123(NodeStatusFooter, { executionStatus, optimizerStatus }),
        hasSourceHandle && /* @__PURE__ */ jsx123(WorkflowHandle, { type: "source", nodeId })
      ]
    }
  );
}
function QuickCreateMenu({
  actions,
  onHoldDrag
}) {
  const handlerMap = {
    login: actions.onCreateLogin,
    navigate: actions.onCreateNavigate,
    action: actions.onCreateAction,
    assertion: actions.onCreateAssertion,
    condition: actions.onCreateCondition,
    wait: actions.onCreateWait,
    api_sequence: actions.onCreateApiSequence,
    sandbox_action: actions.onCreateSandboxAction,
    mcp: actions.onCreateMcp,
    notification: actions.onCreateNotification,
    audio: actions.onCreateAudio,
    "audio-assertion": actions.onCreateAudioAssertion,
    visual_checkpoint: actions.onCreateVisualCheckpoint,
    end: actions.onCreateEnd
  };
  const visibleTypes = Object.entries(handlerMap).filter(([, handler]) => !!handler).map(([type]) => type);
  return /* @__PURE__ */ jsx123("div", { className: "absolute -bottom-10 left-1/2 -translate-x-1/2 z-20", children: /* @__PURE__ */ jsx123(
    AddNodeMenuPopover,
    {
      ariaLabel: "Create next step",
      header: "Add step",
      visibleTypes,
      onSelect: (type) => handlerMap[type]?.(),
      onHoldDrag
    }
  ) });
}
function NodeContextMenu({
  onDelete,
  isDeactivated = false,
  onToggleDeactivate
}) {
  const [isOpen, setIsOpen] = React85.useState(false);
  return /* @__PURE__ */ jsx123(
    "div",
    {
      className: "absolute bottom-2 right-2 z-10",
      onPointerDown: (e) => e.stopPropagation(),
      onClick: (e) => e.stopPropagation(),
      children: /* @__PURE__ */ jsxs106(MenuTrigger, { isOpen, onOpenChange: setIsOpen, children: [
        /* @__PURE__ */ jsx123(
          AriaButton4,
          {
            className: cn(
              "p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-all outline-none",
              isOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus:opacity-100"
            ),
            "aria-label": "Node actions",
            children: /* @__PURE__ */ jsx123(DotsThreeVertical2, { size: 16, weight: "bold" })
          }
        ),
        /* @__PURE__ */ jsxs106(
          Menu,
          {
            onAction: (key) => {
              if (key === "delete") onDelete?.();
              if (key === "toggle-deactivate") onToggleDeactivate?.();
            },
            children: [
              onToggleDeactivate ? /* @__PURE__ */ jsxs106(MenuItem, { id: "toggle-deactivate", children: [
                isDeactivated ? /* @__PURE__ */ jsx123(Play5, { size: 14, className: "mr-2" }) : /* @__PURE__ */ jsx123(Prohibit3, { size: 14, className: "mr-2" }),
                isDeactivated ? "Reactivate" : "Deactivate"
              ] }) : null,
              onDelete ? /* @__PURE__ */ jsxs106(MenuItem, { id: "delete", className: "text-destructive", children: [
                /* @__PURE__ */ jsx123(Trash2, { size: 14, className: "mr-2" }),
                "Delete"
              ] }) : null
            ]
          }
        )
      ] })
    }
  );
}
function NodeOpenIssuesBadge({ issues }) {
  const label = issues.length === 1 ? issues[0].shortcode : `${issues.length} issues`;
  return /* @__PURE__ */ jsx123(
    "div",
    {
      className: "absolute -top-2.5 left-2 z-10",
      onPointerDown: (e) => e.stopPropagation(),
      onClick: (e) => e.stopPropagation(),
      children: /* @__PURE__ */ jsxs106(TooltipTrigger, { delay: 0, offset: 8, placement: "top", children: [
        /* @__PURE__ */ jsx123("a", { href: issues[0].href, className: "inline-flex cursor-pointer", children: /* @__PURE__ */ jsx123(TagBadge, { name: label, color: "red", hideDot: true, size: "sm" }) }),
        /* @__PURE__ */ jsx123(Tooltip, { variant: "light", className: "max-w-xs px-3 py-2 text-left", children: /* @__PURE__ */ jsx123("div", { className: "flex flex-col gap-1", children: issues.map((issue) => /* @__PURE__ */ jsxs106(
          "a",
          {
            href: issue.href,
            className: "text-primary hover:underline",
            onClick: (e) => e.stopPropagation(),
            children: [
              /* @__PURE__ */ jsx123("span", { className: "font-mono text-xs text-muted-foreground mr-1", children: issue.shortcode }),
              issue.title
            ]
          },
          issue.id
        )) }) })
      ] })
    }
  );
}
function NodeHeader({ icon, label, iconBgClass = "bg-gray-500/10" }) {
  return /* @__PURE__ */ jsxs106("div", { className: "flex items-center gap-2 mb-1", children: [
    /* @__PURE__ */ jsx123("div", { className: cn("p-1.5 rounded", iconBgClass), children: icon }),
    /* @__PURE__ */ jsx123("span", { className: "text-xs font-semibold text-muted-foreground uppercase tracking-wide", children: label })
  ] });
}
function PlaybookIndicator({
  playbookStepCount,
  hasCachedReplay = false,
  cacheRequiresAgent = false,
  cachedAt,
  onClick
}) {
  const hasPlaybook = playbookStepCount !== null && playbookStepCount !== void 0 && playbookStepCount > 0;
  let label;
  let colorClass;
  if (hasPlaybook && hasCachedReplay) {
    label = `${playbookStepCount} playbook step${playbookStepCount === 1 ? "" : "s"}`;
    colorClass = cacheRequiresAgent ? "bg-amber-100 dark:bg-amber-900/70 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300" : "bg-cyan-100 dark:bg-cyan-900/70 border border-cyan-300 dark:border-cyan-700 text-cyan-700 dark:text-cyan-300";
  } else if (hasPlaybook) {
    label = `${playbookStepCount} playbook step${playbookStepCount === 1 ? "" : "s"}`;
    colorClass = "bg-muted border border-border text-muted-foreground";
  } else {
    label = cacheRequiresAgent ? "Agent" : "Cached";
    colorClass = cacheRequiresAgent ? "bg-amber-100 dark:bg-amber-900/70 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300" : "bg-cyan-100 dark:bg-cyan-900/70 border border-cyan-300 dark:border-cyan-700 text-cyan-700 dark:text-cyan-300";
  }
  let tooltip = hasPlaybook ? `${playbookStepCount} playbook step${playbookStepCount === 1 ? "" : "s"}` : cacheRequiresAgent ? "Agent-assisted replay" : "Cached replay available";
  if (cachedAt) {
    try {
      const relative = getRelativeTimeString(new Date(cachedAt));
      tooltip += `, cached ${relative}`;
    } catch {
    }
  }
  const hoverClass = onClick ? cacheRequiresAgent && (hasPlaybook ? hasCachedReplay : true) ? "cursor-pointer hover:bg-amber-200 dark:hover:bg-amber-800/50 transition-colors" : hasCachedReplay ? "cursor-pointer hover:bg-cyan-200 dark:hover:bg-cyan-800/50 transition-colors" : "cursor-pointer hover:bg-muted/80 transition-colors" : "";
  const handleClick = onClick ? (e) => {
    e.stopPropagation();
    onClick(e);
  } : void 0;
  return /* @__PURE__ */ jsx123(
    "div",
    {
      className: cn(
        "absolute -top-2 -right-2 z-10 px-1.5 py-0.5 rounded-md text-[10px] font-medium select-none",
        colorClass,
        hoverClass
      ),
      title: tooltip,
      onClick: handleClick,
      onPointerDown: onClick ? (e) => e.stopPropagation() : void 0,
      children: label
    }
  );
}
function getRelativeTimeString(date) {
  const now2 = /* @__PURE__ */ new Date();
  const diffMs = now2.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1e3);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays > 0) {
    return diffDays === 1 ? "1 day ago" : `${diffDays} days ago`;
  }
  if (diffHours > 0) {
    return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
  }
  if (diffMins > 0) {
    return diffMins === 1 ? "1 minute ago" : `${diffMins} minutes ago`;
  }
  return "just now";
}

// src/components/workflow-nodes/LoginNode.tsx
import { jsx as jsx124, jsxs as jsxs107 } from "react/jsx-runtime";
function textOrNull(value) {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}
function LoginNode({ data, nodeId, selected, focused }) {
  const credentialName = textOrNull(data?.credentialName);
  const credentialId = textOrNull(data?.credentialId);
  const credentialTemplateId = textOrNull(data?.credentialTemplateId);
  const credentialTemplateName = textOrNull(data?.credentialTemplateName);
  const propertyName = textOrNull(data?.propertyName);
  const isTemplateSource = data?.credentialSource === "from_template";
  const isSeedSource = data?.credentialSource === "from_seed";
  const seedLabel = textOrNull(data?.seedCredentialRef?.name) ?? textOrNull(data?.seedCredentialRef?.dataKey);
  let displayName;
  if (isTemplateSource) {
    displayName = credentialTemplateName || credentialName || (credentialTemplateId ? "Persona selected" : "Select a persona");
  } else if (credentialName) {
    displayName = credentialName;
  } else if (isSeedSource) {
    displayName = seedLabel || "Select a credential output";
  } else if (credentialId) {
    displayName = "Credential selected";
  } else {
    displayName = "Select a credential";
  }
  const quickCreateActions = data.isPersisted !== false ? {
    onCreateNavigate: data.onCreateNavigate ? () => data.onCreateNavigate(data) : void 0,
    onCreateAction: data.onCreateAction ? () => data.onCreateAction(data) : void 0,
    onCreateAssertion: data.onCreateAssertion ? () => data.onCreateAssertion(data) : void 0,
    onCreateWait: data.onCreateWait ? () => data.onCreateWait(data) : void 0,
    onCreateCondition: data.onCreateCondition ? () => data.onCreateCondition(data) : void 0,
    onCreateApiSequence: data.onCreateApiSequence ?? void 0,
    onCreateNotification: data.onCreateNotification ?? void 0,
    onCreateAudio: data.onCreateAudio ?? void 0,
    onCreateAudioAssertion: data.onCreateAudioAssertion ?? void 0,
    onCreateVisualCheckpoint: data.onCreateVisualCheckpoint ?? void 0,
    onCreateEnd: data.onCreateEnd ?? void 0
  } : void 0;
  return /* @__PURE__ */ jsxs107(
    WorkflowNodeWrapper,
    {
      nodeId,
      selected,
      focused,
      hasValidationError: data.hasValidationError,
      executionStatus: data.executionStatus,
      optimizerStatus: data.optimizerStatus,
      hasTargetHandle: false,
      maxWidth: false,
      quickCreateActions,
      hasOutgoingEdge: data.hasOutgoingEdge,
      onDelete: data.onDelete,
      isDeactivated: data.deactivated === true,
      onToggleDeactivate: data.onToggleDeactivate,
      isDragGhost: data.isDragGhost,
      onHoldDrag: data.onHoldDrag,
      children: [
        /* @__PURE__ */ jsx124(
          NodeHeader,
          {
            icon: /* @__PURE__ */ jsx124(Key2, { size: 16, weight: "duotone", className: "text-blue-600" }),
            label: NODE_TYPE_LABEL2.login,
            iconBgClass: "bg-blue-500/10"
          }
        ),
        /* @__PURE__ */ jsxs107("div", { className: "flex items-center gap-1.5 flex-wrap", children: [
          /* @__PURE__ */ jsx124("span", { className: "text-sm font-medium text-foreground", children: displayName }),
          isTemplateSource && /* @__PURE__ */ jsx124(
            "span",
            {
              className: cn(
                "px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide",
                "bg-blue-500/10 text-blue-700 dark:text-blue-400"
              ),
              children: "Persona"
            }
          )
        ] }),
        propertyName && /* @__PURE__ */ jsx124(
          "div",
          {
            className: cn(
              "mt-2 px-2 py-0.5 rounded text-[11px] font-medium inline-block",
              "bg-muted text-muted-foreground"
            ),
            children: propertyName
          }
        )
      ]
    }
  );
}

// src/components/workflow-nodes/PageNode.tsx
import { Globe as Globe3, ArrowSquareOut as ArrowSquareOut2 } from "@phosphor-icons/react";
import { jsx as jsx125, jsxs as jsxs108 } from "react/jsx-runtime";
var ensureAbsoluteUrl = (url) => {
  if (!url) return void 0;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `https://${url}`;
};
function PageNode({ data, nodeId, selected, focused }) {
  const displayText = data?.pageUrl || "Select page...";
  const isConfigured = !!(data?.canonicalPageId || data?.pageUrl);
  const absolutePageUrl = ensureAbsoluteUrl(data?.pageUrl);
  const quickCreateActions = data.isPersisted !== false && (data.canonicalPageId || data.pageUrl) ? {
    onCreateAction: data.onCreateAction ? () => data.onCreateAction(data) : void 0,
    onCreateAssertion: data.onCreateAssertion ? () => data.onCreateAssertion(data) : void 0,
    onCreateWait: data.onCreateWait ? () => data.onCreateWait(data) : void 0,
    onCreateLogin: data.onCreateLogin ? () => data.onCreateLogin(data) : void 0,
    onCreateCondition: data.onCreateCondition ? () => data.onCreateCondition(data) : void 0,
    onCreateApiSequence: data.onCreateApiSequence ?? void 0,
    onCreateNotification: data.onCreateNotification ?? void 0,
    onCreateAudio: data.onCreateAudio ?? void 0,
    onCreateAudioAssertion: data.onCreateAudioAssertion ?? void 0,
    onCreateVisualCheckpoint: data.onCreateVisualCheckpoint ?? void 0,
    onCreateEnd: data.onCreateEnd ?? void 0
  } : void 0;
  return /* @__PURE__ */ jsxs108(
    WorkflowNodeWrapper,
    {
      nodeId,
      selected,
      focused,
      hasValidationError: data.hasValidationError,
      executionStatus: data.executionStatus,
      optimizerStatus: data.optimizerStatus,
      isConfigured,
      quickCreateActions,
      hasOutgoingEdge: data.hasOutgoingEdge,
      hasCachedReplay: data.hasCachedReplay,
      cacheRequiresAgent: data.cacheRequiresAgent,
      cachedAt: data.cachedAt,
      onCacheClick: data.onCacheClick,
      playbookStepCount: data.playbookStepCount,
      onPlaybookClick: data.onPlaybookClick,
      onDelete: data.onDelete,
      isDeactivated: data.deactivated === true,
      onToggleDeactivate: data.onToggleDeactivate,
      isDragGhost: data.isDragGhost,
      onHoldDrag: data.onHoldDrag,
      children: [
        /* @__PURE__ */ jsx125(
          NodeHeader,
          {
            icon: /* @__PURE__ */ jsx125(Globe3, { size: 16, weight: "duotone", className: "text-green-600" }),
            label: "Navigate",
            iconBgClass: "bg-green-500/10"
          }
        ),
        /* @__PURE__ */ jsx125("div", { className: "text-sm font-medium text-foreground truncate", children: displayText }),
        data?.openInNewTab && /* @__PURE__ */ jsx125("div", { className: "mt-2 flex flex-wrap items-center gap-2", children: /* @__PURE__ */ jsxs108("div", { className: "inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground", children: [
          /* @__PURE__ */ jsx125(ArrowSquareOut2, { size: 12, weight: "bold" }),
          /* @__PURE__ */ jsx125("span", { children: "Opens new tab" })
        ] }) }),
        absolutePageUrl && /* @__PURE__ */ jsx125("div", { className: "absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity", children: /* @__PURE__ */ jsx125(
          "a",
          {
            href: absolutePageUrl,
            target: "_blank",
            rel: "noopener noreferrer",
            onClick: (event) => event.stopPropagation(),
            className: "p-1 rounded bg-background/80 hover:bg-muted border border-border",
            title: "Open page",
            children: /* @__PURE__ */ jsx125(ArrowSquareOut2, { size: 14, weight: "bold", className: "text-muted-foreground" })
          }
        ) })
      ]
    }
  );
}

// src/components/workflow-nodes/ActionNode.tsx
import { Lightning as Lightning2, Sparkle, CheckCircle as CheckCircle9 } from "@phosphor-icons/react";
import { jsx as jsx126, jsxs as jsxs109 } from "react/jsx-runtime";
function ActionNode({ data, nodeId, selected, focused }) {
  const displayText = data?.customActionTitle || data?.actionDescription || data?.actionKind || "Select action...";
  const hasGuidanceImprovement = Boolean(data?.previousActionDescription && data?.guidanceImprovedAt && !data?.guidanceAcknowledgedAt);
  const quickCreateActions = data.isPersisted !== false ? {
    onCreateNavigate: data.onCreateNavigate ? () => data.onCreateNavigate(data) : void 0,
    onCreateAction: data.onCreateAction ? () => data.onCreateAction(data) : void 0,
    onCreateAssertion: data.onCreateAssertion ? () => data.onCreateAssertion(data) : void 0,
    onCreateWait: data.onCreateWait ? () => data.onCreateWait(data) : void 0,
    onCreateLogin: data.onCreateLogin ? () => data.onCreateLogin(data) : void 0,
    onCreateCondition: data.onCreateCondition ? () => data.onCreateCondition(data) : void 0,
    onCreateApiSequence: data.onCreateApiSequence ?? void 0,
    onCreateNotification: data.onCreateNotification ?? void 0,
    onCreateAudio: data.onCreateAudio ?? void 0,
    onCreateAudioAssertion: data.onCreateAudioAssertion ?? void 0,
    onCreateVisualCheckpoint: data.onCreateVisualCheckpoint ?? void 0,
    onCreateEnd: data.onCreateEnd ?? void 0
  } : void 0;
  return /* @__PURE__ */ jsxs109(
    WorkflowNodeWrapper,
    {
      nodeId,
      selected,
      focused,
      hasValidationError: data.hasValidationError,
      executionStatus: data.executionStatus,
      optimizerStatus: data.optimizerStatus,
      quickCreateActions,
      hasOutgoingEdge: data.hasOutgoingEdge,
      hasCachedReplay: data.hasCachedReplay,
      cacheRequiresAgent: data.cacheRequiresAgent,
      cachedAt: data.cachedAt,
      onCacheClick: data.onCacheClick,
      playbookStepCount: data.playbookStepCount,
      onPlaybookClick: data.onPlaybookClick,
      onDelete: data.onDelete,
      isDeactivated: data.deactivated === true,
      onToggleDeactivate: data.onToggleDeactivate,
      isDragGhost: data.isDragGhost,
      onHoldDrag: data.onHoldDrag,
      openIssues: data.openIssues,
      children: [
        /* @__PURE__ */ jsxs109("div", { className: "flex items-center justify-between", children: [
          /* @__PURE__ */ jsx126(
            NodeHeader,
            {
              icon: /* @__PURE__ */ jsx126(Lightning2, { size: 16, weight: "duotone", className: "text-purple-600" }),
              label: "Action",
              iconBgClass: "bg-purple-500/10"
            }
          ),
          hasGuidanceImprovement && /* @__PURE__ */ jsx126(
            "div",
            {
              className: "p-1 rounded-full bg-blue-100 dark:bg-blue-900/30",
              title: "Guidance improved by agent",
              children: /* @__PURE__ */ jsx126(Sparkle, { size: 12, weight: "fill", className: "text-blue-500" })
            }
          )
        ] }),
        /* @__PURE__ */ jsx126("div", { className: "text-sm font-medium text-foreground", children: displayText }),
        (data.inlineAssertionCount ?? 0) > 0 && /* @__PURE__ */ jsxs109("div", { className: "mt-1.5 pt-1.5 border-t border-border/40 flex items-center gap-1.5 text-[10px] text-muted-foreground", children: [
          /* @__PURE__ */ jsx126(CheckCircle9, { size: 12, weight: "duotone", className: "text-orange-500" }),
          data.inlineAssertionCount,
          " assertion",
          data.inlineAssertionCount !== 1 ? "s" : ""
        ] })
      ]
    }
  );
}

// src/components/workflow-nodes/AssertionNode.tsx
import { CheckCircle as CheckCircle10 } from "@phosphor-icons/react";
import { jsx as jsx127, jsxs as jsxs110 } from "react/jsx-runtime";
function AssertionNode({ data, nodeId, selected, focused }) {
  const displayText = data?.condition || "Define condition...";
  const isConfigured = !!data?.condition;
  const quickCreateActions = data.isPersisted !== false ? {
    onCreateNavigate: data.onCreateNavigate ? () => data.onCreateNavigate(data) : void 0,
    onCreateAction: data.onCreateAction ? () => data.onCreateAction(data) : void 0,
    onCreateAssertion: data.onCreateAssertion ? () => data.onCreateAssertion(data) : void 0,
    onCreateWait: data.onCreateWait ? () => data.onCreateWait(data) : void 0,
    onCreateLogin: data.onCreateLogin ? () => data.onCreateLogin(data) : void 0,
    onCreateCondition: data.onCreateCondition ? () => data.onCreateCondition(data) : void 0,
    onCreateApiSequence: data.onCreateApiSequence ?? void 0,
    onCreateNotification: data.onCreateNotification ?? void 0,
    onCreateAudio: data.onCreateAudio ?? void 0,
    onCreateAudioAssertion: data.onCreateAudioAssertion ?? void 0,
    onCreateVisualCheckpoint: data.onCreateVisualCheckpoint ?? void 0,
    onCreateEnd: data.onCreateEnd ?? void 0
  } : void 0;
  return /* @__PURE__ */ jsxs110(
    WorkflowNodeWrapper,
    {
      nodeId,
      selected,
      focused,
      hasValidationError: data.hasValidationError,
      executionStatus: data.executionStatus,
      optimizerStatus: data.optimizerStatus,
      isConfigured,
      quickCreateActions,
      hasOutgoingEdge: data.hasOutgoingEdge,
      hasCachedReplay: data.hasCachedReplay,
      cacheRequiresAgent: data.cacheRequiresAgent,
      cachedAt: data.cachedAt,
      onCacheClick: data.onCacheClick,
      playbookStepCount: data.playbookStepCount,
      onPlaybookClick: data.onPlaybookClick,
      onDelete: data.onDelete,
      isDeactivated: data.deactivated === true,
      onToggleDeactivate: data.onToggleDeactivate,
      isDragGhost: data.isDragGhost,
      onHoldDrag: data.onHoldDrag,
      openIssues: data.openIssues,
      children: [
        /* @__PURE__ */ jsx127(
          NodeHeader,
          {
            icon: /* @__PURE__ */ jsx127(CheckCircle10, { size: 16, weight: "duotone", className: "text-orange-600" }),
            label: "Assertion",
            iconBgClass: "bg-orange-500/10"
          }
        ),
        /* @__PURE__ */ jsx127("div", { className: "text-sm font-medium text-foreground whitespace-pre-wrap break-words", children: displayText })
      ]
    }
  );
}

// src/components/workflow-nodes/SetupNode.tsx
import { useState as useState7, useRef as useRef4, useCallback as useCallback7, useEffect as useEffect4 } from "react";
import { createPortal as createPortal5 } from "react-dom";
import { CheckCircle as CheckCircle11, Gear as Gear4, Globe as Globe4, Key as Key3, Lightning as Lightning3, WarningCircle as WarningCircle4 } from "@phosphor-icons/react";
import { jsx as jsx128, jsxs as jsxs111 } from "react/jsx-runtime";
function getStepIcon(nodeType) {
  const common = { size: 14, weight: "duotone", className: "text-muted-foreground" };
  switch (nodeType) {
    case "login":
      return /* @__PURE__ */ jsx128(Key3, { ...common, className: "text-blue-600" });
    case "navigate":
      return /* @__PURE__ */ jsx128(Globe4, { ...common, className: "text-green-600" });
    case "action":
      return /* @__PURE__ */ jsx128(Lightning3, { ...common, className: "text-purple-600" });
    case "assertion":
      return /* @__PURE__ */ jsx128(CheckCircle11, { ...common, className: "text-orange-600" });
    case "setup":
      return /* @__PURE__ */ jsx128(Gear4, { ...common, className: "text-purple-600" });
    default:
      return /* @__PURE__ */ jsx128(WarningCircle4, { ...common });
  }
}
var FLYOUT_WIDTH = 300;
var FLYOUT_GAP = 12;
function SetupNode({ data, nodeId, selected, focused }) {
  const displayName = data?.setupFlowPreview?.flowName?.trim() || data?.setupFlowName?.trim() || "Select a setup flow";
  const versionDisplay = data.snapshotVersion === null ? "Latest" : data.snapshotVersion ? `v${data.snapshotVersion}` : null;
  const isConfigured = Boolean(data.setupFlowId);
  const preview = data.setupFlowPreview ?? null;
  const previewSteps = preview?.steps ?? [];
  const previewCount = preview?.totalSteps ?? previewSteps.length;
  const shouldShowFlyout = isConfigured && preview !== null;
  const [isHovered, setIsHovered] = useState7(false);
  const [flyoutPos, setFlyoutPos] = useState7(null);
  const nodeRef = useRef4(null);
  const hoverTimeoutRef = useRef4(void 0);
  const computeFlyoutPosition = useCallback7(() => {
    if (!nodeRef.current) return null;
    const rect = nodeRef.current.getBoundingClientRect();
    let left = rect.left - FLYOUT_WIDTH - FLYOUT_GAP;
    if (left < 8) {
      left = rect.right + FLYOUT_GAP;
    }
    const estimatedHeight = 300;
    let top = rect.top + rect.height / 2 - estimatedHeight / 2;
    top = Math.max(8, Math.min(top, window.innerHeight - estimatedHeight - 8));
    return { left, top };
  }, []);
  const handleMouseEnter = useCallback7(() => {
    clearTimeout(hoverTimeoutRef.current);
    setFlyoutPos(computeFlyoutPosition());
    setIsHovered(true);
  }, [computeFlyoutPosition]);
  const handleMouseLeave = useCallback7(() => {
    hoverTimeoutRef.current = setTimeout(() => setIsHovered(false), 100);
  }, []);
  useEffect4(() => () => clearTimeout(hoverTimeoutRef.current), []);
  const quickCreateActions = data.isPersisted !== false ? {
    onCreateNavigate: data.onCreateNavigate ? () => data.onCreateNavigate(data) : void 0,
    onCreateAction: data.onCreateAction ? () => data.onCreateAction(data) : void 0,
    onCreateAssertion: data.onCreateAssertion ? () => data.onCreateAssertion(data) : void 0,
    onCreateWait: data.onCreateWait ? () => data.onCreateWait(data) : void 0,
    onCreateLogin: data.onCreateLogin ? () => data.onCreateLogin(data) : void 0,
    onCreateCondition: data.onCreateCondition ? () => data.onCreateCondition(data) : void 0,
    onCreateApiSequence: data.onCreateApiSequence ?? void 0,
    onCreateNotification: data.onCreateNotification ?? void 0,
    onCreateAudio: data.onCreateAudio ?? void 0,
    onCreateAudioAssertion: data.onCreateAudioAssertion ?? void 0,
    onCreateVisualCheckpoint: data.onCreateVisualCheckpoint ?? void 0,
    onCreateEnd: data.onCreateEnd ?? void 0
  } : void 0;
  return /* @__PURE__ */ jsxs111(
    "div",
    {
      ref: nodeRef,
      onMouseEnter: shouldShowFlyout ? handleMouseEnter : void 0,
      onMouseLeave: shouldShowFlyout ? handleMouseLeave : void 0,
      onFocus: shouldShowFlyout ? handleMouseEnter : void 0,
      onBlur: shouldShowFlyout ? handleMouseLeave : void 0,
      children: [
        /* @__PURE__ */ jsxs111(
          WorkflowNodeWrapper,
          {
            nodeId,
            selected,
            focused,
            hasValidationError: data.hasValidationError,
            executionStatus: data.executionStatus,
            optimizerStatus: data.optimizerStatus,
            hasTargetHandle: false,
            maxWidth: false,
            quickCreateActions,
            hasOutgoingEdge: data.hasOutgoingEdge,
            isConfigured,
            hasCachedReplay: data.hasCachedReplay,
            cacheRequiresAgent: data.cacheRequiresAgent,
            cachedAt: data.cachedAt,
            onCacheClick: data.onCacheClick,
            playbookStepCount: data.playbookStepCount,
            onPlaybookClick: data.onPlaybookClick,
            onDelete: data.onDelete,
            isDeactivated: data.deactivated === true,
            onToggleDeactivate: data.onToggleDeactivate,
            isDragGhost: data.isDragGhost,
            onHoldDrag: data.onHoldDrag,
            children: [
              /* @__PURE__ */ jsx128(
                NodeHeader,
                {
                  icon: /* @__PURE__ */ jsx128(Gear4, { size: 16, weight: "duotone", className: "text-purple-600" }),
                  label: "Setup Flow",
                  iconBgClass: "bg-purple-500/10"
                }
              ),
              /* @__PURE__ */ jsx128("div", { className: "text-sm font-medium text-foreground", children: displayName }),
              versionDisplay && /* @__PURE__ */ jsx128("div", { className: "text-xs text-muted-foreground mt-1", children: versionDisplay }),
              shouldShowFlyout && /* @__PURE__ */ jsx128("div", { className: "mt-2", children: /* @__PURE__ */ jsxs111("div", { className: "inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground", children: [
                /* @__PURE__ */ jsxs111("span", { children: [
                  previewCount,
                  " step",
                  previewCount === 1 ? "" : "s"
                ] }),
                /* @__PURE__ */ jsx128("span", { className: "opacity-70", children: "\xB7" }),
                /* @__PURE__ */ jsx128("span", { className: "opacity-70", children: "Hover to preview" })
              ] }) })
            ]
          }
        ),
        shouldShowFlyout && isHovered && flyoutPos && createPortal5(
          /* @__PURE__ */ jsxs111(
            "div",
            {
              className: "fixed z-[9999] pointer-events-auto",
              style: { left: flyoutPos.left, top: flyoutPos.top, width: FLYOUT_WIDTH },
              onMouseEnter: handleMouseEnter,
              onMouseLeave: handleMouseLeave,
              onPointerDown: (e) => e.stopPropagation(),
              onClick: (e) => e.stopPropagation(),
              children: [
                /* @__PURE__ */ jsx128("div", { className: "absolute top-0 bottom-0 -right-4 w-4", "aria-hidden": "true" }),
                /* @__PURE__ */ jsxs111("div", { className: "rounded-xl border border-border/70 bg-card/95 backdrop-blur shadow-xl overflow-hidden", children: [
                  /* @__PURE__ */ jsx128("div", { className: "px-3 py-2.5 border-b border-border/60 bg-gradient-to-r from-purple-500/10 via-transparent to-transparent", children: /* @__PURE__ */ jsx128("div", { className: "flex items-start justify-between gap-3", children: /* @__PURE__ */ jsx128("div", { className: "min-w-0", children: /* @__PURE__ */ jsxs111("div", { className: "flex items-center gap-2", children: [
                    /* @__PURE__ */ jsx128("div", { className: "h-7 w-7 rounded-lg bg-purple-500/10 flex items-center justify-center", children: /* @__PURE__ */ jsx128(Gear4, { size: 14, weight: "duotone", className: "text-purple-600" }) }),
                    /* @__PURE__ */ jsxs111("div", { className: "min-w-0", children: [
                      /* @__PURE__ */ jsx128("div", { className: "text-sm font-semibold text-foreground truncate", children: preview.flowName || displayName }),
                      /* @__PURE__ */ jsxs111("div", { className: "text-[11px] text-muted-foreground", children: [
                        "Runs before this flow \xB7 ",
                        previewCount,
                        " step",
                        previewCount === 1 ? "" : "s",
                        versionDisplay ? ` \xB7 ${versionDisplay}` : ""
                      ] })
                    ] })
                  ] }) }) }) }),
                  /* @__PURE__ */ jsxs111("div", { className: "px-2 py-2 max-h-[220px] overflow-auto", children: [
                    previewSteps.length === 0 ? /* @__PURE__ */ jsx128("div", { className: "px-3 py-3 text-xs text-muted-foreground", children: "Couldn't load steps for this setup flow." }) : /* @__PURE__ */ jsx128("ol", { className: "space-y-1", children: previewSteps.slice(0, 10).map((step, idx) => /* @__PURE__ */ jsxs111(
                      "li",
                      {
                        className: cn(
                          "flex items-start gap-2 rounded-lg px-3 py-2",
                          "hover:bg-muted/60 transition-colors"
                        ),
                        children: [
                          /* @__PURE__ */ jsxs111("div", { className: "mt-0.5 flex items-center gap-2", children: [
                            /* @__PURE__ */ jsx128("div", { className: "w-5 text-[10px] font-semibold text-muted-foreground tabular-nums", children: idx + 1 }),
                            /* @__PURE__ */ jsx128("div", { className: "h-6 w-6 rounded-md bg-muted/50 border border-border/50 flex items-center justify-center", children: getStepIcon(step.nodeType) })
                          ] }),
                          /* @__PURE__ */ jsxs111("div", { className: "min-w-0 flex-1", children: [
                            /* @__PURE__ */ jsx128("div", { className: "text-xs font-semibold text-foreground leading-5 truncate", children: step.title }),
                            step.subtitle && /* @__PURE__ */ jsx128("div", { className: "text-[11px] text-muted-foreground leading-4 truncate", children: step.subtitle })
                          ] })
                        ]
                      },
                      step.id
                    )) }),
                    previewSteps.length > 10 && /* @__PURE__ */ jsxs111("div", { className: "px-3 pt-2 text-[11px] text-muted-foreground", children: [
                      "+",
                      previewSteps.length - 10,
                      " more step",
                      previewSteps.length - 10 === 1 ? "" : "s"
                    ] })
                  ] })
                ] })
              ]
            }
          ),
          document.body
        )
      ]
    }
  );
}

// src/components/workflow-nodes/WaitNode.tsx
import { Hourglass as Hourglass2 } from "@phosphor-icons/react";

// src/hooks/useCountdown.ts
import { useState as useState8, useEffect as useEffect5 } from "react";
function useCountdown(resumeAt, totalDurationSeconds) {
  const [state, setState] = useState8({
    remaining: 0,
    progress: 0,
    formatted: "",
    isComplete: false,
    isResuming: false
  });
  useEffect5(() => {
    if (!resumeAt) {
      setState({
        remaining: 0,
        progress: 0,
        formatted: "",
        isComplete: false,
        isResuming: false
      });
      return;
    }
    const targetTime = new Date(resumeAt).getTime();
    const startTime = totalDurationSeconds ? targetTime - totalDurationSeconds * 1e3 : Date.now();
    const totalDuration = targetTime - startTime;
    const update = () => {
      const now2 = Date.now();
      const remaining = Math.max(0, targetTime - now2);
      const elapsed = now2 - startTime;
      const progress = totalDuration > 0 ? Math.min(1, Math.max(0, elapsed / totalDuration)) : 0;
      const remainingSecs = Math.ceil(remaining / 1e3);
      const mins = Math.floor(remainingSecs / 60);
      const secs = remainingSecs % 60;
      let formatted;
      if (mins > 0) {
        formatted = `${mins}m ${secs}s`;
      } else {
        formatted = `${secs}s`;
      }
      const isComplete = remaining <= 0;
      const isResuming = isComplete && now2 - targetTime < 5e3;
      setState({
        remaining: remainingSecs,
        progress,
        formatted,
        isComplete,
        isResuming
      });
    };
    update();
    const interval = setInterval(update, 1e3);
    return () => clearInterval(interval);
  }, [resumeAt, totalDurationSeconds]);
  return state;
}

// src/components/workflow-nodes/WaitNode.tsx
import { jsx as jsx129, jsxs as jsxs112 } from "react/jsx-runtime";
var formatDelay = (seconds) => {
  const minute = 60;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (seconds % day === 0) return `${seconds / day}d`;
  if (seconds % hour === 0) return `${seconds / hour}h`;
  if (seconds % minute === 0) return `${seconds / minute}m`;
  return `${seconds}s`;
};
function WaitNode({ data, nodeId, selected, focused }) {
  const delaySeconds = typeof data.delaySeconds === "number" ? data.delaySeconds : 1800;
  const isConditionWait = data.mode === "condition";
  const passCondition = data.passCondition?.trim() ?? "";
  const isConfigured = isConditionWait ? passCondition.length > 0 : delaySeconds > 0;
  const isWaiting = data.executionStatus === "waiting" && data.resumeAt;
  const countdown = useCountdown(
    isWaiting ? data.resumeAt : null,
    isConditionWait && typeof data.estimatedWaitSeconds === "number" ? data.estimatedWaitSeconds : delaySeconds
  );
  const quickCreateActions = data.isPersisted !== false ? {
    onCreateNavigate: data.onCreateNavigate ? () => data.onCreateNavigate(data) : void 0,
    onCreateAction: data.onCreateAction ? () => data.onCreateAction(data) : void 0,
    onCreateAssertion: data.onCreateAssertion ? () => data.onCreateAssertion(data) : void 0,
    onCreateLogin: data.onCreateLogin ? () => data.onCreateLogin(data) : void 0,
    onCreateCondition: data.onCreateCondition ? () => data.onCreateCondition(data) : void 0,
    onCreateApiSequence: data.onCreateApiSequence ?? void 0,
    onCreateNotification: data.onCreateNotification ?? void 0,
    onCreateAudio: data.onCreateAudio ?? void 0,
    onCreateAudioAssertion: data.onCreateAudioAssertion ?? void 0,
    onCreateVisualCheckpoint: data.onCreateVisualCheckpoint ?? void 0,
    onCreateEnd: data.onCreateEnd ?? void 0
  } : void 0;
  return /* @__PURE__ */ jsxs112(
    WorkflowNodeWrapper,
    {
      nodeId,
      selected,
      focused,
      hasValidationError: data.hasValidationError,
      executionStatus: data.executionStatus,
      optimizerStatus: data.optimizerStatus,
      isConfigured,
      quickCreateActions,
      hasOutgoingEdge: data.hasOutgoingEdge,
      hasCachedReplay: data.hasCachedReplay,
      cacheRequiresAgent: data.cacheRequiresAgent,
      cachedAt: data.cachedAt,
      onCacheClick: data.onCacheClick,
      playbookStepCount: data.playbookStepCount,
      onPlaybookClick: data.onPlaybookClick,
      onDelete: data.onDelete,
      isDeactivated: data.deactivated === true,
      onToggleDeactivate: data.onToggleDeactivate,
      isDragGhost: data.isDragGhost,
      onHoldDrag: data.onHoldDrag,
      children: [
        /* @__PURE__ */ jsx129(
          NodeHeader,
          {
            icon: /* @__PURE__ */ jsx129(Hourglass2, { size: 16, weight: "duotone", className: "text-amber-700" }),
            label: "Wait",
            iconBgClass: "bg-amber-500/10"
          }
        ),
        isConditionWait ? /* @__PURE__ */ jsxs112(
          "div",
          {
            className: "text-sm font-medium text-foreground truncate",
            title: passCondition || void 0,
            children: [
              "Wait until ",
              passCondition || "condition passes"
            ]
          }
        ) : /* @__PURE__ */ jsxs112("div", { className: "text-sm font-medium text-foreground truncate", children: [
          "Delay ",
          formatDelay(delaySeconds)
        ] }),
        isWaiting && countdown.formatted ? /* @__PURE__ */ jsx129("div", { className: "text-xs text-amber-600 dark:text-amber-400 mt-1 font-medium tabular-nums", children: countdown.isResuming ? "Resuming..." : `${countdown.formatted} remaining` }) : /* @__PURE__ */ jsx129("div", { className: "text-xs text-muted-foreground mt-1", children: isConditionWait ? "Checks the page asynchronously until the condition passes" : "Schedules the next segment to resume later" })
      ]
    }
  );
}

// src/components/workflow-nodes/ConditionNode.tsx
import React87 from "react";
import { Button as AriaButton5 } from "react-aria-components";
import { GitBranch as GitBranch3, DotsThreeVertical as DotsThreeVertical3, Trash as Trash3 } from "@phosphor-icons/react";
import { Plus as Plus5 } from "@phosphor-icons/react";
import { jsx as jsx130, jsxs as jsxs113 } from "react/jsx-runtime";
var OPERATOR_SYMBOLS = {
  equals: "=",
  not_equals: "\u2260",
  gt: ">",
  lt: "<",
  gte: "\u2265",
  lte: "\u2264",
  contains: "contains",
  not_contains: "does not contain",
  is_set: "is set",
  is_not_set: "is not set"
};
var UNARY_OPERATORS = /* @__PURE__ */ new Set(["is_set", "is_not_set"]);
function formatPredicate(p) {
  const subject = p.subject.kind === "acting_as" ? "acting as" : `$var.${p.subject.name}`;
  const op = OPERATOR_SYMBOLS[p.operator] ?? p.operator;
  if (UNARY_OPERATORS.has(p.operator)) return `${subject} ${op}`;
  const value = p.subject.kind === "acting_as" ? "persona" : p.value ?? "";
  return `${subject} ${op} ${value}`.trim();
}
function ConditionNode({ data, nodeId, selected, focused }) {
  const displayText = data?.predicate ? formatPredicate(data.predicate) : data?.conditionDescription || "Define condition...";
  const isConfigured = !!data?.predicate || !!data?.conditionDescription;
  const resultIndicator = data.conditionResult === "true" ? /* @__PURE__ */ jsx130("span", { className: "text-teal-600 font-semibold", children: "(Yes)" }) : data.conditionResult === "false" ? /* @__PURE__ */ jsx130("span", { className: "text-slate-500 font-semibold", children: "(No)" }) : null;
  const showYesBranchMenu = !data.hasYesBranchEdge && data.isPersisted !== false && data.onCreateYesBranchNode;
  const showNoBranchMenu = !data.hasNoBranchEdge && data.isPersisted !== false && data.onCreateNoBranchNode;
  return /* @__PURE__ */ jsxs113(
    "div",
    {
      className: cn(
        "px-4 py-3 rounded-lg border bg-card min-w-[200px] max-w-[300px] relative group",
        "transition-all duration-200",
        getStatusBorderClasses(data.executionStatus || "idle", selected, focused),
        !isConfigured && "border-dashed opacity-75",
        data.hasValidationError && "border-destructive border-dashed ring-1 ring-destructive/20",
        // Diamond-like styling with clipped corners
        "bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-950/20 dark:to-teal-950/20"
      ),
      children: [
        (data.playbookStepCount || data.hasCachedReplay) && /* @__PURE__ */ jsx130(
          ConditionPlaybookIndicator,
          {
            playbookStepCount: data.playbookStepCount,
            hasCachedReplay: data.hasCachedReplay,
            cacheRequiresAgent: data.cacheRequiresAgent,
            cachedAt: data.cachedAt,
            onClick: data.onPlaybookClick ?? data.onCacheClick
          }
        ),
        data.onDelete && /* @__PURE__ */ jsx130(ConditionNodeContextMenu, { onDelete: data.onDelete }),
        /* @__PURE__ */ jsx130(WorkflowHandle, { type: "target", nodeId }),
        /* @__PURE__ */ jsxs113("div", { className: "flex items-center gap-2 mb-1", children: [
          /* @__PURE__ */ jsx130("div", { className: "p-1.5 rounded bg-cyan-500/10", children: /* @__PURE__ */ jsx130(GitBranch3, { size: 16, weight: "duotone", className: "text-cyan-600" }) }),
          /* @__PURE__ */ jsx130("span", { className: "text-xs font-semibold text-muted-foreground uppercase tracking-wide", children: "Condition" }),
          resultIndicator
        ] }),
        /* @__PURE__ */ jsx130("div", { className: "text-sm font-medium text-foreground whitespace-pre-wrap break-words", children: displayText }),
        data.evaluationHints && /* @__PURE__ */ jsxs113("div", { className: "mt-2 text-xs text-muted-foreground italic", children: [
          "Hint: ",
          data.evaluationHints
        ] }),
        showYesBranchMenu && /* @__PURE__ */ jsx130(
          BranchQuickMenu,
          {
            branchType: "yes",
            onCreateNode: data.onCreateYesBranchNode,
            enableApiSequence: data.enableApiSequence,
            className: "absolute -bottom-10 left-1/2 -translate-x-1/2 z-20"
          }
        ),
        showNoBranchMenu && /* @__PURE__ */ jsx130(
          BranchQuickMenu,
          {
            branchType: "no",
            onCreateNode: data.onCreateNoBranchNode,
            enableApiSequence: data.enableApiSequence,
            className: "absolute top-1/2 -left-12 -translate-y-1/2 z-20"
          }
        ),
        /* @__PURE__ */ jsx130(
          NodeStatusFooter,
          {
            executionStatus: data.executionStatus || "idle",
            optimizerStatus: data.optimizerStatus
          }
        ),
        /* @__PURE__ */ jsx130(
          WorkflowHandle,
          {
            type: "source",
            nodeId,
            handleId: "true",
            position: "center",
            tooltip: "Yes - condition is true"
          }
        ),
        /* @__PURE__ */ jsx130(
          WorkflowHandle,
          {
            type: "source",
            nodeId,
            handleId: "false",
            position: "side-left",
            tooltip: "No - condition is false"
          }
        )
      ]
    }
  );
}
function ConditionPlaybookIndicator({
  playbookStepCount,
  hasCachedReplay = false,
  cacheRequiresAgent = false,
  cachedAt,
  onClick
}) {
  const hasPlaybook = playbookStepCount !== null && playbookStepCount !== void 0 && playbookStepCount > 0;
  let label;
  let colorClass;
  if (hasPlaybook && hasCachedReplay) {
    label = `${playbookStepCount} step${playbookStepCount === 1 ? "" : "s"}`;
    colorClass = cacheRequiresAgent ? "bg-amber-100 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300" : "bg-cyan-100 dark:bg-cyan-900/40 border border-cyan-300 dark:border-cyan-700 text-cyan-700 dark:text-cyan-300";
  } else if (hasPlaybook) {
    label = `${playbookStepCount} step${playbookStepCount === 1 ? "" : "s"}`;
    colorClass = "bg-muted border border-border text-muted-foreground";
  } else {
    label = cacheRequiresAgent ? "Agent" : "Cached";
    colorClass = cacheRequiresAgent ? "bg-amber-100 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300" : "bg-cyan-100 dark:bg-cyan-900/40 border border-cyan-300 dark:border-cyan-700 text-cyan-700 dark:text-cyan-300";
  }
  let tooltip = hasPlaybook ? `${playbookStepCount} playbook step${playbookStepCount === 1 ? "" : "s"}` : cacheRequiresAgent ? "Agent-assisted replay" : "Cached replay available";
  if (cachedAt) {
    try {
      const relative = getRelativeTimeString2(new Date(cachedAt));
      tooltip += `, cached ${relative}`;
    } catch {
    }
  }
  const handleClick = onClick ? (e) => {
    e.stopPropagation();
    onClick(e);
  } : void 0;
  return /* @__PURE__ */ jsx130(
    "div",
    {
      className: cn(
        "absolute -top-2 -right-2 z-10 px-1.5 py-0.5 rounded-md text-[10px] font-medium select-none",
        colorClass,
        onClick && "cursor-pointer transition-colors"
      ),
      title: tooltip,
      onClick: handleClick,
      onPointerDown: onClick ? (e) => e.stopPropagation() : void 0,
      children: label
    }
  );
}
function getRelativeTimeString2(date) {
  const now2 = /* @__PURE__ */ new Date();
  const diffMs = now2.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1e3);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays > 0) {
    return diffDays === 1 ? "1 day ago" : `${diffDays} days ago`;
  }
  if (diffHours > 0) {
    return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
  }
  if (diffMins > 0) {
    return diffMins === 1 ? "1 minute ago" : `${diffMins} minutes ago`;
  }
  return "just now";
}
function ConditionNodeContextMenu({ onDelete }) {
  const [isOpen, setIsOpen] = React87.useState(false);
  return /* @__PURE__ */ jsx130(
    "div",
    {
      className: "absolute bottom-2 right-2 z-10",
      onPointerDown: (e) => e.stopPropagation(),
      onClick: (e) => e.stopPropagation(),
      children: /* @__PURE__ */ jsxs113(MenuTrigger, { isOpen, onOpenChange: setIsOpen, children: [
        /* @__PURE__ */ jsx130(
          AriaButton5,
          {
            className: cn(
              "p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-all outline-none",
              isOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus:opacity-100"
            ),
            "aria-label": "Node actions",
            children: /* @__PURE__ */ jsx130(DotsThreeVertical3, { size: 16, weight: "bold" })
          }
        ),
        /* @__PURE__ */ jsx130(
          Menu,
          {
            onAction: (key) => {
              if (key === "delete") onDelete();
            },
            children: /* @__PURE__ */ jsxs113(MenuItem, { id: "delete", className: "text-destructive", children: [
              /* @__PURE__ */ jsx130(Trash3, { size: 14, className: "mr-2" }),
              "Delete"
            ] })
          }
        )
      ] })
    }
  );
}
function BranchQuickMenu({
  branchType,
  onCreateNode,
  enableApiSequence,
  className
}) {
  const isYes = branchType === "yes";
  const colorClass = isYes ? "text-teal-600" : "text-slate-500";
  const label = isYes ? "Add to Yes branch" : "Add to No branch";
  return /* @__PURE__ */ jsx130(
    "div",
    {
      className,
      onPointerDown: (e) => e.stopPropagation(),
      onClick: (e) => e.stopPropagation(),
      children: /* @__PURE__ */ jsxs113(MenuTrigger, { children: [
        /* @__PURE__ */ jsx130(Button, { variant: "outline", size: "sm", iconOnly: true, "aria-label": label, className: "shadow-sm", children: /* @__PURE__ */ jsx130(Plus5, { size: 14, weight: "bold", className: colorClass }) }),
        /* @__PURE__ */ jsxs113(Menu, { "aria-label": label, onAction: (key) => onCreateNode(key), children: [
          /* @__PURE__ */ jsx130(MenuItem, { id: "navigate", children: "Navigate" }),
          /* @__PURE__ */ jsx130(MenuItem, { id: "action", children: "Action" }),
          /* @__PURE__ */ jsx130(MenuItem, { id: "assertion", children: "Assertion" }),
          /* @__PURE__ */ jsx130(MenuItem, { id: "wait", children: "Wait" }),
          /* @__PURE__ */ jsx130(MenuItem, { id: "condition", children: "Condition" }),
          enableApiSequence && /* @__PURE__ */ jsx130(MenuItem, { id: "api_sequence", children: "API Sequence" }),
          /* @__PURE__ */ jsx130(MenuItem, { id: "end", children: "End" })
        ] })
      ] })
    }
  );
}

// src/components/workflow-nodes/ApiSequenceNode.tsx
import { Plugs as Plugs2 } from "@phosphor-icons/react";
import { jsx as jsx131, jsxs as jsxs114 } from "react/jsx-runtime";
function ApiSequenceNode({ data, nodeId, selected, focused }) {
  const steps = data._stepLabels ?? data.steps ?? [];
  const isConfigured = steps.length > 0;
  const stepCount = steps.length;
  const quickCreateActions = data.isPersisted !== false ? {
    onCreateNavigate: data.onCreateNavigate ? () => data.onCreateNavigate(data) : void 0,
    onCreateAction: data.onCreateAction ? () => data.onCreateAction(data) : void 0,
    onCreateAssertion: data.onCreateAssertion ? () => data.onCreateAssertion(data) : void 0,
    onCreateWait: data.onCreateWait ? () => data.onCreateWait(data) : void 0,
    onCreateLogin: data.onCreateLogin ? () => data.onCreateLogin(data) : void 0,
    onCreateCondition: data.onCreateCondition ? () => data.onCreateCondition(data) : void 0,
    onCreateApiSequence: data.onCreateApiSequence ?? void 0,
    onCreateNotification: data.onCreateNotification ?? void 0,
    onCreateAudio: data.onCreateAudio ?? void 0,
    onCreateAudioAssertion: data.onCreateAudioAssertion ?? void 0,
    onCreateVisualCheckpoint: data.onCreateVisualCheckpoint ?? void 0,
    onCreateEnd: data.onCreateEnd ?? void 0
  } : void 0;
  return /* @__PURE__ */ jsxs114(
    WorkflowNodeWrapper,
    {
      nodeId,
      selected,
      focused,
      hasValidationError: data.hasValidationError,
      executionStatus: data.executionStatus,
      optimizerStatus: data.optimizerStatus,
      isConfigured,
      quickCreateActions,
      hasOutgoingEdge: data.hasOutgoingEdge,
      hasCachedReplay: data.hasCachedReplay,
      cacheRequiresAgent: data.cacheRequiresAgent,
      cachedAt: data.cachedAt,
      onCacheClick: data.onCacheClick,
      playbookStepCount: data.playbookStepCount,
      onPlaybookClick: data.onPlaybookClick,
      onDelete: data.onDelete,
      isDeactivated: data.deactivated === true,
      onToggleDeactivate: data.onToggleDeactivate,
      isDragGhost: data.isDragGhost,
      onHoldDrag: data.onHoldDrag,
      children: [
        /* @__PURE__ */ jsx131(
          NodeHeader,
          {
            icon: /* @__PURE__ */ jsx131(Plugs2, { size: 16, weight: "duotone", className: "text-cyan-600" }),
            label: "API Sequence",
            iconBgClass: "bg-cyan-500/10"
          }
        ),
        /* @__PURE__ */ jsx131("div", { className: "text-sm font-medium text-foreground truncate", children: data.description || (stepCount > 0 ? `${stepCount} API call${stepCount !== 1 ? "s" : ""}` : "Configure API calls") }),
        stepCount > 0 ? /* @__PURE__ */ jsxs114("div", { className: "mt-2 space-y-1", children: [
          steps.slice(0, 3).map((step, index) => /* @__PURE__ */ jsxs114("div", { className: "flex items-center gap-2 text-xs text-muted-foreground", children: [
            /* @__PURE__ */ jsx131("span", { className: "px-1.5 py-0.5 rounded bg-muted font-mono text-[10px] font-semibold", children: step.method || "GET" }),
            /* @__PURE__ */ jsx131("span", { className: "truncate flex-1", children: step.urlPattern || "No URL" })
          ] }, step.stepId || index)),
          stepCount > 3 && /* @__PURE__ */ jsxs114("div", { className: "text-xs text-muted-foreground", children: [
            "+",
            stepCount - 3,
            " more step",
            stepCount - 3 !== 1 ? "s" : ""
          ] })
        ] }) : /* @__PURE__ */ jsx131("div", { className: "text-xs text-muted-foreground mt-1", children: "Execute multiple API calls in sequence" })
      ]
    }
  );
}

// src/components/workflow-nodes/EndNode.tsx
import { CheckCircle as CheckCircle12, XCircle as XCircle5 } from "@phosphor-icons/react";
import { jsx as jsx132, jsxs as jsxs115 } from "react/jsx-runtime";
function EndNode({ data, nodeId, selected, focused }) {
  const outcome = data.outcome ?? "pass";
  const isFail = outcome === "fail";
  return /* @__PURE__ */ jsxs115(
    WorkflowNodeWrapper,
    {
      nodeId,
      selected,
      focused,
      hasValidationError: data.hasValidationError,
      executionStatus: data.executionStatus,
      optimizerStatus: data.optimizerStatus,
      isConfigured: true,
      hasSourceHandle: false,
      hasOutgoingEdge: true,
      onDelete: data.onDelete,
      isDragGhost: data.isDragGhost,
      onHoldDrag: data.onHoldDrag,
      children: [
        /* @__PURE__ */ jsx132(
          NodeHeader,
          {
            icon: isFail ? /* @__PURE__ */ jsx132(XCircle5, { size: 16, weight: "duotone", className: "text-red-600" }) : /* @__PURE__ */ jsx132(CheckCircle12, { size: 16, weight: "duotone", className: "text-emerald-600" }),
            label: "End",
            iconBgClass: isFail ? "bg-red-500/10" : "bg-emerald-500/10"
          }
        ),
        /* @__PURE__ */ jsx132("div", { className: "text-sm font-medium text-foreground truncate", children: isFail ? "Fail" : "Pass" }),
        /* @__PURE__ */ jsx132("div", { className: "text-xs text-muted-foreground mt-1", children: isFail ? "Terminates workflow with failure" : "Terminates workflow with success" })
      ]
    }
  );
}

// src/components/workflow-nodes/SeedNode.tsx
import { ArrowSquareOut as ArrowSquareOut3, Plant as Plant2 } from "@phosphor-icons/react";
import { jsx as jsx133, jsxs as jsxs116 } from "react/jsx-runtime";
function SeedNode({ data, nodeId, selected, focused }) {
  const displayName = data?.seedWorkflowName?.trim() || "Select a seed script";
  const versionDisplay = data.snapshotVersion === null ? "Latest" : data.snapshotVersion ? `v${data.snapshotVersion}` : null;
  const isConfigured = Boolean(data.seedWorkflowId);
  const failureMode = data.onFailure ?? "block";
  const quickCreateActions = data.isPersisted !== false ? {
    onCreateNavigate: data.onCreateNavigate ? () => data.onCreateNavigate(data) : void 0,
    onCreateAction: data.onCreateAction ? () => data.onCreateAction(data) : void 0,
    onCreateAssertion: data.onCreateAssertion ? () => data.onCreateAssertion(data) : void 0,
    onCreateWait: data.onCreateWait ? () => data.onCreateWait(data) : void 0,
    onCreateLogin: data.onCreateLogin ? () => data.onCreateLogin(data) : void 0,
    onCreateCondition: data.onCreateCondition ? () => data.onCreateCondition(data) : void 0,
    onCreateApiSequence: data.onCreateApiSequence ?? void 0,
    onCreateNotification: data.onCreateNotification ?? void 0,
    onCreateAudio: data.onCreateAudio ?? void 0,
    onCreateAudioAssertion: data.onCreateAudioAssertion ?? void 0,
    onCreateVisualCheckpoint: data.onCreateVisualCheckpoint ?? void 0,
    onCreateEnd: data.onCreateEnd ?? void 0
  } : void 0;
  return /* @__PURE__ */ jsxs116(
    WorkflowNodeWrapper,
    {
      nodeId,
      selected,
      focused,
      hasValidationError: data.hasValidationError,
      executionStatus: data.executionStatus,
      optimizerStatus: data.optimizerStatus,
      hasTargetHandle: false,
      maxWidth: false,
      quickCreateActions,
      hasOutgoingEdge: data.hasOutgoingEdge,
      isConfigured,
      hasCachedReplay: data.hasCachedReplay,
      cacheRequiresAgent: data.cacheRequiresAgent,
      cachedAt: data.cachedAt,
      onCacheClick: data.onCacheClick,
      playbookStepCount: data.playbookStepCount,
      onPlaybookClick: data.onPlaybookClick,
      onDelete: data.onDelete,
      isDeactivated: data.deactivated === true,
      onToggleDeactivate: data.onToggleDeactivate,
      isDragGhost: data.isDragGhost,
      onHoldDrag: data.onHoldDrag,
      children: [
        data.seedWorkflowId && data.onViewFlow && /* @__PURE__ */ jsx133("div", { className: "absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity", children: /* @__PURE__ */ jsx133(
          "button",
          {
            type: "button",
            onClick: (e) => {
              e.stopPropagation();
              data.onViewFlow();
            },
            className: "p-1 rounded bg-background/80 hover:bg-muted border border-border",
            title: "View seed data flow",
            children: /* @__PURE__ */ jsx133(ArrowSquareOut3, { size: 14, weight: "bold", className: "text-muted-foreground" })
          }
        ) }),
        /* @__PURE__ */ jsx133(
          NodeHeader,
          {
            icon: /* @__PURE__ */ jsx133(Plant2, { size: 16, weight: "duotone", className: "text-teal-600" }),
            label: "Seed Data",
            iconBgClass: "bg-teal-500/10"
          }
        ),
        /* @__PURE__ */ jsx133("div", { className: "text-sm font-medium text-foreground", children: displayName }),
        isConfigured && versionDisplay && /* @__PURE__ */ jsx133("div", { className: "text-xs text-muted-foreground mt-1", children: versionDisplay }),
        isConfigured && failureMode === "warn" && /* @__PURE__ */ jsx133("div", { className: "text-[10px] text-amber-500 mt-1", children: "Continue on failure" })
      ]
    }
  );
}

// src/components/workflow-nodes/CallFlowNode.tsx
import { ArrowSquareOut as ArrowSquareOut4, FlowArrow as FlowArrow3 } from "@phosphor-icons/react";
import { jsx as jsx134, jsxs as jsxs117 } from "react/jsx-runtime";
function CallFlowNode({ data, nodeId, selected, focused }) {
  const displayName = data?.flowName?.trim() || "Select a flow";
  const versionDisplay = data.snapshotVersion === null ? "Latest" : data.snapshotVersion ? `v${data.snapshotVersion}` : null;
  const isConfigured = Boolean(data.flowId);
  const failureMode = data.onFailure ?? "block";
  const quickCreateActions = data.isPersisted !== false ? {
    onCreateNavigate: data.onCreateNavigate ? () => data.onCreateNavigate(data) : void 0,
    onCreateAction: data.onCreateAction ? () => data.onCreateAction(data) : void 0,
    onCreateAssertion: data.onCreateAssertion ? () => data.onCreateAssertion(data) : void 0,
    onCreateWait: data.onCreateWait ? () => data.onCreateWait(data) : void 0,
    onCreateLogin: data.onCreateLogin ? () => data.onCreateLogin(data) : void 0,
    onCreateCondition: data.onCreateCondition ? () => data.onCreateCondition(data) : void 0,
    onCreateApiSequence: data.onCreateApiSequence ?? void 0,
    onCreateNotification: data.onCreateNotification ?? void 0,
    onCreateAudio: data.onCreateAudio ?? void 0,
    onCreateAudioAssertion: data.onCreateAudioAssertion ?? void 0,
    onCreateVisualCheckpoint: data.onCreateVisualCheckpoint ?? void 0,
    onCreateEnd: data.onCreateEnd ?? void 0
  } : void 0;
  return /* @__PURE__ */ jsxs117(
    WorkflowNodeWrapper,
    {
      nodeId,
      selected,
      focused,
      hasValidationError: data.hasValidationError,
      executionStatus: data.executionStatus,
      optimizerStatus: data.optimizerStatus,
      maxWidth: false,
      quickCreateActions,
      hasOutgoingEdge: data.hasOutgoingEdge,
      isConfigured,
      hasCachedReplay: data.hasCachedReplay,
      cacheRequiresAgent: data.cacheRequiresAgent,
      cachedAt: data.cachedAt,
      onCacheClick: data.onCacheClick,
      playbookStepCount: data.playbookStepCount,
      onPlaybookClick: data.onPlaybookClick,
      onDelete: data.onDelete,
      isDeactivated: data.deactivated === true,
      onToggleDeactivate: data.onToggleDeactivate,
      isDragGhost: data.isDragGhost,
      onHoldDrag: data.onHoldDrag,
      children: [
        data.flowId && data.onViewFlow && /* @__PURE__ */ jsx134("div", { className: "absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity", children: /* @__PURE__ */ jsx134(
          "button",
          {
            type: "button",
            onClick: (e) => {
              e.stopPropagation();
              data.onViewFlow();
            },
            className: "p-1 rounded bg-background/80 hover:bg-muted border border-border",
            title: "View called flow",
            children: /* @__PURE__ */ jsx134(ArrowSquareOut4, { size: 14, weight: "bold", className: "text-muted-foreground" })
          }
        ) }),
        /* @__PURE__ */ jsx134(
          NodeHeader,
          {
            icon: /* @__PURE__ */ jsx134(FlowArrow3, { size: 16, weight: "duotone", className: "text-sky-600" }),
            label: "Run a Flow",
            iconBgClass: "bg-sky-500/10"
          }
        ),
        /* @__PURE__ */ jsx134("div", { className: "text-sm font-medium text-foreground", children: displayName }),
        isConfigured && versionDisplay && /* @__PURE__ */ jsx134("div", { className: "text-xs text-muted-foreground mt-1", children: versionDisplay }),
        isConfigured && failureMode === "warn" && /* @__PURE__ */ jsx134("div", { className: "text-[10px] text-amber-500 mt-1", children: "Continue on failure" })
      ]
    }
  );
}

// src/components/workflow-nodes/FlowLineageNode.tsx
import {
  ArrowSquareOut as ArrowSquareOut5,
  Broom,
  Plant as Plant3,
  TestTube,
  Wrench
} from "@phosphor-icons/react";
import { jsx as jsx135, jsxs as jsxs118 } from "react/jsx-runtime";
var FLOW_TYPE_META = {
  standard: { Icon: TestTube, label: "Test" },
  seed: { Icon: Plant3, label: "Seed data" },
  teardown: { Icon: Broom, label: "Teardown" },
  setup: { Icon: Wrench, label: "Setup" }
};
function FlowLineageNode({ data, nodeId, selected, focused }) {
  const meta = FLOW_TYPE_META[data.flowType] ?? { Icon: TestTube, label: data.flowType };
  const { Icon } = meta;
  return /* @__PURE__ */ jsxs118(
    WorkflowNodeWrapper,
    {
      nodeId,
      selected: selected || data.isFocus,
      focused,
      hasTargetHandle: data.hasTargetHandle ?? false,
      hasSourceHandle: data.hasSourceHandle ?? false,
      onDelete: data.onDelete,
      className: data.isFocus ? "ring-2 ring-primary border-primary bg-primary/5" : void 0,
      children: [
        data.viewFlowHref && !data.isFocus && /* @__PURE__ */ jsx135("div", { className: "absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity", children: /* @__PURE__ */ jsx135(
          "a",
          {
            href: data.viewFlowHref,
            target: "_blank",
            rel: "noopener noreferrer",
            onClick: (e) => e.stopPropagation(),
            className: "p-1 rounded bg-background/80 hover:bg-muted border border-border",
            title: "Open flow in a new tab",
            children: /* @__PURE__ */ jsx135(ArrowSquareOut5, { size: 14, weight: "bold", className: "text-muted-foreground" })
          }
        ) }),
        /* @__PURE__ */ jsx135(
          NodeHeader,
          {
            icon: /* @__PURE__ */ jsx135(Icon, { size: 16, weight: "duotone", className: "text-teal-600" }),
            label: meta.label,
            iconBgClass: "bg-teal-500/10"
          }
        ),
        /* @__PURE__ */ jsx135("div", { className: "text-sm font-medium text-foreground break-words", title: data.name, children: data.name }),
        data.isFocus && /* @__PURE__ */ jsx135("div", { className: "text-[10px] font-medium text-primary uppercase tracking-wide mt-0.5", children: "This flow" })
      ]
    }
  );
}

// src/components/workflow-nodes/SandboxActionNode.tsx
import { Cube as Cube2 } from "@phosphor-icons/react";
import { jsx as jsx136, jsxs as jsxs119 } from "react/jsx-runtime";
var SECRET_PARAM_NAME = /pass(word)?|secret|token|api_?key|private_?key|credential|auth|dsn/i;
function formatSandboxActionParams(params, isSecret) {
  const entries = Object.entries(params ?? {}).map(
    ([key, value]) => [key, SECRET_PARAM_NAME.test(key) || isSecret(key) ? "********" : value]
  );
  if (entries.length === 0) return null;
  return entries.length === 1 ? entries[0][1] : entries.map(([key, value]) => `${key}: ${value}`).join("\n");
}
function SandboxActionNode({ data, nodeId, selected, focused }) {
  const isConfigured = Boolean(data.actionSlug);
  const outputKeys = data.outputKeys ?? [];
  const heading = data.title || data.actionSlug || "Choose a sandbox action";
  const preview = formatSandboxActionParams(
    data.params,
    (key) => data.sensitiveParams === void 0 || data.sensitiveParams.includes(key)
  );
  const quickCreateActions = data.isPersisted !== false ? {
    onCreateNavigate: data.onCreateNavigate ? () => data.onCreateNavigate(data) : void 0,
    onCreateAction: data.onCreateAction ? () => data.onCreateAction(data) : void 0,
    onCreateAssertion: data.onCreateAssertion ? () => data.onCreateAssertion(data) : void 0,
    onCreateWait: data.onCreateWait ? () => data.onCreateWait(data) : void 0,
    onCreateLogin: data.onCreateLogin ? () => data.onCreateLogin(data) : void 0,
    onCreateCondition: data.onCreateCondition ? () => data.onCreateCondition(data) : void 0,
    onCreateApiSequence: data.onCreateApiSequence ?? void 0,
    onCreateSandboxAction: data.onCreateSandboxAction ?? void 0,
    onCreateNotification: data.onCreateNotification ?? void 0,
    onCreateAudio: data.onCreateAudio ?? void 0,
    onCreateAudioAssertion: data.onCreateAudioAssertion ?? void 0,
    onCreateVisualCheckpoint: data.onCreateVisualCheckpoint ?? void 0,
    onCreateEnd: data.onCreateEnd ?? void 0
  } : void 0;
  return /* @__PURE__ */ jsxs119(
    WorkflowNodeWrapper,
    {
      nodeId,
      selected,
      focused,
      hasValidationError: data.hasValidationError,
      executionStatus: data.executionStatus,
      optimizerStatus: data.optimizerStatus,
      isConfigured,
      quickCreateActions,
      hasOutgoingEdge: data.hasOutgoingEdge,
      onDelete: data.onDelete,
      isDeactivated: data.deactivated === true,
      onToggleDeactivate: data.onToggleDeactivate,
      isDragGhost: data.isDragGhost,
      onHoldDrag: data.onHoldDrag,
      children: [
        /* @__PURE__ */ jsx136(
          NodeHeader,
          {
            icon: /* @__PURE__ */ jsx136(Cube2, { size: 16, weight: "duotone", className: "text-violet-600" }),
            label: "Sandbox action",
            iconBgClass: "bg-violet-500/10"
          }
        ),
        /* @__PURE__ */ jsx136("div", { className: "text-sm font-medium text-foreground truncate", children: heading }),
        isConfigured ? /* @__PURE__ */ jsxs119("div", { className: "mt-1 space-y-1", children: [
          data.actionSlug && data.actionSlug !== heading && /* @__PURE__ */ jsx136("div", { className: "font-mono text-[10px] text-muted-foreground truncate", children: data.actionSlug }),
          preview && /* @__PURE__ */ jsx136("pre", { className: "rounded bg-muted px-1.5 py-1 font-mono text-[10px] leading-snug text-muted-foreground line-clamp-4 whitespace-pre-wrap break-all", children: preview }),
          data.description && /* @__PURE__ */ jsx136("div", { className: "text-xs text-muted-foreground line-clamp-2", children: data.description }),
          outputKeys.length > 0 && /* @__PURE__ */ jsxs119("div", { className: "flex flex-wrap gap-1 mt-1", children: [
            outputKeys.slice(0, 4).map((key) => /* @__PURE__ */ jsxs119(
              "span",
              {
                className: "px-1.5 py-0.5 rounded bg-muted font-mono text-[10px] text-muted-foreground",
                children: [
                  "$var.",
                  key
                ]
              },
              key
            )),
            outputKeys.length > 4 && /* @__PURE__ */ jsxs119("span", { className: "text-[10px] text-muted-foreground", children: [
              "+",
              outputKeys.length - 4,
              " more"
            ] })
          ] })
        ] }) : /* @__PURE__ */ jsx136("div", { className: "text-xs text-muted-foreground mt-1", children: "Run a server-side action on the sandbox instance" })
      ]
    }
  );
}

// src/components/workflow-nodes/McpNode.tsx
import { PlugsConnected as PlugsConnected2 } from "@phosphor-icons/react";
import { jsx as jsx137, jsxs as jsxs120 } from "react/jsx-runtime";
function McpNode({ data, nodeId, selected, focused }) {
  const firstInstructionLine = (data.instructions ?? "").split("\n")[0].trim();
  const isConfigured = firstInstructionLine.length > 0;
  const declaredOutputs = data.declaredOutputs ?? [];
  const quickCreateActions = data.isPersisted !== false ? {
    onCreateNavigate: data.onCreateNavigate ? () => data.onCreateNavigate(data) : void 0,
    onCreateAction: data.onCreateAction ? () => data.onCreateAction(data) : void 0,
    onCreateAssertion: data.onCreateAssertion ? () => data.onCreateAssertion(data) : void 0,
    onCreateWait: data.onCreateWait ? () => data.onCreateWait(data) : void 0,
    onCreateLogin: data.onCreateLogin ? () => data.onCreateLogin(data) : void 0,
    onCreateCondition: data.onCreateCondition ? () => data.onCreateCondition(data) : void 0,
    onCreateApiSequence: data.onCreateApiSequence ?? void 0,
    onCreateSandboxAction: data.onCreateSandboxAction ?? void 0,
    onCreateMcp: data.onCreateMcp ?? void 0,
    onCreateNotification: data.onCreateNotification ?? void 0,
    onCreateAudio: data.onCreateAudio ?? void 0,
    onCreateAudioAssertion: data.onCreateAudioAssertion ?? void 0,
    onCreateVisualCheckpoint: data.onCreateVisualCheckpoint ?? void 0,
    onCreateEnd: data.onCreateEnd ?? void 0
  } : void 0;
  return /* @__PURE__ */ jsxs120(
    WorkflowNodeWrapper,
    {
      nodeId,
      selected,
      focused,
      hasValidationError: data.hasValidationError,
      executionStatus: data.executionStatus,
      optimizerStatus: data.optimizerStatus,
      isConfigured,
      quickCreateActions,
      hasOutgoingEdge: data.hasOutgoingEdge,
      hasCachedReplay: data.hasCachedReplay,
      cacheRequiresAgent: data.cacheRequiresAgent,
      cachedAt: data.cachedAt,
      onCacheClick: data.onCacheClick,
      playbookStepCount: data.playbookStepCount,
      onPlaybookClick: data.onPlaybookClick,
      onDelete: data.onDelete,
      isDeactivated: data.deactivated === true,
      onToggleDeactivate: data.onToggleDeactivate,
      isDragGhost: data.isDragGhost,
      onHoldDrag: data.onHoldDrag,
      children: [
        /* @__PURE__ */ jsx137(
          NodeHeader,
          {
            icon: /* @__PURE__ */ jsx137(PlugsConnected2, { size: 16, weight: "duotone", className: "text-fuchsia-600" }),
            label: "MCP",
            iconBgClass: "bg-fuchsia-500/10"
          }
        ),
        /* @__PURE__ */ jsx137("div", { className: "text-sm font-medium text-foreground truncate", children: firstInstructionLine || "Describe what to do" }),
        isConfigured ? declaredOutputs.length > 0 && /* @__PURE__ */ jsxs120("div", { className: "flex flex-wrap gap-1 mt-1", children: [
          declaredOutputs.slice(0, 4).map((name) => /* @__PURE__ */ jsxs120(
            "span",
            {
              className: "px-1.5 py-0.5 rounded bg-muted font-mono text-[10px] text-muted-foreground",
              children: [
                "$var.",
                name
              ]
            },
            name
          )),
          declaredOutputs.length > 4 && /* @__PURE__ */ jsxs120("span", { className: "text-[10px] text-muted-foreground", children: [
            "+",
            declaredOutputs.length - 4,
            " more"
          ] })
        ] }) : /* @__PURE__ */ jsx137("div", { className: "text-xs text-muted-foreground mt-1", children: "Drive an MCP server with plain-language instructions" })
      ]
    }
  );
}

// src/components/workflow-nodes/NotificationNode.tsx
import { Bell as Bell3 } from "@phosphor-icons/react";
import { Fragment as Fragment27, jsx as jsx138, jsxs as jsxs121 } from "react/jsx-runtime";
function NotificationNode({ data, nodeId, selected, focused }) {
  const channel = data.channel ?? "email";
  const isConfigured = !!data.to;
  return /* @__PURE__ */ jsxs121(
    WorkflowNodeWrapper,
    {
      nodeId,
      selected,
      focused,
      hasValidationError: data.hasValidationError,
      executionStatus: data.executionStatus,
      optimizerStatus: data.optimizerStatus,
      isConfigured,
      hasOutgoingEdge: data.hasOutgoingEdge,
      onDelete: data.onDelete,
      isDeactivated: data.deactivated === true,
      onToggleDeactivate: data.onToggleDeactivate,
      isDragGhost: data.isDragGhost,
      onHoldDrag: data.onHoldDrag,
      children: [
        /* @__PURE__ */ jsx138(
          NodeHeader,
          {
            icon: /* @__PURE__ */ jsx138(Bell3, { size: 16, weight: "duotone", className: "text-pink-600" }),
            label: "Notification",
            iconBgClass: "bg-pink-500/10"
          }
        ),
        isConfigured ? /* @__PURE__ */ jsxs121(Fragment27, { children: [
          /* @__PURE__ */ jsxs121("div", { className: "text-sm font-medium text-foreground truncate", children: [
            channel === "email" ? "Email" : channel,
            " \u2192 ",
            data.to
          ] }),
          data.subject && /* @__PURE__ */ jsx138("div", { className: "text-xs text-muted-foreground mt-1 truncate", children: data.subject })
        ] }) : /* @__PURE__ */ jsx138("div", { className: "text-xs text-muted-foreground", children: "Not configured" })
      ]
    }
  );
}

// src/components/workflow-nodes/AudioNode.tsx
import { Microphone as Microphone3 } from "@phosphor-icons/react";
import { Fragment as Fragment28, jsx as jsx139, jsxs as jsxs122 } from "react/jsx-runtime";
function AudioNode({ data, nodeId, selected, focused }) {
  const isConfigured = Array.isArray(data.attachmentIds) && data.attachmentIds.length > 0;
  return /* @__PURE__ */ jsxs122(
    WorkflowNodeWrapper,
    {
      nodeId,
      selected,
      focused,
      hasValidationError: data.hasValidationError,
      executionStatus: data.executionStatus,
      optimizerStatus: data.optimizerStatus,
      isConfigured,
      hasOutgoingEdge: data.hasOutgoingEdge,
      onDelete: data.onDelete,
      isDeactivated: data.deactivated === true,
      onToggleDeactivate: data.onToggleDeactivate,
      isDragGhost: data.isDragGhost,
      onHoldDrag: data.onHoldDrag,
      children: [
        /* @__PURE__ */ jsx139(
          NodeHeader,
          {
            icon: /* @__PURE__ */ jsx139(Microphone3, { size: 16, weight: "duotone", className: "text-indigo-600" }),
            label: "Audio input",
            iconBgClass: "bg-indigo-500/10"
          }
        ),
        isConfigured ? /* @__PURE__ */ jsxs122(Fragment28, { children: [
          /* @__PURE__ */ jsx139("div", { className: "text-sm font-medium text-foreground truncate", children: data.label || "Audio clip" }),
          data.loop && /* @__PURE__ */ jsx139("div", { className: "text-xs text-muted-foreground mt-1", children: "Looping" })
        ] }) : /* @__PURE__ */ jsx139("div", { className: "text-xs text-muted-foreground", children: "No clip selected" })
      ]
    }
  );
}

// src/components/workflow-nodes/AudioAssertionNode.tsx
import { WaveformSlash as WaveformSlash2 } from "@phosphor-icons/react";
import { Fragment as Fragment29, jsx as jsx140, jsxs as jsxs123 } from "react/jsx-runtime";
function AudioAssertionNode({ data, nodeId, selected, focused }) {
  const det = data.deterministic ?? {};
  const hasDeterministic = det.minDurationMs !== void 0 || det.maxDurationMs !== void 0 || det.minRmsDb !== void 0 || det.silenceFractionMax !== void 0 || det.envelopeCorrelationMin !== void 0;
  const hasNaturalLanguage = typeof data.naturalLanguage?.prompt === "string" && data.naturalLanguage.prompt.trim().length > 0;
  const isConfigured = hasNaturalLanguage || hasDeterministic;
  const modeBits = [];
  if (hasDeterministic) modeBits.push("deterministic");
  if (hasNaturalLanguage) modeBits.push("NL");
  return /* @__PURE__ */ jsxs123(
    WorkflowNodeWrapper,
    {
      nodeId,
      selected,
      focused,
      hasValidationError: data.hasValidationError,
      executionStatus: data.executionStatus,
      optimizerStatus: data.optimizerStatus,
      isConfigured,
      hasOutgoingEdge: data.hasOutgoingEdge,
      onDelete: data.onDelete,
      isDeactivated: data.deactivated === true,
      onToggleDeactivate: data.onToggleDeactivate,
      isDragGhost: data.isDragGhost,
      onHoldDrag: data.onHoldDrag,
      children: [
        /* @__PURE__ */ jsx140(
          NodeHeader,
          {
            icon: /* @__PURE__ */ jsx140(WaveformSlash2, { size: 16, weight: "duotone", className: "text-indigo-600" }),
            label: "Audio assertion",
            iconBgClass: "bg-indigo-500/10"
          }
        ),
        isConfigured ? /* @__PURE__ */ jsxs123(Fragment29, { children: [
          /* @__PURE__ */ jsx140("div", { className: "text-sm font-medium text-foreground truncate", children: data.label || "Verify audio playback" }),
          /* @__PURE__ */ jsxs123("div", { className: "text-xs text-muted-foreground mt-1", children: [
            modeBits.join(" + "),
            " \xB7 last capture"
          ] })
        ] }) : /* @__PURE__ */ jsx140("div", { className: "text-xs text-muted-foreground", children: "No checks configured" })
      ]
    }
  );
}

// src/components/workflow-nodes/VisualCheckpointNode.tsx
import { Camera as Camera4 } from "@phosphor-icons/react";
import { Fragment as Fragment30, jsx as jsx141, jsxs as jsxs124 } from "react/jsx-runtime";
function VisualCheckpointNode({ data, nodeId, selected, focused }) {
  const name = (data.name ?? "").trim();
  const isConfigured = name.length > 0;
  return /* @__PURE__ */ jsxs124(
    WorkflowNodeWrapper,
    {
      nodeId,
      selected,
      focused,
      hasValidationError: data.hasValidationError,
      executionStatus: data.executionStatus,
      optimizerStatus: data.optimizerStatus,
      isConfigured,
      hasOutgoingEdge: data.hasOutgoingEdge,
      onDelete: data.onDelete,
      isDeactivated: data.deactivated === true,
      onToggleDeactivate: data.onToggleDeactivate,
      isDragGhost: data.isDragGhost,
      onHoldDrag: data.onHoldDrag,
      children: [
        /* @__PURE__ */ jsx141(
          NodeHeader,
          {
            icon: /* @__PURE__ */ jsx141(Camera4, { size: 16, weight: "duotone", className: "text-indigo-600" }),
            label: "Visual checkpoint",
            iconBgClass: "bg-indigo-500/10"
          }
        ),
        isConfigured ? /* @__PURE__ */ jsxs124(Fragment30, { children: [
          /* @__PURE__ */ jsx141("div", { className: "text-sm font-medium text-foreground truncate", children: name }),
          /* @__PURE__ */ jsxs124("div", { className: "text-xs text-muted-foreground mt-1 truncate", children: [
            data.target?.trim() || "Whole page",
            " \xB7 ",
            data.severity === "fail" ? "fails on change" : "records"
          ] })
        ] }) : /* @__PURE__ */ jsx141("div", { className: "text-xs text-muted-foreground", children: "No name set" })
      ]
    }
  );
}

// src/components/VerticalFlowCanvas/VerticalFlowCanvas.tsx
import React93 from "react";
import { motion as motion11, AnimatePresence as AnimatePresence6 } from "framer-motion";

// src/components/VerticalFlowCanvas/VerticalEdge.tsx
import React88 from "react";
import { Fragment as Fragment31, jsx as jsx142, jsxs as jsxs125 } from "react/jsx-runtime";
function getEdgeColor(edgeType, selected, isActive) {
  if (selected) return "#06b6d4";
  if (isActive) return "#3b82f6";
  switch (edgeType) {
    case "condition_true":
      return "#0d9488";
    // teal-600 - subtle but distinct for positive branch
    case "condition_false":
      return "#94a3b8";
    // slate - same as default, less visually loud
    default:
      return "#94a3b8";
  }
}
function getEdgeGlowColor(edgeType, selected) {
  if (selected) return "rgba(6, 182, 212, 0.3)";
  switch (edgeType) {
    case "condition_true":
      return "rgba(13, 148, 136, 0.3)";
    // teal glow for positive branch
    case "condition_false":
      return "rgba(59, 130, 246, 0.3)";
    // same as default - no special glow
    default:
      return "rgba(59, 130, 246, 0.3)";
  }
}
var CORNER_RADIUS = 8;
var NODE_WIDTH = 280;
function generateStandardPath(sourceX, sourceY, targetX, targetY) {
  const r = CORNER_RADIUS;
  const midY = (sourceY + targetY) / 2;
  if (Math.abs(sourceX - targetX) < 2) {
    return `M ${sourceX} ${sourceY} V ${targetY}`;
  }
  const dir = targetX > sourceX ? 1 : -1;
  const verticalSpace = Math.abs(midY - sourceY);
  const actualR = Math.min(r, verticalSpace - 2, Math.abs(targetX - sourceX) / 2);
  if (actualR < 2) {
    return `M ${sourceX} ${sourceY} V ${midY} H ${targetX} V ${targetY}`;
  }
  return `
    M ${sourceX} ${sourceY}
    V ${midY - actualR}
    A ${actualR} ${actualR} 0 0 ${dir === 1 ? 1 : 0} ${sourceX + dir * actualR} ${midY}
    H ${targetX - dir * actualR}
    A ${actualR} ${actualR} 0 0 ${dir === 1 ? 0 : 1} ${targetX} ${midY + actualR}
    V ${targetY}
  `.trim();
}
function generateSideSourcePath(sourceX, sourceY, targetX, targetY) {
  const r = CORNER_RADIUS;
  const horizontalOffset = 50;
  const leftX = Math.min(sourceX, targetX) - horizontalOffset;
  const verticalDistance = Math.abs(targetY - sourceY);
  const actualR = Math.min(r, horizontalOffset / 2 - 2, verticalDistance / 2 - 2);
  if (actualR < 2 || verticalDistance < 10) {
    return `M ${sourceX} ${sourceY} H ${leftX} V ${targetY} H ${targetX}`;
  }
  return `
    M ${sourceX} ${sourceY}
    H ${leftX + actualR}
    A ${actualR} ${actualR} 0 0 0 ${leftX} ${sourceY + actualR}
    V ${targetY - actualR}
    A ${actualR} ${actualR} 0 0 0 ${leftX + actualR} ${targetY}
    H ${targetX}
  `.trim();
}
function generateSideToTopPath(sourceX, sourceY, targetX, targetY) {
  const r = CORNER_RADIUS;
  const horizontalDistance = sourceX - targetX;
  const verticalDistance = targetY - sourceY;
  const actualR = Math.min(r, horizontalDistance / 2 - 2, verticalDistance - 2);
  if (actualR < 2 || verticalDistance < 10 || horizontalDistance < 10) {
    return `M ${sourceX} ${sourceY} H ${targetX} V ${targetY}`;
  }
  return `
    M ${sourceX} ${sourceY}
    H ${targetX + actualR}
    A ${actualR} ${actualR} 0 0 0 ${targetX} ${sourceY + actualR}
    V ${targetY}
  `.trim();
}
function generateBottomToSidePath(sourceX, sourceY, targetX, targetY) {
  const r = CORNER_RADIUS;
  const hDist = targetX - sourceX;
  if (targetY > sourceY + r * 2) {
    const actualR2 = Math.min(r, hDist / 2, (targetY - sourceY) / 2);
    if (actualR2 < 2 || hDist < 10) {
      return `M ${sourceX} ${sourceY} V ${targetY} H ${targetX}`;
    }
    return `M ${sourceX} ${sourceY} V ${targetY - actualR2} A ${actualR2} ${actualR2} 0 0 0 ${sourceX + actualR2} ${targetY} H ${targetX}`;
  }
  const floorY = sourceY + 30;
  const gapStart = sourceX + NODE_WIDTH / 2;
  const routeX = gapStart + (targetX - gapStart) / 2;
  const actualR = Math.min(r, (routeX - sourceX) / 2, (targetX - routeX) / 2, 14);
  if (actualR < 2 || hDist < 4) {
    return `M ${sourceX} ${sourceY} V ${floorY} H ${routeX} V ${targetY} H ${targetX}`;
  }
  return `M ${sourceX} ${sourceY} V ${floorY - actualR} A ${actualR} ${actualR} 0 0 0 ${sourceX + actualR} ${floorY} H ${routeX - actualR} A ${actualR} ${actualR} 0 0 1 ${routeX} ${floorY - actualR} V ${targetY + actualR} A ${actualR} ${actualR} 0 0 0 ${routeX + actualR} ${targetY} H ${targetX}`;
}
function VerticalEdge({
  id,
  sourceNodeId,
  targetNodeId,
  sourceX,
  sourceY,
  targetX,
  targetY,
  animated = true,
  isActive = false,
  onClick,
  selected = false,
  edgeType = "default",
  isMergeTarget = false,
  isMergeEdge = false,
  highlighted = false
}) {
  const [isHovered, setIsHovered] = React88.useState(false);
  const isSideSource = edgeType === "condition_false";
  let path;
  if (isMergeEdge) {
    path = generateBottomToSidePath(sourceX, sourceY, targetX, targetY);
  } else if (isSideSource) {
    path = isMergeTarget ? generateSideSourcePath(sourceX, sourceY, targetX, targetY) : generateSideToTopPath(sourceX, sourceY, targetX, targetY);
  } else {
    path = generateStandardPath(sourceX, sourceY, targetX, targetY);
  }
  const handleClick = React88.useCallback(
    (e) => {
      e.stopPropagation();
      onClick?.(id, e);
    },
    [id, onClick]
  );
  const strokeColor = getEdgeColor(edgeType, selected, isActive);
  const glowColor = getEdgeGlowColor(edgeType, selected);
  const showPermanentGlow = isActive || selected || edgeType === "condition_true";
  return /* @__PURE__ */ jsxs125(
    "g",
    {
      "data-edge-id": id,
      "data-edge-type": edgeType,
      "data-source-node-id": sourceNodeId,
      "data-target-node-id": targetNodeId,
      children: [
        /* @__PURE__ */ jsx142(
          "path",
          {
            d: path,
            fill: "none",
            stroke: "transparent",
            strokeWidth: 32,
            style: {
              cursor: onClick ? "pointer" : "default",
              pointerEvents: "stroke"
            },
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
            onClick: onClick ? handleClick : void 0
          }
        ),
        highlighted && !selected && /* @__PURE__ */ jsx142(Fragment31, { children: /* @__PURE__ */ jsx142(
          "path",
          {
            d: path,
            fill: "none",
            stroke: "rgba(99, 102, 241, 0.4)",
            strokeWidth: 12,
            strokeLinecap: "round",
            style: { pointerEvents: "none" },
            children: /* @__PURE__ */ jsx142(
              "animate",
              {
                attributeName: "opacity",
                values: "0.4;0.8;0.4",
                dur: "1s",
                repeatCount: "indefinite"
              }
            )
          }
        ) }),
        isHovered && !selected && !highlighted && /* @__PURE__ */ jsx142(
          "path",
          {
            d: path,
            fill: "none",
            stroke: glowColor,
            strokeWidth: 12,
            strokeLinecap: "round",
            style: { opacity: 0.6, pointerEvents: "none" }
          }
        ),
        showPermanentGlow && !isHovered && /* @__PURE__ */ jsx142(
          "path",
          {
            d: path,
            fill: "none",
            stroke: glowColor,
            strokeWidth: 6,
            className: "blur-sm",
            style: { pointerEvents: "none" }
          }
        ),
        /* @__PURE__ */ jsx142(
          "path",
          {
            d: path,
            fill: "none",
            stroke: strokeColor,
            strokeWidth: highlighted ? 3 : isHovered ? 3 : selected ? 3 : 2,
            strokeLinecap: "round",
            style: {
              pointerEvents: "none",
              transition: "stroke-width 150ms ease-out",
              ...animated && !selected ? {
                strokeDasharray: "5 5",
                animation: "verticalEdgeDash 1s linear infinite"
              } : {}
            }
          }
        ),
        isMergeEdge || isSideSource && isMergeTarget ? (
          // Arrow pointing right (for merge edges and side-to-side merge connections)
          /* @__PURE__ */ jsx142(
            "polygon",
            {
              points: `${targetX - 8},${targetY - 4} ${targetX - 8},${targetY + 4} ${targetX},${targetY}`,
              fill: strokeColor,
              style: { pointerEvents: "none", transition: "fill 150ms ease-out" }
            }
          )
        ) : (
          // Arrow pointing down (for top-to-bottom connections, including non-merge condition_false)
          /* @__PURE__ */ jsx142(
            "polygon",
            {
              points: `${targetX - 4},${targetY - 8} ${targetX + 4},${targetY - 8} ${targetX},${targetY}`,
              fill: strokeColor,
              style: { pointerEvents: "none", transition: "fill 150ms ease-out" }
            }
          )
        )
      ]
    }
  );
}
var edgeAnimationStyles = `
  @keyframes verticalEdgeDash {
    to {
      stroke-dashoffset: -10;
    }
  }
`;

// src/components/VerticalFlowCanvas/useNodeReorder.ts
import React89 from "react";

// src/components/VerticalFlowCanvas/chainUtils.ts
function extractLinearChain(nodeId, nodes, edges) {
  const nodeSet = new Set(nodes.map((n) => n.id));
  const nodeTypeMap = new Map(nodes.map((n) => [n.id, n.type]));
  const outgoing = /* @__PURE__ */ new Map();
  const incoming = /* @__PURE__ */ new Map();
  for (const id of nodeSet) {
    outgoing.set(id, []);
    incoming.set(id, []);
  }
  for (const e of edges) {
    const edgeType = e.type ?? e.edgeType ?? "default";
    if (edgeType !== "default") continue;
    outgoing.get(e.source)?.push(e.target);
    incoming.get(e.target)?.push(e.source);
  }
  const isChainEligible = (id) => {
    return nodeTypeMap.get(id) !== "condition";
  };
  const canWalkForward = (id) => {
    const targets = outgoing.get(id);
    if (!targets || targets.length !== 1) return null;
    const target = targets[0];
    const targetSources = incoming.get(target);
    if (!targetSources || targetSources.length !== 1) return null;
    if (!isChainEligible(target)) return null;
    return target;
  };
  const canWalkBackward = (id) => {
    const sources = incoming.get(id);
    if (!sources || sources.length !== 1) return null;
    const source = sources[0];
    if (!isChainEligible(source)) return null;
    const sourceTargets = outgoing.get(source);
    if (!sourceTargets || sourceTargets.length !== 1) return null;
    return source;
  };
  if (!isChainEligible(nodeId)) return [nodeId];
  const backward = [];
  let current = nodeId;
  while (true) {
    const prev = canWalkBackward(current);
    if (!prev) break;
    backward.unshift(prev);
    current = prev;
  }
  const forward = [];
  current = nodeId;
  while (true) {
    const next = canWalkForward(current);
    if (!next) break;
    forward.push(next);
    current = next;
  }
  return [...backward, nodeId, ...forward];
}
function computeReorderEdgeOps(nodeId, newIndex, chain, edges) {
  const currentIndex = chain.indexOf(nodeId);
  if (currentIndex === -1 || currentIndex === newIndex || chain.length < 2) {
    return { edgesToDelete: [], edgesToCreate: [] };
  }
  const edgesToDelete = [];
  const edgesToCreate = [];
  const findEdge = (source, target) => {
    const edge = edges.find((e) => {
      if (e.source !== source || e.target !== target) return false;
      const edgeType = e.type ?? e.edgeType ?? "default";
      return edgeType === "default";
    });
    return edge?.id ?? null;
  };
  if (currentIndex > 0) {
    const prevId = chain[currentIndex - 1];
    const edgeId = findEdge(prevId, nodeId);
    if (edgeId) edgesToDelete.push(edgeId);
  }
  if (currentIndex < chain.length - 1) {
    const nextId = chain[currentIndex + 1];
    const edgeId = findEdge(nodeId, nextId);
    if (edgeId) edgesToDelete.push(edgeId);
  }
  if (currentIndex > 0 && currentIndex < chain.length - 1) {
    const prevId = chain[currentIndex - 1];
    const nextId = chain[currentIndex + 1];
    edgesToCreate.push({ source: prevId, target: nextId, edgeType: "default" });
  }
  const chainWithoutNode = chain.filter((id) => id !== nodeId);
  const insertAt = Math.min(newIndex, chainWithoutNode.length);
  if (insertAt > 0 && insertAt < chainWithoutNode.length) {
    const beforeId = chainWithoutNode[insertAt - 1];
    const afterId = chainWithoutNode[insertAt];
    const existingEdgeId = findEdge(beforeId, afterId);
    if (existingEdgeId) {
      edgesToDelete.push(existingEdgeId);
    } else {
      const createIdx = edgesToCreate.findIndex(
        (e) => e.source === beforeId && e.target === afterId
      );
      if (createIdx !== -1) edgesToCreate.splice(createIdx, 1);
    }
    edgesToCreate.push({ source: beforeId, target: nodeId, edgeType: "default" });
    edgesToCreate.push({ source: nodeId, target: afterId, edgeType: "default" });
  } else if (insertAt === 0 && chainWithoutNode.length > 0) {
    const afterId = chainWithoutNode[0];
    edgesToCreate.push({ source: nodeId, target: afterId, edgeType: "default" });
  } else if (insertAt === chainWithoutNode.length && chainWithoutNode.length > 0) {
    const beforeId = chainWithoutNode[chainWithoutNode.length - 1];
    edgesToCreate.push({ source: beforeId, target: nodeId, edgeType: "default" });
  }
  return { edgesToDelete, edgesToCreate };
}
function computeGroupReorderEdgeOps(nodeIds, newStartIndex, chain, edges) {
  if (nodeIds.length === 0 || chain.length < 2) {
    return { edgesToDelete: [], edgesToCreate: [] };
  }
  if (nodeIds.length === 1) {
    return computeReorderEdgeOps(nodeIds[0], newStartIndex, chain, edges);
  }
  const groupSet = new Set(nodeIds);
  const firstGroupIdx = chain.indexOf(nodeIds[0]);
  const lastGroupIdx = chain.indexOf(nodeIds[nodeIds.length - 1]);
  if (firstGroupIdx === -1 || lastGroupIdx === -1) {
    return { edgesToDelete: [], edgesToCreate: [] };
  }
  if (firstGroupIdx === newStartIndex) {
    return { edgesToDelete: [], edgesToCreate: [] };
  }
  const edgesToDelete = [];
  const edgesToCreate = [];
  const findEdge = (source, target) => {
    const edge = edges.find((e) => {
      if (e.source !== source || e.target !== target) return false;
      const edgeType = e.type ?? e.edgeType ?? "default";
      return edgeType === "default";
    });
    return edge?.id ?? null;
  };
  if (firstGroupIdx > 0) {
    const prevId = chain[firstGroupIdx - 1];
    const edgeId = findEdge(prevId, nodeIds[0]);
    if (edgeId) edgesToDelete.push(edgeId);
  }
  if (lastGroupIdx < chain.length - 1) {
    const nextId = chain[lastGroupIdx + 1];
    const edgeId = findEdge(nodeIds[nodeIds.length - 1], nextId);
    if (edgeId) edgesToDelete.push(edgeId);
  }
  if (firstGroupIdx > 0 && lastGroupIdx < chain.length - 1) {
    const prevId = chain[firstGroupIdx - 1];
    const nextId = chain[lastGroupIdx + 1];
    edgesToCreate.push({ source: prevId, target: nextId, edgeType: "default" });
  }
  const chainWithoutGroup = chain.filter((id) => !groupSet.has(id));
  const insertAt = Math.min(newStartIndex, chainWithoutGroup.length);
  const groupFirst = nodeIds[0];
  const groupLast = nodeIds[nodeIds.length - 1];
  if (insertAt > 0 && insertAt < chainWithoutGroup.length) {
    const beforeId = chainWithoutGroup[insertAt - 1];
    const afterId = chainWithoutGroup[insertAt];
    const existingEdgeId = findEdge(beforeId, afterId);
    if (existingEdgeId) {
      edgesToDelete.push(existingEdgeId);
    } else {
      const createIdx = edgesToCreate.findIndex(
        (e) => e.source === beforeId && e.target === afterId
      );
      if (createIdx !== -1) edgesToCreate.splice(createIdx, 1);
    }
    edgesToCreate.push({ source: beforeId, target: groupFirst, edgeType: "default" });
    edgesToCreate.push({ source: groupLast, target: afterId, edgeType: "default" });
  } else if (insertAt === 0 && chainWithoutGroup.length > 0) {
    const afterId = chainWithoutGroup[0];
    edgesToCreate.push({ source: groupLast, target: afterId, edgeType: "default" });
  } else if (insertAt === chainWithoutGroup.length && chainWithoutGroup.length > 0) {
    const beforeId = chainWithoutGroup[chainWithoutGroup.length - 1];
    edgesToCreate.push({ source: beforeId, target: groupFirst, edgeType: "default" });
  }
  return { edgesToDelete, edgesToCreate };
}
function getContiguousSelectedInChain(chain, selectedNodeIds) {
  const selectedInChain = chain.filter((id) => selectedNodeIds.has(id));
  if (selectedInChain.length === 0) return null;
  const firstIdx = chain.indexOf(selectedInChain[0]);
  const lastIdx = chain.indexOf(selectedInChain[selectedInChain.length - 1]);
  const expectedLen = lastIdx - firstIdx + 1;
  if (expectedLen !== selectedInChain.length) return null;
  for (let i = firstIdx; i <= lastIdx; i++) {
    if (!selectedNodeIds.has(chain[i])) return null;
  }
  return selectedInChain;
}

// src/components/VerticalFlowCanvas/useNodeReorder.ts
function useNodeReorder({
  nodes,
  edges,
  nodePositions,
  nodeOrder: _nodeOrder,
  zoomScale,
  containerRef,
  onReorder,
  selectedNodeIds,
  onGroupReorder
}) {
  const [isDragging, setIsDragging] = React89.useState(false);
  const [draggedNodeId, setDraggedNodeId] = React89.useState(null);
  const [draggedNodeIds, setDraggedNodeIds] = React89.useState([]);
  const [ghostPosition, setGhostPosition] = React89.useState(null);
  const [activeDropZoneIndex, setActiveDropZoneIndex] = React89.useState(null);
  const [activeChain, setActiveChain] = React89.useState([]);
  const [virtualChainOrder, setVirtualChainOrder] = React89.useState([]);
  const dragStateRef = React89.useRef(null);
  const computeDropZone = React89.useCallback((cursorCanvasY) => {
    const state = dragStateRef.current;
    if (!state || state.chain.length < 2) return null;
    const { chain, zoneMidpoints, originalIndex, groupSize } = state;
    let zoneIndex = chain.length;
    for (let i = 0; i < zoneMidpoints.length; i++) {
      if (cursorCanvasY < zoneMidpoints[i]) {
        zoneIndex = i;
        break;
      }
    }
    if (zoneIndex >= originalIndex && zoneIndex <= originalIndex + groupSize) {
      return null;
    }
    return zoneIndex;
  }, []);
  const buildVirtualOrder = React89.useCallback(
    (chain, groupNodeIds, dropZone) => {
      if (dropZone === null) return chain;
      const groupSet = new Set(groupNodeIds);
      const firstIdx = chain.indexOf(groupNodeIds[0]);
      if (firstIdx === -1) return chain;
      const without = chain.filter((id) => !groupSet.has(id));
      const insertAt = dropZone > firstIdx ? dropZone - groupNodeIds.length : dropZone;
      const clamped = Math.max(0, Math.min(insertAt, without.length));
      const result = [...without];
      result.splice(clamped, 0, ...groupNodeIds);
      return result;
    },
    []
  );
  const startDrag = React89.useCallback(
    (nodeId, e) => {
      if (!onReorder && !onGroupReorder) return;
      const chain = extractLinearChain(nodeId, nodes, edges);
      if (chain.length < 2) return;
      let groupNodeIds = [nodeId];
      if (selectedNodeIds && selectedNodeIds.has(nodeId) && selectedNodeIds.size > 1) {
        const contiguous = getContiguousSelectedInChain(chain, selectedNodeIds);
        if (contiguous && contiguous.length > 1) {
          groupNodeIds = contiguous;
        }
      }
      const originalIndex = chain.indexOf(groupNodeIds[0]);
      if (originalIndex === -1) return;
      const nodePos = nodePositions.get(nodeId);
      if (!nodePos) return;
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const scrollTop = container.scrollTop;
      const cursorCanvasY = (e.clientY - rect.top + scrollTop) / zoomScale;
      const nodeCenterY = nodePos.y + nodePos.height / 2;
      const cursorOffsetY = cursorCanvasY - nodeCenterY;
      const zoneMidpoints = [];
      for (let i = 0; i < chain.length - 1; i++) {
        const posA = nodePositions.get(chain[i]);
        const posB = nodePositions.get(chain[i + 1]);
        if (posA && posB) {
          const bottomA = posA.y + posA.height;
          const topB = posB.y;
          zoneMidpoints.push((bottomA + topB) / 2);
        } else {
          zoneMidpoints.push(0);
        }
      }
      dragStateRef.current = {
        nodeId,
        groupNodeIds,
        chain,
        originalIndex,
        groupSize: groupNodeIds.length,
        cursorOffsetY,
        zoneMidpoints
      };
      setDraggedNodeId(nodeId);
      setDraggedNodeIds(groupNodeIds);
      setActiveChain(chain);
      setVirtualChainOrder(chain);
      setGhostPosition({ x: nodePos.x, y: nodePos.y });
      setActiveDropZoneIndex(null);
      setIsDragging(true);
    },
    [
      nodes,
      edges,
      nodePositions,
      zoomScale,
      containerRef,
      onReorder,
      onGroupReorder,
      selectedNodeIds
    ]
  );
  const handlePointerMove = React89.useCallback(
    (e) => {
      const state = dragStateRef.current;
      if (!state) return;
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const scrollTop = container.scrollTop;
      const cursorCanvasY = (e.clientY - rect.top + scrollTop) / zoomScale;
      const nodePos = nodePositions.get(state.nodeId);
      if (nodePos) {
        setGhostPosition({
          x: nodePos.x,
          y: cursorCanvasY - state.cursorOffsetY - nodePos.height / 2
        });
      }
      const zone = computeDropZone(cursorCanvasY);
      setActiveDropZoneIndex(zone);
      setVirtualChainOrder(buildVirtualOrder(state.chain, state.groupNodeIds, zone));
    },
    [containerRef, zoomScale, nodePositions, computeDropZone, buildVirtualOrder]
  );
  const finishDrag = React89.useCallback(
    (committed) => {
      const state = dragStateRef.current;
      if (!state) return;
      if (committed && activeDropZoneIndex !== null) {
        const { chain, groupNodeIds, originalIndex, groupSize } = state;
        const isGroup = groupSize > 1;
        const newIndex = activeDropZoneIndex > originalIndex ? activeDropZoneIndex - groupSize : activeDropZoneIndex;
        if (isGroup) {
          if (newIndex !== originalIndex && onGroupReorder) {
            onGroupReorder(groupNodeIds, newIndex, chain);
          }
        } else {
          if (newIndex !== originalIndex) {
            onReorder?.(state.nodeId, newIndex, chain);
          }
        }
      }
      dragStateRef.current = null;
      setIsDragging(false);
      setDraggedNodeId(null);
      setDraggedNodeIds([]);
      setGhostPosition(null);
      setActiveDropZoneIndex(null);
      setActiveChain([]);
      setVirtualChainOrder([]);
    },
    [activeDropZoneIndex, onReorder, onGroupReorder]
  );
  const handlePointerUp = React89.useCallback(() => {
    finishDrag(true);
  }, [finishDrag]);
  const cancelDrag = React89.useCallback(() => {
    finishDrag(false);
  }, [finishDrag]);
  const handleKeyDown = React89.useCallback(
    (e) => {
      if (e.key === "Escape" && isDragging) {
        e.preventDefault();
        cancelDrag();
      }
    },
    [isDragging, cancelDrag]
  );
  React89.useEffect(() => {
    if (!isDragging) return;
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDragging, handlePointerMove, handlePointerUp, handleKeyDown]);
  return {
    isDragging,
    draggedNodeId,
    draggedNodeIds,
    ghostPosition,
    activeDropZoneIndex,
    activeChain,
    virtualChainOrder,
    startDrag,
    cancelDrag
  };
}

// src/components/VerticalFlowCanvas/useOrphanInsert.ts
import React90 from "react";
var NODE_WIDTH2 = 280;
var EDGE_HIT_THRESHOLD = 30;
function pointToSegmentDistance(px, py, ax, ay, bx, by) {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    const ex2 = px - ax;
    const ey2 = py - ay;
    return Math.sqrt(ex2 * ex2 + ey2 * ey2);
  }
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const projX = ax + t * dx;
  const projY = ay + t * dy;
  const ex = px - projX;
  const ey = py - projY;
  return Math.sqrt(ex * ex + ey * ey);
}
function findNearestEdge(canvasX, canvasY, edges, nodePositions, draggedNodeId) {
  let bestEdgeId = null;
  let bestDistance = EDGE_HIT_THRESHOLD;
  for (const edge of edges) {
    if (edge.source === draggedNodeId || edge.target === draggedNodeId) continue;
    const edgeType = edge.type ?? edge.edgeType ?? "default";
    if (edgeType === "condition_false") continue;
    const sourcePos = nodePositions.get(edge.source);
    const targetPos = nodePositions.get(edge.target);
    if (!sourcePos || !targetPos) continue;
    const sourceX = sourcePos.x + NODE_WIDTH2 / 2;
    const targetX = targetPos.x + NODE_WIDTH2 / 2;
    if (Math.abs(sourceX - targetX) > NODE_WIDTH2) continue;
    const sx = sourcePos.x + NODE_WIDTH2 / 2;
    const sy = sourcePos.y + sourcePos.height;
    const tx = targetPos.x + NODE_WIDTH2 / 2;
    const ty = targetPos.y;
    const dist = pointToSegmentDistance(canvasX, canvasY, sx, sy, tx, ty);
    if (dist < bestDistance) {
      bestDistance = dist;
      bestEdgeId = edge.id;
    }
  }
  return bestEdgeId;
}
function useOrphanInsert({
  nodes: _nodes,
  edges,
  nodePositions,
  zoomScale,
  containerRef,
  onInsert
}) {
  const [isDragging, setIsDragging] = React90.useState(false);
  const [draggedNodeId, setDraggedNodeId] = React90.useState(null);
  const [ghostPosition, setGhostPosition] = React90.useState(null);
  const [highlightedEdgeId, setHighlightedEdgeId] = React90.useState(null);
  const dragStateRef = React90.useRef(null);
  const startDrag = React90.useCallback(
    (nodeId, e) => {
      if (!onInsert) return;
      const nodePos = nodePositions.get(nodeId);
      if (!nodePos) return;
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const scrollTop = container.scrollTop;
      const scrollLeft = container.scrollLeft;
      const cursorCanvasX = (e.clientX - rect.left + scrollLeft) / zoomScale;
      const cursorCanvasY = (e.clientY - rect.top + scrollTop) / zoomScale;
      dragStateRef.current = {
        nodeId,
        cursorOffsetX: cursorCanvasX - nodePos.x,
        cursorOffsetY: cursorCanvasY - nodePos.y
      };
      setDraggedNodeId(nodeId);
      setGhostPosition({ x: nodePos.x, y: nodePos.y });
      setHighlightedEdgeId(null);
      setIsDragging(true);
    },
    [nodePositions, zoomScale, containerRef, onInsert]
  );
  const handlePointerMove = React90.useCallback(
    (e) => {
      const state = dragStateRef.current;
      if (!state) return;
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const scrollTop = container.scrollTop;
      const scrollLeft = container.scrollLeft;
      const cursorCanvasX = (e.clientX - rect.left + scrollLeft) / zoomScale;
      const cursorCanvasY = (e.clientY - rect.top + scrollTop) / zoomScale;
      setGhostPosition({
        x: cursorCanvasX - state.cursorOffsetX,
        y: cursorCanvasY - state.cursorOffsetY
      });
      const nearest = findNearestEdge(
        cursorCanvasX,
        cursorCanvasY,
        edges,
        nodePositions,
        state.nodeId
      );
      setHighlightedEdgeId(nearest);
    },
    [containerRef, zoomScale, edges, nodePositions]
  );
  const finishDrag = React90.useCallback(
    (committed) => {
      const state = dragStateRef.current;
      if (!state) return;
      if (committed && highlightedEdgeId) {
        onInsert?.(state.nodeId, highlightedEdgeId);
      }
      dragStateRef.current = null;
      setIsDragging(false);
      setDraggedNodeId(null);
      setGhostPosition(null);
      setHighlightedEdgeId(null);
    },
    [highlightedEdgeId, onInsert]
  );
  const handlePointerUp = React90.useCallback(() => {
    finishDrag(true);
  }, [finishDrag]);
  const cancelDrag = React90.useCallback(() => {
    finishDrag(false);
  }, [finishDrag]);
  const handleKeyDown = React90.useCallback(
    (e) => {
      if (e.key === "Escape" && isDragging) {
        e.preventDefault();
        cancelDrag();
      }
    },
    [isDragging, cancelDrag]
  );
  React90.useEffect(() => {
    if (!isDragging) return;
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDragging, handlePointerMove, handlePointerUp, handleKeyDown]);
  return {
    isDragging,
    draggedNodeId,
    ghostPosition,
    highlightedEdgeId,
    startDrag,
    cancelDrag
  };
}

// src/components/VerticalFlowCanvas/useMultiSelect.ts
import React91 from "react";
var EMPTY_SET = /* @__PURE__ */ new Set();
function useMultiSelect({
  nodeOrder,
  selectedNodeIds: controlledIds,
  onSelectionChange
}) {
  const [internalIds, setInternalIds] = React91.useState(EMPTY_SET);
  const lastSelectedRef = React91.useRef(null);
  const isControlled = controlledIds !== void 0;
  const selectedNodeIds = isControlled ? controlledIds : internalIds;
  const emit = React91.useCallback(
    (next) => {
      if (!isControlled) {
        setInternalIds(next);
      }
      onSelectionChange?.(next);
    },
    [isControlled, onSelectionChange]
  );
  const nodeOrderSet = React91.useMemo(() => new Set(nodeOrder), [nodeOrder]);
  React91.useEffect(() => {
    if (selectedNodeIds.size === 0) return;
    let hasStale = false;
    for (const id of selectedNodeIds) {
      if (!nodeOrderSet.has(id)) {
        hasStale = true;
        break;
      }
    }
    if (hasStale) {
      const cleaned = /* @__PURE__ */ new Set();
      for (const id of selectedNodeIds) {
        if (nodeOrderSet.has(id)) cleaned.add(id);
      }
      emit(cleaned);
    }
  }, [nodeOrderSet]);
  const handleNodeSelect = React91.useCallback(
    (nodeId, modifiers) => {
      const isCmdCtrl = modifiers.metaKey || modifiers.ctrlKey;
      if (modifiers.shiftKey && lastSelectedRef.current) {
        const anchorIdx = nodeOrder.indexOf(lastSelectedRef.current);
        const targetIdx = nodeOrder.indexOf(nodeId);
        if (anchorIdx !== -1 && targetIdx !== -1) {
          const start = Math.min(anchorIdx, targetIdx);
          const end = Math.max(anchorIdx, targetIdx);
          const next = new Set(isCmdCtrl ? selectedNodeIds : []);
          for (let i = start; i <= end; i++) {
            next.add(nodeOrder[i]);
          }
          emit(next);
          return "range";
        }
      }
      if (isCmdCtrl) {
        const next = new Set(selectedNodeIds);
        if (next.has(nodeId)) {
          next.delete(nodeId);
        } else {
          next.add(nodeId);
        }
        lastSelectedRef.current = nodeId;
        emit(next);
        return "toggle";
      }
      lastSelectedRef.current = nodeId;
      emit(/* @__PURE__ */ new Set([nodeId]));
      return "single";
    },
    [nodeOrder, selectedNodeIds, emit]
  );
  const clearSelection = React91.useCallback(() => {
    lastSelectedRef.current = null;
    emit(/* @__PURE__ */ new Set());
  }, [emit]);
  const selectAll = React91.useCallback(() => {
    if (nodeOrder.length === 0) return;
    lastSelectedRef.current = nodeOrder[nodeOrder.length - 1];
    emit(new Set(nodeOrder));
  }, [nodeOrder, emit]);
  const isSelected = React91.useCallback(
    (nodeId) => selectedNodeIds.has(nodeId),
    [selectedNodeIds]
  );
  return {
    selectedNodeIds,
    handleNodeSelect,
    clearSelection,
    isSelected,
    selectAll
  };
}

// src/components/VerticalFlowCanvas/useMarqueeSelect.ts
import React92 from "react";
var DRAG_THRESHOLD = 5;
var AUTO_SCROLL_EDGE = 40;
var AUTO_SCROLL_SPEED = 8;
function useMarqueeSelect({
  containerRef,
  zoomScaleRef,
  nodePositions,
  nodeWidth,
  onSelectionChange,
  selectedNodeIds,
  enabled
}) {
  const suppressPaneClickRef = React92.useRef(false);
  const [marqueeRect, setMarqueeRect] = React92.useState(null);
  const [previewSelectedIds, setPreviewSelectedIds] = React92.useState(
    null
  );
  const dragRef = React92.useRef(null);
  const rafRef = React92.useRef(null);
  const toCanvas = React92.useCallback(
    (clientX, clientY) => {
      const container = containerRef.current;
      if (!container) return { x: 0, y: 0 };
      const rect = container.getBoundingClientRect();
      const zoomScale = zoomScaleRef.current;
      const centerX = rect.width / 2;
      return {
        x: centerX + (clientX - rect.left + container.scrollLeft - centerX) / zoomScale,
        y: (clientY - rect.top + container.scrollTop) / zoomScale
      };
    },
    [containerRef, zoomScaleRef]
  );
  const buildRect = (ax, ay, bx, by) => ({
    left: Math.min(ax, bx),
    top: Math.min(ay, by),
    width: Math.abs(bx - ax),
    height: Math.abs(by - ay)
  });
  const hitTest = React92.useCallback(
    (rect) => {
      const hits = /* @__PURE__ */ new Set();
      const rRight = rect.left + rect.width;
      const rBottom = rect.top + rect.height;
      nodePositions.forEach(({ x, y, height }, nodeId) => {
        const nodeRight = x + nodeWidth;
        const nodeBottom = y + height;
        if (rect.left < nodeRight && rRight > x && rect.top < nodeBottom && rBottom > y) {
          hits.add(nodeId);
        }
      });
      return hits;
    },
    [nodePositions, nodeWidth]
  );
  const autoScroll = React92.useCallback(
    (clientY) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const distFromTop = clientY - rect.top;
      const distFromBottom = rect.bottom - clientY;
      if (distFromTop < AUTO_SCROLL_EDGE) {
        container.scrollTop -= AUTO_SCROLL_SPEED;
      } else if (distFromBottom < AUTO_SCROLL_EDGE) {
        container.scrollTop += AUTO_SCROLL_SPEED;
      }
    },
    [containerRef]
  );
  const isActive = React92.useCallback(() => dragRef.current !== null, []);
  const teardownRef = React92.useRef(null);
  const handleMouseDown = React92.useCallback(
    (e) => {
      if (!enabled) return;
      if (e.button !== 0) return;
      const target = e.target;
      const isPaneBackground = target.dataset.pane === "true" || target.tagName.toLowerCase() === "svg";
      if (!isPaneBackground) return;
      const additive = e.shiftKey || e.metaKey || e.ctrlKey;
      const canvasPos = toCanvas(e.clientX, e.clientY);
      dragRef.current = {
        startCanvasX: canvasPos.x,
        startCanvasY: canvasPos.y,
        startClientX: e.clientX,
        startClientY: e.clientY,
        activated: false,
        cancelled: false,
        baseSelection: additive ? new Set(selectedNodeIds) : /* @__PURE__ */ new Set(),
        latestSelection: null
      };
      const cleanup = () => {
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
        dragRef.current = null;
        setMarqueeRect(null);
        setPreviewSelectedIds(null);
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        window.removeEventListener("keydown", onEscape);
        teardownRef.current = null;
      };
      const onMove = (ev) => {
        const state = dragRef.current;
        if (!state || state.cancelled) return;
        if (!state.activated) {
          const dx = ev.clientX - state.startClientX;
          const dy = ev.clientY - state.startClientY;
          if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
          state.activated = true;
        }
        autoScroll(ev.clientY);
        const current = toCanvas(ev.clientX, ev.clientY);
        const rect = buildRect(state.startCanvasX, state.startCanvasY, current.x, current.y);
        setMarqueeRect(rect);
        const hits = hitTest(rect);
        const next = new Set(state.baseSelection);
        hits.forEach((id) => next.add(id));
        state.latestSelection = next;
        setPreviewSelectedIds(next);
      };
      const onUp = () => {
        const state = dragRef.current;
        if (state && (state.activated || state.cancelled)) {
          suppressPaneClickRef.current = true;
          requestAnimationFrame(() => {
            suppressPaneClickRef.current = false;
          });
        }
        const finalSelection = state && !state.cancelled ? state.latestSelection : null;
        cleanup();
        if (finalSelection) onSelectionChange(finalSelection);
      };
      const onEscape = (ev) => {
        const state = dragRef.current;
        if (ev.key === "Escape" && state && !state.cancelled) {
          ev.preventDefault();
          state.cancelled = true;
          setMarqueeRect(null);
          setPreviewSelectedIds(null);
          window.removeEventListener("keydown", onEscape);
        }
      };
      teardownRef.current = cleanup;
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      window.addEventListener("keydown", onEscape);
    },
    [enabled, toCanvas, selectedNodeIds, hitTest, onSelectionChange, autoScroll]
  );
  React92.useEffect(() => {
    return () => {
      teardownRef.current?.();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);
  return { isActive, suppressPaneClickRef, marqueeRect, previewSelectedIds, handleMouseDown };
}

// src/components/VerticalFlowCanvas/VerticalFlowCanvas.tsx
import { CaretUp as CaretUp4, CaretDown as CaretDown11, CircleNotch as CircleNotch3 } from "@phosphor-icons/react";
import { Fragment as Fragment32, jsx as jsx143, jsxs as jsxs126 } from "react/jsx-runtime";
var NODE_WIDTH3 = 280;
var VERTICAL_GAP = 60;
var HORIZONTAL_GAP = 100;
var TOP_PADDING = 120;
var ZOOM_NORMAL = 0.9;
var ZOOM_FOCUSED = 1.15;
var getClosestElementTarget = (target) => {
  if (typeof Element !== "undefined" && target instanceof Element) {
    return target;
  }
  if (typeof Node !== "undefined" && target instanceof Node) {
    return target.parentElement;
  }
  return null;
};
function VerticalFlowCanvas({
  nodes,
  edges,
  nodeTypes,
  onNodeClick,
  onPaneClick,
  onNodeDelete,
  onEdgeClick,
  onConnect,
  className,
  currentStepId,
  selectedEdgeId,
  focusedNodeId: externalFocusedNodeId,
  onPrependNode,
  isGenerating,
  onNodeReorder,
  selectedNodeIds: externalSelectedNodeIds,
  onSelectionChange,
  onNodesDelete,
  onNodesReorder,
  onOrphanInsertOnEdge
}) {
  const containerRef = React93.useRef(null);
  const nodeRefs = React93.useRef(/* @__PURE__ */ new Map());
  const [containerWidth, setContainerWidth] = React93.useState(800);
  const [containerHeight, setContainerHeight] = React93.useState(600);
  const [nodeHeights, setNodeHeights] = React93.useState(/* @__PURE__ */ new Map());
  const [internalFocusedNodeId, setInternalFocusedNodeId] = React93.useState(null);
  const [scrollTop, setScrollTop] = React93.useState(0);
  const focusedNodeId = externalFocusedNodeId ?? internalFocusedNodeId;
  const setFocusedNodeId = setInternalFocusedNodeId;
  const prevNodeIdsRef = React93.useRef(/* @__PURE__ */ new Set());
  const newNodeIdsRef = React93.useRef(/* @__PURE__ */ new Set());
  const prevFocusedNodeIdRef = React93.useRef(null);
  const prevCurrentStepIdRef = React93.useRef(null);
  const userScrolledRef = React93.useRef(false);
  const followExecutionRef = React93.useRef(true);
  const scrollTimeoutRef = React93.useRef(null);
  const nodePositionsRef = React93.useRef(/* @__PURE__ */ new Map());
  const zoomScaleRef = React93.useRef(1);
  React93.useEffect(() => {
    const currentIds = new Set(nodes.map((n) => n.id));
    const newIds = /* @__PURE__ */ new Set();
    currentIds.forEach((id) => {
      if (!prevNodeIdsRef.current.has(id)) {
        newIds.add(id);
      }
    });
    newNodeIdsRef.current = newIds;
    prevNodeIdsRef.current = currentIds;
    if (newIds.size > 0) {
      const timer = setTimeout(() => {
        newNodeIdsRef.current = /* @__PURE__ */ new Set();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [nodes]);
  const [dragState, setDragState] = React93.useState(null);
  const SNAP_THRESHOLD = 50;
  React93.useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      setContainerWidth(rect?.width ?? 800);
      setContainerHeight(rect?.height ?? 600);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);
  React93.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleScroll = () => {
      setScrollTop(container.scrollTop);
      userScrolledRef.current = true;
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        userScrolledRef.current = false;
      }, 1e3);
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);
  React93.useLayoutEffect(() => {
    const newHeights = /* @__PURE__ */ new Map();
    nodeRefs.current.forEach((el, id) => {
      if (el) {
        newHeights.set(id, el.offsetHeight);
      }
    });
    const hasChanged = newHeights.size !== nodeHeights.size || Array.from(newHeights.entries()).some(([id, h]) => nodeHeights.get(id) !== h);
    if (hasChanged) {
      setNodeHeights(newHeights);
    }
  });
  const { nodeOrder, nodeColumns, branchSources } = React93.useMemo(() => {
    if (nodes.length === 0) return { nodeOrder: [], nodeColumns: /* @__PURE__ */ new Map(), branchSources: /* @__PURE__ */ new Map() };
    const outgoing = /* @__PURE__ */ new Map();
    const incoming = /* @__PURE__ */ new Map();
    nodes.forEach((n) => {
      outgoing.set(n.id, []);
      incoming.set(n.id, []);
    });
    edges.forEach((e) => {
      const edgeType = e.type ?? e.edgeType ?? "default";
      const sourceTargets = outgoing.get(e.source);
      if (sourceTargets && !sourceTargets.some((t) => t.targetId === e.target)) {
        sourceTargets.push({ targetId: e.target, edgeType });
      }
      const targetSources = incoming.get(e.target);
      if (targetSources && !targetSources.some((s) => s.sourceId === e.source)) {
        targetSources.push({ sourceId: e.source, edgeType });
      }
    });
    const orphanedNodes = nodes.filter(
      (n) => incoming.get(n.id)?.length === 0 && (outgoing.get(n.id)?.length ?? 0) === 0
    );
    let roots = nodes.filter(
      (n) => incoming.get(n.id)?.length === 0 && (outgoing.get(n.id)?.length ?? 0) > 0
    );
    if (roots.length === 0 && nodes.length > 0) {
      const sorted = [...nodes].sort((a, b) => a.id.localeCompare(b.id));
      roots = [sorted[0]];
    }
    const order = [];
    const columns = /* @__PURE__ */ new Map();
    const branchSources2 = /* @__PURE__ */ new Map();
    const visited = /* @__PURE__ */ new Set();
    const queue = [];
    for (const root of roots) {
      if (visited.has(root.id)) continue;
      queue.length = 0;
      queue.push({ id: root.id, column: 0 });
      while (queue.length > 0) {
        const { id, column, branchSourceId } = queue.shift();
        if (visited.has(id)) continue;
        visited.add(id);
        order.push(id);
        columns.set(id, column);
        if (branchSourceId) {
          branchSources2.set(id, branchSourceId);
        }
        const node = nodes.find((n) => n.id === id);
        const children = outgoing.get(id) || [];
        for (const { targetId, edgeType } of children) {
          if (visited.has(targetId)) continue;
          let childColumn = column;
          let childBranchSource;
          if (node?.type === "condition") {
            if (edgeType === "condition_true") {
              childColumn = 0;
            } else if (edgeType === "condition_false") {
              childColumn = -1;
              childBranchSource = id;
            }
          }
          queue.push({ id: targetId, column: childColumn, branchSourceId: childBranchSource });
        }
      }
    }
    nodes.forEach((n) => {
      if (!visited.has(n.id) && !orphanedNodes.some((o) => o.id === n.id)) {
        order.push(n.id);
        columns.set(n.id, 0);
      }
    });
    orphanedNodes.forEach((n) => {
      if (!visited.has(n.id)) {
        order.push(n.id);
        columns.set(n.id, 0);
      }
    });
    return { nodeOrder: order, nodeColumns: columns, branchSources: branchSources2 };
  }, [nodes, edges]);
  const nodePositions = React93.useMemo(() => {
    const positions = /* @__PURE__ */ new Map();
    const columnValues = Array.from(nodeColumns.values());
    const minCol = Math.min(...columnValues, 0);
    const maxCol = Math.max(...columnValues, 0);
    const colSpan = maxCol - minCol + 1;
    const totalWidth = colSpan * (NODE_WIDTH3 + HORIZONTAL_GAP) - HORIZONTAL_GAP;
    const startX = Math.max((containerWidth - totalWidth) / 2, 20);
    const columnCurrentY = /* @__PURE__ */ new Map();
    for (let col = minCol; col <= maxCol; col++) {
      columnCurrentY.set(col, TOP_PADDING);
    }
    const BRANCH_BELOW_MIDPOINT_OFFSET = 30;
    nodeOrder.forEach((nodeId) => {
      const column = nodeColumns.get(nodeId) ?? 0;
      const normalizedCol = column - minCol;
      const x = startX + normalizedCol * (NODE_WIDTH3 + HORIZONTAL_GAP);
      const height = nodeHeights.get(nodeId) ?? 100;
      const incomingEdges = edges.filter((e) => e.target === nodeId);
      const isMergePoint = incomingEdges.length > 1;
      const branchSourceId = branchSources.get(nodeId);
      let y;
      if (isMergePoint) {
        const sourceYs = incomingEdges.map((e) => {
          const sourcePos = positions.get(e.source);
          if (sourcePos) {
            return sourcePos.y + sourcePos.height + VERTICAL_GAP;
          }
          return columnCurrentY.get(column) ?? TOP_PADDING;
        });
        y = Math.max(...sourceYs, columnCurrentY.get(column) ?? TOP_PADDING);
      } else if (branchSourceId) {
        const sourcePos = positions.get(branchSourceId);
        if (sourcePos) {
          y = sourcePos.y + sourcePos.height / 2 + BRANCH_BELOW_MIDPOINT_OFFSET;
        } else {
          y = columnCurrentY.get(column) ?? TOP_PADDING;
        }
      } else {
        y = columnCurrentY.get(column) ?? TOP_PADDING;
      }
      positions.set(nodeId, { x, y, height });
      columnCurrentY.set(column, y + height + VERTICAL_GAP);
    });
    return positions;
  }, [nodeOrder, nodeColumns, branchSources, nodeHeights, containerWidth, edges]);
  const canvasHeight = React93.useMemo(() => {
    let maxY = 200;
    nodePositions.forEach(({ y, height }) => {
      maxY = Math.max(maxY, y + height);
    });
    return maxY + 60;
  }, [nodePositions]);
  const multiSelect = useMultiSelect({
    nodeOrder,
    selectedNodeIds: externalSelectedNodeIds,
    onSelectionChange
  });
  const isZoomed = focusedNodeId !== null && multiSelect.selectedNodeIds.size <= 1;
  const zoomScale = isZoomed ? ZOOM_FOCUSED : ZOOM_NORMAL;
  React93.useEffect(() => {
    nodePositionsRef.current = nodePositions;
  }, [nodePositions]);
  React93.useEffect(() => {
    zoomScaleRef.current = zoomScale;
  }, [zoomScale]);
  const reorder = useNodeReorder({
    nodes,
    edges,
    nodePositions,
    nodeOrder,
    zoomScale,
    containerRef,
    onReorder: onNodeReorder,
    selectedNodeIds: multiSelect.selectedNodeIds,
    onGroupReorder: onNodesReorder
  });
  const orphanInsert = useOrphanInsert({
    nodes,
    edges,
    nodePositions,
    zoomScale,
    containerRef,
    onInsert: onOrphanInsertOnEdge
  });
  const marquee = useMarqueeSelect({
    containerRef,
    zoomScaleRef,
    nodePositions,
    nodeWidth: NODE_WIDTH3,
    onSelectionChange: React93.useCallback(
      (ids) => {
        onSelectionChange?.(ids);
      },
      [onSelectionChange]
    ),
    selectedNodeIds: multiSelect.selectedNodeIds,
    enabled: !reorder.isDragging && !orphanInsert.isDragging && !!onSelectionChange
  });
  const displaySelectedIds = marquee.previewSelectedIds ?? multiSelect.selectedNodeIds;
  const { suppressPaneClickRef, isActive: isMarqueeActive } = marquee;
  const adjustedNodePositions = React93.useMemo(() => {
    if (orphanInsert.isDragging && orphanInsert.highlightedEdgeId && orphanInsert.draggedNodeId) {
      const edge = edges.find((e) => e.id === orphanInsert.highlightedEdgeId);
      if (edge) {
        const draggedHeight = nodeHeights.get(orphanInsert.draggedNodeId) ?? 100;
        const gapSize = draggedHeight + VERTICAL_GAP;
        const targetPos = nodePositions.get(edge.target);
        if (targetPos) {
          const adjusted2 = new Map(nodePositions);
          const thresholdY = targetPos.y;
          nodePositions.forEach((pos, nid) => {
            if (nid === orphanInsert.draggedNodeId) return;
            if (pos.y >= thresholdY) {
              adjusted2.set(nid, { ...pos, y: pos.y + gapSize });
            }
          });
          return adjusted2;
        }
      }
    }
    if (!reorder.isDragging || reorder.virtualChainOrder.length === 0) {
      return nodePositions;
    }
    const adjusted = new Map(nodePositions);
    const { activeChain, virtualChainOrder, draggedNodeIds } = reorder;
    const draggedSet = new Set(draggedNodeIds);
    const firstChainNodeId = activeChain[0];
    const firstPos = nodePositions.get(firstChainNodeId);
    if (!firstPos) return nodePositions;
    let currentY = firstPos.y;
    for (const id of virtualChainOrder) {
      if (draggedSet.has(id)) {
        const draggedPos = nodePositions.get(id);
        if (draggedPos) {
          currentY += draggedPos.height + VERTICAL_GAP;
        }
        continue;
      }
      const pos = nodePositions.get(id);
      if (!pos) continue;
      adjusted.set(id, { ...pos, y: currentY });
      currentY += pos.height + VERTICAL_GAP;
    }
    return adjusted;
  }, [nodePositions, nodeHeights, edges, reorder.isDragging, reorder.virtualChainOrder, reorder.activeChain, reorder.draggedNodeIds, orphanInsert.isDragging, orphanInsert.highlightedEdgeId, orphanInsert.draggedNodeId]);
  const holdTimerRef = React93.useRef(null);
  const holdCleanupRef = React93.useRef(null);
  const HOLD_DELAY_MS = 200;
  const HOLD_MOVE_THRESHOLD = 5;
  React93.useEffect(() => {
    return () => {
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
      holdCleanupRef.current?.();
    };
  }, []);
  const cancelHold = React93.useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    holdCleanupRef.current?.();
    holdCleanupRef.current = null;
  }, []);
  const handleNodePointerDown = React93.useCallback(
    (nodeId, e) => {
      if (reorder.isDragging || orphanInsert.isDragging) return;
      cancelHold();
      const startX = e.clientX;
      const startY = e.clientY;
      const syntheticEvent = e;
      const cleanup = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        if (holdTimerRef.current) {
          clearTimeout(holdTimerRef.current);
          holdTimerRef.current = null;
        }
        holdCleanupRef.current = null;
      };
      const onMove = (moveEvt) => {
        const dx = moveEvt.clientX - startX;
        const dy = moveEvt.clientY - startY;
        if (Math.abs(dx) > HOLD_MOVE_THRESHOLD || Math.abs(dy) > HOLD_MOVE_THRESHOLD) {
          cleanup();
        }
      };
      const onUp = () => {
        cleanup();
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      holdCleanupRef.current = cleanup;
      holdTimerRef.current = setTimeout(() => {
        holdTimerRef.current = null;
        cleanup();
        const chain = extractLinearChain(nodeId, nodes, edges);
        if (chain.length >= 2) {
          reorder.startDrag(nodeId, syntheticEvent);
        } else if (onOrphanInsertOnEdge) {
          orphanInsert.startDrag(nodeId, syntheticEvent);
        }
      }, HOLD_DELAY_MS);
    },
    [reorder, orphanInsert, cancelHold, nodes, edges, onOrphanInsertOnEdge]
  );
  const startAnchorDrag = React93.useCallback(
    (nodeId, _e) => {
      if (!onConnect) return;
      setDragState({ sourceNodeId: nodeId, cursorX: 0, cursorY: 0 });
      const onMove = (moveEvt) => {
        const scrollContainer = containerRef.current;
        if (!scrollContainer) return;
        const rect = scrollContainer.getBoundingClientRect();
        const currentScrollTop = scrollContainer.scrollTop;
        const centerX = rect.width / 2;
        const cursorX = centerX + (moveEvt.clientX - rect.left - centerX) / zoomScale;
        const cursorY = (moveEvt.clientY - rect.top + currentScrollTop) / zoomScale;
        setDragState((prev) => {
          if (!prev) return null;
          let closestTarget;
          nodePositions.forEach((pos, nId) => {
            if (nId === nodeId) return;
            const topHandleX = pos.x + NODE_WIDTH3 / 2;
            const topHandleY = pos.y;
            const d = Math.sqrt((cursorX - topHandleX) ** 2 + (cursorY - topHandleY) ** 2);
            if (d < SNAP_THRESHOLD && (!closestTarget || d < closestTarget.distance)) {
              closestTarget = { nodeId: nId, x: topHandleX, y: topHandleY, distance: d, handlePosition: "top" };
            }
          });
          return {
            sourceNodeId: nodeId,
            handleId: prev.handleId,
            cursorX,
            cursorY,
            snappedTarget: closestTarget ? { nodeId: closestTarget.nodeId, x: closestTarget.x, y: closestTarget.y, handlePosition: closestTarget.handlePosition } : void 0
          };
        });
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        setDragState((prev) => {
          if (prev?.snappedTarget) {
            onConnect(nodeId, prev.snappedTarget.nodeId, void 0);
          }
          return null;
        });
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [onConnect, zoomScale, nodePositions, containerRef]
  );
  const { nodesAbove, nodesBelow } = React93.useMemo(() => {
    let above = 0;
    let below = 0;
    const viewportTop = scrollTop;
    const viewportBottom = scrollTop + containerHeight;
    nodeOrder.forEach((nodeId) => {
      const pos = nodePositions.get(nodeId);
      if (!pos) return;
      const scaledTop = pos.y * zoomScale;
      const scaledBottom = (pos.y + pos.height) * zoomScale;
      if (scaledBottom < viewportTop) {
        above++;
      } else if (scaledTop > viewportBottom) {
        below++;
      }
    });
    return { nodesAbove: above, nodesBelow: below };
  }, [nodeOrder, nodePositions, scrollTop, containerHeight, zoomScale]);
  const handlePaneClick = React93.useCallback(
    (e) => {
      if (suppressPaneClickRef.current) return;
      const target = e.target;
      const isPaneBackground = target.dataset.pane === "true" || target === containerRef.current || target.tagName.toLowerCase() === "svg";
      if (isPaneBackground) {
        setFocusedNodeId(null);
        multiSelect.clearSelection();
        onPaneClick?.(e);
      }
    },
    [onPaneClick, multiSelect.clearSelection, suppressPaneClickRef]
  );
  const scrollToNode = React93.useCallback((nodeId, force) => {
    const container = containerRef.current;
    if (!container) return;
    const pos = nodePositionsRef.current.get(nodeId);
    if (!pos) return;
    if (userScrolledRef.current && !force) return;
    const scale = zoomScaleRef.current;
    const scaledNodeY = pos.y * scale;
    const scaledNodeHeight = pos.height * scale;
    const viewportHeight = container.clientHeight;
    const targetScrollTop = scaledNodeY + scaledNodeHeight / 2 - viewportHeight / 2;
    const maxScroll = container.scrollHeight - viewportHeight;
    const clampedScrollTop = Math.max(0, Math.min(targetScrollTop, maxScroll));
    userScrolledRef.current = false;
    container.scrollTo({
      top: clampedScrollTop,
      behavior: "smooth"
    });
  }, []);
  React93.useEffect(() => {
    const focusedChanged = focusedNodeId !== prevFocusedNodeIdRef.current;
    const stepChanged = currentStepId !== prevCurrentStepIdRef.current;
    const prevStepId = prevCurrentStepIdRef.current;
    prevFocusedNodeIdRef.current = focusedNodeId ?? null;
    prevCurrentStepIdRef.current = currentStepId ?? null;
    if (!focusedChanged && !stepChanged) return;
    if (!currentStepId && prevStepId && stepChanged) {
      followExecutionRef.current = true;
    }
    if (focusedChanged && focusedNodeId) {
      scrollToNode(focusedNodeId, true);
      return;
    }
    if (stepChanged && currentStepId && followExecutionRef.current) {
      scrollToNode(currentStepId, true);
      return;
    }
  }, [focusedNodeId, currentStepId, scrollToNode]);
  const handleNodeClick = React93.useCallback(
    (e, node) => {
      e.stopPropagation();
      const hasModifier = e.metaKey || e.ctrlKey || e.shiftKey;
      if (hasModifier) {
        setFocusedNodeId(node.id);
        multiSelect.handleNodeSelect(node.id, {
          metaKey: e.metaKey,
          ctrlKey: e.ctrlKey,
          shiftKey: e.shiftKey
        });
      } else {
        setFocusedNodeId((prev) => prev === node.id ? null : node.id);
        followExecutionRef.current = node.id === currentStepId;
        onNodeClick?.(e, node);
      }
    },
    [onNodeClick, multiSelect.handleNodeSelect, currentStepId]
  );
  const getNodeSiblings = React93.useCallback(
    (nodeId) => {
      const parentEdge = edges.find((e) => e.target === nodeId);
      if (!parentEdge) return { left: null, right: null };
      const siblings = edges.filter((e) => e.source === parentEdge.source).map((e) => e.target);
      const currentIndex = siblings.indexOf(nodeId);
      if (currentIndex === -1) return { left: null, right: null };
      return {
        left: currentIndex > 0 ? siblings[currentIndex - 1] ?? null : null,
        right: currentIndex < siblings.length - 1 ? siblings[currentIndex + 1] ?? null : null
      };
    },
    [edges]
  );
  const getAdjacentNodes = React93.useCallback(
    (nodeId) => {
      const parentEdge = edges.find((e) => e.target === nodeId);
      const above = parentEdge?.source ?? null;
      const childEdge = edges.find((e) => e.source === nodeId);
      const below = childEdge?.target ?? null;
      return { above, below };
    },
    [edges]
  );
  const focusNode = React93.useCallback(
    (nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;
      setFocusedNodeId(nodeId);
      onNodeClick?.(
        { stopPropagation: () => {
        }, preventDefault: () => {
        } },
        node
      );
    },
    [nodes, onNodeClick]
  );
  const handleKeyDown = React93.useCallback(
    (e) => {
      if (reorder.isDragging || orphanInsert.isDragging || isMarqueeActive()) return;
      if ((e.metaKey || e.ctrlKey) && e.key === "a") {
        e.preventDefault();
        multiSelect.selectAll();
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        if (multiSelect.selectedNodeIds.size > 0) {
          multiSelect.clearSelection();
        }
        setFocusedNodeId(null);
        return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        if (multiSelect.selectedNodeIds.size > 1 && onNodesDelete) {
          e.preventDefault();
          onNodesDelete([...multiSelect.selectedNodeIds]);
          multiSelect.clearSelection();
          setFocusedNodeId(null);
          return;
        }
      }
      if (!focusedNodeId) return;
      switch (e.key) {
        case "Delete":
        case "Backspace": {
          e.preventDefault();
          if (onNodeDelete) {
            onNodeDelete(focusedNodeId);
            setFocusedNodeId(null);
          }
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          const { above } = getAdjacentNodes(focusedNodeId);
          if (above) {
            focusNode(above);
          }
          break;
        }
        case "ArrowDown": {
          e.preventDefault();
          const { below } = getAdjacentNodes(focusedNodeId);
          if (below) {
            focusNode(below);
          }
          break;
        }
        case "ArrowLeft": {
          e.preventDefault();
          const { left } = getNodeSiblings(focusedNodeId);
          if (left) {
            focusNode(left);
          } else {
            const { above } = getAdjacentNodes(focusedNodeId);
            if (above) {
              focusNode(above);
            }
          }
          break;
        }
        case "ArrowRight": {
          e.preventDefault();
          const { right } = getNodeSiblings(focusedNodeId);
          if (right) {
            focusNode(right);
          } else {
            const { below } = getAdjacentNodes(focusedNodeId);
            if (below) {
              focusNode(below);
            }
          }
          break;
        }
      }
    },
    [focusedNodeId, onNodeDelete, onNodesDelete, getNodeSiblings, getAdjacentNodes, focusNode, reorder.isDragging, orphanInsert.isDragging, isMarqueeActive, multiSelect]
  );
  const setNodeRef = React93.useCallback((id, el) => {
    if (el) {
      nodeRefs.current.set(id, el);
    } else {
      nodeRefs.current.delete(id);
    }
  }, []);
  const handleCanvasDragOver = React93.useCallback(
    (e) => {
      if (!e.dataTransfer.types.includes("application/x-workflow-connection")) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "link";
      const scrollContainer = containerRef.current;
      if (!scrollContainer) return;
      const rect = scrollContainer.getBoundingClientRect();
      const currentScrollTop = scrollContainer.scrollTop;
      const centerX = rect.width / 2;
      const cursorX = centerX + (e.clientX - rect.left - centerX) / zoomScale;
      const cursorY = (e.clientY - rect.top + currentScrollTop) / zoomScale;
      setDragState((prev) => {
        if (!prev) return null;
        const sourceNodeId = prev.sourceNodeId;
        const isFalseBranchDrag = prev.handleId === "false";
        const sourceColumn = nodeColumns.get(sourceNodeId) ?? 0;
        const isLeftBranchDrag = sourceColumn === -1 && prev.handleId !== "false";
        let closestTarget;
        nodePositions.forEach((pos, nodeId) => {
          if (nodeId === sourceNodeId) return;
          const targetColumn = nodeColumns.get(nodeId) ?? 0;
          const topHandleX = pos.x + NODE_WIDTH3 / 2;
          const topHandleY = pos.y;
          const topDx = cursorX - topHandleX;
          const topDy = cursorY - topHandleY;
          const topDistance = Math.sqrt(topDx * topDx + topDy * topDy);
          if (topDistance < SNAP_THRESHOLD && (!closestTarget || topDistance < closestTarget.distance)) {
            closestTarget = { nodeId, x: topHandleX, y: topHandleY, distance: topDistance, handlePosition: "top" };
          }
          const canSnapToSideLeft = isFalseBranchDrag || isLeftBranchDrag && targetColumn === 0;
          if (canSnapToSideLeft) {
            const sideHandleX = pos.x;
            const sideHandleY = pos.y + pos.height / 2;
            const sideDx = cursorX - sideHandleX;
            const sideDy = cursorY - sideHandleY;
            const sideDistance = Math.sqrt(sideDx * sideDx + sideDy * sideDy);
            if (sideDistance < SNAP_THRESHOLD && (!closestTarget || sideDistance < closestTarget.distance)) {
              closestTarget = { nodeId, x: sideHandleX, y: sideHandleY, distance: sideDistance, handlePosition: "side-left" };
            }
          }
        });
        return {
          sourceNodeId,
          handleId: prev.handleId,
          cursorX,
          cursorY,
          snappedTarget: closestTarget ? { nodeId: closestTarget.nodeId, x: closestTarget.x, y: closestTarget.y, handlePosition: closestTarget.handlePosition } : void 0
        };
      });
    },
    [zoomScale, nodePositions]
  );
  const handleCanvasDragEnter = React93.useCallback(
    (e) => {
      if (!e.dataTransfer.types.includes("application/x-workflow-connection")) return;
      const sourceNodeId = e.dataTransfer.getData("application/x-workflow-connection");
      if (sourceNodeId) {
        setDragState({ sourceNodeId, cursorX: 0, cursorY: 0 });
      }
    },
    []
  );
  const handleCanvasDrop = React93.useCallback(
    (e) => {
      e.preventDefault();
      const rawData = e.dataTransfer.getData("application/x-workflow-connection");
      if (!rawData) {
        setDragState(null);
        return;
      }
      let sourceNodeId;
      let handleId;
      try {
        const parsed = JSON.parse(rawData);
        sourceNodeId = parsed.nodeId;
        handleId = parsed.handleId;
      } catch {
        sourceNodeId = rawData;
        handleId = dragState?.handleId;
      }
      let targetNodeId = null;
      if (dragState?.snappedTarget) {
        targetNodeId = dragState.snappedTarget.nodeId;
      } else {
        const target = document.elementFromPoint(e.clientX, e.clientY);
        const handleEl = target?.closest('[data-handle-type="target"]');
        targetNodeId = handleEl?.getAttribute("data-node-id") ?? null;
      }
      if (targetNodeId && sourceNodeId !== targetNodeId) {
        let edgeType;
        if (handleId === "true") {
          edgeType = "condition_true";
        } else if (handleId === "false") {
          edgeType = "condition_false";
        }
        onConnect?.(sourceNodeId, targetNodeId, edgeType);
      }
      setDragState(null);
    },
    [onConnect, dragState?.snappedTarget, dragState?.handleId]
  );
  const handleCanvasDragLeave = React93.useCallback((e) => {
    if (!containerRef.current?.contains(e.relatedTarget)) {
      setDragState(null);
    }
  }, []);
  React93.useEffect(() => {
    const handleDragStart = (e) => {
      const target = getClosestElementTarget(e.target);
      const handle = target?.closest('[data-handle-type="source"]');
      if (!handle) return;
      const nodeId = handle.getAttribute("data-node-id");
      const handleId = handle.getAttribute("data-handle-id");
      if (nodeId) {
        setDragState({
          sourceNodeId: nodeId,
          handleId: handleId || void 0,
          cursorX: 0,
          cursorY: 0
        });
      }
    };
    const handleDragEnd = () => {
      setDragState(null);
    };
    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("dragend", handleDragEnd);
    return () => {
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("dragend", handleDragEnd);
    };
  }, []);
  return /* @__PURE__ */ jsxs126(Fragment32, { children: [
    /* @__PURE__ */ jsx143("style", { children: edgeAnimationStyles }),
    /* @__PURE__ */ jsxs126(
      "div",
      {
        className: cn("relative w-full h-full outline-none", className),
        tabIndex: 0,
        onKeyDown: handleKeyDown,
        children: [
          /* @__PURE__ */ jsx143(AnimatePresence6, { children: nodesAbove > 0 && /* @__PURE__ */ jsxs126(
            motion11.div,
            {
              initial: { opacity: 0, y: -20 },
              animate: { opacity: 1, y: 0 },
              exit: { opacity: 0, y: -20 },
              transition: { duration: 0.2 },
              className: "absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 px-3 py-1.5 bg-background/90 backdrop-blur-sm border border-border rounded-full shadow-sm text-xs text-muted-foreground",
              children: [
                /* @__PURE__ */ jsx143(CaretUp4, { size: 12, weight: "bold" }),
                /* @__PURE__ */ jsxs126("span", { children: [
                  nodesAbove,
                  " step",
                  nodesAbove !== 1 ? "s" : "",
                  " above"
                ] })
              ]
            }
          ) }),
          /* @__PURE__ */ jsx143(AnimatePresence6, { children: nodesBelow > 0 && /* @__PURE__ */ jsxs126(
            motion11.div,
            {
              initial: { opacity: 0, y: 20 },
              animate: { opacity: 1, y: 0 },
              exit: { opacity: 0, y: 20 },
              transition: { duration: 0.2 },
              className: "absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 px-3 py-1.5 bg-background/90 backdrop-blur-sm border border-border rounded-full shadow-sm text-xs text-muted-foreground",
              children: [
                /* @__PURE__ */ jsx143(CaretDown11, { size: 12, weight: "bold" }),
                /* @__PURE__ */ jsxs126("span", { children: [
                  nodesBelow,
                  " step",
                  nodesBelow !== 1 ? "s" : "",
                  " below"
                ] })
              ]
            }
          ) }),
          /* @__PURE__ */ jsx143(AnimatePresence6, { children: isGenerating && /* @__PURE__ */ jsx143(
            motion11.div,
            {
              initial: { opacity: 0, x: 20 },
              animate: { opacity: 1, x: 0 },
              exit: { opacity: 0, x: 20 },
              transition: { duration: 0.2 },
              className: "absolute bottom-3 right-3 z-20",
              children: /* @__PURE__ */ jsx143(
                TagBadge,
                {
                  name: "Generating",
                  color: "yellow",
                  size: "sm",
                  icon: /* @__PURE__ */ jsx143(CircleNotch3, { size: 10, weight: "bold", className: "animate-spin-fast" })
                }
              )
            }
          ) }),
          /* @__PURE__ */ jsx143(
            "div",
            {
              ref: containerRef,
              className: cn(
                "w-full h-full overflow-auto rounded-lg",
                isGenerating ? "animate-generating-glow border-l-4 border-r-4 border-b-4 border-yellow-400" : "bg-[var(--canvas,hsl(var(--muted)/0.2))]"
              ),
              onClick: handlePaneClick,
              onMouseDown: marquee.handleMouseDown,
              onDragOver: handleCanvasDragOver,
              onDragEnter: handleCanvasDragEnter,
              onDragLeave: handleCanvasDragLeave,
              onDrop: handleCanvasDrop,
              "data-pane": "true",
              "data-flow-canvas": "true",
              children: /* @__PURE__ */ jsxs126(
                "div",
                {
                  className: "relative transition-transform duration-300 ease-out",
                  style: {
                    minHeight: canvasHeight * zoomScale,
                    transform: `scale(${zoomScale})`,
                    transformOrigin: "top center"
                  },
                  "data-pane": "true",
                  children: [
                    /* @__PURE__ */ jsxs126(
                      "svg",
                      {
                        className: "absolute inset-0 w-full",
                        style: { height: canvasHeight, pointerEvents: onEdgeClick ? "auto" : "none" },
                        children: [
                          edges.map((edge) => {
                            const sourcePos = nodePositions.get(edge.source);
                            const targetPos = nodePositions.get(edge.target);
                            if (!sourcePos || !targetPos) return null;
                            const isActive = currentStepId === edge.source || currentStepId === edge.target;
                            const isSelected = selectedEdgeId === edge.id;
                            const edgeType = edge.type ?? edge.edgeType;
                            const sourceNode = nodes.find((n) => n.id === edge.source);
                            const isConditionNode = sourceNode?.type === "condition";
                            const sourceColumn = nodeColumns.get(edge.source) ?? 0;
                            const targetColumn = nodeColumns.get(edge.target) ?? 0;
                            const isMergeEdge = sourceColumn === -1 && targetColumn === 0 && edgeType !== "condition_false" && edgeType !== "condition_true";
                            let sourceX;
                            let sourceY;
                            let targetX;
                            let targetY;
                            const targetIncomingCount = edges.filter((e) => e.target === edge.target).length;
                            const isMergeTarget = targetIncomingCount > 1;
                            if (isMergeEdge) {
                              sourceX = sourcePos.x + NODE_WIDTH3 / 2;
                              sourceY = sourcePos.y + sourcePos.height;
                              targetX = targetPos.x;
                              targetY = targetPos.y + targetPos.height / 2;
                            } else if (isConditionNode && edgeType === "condition_false") {
                              sourceX = sourcePos.x;
                              sourceY = sourcePos.y + sourcePos.height / 2;
                              if (isMergeTarget) {
                                targetX = targetPos.x;
                                targetY = targetPos.y + targetPos.height / 2;
                              } else {
                                targetX = targetPos.x + NODE_WIDTH3 / 2;
                                targetY = targetPos.y;
                              }
                            } else {
                              sourceX = sourcePos.x + NODE_WIDTH3 / 2;
                              sourceY = sourcePos.y + sourcePos.height;
                              targetX = targetPos.x + NODE_WIDTH3 / 2;
                              targetY = targetPos.y;
                            }
                            return /* @__PURE__ */ jsx143(
                              VerticalEdge,
                              {
                                id: edge.id,
                                sourceNodeId: edge.source,
                                targetNodeId: edge.target,
                                sourceX,
                                sourceY,
                                targetX,
                                targetY,
                                isActive,
                                selected: isSelected,
                                edgeType,
                                isMergeTarget,
                                isMergeEdge,
                                onClick: onEdgeClick ? (edgeId, e) => onEdgeClick(edgeId, edge, e) : void 0,
                                highlighted: orphanInsert.isDragging && orphanInsert.highlightedEdgeId === edge.id
                              },
                              edge.id
                            );
                          }),
                          dragState && (() => {
                            const sourcePos = nodePositions.get(dragState.sourceNodeId);
                            if (!sourcePos) return null;
                            const sourceNode = nodes.find((n) => n.id === dragState.sourceNodeId);
                            const isConditionNode = sourceNode?.type === "condition";
                            const isSideHandle = isConditionNode && dragState.handleId === "false";
                            const sourceColumn = nodeColumns.get(dragState.sourceNodeId) ?? 0;
                            const isLeftBranchDrag = sourceColumn === -1 && !isSideHandle;
                            let x1;
                            let y1;
                            if (isSideHandle) {
                              x1 = sourcePos.x;
                              y1 = sourcePos.y + sourcePos.height / 2;
                            } else {
                              x1 = sourcePos.x + NODE_WIDTH3 / 2;
                              y1 = sourcePos.y + sourcePos.height;
                            }
                            const isSnapped = !!dragState.snappedTarget;
                            const x2 = dragState.snappedTarget?.x ?? dragState.cursorX;
                            const y2 = dragState.snappedTarget?.y ?? dragState.cursorY;
                            let path;
                            const r = 8;
                            if (isSideHandle) {
                              const horizontalOffset = 50;
                              const leftX = Math.min(x1, x2) - horizontalOffset;
                              const verticalDistance = Math.abs(y2 - y1);
                              if (verticalDistance < 10) {
                                path = `M ${x1} ${y1} H ${x2}`;
                              } else {
                                path = `M ${x1} ${y1} H ${leftX + r} A ${r} ${r} 0 0 0 ${leftX} ${y1 + r} V ${y2 - r} A ${r} ${r} 0 0 0 ${leftX + r} ${y2} H ${x2}`;
                              }
                            } else if (isLeftBranchDrag && x2 > x1) {
                              const hDist = x2 - x1;
                              if (y2 > y1 + r * 2) {
                                const actualR = Math.min(r, hDist / 2, (y2 - y1) / 2);
                                if (actualR < 2) {
                                  path = `M ${x1} ${y1} V ${y2} H ${x2}`;
                                } else {
                                  path = `M ${x1} ${y1} V ${y2 - actualR} A ${actualR} ${actualR} 0 0 0 ${x1 + actualR} ${y2} H ${x2}`;
                                }
                              } else {
                                const floorY = y1 + 30;
                                const routeX = x1 + NODE_WIDTH3 / 2 + (x2 - x1 - NODE_WIDTH3 / 2) / 2;
                                const actualR = Math.min(r, (routeX - x1) / 2, (x2 - routeX) / 2, 14);
                                if (actualR < 2 || hDist < 4) {
                                  path = `M ${x1} ${y1} V ${floorY} H ${routeX} V ${y2} H ${x2}`;
                                } else {
                                  path = `M ${x1} ${y1} V ${floorY - actualR} A ${actualR} ${actualR} 0 0 0 ${x1 + actualR} ${floorY} H ${routeX - actualR} A ${actualR} ${actualR} 0 0 1 ${routeX} ${floorY - actualR} V ${y2 + actualR} A ${actualR} ${actualR} 0 0 0 ${routeX + actualR} ${y2} H ${x2}`;
                                }
                              }
                            } else {
                              path = `M ${x1} ${y1} L ${x2} ${y2}`;
                            }
                            return /* @__PURE__ */ jsxs126("g", { className: "pointer-events-none", children: [
                              /* @__PURE__ */ jsx143(
                                "path",
                                {
                                  d: path,
                                  fill: "none",
                                  stroke: isSnapped ? "rgba(34, 197, 94, 0.4)" : "rgba(99, 102, 241, 0.3)",
                                  strokeWidth: 6,
                                  className: "blur-sm"
                                }
                              ),
                              /* @__PURE__ */ jsx143(
                                "path",
                                {
                                  d: path,
                                  fill: "none",
                                  stroke: isSnapped ? "#22c55e" : "#6366f1",
                                  strokeWidth: 2,
                                  strokeLinecap: "round",
                                  strokeDasharray: isSnapped ? "none" : "5 5",
                                  style: isSnapped ? void 0 : { animation: "verticalEdgeDash 1s linear infinite" }
                                }
                              ),
                              isSnapped ? (
                                // Solid circle when snapped to indicate valid connection
                                /* @__PURE__ */ jsx143(
                                  "circle",
                                  {
                                    cx: x2,
                                    cy: y2,
                                    r: 6,
                                    fill: "#22c55e",
                                    stroke: "#fff",
                                    strokeWidth: 2
                                  }
                                )
                              ) : (
                                // Arrow head when not snapped
                                /* @__PURE__ */ jsx143(
                                  "polygon",
                                  {
                                    points: `${x2 - 4},${y2 - 8} ${x2 + 4},${y2 - 8} ${x2},${y2}`,
                                    fill: "#6366f1"
                                  }
                                )
                              )
                            ] });
                          })()
                        ]
                      }
                    ),
                    onPrependNode && nodeOrder.length > 0 && (() => {
                      const firstNodeId = nodeOrder[0];
                      const firstNodePos = nodePositions.get(firstNodeId);
                      if (!firstNodePos) return null;
                      return /* @__PURE__ */ jsxs126(
                        "div",
                        {
                          className: "absolute flex flex-col items-center z-30",
                          style: {
                            left: firstNodePos.x + NODE_WIDTH3 / 2,
                            top: firstNodePos.y - 50,
                            transform: "translateX(-50%)"
                          },
                          children: [
                            /* @__PURE__ */ jsx143(
                              AddNodeMenuPopover,
                              {
                                ariaLabel: "Add first step",
                                header: "Insert at beginning",
                                visibleTypes: ["login", "navigate", "action", "assertion", "condition", "wait", "seed"],
                                onSelect: (type) => onPrependNode(type)
                              }
                            ),
                            /* @__PURE__ */ jsx143("div", { className: "w-0.5 h-4 bg-border mt-1" })
                          ]
                        }
                      );
                    })(),
                    reorder.isDragging && reorder.activeChain.length >= 2 && /* @__PURE__ */ jsxs126(
                      "svg",
                      {
                        className: "absolute inset-0 w-full pointer-events-none",
                        style: { height: canvasHeight, zIndex: 25 },
                        children: [
                          reorder.activeChain.map((chainNodeId, i) => {
                            if (chainNodeId === reorder.draggedNodeId) return null;
                            const pos = adjustedNodePositions.get(chainNodeId);
                            if (!pos) return null;
                            const chainIndex = reorder.activeChain.indexOf(chainNodeId);
                            const isActive = reorder.activeDropZoneIndex === chainIndex;
                            const lineY = pos.y - VERTICAL_GAP / 2;
                            const lineX1 = pos.x + 20;
                            const lineX2 = pos.x + NODE_WIDTH3 - 20;
                            return /* @__PURE__ */ jsx143("g", { children: isActive ? /* @__PURE__ */ jsxs126(Fragment32, { children: [
                              /* @__PURE__ */ jsx143(
                                "line",
                                {
                                  x1: lineX1,
                                  y1: lineY,
                                  x2: lineX2,
                                  y2: lineY,
                                  stroke: "rgba(99, 102, 241, 0.3)",
                                  strokeWidth: 6,
                                  className: "blur-sm"
                                }
                              ),
                              /* @__PURE__ */ jsx143(
                                "line",
                                {
                                  x1: lineX1,
                                  y1: lineY,
                                  x2: lineX2,
                                  y2: lineY,
                                  stroke: "#6366f1",
                                  strokeWidth: 2,
                                  strokeLinecap: "round",
                                  children: /* @__PURE__ */ jsx143(
                                    "animate",
                                    {
                                      attributeName: "opacity",
                                      values: "0.6;1;0.6",
                                      dur: "0.8s",
                                      repeatCount: "indefinite"
                                    }
                                  )
                                }
                              )
                            ] }) : /* @__PURE__ */ jsxs126(Fragment32, { children: [
                              /* @__PURE__ */ jsx143(
                                "line",
                                {
                                  x1: lineX1,
                                  y1: lineY,
                                  x2: lineX2,
                                  y2: lineY,
                                  stroke: "currentColor",
                                  strokeWidth: 1,
                                  strokeDasharray: "4 4",
                                  opacity: 0.2,
                                  className: "text-border"
                                }
                              ),
                              /* @__PURE__ */ jsx143(
                                "circle",
                                {
                                  cx: (lineX1 + lineX2) / 2,
                                  cy: lineY,
                                  r: 3,
                                  fill: "currentColor",
                                  opacity: 0.15,
                                  className: "text-border"
                                }
                              )
                            ] }) }, `dropzone-${i}`);
                          }),
                          (() => {
                            const lastNonDraggedId = [...reorder.activeChain].reverse().find((id) => id !== reorder.draggedNodeId);
                            if (!lastNonDraggedId) return null;
                            const pos = adjustedNodePositions.get(lastNonDraggedId);
                            if (!pos) return null;
                            const isActive = reorder.activeDropZoneIndex === reorder.activeChain.length;
                            const lineY = pos.y + pos.height + VERTICAL_GAP / 2;
                            const lineX1 = pos.x + 20;
                            const lineX2 = pos.x + NODE_WIDTH3 - 20;
                            return /* @__PURE__ */ jsx143("g", { children: isActive ? /* @__PURE__ */ jsxs126(Fragment32, { children: [
                              /* @__PURE__ */ jsx143(
                                "line",
                                {
                                  x1: lineX1,
                                  y1: lineY,
                                  x2: lineX2,
                                  y2: lineY,
                                  stroke: "rgba(99, 102, 241, 0.3)",
                                  strokeWidth: 6,
                                  className: "blur-sm"
                                }
                              ),
                              /* @__PURE__ */ jsx143(
                                "line",
                                {
                                  x1: lineX1,
                                  y1: lineY,
                                  x2: lineX2,
                                  y2: lineY,
                                  stroke: "#6366f1",
                                  strokeWidth: 2,
                                  strokeLinecap: "round",
                                  children: /* @__PURE__ */ jsx143("animate", { attributeName: "opacity", values: "0.6;1;0.6", dur: "0.8s", repeatCount: "indefinite" })
                                }
                              )
                            ] }) : /* @__PURE__ */ jsxs126(Fragment32, { children: [
                              /* @__PURE__ */ jsx143(
                                "line",
                                {
                                  x1: lineX1,
                                  y1: lineY,
                                  x2: lineX2,
                                  y2: lineY,
                                  stroke: "currentColor",
                                  strokeWidth: 1,
                                  strokeDasharray: "4 4",
                                  opacity: 0.2,
                                  className: "text-border"
                                }
                              ),
                              /* @__PURE__ */ jsx143(
                                "circle",
                                {
                                  cx: (lineX1 + lineX2) / 2,
                                  cy: lineY,
                                  r: 3,
                                  fill: "currentColor",
                                  opacity: 0.15,
                                  className: "text-border"
                                }
                              )
                            ] }) });
                          })()
                        ]
                      }
                    ),
                    orphanInsert.isDragging && orphanInsert.highlightedEdgeId && (() => {
                      const edge = edges.find((e) => e.id === orphanInsert.highlightedEdgeId);
                      if (!edge) return null;
                      const sourcePos = adjustedNodePositions.get(edge.source);
                      const targetPos = adjustedNodePositions.get(edge.target);
                      if (!sourcePos || !targetPos) return null;
                      const lineY = sourcePos.y + sourcePos.height + (targetPos.y - sourcePos.y - sourcePos.height) / 2;
                      const lineX1 = sourcePos.x + 20;
                      const lineX2 = sourcePos.x + NODE_WIDTH3 - 20;
                      return /* @__PURE__ */ jsxs126(
                        "svg",
                        {
                          className: "absolute inset-0 w-full pointer-events-none",
                          style: { height: canvasHeight * 1.5, zIndex: 25 },
                          children: [
                            /* @__PURE__ */ jsx143(
                              "line",
                              {
                                x1: lineX1,
                                y1: lineY,
                                x2: lineX2,
                                y2: lineY,
                                stroke: "rgba(99, 102, 241, 0.3)",
                                strokeWidth: 6,
                                className: "blur-sm"
                              }
                            ),
                            /* @__PURE__ */ jsx143(
                              "line",
                              {
                                x1: lineX1,
                                y1: lineY,
                                x2: lineX2,
                                y2: lineY,
                                stroke: "#6366f1",
                                strokeWidth: 2,
                                strokeLinecap: "round",
                                children: /* @__PURE__ */ jsx143("animate", { attributeName: "opacity", values: "0.6;1;0.6", dur: "0.8s", repeatCount: "indefinite" })
                              }
                            )
                          ]
                        }
                      );
                    })(),
                    nodeOrder.map((nodeId) => {
                      const node = nodes.find((n) => n.id === nodeId);
                      if (!node) return null;
                      const nodeType = node.type ?? "unknown";
                      const NodeComponent = nodeTypes[nodeType];
                      if (!NodeComponent) {
                        console.warn(`No component registered for node type: ${nodeType}`);
                        return null;
                      }
                      const pos = reorder.isDragging || orphanInsert.isDragging ? adjustedNodePositions.get(nodeId) ?? nodePositions.get(nodeId) : nodePositions.get(nodeId);
                      if (!pos) return null;
                      const isNewNode = newNodeIdsRef.current.has(nodeId);
                      const isBeingDragged = reorder.isDragging && reorder.draggedNodeIds.includes(nodeId) || orphanInsert.isDragging && orphanInsert.draggedNodeId === nodeId;
                      const isConditionNode = node.type === "condition";
                      const canDrag = (!!onNodeReorder || !!onOrphanInsertOnEdge) && !isConditionNode && !reorder.isDragging && !orphanInsert.isDragging;
                      const dragSourceColumn = dragState?.sourceNodeId ? nodeColumns.get(dragState.sourceNodeId) ?? 0 : 0;
                      const thisNodeColumn = nodeColumns.get(nodeId) ?? 0;
                      const isLeftBranchDrag = dragSourceColumn === -1 && dragState?.handleId !== "false" && thisNodeColumn === 0;
                      const showSideTargetHandle = dragState && dragState.sourceNodeId !== nodeId && (dragState.handleId === "false" || isLeftBranchDrag);
                      const hasOutgoingEdge = edges.some((e) => e.source === nodeId);
                      const hasYesBranchEdge = isConditionNode ? edges.some((e) => {
                        if (e.source !== nodeId) return false;
                        const type = e.type ?? e.edgeType;
                        return type !== "condition_false";
                      }) : false;
                      const hasNoBranchEdge = isConditionNode ? edges.some(
                        (e) => e.source === nodeId && (e.type === "condition_false" || e.edgeType === "condition_false")
                      ) : false;
                      const augmentedData = {
                        ...node.data,
                        hasOutgoingEdge,
                        hasYesBranchEdge,
                        hasNoBranchEdge,
                        onDelete: onNodeDelete ? () => onNodeDelete(nodeId) : void 0,
                        onHoldDrag: canDrag ? (e) => startAnchorDrag(nodeId, e) : void 0
                      };
                      return /* @__PURE__ */ jsxs126(
                        motion11.div,
                        {
                          "data-node-id": nodeId,
                          "data-node-type": node.type ?? "default",
                          ref: (el) => setNodeRef(nodeId, el),
                          className: cn(
                            "absolute cursor-pointer",
                            isNewNode && "z-10",
                            isBeingDragged && "pointer-events-none"
                          ),
                          initial: isNewNode ? { opacity: 0, scale: 0.8, y: -20 } : false,
                          animate: {
                            left: pos.x,
                            top: pos.y,
                            opacity: 1,
                            scale: 1,
                            y: 0
                          },
                          transition: {
                            type: "spring",
                            stiffness: 500,
                            damping: 30,
                            mass: 0.8
                          },
                          style: { width: NODE_WIDTH3 },
                          onPointerDown: canDrag ? (e) => handleNodePointerDown(nodeId, e) : void 0,
                          onClick: reorder.isDragging || orphanInsert.isDragging ? void 0 : (e) => handleNodeClick(e, node),
                          children: [
                            isNewNode && !isBeingDragged && /* @__PURE__ */ jsx143(
                              motion11.div,
                              {
                                className: "absolute -inset-1 rounded-xl bg-primary/20 blur-md",
                                initial: { opacity: 0 },
                                animate: { opacity: [0, 0.8, 0] },
                                transition: { duration: 1, times: [0, 0.3, 1] }
                              }
                            ),
                            isBeingDragged ? (
                              // Gray placeholder left behind while dragging
                              /* @__PURE__ */ jsx143(
                                "div",
                                {
                                  className: "rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted/30",
                                  style: { height: nodeHeights.get(nodeId) ?? 80 }
                                }
                              )
                            ) : /* @__PURE__ */ jsx143(NodeComponent, { data: augmentedData, nodeId, selected: displaySelectedIds.has(nodeId), focused: focusedNodeId === nodeId }),
                            showSideTargetHandle && /* @__PURE__ */ jsx143(
                              "div",
                              {
                                "data-handle-type": "target",
                                "data-handle-id": "side-target",
                                "data-node-id": nodeId,
                                "data-handle-position": "side-left",
                                className: cn(
                                  "absolute w-4 h-4 rounded-full border-2 border-background z-20",
                                  "-left-2 top-1/2 -translate-y-1/2",
                                  "bg-red-500 animate-pulse",
                                  dragState?.snappedTarget?.nodeId === nodeId && dragState?.snappedTarget?.handlePosition === "side-left" && "ring-2 ring-red-400 ring-offset-2 scale-125"
                                )
                              }
                            )
                          ]
                        },
                        nodeId
                      );
                    }),
                    marquee.marqueeRect && /* @__PURE__ */ jsx143(
                      "div",
                      {
                        className: "absolute pointer-events-none z-40 rounded-sm",
                        style: {
                          left: marquee.marqueeRect.left,
                          top: marquee.marqueeRect.top,
                          width: marquee.marqueeRect.width,
                          height: marquee.marqueeRect.height,
                          border: "2px solid hsl(48, 96%, 53%)",
                          backgroundColor: "hsla(48, 96%, 53%, 0.08)"
                        }
                      }
                    ),
                    reorder.isDragging && reorder.draggedNodeId && reorder.ghostPosition && (() => {
                      const ghostNodeIds = reorder.draggedNodeIds.length > 0 ? reorder.draggedNodeIds : [reorder.draggedNodeId];
                      return ghostNodeIds.map((ghostId, idx) => {
                        const node = nodes.find((n) => n.id === ghostId);
                        if (!node) return null;
                        const nodeType = node.type ?? "unknown";
                        const GhostNodeComponent = nodeTypes[nodeType];
                        if (!GhostNodeComponent) return null;
                        const ghostData = {
                          ...node.data,
                          hasOutgoingEdge: false,
                          hasYesBranchEdge: false,
                          hasNoBranchEdge: false
                        };
                        const yOffset = idx * 8;
                        const rotation = ghostNodeIds.length > 1 ? idx === 0 ? -0.5 : idx === ghostNodeIds.length - 1 ? 1.5 : 1 : 1;
                        return /* @__PURE__ */ jsx143(
                          "div",
                          {
                            className: "absolute pointer-events-none",
                            style: {
                              left: reorder.ghostPosition.x,
                              top: reorder.ghostPosition.y + yOffset,
                              width: NODE_WIDTH3,
                              zIndex: 50 + idx,
                              transform: `scale(1.03) rotate(${rotation}deg)`,
                              filter: "drop-shadow(0 12px 28px rgba(0,0,0,0.15))",
                              opacity: idx === 0 ? 1 : 0.85
                            },
                            children: /* @__PURE__ */ jsx143(GhostNodeComponent, { data: ghostData, nodeId: ghostId, selected: false, focused: false })
                          },
                          ghostId
                        );
                      });
                    })(),
                    orphanInsert.isDragging && orphanInsert.draggedNodeId && orphanInsert.ghostPosition && (() => {
                      const node = nodes.find((n) => n.id === orphanInsert.draggedNodeId);
                      if (!node) return null;
                      const nodeType = node.type ?? "unknown";
                      const GhostNodeComponent = nodeTypes[nodeType];
                      if (!GhostNodeComponent) return null;
                      const ghostData = {
                        ...node.data,
                        hasOutgoingEdge: false,
                        hasYesBranchEdge: false,
                        hasNoBranchEdge: false
                      };
                      return /* @__PURE__ */ jsx143(
                        "div",
                        {
                          className: "absolute pointer-events-none",
                          style: {
                            left: orphanInsert.ghostPosition.x,
                            top: orphanInsert.ghostPosition.y,
                            width: NODE_WIDTH3,
                            zIndex: 50,
                            transform: "scale(1.03) rotate(1deg)",
                            filter: "drop-shadow(0 12px 28px rgba(0,0,0,0.15))"
                          },
                          children: /* @__PURE__ */ jsx143(GhostNodeComponent, { data: ghostData, nodeId: orphanInsert.draggedNodeId, selected: false, focused: false })
                        }
                      );
                    })()
                  ]
                }
              )
            }
          )
        ]
      }
    )
  ] });
}

// src/components/BrowserStreamPanel.tsx
import React94 from "react";
import { motion as motion13, AnimatePresence as AnimatePresence7 } from "framer-motion";
import { Brain as Brain4, ArrowsOut as ArrowsOut3, X as X5, Lightning as Lightning4 } from "@phosphor-icons/react";

// src/components/BrowserStreamActions.tsx
import { motion as motion12 } from "framer-motion";
import {
  NavigationArrow as NavigationArrow2,
  CursorClick as CursorClick2,
  Keyboard as Keyboard2,
  Eye as Eye3,
  ArrowsOutCardinal as ArrowsOutCardinal2,
  ListBullets as ListBullets2,
  FileArrowUp as FileArrowUp2,
  ChatCircle as ChatCircle3,
  Command as Command2,
  Camera as Camera5,
  Code as Code4,
  ArrowsOut as ArrowsOut2,
  CheckCircle as CheckCircle13,
  CheckSquare,
  Square,
  Clock as Clock5
} from "@phosphor-icons/react";
import { jsx as jsx144, jsxs as jsxs127 } from "react/jsx-runtime";
var actionIcons = {
  navigate: NavigationArrow2,
  click: CursorClick2,
  check: CheckSquare,
  uncheck: Square,
  type: Keyboard2,
  hover: Eye3,
  drag: ArrowsOutCardinal2,
  resize: ArrowsOut2,
  scroll: ListBullets2,
  select: ListBullets2,
  "file-upload": FileArrowUp2,
  "dialog-handle": ChatCircle3,
  "press-key": Command2,
  snapshot: Camera5,
  screenshot: Camera5,
  evaluate: Code4,
  assert: CheckCircle13,
  move: ArrowsOutCardinal2,
  wait: Clock5,
  "run-code": Code4
};
var ActionIcon = ({
  action,
  size = 16,
  className
}) => {
  const Icon = actionIcons[action] ?? NavigationArrow2;
  return /* @__PURE__ */ jsx144(Icon, { size, className });
};
function formatActionDetail(action) {
  switch (action.action) {
    case "press-key":
      return action.key ? `"${action.key}"` : action.value ? `"${action.value}"` : null;
    case "type":
      return action.value ? `"${action.value}"` : null;
    case "navigate":
      return action.url ?? action.value ?? null;
    case "select":
      return action.value ? `"${action.value}"` : null;
    case "click":
    case "hover": {
      const parts = [];
      if (action.clickType === "ref" && action.ref) {
        parts.push(`ref: ${action.ref}`);
      } else if (action.clickType === "coordinates" && action.coordinates) {
        parts.push(`coords: (${action.coordinates.x}, ${action.coordinates.y})`);
      } else if (action.coordinates) {
        parts.push(`(${action.coordinates.x}, ${action.coordinates.y})`);
      } else if (action.ref) {
        parts.push(`ref: ${action.ref}`);
      }
      if (action.clickedElement) {
        const el = action.clickedElement;
        const desc = [el.tagName.toLowerCase()];
        if (el.id) desc.push(`#${el.id}`);
        if (el.role) desc.push(`[${el.role}]`);
        parts.push(`\u2192 ${desc.join("")}`);
      }
      return parts.length > 0 ? parts.join(" ") : null;
    }
    case "assert":
      return action.target ?? action.value ?? null;
    case "drag":
      if (action.coordinates) {
        return `from (${action.coordinates.x}, ${action.coordinates.y})`;
      }
      return null;
    default:
      return action.value ?? null;
  }
}
function formatModifiers(modifiers) {
  if (!modifiers || modifiers.length === 0) return null;
  return modifiers.join("+");
}
var CompactActionCard = ({
  action
}) => {
  if (!action) {
    return /* @__PURE__ */ jsx144("div", { className: "rounded-md border border-dashed border-border bg-muted/50 px-3 py-2 text-center", children: /* @__PURE__ */ jsx144(Text, { size: "xs", className: "text-muted-foreground", children: "No actions yet" }) });
  }
  const detail = formatActionDetail(action);
  const modifiers = formatModifiers(action.modifiers);
  const primary = action.label ?? action.target;
  const tooltipParts = [
    action.label,
    action.target,
    action.ref && `ref: ${action.ref}`,
    action.coordinates && `at (${action.coordinates.x}, ${action.coordinates.y})`
  ].filter(Boolean);
  const tooltip = tooltipParts.length > 0 ? tooltipParts.join(" \xB7 ") : void 0;
  return /* @__PURE__ */ jsxs127(
    motion12.div,
    {
      initial: { opacity: 0, x: -8 },
      animate: { opacity: 1, x: 0 },
      transition: { duration: 0.15 },
      className: cn(
        "flex items-center gap-2 rounded-md border px-3 py-2.5",
        action.success ? "border-border/70 bg-muted/40" : "border-red-500/50 bg-red-500/10"
      ),
      title: tooltip,
      children: [
        /* @__PURE__ */ jsx144(
          ActionIcon,
          {
            action: action.action,
            size: 14,
            className: "text-muted-foreground flex-shrink-0"
          }
        ),
        modifiers && /* @__PURE__ */ jsx144("span", { className: "text-[10px] font-mono bg-muted px-1 rounded text-muted-foreground flex-shrink-0", children: modifiers }),
        /* @__PURE__ */ jsx144(
          TruncatedText,
          {
            text: action.action.replace("-", " "),
            className: "text-xs font-medium capitalize"
          }
        ),
        primary ? /* @__PURE__ */ jsx144(TruncatedText, { text: primary, className: "text-xs text-muted-foreground flex-1" }) : detail ? /* @__PURE__ */ jsx144(TruncatedText, { text: detail, className: "text-xs text-muted-foreground flex-1 font-mono" }) : null,
        primary && detail && (action.action === "type" || action.action === "select") && /* @__PURE__ */ jsx144(
          TruncatedText,
          {
            text: detail,
            className: "text-xs text-muted-foreground max-w-[100px] font-mono"
          }
        ),
        action.action === "press-key" && detail && /* @__PURE__ */ jsx144(TruncatedText, { text: detail, className: "text-xs text-muted-foreground font-mono" }),
        /* @__PURE__ */ jsx144(
          StatusIndicator,
          {
            variant: action.success ? "positive" : "negative",
            size: "sm",
            className: "text-xs flex-shrink-0",
            children: action.success ? "OK" : "Fail"
          }
        )
      ]
    },
    action.timestamp
  );
};
var ActionRow = ({ action }) => {
  const detail = formatActionDetail(action);
  const modifiers = formatModifiers(action.modifiers);
  const primary = action.label ?? action.target;
  const tooltipParts = [
    action.label,
    action.target,
    action.ref && `ref: ${action.ref}`,
    action.coordinates && `at (${action.coordinates.x}, ${action.coordinates.y})`,
    action.error
  ].filter(Boolean);
  const tooltip = tooltipParts.length > 0 ? tooltipParts.join(" \xB7 ") : void 0;
  return /* @__PURE__ */ jsxs127(
    "div",
    {
      className: "flex items-center gap-2 border-b border-border/70 px-3 py-2 text-xs transition-colors last:border-b-0 hover:bg-muted/70",
      title: tooltip,
      children: [
        /* @__PURE__ */ jsx144(
          ActionIcon,
          {
            action: action.action,
            size: 11,
            className: "text-muted-foreground flex-shrink-0"
          }
        ),
        modifiers && /* @__PURE__ */ jsx144("span", { className: "text-[9px] font-mono bg-muted px-0.5 rounded text-muted-foreground flex-shrink-0", children: modifiers }),
        /* @__PURE__ */ jsx144("span", { className: "capitalize font-medium", children: action.action.replace("-", " ") }),
        primary ? /* @__PURE__ */ jsx144(TruncatedText, { text: primary, className: "text-muted-foreground flex-1" }) : detail ? /* @__PURE__ */ jsx144(
          TruncatedText,
          {
            text: detail,
            className: "text-muted-foreground flex-1 font-mono text-[10px]"
          }
        ) : /* @__PURE__ */ jsx144("span", { className: "flex-1" }),
        primary && detail && (action.action === "type" || action.action === "select") && /* @__PURE__ */ jsx144(
          TruncatedText,
          {
            text: detail,
            className: "text-muted-foreground max-w-[60px] font-mono text-[10px]"
          }
        ),
        action.action === "press-key" && detail && /* @__PURE__ */ jsx144(TruncatedText, { text: detail, className: "text-muted-foreground font-mono text-[10px]" }),
        !action.success && /* @__PURE__ */ jsx144("span", { className: "text-red-500 flex-shrink-0", title: action.error, children: "\u2717" })
      ]
    }
  );
};

// src/components/BrowserStreamPanel.tsx
import { Fragment as Fragment33, jsx as jsx145, jsxs as jsxs128 } from "react/jsx-runtime";
var ConnectionStatus = ({ status }) => {
  const variant = {
    idle: "neutral",
    connecting: "notice",
    connected: "positive",
    disconnected: "neutral",
    error: "negative",
    recovering: "notice",
    recovered: "positive"
  }[status];
  const pulse = status === "connecting" || status === "connected" || status === "recovering";
  return /* @__PURE__ */ jsx145(StatusIndicator, { variant, size: "sm", pulse, className: "text-xs capitalize", children: status });
};
var PHASE_STALE_MS = 12e3;
function formatCurrentUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const path = u.pathname === "/" && !u.search ? "" : u.pathname + u.search;
    const out = `${u.host}${path}`;
    return out.length > 60 ? `${out.slice(0, 57)}\u2026` : out;
  } catch {
    return url.length > 60 ? `${url.slice(0, 57)}\u2026` : url;
  }
}
var QuietHint = ({ state, size = "xs" }) => {
  const url = formatCurrentUrl(state.currentUrl) ?? formatCurrentUrl(state.latestScreenshot?.url) ?? formatCurrentUrl(state.latestAction?.url) ?? null;
  const iconSize = size === "sm" ? 18 : 14;
  return /* @__PURE__ */ jsxs128("div", { className: cn("flex items-center", size === "sm" ? "gap-3" : "gap-2"), children: [
    /* @__PURE__ */ jsx145(
      motion13.span,
      {
        animate: { opacity: [0.4, 1, 0.4] },
        transition: { duration: 2, repeat: Infinity, ease: "easeInOut" },
        className: "flex-shrink-0 text-muted-foreground inline-flex",
        children: /* @__PURE__ */ jsx145(Brain4, { size: iconSize, weight: "duotone" })
      }
    ),
    /* @__PURE__ */ jsx145(Text, { size, className: "text-muted-foreground font-mono truncate", children: url ?? "\u2014" })
  ] });
};
function usePhaseFreshness(phase) {
  const [, tick] = React94.useState(0);
  const setAtRef = React94.useRef(Date.now());
  const phaseKey = phase ? `${phase.phase}:${phase.message}:${phase.progress?.current ?? ""}/${phase.progress?.total ?? ""}` : null;
  const lastKeyRef = React94.useRef(phaseKey);
  if (phaseKey !== lastKeyRef.current) {
    lastKeyRef.current = phaseKey;
    setAtRef.current = Date.now();
  }
  React94.useEffect(() => {
    if (!phase) return;
    const id = setTimeout(() => tick((n) => n + 1), PHASE_STALE_MS + 50);
    return () => clearTimeout(id);
  }, [phase, phaseKey]);
  if (!phase) return false;
  return Date.now() - setAtRef.current < PHASE_STALE_MS;
}
var ThoughtCard = ({ thought, phase, isConnected, emptyLabel, state }) => {
  const phaseFresh = usePhaseFreshness(phase);
  const activePhase = phase && phaseFresh && phase.phase !== "agent" && phase.phase !== "idle" ? phase : null;
  return /* @__PURE__ */ jsx145("div", { className: "min-h-[3rem] rounded-md border border-border/70 bg-muted/40 px-3 py-2.5", children: /* @__PURE__ */ jsx145(AnimatePresence7, { mode: "wait", children: thought ? /* @__PURE__ */ jsxs128(
    motion13.div,
    {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.2 },
      className: "flex items-start gap-2",
      children: [
        /* @__PURE__ */ jsx145(Brain4, { size: 14, className: "text-primary flex-shrink-0 mt-0.5", weight: "duotone" }),
        /* @__PURE__ */ jsx145(Text, { size: "xs", className: "leading-relaxed", children: thought })
      ]
    },
    thought.slice(0, 50)
  ) : activePhase ? /* @__PURE__ */ jsxs128(
    motion13.div,
    {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.2 },
      className: "flex items-center gap-2",
      children: [
        /* @__PURE__ */ jsx145(Lightning4, { size: 14, className: "text-muted-foreground flex-shrink-0", weight: "duotone" }),
        isConnected && /* @__PURE__ */ jsx145(Spinner, { size: "xs", className: "flex-shrink-0" }),
        /* @__PURE__ */ jsxs128(Text, { size: "xs", className: "text-muted-foreground", children: [
          activePhase.message,
          activePhase.progress && /* @__PURE__ */ jsxs128("span", { className: "ml-1 font-mono", children: [
            "(",
            activePhase.progress.current,
            " / ",
            activePhase.progress.total,
            ")"
          ] })
        ] })
      ]
    },
    `phase-${activePhase.phase}`
  ) : /* @__PURE__ */ jsx145(
    motion13.div,
    {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      className: "flex items-center gap-2",
      children: isConnected && state ? /* @__PURE__ */ jsx145(QuietHint, { state, size: "xs" }) : /* @__PURE__ */ jsx145(Text, { size: "xs", className: "text-muted-foreground italic", children: emptyLabel })
    },
    "quiet-xs"
  ) }) });
};
var BufferedScreenshotImage = ({ src, alt, className }) => {
  const [activeBuffer, setActiveBuffer] = React94.useState(0);
  const [srcs, setSrcs] = React94.useState([src, ""]);
  const pendingBuffer = activeBuffer === 0 ? 1 : 0;
  React94.useEffect(() => {
    setSrcs((prev) => {
      const next = [...prev];
      next[pendingBuffer] = src;
      return next;
    });
  }, [src]);
  const handleLoad = React94.useCallback(
    (bufferIndex) => {
      if (bufferIndex === pendingBuffer) {
        setActiveBuffer(bufferIndex);
      }
    },
    [pendingBuffer]
  );
  return /* @__PURE__ */ jsxs128(Fragment33, { children: [
    /* @__PURE__ */ jsx145(
      "img",
      {
        src: srcs[0],
        alt,
        className,
        style: {
          zIndex: activeBuffer === 0 ? 1 : 0,
          opacity: srcs[0] ? 1 : 0
        },
        onLoad: () => handleLoad(0)
      }
    ),
    /* @__PURE__ */ jsx145(
      "img",
      {
        src: srcs[1],
        alt,
        className,
        style: {
          zIndex: activeBuffer === 1 ? 1 : 0,
          opacity: srcs[1] ? 1 : 0
        },
        onLoad: () => handleLoad(1)
      }
    )
  ] });
};
var ScreenshotPanel = ({ screenshot, status, aspectRatio, emptyLabel, videoRef, videoActive, currentUrl }) => {
  const imgSrc = screenshot ? screenshot.imageBase64.startsWith("data:") ? screenshot.imageBase64 : `data:image/png;base64,${screenshot.imageBase64}` : null;
  const showVideo = videoActive && videoRef;
  return /* @__PURE__ */ jsxs128(
    "div",
    {
      className: cn(
        "relative rounded-lg border border-border bg-muted overflow-hidden",
        aspectRatio
      ),
      children: [
        showVideo ? /* @__PURE__ */ jsx145(
          "video",
          {
            ref: videoRef,
            autoPlay: true,
            muted: true,
            playsInline: true,
            className: "absolute inset-0 w-full h-full object-contain bg-black/5"
          }
        ) : imgSrc ? /* @__PURE__ */ jsx145(
          BufferedScreenshotImage,
          {
            src: imgSrc,
            alt: screenshot?.label ?? "Browser screenshot",
            className: "absolute inset-0 w-full h-full object-contain bg-black/5"
          }
        ) : /* @__PURE__ */ jsx145("div", { className: "absolute inset-0 flex items-center justify-center", children: status === "connecting" || status === "connected" ? /* @__PURE__ */ jsxs128("div", { className: "flex flex-col items-center gap-2", children: [
          /* @__PURE__ */ jsx145(Spinner, { size: "md", className: "text-muted-foreground" }),
          /* @__PURE__ */ jsx145(Text, { size: "xs", className: "text-muted-foreground", children: emptyLabel })
        ] }) : /* @__PURE__ */ jsx145(Text, { size: "xs", className: "text-muted-foreground", children: emptyLabel }) }),
        (currentUrl ?? screenshot?.url) && /* @__PURE__ */ jsx145("div", { className: "absolute top-1.5 left-1.5 right-1.5 z-10", children: /* @__PURE__ */ jsx145("div", { className: "bg-background/80 backdrop-blur-sm rounded px-1.5 py-0.5 inline-block max-w-full", children: /* @__PURE__ */ jsx145(Text, { size: "xs", className: "text-muted-foreground truncate block font-mono text-[10px]", children: currentUrl ?? screenshot?.url }) }) }),
        screenshot?.label && /* @__PURE__ */ jsx145("div", { className: "absolute bottom-1.5 left-1.5 right-1.5 z-10", children: /* @__PURE__ */ jsx145("div", { className: "bg-background/80 backdrop-blur-sm rounded px-1.5 py-0.5 inline-block max-w-full", children: /* @__PURE__ */ jsx145(Text, { size: "xs", className: "text-muted-foreground truncate block text-[10px]", children: screenshot.label }) }) })
      ]
    }
  );
};
var FullscreenOverlay2 = ({
  state,
  onClose,
  emptyScreenshotLabel,
  emptyReasoningLabel,
  maxActions,
  videoRef,
  videoActive
}) => {
  const isConnected = state.status === "connecting" || state.status === "connected";
  const phaseFresh = usePhaseFreshness(state.phase);
  React94.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);
  return /* @__PURE__ */ jsx145(
    motion13.div,
    {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.2 },
      className: "fixed inset-0 z-50 bg-background/95 backdrop-blur-sm",
      children: /* @__PURE__ */ jsxs128("div", { className: "h-full flex flex-col", children: [
        /* @__PURE__ */ jsxs128("div", { className: "flex items-center justify-between px-6 py-4 border-b border-border", children: [
          /* @__PURE__ */ jsxs128("div", { className: "flex items-center gap-4", children: [
            /* @__PURE__ */ jsx145(Heading, { level: 4, children: "Live Browser View" }),
            /* @__PURE__ */ jsx145(ConnectionStatus, { status: state.status })
          ] }),
          /* @__PURE__ */ jsx145(Button, { variant: "ghost", size: "sm", onPress: onClose, "aria-label": "Close fullscreen", children: /* @__PURE__ */ jsx145(X5, { size: 20 }) })
        ] }),
        /* @__PURE__ */ jsx145("div", { className: "flex-1 overflow-hidden p-6", children: /* @__PURE__ */ jsxs128("div", { className: "h-full grid grid-cols-1 lg:grid-cols-3 gap-6", children: [
          /* @__PURE__ */ jsx145("div", { className: "lg:col-span-2 flex flex-col gap-4", children: /* @__PURE__ */ jsxs128("div", { className: "flex-1 relative rounded-xl border border-border bg-muted overflow-hidden min-h-[300px]", children: [
            videoActive && videoRef ? /* @__PURE__ */ jsx145(
              "video",
              {
                ref: videoRef,
                autoPlay: true,
                muted: true,
                playsInline: true,
                className: "absolute inset-0 w-full h-full object-contain bg-black/5"
              }
            ) : state.latestScreenshot ? /* @__PURE__ */ jsx145(
              BufferedScreenshotImage,
              {
                src: state.latestScreenshot.imageBase64.startsWith("data:") ? state.latestScreenshot.imageBase64 : `data:image/png;base64,${state.latestScreenshot.imageBase64}`,
                alt: state.latestScreenshot.label ?? "Browser screenshot",
                className: "absolute inset-0 w-full h-full object-contain bg-black/5"
              }
            ) : /* @__PURE__ */ jsx145("div", { className: "absolute inset-0 flex items-center justify-center", children: isConnected ? /* @__PURE__ */ jsxs128("div", { className: "flex flex-col items-center gap-3", children: [
              /* @__PURE__ */ jsx145(Spinner, { size: "lg", className: "text-muted-foreground" }),
              /* @__PURE__ */ jsx145(Text, { size: "sm", className: "text-muted-foreground", children: emptyScreenshotLabel })
            ] }) : /* @__PURE__ */ jsx145(Text, { size: "sm", className: "text-muted-foreground", children: emptyScreenshotLabel }) }),
            (state.currentUrl ?? state.latestScreenshot?.url) && /* @__PURE__ */ jsx145("div", { className: "absolute top-3 left-3 right-3 z-10", children: /* @__PURE__ */ jsx145("div", { className: "bg-background/90 backdrop-blur-sm rounded-lg px-3 py-1.5 inline-block max-w-full", children: /* @__PURE__ */ jsx145(Text, { size: "sm", className: "text-muted-foreground truncate block font-mono", children: state.currentUrl ?? state.latestScreenshot?.url }) }) }),
            state.latestScreenshot?.label && /* @__PURE__ */ jsx145("div", { className: "absolute bottom-3 left-3 right-3 z-10", children: /* @__PURE__ */ jsx145("div", { className: "bg-background/90 backdrop-blur-sm rounded-lg px-3 py-1.5 inline-block max-w-full", children: /* @__PURE__ */ jsx145(Text, { size: "sm", className: "text-muted-foreground truncate block", children: state.latestScreenshot.label }) }) })
          ] }) }),
          /* @__PURE__ */ jsxs128("div", { className: "flex flex-col gap-4 overflow-hidden", children: [
            /* @__PURE__ */ jsxs128("div", { className: "space-y-2", children: [
              /* @__PURE__ */ jsx145(Text, { size: "sm", className: "font-medium", children: "Latest Action" }),
              /* @__PURE__ */ jsx145(CompactActionCard, { action: state.latestAction })
            ] }),
            state.actions.length > 0 && /* @__PURE__ */ jsxs128("div", { className: "space-y-2 flex-shrink-0", children: [
              /* @__PURE__ */ jsxs128(Text, { size: "xs", className: "text-muted-foreground", children: [
                "Recent Actions (",
                state.actions.length,
                ")"
              ] }),
              /* @__PURE__ */ jsx145("div", { className: "max-h-40 overflow-y-auto rounded-lg border border-border bg-muted/20 py-1", children: state.actions.slice(-maxActions).map((action, i) => /* @__PURE__ */ jsx145(ActionRow, { action }, `${action.timestamp}-${i}`)) })
            ] }),
            /* @__PURE__ */ jsxs128("div", { className: "space-y-2 flex-1 min-h-0", children: [
              /* @__PURE__ */ jsx145(Text, { size: "sm", className: "font-medium", children: "Agent Thinking" }),
              /* @__PURE__ */ jsx145("div", { className: "rounded-lg border border-border bg-muted/20 p-4 h-full overflow-y-auto", children: /* @__PURE__ */ jsx145(AnimatePresence7, { mode: "wait", children: state.latestThought ? /* @__PURE__ */ jsxs128(
                motion13.div,
                {
                  initial: { opacity: 0 },
                  animate: { opacity: 1 },
                  exit: { opacity: 0 },
                  transition: { duration: 0.2 },
                  className: "flex items-start gap-3",
                  children: [
                    /* @__PURE__ */ jsx145(
                      Brain4,
                      {
                        size: 18,
                        className: "text-primary flex-shrink-0 mt-0.5",
                        weight: "duotone"
                      }
                    ),
                    /* @__PURE__ */ jsx145(Text, { size: "sm", className: "leading-relaxed whitespace-pre-wrap", children: state.latestThought })
                  ]
                },
                state.latestThought.slice(0, 50)
              ) : state.phase && phaseFresh && state.phase.phase !== "agent" && state.phase.phase !== "idle" ? /* @__PURE__ */ jsxs128(
                motion13.div,
                {
                  initial: { opacity: 0 },
                  animate: { opacity: 1 },
                  exit: { opacity: 0 },
                  transition: { duration: 0.2 },
                  className: "flex items-center gap-3",
                  children: [
                    /* @__PURE__ */ jsx145(
                      Lightning4,
                      {
                        size: 18,
                        className: "text-muted-foreground flex-shrink-0",
                        weight: "duotone"
                      }
                    ),
                    isConnected && /* @__PURE__ */ jsx145(Spinner, { size: "sm", className: "flex-shrink-0" }),
                    /* @__PURE__ */ jsxs128(Text, { size: "sm", className: "text-muted-foreground", children: [
                      state.phase.message,
                      state.phase.progress && /* @__PURE__ */ jsxs128("span", { className: "ml-1 font-mono", children: [
                        "(",
                        state.phase.progress.current,
                        " / ",
                        state.phase.progress.total,
                        ")"
                      ] })
                    ] })
                  ]
                },
                `phase-${state.phase.phase}`
              ) : /* @__PURE__ */ jsx145(
                motion13.div,
                {
                  initial: { opacity: 0 },
                  animate: { opacity: 1 },
                  className: "flex items-center gap-3",
                  children: isConnected ? /* @__PURE__ */ jsx145(QuietHint, { state, size: "sm" }) : /* @__PURE__ */ jsx145(Text, { size: "sm", className: "text-muted-foreground italic", children: emptyReasoningLabel })
                },
                "quiet-sm"
              ) }) })
            ] })
          ] })
        ] }) })
      ] })
    }
  );
};
var BrowserStreamPanel = ({
  state,
  className,
  layout = "stacked",
  screenshotAspectRatio = "aspect-video",
  maxActions = 10,
  showReasoningPanel = true,
  showActionHistory = true,
  emptyScreenshotLabel = "Waiting for screenshot...",
  emptyReasoningLabel = "Waiting for agent...",
  allowExpand = true,
  showHeader = true,
  videoRef,
  videoActive
}) => {
  const [isFullscreen, setIsFullscreen] = React94.useState(false);
  const isConnected = state.status === "connecting" || state.status === "connected";
  if (layout === "split") {
    return /* @__PURE__ */ jsxs128(Fragment33, { children: [
      /* @__PURE__ */ jsx145(AnimatePresence7, { children: isFullscreen && /* @__PURE__ */ jsx145(
        FullscreenOverlay2,
        {
          state,
          onClose: () => setIsFullscreen(false),
          emptyScreenshotLabel,
          emptyReasoningLabel,
          maxActions,
          videoRef,
          videoActive
        }
      ) }),
      /* @__PURE__ */ jsxs128(
        "div",
        {
          "data-browser-stream-panel": true,
          className: cn("grid grid-cols-1 gap-4 lg:grid-cols-2", className),
          children: [
            /* @__PURE__ */ jsxs128("div", { className: "space-y-2", children: [
              showHeader && /* @__PURE__ */ jsxs128("div", { className: "flex items-center justify-between", children: [
                /* @__PURE__ */ jsx145(Text, { size: "sm", className: "font-medium", children: "Live View" }),
                /* @__PURE__ */ jsxs128("div", { className: "flex items-center gap-2", children: [
                  /* @__PURE__ */ jsx145(ConnectionStatus, { status: state.status }),
                  allowExpand && /* @__PURE__ */ jsx145(
                    "button",
                    {
                      onClick: () => setIsFullscreen(true),
                      className: "inline-flex min-h-10 min-w-10 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                      "aria-label": "Expand to fullscreen",
                      children: /* @__PURE__ */ jsx145(ArrowsOut3, { size: 16 })
                    }
                  )
                ] })
              ] }),
              /* @__PURE__ */ jsx145(
                ScreenshotPanel,
                {
                  screenshot: state.latestScreenshot,
                  status: state.status,
                  aspectRatio: screenshotAspectRatio,
                  emptyLabel: emptyScreenshotLabel,
                  videoRef,
                  videoActive,
                  currentUrl: state.currentUrl
                }
              )
            ] }),
            /* @__PURE__ */ jsxs128("div", { className: "space-y-3", children: [
              /* @__PURE__ */ jsxs128("div", { className: "space-y-1.5", children: [
                /* @__PURE__ */ jsx145(Text, { size: "xs", className: "font-medium text-muted-foreground", children: "Latest Action" }),
                /* @__PURE__ */ jsx145(CompactActionCard, { action: state.latestAction })
              ] }),
              showActionHistory && state.actions.length > 0 && /* @__PURE__ */ jsxs128("div", { className: "space-y-1", children: [
                /* @__PURE__ */ jsxs128(Text, { size: "xs", className: "text-muted-foreground", children: [
                  "Recent (",
                  state.actions.length,
                  ")"
                ] }),
                /* @__PURE__ */ jsx145("div", { className: "max-h-28 overflow-y-auto rounded-md border border-border/70 bg-muted/20", children: state.actions.slice(-maxActions).map((action, i) => /* @__PURE__ */ jsx145(ActionRow, { action }, `${action.timestamp}-${i}`)) })
              ] }),
              showReasoningPanel && /* @__PURE__ */ jsxs128("div", { className: "space-y-1.5", children: [
                /* @__PURE__ */ jsx145(Text, { size: "xs", className: "font-medium text-muted-foreground", children: "Agent Thinking" }),
                /* @__PURE__ */ jsx145(
                  ThoughtCard,
                  {
                    thought: state.latestThought,
                    phase: state.phase,
                    isConnected,
                    emptyLabel: emptyReasoningLabel,
                    state
                  }
                )
              ] })
            ] })
          ]
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsxs128(Fragment33, { children: [
    /* @__PURE__ */ jsx145(AnimatePresence7, { children: isFullscreen && /* @__PURE__ */ jsx145(
      FullscreenOverlay2,
      {
        state,
        onClose: () => setIsFullscreen(false),
        emptyScreenshotLabel,
        emptyReasoningLabel,
        maxActions
      }
    ) }),
    /* @__PURE__ */ jsxs128("div", { "data-browser-stream-panel": true, className: cn("space-y-3", className), children: [
      showHeader && /* @__PURE__ */ jsxs128("div", { className: "flex items-center justify-between", children: [
        /* @__PURE__ */ jsx145(Text, { size: "sm", className: "font-medium", children: "Live View" }),
        /* @__PURE__ */ jsxs128("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx145(ConnectionStatus, { status: state.status }),
          allowExpand && /* @__PURE__ */ jsx145(
            "button",
            {
              onClick: () => setIsFullscreen(true),
              className: "inline-flex min-h-10 min-w-10 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
              "aria-label": "Expand to fullscreen",
              children: /* @__PURE__ */ jsx145(ArrowsOut3, { size: 16 })
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsx145(
        ScreenshotPanel,
        {
          screenshot: state.latestScreenshot,
          status: state.status,
          aspectRatio: screenshotAspectRatio,
          emptyLabel: emptyScreenshotLabel,
          currentUrl: state.currentUrl
        }
      ),
      /* @__PURE__ */ jsx145(CompactActionCard, { action: state.latestAction }),
      showActionHistory && state.actions.length > 0 && /* @__PURE__ */ jsxs128("div", { className: "space-y-1", children: [
        /* @__PURE__ */ jsxs128(Text, { size: "xs", className: "text-muted-foreground", children: [
          "Recent actions (",
          state.actions.length,
          ")"
        ] }),
        /* @__PURE__ */ jsx145("div", { className: "max-h-28 overflow-y-auto rounded-md border border-border/70 bg-muted/20", children: state.actions.slice(-maxActions).map((action, i) => /* @__PURE__ */ jsx145(ActionRow, { action }, `${action.timestamp}-${i}`)) })
      ] }),
      showReasoningPanel && /* @__PURE__ */ jsxs128("div", { className: "space-y-1", children: [
        /* @__PURE__ */ jsx145(Text, { size: "xs", className: "font-medium text-muted-foreground", children: "Agent Thinking" }),
        /* @__PURE__ */ jsx145(
          ThoughtCard,
          {
            thought: state.latestThought,
            phase: state.phase,
            isConnected,
            emptyLabel: emptyReasoningLabel
          }
        )
      ] })
    ] })
  ] });
};

// src/components/DocsContext.tsx
import {
  createContext as createContext2,
  useContext as useContext3,
  useCallback as useCallback8,
  useMemo as useMemo6,
  useState as useState9,
  useEffect as useEffect6,
  useRef as useRef5
} from "react";
import { jsx as jsx146, jsxs as jsxs129 } from "react/jsx-runtime";
var DocsContextStack = createContext2([]);
var DocsContextMenuContext = createContext2(null);
var DocsConfigContext = createContext2({
  docsBaseUrl: "https://docs.runcofactor.com"
});
var ContextMenuPluginContext = createContext2({
  plugins: [],
  registerPlugin: () => {
  },
  unregisterPlugin: () => {
  }
});
function useNearestDoc() {
  const stack = useContext3(DocsContextStack);
  return stack.length > 0 ? stack[stack.length - 1] : null;
}
function useDocsStack() {
  return useContext3(DocsContextStack);
}
function useDocsContextMenu() {
  return useContext3(DocsContextMenuContext);
}
function useDocsConfig() {
  return useContext3(DocsConfigContext);
}
function useContextMenuPlugin(plugin) {
  const { registerPlugin, unregisterPlugin } = useContext3(ContextMenuPluginContext);
  const pluginRef = useRef5(plugin);
  useEffect6(() => {
    pluginRef.current = plugin;
    registerPlugin(plugin);
    return () => unregisterPlugin(plugin.id);
  }, [plugin, registerPlugin, unregisterPlugin]);
}
function DocsContext({ slug, title, path, children }) {
  const parentStack = useContext3(DocsContextStack);
  const stack = useMemo6(
    () => [...parentStack, { slug, title, path: path ?? `/docs/${slug}` }],
    [parentStack, slug, title, path]
  );
  return /* @__PURE__ */ jsx146(DocsContextStack.Provider, { value: stack, children });
}
function DocsContextProvider({
  children,
  docsBaseUrl = "https://docs.runcofactor.com",
  onOpenChat,
  showMenuWithoutDocs,
  renderMenu
}) {
  const [menuState, setMenuState] = useState9({
    isOpen: false,
    position: null,
    docInfo: null,
    originalEvent: null
  });
  const [plugins, setPlugins] = useState9([]);
  const registerPlugin = useCallback8((plugin) => {
    setPlugins((prev) => {
      const filtered = prev.filter((p) => p.id !== plugin.id);
      return [...filtered, plugin];
    });
  }, []);
  const unregisterPlugin = useCallback8((id) => {
    setPlugins((prev) => prev.filter((p) => p.id !== id));
  }, []);
  const pluginRegistry = useMemo6(
    () => ({ plugins, registerPlugin, unregisterPlugin }),
    [plugins, registerPlugin, unregisterPlugin]
  );
  const targetElementRef = useRef5(null);
  const handledByAreaRef = useRef5(false);
  const openMenu = useCallback8(
    (position, docInfo, event) => {
      handledByAreaRef.current = true;
      targetElementRef.current = event.target;
      setMenuState({
        isOpen: true,
        position,
        docInfo,
        originalEvent: event
      });
    },
    []
  );
  useEffect6(() => {
    if (!showMenuWithoutDocs) return;
    const handler = (event) => {
      if (event._bypassDocsMenu) return;
      if (event.metaKey) return;
      if (handledByAreaRef.current) {
        handledByAreaRef.current = false;
        return;
      }
      event.preventDefault();
      openMenu({ x: event.clientX, y: event.clientY }, null, event);
    };
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, [showMenuWithoutDocs, openMenu]);
  const closeMenu = useCallback8(() => {
    setMenuState({
      isOpen: false,
      position: null,
      docInfo: null,
      originalEvent: null
    });
  }, []);
  const openDocs = useCallback8(() => {
    if (menuState.docInfo) {
      const url = `${docsBaseUrl}${menuState.docInfo.path}`;
      window.open(url, "_blank", "noopener,noreferrer");
    }
    closeMenu();
  }, [menuState.docInfo, docsBaseUrl, closeMenu]);
  const openChat = useCallback8(() => {
    if (menuState.docInfo && onOpenChat) {
      onOpenChat(menuState.docInfo);
    }
    closeMenu();
  }, [menuState.docInfo, onOpenChat, closeMenu]);
  const showStandardMenu = useCallback8(() => {
    const { originalEvent } = menuState;
    const target = targetElementRef.current;
    closeMenu();
    if (target && originalEvent) {
      requestAnimationFrame(() => {
        const syntheticEvent = new MouseEvent("contextmenu", {
          bubbles: true,
          cancelable: true,
          clientX: originalEvent.clientX,
          clientY: originalEvent.clientY,
          screenX: originalEvent.screenX,
          screenY: originalEvent.screenY,
          button: 2,
          buttons: 2
        });
        syntheticEvent._bypassDocsMenu = true;
        target.dispatchEvent(syntheticEvent);
      });
    }
  }, [menuState, closeMenu]);
  const contextMenuAPI = useMemo6(
    () => ({
      menuState,
      openMenu,
      closeMenu,
      openChat,
      openDocs,
      showStandardMenu
    }),
    [menuState, openMenu, closeMenu, openChat, openDocs, showStandardMenu]
  );
  const config = useMemo6(() => ({ docsBaseUrl }), [docsBaseUrl]);
  return /* @__PURE__ */ jsx146(DocsConfigContext.Provider, { value: config, children: /* @__PURE__ */ jsx146(ContextMenuPluginContext.Provider, { value: pluginRegistry, children: /* @__PURE__ */ jsxs129(DocsContextMenuContext.Provider, { value: contextMenuAPI, children: [
    children,
    renderMenu?.({
      menuState,
      onClose: closeMenu,
      onOpenDocs: openDocs,
      onOpenChat: openChat,
      onShowStandardMenu: showStandardMenu,
      plugins
    })
  ] }) }) });
}
function DocsContextArea({ children, className }) {
  const nearestDoc = useNearestDoc();
  const menuAPI = useDocsContextMenu();
  const handleContextMenu = useCallback8(
    (event) => {
      if (event.nativeEvent._bypassDocsMenu) {
        return;
      }
      if (event.metaKey) {
        return;
      }
      if (!nearestDoc || !menuAPI) {
        return;
      }
      event.preventDefault();
      menuAPI.openMenu(
        { x: event.clientX, y: event.clientY },
        nearestDoc,
        event.nativeEvent
      );
    },
    [nearestDoc, menuAPI]
  );
  return /* @__PURE__ */ jsx146("div", { onContextMenu: handleContextMenu, className: cn("contents", className), children });
}
function DocsAnnotation({ slug, title, path, children, className }) {
  return /* @__PURE__ */ jsx146(DocsContext, { slug, title, path, children: /* @__PURE__ */ jsx146(DocsContextArea, { className, children }) });
}

// src/components/DocsContextMenu.tsx
import React96, { useEffect as useEffect7, useRef as useRef6, useCallback as useCallback9 } from "react";
import { createPortal as createPortal6 } from "react-dom";
import { BookOpen, ChatCircle as ChatCircle4 } from "@phosphor-icons/react";
import { Fragment as Fragment34, jsx as jsx147, jsxs as jsxs130 } from "react/jsx-runtime";
function calculateMenuPosition(position, menuRect) {
  const padding = 8;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  let left = position.x;
  let top = position.y;
  if (left + menuRect.width + padding > viewportWidth) {
    left = Math.max(padding, position.x - menuRect.width);
  }
  if (top + menuRect.height + padding > viewportHeight) {
    top = Math.max(padding, position.y - menuRect.height);
  }
  left = Math.max(padding, left);
  top = Math.max(padding, top);
  return { top, left };
}
function DocsContextMenu({
  isOpen,
  position,
  docInfo,
  onClose,
  onOpenDocs,
  onOpenChat,
  onShowStandardMenu: _onShowStandardMenu,
  plugins
}) {
  const menuRef = useRef6(null);
  const [menuPosition, setMenuPosition] = React96.useState(
    null
  );
  useEffect7(() => {
    if (isOpen && position && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const calculatedPosition = calculateMenuPosition(position, rect);
      setMenuPosition(calculatedPosition);
    } else {
      setMenuPosition(null);
    }
  }, [isOpen, position]);
  useEffect7(() => {
    if (!isOpen) return;
    const handleKeyDown2 = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown2);
    return () => document.removeEventListener("keydown", handleKeyDown2);
  }, [isOpen, onClose]);
  useEffect7(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);
  useEffect7(() => {
    if (isOpen && menuRef.current) {
      const firstItem = menuRef.current.querySelector('[role="menuitem"]');
      firstItem?.focus();
    }
  }, [isOpen, menuPosition]);
  const handleKeyDown = useCallback9((event) => {
    const items = menuRef.current?.querySelectorAll('[role="menuitem"]');
    if (!items?.length) return;
    const currentIndex = Array.from(items).findIndex((item) => item === document.activeElement);
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        items[(currentIndex + 1) % items.length]?.focus();
        break;
      case "ArrowUp":
        event.preventDefault();
        items[(currentIndex - 1 + items.length) % items.length]?.focus();
        break;
      case "Home":
        event.preventDefault();
        items[0]?.focus();
        break;
      case "End":
        event.preventDefault();
        items[items.length - 1]?.focus();
        break;
    }
  }, []);
  if (!isOpen || !position) {
    return null;
  }
  const hasDocContext = docInfo !== null;
  const menuContent = /* @__PURE__ */ jsxs130(
    "div",
    {
      ref: menuRef,
      role: "menu",
      "aria-label": "Context menu",
      onKeyDown: handleKeyDown,
      "data-react-aria-top-layer": true,
      className: cn(
        "fixed z-[60] min-w-[200px] max-w-[280px] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg",
        "animate-in fade-in-0 zoom-in-95 duration-100",
        // Position off-screen initially until we calculate proper position
        menuPosition ? "" : "invisible"
      ),
      style: menuPosition ? { top: menuPosition.top, left: menuPosition.left } : { top: position.y, left: position.x },
      children: [
        hasDocContext ? /* @__PURE__ */ jsxs130(Fragment34, { children: [
          /* @__PURE__ */ jsx147(MenuItem2, { icon: /* @__PURE__ */ jsx147(BookOpen, { size: 16 }), onClick: onOpenDocs, autoFocus: true, children: /* @__PURE__ */ jsx147("span", { className: "truncate", children: docInfo.title }) }),
          /* @__PURE__ */ jsx147(MenuSeparator2, {}),
          /* @__PURE__ */ jsx147(MenuItem2, { icon: /* @__PURE__ */ jsx147(ChatCircle4, { size: 16 }), onClick: onOpenChat, children: "Ask about this..." })
        ] }) : /* @__PURE__ */ jsx147("div", { className: "px-2 py-1.5 text-xs text-muted-foreground italic", children: "No docs annotation for this area" }),
        plugins && plugins.length > 0 && /* @__PURE__ */ jsxs130(Fragment34, { children: [
          /* @__PURE__ */ jsx147(MenuSeparator2, {}),
          plugins.map((plugin) => /* @__PURE__ */ jsx147(
            MenuItem2,
            {
              icon: plugin.icon,
              onClick: () => {
                plugin.onAction();
                onClose();
              },
              children: plugin.label
            },
            plugin.id
          ))
        ] }),
        /* @__PURE__ */ jsx147(MenuSeparator2, {}),
        /* @__PURE__ */ jsxs130("div", { className: "px-2 py-1.5 text-xs text-muted-foreground", children: [
          /* @__PURE__ */ jsx147("kbd", { className: "rounded bg-muted px-1 py-0.5 font-mono text-[10px]", children: "Cmd" }),
          " + right-click for standard menu"
        ] })
      ]
    }
  );
  return createPortal6(menuContent, document.body);
}
function MenuItem2({ children, icon, onClick, autoFocus, muted }) {
  return /* @__PURE__ */ jsxs130(
    "button",
    {
      role: "menuitem",
      onClick,
      autoFocus,
      className: cn(
        "flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors",
        "hover:bg-accent hover:text-accent-foreground",
        "focus:bg-accent focus:text-accent-foreground",
        muted && "text-muted-foreground"
      ),
      children: [
        icon && /* @__PURE__ */ jsx147("span", { className: "flex-shrink-0", children: icon }),
        /* @__PURE__ */ jsx147("span", { className: "flex-1 text-left", children })
      ]
    }
  );
}
function MenuSeparator2() {
  return /* @__PURE__ */ jsx147("div", { className: "-mx-1 my-1 h-px bg-muted", role: "separator" });
}
function DocsContextMenuWrapper({
  menuState,
  onClose,
  onOpenDocs,
  onOpenChat,
  onShowStandardMenu,
  plugins
}) {
  return /* @__PURE__ */ jsx147(
    DocsContextMenu,
    {
      isOpen: menuState.isOpen,
      position: menuState.position,
      docInfo: menuState.docInfo,
      onClose,
      onOpenDocs,
      onOpenChat,
      onShowStandardMenu,
      plugins
    }
  );
}

// src/components/FloatingChatPanel.tsx
import { createPortal as createPortal7 } from "react-dom";
import { ArrowsOut as ArrowsOut4, X as X6 } from "@phosphor-icons/react";
import { jsx as jsx148, jsxs as jsxs131 } from "react/jsx-runtime";
function FloatingChatPanel({
  isOpen,
  title,
  subtitle,
  badge,
  onClose,
  onExpand,
  headerActions,
  children,
  width = 400,
  className
}) {
  if (!isOpen) {
    return null;
  }
  const panel = /* @__PURE__ */ jsxs131(
    "div",
    {
      className: cn(
        "fixed right-0 top-0 bottom-0 z-40",
        "flex flex-col",
        "bg-background border-l border-border shadow-xl",
        "animate-in slide-in-from-right duration-200",
        className
      ),
      style: { width: `${width}px` },
      children: [
        /* @__PURE__ */ jsxs131("div", { className: "flex items-center justify-between border-b px-4 h-16 shrink-0", children: [
          /* @__PURE__ */ jsxs131("div", { className: "flex flex-col gap-1 min-w-0", children: [
            /* @__PURE__ */ jsx148("span", { className: "text-sm font-medium text-foreground", children: title }),
            badge ? badge : subtitle && /* @__PURE__ */ jsx148("span", { className: "text-xs text-muted-foreground truncate", children: subtitle })
          ] }),
          /* @__PURE__ */ jsxs131("div", { className: "flex items-center gap-1", children: [
            headerActions,
            onExpand && /* @__PURE__ */ jsxs131(TooltipTrigger, { children: [
              /* @__PURE__ */ jsx148(
                "button",
                {
                  onClick: onExpand,
                  className: "p-1 text-muted-foreground hover:text-foreground transition-colors",
                  "aria-label": "Expand to full view",
                  children: /* @__PURE__ */ jsx148(ArrowsOut4, { size: 16 })
                }
              ),
              /* @__PURE__ */ jsx148(Tooltip, { children: "Expand" })
            ] }),
            /* @__PURE__ */ jsxs131(TooltipTrigger, { children: [
              /* @__PURE__ */ jsx148(
                "button",
                {
                  onClick: onClose,
                  className: "p-1 text-muted-foreground hover:text-foreground transition-colors",
                  "aria-label": "Close",
                  children: /* @__PURE__ */ jsx148(X6, { size: 16 })
                }
              ),
              /* @__PURE__ */ jsx148(Tooltip, { children: "Close" })
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsx148("div", { className: "flex-1 flex flex-col overflow-hidden", children })
      ]
    }
  );
  return createPortal7(panel, document.body);
}

// src/components/WaitCountdown.tsx
import { motion as motion14, AnimatePresence as AnimatePresence8 } from "framer-motion";
import { Hourglass as Hourglass3, ArrowClockwise as ArrowClockwise2, CheckCircle as CheckCircle14 } from "@phosphor-icons/react";
import { jsx as jsx149, jsxs as jsxs132 } from "react/jsx-runtime";
var sizeConfig4 = {
  sm: { ring: 48, stroke: 3, icon: 16, text: "xs" },
  md: { ring: 80, stroke: 4, icon: 24, text: "sm" },
  lg: { ring: 120, stroke: 5, icon: 32, text: "md" }
};
function WaitCountdown({
  formatted,
  progress,
  isComplete,
  isResuming,
  size = "md",
  className
}) {
  const config = sizeConfig4[size];
  const radius = (config.ring - config.stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress);
  return /* @__PURE__ */ jsxs132("div", { className: cn("flex flex-col items-center gap-3", className), children: [
    /* @__PURE__ */ jsxs132("div", { className: "relative", style: { width: config.ring, height: config.ring }, children: [
      /* @__PURE__ */ jsxs132(
        "svg",
        {
          className: "absolute inset-0 -rotate-90",
          viewBox: `0 0 ${config.ring} ${config.ring}`,
          "aria-hidden": "true",
          children: [
            /* @__PURE__ */ jsx149(
              "circle",
              {
                cx: config.ring / 2,
                cy: config.ring / 2,
                r: radius,
                fill: "none",
                stroke: "currentColor",
                strokeWidth: config.stroke,
                className: "text-gray-200 dark:text-gray-700"
              }
            ),
            /* @__PURE__ */ jsx149(
              motion14.circle,
              {
                cx: config.ring / 2,
                cy: config.ring / 2,
                r: radius,
                fill: "none",
                stroke: "currentColor",
                strokeWidth: config.stroke,
                strokeLinecap: "round",
                className: cn(isComplete ? "text-emerald-500" : "text-amber-500"),
                initial: { strokeDashoffset: circumference },
                animate: { strokeDashoffset },
                transition: { duration: 0.5, ease: "easeOut" },
                style: { strokeDasharray: circumference }
              }
            )
          ]
        }
      ),
      /* @__PURE__ */ jsx149("div", { className: "absolute inset-0 flex items-center justify-center", children: /* @__PURE__ */ jsx149(AnimatePresence8, { mode: "wait", children: isResuming ? /* @__PURE__ */ jsx149(
        motion14.div,
        {
          initial: { scale: 0.8, opacity: 0 },
          animate: { scale: 1, opacity: 1 },
          exit: { scale: 0.8, opacity: 0 },
          children: /* @__PURE__ */ jsx149(
            motion14.div,
            {
              animate: { rotate: 360 },
              transition: { duration: 1, repeat: Infinity, ease: "linear" },
              children: /* @__PURE__ */ jsx149(ArrowClockwise2, { size: config.icon, weight: "bold", className: "text-emerald-500" })
            }
          )
        },
        "resuming"
      ) : isComplete ? /* @__PURE__ */ jsx149(
        motion14.div,
        {
          initial: { scale: 0.8, opacity: 0 },
          animate: { scale: 1, opacity: 1 },
          children: /* @__PURE__ */ jsx149(CheckCircle14, { size: config.icon, weight: "fill", className: "text-emerald-500" })
        },
        "complete"
      ) : /* @__PURE__ */ jsx149(
        motion14.div,
        {
          animate: { scale: [1, 1.1, 1] },
          transition: { duration: 2, repeat: Infinity },
          children: /* @__PURE__ */ jsx149(Hourglass3, { size: config.icon, weight: "duotone", className: "text-amber-500" })
        },
        "waiting"
      ) }) })
    ] }),
    /* @__PURE__ */ jsx149("div", { className: "text-center", role: "timer", "aria-live": "polite", children: /* @__PURE__ */ jsx149(AnimatePresence8, { mode: "wait", children: isResuming ? /* @__PURE__ */ jsx149(
      motion14.div,
      {
        initial: { opacity: 0, y: 5 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -5 },
        children: /* @__PURE__ */ jsx149(
          Text,
          {
            size: config.text,
            weight: "medium",
            className: "text-emerald-600 dark:text-emerald-400",
            children: "Resuming..."
          }
        )
      },
      "resuming-text"
    ) : isComplete ? /* @__PURE__ */ jsx149(
      motion14.div,
      {
        initial: { opacity: 0, y: 5 },
        animate: { opacity: 1, y: 0 },
        children: /* @__PURE__ */ jsx149(
          Text,
          {
            size: config.text,
            weight: "medium",
            className: "text-emerald-600 dark:text-emerald-400",
            children: "Ready to continue"
          }
        )
      },
      "complete-text"
    ) : /* @__PURE__ */ jsxs132(
      motion14.div,
      {
        initial: { opacity: 0, y: 5 },
        animate: { opacity: 1, y: 0 },
        children: [
          /* @__PURE__ */ jsx149(
            Text,
            {
              size: config.text,
              weight: "semibold",
              className: "text-amber-700 dark:text-amber-300 tabular-nums",
              children: formatted
            }
          ),
          /* @__PURE__ */ jsx149(Text, { size: "xs", color: "muted", className: "mt-0.5", children: "remaining" })
        ]
      },
      "countdown-text"
    ) }) })
  ] });
}

// src/components/markdown-editor/MarkdownEditor.tsx
import React97, { useCallback as useCallback10, useRef as useRef7 } from "react";
import { useEditor as useEditor2, EditorContent as EditorContent2 } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image3 from "@tiptap/extension-image";
import { Markdown as TiptapMarkdown } from "tiptap-markdown";

// src/components/markdown-editor/EditorToolbar.tsx
import {
  TextB,
  TextItalic,
  TextStrikethrough,
  Code as Code5,
  CodeBlock,
  ListBullets as ListBullets3,
  ListNumbers,
  Quotes,
  LinkSimple as LinkSimple2,
  Image as Image2,
  Minus as Minus4,
  TextHOne,
  TextHTwo,
  TextHThree
} from "@phosphor-icons/react";
import { jsx as jsx150, jsxs as jsxs133 } from "react/jsx-runtime";
function ToolbarButton({ onPress, isActive, title, children }) {
  return /* @__PURE__ */ jsx150(
    "button",
    {
      type: "button",
      className: cn(
        "inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
        isActive && "bg-muted text-foreground"
      ),
      onMouseDown: (e) => {
        e.preventDefault();
        onPress();
      },
      title,
      children
    }
  );
}
function ToolbarDivider() {
  return /* @__PURE__ */ jsx150("div", { className: "mx-1 h-5 w-px bg-border" });
}
function EditorToolbar({ editor, onInsertImage, onInsertLink }) {
  const iconSize = 18;
  return /* @__PURE__ */ jsxs133("div", { className: "flex items-center gap-0.5 border-b border-border px-3 py-1.5 bg-muted/30 flex-wrap", children: [
    /* @__PURE__ */ jsx150(
      ToolbarButton,
      {
        onPress: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
        isActive: editor.isActive("heading", { level: 1 }),
        title: "Heading 1",
        children: /* @__PURE__ */ jsx150(TextHOne, { size: iconSize })
      }
    ),
    /* @__PURE__ */ jsx150(
      ToolbarButton,
      {
        onPress: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
        isActive: editor.isActive("heading", { level: 2 }),
        title: "Heading 2",
        children: /* @__PURE__ */ jsx150(TextHTwo, { size: iconSize })
      }
    ),
    /* @__PURE__ */ jsx150(
      ToolbarButton,
      {
        onPress: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
        isActive: editor.isActive("heading", { level: 3 }),
        title: "Heading 3",
        children: /* @__PURE__ */ jsx150(TextHThree, { size: iconSize })
      }
    ),
    /* @__PURE__ */ jsx150(ToolbarDivider, {}),
    /* @__PURE__ */ jsx150(
      ToolbarButton,
      {
        onPress: () => editor.chain().focus().toggleBold().run(),
        isActive: editor.isActive("bold"),
        title: "Bold",
        children: /* @__PURE__ */ jsx150(TextB, { size: iconSize })
      }
    ),
    /* @__PURE__ */ jsx150(
      ToolbarButton,
      {
        onPress: () => editor.chain().focus().toggleItalic().run(),
        isActive: editor.isActive("italic"),
        title: "Italic",
        children: /* @__PURE__ */ jsx150(TextItalic, { size: iconSize })
      }
    ),
    /* @__PURE__ */ jsx150(
      ToolbarButton,
      {
        onPress: () => editor.chain().focus().toggleStrike().run(),
        isActive: editor.isActive("strike"),
        title: "Strikethrough",
        children: /* @__PURE__ */ jsx150(TextStrikethrough, { size: iconSize })
      }
    ),
    /* @__PURE__ */ jsx150(
      ToolbarButton,
      {
        onPress: () => editor.chain().focus().toggleCode().run(),
        isActive: editor.isActive("code"),
        title: "Inline code",
        children: /* @__PURE__ */ jsx150(Code5, { size: iconSize })
      }
    ),
    /* @__PURE__ */ jsx150(ToolbarDivider, {}),
    /* @__PURE__ */ jsx150(
      ToolbarButton,
      {
        onPress: () => editor.chain().focus().toggleBulletList().run(),
        isActive: editor.isActive("bulletList"),
        title: "Bullet list",
        children: /* @__PURE__ */ jsx150(ListBullets3, { size: iconSize })
      }
    ),
    /* @__PURE__ */ jsx150(
      ToolbarButton,
      {
        onPress: () => editor.chain().focus().toggleOrderedList().run(),
        isActive: editor.isActive("orderedList"),
        title: "Numbered list",
        children: /* @__PURE__ */ jsx150(ListNumbers, { size: iconSize })
      }
    ),
    /* @__PURE__ */ jsx150(
      ToolbarButton,
      {
        onPress: () => editor.chain().focus().toggleBlockquote().run(),
        isActive: editor.isActive("blockquote"),
        title: "Blockquote",
        children: /* @__PURE__ */ jsx150(Quotes, { size: iconSize })
      }
    ),
    /* @__PURE__ */ jsx150(
      ToolbarButton,
      {
        onPress: () => editor.chain().focus().toggleCodeBlock().run(),
        isActive: editor.isActive("codeBlock"),
        title: "Code block",
        children: /* @__PURE__ */ jsx150(CodeBlock, { size: iconSize })
      }
    ),
    /* @__PURE__ */ jsx150(ToolbarDivider, {}),
    /* @__PURE__ */ jsx150(
      ToolbarButton,
      {
        onPress: () => {
          if (onInsertLink) {
            onInsertLink();
          } else {
            const url = window.prompt("Link URL");
            if (url) {
              editor.chain().focus().setLink({ href: url }).run();
            }
          }
        },
        isActive: editor.isActive("link"),
        title: "Insert link",
        children: /* @__PURE__ */ jsx150(LinkSimple2, { size: iconSize })
      }
    ),
    /* @__PURE__ */ jsx150(
      ToolbarButton,
      {
        onPress: () => {
          if (onInsertImage) {
            onInsertImage();
          } else {
            const url = window.prompt("Image URL");
            if (url) {
              editor.chain().focus().setImage({ src: url }).run();
            }
          }
        },
        title: "Insert image",
        children: /* @__PURE__ */ jsx150(Image2, { size: iconSize })
      }
    ),
    /* @__PURE__ */ jsx150(
      ToolbarButton,
      {
        onPress: () => editor.chain().focus().setHorizontalRule().run(),
        title: "Horizontal rule",
        children: /* @__PURE__ */ jsx150(Minus4, { size: iconSize })
      }
    )
  ] });
}

// src/components/markdown-editor/MarkdownEditor.tsx
import { jsx as jsx151, jsxs as jsxs134 } from "react/jsx-runtime";
function MarkdownEditor({
  value,
  onChange,
  onImageDrop,
  placeholder = "Start writing...",
  className,
  readOnly = false
}) {
  const onChangeRef = useRef7(onChange);
  onChangeRef.current = onChange;
  const onImageDropRef = useRef7(onImageDrop);
  onImageDropRef.current = onImageDrop;
  const isPropUpdate = useRef7(false);
  const editor = useEditor2({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
        codeBlock: { HTMLAttributes: { class: "code-block" } }
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: "editor-link" }
      }),
      Image3.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: { class: "editor-image" }
      }),
      TiptapMarkdown.configure({
        html: true,
        tightLists: true,
        bulletListMarker: "-",
        transformPastedText: true,
        transformCopiedText: true
      })
    ],
    content: value,
    editable: !readOnly,
    editorProps: {
      attributes: {
        class: "prose prose-invert max-w-none outline-none min-h-full px-8 py-6",
        "data-placeholder": placeholder
      },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (!files?.length || !onImageDropRef.current) return false;
        const imageFiles = Array.from(files).filter((f) => f.type.startsWith("image/"));
        if (imageFiles.length === 0) return false;
        event.preventDefault();
        const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
        for (const file of imageFiles) {
          void onImageDropRef.current(file).then((url) => {
            if (url && !view.isDestroyed) {
              const node = view.state.schema.nodes.image.create({ src: url, alt: file.name });
              const insertPos = pos ?? view.state.selection.anchor;
              const tr = view.state.tr.insert(insertPos, node);
              view.dispatch(tr);
            }
          });
        }
        return true;
      },
      handlePaste: (view, event) => {
        const files = event.clipboardData?.files;
        if (!files?.length || !onImageDropRef.current) return false;
        const imageFiles = Array.from(files).filter((f) => f.type.startsWith("image/"));
        if (imageFiles.length === 0) return false;
        event.preventDefault();
        for (const file of imageFiles) {
          void onImageDropRef.current(file).then((url) => {
            if (url && !view.isDestroyed) {
              const node = view.state.schema.nodes.image.create({ src: url, alt: file.name });
              const tr = view.state.tr.replaceSelectionWith(node);
              view.dispatch(tr);
            }
          });
        }
        return true;
      }
    },
    onUpdate: ({ editor: ed }) => {
      if (!isPropUpdate.current) {
        const storage = ed.storage;
        const mdStorage = storage.markdown;
        const md = mdStorage?.getMarkdown() ?? "";
        onChangeRef.current(md);
      }
    }
  });
  React97.useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const storage = editor.storage;
    const mdStorage = storage.markdown;
    const currentMd = mdStorage?.getMarkdown() ?? "";
    if (currentMd !== value) {
      isPropUpdate.current = true;
      editor.commands.setContent(value);
      isPropUpdate.current = false;
    }
  }, [value, editor]);
  React97.useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.setEditable(!readOnly);
  }, [readOnly, editor]);
  const handleInsertImage = useCallback10(() => {
    if (!editor) return;
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    fileInput.onchange = async () => {
      const file = fileInput.files?.[0];
      if (!file || !onImageDropRef.current) return;
      const url = await onImageDropRef.current(file);
      if (url) {
        editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      }
    };
    fileInput.click();
  }, [editor]);
  if (!editor) return null;
  return /* @__PURE__ */ jsxs134(
    "div",
    {
      className: cn(
        "flex flex-col border border-border rounded-lg overflow-hidden bg-card",
        className
      ),
      children: [
        !readOnly && /* @__PURE__ */ jsx151(
          EditorToolbar,
          {
            editor,
            onInsertImage: onImageDrop ? handleInsertImage : void 0
          }
        ),
        /* @__PURE__ */ jsx151("div", { className: "flex-1 overflow-y-auto", children: /* @__PURE__ */ jsx151(
          EditorContent2,
          {
            editor,
            className: cn(
              "min-h-full",
              // Prose-like typography for the editing surface
              "[&_.ProseMirror]:outline-none",
              "[&_.ProseMirror]:min-h-full",
              "[&_.ProseMirror_h1]:text-3xl [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h1]:mt-8 [&_.ProseMirror_h1]:mb-4",
              "[&_.ProseMirror_h2]:text-2xl [&_.ProseMirror_h2]:font-semibold [&_.ProseMirror_h2]:mt-6 [&_.ProseMirror_h2]:mb-3",
              "[&_.ProseMirror_h3]:text-xl [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:mt-5 [&_.ProseMirror_h3]:mb-2",
              "[&_.ProseMirror_h4]:text-lg [&_.ProseMirror_h4]:font-medium [&_.ProseMirror_h4]:mt-4 [&_.ProseMirror_h4]:mb-2",
              "[&_.ProseMirror_p]:my-3 [&_.ProseMirror_p]:leading-7",
              "[&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-6 [&_.ProseMirror_ul]:my-3",
              "[&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-6 [&_.ProseMirror_ol]:my-3",
              "[&_.ProseMirror_li]:my-1",
              "[&_.ProseMirror_blockquote]:border-l-4 [&_.ProseMirror_blockquote]:border-border [&_.ProseMirror_blockquote]:pl-4 [&_.ProseMirror_blockquote]:italic [&_.ProseMirror_blockquote]:text-muted-foreground [&_.ProseMirror_blockquote]:my-4",
              "[&_.ProseMirror_pre]:bg-muted [&_.ProseMirror_pre]:rounded-lg [&_.ProseMirror_pre]:p-4 [&_.ProseMirror_pre]:my-4 [&_.ProseMirror_pre]:overflow-x-auto",
              "[&_.ProseMirror_code]:bg-muted [&_.ProseMirror_code]:rounded [&_.ProseMirror_code]:px-1.5 [&_.ProseMirror_code]:py-0.5 [&_.ProseMirror_code]:text-sm [&_.ProseMirror_code]:font-mono",
              "[&_.ProseMirror_pre_code]:bg-transparent [&_.ProseMirror_pre_code]:p-0",
              "[&_.ProseMirror_a]:text-primary [&_.ProseMirror_a]:underline",
              "[&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:rounded-lg [&_.ProseMirror_img]:my-4",
              "[&_.ProseMirror_hr]:border-border [&_.ProseMirror_hr]:my-6",
              // Empty placeholder
              "[&_.ProseMirror.is-editor-empty:first-child::before]:text-muted-foreground",
              "[&_.ProseMirror.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]",
              "[&_.ProseMirror.is-editor-empty:first-child::before]:float-left",
              "[&_.ProseMirror.is-editor-empty:first-child::before]:h-0",
              "[&_.ProseMirror.is-editor-empty:first-child::before]:pointer-events-none"
            )
          }
        ) })
      ]
    }
  );
}

// src/components/json-editor/JsonCodeEditor.tsx
import { useRef as useRef9 } from "react";

// src/components/json-editor/useJsonCodeEditor.ts
import { useEffect as useEffect8, useRef as useRef8 } from "react";
import { EditorView as EditorView2, keymap, placeholder as cmPlaceholder } from "@codemirror/view";
import { EditorState, Compartment } from "@codemirror/state";
import { json } from "@codemirror/lang-json";
import { indentUnit } from "@codemirror/language";
import { indentWithTab } from "@codemirror/commands";
import { autocompletion } from "@codemirror/autocomplete";

// src/components/json-editor/theme.ts
import { EditorView } from "@codemirror/view";
var darkTheme = EditorView.theme(
  {
    "&": {
      backgroundColor: "#1e1e1e",
      color: "#d4d4d4",
      fontSize: "11px",
      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
      borderRadius: "6px",
      border: "1px solid hsl(240 3.7% 25%)"
    },
    "&.cm-focused": {
      outline: "1px solid hsl(217.2 91.2% 59.8%)",
      outlineOffset: "-1px"
    },
    ".cm-content": {
      padding: "8px 0",
      caretColor: "#d4d4d4"
    },
    ".cm-line": {
      padding: "0 12px"
    },
    ".cm-gutters": {
      display: "none"
    },
    ".cm-activeLine": {
      backgroundColor: "rgba(255, 255, 255, 0.04)"
    },
    ".cm-selectionBackground": {
      backgroundColor: "rgba(38, 79, 120, 0.6) !important"
    },
    ".cm-cursor": {
      borderLeftColor: "#d4d4d4"
    },
    ".cm-placeholder": {
      color: "hsl(240 5% 45%)",
      fontStyle: "italic"
    },
    // Scrollbar styling
    ".cm-scroller": {
      overflow: "auto"
    },
    // Token pill base styles
    ".cm-macro-token": {
      backgroundColor: "rgba(59, 130, 246, 0.15)",
      color: "#93c5fd",
      borderRadius: "3px",
      padding: "0 2px",
      border: "1px solid rgba(59, 130, 246, 0.3)"
    },
    ".cm-variable-token": {
      backgroundColor: "rgba(16, 185, 129, 0.15)",
      color: "#6ee7b7",
      borderRadius: "3px",
      padding: "0 2px",
      border: "1px solid rgba(16, 185, 129, 0.3)"
    },
    ".cm-step-ref-token": {
      backgroundColor: "rgba(245, 158, 11, 0.15)",
      color: "#fcd34d",
      borderRadius: "3px",
      padding: "0 2px",
      border: "1px solid rgba(245, 158, 11, 0.3)"
    },
    // Autocomplete popover
    ".cm-tooltip-autocomplete": {
      backgroundColor: "#1e1e1e !important",
      border: "1px solid hsl(240 3.7% 25%) !important",
      borderRadius: "6px !important",
      overflow: "hidden"
    },
    ".cm-tooltip-autocomplete ul": {
      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
      fontSize: "11px"
    },
    ".cm-tooltip-autocomplete ul li": {
      padding: "4px 8px !important",
      color: "#d4d4d4"
    },
    ".cm-tooltip-autocomplete ul li[aria-selected]": {
      backgroundColor: "rgba(59, 130, 246, 0.2) !important",
      color: "#ffffff"
    },
    ".cm-completionLabel": {
      color: "#d4d4d4"
    },
    ".cm-completionDetail": {
      color: "hsl(240 5% 55%)",
      fontStyle: "italic",
      marginLeft: "8px"
    },
    // JSON syntax colors (via one-dark highlight style base)
    ".cm-string": { color: "#ce9178" },
    ".cm-number": { color: "#b5cea8" },
    ".cm-bool": { color: "#569cd6" },
    ".cm-null": { color: "#569cd6" },
    ".cm-propertyName": { color: "#9cdcfe" },
    ".cm-punctuation": { color: "#d4d4d4" }
  },
  { dark: true }
);

// src/components/json-editor/tokenHighlight.ts
import { ViewPlugin, Decoration } from "@codemirror/view";
import { RangeSetBuilder } from "@codemirror/state";
function buildDecorations(view, patterns) {
  const builder = new RangeSetBuilder();
  const matches = [];
  for (const { from, to } of view.visibleRanges) {
    const text = view.state.doc.sliceString(from, to);
    for (const pattern of patterns) {
      const re = new RegExp(pattern.regex.source, pattern.regex.flags);
      let match;
      while ((match = re.exec(text)) !== null) {
        matches.push({
          from: from + match.index,
          to: from + match.index + match[0].length,
          className: pattern.className
        });
      }
    }
  }
  matches.sort((a, b) => a.from - b.from || a.to - b.to);
  for (const m of matches) {
    builder.add(m.from, m.to, Decoration.mark({ class: m.className }));
  }
  return builder.finish();
}
function tokenHighlightPlugin(patterns) {
  return ViewPlugin.fromClass(
    class {
      decorations;
      constructor(view) {
        this.decorations = buildDecorations(view, patterns);
      }
      update(update) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = buildDecorations(update.view, patterns);
        }
      }
    },
    { decorations: (v) => v.decorations }
  );
}

// src/components/json-editor/formatJson.ts
function tryFormatJson(raw) {
  if (!raw.trim()) return null;
  const placeholders = [];
  let idx = 0;
  const withPlaceholders = raw.replace(
    /\$[A-Za-z_]+\([^)]*\)|\$var\.[\w.]+|\{\{[^}]+\}\}/g,
    (match) => {
      const placeholder = `MACROPH${idx++}ENDPH`;
      placeholders.push({ placeholder, original: match });
      return placeholder;
    }
  );
  try {
    const parsed = JSON.parse(withPlaceholders);
    let formatted = JSON.stringify(parsed, null, 2);
    for (const { placeholder, original } of placeholders) {
      formatted = formatted.replaceAll(placeholder, original);
    }
    return formatted;
  } catch {
    return null;
  }
}

// src/components/json-editor/useJsonCodeEditor.ts
function useJsonCodeEditor(options) {
  const {
    containerRef,
    value,
    onChange,
    readOnly = false,
    placeholder = "",
    formatJson = true,
    minHeight = "100px",
    maxHeight = "400px",
    tokenPatterns = [],
    completionEntries = [],
    completionTrigger = "$"
  } = options;
  const viewRef = useRef8(null);
  const isPropUpdate = useRef8(false);
  const onChangeRef = useRef8(onChange);
  const completionCompartment = useRef8(new Compartment());
  const hasFormattedInitial = useRef8(false);
  onChangeRef.current = onChange;
  useEffect8(() => {
    const container = containerRef.current;
    if (!container) return;
    const completionSource = (context) => {
      const line = context.state.doc.lineAt(context.pos);
      const textBefore = line.text.slice(0, context.pos - line.from);
      const triggerIdx = textBefore.lastIndexOf(completionTrigger);
      if (triggerIdx === -1) return null;
      const prefix = textBefore.slice(triggerIdx + 1);
      if (/[^A-Za-z0-9_.]/.test(prefix)) return null;
      const from = line.from + triggerIdx;
      return {
        from,
        options: completionEntries.map((e) => ({
          label: e.label,
          detail: e.detail,
          type: e.type ?? "variable",
          apply: e.apply ?? e.label
        })),
        filter: true
      };
    };
    const updateListener = EditorView2.updateListener.of((update) => {
      if (update.docChanged && !isPropUpdate.current) {
        const newValue = update.state.doc.toString();
        onChangeRef.current(newValue);
      }
    });
    const blurHandler = EditorView2.domEventHandlers({
      blur: (_event, view2) => {
        if (formatJson) {
          const currentValue = view2.state.doc.toString();
          const formatted = tryFormatJson(currentValue);
          if (formatted && formatted !== currentValue) {
            onChangeRef.current(formatted);
          }
        }
      }
    });
    const heightStyles = EditorView2.theme({
      "&": {
        minHeight,
        maxHeight
      },
      ".cm-scroller": {
        minHeight,
        maxHeight
      }
    });
    const state = EditorState.create({
      doc: value,
      extensions: [
        json(),
        darkTheme,
        heightStyles,
        ...tokenPatterns.length > 0 ? [tokenHighlightPlugin(tokenPatterns)] : [],
        completionCompartment.current.of(
          completionEntries.length > 0 ? autocompletion({ override: [completionSource], activateOnTyping: true }) : []
        ),
        keymap.of([indentWithTab]),
        indentUnit.of("  "),
        EditorView2.lineWrapping,
        EditorState.readOnly.of(readOnly),
        cmPlaceholder(placeholder),
        updateListener,
        blurHandler
      ]
    });
    const view = new EditorView2({ state, parent: container });
    viewRef.current = view;
    if (formatJson && value && !hasFormattedInitial.current) {
      hasFormattedInitial.current = true;
      const formatted = tryFormatJson(value);
      if (formatted && formatted !== value) {
        onChangeRef.current(formatted);
      }
    }
    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, []);
  useEffect8(() => {
    const view = viewRef.current;
    if (!view) return;
    const currentValue = view.state.doc.toString();
    if (currentValue !== value) {
      isPropUpdate.current = true;
      view.dispatch({
        changes: {
          from: 0,
          to: currentValue.length,
          insert: value
        }
      });
      isPropUpdate.current = false;
    }
  }, [value]);
  useEffect8(() => {
    const view = viewRef.current;
    if (!view) return;
    const completionSource = (context) => {
      const line = context.state.doc.lineAt(context.pos);
      const textBefore = line.text.slice(0, context.pos - line.from);
      const triggerIdx = textBefore.lastIndexOf(completionTrigger);
      if (triggerIdx === -1) return null;
      const prefix = textBefore.slice(triggerIdx + 1);
      if (/[^A-Za-z0-9_.]/.test(prefix)) return null;
      const from = line.from + triggerIdx;
      return {
        from,
        options: completionEntries.map((e) => ({
          label: e.label,
          detail: e.detail,
          type: e.type ?? "variable",
          apply: e.apply ?? e.label
        })),
        filter: true
      };
    };
    view.dispatch({
      effects: completionCompartment.current.reconfigure(
        completionEntries.length > 0 ? autocompletion({ override: [completionSource], activateOnTyping: true }) : []
      )
    });
  }, [completionEntries, completionTrigger]);
}

// src/components/json-editor/JsonCodeEditor.tsx
import { jsx as jsx152, jsxs as jsxs135 } from "react/jsx-runtime";
var JsonCodeEditor = ({
  value,
  onChange,
  placeholder,
  label,
  description,
  readOnly = false,
  formatJson = true,
  minHeight = "200px",
  maxHeight = "400px",
  className,
  "aria-label": ariaLabel,
  tokenPatterns,
  completionEntries,
  completionTrigger
}) => {
  const containerRef = useRef9(null);
  useJsonCodeEditor({
    containerRef,
    value,
    onChange,
    readOnly,
    placeholder,
    formatJson,
    minHeight,
    maxHeight,
    tokenPatterns,
    completionEntries,
    completionTrigger
  });
  return /* @__PURE__ */ jsxs135("div", { className, children: [
    label && /* @__PURE__ */ jsx152("label", { className: "block text-xs font-medium text-muted-foreground mb-1.5", children: label }),
    description && /* @__PURE__ */ jsx152("p", { className: "text-xs text-muted-foreground/70 mb-1.5", children: description }),
    /* @__PURE__ */ jsx152("div", { ref: containerRef, "aria-label": ariaLabel ?? label })
  ] });
};

// src/components/HintCards.tsx
import { useState as useState10, useCallback as useCallback11, useEffect as useEffect9, useRef as useRef10 } from "react";
import { jsx as jsx153, jsxs as jsxs136 } from "react/jsx-runtime";
var AUTO_HIDE_MS = 3e4;
function HintCards({
  hints,
  initialIndex = 0,
  autoHideMs = AUTO_HIDE_MS,
  onDismissToday,
  onDismissForever,
  onSeen,
  onClose
}) {
  const [currentIndex, setCurrentIndex] = useState10(initialIndex);
  const [interacted, setInteracted] = useState10(false);
  const [visible, setVisible] = useState10(false);
  const [exiting, setExiting] = useState10(false);
  const timerRef = useRef10(null);
  const progressRef = useRef10(null);
  useEffect9(() => {
    const frame = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  useEffect9(() => {
    if (!autoHideMs || interacted) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }
    timerRef.current = setTimeout(() => {
      setExiting(true);
      setTimeout(() => onClose?.(), 200);
    }, autoHideMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [autoHideMs, interacted, onClose]);
  useEffect9(() => {
    if (interacted && progressRef.current) {
      progressRef.current.style.animationPlayState = "paused";
    }
  }, [interacted]);
  const handleInteract = useCallback11(() => {
    if (!interacted) setInteracted(true);
  }, [interacted]);
  const goTo = useCallback11(
    (idx) => {
      handleInteract();
      setCurrentIndex(idx);
      const hint2 = hints[idx];
      if (hint2) onSeen?.(hint2.id);
    },
    [hints, onSeen, handleInteract]
  );
  const handleClose = useCallback11(() => {
    setExiting(true);
    setTimeout(() => onClose?.(), 200);
  }, [onClose]);
  if (hints.length === 0) return null;
  const hint = hints[currentIndex];
  if (!hint) return null;
  const showProgress = autoHideMs > 0;
  return /* @__PURE__ */ jsxs136(
    "div",
    {
      className: `w-[340px] rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/80 shadow-xl transition-all duration-200 ease-out ${visible && !exiting ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-[calc(100%+2rem)]"}`,
      onMouseEnter: handleInteract,
      onClick: handleInteract,
      role: "status",
      "aria-live": "polite",
      children: [
        showProgress && /* @__PURE__ */ jsx153("div", { className: "h-1 w-full bg-amber-200/50 dark:bg-amber-800/30 rounded-t-xl overflow-hidden", children: /* @__PURE__ */ jsx153(
          "div",
          {
            ref: progressRef,
            className: "h-full bg-amber-400 dark:bg-amber-500",
            style: {
              animation: `hint-progress ${autoHideMs}ms linear forwards`
            }
          }
        ) }),
        /* @__PURE__ */ jsxs136("div", { className: "p-4", children: [
          /* @__PURE__ */ jsxs136("div", { className: "flex items-start justify-between gap-2 mb-2", children: [
            /* @__PURE__ */ jsxs136("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx153("span", { className: "text-amber-500 dark:text-amber-400 text-lg", children: "\u{1F4A1}" }),
              /* @__PURE__ */ jsx153("h4", { className: "text-sm font-semibold text-amber-900 dark:text-amber-100", children: hint.title })
            ] }),
            /* @__PURE__ */ jsx153(
              "button",
              {
                onClick: handleClose,
                className: "shrink-0 text-amber-400 hover:text-amber-700 dark:text-amber-500 dark:hover:text-amber-200 transition-colors text-lg leading-none mt-0.5",
                "aria-label": "Close",
                children: "\xD7"
              }
            )
          ] }),
          /* @__PURE__ */ jsx153("p", { className: "text-sm text-amber-800 dark:text-amber-200/90 mb-4 whitespace-pre-wrap leading-relaxed", children: hint.content }),
          /* @__PURE__ */ jsxs136("div", { className: "flex items-center justify-between gap-2", children: [
            /* @__PURE__ */ jsxs136("div", { className: "flex gap-2", children: [
              /* @__PURE__ */ jsx153(Button, { variant: "outline", size: "sm", onPress: () => {
                handleInteract();
                onDismissToday?.();
              }, children: "Dismiss today" }),
              /* @__PURE__ */ jsx153(Button, { variant: "outline", size: "sm", onPress: () => {
                handleInteract();
                onDismissForever?.();
              }, children: "Don't show again" })
            ] }),
            hints.length > 1 && /* @__PURE__ */ jsxs136("div", { className: "flex items-center gap-1", children: [
              /* @__PURE__ */ jsx153(
                "button",
                {
                  disabled: currentIndex === 0,
                  onClick: () => goTo(currentIndex - 1),
                  className: "px-1.5 py-0.5 text-xs text-amber-600 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-100 disabled:opacity-30 transition-colors",
                  children: "\u2039"
                }
              ),
              /* @__PURE__ */ jsxs136("span", { className: "text-xs text-amber-500 dark:text-amber-400 tabular-nums", children: [
                currentIndex + 1,
                "/",
                hints.length
              ] }),
              /* @__PURE__ */ jsx153(
                "button",
                {
                  disabled: currentIndex === hints.length - 1,
                  onClick: () => goTo(currentIndex + 1),
                  className: "px-1.5 py-0.5 text-xs text-amber-600 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-100 disabled:opacity-30 transition-colors",
                  children: "\u203A"
                }
              )
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsx153("style", { children: `
        @keyframes hint-progress {
          from { width: 100%; }
          to { width: 0%; }
        }
      ` })
      ]
    }
  );
}

// src/components/PlaybookViewer.tsx
import { useState as useState11, useRef as useRef11, useCallback as useCallback12, useEffect as useEffect10 } from "react";
import { createPortal as createPortal8 } from "react-dom";
import { jsx as jsx154, jsxs as jsxs137 } from "react/jsx-runtime";
var ACTION_ICONS2 = {
  click: "\u{1F446}",
  fill: "\u270F\uFE0F",
  fill_form: "\u270F\uFE0F",
  select: "\u25BC",
  navigate: "\u{1F517}",
  wait: "\u23F3",
  scroll: "\u2195\uFE0F",
  hover: "\u{1F91A}",
  upload: "\u{1F4E4}",
  verify: "\u{1F441}",
  handle_native_dialog: "\u{1F4AC}",
  toggle: "\u{1F518}",
  press_key: "\u2328\uFE0F",
  focus: "\u{1F526}",
  drag: "\u2194\uFE0F",
  resize: "\u25A3",
  capture: "\u{1F3AF}",
  extract_table: "\u{1F4CA}",
  python_execute: "\u{1F5A5}\uFE0F",
  verify_download: "\u2B07\uFE0F",
  verify_download_content: "\u2B07\uFE0F",
  screenshot: "\u{1F4F7}",
  agent_loop: "\u{1F501}",
  call: "\u{1F4D6}",
  other: "\u2022"
};
var NODE_TYPE_COLORS = {
  login: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
  navigate: "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20",
  action: "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20",
  assertion: "bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20",
  wait: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20",
  setup: "bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20",
  condition: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20"
};
function formatAction2(action) {
  return action.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}
function ActionRow2({
  step,
  onHover,
  onLeave,
  isHovered,
  onFocus,
  onBlur
}) {
  const hasParams = step.params && Object.keys(step.params).length > 0;
  return /* @__PURE__ */ jsxs137(
    "div",
    {
      className: cn(
        "flex items-center gap-2 px-3 py-1.5 text-xs transition-colors cursor-default",
        "hover:bg-muted/60",
        isHovered && "bg-muted/60"
      ),
      onMouseEnter: onHover,
      onMouseLeave: onLeave,
      onFocus,
      onBlur,
      tabIndex: 0,
      role: "button",
      "aria-label": `View screenshot for step ${step.order}`,
      children: [
        /* @__PURE__ */ jsx154("span", { className: "flex items-center justify-center w-5 h-5 rounded bg-muted text-muted-foreground text-[10px] font-mono font-medium shrink-0", children: step.order }),
        /* @__PURE__ */ jsx154("span", { className: "shrink-0 w-4 text-center", "aria-hidden": true, children: ACTION_ICONS2[step.action] || "\u2022" }),
        /* @__PURE__ */ jsxs137("span", { className: "min-w-0 flex-1", children: [
          /* @__PURE__ */ jsx154("span", { className: "font-medium", children: formatAction2(step.action) }),
          " ",
          /* @__PURE__ */ jsxs137("span", { className: "text-muted-foreground", children: [
            step.target,
            hasParams && Object.entries(step.params).map(([key, value]) => /* @__PURE__ */ jsxs137("span", { children: [
              " ",
              "(",
              /* @__PURE__ */ jsxs137("span", { className: "italic", children: [
                key,
                ": ",
                value
              ] }),
              ")"
            ] }, key)),
            step.note && /* @__PURE__ */ jsxs137("span", { className: "italic ml-1", children: [
              "(",
              step.note,
              ")"
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsx154(
          "span",
          {
            className: cn(
              "text-[10px] text-muted-foreground/50 shrink-0 transition-opacity",
              isHovered ? "opacity-100" : "opacity-0"
            ),
            children: step.screenshotUrl ? "\u{1F4F7}" : "\u{1F4F7}"
          }
        )
      ]
    }
  );
}
function computePopoverPosition(anchorRect, popoverWidth, estimatedHeight) {
  const gap = 8;
  const spaceRight = window.innerWidth - anchorRect.right;
  const placeRight = spaceRight >= popoverWidth + gap;
  const left = placeRight ? anchorRect.right + gap : anchorRect.left - popoverWidth - gap;
  const top = Math.max(
    8,
    Math.min(
      anchorRect.top - estimatedHeight / 2 + anchorRect.height / 2,
      window.innerHeight - estimatedHeight - 8
    )
  );
  return { left, top };
}
var POPOVER_WIDTH = 420;
function ScreenshotPopover({
  screenshotUrl,
  anchorRect
}) {
  if (!anchorRect) return null;
  const { left, top } = computePopoverPosition(anchorRect, POPOVER_WIDTH, 280);
  return createPortal8(
    /* @__PURE__ */ jsx154(
      "div",
      {
        className: "fixed z-[9999] pointer-events-none",
        style: { left: `${left}px`, top: `${top}px`, width: `${POPOVER_WIDTH}px` },
        children: /* @__PURE__ */ jsx154("div", { className: "rounded-lg border border-border bg-card shadow-lg overflow-hidden pointer-events-auto", children: /* @__PURE__ */ jsx154("img", { src: screenshotUrl, alt: "Step screenshot", className: "w-full h-auto", loading: "eager" }) })
      }
    ),
    document.body
  );
}
function NoScreenshotPopover({ anchorRect }) {
  if (!anchorRect) return null;
  const placeholderHeight = 120;
  const { left, top } = computePopoverPosition(anchorRect, POPOVER_WIDTH, placeholderHeight);
  return createPortal8(
    /* @__PURE__ */ jsx154(
      "div",
      {
        className: "fixed z-[9999] pointer-events-none",
        style: { left: `${left}px`, top: `${top}px`, width: `${POPOVER_WIDTH}px` },
        children: /* @__PURE__ */ jsxs137(
          "div",
          {
            className: "rounded-lg border border-border bg-card shadow-lg overflow-hidden pointer-events-auto flex flex-col items-center justify-center gap-1.5 text-muted-foreground",
            style: { height: `${placeholderHeight}px` },
            children: [
              /* @__PURE__ */ jsx154("span", { className: "text-2xl opacity-40", children: "\u{1F4F7}" }),
              /* @__PURE__ */ jsx154("span", { className: "text-xs", children: "No screenshot yet" }),
              /* @__PURE__ */ jsx154("span", { className: "text-[10px] opacity-60", children: "Will capture on next run" })
            ]
          }
        )
      }
    ),
    document.body
  );
}
function BranchDivider({ label }) {
  return /* @__PURE__ */ jsxs137("div", { className: "flex items-center gap-2 px-3 py-1.5", children: [
    /* @__PURE__ */ jsx154("div", { className: "flex-1 border-t border-dashed border-border" }),
    /* @__PURE__ */ jsx154("span", { className: "text-[10px] font-medium text-muted-foreground uppercase tracking-wider", children: label }),
    /* @__PURE__ */ jsx154("div", { className: "flex-1 border-t border-dashed border-border" })
  ] });
}
function computeBranchDepths(nodes) {
  const depths = /* @__PURE__ */ new Map();
  let currentDepth = 0;
  const depthStack = [];
  for (const node of nodes) {
    if (node.branchInfo?.type === "convergence") {
      currentDepth = depthStack.length > 0 ? depthStack.pop() : 0;
      depths.set(node.nodeId, currentDepth);
    } else if (node.branchInfo?.type === "branch-start") {
      depths.set(node.nodeId, currentDepth);
      depthStack.push(currentDepth);
      currentDepth += 1;
    } else if (node.branchInfo?.type === "branch-child") {
      depths.set(node.nodeId, currentDepth);
    } else {
      depths.set(node.nodeId, currentDepth);
    }
  }
  return depths;
}
var PlaybookViewer = ({ nodes, className }) => {
  const [hoveredStep, setHoveredStep] = useState11(null);
  const hoverTimeoutRef = useRef11(null);
  const rowRefs = useRef11(/* @__PURE__ */ new Map());
  const getStepKey = (nodeIdx, stepIdx) => `${nodeIdx}-${stepIdx}`;
  const handleHover = useCallback12((nodeIdx, stepIdx) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      const key = getStepKey(nodeIdx, stepIdx);
      const el = rowRefs.current.get(key);
      setHoveredStep({
        nodeIdx,
        stepIdx,
        rect: el?.getBoundingClientRect() ?? null
      });
    }, 100);
  }, []);
  const handleLeave = useCallback12(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredStep(null);
    }, 150);
  }, []);
  useEffect10(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);
  const hoveredScreenshotUrl = hoveredStep !== null ? nodes[hoveredStep.nodeIdx]?.playbookSteps[hoveredStep.stepIdx]?.screenshotUrl : void 0;
  if (nodes.length === 0) {
    return /* @__PURE__ */ jsx154(Box, { className: cn("flex items-center justify-center p-8", className), children: /* @__PURE__ */ jsx154(Text, { size: "sm", className: "text-muted-foreground", children: "No playbook steps recorded" }) });
  }
  const branchDepths = computeBranchDepths(nodes);
  let lastBranchLabel = null;
  let lastConditionNodeId = null;
  return /* @__PURE__ */ jsxs137("div", { className: cn("relative", className), children: [
    nodes.map((node, nodeIdx) => {
      const typeColorClass = NODE_TYPE_COLORS[node.nodeType] || "bg-muted text-muted-foreground border-border";
      const depth = branchDepths.get(node.nodeId) ?? 0;
      const indentStyle = depth > 0 ? { paddingLeft: `${depth * 1.5}rem` } : void 0;
      let dividerBefore = null;
      if (node.branchInfo?.type === "branch-child") {
        const isNewBranch = node.branchInfo.conditionNodeId !== lastConditionNodeId || node.branchInfo.branchLabel !== lastBranchLabel;
        if (isNewBranch) {
          const label = node.branchInfo.branchLabel === "true" ? "If true" : "If false";
          dividerBefore = /* @__PURE__ */ jsx154("div", { style: indentStyle, children: /* @__PURE__ */ jsx154(BranchDivider, { label }) });
        }
        lastBranchLabel = node.branchInfo.branchLabel;
        lastConditionNodeId = node.branchInfo.conditionNodeId;
      } else if (node.branchInfo?.type === "convergence") {
        dividerBefore = /* @__PURE__ */ jsx154("div", { style: indentStyle, children: /* @__PURE__ */ jsx154(BranchDivider, { label: "continues" }) });
        lastBranchLabel = null;
        lastConditionNodeId = null;
      } else if (node.branchInfo?.type === "branch-start") {
        lastBranchLabel = null;
        lastConditionNodeId = null;
      }
      return /* @__PURE__ */ jsxs137("div", { children: [
        dividerBefore,
        /* @__PURE__ */ jsx154(
          "div",
          {
            className: "sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-3 py-2",
            style: indentStyle,
            children: /* @__PURE__ */ jsxs137(Flex, { align: "center", gap: "sm", children: [
              /* @__PURE__ */ jsx154("span", { className: "flex items-center justify-center w-6 h-6 rounded-full bg-foreground text-background text-xs font-semibold shrink-0", children: node.stepNumber }),
              /* @__PURE__ */ jsx154(Text, { size: "sm", weight: "medium", className: "flex-1 min-w-0 truncate", children: node.nodeName }),
              /* @__PURE__ */ jsx154(
                "span",
                {
                  className: cn(
                    "text-[10px] font-medium px-1.5 py-0.5 rounded border",
                    typeColorClass
                  ),
                  children: node.nodeType
                }
              )
            ] })
          }
        ),
        node.playbookSteps.length > 0 && /* @__PURE__ */ jsx154("div", { className: "py-0.5", style: indentStyle, children: node.playbookSteps.map((step, stepIdx) => {
          const key = getStepKey(nodeIdx, stepIdx);
          const isHovered = hoveredStep?.nodeIdx === nodeIdx && hoveredStep?.stepIdx === stepIdx;
          return /* @__PURE__ */ jsx154(
            "div",
            {
              ref: (el) => {
                if (el) rowRefs.current.set(key, el);
                else rowRefs.current.delete(key);
              },
              children: /* @__PURE__ */ jsx154(
                ActionRow2,
                {
                  step,
                  isHovered,
                  onHover: () => handleHover(nodeIdx, stepIdx),
                  onLeave: handleLeave,
                  onFocus: () => handleHover(nodeIdx, stepIdx),
                  onBlur: handleLeave
                }
              )
            },
            step.order
          );
        }) })
      ] }, node.nodeId);
    }),
    hoveredStep && (hoveredScreenshotUrl ? /* @__PURE__ */ jsx154(ScreenshotPopover, { screenshotUrl: hoveredScreenshotUrl, anchorRect: hoveredStep.rect }) : /* @__PURE__ */ jsx154(NoScreenshotPopover, { anchorRect: hoveredStep.rect }))
  ] });
};

// src/components/TableOfContents.tsx
import React101 from "react";
import { jsx as jsx155, jsxs as jsxs138 } from "react/jsx-runtime";
var prefersReducedMotion2 = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
function TableOfContents({ headings, variant = "docs" }) {
  const [activeId, setActiveId] = React101.useState("");
  const listRef = React101.useRef(null);
  const isClickScrolling = React101.useRef(false);
  const clickTimer = React101.useRef(void 0);
  const [indicatorStyle, setIndicatorStyle] = React101.useState({
    opacity: 0
  });
  React101.useEffect(() => {
    const elements = headings.map((h) => document.getElementById(h.id)).filter(Boolean);
    if (elements.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (isClickScrolling.current) return;
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [headings]);
  React101.useEffect(() => {
    if (!activeId || !listRef.current) return;
    const activeLink = listRef.current.querySelector(
      `a[href="#${CSS.escape(activeId)}"]`
    );
    if (!activeLink) return;
    const listRect = listRef.current.getBoundingClientRect();
    const linkRect = activeLink.getBoundingClientRect();
    setIndicatorStyle({
      top: linkRect.top - listRect.top,
      height: linkRect.height,
      opacity: 1
    });
  }, [activeId]);
  const isPage = variant === "page";
  return /* @__PURE__ */ jsxs138(
    "nav",
    {
      className: isPage ? "sticky top-6 hidden max-h-[calc(100vh-10rem)] w-[200px] shrink-0 self-start overflow-y-auto min-[1100px]:block" : "toc",
      "aria-label": "Table of contents",
      children: [
        /* @__PURE__ */ jsx155(
          "div",
          {
            className: isPage ? "mb-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground" : "toc-title",
            children: "On this page"
          }
        ),
        /* @__PURE__ */ jsxs138(
          "ul",
          {
            className: isPage ? "relative m-0 list-none border-l border-border p-0" : "toc-list",
            ref: listRef,
            children: [
              /* @__PURE__ */ jsx155(
                "div",
                {
                  className: isPage ? "pointer-events-none absolute -left-px w-0.5 bg-primary transition-[top,height,opacity] duration-200" : "toc-indicator",
                  style: indicatorStyle
                }
              ),
              headings.map((heading) => /* @__PURE__ */ jsx155("li", { className: heading.level === 3 ? "toc-indent" : "", children: /* @__PURE__ */ jsx155(
                "a",
                {
                  href: `#${heading.id}`,
                  className: isPage ? cn(
                    "block px-3.5 py-1.5 text-[13px] leading-snug no-underline transition-colors hover:text-foreground",
                    activeId === heading.id ? "font-medium text-foreground" : "text-muted-foreground"
                  ) : activeId === heading.id ? "toc-active" : "",
                  onClick: (e) => {
                    e.preventDefault();
                    const el = document.getElementById(heading.id);
                    if (el) {
                      isClickScrolling.current = true;
                      clearTimeout(clickTimer.current);
                      clickTimer.current = setTimeout(() => {
                        isClickScrolling.current = false;
                      }, 800);
                      setActiveId(heading.id);
                      el.scrollIntoView({ behavior: prefersReducedMotion2() ? "auto" : "smooth", block: "start" });
                    }
                  },
                  children: heading.text
                }
              ) }, heading.id))
            ]
          }
        )
      ]
    }
  );
}

// src/components/LiveSearch.tsx
import React102 from "react";
import { createPortal as createPortal9 } from "react-dom";
import { MagnifyingGlass as MagnifyingGlass6, X as X7 } from "@phosphor-icons/react";
import { Fragment as Fragment35, jsx as jsx156, jsxs as jsxs139 } from "react/jsx-runtime";
function LiveSearch({
  isOpen,
  onClose,
  query,
  onQueryChange,
  items,
  getItemKey,
  renderItem,
  onSelect,
  placeholder = "Search\u2026",
  hint,
  emptyState,
  footer,
  closeOnSelect = false,
  "aria-label": ariaLabel = "Search"
}) {
  const [activeIndex, setActiveIndex] = React102.useState(0);
  const inputRef = React102.useRef(null);
  const activeRef = React102.useRef(null);
  React102.useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);
  React102.useEffect(() => {
    if (!isOpen) return;
    setActiveIndex(0);
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [isOpen]);
  React102.useEffect(() => {
    setActiveIndex((i) => Math.min(Math.max(0, i), Math.max(0, items.length - 1)));
  }, [items.length]);
  React102.useEffect(() => setActiveIndex(0), [query]);
  React102.useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);
  if (!isOpen) return null;
  const select = (item) => {
    onSelect(item);
    if (closeOnSelect) onClose();
    else inputRef.current?.focus();
  };
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = items[activeIndex];
      if (item) select(item);
    }
  };
  const trimmed = query.trim();
  return createPortal9(
    /* @__PURE__ */ jsxs139(
      "div",
      {
        className: "fixed inset-0 z-[100] flex items-start justify-center",
        role: "dialog",
        "aria-modal": "true",
        "aria-label": ariaLabel,
        children: [
          /* @__PURE__ */ jsx156(
            "button",
            {
              type: "button",
              "aria-label": "Close search",
              className: "absolute inset-0 cursor-default bg-foreground/30",
              onClick: onClose
            }
          ),
          /* @__PURE__ */ jsxs139(
            "div",
            {
              className: "relative mt-[12vh] flex max-h-[70vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl",
              onKeyDown: handleKeyDown,
              children: [
                /* @__PURE__ */ jsxs139("div", { className: "flex items-center gap-3 px-4 py-3.5", children: [
                  /* @__PURE__ */ jsx156(MagnifyingGlass6, { size: 18, weight: "bold", className: "shrink-0 text-primary" }),
                  /* @__PURE__ */ jsx156(
                    "input",
                    {
                      ref: inputRef,
                      value: query,
                      onChange: (e) => onQueryChange(e.target.value),
                      placeholder,
                      className: "h-7 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground"
                    }
                  ),
                  query.length > 0 && /* @__PURE__ */ jsx156(
                    "button",
                    {
                      type: "button",
                      "aria-label": "Clear search",
                      onClick: () => {
                        onQueryChange("");
                        inputRef.current?.focus();
                      },
                      className: "text-primary hover:text-primary/80",
                      children: /* @__PURE__ */ jsx156(X7, { size: 16, weight: "bold" })
                    }
                  ),
                  /* @__PURE__ */ jsx156("kbd", { className: "rounded border border-border bg-muted/50 px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground", children: "ESC" })
                ] }),
                /* @__PURE__ */ jsx156("div", { className: "border-t border-border" }),
                hint && !trimmed ? /* @__PURE__ */ jsx156("div", { className: "px-3 pt-2", children: hint }) : null,
                /* @__PURE__ */ jsx156("div", { className: "min-h-0 flex-1 overflow-y-auto p-2", children: items.length === 0 ? emptyState ?? /* @__PURE__ */ jsx156("div", { className: "py-10 text-center text-sm text-muted-foreground", children: trimmed ? /* @__PURE__ */ jsxs139(Fragment35, { children: [
                  "No results for \u201C",
                  trimmed,
                  "\u201D."
                ] }) : "No results." }) : /* @__PURE__ */ jsx156("div", { className: "flex flex-col", children: items.map((item, i) => /* @__PURE__ */ jsx156(
                  "button",
                  {
                    type: "button",
                    ref: i === activeIndex ? activeRef : void 0,
                    onMouseEnter: () => setActiveIndex(i),
                    onClick: () => select(item),
                    className: cn(
                      "w-full rounded-md text-left transition-colors",
                      i === activeIndex ? "bg-muted" : "hover:bg-muted/60"
                    ),
                    children: renderItem(item, { active: i === activeIndex })
                  },
                  getItemKey(item)
                )) }) }),
                footer ? /* @__PURE__ */ jsxs139(Fragment35, { children: [
                  /* @__PURE__ */ jsx156("div", { className: "border-t border-border" }),
                  footer
                ] }) : null
              ]
            }
          )
        ]
      }
    ),
    document.body
  );
}
export {
  ActionNode,
  AddNodeMenu,
  AddNodeMenuPopover,
  AnimatedNumber,
  ApiSequenceNode,
  Archive,
  AreaChart,
  ArrowClockwise,
  ArrowCounterClockwise,
  ArrowRight,
  ArrowsLeftRight,
  AssertionNode,
  AudioAssertionNode,
  AudioNode,
  Avatar,
  Badge,
  Banner,
  BarChart,
  Bell,
  Bookmark,
  Box,
  Breadcrumb,
  BreadcrumbDropdown,
  Breadcrumbs,
  BrowserActionsTimeline,
  BrowserStreamPanel,
  Buildings,
  BulkActionBar,
  Button,
  Calendar,
  CalendarDate,
  CalendarDateTime3 as CalendarDateTime,
  CallFlowNode,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CaretDown4 as CaretDown,
  CaretLeft2 as CaretLeft,
  CaretRight4 as CaretRight,
  CaretUp2 as CaretUp,
  CaretUpDown,
  ChartBar2 as ChartBar,
  ChartEmptyState,
  ChartLine,
  ChatCircle,
  ChatComposer,
  ChatMessage,
  ChatMessageList,
  Check,
  CheckCircle,
  Checkbox,
  CaretDown3 as ChevronDown,
  CaretLeft as ChevronLeft,
  CaretRight3 as ChevronRight,
  CaretUp as ChevronUp,
  Clock,
  CloudArrowDown,
  CloudArrowUp,
  Code,
  CollapsibleSection,
  Columns,
  ComboBox,
  ConditionNode,
  ConsoleDiagnosticsPanel,
  Copy,
  Cpu,
  CreditCard,
  DangerAction,
  DangerSection,
  DataTable,
  Database,
  DatePicker,
  DateRangePicker,
  DateTimePicker,
  DateTimeRangePicker,
  Dialog5 as Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Divider,
  DocsAnnotation,
  DocsContext,
  DocsContextArea,
  DocsContextMenu,
  DocsContextMenuWrapper,
  DocsContextProvider,
  DotsThree,
  DotsThreeVertical,
  Download,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerExpandButton,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
  DrawerWideButton,
  PencilSimple as Edit,
  EditableBadge,
  EmptyState,
  EmptyStateCompareGraphic,
  EmptyStateGraphic,
  EmptyStateScanGraphic,
  EmptyStateSpeedGraphic,
  EndNode,
  EntityCard,
  Envelope,
  Export,
  Eye,
  EyeSlash,
  File,
  FilePdf,
  FileText,
  FilterCountBadge,
  FilterToolbar,
  FilterToolbarChips,
  FilterToolbarDivider,
  FilterToolbarSearch,
  FilterToolbarSpacer,
  Flex,
  FloatingChatPanel,
  FlowArrow,
  FlowLineageNode,
  Folder,
  FolderOpen,
  GaugeChart,
  Gear2 as Gear,
  GitBranch,
  GridFour,
  HIDDEN_ACTION_TYPES,
  HardDrives,
  Heading,
  Heart,
  HintCards,
  House,
  HoverCardTrigger as HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Image,
  ArrowSquareIn as Import,
  Info,
  InlineEditableText,
  JsonCodeEditor,
  KPICard,
  KeyValueEditor,
  Lightbulb,
  LineChart,
  LinkSimple,
  List,
  ListCard,
  ListCardRow,
  LiveSearch,
  LoadingBlob,
  LoadingHelix,
  LoadingOrbit,
  LoadingWave,
  LoginNode,
  MagnifyingGlass,
  MapPin,
  Markdown,
  MarkdownEditor,
  McpNode,
  MediaPlayer,
  Menu,
  MenuHeader,
  MenuItem,
  MenuSection,
  MenuSeparator,
  MenuTrigger,
  MetadataItem,
  MetricChart,
  Microphone,
  MicrophoneSlash,
  Minus2 as Minus,
  Moon,
  MoreFiltersButton,
  MorphButton,
  MultiSegmentBar,
  MultiSegmentGauge,
  MultiSelectComboBox,
  MusicNote as Music,
  NetworkDiagnosticsPanel,
  NodeIconPill,
  NotificationNode,
  NumberField,
  OptimizerChip,
  Package,
  Page,
  PageHeader,
  PageNode,
  PasswordField,
  PasswordStrengthIndicator,
  Phone,
  PieChart,
  PillToggle,
  Play,
  PlaybookViewer,
  Plus3 as Plus,
  Popover10 as Popover,
  PopoverContent,
  PopoverTrigger,
  Progress,
  PulsingDot,
  Question,
  Receipt,
  RichTextEditor,
  Rows,
  RunSummaryTooltip,
  SandboxActionNode,
  ScreenshotCarousel,
  SeedNode,
  Select,
  Gear as Settings,
  SettingsRow,
  SettingsSection,
  SetupNode,
  Share,
  Shield,
  ShieldCheck,
  ShoppingCart,
  SideNav,
  SideNavGroup,
  SideNavLink,
  Sidebar,
  SignOut,
  Skeleton,
  SkeletonCard,
  SkeletonChart,
  SkeletonKPI,
  SkeletonKPIRow,
  SkeletonText,
  Sparkline,
  Spinner,
  SplitButton,
  Stack,
  StackedBarChart,
  Star,
  StatusBadge,
  StatusIconBar,
  StatusIndicator,
  StatusTimeline,
  StepIndicator,
  StepProgressBar,
  SteppedDialog,
  StepsPreviewSkeleton,
  SubNav,
  SubNavLinkContent,
  SubmenuTrigger,
  Sun,
  Switch,
  Switcher,
  TAG_COLORS,
  TAG_COLOR_OPTIONS,
  ACTION_LABELS as TIMELINE_ACTION_LABELS,
  TOKEN_COLORS,
  Tab,
  TabList,
  TabPanel,
  Table2 as Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableOfContents,
  TableRow,
  Tabs,
  Tag,
  TagBadge,
  TagGroup,
  TagPicker,
  Terminal,
  TestStatusCell,
  Text,
  TextArea,
  TextField,
  ThemeProvider,
  ThoughtsDiagnosticsPanel,
  ThumbsDown,
  ThumbsUp,
  Time3 as Time,
  TimeField,
  Timeline2 as Timeline,
  TimelineSyncedList,
  Toast,
  ToastProvider,
  ToolCall,
  ToolCallGroup,
  ToolbarFilterMenu,
  ToolbarFilterMenuTriState,
  Tooltip,
  TooltipTrigger,
  Trash,
  TrendDown,
  TrendUp,
  TruncatedText,
  UnderlineTab,
  UnderlineTabs,
  Upload,
  User,
  UsersThree,
  VerticalEdge,
  VerticalFlowCanvas,
  Video,
  VideoCamera,
  VideoPlayerWithTimeline,
  VisualCheckpointNode,
  WaitCountdown,
  WaitNode,
  Warning,
  WaterfallChart,
  WorkflowHandle,
  X2 as X,
  XCircle,
  ZonedDateTime,
  badgeVariantToTagColor,
  calculatePasswordStrength,
  chartThemes,
  cn,
  computeGroupReorderEdgeOps,
  computeReorderEdgeOps,
  defaultThemeMode,
  edgeAnimationStyles,
  extractLinearChain,
  failHatchStyle,
  filterTimelineEntries,
  findActiveTimelineIndex,
  formatDurationLabel,
  formatSandboxActionParams,
  formatStepLabel,
  formatTimelineTimestamp,
  gaugeRanges,
  getContiguousSelectedInChain,
  getGaugeColor,
  getGaugeLabel,
  getGlobalToast,
  getLocalTimeZone,
  getNodeIcon,
  getOptimizerChip,
  getStatusBadgeVariant,
  getStatusHex,
  getStatusLabel,
  getSubNavLinkClassName,
  getTimelineActionIcon,
  getTimelineActionLabel,
  getTimelineAnnotationColors,
  getTimelineAnnotationIcon,
  getTimelineCacheBadge,
  groupToolCallParts,
  hashStringToTagColor,
  iconSizes,
  isStepAnnotation,
  normalizeRunStatus,
  normalizeTestStatus,
  now,
  parseAbsolute,
  parseAbsoluteToLocal,
  parseDate,
  parseDateTime,
  parseThoughtContent,
  parseTime,
  parseZonedDateTime,
  runBarColor,
  runStatusColors,
  sectionIdFromTitle,
  startViewTransition,
  statusHexColors,
  statusTailwindClasses,
  summarizeToolInput,
  themes,
  toCalendarDateTime3 as toCalendarDateTime,
  today,
  tokenize,
  tryFormatJson,
  urlAtTimelinePosition,
  useAnimatedNumber,
  useContextMenuPlugin,
  useCountdown,
  useDocsConfig,
  useDocsContextMenu,
  useDocsStack,
  useMultiSelect,
  useNearestDoc,
  useNumberInput,
  usePasswordStrength,
  useTheme,
  useToast
};
//# sourceMappingURL=index.js.map